// @ts-nocheck
// Worksheet Query Builder
// Builds DuckDB SQL queries from Tableau worksheet specs for published extracts

import type { WorksheetSpecV1, CrosstabSpecV1 } from './spec'
import { compileTableauExpressionToDuckdbSql } from '../query_builder/calcs/duckdbCompiler'
import type { VisualSpec, SetDef } from '../query_builder/visualSpec'
import { renderOverClause } from '../query_builder/calcs/tableCalcs'
import {
  normalizeTableauFieldKeyStrict,
  buildTemplateParamSafeKey,
  isWorkbookParameterFieldEntry,
  normalizeTableauFieldKey,
  normalizeTableauFieldToken,
  normalizeTableauDatasourceKey,
  cleanBracketedFieldRef as cleanBracketedFieldRef0,
  parseTableauFieldRef,
  resolveExtractColumnName,
  stripTableauAutoGeneratedSuffixes,
} from './fieldIdentity'
import {
  isFieldMappingDebugEnabled,
  isFieldMappingVerboseEnabled,
  logFieldMapping,
  pushFieldMappingTrace,
} from '../utils/fieldMappingDebug'
import { postOptionalLocalDebugLog } from '../utils/localDebugLog'
import { deriveComputeUsingFromTableauAttrs, normalizeTableauTableCalcAttrs, type TableCalcDim } from './tableCalcComputeUsing'
import type { WorksheetCompilation } from './worksheetQueryArtifacts'
import { buildAggBasePlannerSignature } from './worksheetAggBaseIdentity'
import { buildDatasourceExecutionNodePlannerSignature } from './datasourceExecutionGraph'
import { buildTableauDatePillLabel, normalizeTableauDateTimeGrain } from './datePill'
import { isHideDisplayFilter } from './hideFilterPolicy'
import { resolveWorksheetStrictness } from './worksheetStrictness'
import {
  assertNonEmptySourceSchemaForLocalExecution,
  isColumnInSourceSchema,
  sourceSchemaColumnNames,
  type SourceSchema,
} from './sourceSchema'

const hashString = (input: string): number => {
  let hash = 0
  const s = String(input ?? '')
  for (let i = 0; i < s.length; i++) {
    hash = ((hash << 5) - hash + s.charCodeAt(i)) | 0
  }
  return hash
}
const PLANNER_ROOT_TRACE_COLUMN_KEYS = new Set(['productdiscounts', 'servicediscounts'])
const plannerRootTraceKey = (raw: any): string =>
  normalizeTableauFieldKey(String(raw ?? '').trim()) ||
  String(raw ?? '').trim().toLowerCase().replace(/[^a-z0-9_]+/g, '')
const shouldTracePlannerColumn = (...values: any[]): boolean =>
  values.some((value) => PLANNER_ROOT_TRACE_COLUMN_KEYS.has(plannerRootTraceKey(value)))
const logPlannerRootTrace = (event: string, payload: Record<string, any>): void => {
  try {
    console.warn('[FG-PLANNER-ROOT]', JSON.stringify({
      event,
      ts: Date.now(),
      ...payload,
    }))
  } catch { }
}
const safeStableStringify = (value: any): string => {
  // Deterministic JSON stringify (sorted keys + circular-safe).
  try {
    const seen = new WeakSet<object>()
    const norm = (v: any): any => {
      if (v === null || typeof v === 'undefined') return v
      if (typeof v === 'bigint') return String(v)
      if (typeof v !== 'object') return v
      if (v instanceof Date) {
        const time = v.getTime()
        return Number.isFinite(time) ? v.toISOString() : 'Invalid Date'
      }
      const obj = v as object
      if (seen.has(obj)) return '[Circular]'
      seen.add(obj)
      if (Array.isArray(v)) return v.map(norm)
      if (v instanceof Map) {
        const entries = Array.from(v.entries()).map(([k, vv]) => [norm(k), norm(vv)])
        entries.sort((a, b) => String(a[0]).localeCompare(String(b[0])))
        return { __map__: entries }
      }
      if (v instanceof Set) {
        const vals = Array.from(v.values()).map(norm)
        vals.sort((a, b) => String(a).localeCompare(String(b)))
        return { __set__: vals }
      }
      const out: Record<string, any> = {}
      const keys = Object.keys(v).sort((a, b) => a.localeCompare(b))
      for (const k of keys) out[k] = norm((v as any)[k])
      return out
    }
    return JSON.stringify(norm(value))
  } catch {
    try {
      return JSON.stringify(value)
    } catch {
      return ''
    }
  }
}
const STRICT_NO_FALLBACKS = resolveWorksheetStrictness().strictNoFallbacks
const ALLOW_FALLBACKS = !STRICT_NO_FALLBACKS
const UNRESOLVED_FIELD = '__fg_unresolved__'
const isUnresolvedField = (v: any): boolean => String(v ?? '').trim().toLowerCase() === UNRESOLVED_FIELD
const looksSqlWindowExpression = (exprRaw: any): boolean => {
  const expr = String(exprRaw ?? '')
  return /\bOVER\s*\(/i.test(expr) || /\bROW_NUMBER\s*\(/i.test(expr) || /\bLAG\s*\(/i.test(expr) || /\bLEAD\s*\(/i.test(expr)
}
const hasSqlAggregateCall = (exprRaw: any): boolean => {
  const expr = String(exprRaw ?? '')
  if (!expr) return false
  const aggregateFns = new Set([
    'SUM',
    'AVG',
    'AVERAGE',
    'MEAN',
    'MIN',
    'MAX',
    'COUNT',
    'COUNTD',
    'ANY_VALUE',
    'MEDIAN',
    'STDDEV',
    'STDDEV_SAMP',
    'STDDEV_POP',
    'VAR',
    'VAR_SAMP',
    'VAR_POP',
    'FIRST',
    'LAST',
    'STRING_AGG',
    'LIST',
    'ARRAY_AGG',
    'BIT_AND',
    'BIT_OR',
    'BIT_XOR',
    'BOOL_AND',
    'BOOL_OR',
    'QUANTILE',
    'QUANTILE_CONT',
    'QUANTILE_DISC',
  ])
  let inStr = false
  let inIdent = false
  let inLineComment = false
  let inBlockComment = false
  const isWordChar = (c: string | undefined): boolean => Boolean(c && /[A-Za-z0-9_]/.test(c))
  for (let i = 0; i < expr.length; i += 1) {
    const ch = expr[i]
    const next = expr[i + 1]
    if (inLineComment) {
      if (ch === '\n') inLineComment = false
      continue
    }
    if (inBlockComment) {
      if (ch === '*' && next === '/') {
        inBlockComment = false
        i += 1
      }
      continue
    }
    if (inStr) {
      if (ch === "'" && next === "'") {
        i += 1
        continue
      }
      if (ch === "'") inStr = false
      continue
    }
    if (inIdent) {
      if (ch === '"' && next === '"') {
        i += 1
        continue
      }
      if (ch === '"') inIdent = false
      continue
    }
    if (ch === '-' && next === '-') {
      inLineComment = true
      i += 1
      continue
    }
    if (ch === '/' && next === '*') {
      inBlockComment = true
      i += 1
      continue
    }
    if (ch === "'") {
      inStr = true
      continue
    }
    if (ch === '"') {
      inIdent = true
      continue
    }
    if (!/[A-Za-z_]/.test(ch)) continue
    let j = i + 1
    while (j < expr.length && /[A-Za-z0-9_]/.test(expr[j])) j += 1
    const word = expr.slice(i, j).toUpperCase()
    if (aggregateFns.has(word) && !isWordChar(expr[i - 1])) {
      let k = j
      while (k < expr.length && /\s/.test(expr[k])) k += 1
      if (expr[k] === '(') return true
    }
    i = j - 1
  }
  return false
}
const isSqlConstantLiteralExpression = (exprRaw: any): boolean => {
  let expr = String(exprRaw ?? '').trim()
  if (!expr) return false
  while (expr.startsWith('(') && expr.endsWith(')')) {
    let depth = 0
    let balanced = true
    for (let i = 0; i < expr.length; i += 1) {
      const ch = expr[i]
      if (ch === '(') depth += 1
      else if (ch === ')') depth -= 1
      if (depth === 0 && i < expr.length - 1) {
        balanced = false
        break
      }
    }
    if (!balanced) break
    expr = expr.slice(1, -1).trim()
  }
  if (/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i.test(expr)) return true
  if (/^(?:NULL|TRUE|FALSE)$/i.test(expr)) return true
  if (/^'(?:[^']|'')*'$/.test(expr)) return true
  return false
}
const isSqlNullPlaceholderReference = (exprRaw: any): boolean => {
  let expr = String(exprRaw ?? '').trim()
  if (!expr) return false
  while (expr.startsWith('(') && expr.endsWith(')')) {
    let depth = 0
    let balanced = true
    let inStr = false
    let inIdent = false
    for (let i = 0; i < expr.length; i += 1) {
      const ch = expr[i]
      if (inStr) {
        if (ch === "'" && expr[i + 1] === "'") { i += 1; continue }
        if (ch === "'") inStr = false
        continue
      }
      if (inIdent) {
        if (ch === '"' && expr[i + 1] === '"') { i += 1; continue }
        if (ch === '"') inIdent = false
        continue
      }
      if (ch === "'") { inStr = true; continue }
      if (ch === '"') { inIdent = true; continue }
      if (ch === '(') depth += 1
      else if (ch === ')') depth -= 1
      if (depth === 0 && i < expr.length - 1) {
        balanced = false
        break
      }
    }
    if (!balanced) break
    expr = expr.slice(1, -1).trim()
  }
  expr = expr.replace(/^(?:[A-Za-z_][A-Za-z0-9_]*|"(?:""|[^"])+")\s*\.\s*/i, '').trim()
  return /^"NULL"$/i.test(expr)
}
const canSqlExpressionAppearInGroupBy = (exprRaw: any): boolean => {
  const expr = String(exprRaw ?? '').trim()
  return Boolean(expr) && !isSqlConstantLiteralExpression(expr) && !isSqlNullPlaceholderReference(expr) && !looksSqlWindowExpression(expr) && !hasSqlAggregateCall(expr)
}

const resolveWorksheetDebug = (worksheetName?: string) => {
  if (typeof window === 'undefined') return { enabled: false, sheet: null, matches: false }
  const qp = new URLSearchParams(window.location.search)
  const rawFlag =
    qp.get('fgMarksDebug') ??
    qp.get('fgTableCalcDebug') ??
    window.localStorage.getItem('fg_marks_debug') ??
    window.localStorage.getItem('fg_table_calc_debug')
  const enabled = Boolean(rawFlag && !['0', 'false', 'off', 'no'].includes(String(rawFlag).trim().toLowerCase()))
  const sheet =
    (qp.get('fgMarksSheet') ??
      qp.get('fgTableCalcSheet') ??
      window.localStorage.getItem('fg_marks_debug_sheet') ??
      window.localStorage.getItem('fg_table_calc_debug_sheet') ??
      '').trim() || null
  if (!enabled) return { enabled: false, sheet, matches: false }
  if (!sheet) return { enabled: true, sheet: null, matches: true }
  const norm = (s: any) => String(s ?? '').trim().toLowerCase()
  return { enabled: true, sheet, matches: norm(sheet) === norm(worksheetName) }
}

export function __test_findWindowCalls(sql: string): Array<{ start: number; end: number; full: string }> {
  const calls: Array<{ start: number; end: number; full: string }> = []
  const isWordChar = (c: string | undefined): boolean => Boolean(c && /[A-Za-z0-9_]/.test(c))
  const isWs = (c: string) => c === ' ' || c === '\n' || c === '\t' || c === '\r'
  const stack: Array<{ fnStart: number; lParen: number; isFnCall: boolean }> = []
  let inStr = false
  let inIdent = false
  let lastWordEnd = -1
  let lastWordStart = -1
  for (let i = 0; i < sql.length; i++) {
    const ch = sql[i]
    if (inStr) {
      if (ch === "'" && sql[i + 1] === "'") { i++; continue }
      if (ch === "'") inStr = false
      continue
    }
    if (inIdent) {
      if (ch === '"' && sql[i + 1] === '"') { i++; continue }
      if (ch === '"') inIdent = false
      continue
    }
    if (ch === "'") { inStr = true; continue }
    if (ch === '"') { inIdent = true; continue }
    if (isWordChar(ch)) {
      if (lastWordEnd !== i) lastWordStart = i
      lastWordEnd = i + 1
      continue
    }
    if (ch === '(') {
      const isFnCall =
        lastWordEnd > 0
        && lastWordEnd >= lastWordStart
        && (lastWordEnd === i || sql.slice(lastWordEnd, i).trim() === '')
      stack.push({ fnStart: isFnCall ? lastWordStart : i, lParen: i, isFnCall })
      continue
    }
    if (ch !== ')') continue
    const frame = stack.pop()
    if (!frame || !frame.isFnCall) continue
    let j = i + 1
    while (j < sql.length && isWs(sql[j])) j++
    const c0 = sql[j]
    const c1 = sql[j + 1]
    const c2 = sql[j + 2]
    const c3 = sql[j + 3]
    if (!((c0 === 'O' || c0 === 'o') && (c1 === 'V' || c1 === 'v') && (c2 === 'E' || c2 === 'e') && (c3 === 'R' || c3 === 'r'))) continue
    const afterOver = sql[j + 4]
    if (afterOver && isWordChar(afterOver)) continue
    let k = j + 4
    while (k < sql.length && isWs(sql[k])) k++
    if (sql[k] === '(') {
      let depth = 0
      let m = k
      let inStr2 = false
      for (; m < sql.length; m++) {
        const c = sql[m]
        if (c === "'") {
          if (inStr2 && sql[m + 1] === "'") { m++; continue }
          inStr2 = !inStr2
          continue
        }
        if (inStr2) continue
        if (c === '(') depth++
        else if (c === ')') {
          depth--
          if (depth === 0) break
        }
      }
      if (m >= sql.length) continue
      const callStart = frame.fnStart
      const callEnd = m + 1
      calls.push({ start: callStart, end: callEnd, full: sql.slice(callStart, callEnd) })
      continue
    }
    let m = k
    if (sql[m] === '"') {
      m++
      while (m < sql.length) {
        if (sql[m] === '"') {
          if (sql[m + 1] === '"') { m += 2; continue }
          m++
          break
        }
        m++
      }
    } else if (/[A-Za-z_]/.test(sql[m] || '')) {
      m++
      while (m < sql.length && /[A-Za-z0-9_$]/.test(sql[m] || '')) m++
    } else {
      continue
    }
    if (m <= k) continue
    const callStart = frame.fnStart
    const callEnd = m
    calls.push({ start: callStart, end: callEnd, full: sql.slice(callStart, callEnd) })
  }
  return calls
}

type StagedChunkSharedStageDefinition = {
  placeholderName: string
  sql: string
}

type StagedChunkReusableAggBaseDefinition = {
  placeholderName: string
  materializedSql: string
  chunkIndexes: number[]
  // Direct projection aliases that staged chunks expect to read back from the
  // reusable agg-base temp table. When the agg-base is split into
  // `_agg_base_src` + `_agg_base`, later window-only wrappers can otherwise
  // drop these passthrough caption aliases even though downstream chunk bases
  // still project them directly (for example `t."East/West"`).
  requiredProjectedAliases?: string[]
  // Some chunks derive visible aliases from grouped keys (for example
  // `CASE ... "__fg_group_key_3" ... AS "Time Period (Monthly)"`) while later
  // chunks read those aliases back directly from the shared agg base. Preserve
  // those full select items as well so the reusable agg base can expose both
  // the internal grouped keys and the derived caption aliases.
  requiredProjectedSelectItems?: Array<{
    selectItem: string
    sourceAlias: string
  }>
  // When true, this agg base was produced by the synthetic unified-fallback
  // path (i.e. buildBaseWideOptimizedSql over the *entire* plan's measure
  // list). Its materializedSql already has LOD windows expanded inline, and
  // running the expensive `splitReusableAggBaseWindowSource` on it triggers
  // a quadratic `finalizeSql` + `collapseRedundantGroupedPassthroughWrappers`
  // cascade (observed: 25s+ on modestly-sized Weekly/Monthly sheets).
  // Skipping the split entirely, however, forces DuckDB to materialize the
  // aggregate+GROUPING-SETS plus 14+ window functions inline in one pass,
  // which OOM'd the OPFS temp quota on Monthly-Comparison. This flag
  // therefore redirects the emit path to the cheap linear-time
  // `cheapSplitReusableAggBaseWindowSource` splitter, which structurally
  // pulls the window-free inner grouped SELECT into `_agg_base_src` and
  // rebinds the outer window SELECT to that materialized table in
  // `_agg_base` without re-running any finalize/collapse passes.
  skipWindowSourceSplit?: boolean
}

const renderStagedChunkReusableAggBaseSourceSql = (input: {
  wrapperSourceSql: string
  reusableAggBases: Array<{
    placeholderName: string
    materializedSql: string
    sourceSql?: string | null
    sourceTableName?: string | null
    tableName?: string | null
  }>
  sharedStages: StagedChunkSharedStageDefinition[]
}): string => {
  const quoteStagedPlanIdent = (nameRaw: string): string => {
    const name = String(nameRaw || '').trim()
    if (!name) return '""'
    if (name.startsWith('"') && name.endsWith('"')) return name
    return `"${name.replace(/"/g, '""')}"`
  }
  const wrapperSourceSql = String(input.wrapperSourceSql || '')
  let seeded = wrapperSourceSql
  const placeholderTableRebindings = new Map<string, string>()
  const unquoteStagedPlanIdent = (nameRaw: string): string => {
    const name = String(nameRaw || '').trim()
    return name.startsWith('"') && name.endsWith('"') ? name.slice(1, -1) : name
  }
  const sqlRefsPlaceholder = (sqlRaw: string, placeholderRaw: string): boolean => {
    const sql = String(sqlRaw || '')
    const placeholder = String(placeholderRaw || '').trim()
    if (!sql || !placeholder) return false
    const unquoted = unquoteStagedPlanIdent(placeholder)
    if (placeholder.startsWith('"') && placeholder.endsWith('"') && sql.includes(placeholder)) return true
    if (!unquoted) return false
    return new RegExp(`(^|[^A-Za-z0-9_"])${unquoted.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?=$|[^A-Za-z0-9_"])`, 'i').test(sql)
  }
  const reusableAggDefs = input.reusableAggBases.flatMap((reusableAggBase) => {
    const placeholderName = String(reusableAggBase.placeholderName || '').trim()
    if (!placeholderName) return []
    const tableName = String(reusableAggBase.tableName || '').trim()
    if (tableName) {
      const quotedPlaceholderName = quoteStagedPlanIdent(placeholderName)
      const quotedTableName = quoteStagedPlanIdent(tableName)
      placeholderTableRebindings.set(quotedPlaceholderName, quotedTableName)
      seeded = seeded.replaceAll(quotedPlaceholderName, quotedTableName)
      return []
    }
    const materializedSql = String(reusableAggBase.materializedSql || '').trim()
    if (!materializedSql) return []
    const sourceSql = String(reusableAggBase.sourceSql || '').trim()
    const sourceTableName = String(reusableAggBase.sourceTableName || '').trim()
    const defs: Array<{ placeholderName: string; sql: string }> = []
    if (sourceTableName && sourceSql) {
      defs.push({
        placeholderName: sourceTableName,
        sql: sourceSql,
      })
    }
    defs.push({
      placeholderName,
      sql: materializedSql,
    })
    return defs
  })
  const cteDefs = [
    ...input.sharedStages.map((sharedStage) => ({
      placeholderName: String(sharedStage.placeholderName || '').trim(),
      sql: String(sharedStage.sql || '').trim(),
    })),
    ...reusableAggDefs,
  ].filter((def) => def.placeholderName && def.sql)
  if (placeholderTableRebindings.size > 0) {
    for (const def of cteDefs) {
      for (const [placeholderName, tableName] of placeholderTableRebindings.entries()) {
        def.sql = def.sql.replaceAll(placeholderName, tableName)
      }
    }
  }
  const neededPlaceholders = new Set(
    cteDefs
      .filter((def) => sqlRefsPlaceholder(seeded, def.placeholderName))
      .map((def) => def.placeholderName),
  )
  let changed = true
  while (changed) {
    changed = false
    for (const def of cteDefs) {
      if (!neededPlaceholders.has(def.placeholderName)) continue
      for (const maybeDep of cteDefs) {
        if (neededPlaceholders.has(maybeDep.placeholderName)) continue
        if (!sqlRefsPlaceholder(def.sql, maybeDep.placeholderName)) continue
        neededPlaceholders.add(maybeDep.placeholderName)
        changed = true
      }
    }
  }
  // DuckDB: `AS MATERIALIZED` forces an extra physical copy of the CTE result. For the common
  // reusable-agg-base path, shared stages are often `SELECT * FROM "<persisted plan table>" AS ...`
  // where the table is already materialized in the session. Materializing again doubles memory and
  // wall time on `window_input_base` / datasource-node builds without changing semantics.
  const isSimpleSelectStarFromSingleRelation = (sqlRaw: string): boolean => {
    const s = String(sqlRaw || '').trim()
    if (!s) return false
    return /^SELECT\s+\*\s+FROM\s+("[^"]+"|[A-Za-z_][A-Za-z0-9_]*)\s+(?:AS\s+("[^"]+"|[A-Za-z_][A-Za-z0-9_]*)\s*)?$/i.test(s)
  }
  const allCtes = cteDefs
    .filter((def) => neededPlaceholders.has(def.placeholderName))
    .map((def) => {
      const mat = !isSimpleSelectStarFromSingleRelation(def.sql)
      return `${def.placeholderName} AS ${mat ? `MATERIALIZED (${def.sql})` : `(${def.sql})`}`
    })
  if (allCtes.length <= 0) return seeded
  return `WITH ${allCtes.join(',\n')}\n${seeded}`
}

export function __test_renderStagedChunkReusableAggBaseSourceSql(input: {
  wrapperSourceSql: string
  reusableAggBases: Array<{
    placeholderName: string
    materializedSql: string
    sourceSql?: string | null
    sourceTableName?: string | null
    tableName?: string | null
  }>
  sharedStages: StagedChunkSharedStageDefinition[]
}): string {
  return renderStagedChunkReusableAggBaseSourceSql(input)
}

export type WorksheetQueryOptions = {
  extractUri: string
  // Optional: override the base FROM source with a subquery.
  //
  // Use this for union/join modeling where the "logical table" is constructed from multiple extracts.
  // This string must be a valid table-producing SQL fragment, typically a SELECT statement:
  //   SELECT ... FROM read_parquet('...') UNION ALL SELECT ... FROM read_parquet('...')
  //
  // When provided, `extractUri` is ignored for FROM purposes (but may still be used for debug).
  baseFromSql?: string | null
  // Optional: list of physical extract columns (DuckDB schema).
  // When provided, we "snap" resolved ids to actually-present columns to avoid
  // runtime "column not found" failures.
  extractColumns?: string[] | null
  sourceSchema?: SourceSchema | null
  filters?: Array<{
    field_name: string
    operator: string
    value: any
    // Optional Tableau order-of-ops hints (best-effort)
    filter_type?: 'context' | 'dimension' | 'measure' | string | null
    is_measure?: boolean
    aggregation?: string | null
    context?: boolean
    kind?: string | null
  }>
  parameters?: Record<string, any>
  rowPage?: number | null
  rowPageSize?: number | null
  colPage?: number | null
  colPageSize?: number | null
  // When enabled, the query builder emits a SQL *template*:
  // - parameters/filters are compiled as placeholders (no value inlining)
  // - caller must instantiate/bind before execution
  templateMode?: boolean
  limit?: number
  // Optional: Tableau-style densification / domain completion for crosstabs.
  // When enabled, we CROSS JOIN distinct dimension domains (and Measure Names)
  // then LEFT JOIN the aggregated results to emit missing combinations.
  densify?: boolean
  // Optional: emit Tableau-style totals/subtotals rows ("All" sentinels) in SQL.
  // This is important for ratio/percent measures where summing leaf values is incorrect.
  includeTotals?: boolean
  // Optional: drop auto tooltip expansion ("All Fields") and only include explicit tooltip fields.
  lazyTooltips?: boolean
  // Optional: prefer LOD pre-aggregation over window functions (best-effort).
  lodPreAggregate?: boolean
  // Optional: skip heavy builder features for debugging.
  fastFeatures?: boolean
  // Optional debug-only filter handoff/lowering diagnostics.
  debugFilters?: boolean
  // Optional interaction-cube slicers. These are not rendered worksheet
  // dimensions; they are extra grouping dimensions used only for the
  // worksheet interaction cube build SQL.
  interactionCubeSlicers?: Array<{
    fieldName?: string | null
    displayName?: string | null
    columnName?: string | null
    calculationToken?: string | null
  }> | null
  skipCalcCompilation?: boolean
  skipNestedWindowRewrite?: boolean
  // Optional: semantic precompute plumbing (perf-only; local mode).
  semanticPrecompute?: {
    collectExtras?: boolean
    precomputedExtras?: Record<string, string>
    precomputedLods?: Record<string, string>
  }
  traceUnresolved?: boolean
  builderTimeoutMs?: number
  // Optional: cap rewriteNestedSubqueries loop iterations (fail fast).
  rewriteLoopLimit?: number
  // When provided, we can compile Tableau calculated fields/table calcs into DuckDB SQL
  // instead of relying on Tableau oracle data exports.
  workbookIr?: any
  // Optional extract field manifest from backend.
  fieldManifest?: any
  // Optional binding map from semantic registry resolution.
  bindingMap?: any
  // Active datasource identity for strict binding resolution.
  datasourceKey?: string | null
  datasourceKeys?: string[] | null
  worksheetName?: string
  // Optional: override Measure Values list when Measure Names is used.
  measureValuesOverride?: Array<{ field_name?: string; field?: string } | string> | null
  // Internal server-artifact export mode. Forces deterministic staged execution
  // plans for risky Tableau worksheet shapes instead of handing native DuckDB a
  // single repeated Measure Names/Values + window graph.
  artifactExportMode?: boolean

  // Optional: Tableau-like logical relationships mode.
  //
  // When provided, the query builder will attempt "late materialization" behavior:
  // - Aggregate each table to the viz grain (dims used from that table + relationship keys)
  // - Join the aggregated tables on relationship clauses
  // - Project dims + measures without fanout (best-effort)
  //
  // This is intentionally scoped to physical columns (no calc compilation) for now.
  relationshipModel?: {
    version?: number
    primary_table?: string | null
    tables: Array<{
      name: string
      // SQL table expression usable in FROM (e.g. read_parquet('...') or (SELECT ... UNION ALL ...))
      fromSql: string
      extractColumns?: string[] | null
      fieldManifest?: any | null
    }>
    relationships: Array<{
      a: string
      b: string
      clauses: Array<{ left: string; right: string }>
      cardinality?: string | null
    }>
  } | null
}

type TooltipTokenNeed =
  | { kind: 'all_fields' }
  | { kind: 'field'; field: string; token: string }
  | { kind: 'func'; fn: string; arg: string; token: string }

type WorkbookIrDerived = {
  ts: number
  fdCols: any[]
  fdCis: any[]
  metaByName: Map<string, any>
  metaByCaptionKey: Map<string, any>
  metaByNameKey: Map<string, any>
  metaByNameKeyStrict: Map<string, any>
  calcFormulaByColumn: Map<string, string>
  fdBaseColNames: string[]
  calcEntries: any[]
  sets: SetDef[]
}

// Cache expensive WorkbookIR-derived indices across builds.
// This is especially important on desktop where repeated worksheet interactions can rebuild queries frequently.
const WORKBOOK_IR_DERIVED = new WeakMap<object, WorkbookIrDerived>()

function getWorkbookIrDerived(workbookIr: any): WorkbookIrDerived {
  const ir = (workbookIr && typeof workbookIr === 'object') ? (workbookIr as any) : null
  if (!ir) {
    return {
      ts: Date.now(),
      fdCols: [],
      fdCis: [],
      metaByName: new Map(),
      metaByCaptionKey: new Map(),
      metaByNameKey: new Map(),
      metaByNameKeyStrict: new Map(),
      calcFormulaByColumn: new Map(),
      fdBaseColNames: [],
      calcEntries: [],
      sets: [],
    }
  }
  const cached = WORKBOOK_IR_DERIVED.get(ir as any)
  if (cached) return cached

  const fd = (ir as any)?.field_dictionary || null
  const fdCols: any[] = Array.isArray(fd?.columns) ? (fd.columns as any[]) : []
  const fdCis: any[] = Array.isArray(fd?.column_instances) ? (fd.column_instances as any[]) : []

  const metaByName = new Map<string, any>()
  const metaByCaptionKey = new Map<string, any>()
  const metaByNameKey = new Map<string, any>()
  const metaByNameKeyStrict = new Map<string, any>()
  const calcFormulaByColumn = new Map<string, string>()
  const fdBaseColNames: string[] = []

  for (const c of fdCols) {
    const nm = String((c as any)?.name ?? '').trim()
    const cap = String((c as any)?.caption ?? '').trim()
    if (nm) metaByName.set(nm, c)
    const capKey = cap ? normalizeTableauFieldKey(cap) : ''
    if (capKey && !metaByCaptionKey.has(capKey)) metaByCaptionKey.set(capKey, c)
    const nmKey = nm ? normalizeTableauFieldKey(nm) : ''
    if (nmKey && !metaByNameKey.has(nmKey)) metaByNameKey.set(nmKey, c)
    const nmKeyStrict = nm ? normalizeTableauFieldKeyStrict(nm) : ''
    if (nmKeyStrict && !metaByNameKeyStrict.has(nmKeyStrict)) metaByNameKeyStrict.set(nmKeyStrict, c)

    const formula = String((c as any)?.calculation?.formula ?? '').trim()
    if (nm && formula) calcFormulaByColumn.set(nm, formula)
    if (nm && !formula) fdBaseColNames.push(nm)
  }

  // Some Tableau exports omit physical base columns from `field_dictionary.columns`
  // while still referencing them via `column_instances[*].column` (for example
  // `[daysworked]`, `[haircutcount]` in FIXED LOD calcs). Synthesize minimal metadata
  // so calc compilation can still resolve these source fields.
  for (const ci of fdCis) {
    const baseRaw = String((ci as any)?.column ?? '').trim()
    if (!baseRaw) continue
    if (metaByName.has(baseRaw)) continue
    const baseCaption = cleanBracketedFieldRef0(baseRaw) || baseRaw
    const synthetic = {
      name: baseRaw,
      caption: baseCaption,
      role: String((ci as any)?.role ?? '').trim() || null,
      datatype: String((ci as any)?.datatype ?? '').trim() || null,
      calculation: null,
      __synthetic_from_column_instance: true,
    } as any
    metaByName.set(baseRaw, synthetic)
    const capKey = baseCaption ? normalizeTableauFieldKey(baseCaption) : ''
    if (capKey && !metaByCaptionKey.has(capKey)) metaByCaptionKey.set(capKey, synthetic)
    const nmKey = normalizeTableauFieldKey(baseRaw)
    if (nmKey && !metaByNameKey.has(nmKey)) metaByNameKey.set(nmKey, synthetic)
    const nmKeyStrict = normalizeTableauFieldKeyStrict(baseRaw)
    if (nmKeyStrict && !metaByNameKeyStrict.has(nmKeyStrict)) metaByNameKeyStrict.set(nmKeyStrict, synthetic)
    fdCols.push(synthetic)
    fdBaseColNames.push(baseRaw)
  }

  const calcEntries: any[] = []
  const calcSeen = new Set<string>()
  const addCalcEntry = (nameRaw: any, formula: string, tableCalcsRaw?: any[] | null) => {
    const name = cleanBracketedFieldRef0(String(nameRaw ?? '').trim()) || String(nameRaw ?? '').trim()
    if (!name) return
    const k = name.trim().toLowerCase()
    if (!k || calcSeen.has(k)) return
    calcSeen.add(k)
    calcEntries.push({
      id: `tableau_calc:${name}`,
      name,
      expression: formula,
      language: 'tableau',
      // NOTE: base alias is hard-coded to `t` throughout this builder.
      scope: { kind: 'source', source_id: 't' },
      source_id: 't',
      tableau_table_calcs: Array.isArray(tableCalcsRaw) ? tableCalcsRaw : undefined,
    })
  }

  for (const c of fdCols) {
    const formula = String((c as any)?.calculation?.formula ?? '').trim()
    if (!formula) continue
    const tableCalcsRaw = Array.isArray((c as any)?.calculation?.table_calcs)
      ? ((c as any).calculation.table_calcs as any[])
      : null
    addCalcEntry((c as any)?.name, formula, tableCalcsRaw)
    addCalcEntry((c as any)?.caption, formula, tableCalcsRaw)
  }

  // Column instances can reference calculated columns; add aliases so tokenized names resolve.
  for (const ci of fdCis) {
    const base = String((ci as any)?.column ?? '').trim()
    if (!base) continue
    const formula = calcFormulaByColumn.get(base)
    if (!formula) continue
    const baseMeta = metaByName.get(base) || null
    const tableCalcsRaw =
      Array.isArray((ci as any)?.table_calcs) && (ci as any).table_calcs.length
        ? ((ci as any).table_calcs as any[])
        : (Array.isArray((baseMeta as any)?.calculation?.table_calcs) ? ((baseMeta as any).calculation.table_calcs as any[]) : null)
    addCalcEntry((ci as any)?.name, formula, tableCalcsRaw)
    addCalcEntry((ci as any)?.caption, formula, tableCalcsRaw)
  }

  // Last-resort: sample calculations from the workbook IR (best-effort parser capture).
  try {
    const samples: any[] = Array.isArray((ir as any)?.sample_calculations)
      ? ((ir as any).sample_calculations as any[])
      : []
    for (const s of samples) {
      const nm = String((s as any)?.name ?? '').trim()
      const formula = String((s as any)?.formula ?? '').trim()
      if (!nm || !formula) continue
      addCalcEntry(nm, formula)
    }
  } catch { }

  const sets: SetDef[] = (() => {
    try {
      const rawSets: any[] = Array.isArray((ir as any)?.sets) ? (((ir as any).sets) as any[]) : []
      if (!rawSets.length) return []
      const out: SetDef[] = []
      for (let i = 0; i < rawSets.length; i++) {
        const s = rawSets[i]
        const name = String(s?.name ?? s?.raw_name ?? '').trim()
        if (!name) continue
        const members = Array.isArray(s?.members) ? (s.members as any[]) : []
        const excluded_members = Array.isArray(s?.excluded_members) ? (s.excluded_members as any[]) : []
        const topnRaw = s?.topn
        const topn = topnRaw && typeof topnRaw === 'object'
          ? {
              n: (topnRaw as any)?.n ?? null,
              count_param: (topnRaw as any)?.count_param ?? null,
              direction: String((topnRaw as any)?.direction ?? 'top').trim().toLowerCase() as 'top' | 'bottom',
              units: (topnRaw as any)?.units ?? null,
              by: ((topnRaw as any)?.by_field || (topnRaw as any)?.aggregation)
                ? {
                    aggregation: (topnRaw as any)?.aggregation ?? null,
                    field: (topnRaw as any)?.by_field ?? null,
                  }
                : null,
              include_other: Boolean((topnRaw as any)?.include_other ?? false),
            }
          : null
        const conditionFormula = String(s?.condition_formula ?? '').trim()
        const conditionExpr = (s as any)?.condition ?? null
        const condition = (conditionFormula || conditionExpr)
          ? { formula: conditionFormula || null, expression: conditionExpr }
          : null
        const combination = (s as any)?.combination ?? null
        const kind = topn
          ? 'topn'
          : condition
            ? 'condition'
            : (combination ? 'combined' : (members.length || excluded_members.length ? 'manual' : 'unknown'))
        out.push({
          id: String(s?.id ?? name ?? `set_${i}`),
          name,
          raw_name: s?.raw_name ?? null,
          scope: null,
          scope_field: s?.scope_field ?? null,
          datasource: s?.datasource ?? null,
          members,
          excluded_members,
          kind,
          topn,
          condition,
          combination,
        })
      }
      return out
    } catch {
      return []
    }
  })()

  const derived: WorkbookIrDerived = {
    ts: Date.now(),
    fdCols,
    fdCis,
    metaByName,
    metaByCaptionKey,
    metaByNameKey,
    metaByNameKeyStrict,
    calcFormulaByColumn,
    fdBaseColNames,
    calcEntries,
    sets,
  }
  WORKBOOK_IR_DERIVED.set(ir as any, derived)
  return derived
}

// Optional: let callers (e.g. the worker) precompute the derived cache on idle / in parallel.
export function primeWorkbookIrDerived(workbookIr: any): {
  ok: boolean
  fdCols: number
  fdCis: number
  calcEntries: number
  sets: number
} {
  try {
    const d = getWorkbookIrDerived(workbookIr)
    return {
      ok: true,
      fdCols: Array.isArray(d.fdCols) ? d.fdCols.length : 0,
      fdCis: Array.isArray(d.fdCis) ? d.fdCis.length : 0,
      calcEntries: Array.isArray(d.calcEntries) ? d.calcEntries.length : 0,
      sets: Array.isArray(d.sets) ? d.sets.length : 0,
    }
  } catch {
    return { ok: false, fdCols: 0, fdCis: 0, calcEntries: 0, sets: 0 }
  }
}

function extractTooltipTokensFromTemplate(template: string): string[] {
  const s = String(template ?? '')
  if (!s) return []
  const out: string[] = []
  const htmlTags = new Set<string>([
    'a', 'b', 'br', 'code', 'div', 'em', 'font', 'g', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'i', 'img', 'li', 'line', 'ol', 'p', 'path', 'polygon', 'polyline', 'pre', 'rect', 'script',
    'small', 'span', 'strong', 'style', 'sub', 'sup', 'svg', 'table', 'tbody', 'td', 'tfoot', 'th',
    'thead', 'tr', 'u', 'ul', 'circle',
  ])
  // Prefer our internal token format
  for (const m of s.matchAll(/\{\{\s*([^}]+?)\s*\}\}/g)) {
    const key = String(m?.[1] ?? '').trim()
    if (key) out.push(key)
  }
  // Back-compat / import fallback: Tableau placeholders like <Field>
  for (const m of s.matchAll(/<([^<>]+)>/g)) {
    const key = String(m?.[1] ?? '').trim()
    if (!key) continue
    const lower = key.toLowerCase()
    if (lower.startsWith('/')) continue
    // Avoid treating HTML tags (including self-closing tags like <br/>, and tags with attrs like <span style="...">) as placeholders.
    const tag = lower.split(/\s+/)[0]?.replace(/\/$/, '') || ''
    if (tag && htmlTags.has(tag)) continue
    out.push(key)
  }
  // Dedup preserve order
  const seen = new Set<string>()
  const deduped: string[] = []
  for (const t of out) {
    const k = t.toLowerCase()
    if (seen.has(k)) continue
    seen.add(k)
    deduped.push(t)
  }
  return deduped
}

function parseTooltipTokenNeed(raw: string): TooltipTokenNeed | null {
  const t = String(raw ?? '').trim()
  if (!t) return null
  if (t.toLowerCase() === 'all fields') return { kind: 'all_fields' }
  // Allow empty-arg functions like INDEX(), FIRST(), LAST().
  const m = t.match(/^([A-Za-z_][A-Za-z0-9_]*)\s*\(\s*(.*?)\s*\)\s*$/)
  if (m) {
    const fn = String(m[1] ?? '').trim().toUpperCase()
    const arg = String(m[2] ?? '').trim()
    if (!fn) return null
    return { kind: 'func', fn, arg, token: t }
  }
  return { kind: 'field', field: t, token: t }
}

function stripBracketsAroundFieldName(s: string): string {
  // Normalize a Tableau-ish field ref inside a token (handles qualification), e.g.:
  // - "[Sales]" -> "Sales"
  // - "[DS].[Sales]" -> "Sales"
  // - "Sales" -> "Sales"
  const raw = String(s ?? '').trim()
  if (!raw) return raw
  return cleanBracketedFieldRef0(raw) || raw
}

/**
 * Dependencies for the shared `orderByForFieldRef` factory used by both
 * `tableCalcOverClause` (pre-compile of measure calcs) and
 * `overClauseForMeasure` (MNV calc ordering). The factory resolves a
 * Tableau-style field reference — possibly qualified with datasource prefixes
 * like `[sqlproxy.xyz].reportenddate` — into an aggregate-safe ORDER BY
 * expression and direction for use inside window OVER clauses.
 */
interface OrderByForFieldRefDeps {
  /** Dims to match against first (row/col/other shelves). */
  dims: readonly TableCalcDim[]
  /** Resolve a matched dim into its order expression + direction. */
  dimOrderResolver: (dim: TableCalcDim) => { expr: string; dir: 'ASC' | 'DESC' } | null
  /** Resolve an unmatched resolved expression into order expression + direction. */
  fallbackOrderResolver: (expr: string, fieldName: string) => { expr: string; dir: 'ASC' | 'DESC' }
  /** Optional post-processor (e.g. wrap in MIN/MAX for aggregate-safe grouping). */
  postProcess?: (order: { expr: string; dir: 'ASC' | 'DESC' } | null) => { expr: string; dir: 'ASC' | 'DESC' } | null
  /** Physical column resolver (Tableau field-id → extract column). */
  resolveCol: (fieldOrId: string) => string
  /** Whether unresolved raw field refs may be treated as physical columns. */
  allowPhysicalFallback?: boolean
  /** SQL alias for the base table. */
  baseRef: string
  /** Quote a SQL identifier. */
  quoteIdent: (id: string) => string
  /** Build an expression for a raw field (e.g. from calc formulas). */
  fieldExprFor: (raw: string, baseRef: string) => string
  /** Weekly time-period resolver (optional; may return null). */
  resolvedWeeklyTimePeriodExprForField?: ((fieldNameRaw: string) => string | null) | null
  /** Predicate for nullish / unresolved SQL expressions. */
  isNullishExpr: (expr: string | null | undefined) => boolean
}

/**
 * Build an `orderByForFieldRef(rawFieldRef)` closure that resolves Tableau
 * `ordering-field` strings (potentially prefixed with `[sqlproxy.<id>]`,
 * `[DS]`, or bracketed identifiers) to a SQL order expression bound to the
 * base extract table.
 *
 * Centralizing this logic keeps the datasource-prefix stripping and
 * aggregate-safe grouping consistent between the pre-compile (MNV measure
 * calc) and the `overClauseForMeasure` (table-calc) paths.
 */
function makeOrderByForFieldRef(
  deps: OrderByForFieldRefDeps,
): (rawFieldRef: string) => { expr: string; dir: 'ASC' | 'DESC' } | null {
  return (rawFieldRef: string): { expr: string; dir: 'ASC' | 'DESC' } | null => {
    const raw = String(rawFieldRef ?? '').trim()
    if (!raw) return null
    const rawLastSegment = raw
      .split('.')
      .map((part) => String(part ?? '').trim().replace(/^\[|\]$/g, '').replace(/^"|"$/g, ''))
      .filter(Boolean)
      .pop() || ''
    const cleanedCandidate = String(cleanBracketedFieldRef0(raw) || '').trim()
    const cleaned = rawLastSegment || (cleanedCandidate && !/^null$/i.test(cleanedCandidate) ? cleanedCandidate : raw)
    const normalizedCandidate = String(normalizeTableauFieldToken(cleaned) || '').trim()
    const normalized = normalizedCandidate && !/^null$/i.test(normalizedCandidate) ? normalizedCandidate : cleaned
    const unqualified = [raw, cleaned, normalized]
      .flatMap((value) => String(value ?? '').split('.'))
      .map((part) => String(part ?? '').trim().replace(/^\[|\]$/g, '').replace(/^"|"$/g, ''))
      .filter(Boolean)
      .pop() || normalized
    const fieldKeyCandidates = new Set(
      [raw, cleaned, normalized, unqualified]
        .map((value) => normalizeTableauFieldKey(value) || String(value ?? '').trim().toLowerCase())
        .filter(Boolean)
    )
    const matchedDim = deps.dims.find((dim) => {
      const dimKey =
        normalizeTableauFieldKey(String((dim as any)?.field ?? '')) ||
        String((dim as any)?.field ?? '').trim().toLowerCase()
      return fieldKeyCandidates.has(dimKey)
    }) || null
    if (matchedDim) {
      const order = deps.dimOrderResolver(matchedDim)
      return deps.postProcess ? deps.postProcess(order) : order
    }

    const resolvedColCandidate = String(deps.resolveCol(normalized) || deps.resolveCol(unqualified) || '').trim()
    const resolvedColId = resolvedColCandidate && !/^null$/i.test(resolvedColCandidate) ? resolvedColCandidate : ''
    const physicalFallbackExpr =
      deps.allowPhysicalFallback !== false &&
      !resolvedColId &&
      unqualified &&
      !/^calculation(?:_|$)/i.test(unqualified)
        ? `${deps.baseRef}.${deps.quoteIdent(unqualified)}`
        : ''
    const resolvedExpr =
      (resolvedColId ? `${deps.baseRef}.${deps.quoteIdent(resolvedColId)}` : '') ||
      physicalFallbackExpr ||
      deps.resolvedWeeklyTimePeriodExprForField?.(normalized) ||
      deps.resolvedWeeklyTimePeriodExprForField?.(unqualified) ||
      deps.fieldExprFor(normalized, deps.baseRef) ||
      deps.fieldExprFor(unqualified, deps.baseRef)
    if (deps.isNullishExpr(resolvedExpr)) return null
    const order = deps.fallbackOrderResolver(String(resolvedExpr ?? '').trim(), unqualified)
    if (!order.expr) return null
    return deps.postProcess ? deps.postProcess(order) : order
  }
}

/**
 * Produce a post-processor that wraps bare (non-aggregate, non-grouped)
 * ORDER BY expressions in `MIN()` / `MAX()` so the expression is safe to use
 * inside a window OVER clause that sits over a GROUP BY layer. Expressions
 * already present in the grouping set or already containing an aggregate are
 * returned unchanged.
 */
function makeAggregateSafeOrderByPostProcessor(
  groupByExprs: readonly string[],
  opts?: {
    isNullishExpr?: (expr: string | null | undefined) => boolean
    isGroupedSelectAliasExpr?: (expr: string) => boolean
  },
): (order: { expr: string; dir: 'ASC' | 'DESC' } | null) => { expr: string; dir: 'ASC' | 'DESC' } | null {
  const normalizeExprForGrouping = (rawExpr: string): string =>
    String(rawExpr ?? '').replace(/\s+/g, ' ').trim().toLowerCase()
  const groupedExprSet = new Set(
    (Array.isArray(groupByExprs) ? groupByExprs : [])
      .map((expr) => normalizeExprForGrouping(String(expr ?? '')))
      .filter(Boolean)
  )
  const isNullish = opts?.isNullishExpr ?? ((v: string | null | undefined): boolean => !String(v ?? '').trim())
  const isGroupedSelectAliasExpr = opts?.isGroupedSelectAliasExpr ?? ((expr: string): boolean => {
    const s = String(expr ?? '').trim().replace(/^(?:"t"|t)\s*\.\s*/i, '').trim()
    return /^"__fg_group_key_\d+"$/i.test(s) || /^__fg_group_key_\d+$/i.test(s)
  })
  return (order) => {
    if (!order) return null
    const expr = String(order.expr ?? '').trim()
    if (!expr) return null
    if (isNullish(expr)) return null
    if (groupedExprSet.size <= 0) return order
    if (/\b(?:MIN|MAX|SUM|AVG|COUNT|ANY_VALUE)\s*\(/i.test(expr)) return order
    if (isGroupedSelectAliasExpr(expr)) {
      return {
        expr: `${order.dir === 'DESC' ? 'MAX' : 'MIN'}(${expr})`,
        dir: order.dir,
      }
    }
    const exprNorm = normalizeExprForGrouping(expr)
    if (exprNorm && groupedExprSet.has(exprNorm)) return order
    const groupedExpr = `${order.dir === 'DESC' ? 'MAX' : 'MIN'}(${expr})`
    if (isNullish(groupedExpr)) return null
    return { expr: groupedExpr, dir: order.dir }
  }
}

/**
 * Build a DuckDB SQL query from a worksheet spec to query a published extract
 */
function buildWorksheetQuerySql(
  spec: WorksheetSpecV1,
  options: WorksheetQueryOptions
): string {
  if (!(options as any)?.bindingMap) {
    throw new Error('binding map required for worksheet query')
  }
  const { extractUri, extractColumns: extractColumnsRaw = null, sourceSchema: sourceSchemaRaw = null, filters = [], parameters: rawParameters = {}, limit = 10000, densify = false, includeTotals = false, workbookIr, lazyTooltips = false, lodPreAggregate = false } = options
  const sourceSchema = (sourceSchemaRaw || (options as any)?.source_schema || null) as SourceSchema | null
  const localDatasetId = String((options as any)?.datasetId ?? (options as any)?.dataset_id ?? '').trim()
  if (localDatasetId) {
    assertNonEmptySourceSchemaForLocalExecution(sourceSchema, 'worksheet query builder')
  }
  const sourceSchemaColumns = sourceSchemaColumnNames(sourceSchema)
  const schemaBackedExtractColumns = sourceSchemaColumns.length > 0 ? sourceSchemaColumns : extractColumnsRaw
  const extractColumns = schemaBackedExtractColumns
  let resolvedWeeklyTimePeriodExprForField: ((fieldNameRaw: string) => string | null) | null = null
  const templateMode = Boolean((options as any)?.templateMode)
  const filtersArr: any[] = Array.isArray(filters) ? (filters as any[]) : []
  const filterIndexByRef = new Map<any, number>()
  for (let i = 0; i < filtersArr.length; i++) {
    const f = filtersArr[i]
    if (f && typeof f === 'object') filterIndexByRef.set(f, i)
  }
  const hideDisplayFilterFieldKeys = new Set<string>(
    filtersArr
      .map((f) => {
        if (!isHideDisplayFilter(f)) return ''
        return normalizeTableauFieldKey(String((f as any)?.field_name ?? (f as any)?.field ?? ''))
      })
      .filter(Boolean)
  )
  const skipCalcs = Boolean((options as any)?.skipCalcCompilation)
  const trustSemanticPrecomputeAliases = Boolean((options as any)?.trustSemanticPrecomputeAliases)
  // Nested window functions are invalid in DuckDB; we must de-nest at build time.
  // Allow skipping only via an explicit flag (never implicitly via fastFeatures).
  const skipNestedWindowRewrite = Boolean((options as any)?.skipNestedWindowRewrite)
  const semanticPrecompute = (options as any)?.semanticPrecompute ?? null
  const normalizePrecomputeExpr = (expr: string): string => {
    let s = String(expr ?? '')
    s = s.replace(/"t"\s*\./g, '')
    s = s.replace(/\bt\s*\./g, '')
    s = s.replace(/\s+/g, ' ').trim()
    return s
  }
  const precomputedExtrasMap = (() => {
    if (!trustSemanticPrecomputeAliases) return null
    const raw = semanticPrecompute?.precomputedExtras
    if (!raw || typeof raw !== 'object') return null
    const out = new Map<string, string>()
    for (const [expr, alias] of Object.entries(raw)) {
      const key = normalizePrecomputeExpr(expr)
      const val = String(alias ?? '').trim()
      if (!key || !val) continue
      out.set(key, val)
    }
    return out.size ? out : null
  })()
  const precomputedLodsMap = (() => {
    if (!trustSemanticPrecomputeAliases) return null
    const raw = semanticPrecompute?.precomputedLods
    if (!raw || typeof raw !== 'object') return null
    const out = new Map<string, string>()
    for (const [alias, expr] of Object.entries(raw)) {
      const key = String(alias ?? '').trim()
      const val = String(expr ?? '').trim()
      if (!key || !val) continue
      out.set(key, val)
    }
    return out.size ? out : null
  })()
  const precomputedLodAliasSet = precomputedLodsMap ? new Set<string>(Array.from(precomputedLodsMap.keys())) : null
  const precomputeOut = (options as any)?.semanticPrecomputeOut ?? null
  const precomputeExtrasOut = precomputeOut ? new Map<string, { alias: string; kind: 'win' | 'agg' | 'ord' }>() : null
  let precomputeLodsOut: Array<{ alias: string; expr: string }> = []
  const totalsOnly = Boolean((options as any)?.totalsOnly)
  const artifactExportMode = Boolean((options as any)?.artifactExportMode ?? (options as any)?.artifact_export_mode)
  const emitExecPlan = Boolean((options as any)?.emitExecPlan)
  const collectExecPlan =
    artifactExportMode ||
    emitExecPlan ||
    Boolean((options as any)?.collectExecPlan ?? (options as any)?.collect_exec_plan)
  const traceUnresolved = Boolean((options as any)?.traceUnresolved)
  const unresolvedEvents: Array<Record<string, any>> = []
  const normalizeResidualTaggedLodWindowAggregateExpr = (exprRaw: string): string => {
    const src = String(exprRaw ?? '').trim()
    if (!src || !/LOD_OUTER_AGG/i.test(src) || !/\bover\s*\(/i.test(src)) return src
    const leading = src.match(/^(?:(?:\/\*[\s\S]*?\*\/)\s*)+/)?.[0] ?? ''
    const outerAggs = Array.from(leading.matchAll(/LOD_OUTER_AGG\s+([A-Za-z_][A-Za-z0-9_]*)/ig))
      .map((m) => String(m[1] ?? '').trim().toUpperCase())
      .filter(Boolean)
    const outerAgg = outerAggs.length ? outerAggs[outerAggs.length - 1] : ''
    if (!outerAgg) return src
    const raw = src.slice(leading.length).trim()
    let overIdx = -1
    let depth = 0
    let inSq = false
    let inDq = false
    for (let i = 0; i < raw.length - 3; i++) {
      const ch = raw[i]
      if (!inDq && ch === "'" && raw[i - 1] !== '\\') { inSq = !inSq; continue }
      if (!inSq && ch === '"' && raw[i - 1] !== '\\') { inDq = !inDq; continue }
      if (inSq || inDq) continue
      if (ch === '(') { depth += 1; continue }
      if (ch === ')') { depth = Math.max(0, depth - 1); continue }
      if (depth === 0 && raw.slice(i, i + 4).toUpperCase() === 'OVER') {
        overIdx = i
        break
      }
    }
    if (overIdx < 0) return src
    const aggRaw = raw.slice(0, overIdx).trim()
    const overRaw = raw.slice(overIdx).trim()
    if (!aggRaw) return src
    const currentAggMatch = aggRaw.match(/^([A-Za-z_][A-Za-z0-9_]*)\s*\(/)
    const currentAgg = String(currentAggMatch?.[1] ?? '').trim().toUpperCase()
    const alreadyWrapped =
      (currentAgg && currentAgg === outerAgg) ||
      (outerAgg === 'COUNTD' && /^COUNT\s*\(\s*DISTINCT\b/i.test(aggRaw))
    if (alreadyWrapped) return src
    if (outerAgg === 'COUNT') return `COUNT(${aggRaw}) ${overRaw}`
    if (outerAgg === 'COUNTD') return `COUNT(DISTINCT ${aggRaw}) ${overRaw}`
    if (outerAgg === 'MIN' || outerAgg === 'MAX') return `${outerAgg}(${aggRaw}) ${overRaw}`
    if (outerAgg === 'SUM' || outerAgg === 'AVG') return `${outerAgg}(TRY_CAST(${aggRaw} AS DOUBLE)) ${overRaw}`
    return src
  }
  const normalizeResidualTaggedLodWindowAggregates = (sqlIn: string): string => {
    const src = String(sqlIn ?? '')
    if (!/LOD_OUTER_AGG/i.test(src) || !/\bover\s*\(/i.test(src)) return src
    const findMatchingParenLocal = (text: string, start: number): number => {
      let depth = 0
      let inSq = false
      let inDq = false
      for (let i = start; i < text.length; i++) {
        const ch = text[i]
        if (!inDq && ch === "'" && text[i - 1] !== '\\') { inSq = !inSq; continue }
        if (!inSq && ch === '"' && text[i - 1] !== '\\') { inDq = !inDq; continue }
        if (inSq || inDq) continue
        if (ch === '(') { depth += 1; continue }
        if (ch === ')') {
          depth -= 1
          if (depth === 0) return i
        }
      }
      return -1
    }
    const consumeLeadingCommentsLocal = (text: string, start: number): { bodyStart: number; outerAggs: string[] } => {
      let pos = Math.max(0, start)
      const outerAggs: string[] = []
      while (pos < text.length) {
        while (pos < text.length && /\s/.test(text[pos])) pos++
        if (text.slice(pos, pos + 2) !== '/*') break
        const end = text.indexOf('*/', pos + 2)
        if (end < 0) break
        const comment = text.slice(pos + 2, end)
        const outerAggMatch = comment.match(/LOD_OUTER_AGG\s+([A-Za-z_][A-Za-z0-9_]*)/i)
        if (outerAggMatch?.[1]) outerAggs.push(String(outerAggMatch[1]).trim().toUpperCase())
        pos = end + 2
      }
      while (pos < text.length && /\s/.test(text[pos])) pos++
      return { bodyStart: pos, outerAggs }
    }
    const findResidualWindowEnd = (text: string, bodyStart: number): number => {
      let depth = 0
      let inSq = false
      let inDq = false
      for (let i = bodyStart; i < text.length - 3; i++) {
        const ch = text[i]
        if (!inDq && ch === "'" && text[i - 1] !== '\\') { inSq = !inSq; continue }
        if (!inSq && ch === '"' && text[i - 1] !== '\\') { inDq = !inDq; continue }
        if (inSq || inDq) continue
        if (ch === '(') { depth += 1; continue }
        if (ch === ')') {
          if (depth === 0) return -1
          depth = Math.max(0, depth - 1)
          continue
        }
        if (depth === 0 && text.slice(i, i + 4).toUpperCase() === 'OVER') {
          let overPos = i + 4
          while (overPos < text.length && /\s/.test(text[overPos])) overPos++
          if (text[overPos] !== '(') return -1
          return findMatchingParenLocal(text, overPos)
        }
      }
      return -1
    }
    let out = src
    let idx = 0
    while (idx < out.length) {
      const start = out.indexOf('/*', idx)
      if (start < 0) break
      const end = out.indexOf('*/', start + 2)
      if (end < 0) break
      const comment = out.slice(start + 2, end)
      if (!/LOD_OUTER_AGG/i.test(comment)) {
        idx = end + 2
        continue
      }
      const { bodyStart, outerAggs } = consumeLeadingCommentsLocal(out, start)
      if (outerAggs.length === 0) {
        idx = end + 2
        continue
      }
      const exprEnd = findResidualWindowEnd(out, bodyStart)
      if (exprEnd < 0) {
        idx = end + 2
        continue
      }
      const expr = out.slice(start, exprEnd + 1).trim()
      const replacement = normalizeResidualTaggedLodWindowAggregateExpr(expr)
      if (replacement && replacement !== expr) {
        out = `${out.slice(0, start)}${replacement}${out.slice(exprEnd + 1)}`
        idx = start + replacement.length
        continue
      }
      idx = exprEnd + 1
    }
    return out
  }
  const noteUnresolved = (field: string, reason: string, extra?: Record<string, any>) => {
    if (!field) return
    unresolvedEvents.push({
      field,
      reason,
      ...(extra || {}),
    })
  }
  const buildStartedAt = (typeof performance !== 'undefined' && typeof performance.now === 'function')
    ? performance.now()
    : Date.now()
  const _phaseTimings = new Map<string, number>()
  let _lastPhaseTs = buildStartedAt
  const _markPhase = (name: string) => {
    const now = (typeof performance !== 'undefined' && typeof performance.now === 'function') ? performance.now() : Date.now()
    const elapsed = Math.round(now - _lastPhaseTs)
    _phaseTimings.set(name, (_phaseTimings.get(name) ?? 0) + elapsed)
    _lastPhaseTs = now
  }
  const _emitTimings = () => {
    const totalMs = Math.round(((typeof performance !== 'undefined' && typeof performance.now === 'function') ? performance.now() : Date.now()) - buildStartedAt)
    const entries: Record<string, number> = { totalMs }
    for (const [k, v] of _phaseTimings) entries[k] = v
    try { console.log('[FG-BUILD-TIMING]', JSON.stringify(entries)) } catch {}
  }
  const builderTimeoutMs = Number((options as any)?.builderTimeoutMs ?? 0)
  const guard = (phase: string) => {
    if (!builderTimeoutMs || builderTimeoutMs <= 0) return
    const nowMs = (typeof performance !== 'undefined' && typeof performance.now === 'function')
      ? performance.now()
      : Date.now()
    const elapsedMs = Math.max(0, Math.round(nowMs - buildStartedAt))
    if (elapsedMs > builderTimeoutMs) {
      throw new Error(`builder timeout at ${phase} after ${elapsedMs}ms`)
    }
  }
  const debugFieldMapping = isFieldMappingDebugEnabled()
  const debugFieldMappingVerbose = debugFieldMapping && isFieldMappingVerboseEnabled()
  const worksheetNameDebug = String(options.worksheetName ?? '').trim() || null
  const worksheetDebugCfg = resolveWorksheetDebug(options.worksheetName)
  const worksheetDebugEnabled = worksheetDebugCfg.enabled && worksheetDebugCfg.matches
  const debugFilterLowering = Boolean((options as any)?.debugFilters || debugFieldMapping)
  const filterLoweringDebugRows: any[] = []
  const filterValuePreview = (value: any): any => {
    if (Array.isArray(value)) return value.slice(0, 8)
    if (value && typeof value === 'object') {
      try {
        return JSON.parse(JSON.stringify(value))
      } catch {
        return String(value)
      }
    }
    return value ?? null
  }
  const pushFilterLoweringDebug = (filter: any, patch: Record<string, any>): void => {
    if (!debugFilterLowering || filterLoweringDebugRows.length >= 80) return
    const rawField = String((filter as any)?.field_name ?? (filter as any)?.field ?? '').trim()
    const value = (filter as any)?.value
    filterLoweringDebugRows.push({
      field: rawField || null,
      operator: String((filter as any)?.operator ?? '').trim().toUpperCase() || null,
      kind: String((filter as any)?.kind ?? '').trim().toLowerCase() || null,
      filterType: String((filter as any)?.filter_type ?? '').trim().toLowerCase() || null,
      valueCount: Array.isArray(value) ? value.length : (value === null || typeof value === 'undefined' ? 0 : 1),
      valuePreview: filterValuePreview(value),
      filterIndex: filterIndexByRef.get(filter) ?? null,
      ...patch,
    })
  }
  const debugLog = (...args: any[]) => {
    if (!worksheetDebugEnabled) return
    try { console.log('[FG-TABLE-CALC]', ...args) } catch { }
  }
  // Avoid unconditional logging in this hot path (can be very slow in desktop WebViews).
  if (worksheetDebugEnabled || debugFieldMappingVerbose || traceUnresolved) {
    try { console.log('[FG-DEBUG] buildWorksheetQuery', options.worksheetName) } catch { }
  }

  // --- Parameters: parse declared datatype from WorkbookIR (root fix) ---
  //
  // Tableau parameters have a declared type in TWB XML:
  // - `<parameter datatype="date" data-type="datetime" ...>`
  // - and/or a `<column datasource="Parameters" datatype="date" ...>`
  //
  // We already parse these into `workbookIr.parameters[*]` and `workbookIr.field_dictionary.columns[*]`
  // on the Rust side. This helper wires that typed metadata into:
  // - calc compilation parameter defs (`VisualSpec.data_pane.parameters`)
  // - final SQL parameter substitution (`[Param]` -> typed literal/cast)
  const normalizeParamKey = (raw: any): string => {
    const s = String(raw ?? '').trim()
    if (!s) return ''
    const k = normalizeTableauFieldKeyStrict(s)
    return k || s.toLowerCase()
  }
  const classifyParamType = (raw: any): 'date' | 'timestamp' | 'number' | 'string' | 'bool' | 'unknown' => {
    const s0 = String(raw ?? '').trim().toLowerCase()
    if (!s0) return 'unknown'
    const s = s0.replace(/[_-]+/g, ' ')
    if (s.includes('datetime') || s.includes('timestamp') || s.includes('date time') || s.includes('date_time') || (s.includes('date') && s.includes('time'))) {
      return 'timestamp'
    }
    if (s.includes('date')) return 'date'
    if (s.includes('bool') || s.includes('boolean')) return 'bool'
    if (s.includes('int') || s.includes('integer') || s.includes('real') || s.includes('double') || s.includes('decimal') || s.includes('numeric') || s.includes('float') || s.includes('number')) {
      return 'number'
    }
    if (s.includes('string') || s.includes('str') || s.includes('text')) return 'string'
    return 'unknown'
  }
  const isWorkbookParamColumn = (c: any): boolean => {
    return (
      isWorkbookParameterFieldEntry(c, workbookIr) ||
      Boolean((c as any)?.default_value_field)
    )
  }
  const buildParamTypeByKeyFromWorkbookIr = (fdCols: any[]): Map<string, any> => {
    const out = new Map<string, any>()

    // 1) Prefer explicit `<parameter ...>` declarations (workbookIr.parameters).
    try {
      const ps: any[] = Array.isArray((workbookIr as any)?.parameters) ? ((workbookIr as any).parameters as any[]) : []
      for (const p of ps) {
        const cand = [
          (p as any)?.datatype,
          (p as any)?.data_type,
          (p as any)?.param_type,
          (p as any)?.attrs?.datatype,
          (p as any)?.attrs?.['data-type'],
          (p as any)?.attrs?.['param-type'],
          (p as any)?.attrs?.type,
        ].filter(Boolean).join(' ')
        const t = classifyParamType(cand)
        if (t === 'unknown') continue
        const names = [
          String((p as any)?.name ?? '').trim(),
          String((p as any)?.caption ?? '').trim(),
          String((p as any)?.parameter_name ?? '').trim(),
        ]
        for (const nm of names) {
          const cleaned = stripBracketsAroundFieldName(nm) || nm
          const k = normalizeParamKey(cleaned)
          if (!k || out.has(k)) continue
          out.set(k, t)
        }
      }
    } catch { }

    // 2) Supplement from the Parameters datasource columns (field dictionary).
    const dtFromCol = (c: any): string => {
      const cand: string[] = [
        String((c as any)?.datatype ?? ''),
        String((c as any)?.data_type ?? ''),
        String((c as any)?.user_datatype ?? ''),
        String((c as any)?.['r#type'] ?? (c as any)?.type ?? ''),
        String((c as any)?.default_type ?? ''),
        String((c as any)?.attrs?.datatype ?? ''),
        String((c as any)?.attrs?.['data-type'] ?? ''),
        String((c as any)?.attrs?.['r#type'] ?? (c as any)?.attrs?.type ?? ''),
      ]
        .map((x) => String(x || '').trim())
        .filter(Boolean)
      return cand.join(' ')
    }
    for (const c of (Array.isArray(fdCols) ? fdCols : [])) {
      if (!isWorkbookParamColumn(c)) continue
      const t = classifyParamType(dtFromCol(c))
      if (t === 'unknown') continue
      const names = [
        cleanBracketedFieldRef0(String((c as any)?.name ?? '').trim()),
        String((c as any)?.name ?? '').trim(),
        String((c as any)?.caption ?? '').trim(),
      ]
      for (const nm of names) {
        const cleaned = stripBracketsAroundFieldName(nm) || String(nm ?? '').trim()
        const k = normalizeParamKey(cleaned)
        if (!k || out.has(k)) continue
        out.set(k, t)
      }
    }

    return out
  }
  const fd = (workbookIr && typeof workbookIr === 'object') ? ((workbookIr as any).field_dictionary || null) : null
  const paramFdCols: any[] = Array.isArray(fd?.columns) ? fd.columns : []
  const paramTypeByKeyForSubst: Map<string, any> = (() => {
    try {
      const m = buildParamTypeByKeyFromWorkbookIr(paramFdCols)
      if (worksheetDebugEnabled) {
        const sample = Object.keys(rawParameters || {}).slice(0, 32).map((k) => ({
          name: String(k),
          key: normalizeParamKey(k),
          type: m.get(normalizeParamKey(k)) || 'unknown',
        }))
        debugLog('param types', sample)
      }
      return m
    } catch {
      return new Map()
    }
  })()
  const inferEffectiveParamType = (
    key: string,
    explicitType?: any,
    value?: any,
  ): 'date' | 'timestamp' | 'number' | 'string' | 'bool' | 'unknown' => {
    const rawType = explicitType ?? paramTypeByKeyForSubst.get(normalizeParamKey(key)) ?? ''
    const t0 = String(rawType).trim().toLowerCase()
    if (t0 === 'date' || t0 === 'timestamp' || t0 === 'number' || t0 === 'bool' || t0 === 'string') {
      return t0
    }
    if (value instanceof Date && Number.isFinite(value.getTime())) return 'timestamp'
    if (typeof value === 'boolean') return 'bool'
    if (typeof value === 'number' && Number.isFinite(value)) {
      if (Math.abs(value) >= 1e12) return 'timestamp'
      return 'number'
    }
    const keyHint = String(key ?? '').trim().toLowerCase()
    if (typeof value === 'string') {
      const trimmed = value.trim()
      const unwrapped = (
        trimmed.startsWith('#') &&
        trimmed.endsWith('#') &&
        trimmed.length >= 2
      )
        ? trimmed.slice(1, -1).trim()
        : trimmed
      if (/^-?\d+$/.test(unwrapped)) {
        const numeric = Number(unwrapped)
        if (Number.isFinite(numeric)) {
          if (Math.abs(numeric) >= 1e12) return 'timestamp'
          if (keyHint.includes('date') || keyHint.includes('time') || keyHint.includes('timestamp')) {
            return 'timestamp'
          }
          return 'number'
        }
      }
      if (/^Timestamp\(\s*[A-Za-z]+\s*,\s*-?\d+\s*\)$/.test(unwrapped)) return 'timestamp'
      if (/^\d{4}-\d{2}-\d{2}$/.test(unwrapped)) {
        return keyHint.includes('time') || keyHint.includes('timestamp') ? 'timestamp' : 'date'
      }
      if (/^\d{4}-\d{2}-\d{2}(?:[ T]\d{2}:\d{2}:\d{2}(?:\.\d+)?)?$/.test(unwrapped)) return 'timestamp'
    }
    if (keyHint.includes('date')) return 'date'
    if (keyHint.includes('time') || keyHint.includes('timestamp')) return 'timestamp'
    return 'unknown'
  }
  const buildParamTemplatePlaceholder = (key: string, explicitType?: any, value?: any): string => {
    const safeKey = buildTemplateParamSafeKey(String(key ?? ''))
    const t = inferEffectiveParamType(key, explicitType, value)
    return renderTypedParamTemplatePlaceholder(safeKey, t)
  }
  const renderTypedParamTemplatePlaceholder = (safeKeyRaw: string, typeRaw: any): string => {
    const safeKey = String(safeKeyRaw || '').trim()
    const t = String(typeRaw || 'unknown').trim().toLowerCase()
    const raw = `/*__FG_PARAM:${safeKey}:${t}__*/ NULL`
    if (t === 'date') return `CAST(${raw} AS DATE)`
    if (t === 'timestamp' || /^timestamp_/.test(t)) return `CAST(${raw} AS TIMESTAMP)`
    if (t === 'number') return `CAST(${raw} AS DOUBLE)`
    if (t === 'bool' || t === 'boolean') return `CAST(${raw} AS BOOLEAN)`
    if (t === 'string') return `CAST(${raw} AS VARCHAR)`
    return raw
  }
  const parameters: Record<string, any> = (() => {
    const rawEntries = Object.entries((rawParameters && typeof rawParameters === 'object') ? rawParameters : {})
    const groups = new Map<string, { aliases: Set<string>; value: any }>()
    const aliasToCanonical = new Map<string, string>()

    const addAlias = (group: { aliases: Set<string>; value: any }, raw: any) => {
      const s0 = String(raw ?? '').trim()
      if (!s0) return
      const s = stripBracketsAroundFieldName(s0) || s0
      if (!s) return
      group.aliases.add(s)
    }

    const registerWorkbookParam = (aliasesRaw: any[], valueRaw: any) => {
      const aliasNorms = aliasesRaw
        .map((x) => normalizeParamKey(stripBracketsAroundFieldName(String(x ?? '').trim()) || String(x ?? '').trim()))
        .filter(Boolean)
      if (!aliasNorms.length) return
      let canonical = aliasNorms[0]
      for (const nk of aliasNorms) {
        const mapped = aliasToCanonical.get(nk)
        if (mapped) {
          canonical = mapped
          break
        }
      }
      let group = groups.get(canonical)
      if (!group) {
        group = { aliases: new Set<string>(), value: undefined }
        groups.set(canonical, group)
      }
      for (const a of aliasesRaw) addAlias(group, a)
      for (const nk of aliasNorms) aliasToCanonical.set(nk, canonical)
      if (group.value === undefined && valueRaw !== undefined) {
        group.value = valueRaw
      }
    }

    for (const [rawKey, value] of rawEntries) {
      const normalized = normalizeParamKey(rawKey)
      const canonical = aliasToCanonical.get(normalized) || normalized
      let group = groups.get(canonical)
      if (!group) {
        group = { aliases: new Set<string>(), value: undefined }
        groups.set(canonical, group)
      }
      addAlias(group, rawKey)
      if (normalized && !aliasToCanonical.has(normalized)) aliasToCanonical.set(normalized, canonical)
      if (value !== undefined) group.value = value
    }

    try {
      const wbParams: any[] = Array.isArray((workbookIr as any)?.parameters) ? ((workbookIr as any).parameters as any[]) : []
      for (const p of wbParams) {
        registerWorkbookParam(
          [
            String((p as any)?.name ?? '').trim(),
            String((p as any)?.caption ?? '').trim(),
            String((p as any)?.parameter_name ?? '').trim(),
          ],
          (p as any)?.current_value ?? (p as any)?.value ?? (p as any)?.default_value ?? undefined,
        )
      }
      // Root source is runtime `options.parameters`; workbook IR only backfills
      // aliases or missing values when upstream artifacts omitted explicit params.
      if (wbParams.length === 0) {
        for (const c of paramFdCols) {
          if (!isWorkbookParamColumn(c)) continue
          registerWorkbookParam(
            [
              cleanBracketedFieldRef0(String((c as any)?.name ?? '').trim()),
              String((c as any)?.name ?? '').trim(),
              String((c as any)?.caption ?? '').trim(),
            ],
            (c as any)?.value ?? (c as any)?.current_value ?? (c as any)?.default_value ?? undefined,
          )
        }
      }
    } catch { }

    const out: Record<string, any> = {}
    for (const [, group] of groups) {
      if (group.value === undefined) continue
      for (const alias of group.aliases) out[alias] = group.value
    }
    for (const [rawKey, value] of rawEntries) {
      const s = stripBracketsAroundFieldName(String(rawKey ?? '').trim()) || String(rawKey ?? '').trim()
      if (!s) continue
      if (!(s in out)) out[s] = value
    }
    return out
  })()

  // Optional: emit a multi-statement execution plan (DuckDB session-local temp tables).
  // We side-effect the plan onto the `options` object so callers can opt-in without
  // changing the return type of `buildWorksheetQuery`.
  const noteExecPlan = (plan: {
    statements: string[]
    finalSql: string
    cleanup?: string[]
    materializedTable?: string | null
    materializedSqlHash?: string | null
	    materializedSql?: string | null
	    aggBaseWrapperSourceTable?: string | null
	    interactionCubeBuildSql?: string | null
	    interactionCubeSlicerColumns?: string[] | null
	    interactionCubeAxisFields?: Array<Record<string, any>> | null
	    interactionCubeMeasureComponents?: Array<Record<string, any>> | null
	  }) => {
    if (!collectExecPlan) return
    try {
      const normalized: any = { ...plan }
      if (typeof normalized.materializedSql === 'string' && normalized.materializedSql) {
        normalized.materializedSql = stripRedundantAnyValueAroundGroupKeys(normalized.materializedSql)
      }
      if (typeof normalized.aggBaseMaterializedSql === 'string' && normalized.aggBaseMaterializedSql) {
        normalized.aggBaseMaterializedSql = stripRedundantAnyValueAroundGroupKeys(normalized.aggBaseMaterializedSql)
      }
      if (typeof normalized.aggBaseWrapperSql === 'string' && normalized.aggBaseWrapperSql) {
        normalized.aggBaseWrapperSql = stripRedundantAnyValueAroundGroupKeys(normalized.aggBaseWrapperSql)
      }
      if (typeof normalized.windowInputSql === 'string' && normalized.windowInputSql) {
        normalized.windowInputSql = stripRedundantAnyValueAroundGroupKeys(normalized.windowInputSql)
      }
      if (typeof normalized.interactionCubeBuildSql === 'string' && normalized.interactionCubeBuildSql) {
        normalized.interactionCubeBuildSql = stripRedundantAnyValueAroundGroupKeys(normalized.interactionCubeBuildSql)
      }
      if (Array.isArray(normalized.statements)) {
        normalized.statements = normalized.statements.map((stmt: any) =>
          typeof stmt === 'string' ? stripRedundantAnyValueAroundGroupKeys(stmt) : stmt,
        )
      }
      if (typeof normalized.finalSql === 'string' && normalized.finalSql) {
        normalized.finalSql = stripRedundantAnyValueAroundGroupKeys(normalized.finalSql)
      }
      ;(options as any).__fg_duckdb_exec_plan = normalized
    } catch {
      try { (options as any).__fg_duckdb_exec_plan = plan } catch { }
    }
  }

  const quoteIdent = (id: string): string => {
    const cleaned = cleanBracketedFieldRef0(String(id ?? '').trim()) || String(id ?? '').trim()
    const noColon = cleaned.startsWith(':') ? cleaned.slice(1).trim() : cleaned
    return `"${noColon.replace(/"/g, '""')}"`
  }
  const quoteDisplayAlias = (id: string): string => {
    const raw = String(id ?? '').trim()
    const noColon = raw.startsWith(':') ? raw.slice(1).trim() : raw
    return `"${noColon.replace(/"/g, '""')}"`
  }
  const tableauDateGrainAliasSuffix = (grainRaw: any): string => {
    const grain = String(grainRaw ?? '').trim().toLowerCase()
    if (grain === 'year') return 'Year'
    if (grain === 'quarter') return 'Quarter'
    if (grain === 'month') return 'Month'
    if (grain === 'week') return 'Week'
    if (grain === 'day') return 'Day'
    if (grain === 'hour') return 'Hour'
    if (grain === 'minute') return 'Minute'
    if (grain === 'second') return 'Second'
    return ''
  }
  const dimensionProjectionAlias = (fieldRaw: any, grainRaw?: any): string => {
    const field = cleanBracketedFieldRef0(String(fieldRaw ?? '').trim()) || String(fieldRaw ?? '').trim()
    const suffix = tableauDateGrainAliasSuffix(grainRaw)
    if (!field || !suffix) return field
    if (new RegExp(`\\(${suffix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\)$`, 'i').test(field)) return field
    return `${field} (${suffix})`
  }
  const normalizeOrderExpr = (expr: string): string => normalizePrecomputeExpr(expr)
  const recordPrecomputeOrderExpr = (exprRaw: string): void => {
    if (!precomputeExtrasOut) return
    const expr = normalizeOrderExpr(exprRaw)
    if (!expr || precomputeExtrasOut.has(expr)) return
    const alias = `__fg_ord_${Math.abs(hashString(expr)).toString(36)}`
    precomputeExtrasOut.set(expr, { alias, kind: 'ord' })
  }
  const resolvePrecomputedOrderExpr = (exprRaw: string, orderExtraAliases?: Set<string>): string => {
    const expr = normalizeOrderExpr(exprRaw)
    if (!expr) return expr
    const precomputed = precomputedExtrasMap?.get(expr)
    if (precomputed) {
      const alias = String((precomputed as any)?.alias ?? '').trim()
      if (!alias) return expr
      if (orderExtraAliases) orderExtraAliases.add(alias)
      return quoteIdent(alias)
    }
    return expr
  }
  const pushOrderTerm = (
    target: string[],
    exprRaw: string,
    dir: 'ASC' | 'DESC',
    nulls: string = 'NULLS LAST',
    orderExtraAliases?: Set<string>,
    opts?: { allowPrecompute?: boolean },
  ): void => {
    const expr = normalizeOrderExpr(exprRaw)
    if (!expr) return
    if (isNullishExpr(expr)) return
    if (isScalarLiteralExpr(expr)) return
    // Constants do not affect sort order, and integer constants are parsed by DuckDB
    // as ordinal column references. Keep explicit ordinal fallback sorts out of this
    // helper so calculated-field constants like `500000` cannot break dashboard SQL.
    if (isSqlConstantLiteralExpression(expr)) return
    recordPrecomputeOrderExpr(expr)
    const applied =
      (opts?.allowPrecompute === false || !precomputedExtrasMap)
        ? expr
        : resolvePrecomputedOrderExpr(expr, orderExtraAliases)
    target.push(`${applied} ${dir} ${nulls}`.replace(/\s+/g, ' ').trim())
  }
  const escapeRegExp = (v: string): string => String(v ?? '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

  // Final-pass SQL sanitizer: collapse compiler artifacts like MAX(MAX(NULL)) OVER (...) → NULL.
  // This prevents invalid nested window expressions when unresolved Tableau fields compile to NULL.
  const sanitizeNullArtifacts = (sqlIn: string): string => {
    let sql = String(sqlIn ?? '')
    try {
      const findMatchingParen = (src: string, openIdx: number): number => {
        if (openIdx < 0 || src[openIdx] !== '(') return -1
        let depth = 0
        let inStr = false
        let inIdent = false
        for (let i = openIdx; i < src.length; i += 1) {
          const ch = src[i]
          if (inStr) {
            if (ch === "'" && src[i + 1] === "'") { i += 1; continue }
            if (ch === "'") inStr = false
            continue
          }
          if (inIdent) {
            if (ch === '"' && src[i + 1] === '"') { i += 1; continue }
            if (ch === '"') inIdent = false
            continue
          }
          if (ch === "'") { inStr = true; continue }
          if (ch === '"') { inIdent = true; continue }
          if (ch === '(') depth += 1
          else if (ch === ')') {
            depth -= 1
            if (depth === 0) return i
          }
        }
        return -1
      }
      const stripOuterParens = (rawIn: string): string => {
        let raw = String(rawIn ?? '').trim()
        while (raw.startsWith('(') && raw.endsWith(')')) {
          const end = findMatchingParen(raw, 0)
          if (end !== raw.length - 1) break
          raw = raw.slice(1, -1).trim()
        }
        return raw
      }
      const collapseDuplicateAnyValueWrappers = (rawIn: string): string => {
        let input = String(rawIn ?? '')
        let changed = true
        while (changed) {
          changed = false
          let out = ''
          let cursor = 0
          while (cursor < input.length) {
            const match = /\bANY_VALUE\s*\(/ig.exec(input.slice(cursor))
            if (!match || match.index == null) {
              out += input.slice(cursor)
              break
            }
            const fnStart = cursor + match.index
            const openIdx = input.indexOf('(', fnStart)
            if (openIdx < 0) {
              out += input.slice(cursor)
              break
            }
            const closeIdx = findMatchingParen(input, openIdx)
            if (closeIdx < 0) {
              out += input.slice(cursor)
              break
            }
            out += input.slice(cursor, fnStart)
            const inner = input.slice(openIdx + 1, closeIdx)
            const strippedInner = stripOuterParens(inner)
            if (/^ANY_VALUE\s*\(/i.test(strippedInner)) {
              out += strippedInner
              changed = true
            } else {
              out += input.slice(fnStart, closeIdx + 1)
            }
            cursor = closeIdx + 1
          }
          input = out
        }
        return input
      }
      const nullAtom = String.raw`(?:\(+\s*)?NULL(?:\s*\)+)?`
      const step = (s: string): string => {
        let out = s
        out = collapseDuplicateAnyValueWrappers(out)
        // Strong catch-all: collapse nested aggregates-of-NULL inside a window, regardless of parentheses depth.
        // Example: MAX((MAX(NULL))) OVER (...) → NULL
        out = out.replace(/\b(MAX|MIN|AVG|SUM|COUNT)\s*\(\s*\(*\s*(MAX|MIN|AVG|SUM|COUNT)\s*\(\s*\(*\s*NULL\s*\)*\s*\)\s*\)*\s*\)\s*OVER\s*\([^)]*\)/gi, 'NULL')
        out = out.replace(
          new RegExp(String.raw`\\b(MAX|MIN|AVG|SUM|COUNT)\\s*\\(\\s*(?:\\(+\\s*)?(MAX|MIN|AVG|SUM|COUNT)\\s*\\(\\s*${nullAtom}\\s*\\)(?:\\s*\\)+)?\\s*\\)\\s*OVER\\s*\\([^)]*\\)`, 'gi'),
          'NULL'
        )
        out = out.replace(new RegExp(String.raw`\\b(MAX|MIN|AVG|SUM|COUNT)\\s*\\(\\s*${nullAtom}\\s*\\)\\s*OVER\\s*\\([^)]*\\)`, 'gi'), 'NULL')
        out = out.replace(
          new RegExp(String.raw`\\b(MAX|MIN|AVG|SUM|COUNT)\\s*\\(\\s*TRY_CAST\\s*\\(\\s*${nullAtom}[^)]*\\)\\s*\\)\\s*OVER\\s*\\([^)]*\\)`, 'gi'),
          'NULL'
        )
        out = out.replace(new RegExp(String.raw`\\b(MAX|MIN|AVG|SUM|COUNT)\\s*\\(\\s*(?:\\(+\\s*)?(MAX|MIN|AVG|SUM|COUNT)\\s*\\(\\s*${nullAtom}\\s*\\)(?:\\s*\\)+)?\\s*\\)\\s*`, 'gi'), 'NULL')
        out = out.replace(new RegExp(String.raw`\\b(MAX|MIN|AVG|SUM|COUNT)\\s*\\(\\s*${nullAtom}\\s*\\)\\s*`, 'gi'), 'NULL')
        out = out.replace(/\bSUM\s*\(\s*TRY_CAST\s*\(\s*NULL[^)]*\)\s*\)\s*/gi, 'NULL')
        return out
      }
      for (let i = 0; i < 8; i++) {
        const prev = sql
        sql = step(sql)
        if (sql === prev) break
      }
    } catch { }
    try {
      // Strip Tableau date literal markers that can sneak through calc compilation.
      // Example: '#2021-05-03#' -> '2021-05-03'
      sql = sql.replace(/'#(\d{4}-\d{2}-\d{2}(?:[ T][^'#]+)?)#'/g, "'$1'")
      sql = sql.replace(/#(\d{4}-\d{2}-\d{2}(?:[ T][^#\s]+)?)#/g, "'$1'")
    } catch { }
    return sql
  }

  // Replace any leaked unresolved-field column refs with NULL so DuckDB won't bind them.
  const scrubUnresolvedFieldRefs = (sqlIn: string): string => {
    let sql = String(sqlIn ?? '')
    if (!sql) return sql
    try {
      const unresolved = escapeRegExp(UNRESOLVED_FIELD)
      // Match optional alias (quoted or unquoted) + ".<unresolved>".
      sql = sql.replace(new RegExp(String.raw`(?:(?:"[^"]+"|\b\w+\b)\s*\.\s*)?"${unresolved}"`, 'g'), 'NULL')
      sql = sql.replace(new RegExp(String.raw`(?:(?:"[^"]+"|\b\w+\b)\s*\.\s*)?${unresolved}\b`, 'g'), 'NULL')
      // Window/LOD partition derivation can materialize unresolved null placeholders
      // as alias-qualified `"NULL"` column refs. Those are not source columns.
      sql = sql.replace(/(?:(?:"[^"]+"|\b\w+\b)\s*\.\s*)"NULL"/g, 'NULL')
    } catch { }
    return sql
  }

  // Lift LOD window expressions into a base subquery when grouping, so DuckDB
  // doesn't reject window refs that aren't grouped/aggregated.
  const rewriteLodWindowsForGroupedQuery = (sqlIn: string): string => {
    const sql = String(sqlIn ?? '')
    try {
      const lodRewriteDebug = (() => {
        try {
          const search = String((globalThis as any)?.location?.search ?? '')
          return /(?:\?|&)fg_debug_exec_plan=(?:1|true|yes|on)(?:&|$)/i.test(search)
        } catch {
          return false
        }
      })()
      if (!/\bgroup\s+by\b/i.test(sql)) return sql
      if (!/\/\*\s*LOD\b/i.test(sql) || !/\bover\s*\(/i.test(sql)) return sql
      const hasReadParquet = /from\s+read_parquet\s*\(/i.test(sql)
      const hasSubquery = /from\s+\(/i.test(sql)
      if (!hasReadParquet && !hasSubquery) return sql

      const normColKey = (raw: string): string => {
        const s = String(raw ?? '').trim()
        if (!s) return ''
        const cleaned = cleanBracketedFieldRef0(s) || s
        return cleaned.trim().toLowerCase()
      }
      const extractColsByKey = (() => {
        const map = new Map<string, string>()
        if (Array.isArray(extractColumns)) {
          for (const col of extractColumns) {
            const key = normColKey(col)
            if (key && !map.has(key)) map.set(key, String(col))
          }
        }
        return map
      })()

      const collectBaseCols = (s: string): string[] => {
        const out = new Set<string>()
        const reQuoted = /(?:(?:"t")|\bt)\s*\.\s*"([^"]+)"/g
        const reBare = /(?:(?:"t")|\bt)\s*\.\s*([A-Za-z_][A-Za-z0-9_]*)/g
        let m: RegExpExecArray | null
        while ((m = reQuoted.exec(s))) {
          const col = String(m[1] ?? '').trim()
          if (col) out.add(col)
        }
        while ((m = reBare.exec(s))) {
          const col = String(m[1] ?? '').trim()
          if (col) out.add(col)
        }
        return Array.from(out)
      }

      const findMatchingParen = (s: string, start: number): number => {
        let depth = 0
        let inStr = false
        for (let i = start; i < s.length; i++) {
          const ch = s[i]
          if (ch === "'") {
            if (inStr && s[i + 1] === "'") {
              i++
              continue
            }
            inStr = !inStr
            continue
          }
          if (inStr) continue
          if (ch === '(') depth += 1
          else if (ch === ')') {
            depth -= 1
            if (depth === 0) return i
          }
        }
        return -1
      }

      const consumeLeadingComments = (s: string, start: number): { bodyStart: number; outerAggTagged: boolean } => {
        let pos = Math.max(0, start)
        let outerAggTagged = false
        while (pos < s.length) {
          while (pos < s.length && /\s/.test(s[pos])) pos++
          if (s.slice(pos, pos + 2) !== '/*') break
          const end = s.indexOf('*/', pos + 2)
          if (end < 0) break
          const comment = s.slice(pos + 2, end)
          if (/LOD_OUTER_AGG/i.test(comment)) outerAggTagged = true
          pos = end + 2
        }
        while (pos < s.length && /\s/.test(s[pos])) pos++
        return { bodyStart: pos, outerAggTagged }
      }

      const findDirectLodWindowBounds = (s: string, bodyStart: number): { overKeywordStart: number; overEnd: number } | null => {
        if (bodyStart < 0 || bodyStart >= s.length) return null
        const isWordChar = (ch: string | undefined): boolean => /[A-Za-z0-9_]/.test(String(ch ?? ''))
        const hasTokenAt = (idx: number, token: string): boolean => {
          const upperToken = token.toUpperCase()
          if (s.slice(idx, idx + upperToken.length).toUpperCase() !== upperToken) return false
          return !isWordChar(s[idx - 1]) && !isWordChar(s[idx + upperToken.length])
        }
        let depth = 0
        let inSq = false
        let inDq = false
        for (let i = bodyStart; i < s.length - 3; i++) {
          const ch = s[i]
          if (!inDq && ch === "'" && s[i - 1] !== '\\') { inSq = !inSq; continue }
          if (!inSq && ch === '"' && s[i - 1] !== '\\') { inDq = !inDq; continue }
          if (inSq || inDq) continue
          if (ch === '(') { depth += 1; continue }
          if (ch === ')') {
            if (depth === 0) return null
            depth = Math.max(0, depth - 1)
            continue
          }
          if (depth === 0) {
            if (ch === ','
              || ch === ';'
              || hasTokenAt(i, 'AS')
              || hasTokenAt(i, 'WHEN')
              || hasTokenAt(i, 'ELSE')) {
              return null
            }
            if (hasTokenAt(i, 'OVER')) {
              const after = i + 4
              if (after < s.length && !/\s|\(/.test(s[after])) continue
              let overPos = after
              while (overPos < s.length && /\s/.test(s[overPos])) overPos++
              if (s[overPos] !== '(') return null
              const overEnd = findMatchingParen(s, overPos)
              if (overEnd < 0) return null
              return { overKeywordStart: i, overEnd }
            }
          }
        }
        return null
      }

      const extractLodWindowExprs = (s: string): string[] => {
        const out: string[] = []
        const seen = new Set<string>()
        let idx = 0
        while (idx < s.length) {
          const start = s.indexOf('/*', idx)
          if (start < 0) break
          const end = s.indexOf('*/', start + 2)
          if (end < 0) break
          const comment = s.slice(start + 2, end)
          if (!/LOD/i.test(comment)) {
            idx = end + 2
            continue
          }
          const { bodyStart, outerAggTagged } = consumeLeadingComments(s, start)
          const directWindow = findDirectLodWindowBounds(s, bodyStart)
          if (!directWindow) {
            idx = end + 2
            continue
          }
          const exprStart = outerAggTagged ? start : bodyStart
          const expr = s.slice(exprStart, directWindow.overEnd + 1).trim()
          if (expr && !seen.has(expr)) {
            seen.add(expr)
            out.push(expr)
          }
          idx = directWindow.overEnd + 1
        }
        return out
      }

      const lodExprs = extractLodWindowExprs(sql)
      if (lodRewriteDebug) {
        console.log('[lodRewrite] extracted expressions', {
          count: lodExprs.length,
          exprs: lodExprs.slice(0, 6),
        })
      }
      if (lodExprs.length === 0) return sql

      const splitTopLevelArgs = (src: string): string[] => {
        const s = String(src ?? '')
        const out: string[] = []
        let buf = ''
        let depth = 0
        let inSq = false
        let inDq = false
        for (let i = 0; i < s.length; i++) {
          const ch = s[i]
          if (!inDq && ch === "'" && s[i - 1] !== '\\') { inSq = !inSq; buf += ch; continue }
          if (!inSq && ch === '"' && s[i - 1] !== '\\') { inDq = !inDq; buf += ch; continue }
          if (inSq || inDq) { buf += ch; continue }
          if (ch === '(') { depth++; buf += ch; continue }
          if (ch === ')') { depth = Math.max(0, depth - 1); buf += ch; continue }
          if (ch === ',' && depth === 0) {
            if (buf.trim()) out.push(buf.trim())
            buf = ''
            continue
          }
          buf += ch
        }
        if (buf.trim()) out.push(buf.trim())
        return out
      }

      const parseLodWindowExpr = (expr: string): { aggFn: string; aggArg: string; partitionCols: string[]; outerAgg: string | null } | null => {
        const raw0 = String(expr ?? '').trim()
        if (!raw0) return null
        const leadingCommentBlock = raw0.match(/^(?:(?:\/\*[\s\S]*?\*\/)\s*)+/)?.[0] ?? ''
        const outerAggMatches = Array.from(
          leadingCommentBlock.matchAll(/\/\*\s*LOD_OUTER_AGG\s+([A-Za-z_][A-Za-z0-9_]*)\s*\*\//ig)
        )
        const outerAgg = outerAggMatches.length
          ? String(outerAggMatches[outerAggMatches.length - 1]?.[1] ?? '').trim().toUpperCase()
          : null
        const raw = raw0.slice(leadingCommentBlock.length).trim()
        if (!raw) return null
        const overIdx = (() => {
          const up = raw.toUpperCase()
          let depth = 0
          let inSq = false
          for (let i = 0; i < up.length; i++) {
            const ch = raw[i]
            if (ch === "'" && raw[i - 1] !== '\\') { inSq = !inSq; continue }
            if (inSq) continue
            if (ch === '(') depth++
            else if (ch === ')') depth = Math.max(0, depth - 1)
            if (depth === 0 && up.slice(i, i + 4) === 'OVER') return i
          }
          return -1
        })()
        if (overIdx < 0) return null
        const aggRaw = raw.slice(0, overIdx).trim()
        const overRaw = raw.slice(overIdx + 4).trim()
        const aggMatch = aggRaw.match(/^([A-Za-z_][A-Za-z0-9_]*)\s*\(([\s\S]+)\)$/)
        const aggFn = aggMatch
          ? String(aggMatch[1] ?? '').trim()
          : outerAgg
            ? outerAgg
            : ''
        const aggArg = aggMatch
          ? String(aggMatch[2] ?? '').trim()
          : aggRaw
        const overMatch = overRaw.match(/^\(\s*PARTITION\s+BY\s+([\s\S]+)\)$/i)
        if (!overMatch) return null
        const partRaw = String(overMatch[1] ?? '').trim()
        const partitionCols = splitTopLevelArgs(partRaw)
        if (!aggFn || !aggArg || !partitionCols.length) return null
        return { aggFn, aggArg, partitionCols, outerAgg }
      }

      const replaceBaseAlias = (src: string, newAlias: string): string => {
        const repl = String(newAlias || '').trim()
        if (!repl) return src
        return src
          // NOTE: do NOT use a word-boundary before `"t"` since `"` is not a word char,
          // and many compiled expressions appear as `("t"."col")` or `CASE ... THEN "t"."col"`.
          // Using `\b"t"` would fail to match in those cases and break correlated LOD rewrites.
          .replace(/"t"\s*\./g, `${repl}.`)
          .replace(/\bt\s*\./g, `${repl}.`)
      }

      const lodDefs = lodExprs.map((expr) => parseLodWindowExpr(expr))
      const normalizeLodAliasKey = (raw: string): string => normColKey(raw)
      let activePrecomputedLodKeys: Set<string> | null = (() => {
        if (!precomputedLodAliasSet || precomputedLodAliasSet.size === 0) return null
        const out = new Set<string>()
        for (const raw of precomputedLodAliasSet) {
          const key = normalizeLodAliasKey(String(raw ?? ''))
          if (key) out.add(key)
        }
        return out.size ? out : null
      })()
      const hasActivePrecomputedLod = (raw: string): boolean => {
        const key = normalizeLodAliasKey(raw)
        return Boolean(key && activePrecomputedLodKeys && activePrecomputedLodKeys.has(key))
      }
      const fromSourceWithAlias = (baseSourceSql: string, alias: string): string => {
        const s0 = String(baseSourceSql ?? '').trim().replace(/;+\s*$/, '')
        const a = String(alias || '').trim()
        if (!s0) return a ? `(${''}) AS ${a}` : `(${''})`
        if (!a) return s0
        const lower = s0.toLowerCase()
        // Subqueries must be parenthesized in FROM.
        if (lower.startsWith('select ') || lower.startsWith('with ') || lower.startsWith('select\n') || lower.startsWith('with\n')) {
          return `(${s0}) AS ${a}`
        }
        // If it's already a parenthesized SELECT/WITH, keep it as-is.
        if (s0.startsWith('(') && s0.endsWith(')')) {
          const inner = s0.slice(1, -1).trim()
          const innerLower = inner.toLowerCase()
          if (innerLower.startsWith('select ') || innerLower.startsWith('with ') || innerLower.startsWith('select\n') || innerLower.startsWith('with\n')) {
            return `${s0} AS ${a}`
          }
          // If it's a parenthesized *identifier* (e.g. `("main"."table")`), unwrap it.
          // DuckDB treats `FROM (<ident>)` as a subquery and the alias won't bind.
          return `${inner} AS ${a}`
        }
        return `${s0} AS ${a}`
      }
      const buildLodCorrelatedExpr = (def: { aggFn: string; aggArg: string; partitionCols: string[] } | null, baseSourceSql: string, outerAlias: string, idx: number): string => {
        if (!def) return lodExprs[idx]
        // If the aggregate argument compiled to NULL (unresolved field), skip the expensive
        // correlated subquery entirely — MAX(NULL) always returns NULL regardless of partitioning.
        // This avoids DuckDB scoping errors from unnecessary correlated subquery table aliases.
        const aggArgTrimmed = String(def.aggArg ?? '').trim()
        if (!aggArgTrimmed || aggArgTrimmed.toUpperCase() === 'NULL') {
          return 'NULL'
        }
        const innerAlias = `"__lod_src_${idx}"`
        const aggArgInner = replaceBaseAlias(def.aggArg, innerAlias)
        const partitionPairs = def.partitionCols.map((p) => {
          const inner = replaceBaseAlias(p, innerAlias)
          const outer = replaceBaseAlias(p, outerAlias)
          return `(${inner}) IS NOT DISTINCT FROM (${outer})`
        }).join(' AND ')
        return `(SELECT ${def.aggFn}(${aggArgInner}) FROM ${fromSourceWithAlias(baseSourceSql, innerAlias)} WHERE ${partitionPairs})`
      }

      const aliasFor = (i: number) => `__lod_${i + 1}`
      const onceAliasFor = (i: number) => `__lod_once_${i + 1}`
      const outerAggFor = (i: number): string => String((lodDefs[i] as any)?.outerAgg ?? '').trim().toUpperCase()
      const shouldUseLodOnceAlias = (i: number): boolean => {
        const outer = outerAggFor(i)
        return outer === 'SUM' || outer === 'AVG' || outer === 'COUNT' || outer === 'COUNTD'
      }
      const baseAlias = quoteIdent('t')
      if (precomputeOut) {
        precomputeLodsOut = lodExprs.map((expr, i) => ({ alias: aliasFor(i), expr }))
        ;(precomputeOut as any).lods = precomputeLodsOut
      }
      const generatedLodKeys = (() => {
        const s = new Set<string>()
        for (let i = 0; i < lodExprs.length; i++) {
          const k = normColKey(aliasFor(i))
          if (k) s.add(k)
          if (shouldUseLodOnceAlias(i)) {
            const onceKey = normColKey(onceAliasFor(i))
            if (onceKey) s.add(onceKey)
          }
        }
        return s
      })()
      const buildExtras = (baseSourceSql: string): string => {
        if (!lodPreAggregate) {
          const parts: string[] = []
          for (let i = 0; i < lodExprs.length; i++) {
            const expr = lodExprs[i]
            const alias = aliasFor(i)
            if (!hasActivePrecomputedLod(alias)) {
              parts.push(`${expr} AS ${quoteIdent(alias)}`)
            }
            const def = lodDefs[i]
            if (shouldUseLodOnceAlias(i) && def && def.partitionCols.length > 0 && !hasActivePrecomputedLod(onceAliasFor(i))) {
              const onceExpr = `CASE WHEN ROW_NUMBER() OVER (PARTITION BY ${def.partitionCols.join(', ')}) = 1 THEN ${expr} ELSE NULL END`
              parts.push(`${onceExpr} AS ${quoteIdent(onceAliasFor(i))}`)
            }
          }
          return parts.join(', ')
        }
        const parts: string[] = []
        for (let i = 0; i < lodExprs.length; i++) {
          const def = lodDefs[i]
          const lodAlias = aliasFor(i)
          const hasPrecomputedAlias = hasActivePrecomputedLod(lodAlias)
          const needsLodOnce = shouldUseLodOnceAlias(i)
          const normalizeFromSource = (src: string): string => {
            const s = String(src ?? '').trim()
            if (!s) return s
            const lower = s.toLowerCase()
            // Keep true subqueries (SELECT/WITH), regardless of parentheses.
            if (lower.startsWith('select ') || lower.startsWith('with ')) return s
            if (lower.startsWith('(select') || lower.startsWith('(with')) return s
            // Some upstream builders wrap bare table identifiers as `(schema.table)` which is not
            // a valid FROM source in DuckDB (it expects a subquery after '('). Unwrap in that case.
            if (s.startsWith('(') && s.endsWith(')')) {
              const inner = s.slice(1, -1).trim()
              const innerLower = inner.toLowerCase()
              if (!(innerLower.startsWith('select ') || innerLower.startsWith('with ') || innerLower.startsWith('select(') || innerLower.startsWith('with('))) {
                return inner
              }
            }
            return s
          }
          const lodExprForAlias = hasPrecomputedAlias
            ? `${baseAlias}.${quoteIdent(lodAlias)}`
            : (needsLodOnce
              ? lodExprs[i]
              : buildLodCorrelatedExpr(def, normalizeFromSource(baseSourceSql), baseAlias, i))
          if (!hasPrecomputedAlias) {
            parts.push(`${lodExprForAlias} AS ${quoteIdent(lodAlias)}`)
          }
          if (needsLodOnce && def && def.partitionCols.length > 0 && !hasActivePrecomputedLod(onceAliasFor(i))) {
            const onceExpr = `CASE WHEN ROW_NUMBER() OVER (PARTITION BY ${def.partitionCols.join(', ')}) = 1 THEN ${lodExprForAlias} ELSE NULL END`
            parts.push(`${onceExpr} AS ${quoteIdent(onceAliasFor(i))}`)
          }
        }
        return parts.join(', ')
      }
      const baseCols = collectBaseCols(sql)

      const findTopLevelKeyword = (s: string, phraseRaw: string, start = 0): number => {
        const words = String(phraseRaw ?? '').trim().toUpperCase().split(/\s+/).filter(Boolean)
        if (!words.length) return -1
        const isWordChar = (c: string | undefined): boolean => /[A-Za-z0-9_]/.test(String(c ?? ''))
        const matchesAt = (idx: number): boolean => {
          let pos = idx
          for (let wi = 0; wi < words.length; wi++) {
            const word = words[wi]!
            if (s.slice(pos, pos + word.length).toUpperCase() !== word) return false
            const before = wi === 0 ? s[pos - 1] : undefined
            const after = s[pos + word.length]
            if (wi === 0 && isWordChar(before)) return false
            if (wi === words.length - 1 && isWordChar(after)) return false
            pos += word.length
            if (wi < words.length - 1) {
              if (!/\s/.test(s[pos] ?? '')) return false
              while (pos < s.length && /\s/.test(s[pos])) pos++
            }
          }
          return true
        }
        let depth = 0
        let inSq = false
        let inDq = false
        for (let i = Math.max(0, start); i < s.length; i++) {
          const ch = s[i]
          if (!inDq && ch === "'" && s[i - 1] !== '\\') { inSq = !inSq; continue }
          if (!inSq && ch === '"' && s[i - 1] !== '\\') { inDq = !inDq; continue }
          if (inSq || inDq) continue
          if (ch === '(') { depth++; continue }
          if (ch === ')') { depth = Math.max(0, depth - 1); continue }
          if (depth === 0 && matchesAt(i)) return i
        }
        return -1
      }

      const appendTopLevelGroupByExpressions = (s: string, exprsRaw: string[]): string => {
        const exprs = Array.from(new Set(exprsRaw.map((expr) => String(expr ?? '').trim()).filter(Boolean)))
        if (!exprs.length) return s
        const groupIdx = findTopLevelKeyword(s, 'GROUP BY')
        if (groupIdx < 0) return s
        const groupStart = groupIdx + String(s.slice(groupIdx).match(/^GROUP\s+BY/i)?.[0] ?? 'GROUP BY').length
        const clauseEnds = ['HAVING', 'WINDOW', 'QUALIFY', 'ORDER BY', 'LIMIT', 'OFFSET', 'FETCH']
          .map((kw) => findTopLevelKeyword(s, kw, groupStart))
          .filter((idx) => idx >= 0)
        const end = clauseEnds.length ? Math.min(...clauseEnds) : s.length
        const existing = s.slice(groupStart, end).toLowerCase().replace(/\s+/g, ' ')
        const additions = exprs.filter((expr) => {
          const key = expr.toLowerCase().replace(/\s+/g, ' ')
          return key && !existing.includes(key)
        })
        if (!additions.length) return s
        const before = s.slice(0, end).replace(/\s+$/, '')
        const after = s.slice(end)
        const spacer = after && !/^\s/.test(after) ? ' ' : ''
        return `${before}, ${additions.join(', ')}${spacer}${after}`
      }

      const stripOuterSqlParens = (exprRaw: string): string => {
        let expr = String(exprRaw ?? '').trim()
        while (expr.startsWith('(') && expr.endsWith(')')) {
          const close = findMatchingParen(expr, 0)
          if (close !== expr.length - 1) break
          expr = expr.slice(1, -1).trim()
        }
        return expr
      }

      const stripTopLevelSelectAlias = (itemRaw: string): string => {
        const item = String(itemRaw ?? '').trim()
        if (!item) return ''
        const asIdx = findTopLevelKeyword(item, 'AS')
        if (asIdx >= 0) return item.slice(0, asIdx).trim()
        return item
      }

      const stripOrderTermModifiers = (termRaw: string): string => {
        let term = String(termRaw ?? '').trim()
        const nullsMatch = term.match(/\s+NULLS\s+(?:FIRST|LAST)\s*$/i)
        if (nullsMatch) term = term.slice(0, nullsMatch.index).trim()
        const dirMatch = term.match(/\s+(?:ASC|DESC)\s*$/i)
        if (dirMatch) term = term.slice(0, dirMatch.index).trim()
        return term
      }

      const unwrapAnyValueForGrouping = (exprRaw: string): string => {
        const expr = stripOuterSqlParens(exprRaw)
        const prefix = expr.match(/^ANY_VALUE\s*\(/i)
        if (!prefix) return expr
        const openIdx = expr.indexOf('(')
        if (openIdx < 0) return expr
        const closeIdx = findMatchingParen(expr, openIdx)
        if (closeIdx < 0) return expr
        if (expr.slice(closeIdx + 1).trim()) return expr
        return stripOuterSqlParens(expr.slice(openIdx + 1, closeIdx))
      }

      const collectDirectLodGroupExpressions = (s: string, lodRefsRaw: string[]): string[] => {
        const lodRefs = Array.from(new Set(lodRefsRaw.map((ref) => String(ref ?? '').trim()).filter(Boolean)))
        if (!lodRefs.length) return []
        const containsDirectLod = (expr: string): boolean => lodRefs.some((ref) => expr.includes(ref))
        const normalize = (expr: string): string => stripOuterSqlParens(expr).replace(/\s+/g, ' ').trim()
        const out: string[] = []
        const addCandidate = (exprRaw: string): void => {
          const expr = normalize(exprRaw)
          if (!expr || !containsDirectLod(expr)) return
          if (/^(?:SUM|AVG|COUNT|MIN|MAX|COUNTD)\s*\(/i.test(expr)) return
          out.push(expr)
        }
        const selectIdx = findTopLevelKeyword(s, 'SELECT')
        const fromIdx = selectIdx >= 0 ? findTopLevelKeyword(s, 'FROM', selectIdx + 'SELECT'.length) : -1
        if (selectIdx >= 0 && fromIdx > selectIdx) {
          const selectBody = s.slice(selectIdx + 'SELECT'.length, fromIdx)
          for (const item of splitTopLevelArgs(selectBody)) {
            const expr = unwrapAnyValueForGrouping(stripTopLevelSelectAlias(item))
            addCandidate(expr)
          }
        }
        const orderIdx = findTopLevelKeyword(s, 'ORDER BY')
        if (orderIdx >= 0) {
          const orderStart = orderIdx + String(s.slice(orderIdx).match(/^ORDER\s+BY/i)?.[0] ?? 'ORDER BY').length
          const clauseEnds = ['LIMIT', 'OFFSET', 'FETCH']
            .map((kw) => findTopLevelKeyword(s, kw, orderStart))
            .filter((idx) => idx >= 0)
          const orderEnd = clauseEnds.length ? Math.min(...clauseEnds) : s.length
          for (const item of splitTopLevelArgs(s.slice(orderStart, orderEnd))) {
            addCandidate(stripOrderTermModifiers(item))
          }
        }
        return out.length ? out : lodRefs
      }

      const directLodRefs: string[] = []
      let out = sql
      for (let i = 0; i < lodExprs.length; i++) {
        const expr = lodExprs[i]
        const alias = aliasFor(i)
        const def = lodDefs[i]
        const lodRef = `${baseAlias}.${quoteIdent(alias)}`
        const lodOnceRef = `${baseAlias}.${quoteIdent(onceAliasFor(i))}`
        const lodRefNumeric = `TRY_CAST(${lodRef} AS DOUBLE)`
        const lodOnceRefNumeric = `TRY_CAST(${lodOnceRef} AS DOUBLE)`
        const outerAgg = outerAggFor(i)
        const canUseLodOnce = Boolean(def && def.partitionCols.length > 0)
        let replacement = lodRef
        if (!outerAgg) directLodRefs.push(lodRef)
        else if (outerAgg === 'SUM') replacement = canUseLodOnce ? `SUM(${lodOnceRefNumeric})` : `SUM(${lodRefNumeric})`
        else if (outerAgg === 'AVG') replacement = canUseLodOnce ? `AVG(${lodOnceRefNumeric})` : `AVG(${lodRefNumeric})`
        else if (outerAgg === 'COUNT') replacement = canUseLodOnce ? `COUNT(${lodOnceRef})` : `COUNT(${lodRef})`
        else if (outerAgg === 'COUNTD') replacement = canUseLodOnce ? `COUNT(DISTINCT ${lodOnceRef})` : `COUNT(DISTINCT ${lodRef})`
        else if (outerAgg === 'MIN' || outerAgg === 'MAX') replacement = `${outerAgg}(${lodRef})`
        out = out.split(expr).join(replacement)
      }
      out = appendTopLevelGroupByExpressions(out, collectDirectLodGroupExpressions(out, directLodRefs))

      // Locate the primary `FROM <source> AS "t"` clause using paren-balanced parsing
      // so that we correctly handle nested CTEs/subqueries without false-matching
      // things like `AS TIMESTAMP`.
      const findPrimaryFromClause = (s: string): { start: number; end: number; sourceStart: number; sourceEnd: number; alias: string; innerSql: string; kind: 'read_parquet' | 'subquery' } | null => {
        const upper = s.toUpperCase()
        const isWordChar = (c: string) => /[A-Za-z0-9_]/.test(c)
        // Scan for top-level `FROM` keywords (ignore nested subqueries/strings).
        const fromPositions: number[] = []
        let depth = 0
        let inStr = false
        let inIdent = false
        for (let i = 0; i < upper.length - 4; i++) {
          const ch = s[i]
          if (ch === "'" && !inIdent) {
            if (inStr && s[i + 1] === "'") { i++; continue }
            inStr = !inStr
            continue
          }
          if (!inStr && ch === '"') {
            if (inIdent && s[i + 1] === '"') { i++; continue }
            inIdent = !inIdent
            continue
          }
          if (inStr || inIdent) continue
          if (ch === '(') { depth += 1; continue }
          if (ch === ')') { depth = Math.max(0, depth - 1); continue }
          if (depth !== 0) continue
          if (upper.startsWith('FROM', i)) {
            const before = s[i - 1]
            const after = s[i + 4]
            if ((before && isWordChar(before)) || (after && isWordChar(after))) continue
            // Ensure the char after FROM is whitespace
            if (i + 4 < s.length && /\s/.test(s[i + 4])) {
              fromPositions.push(i)
            }
          }
        }
        for (const fi of fromPositions) {
          let pos = fi + 4
          // skip whitespace
          while (pos < s.length && /\s/.test(s[pos])) pos++

          // Check for read_parquet(...)
          if (upper.startsWith('READ_PARQUET', pos)) {
            const rpStart = pos
            pos += 'READ_PARQUET'.length
            while (pos < s.length && /\s/.test(s[pos])) pos++
            if (s[pos] !== '(') continue
            // Balance parens to find end of read_parquet(...)
            let depth = 1
            pos++
            while (pos < s.length && depth > 0) {
              if (s[pos] === '(') depth++
              else if (s[pos] === ')') depth--
              if (depth > 0) pos++
              else break
            }
            if (depth !== 0) continue
            const rpEnd = pos + 1 // past closing )
            // Skip whitespace, expect AS
            let after = rpEnd
            while (after < s.length && /\s/.test(s[after])) after++
            if (upper.slice(after, after + 2) !== 'AS') continue
            after += 2
            while (after < s.length && /\s/.test(s[after])) after++
            // Match "t" or t
            let alias = ''
            let aliasEnd = after
            if (s[after] === '"') {
              if (s[after + 1]?.toLowerCase() === 't' && s[after + 2] === '"') {
                alias = '"t"'
                aliasEnd = after + 3
              } else continue
            } else if (s[after]?.toLowerCase() === 't' && (after + 1 >= s.length || /[\s,);]/.test(s[after + 1]))) {
              alias = 't'
              aliasEnd = after + 1
            } else continue
            // Extract the URL from read_parquet('...')
            const rpContent = s.substring(rpStart + 13, rpEnd - 1).trim() // skip "read_parquet" and parens
            const rpInner = rpContent.startsWith('(') ? rpContent.slice(1).trim() : rpContent
            const urlMatch = rpInner.match(/^'([^']+)'/)
            const url = urlMatch ? urlMatch[1] : ''
            return {
              start: fi,
              end: aliasEnd,
              sourceStart: rpStart,
              sourceEnd: rpEnd,
              alias,
              innerSql: url ? `read_parquet('${url}')` : s.substring(rpStart, rpEnd),
              kind: 'read_parquet',
            }
          }

          // Check for (...) subquery
          if (s[pos] === '(') {
            // Balance parens to find the matching close
            let depth = 1
            const subStart = pos
            pos++
            let inSingleQuote = false
            let inDoubleQuote = false
            while (pos < s.length && depth > 0) {
              const ch = s[pos]
              if (!inDoubleQuote && ch === "'" && s[pos - 1] !== '\\') { inSingleQuote = !inSingleQuote }
              else if (!inSingleQuote && ch === '"' && s[pos - 1] !== '\\') { inDoubleQuote = !inDoubleQuote }
              else if (!inSingleQuote && !inDoubleQuote) {
                if (ch === '(') depth++
                else if (ch === ')') depth--
              }
              if (depth > 0) pos++
              else break
            }
            if (depth !== 0) continue
            const subEnd = pos + 1 // past closing )
            // Skip whitespace, expect AS
            let after = subEnd
            while (after < s.length && /\s/.test(s[after])) after++
            if (upper.slice(after, after + 2) !== 'AS') continue
            after += 2
            while (after < s.length && /\s/.test(s[after])) after++
            // Match "t" or t (exact identifier, not prefix of TIMESTAMP etc.)
            let alias = ''
            let aliasEnd = after
            if (s[after] === '"') {
              if (s[after + 1]?.toLowerCase() === 't' && s[after + 2] === '"') {
                alias = '"t"'
                aliasEnd = after + 3
              } else continue
            } else if (s[after]?.toLowerCase() === 't' && (after + 1 >= s.length || /[\s,);]/.test(s[after + 1]))) {
              alias = 't'
              aliasEnd = after + 1
            } else continue
            const inner = s.substring(subStart + 1, subEnd - 1)
            return {
              start: fi,
              end: aliasEnd,
              sourceStart: subStart,
              sourceEnd: subEnd,
              alias,
              innerSql: inner,
              kind: 'subquery',
            }
          }
        }
        return null
      }
      const buildProjectionCols = (): string[] => {
        const seen = new Set<string>()
        const projectionCols: string[] = []
        const sourceAliasKeys = (() => {
          const out = new Set<string>()
          if (fromMatch?.kind !== 'subquery') return out
          const sourceStage = extractTopLevelSelectStage(String(fromMatch.innerSql ?? '').trim())
          const sourceItems = Array.isArray(sourceStage?.selectItems) ? sourceStage.selectItems : []
          if (selectItemsContainWildcard(sourceItems)) return out
          for (const selectItem of sourceItems) {
            const alias = unquoteProjectedAliasName(extractSelectItemAlias(String(selectItem || '')))
            const key = normColKey(alias)
            if (key) out.add(key)
          }
          return out
        })()
        for (const raw of baseCols) {
          const key = normColKey(raw)
          const schemaMatch = extractColsByKey.get(key) || ''
          const actual = extractColsByKey.size
            ? (
              schemaMatch ||
              (fromMatch?.kind === 'subquery' && sourceAliasKeys.has(key) ? raw : '')
            )
            : raw
          if (!actual) continue
          const actualKey = normColKey(actual)
          if (generatedLodKeys.has(actualKey)) {
            const allowPrecomputed = hasActivePrecomputedLod(actual) || hasActivePrecomputedLod(actualKey)
            if (!allowPrecomputed) continue
          }
          if (seen.has(actualKey)) continue
          seen.add(actualKey)
          projectionCols.push(actual)
        }
        if (activePrecomputedLodKeys) {
          for (const key of activePrecomputedLodKeys) {
            if (!key || seen.has(key)) continue
            seen.add(key)
            projectionCols.push(key)
          }
        }
        return projectionCols
      }
      const fromMatch = findPrimaryFromClause(out)
      if (!fromMatch) {
        // If we couldn't locate the primary FROM clause, avoid a partial rewrite
        // that would reference __lod_* columns without injecting them.
        if (lodRewriteDebug) {
          console.warn('[lodRewrite] primary FROM match missing; leaving SQL unchanged')
        }
        return sql
      }
      if (activePrecomputedLodKeys && activePrecomputedLodKeys.size > 0) {
        const sourceSql = String(fromMatch.innerSql ?? '')
        const available = new Set<string>()
        for (const key of activePrecomputedLodKeys) {
          if (!key) continue
          if (extractColsByKey.has(key)) {
            available.add(key)
            continue
          }
          if (fromMatch.kind !== 'subquery') continue
          const esc = escapeRegExp(key)
          const asQuoted = new RegExp(`\\bAS\\s+"${esc}"(?=[\\s,\\)])`, 'i')
          const asBare = new RegExp(`\\bAS\\s+${esc}(?=[\\s,\\)])`, 'i')
          if (asQuoted.test(sourceSql) || asBare.test(sourceSql)) {
            available.add(key)
          }
        }
        activePrecomputedLodKeys = available.size > 0 ? available : null
      }
      const alias = fromMatch.alias
      const projectionCols = buildProjectionCols()
      const projection =
        projectionCols.length > 0
          ? projectionCols.map((col) => `${alias}.${quoteIdent(col)}`).join(', ')
          : '*'
      const source = fromMatch.kind === 'read_parquet'
        ? fromMatch.innerSql
        : `(${fromMatch.innerSql.trim()})`
      const extras = buildExtras(fromMatch.kind === 'read_parquet' ? fromMatch.innerSql : fromMatch.innerSql.trim())
      if (lodRewriteDebug) {
        console.log('[lodRewrite] rewrote SQL', {
          extrasPreview: extras.slice(0, 400),
          changed: out !== sql,
        })
      }
      const replacement = `FROM (SELECT ${projection}, ${extras} FROM ${source} AS ${alias}) AS ${alias}`
      out = out.substring(0, fromMatch.start) + replacement + out.substring(fromMatch.end)
      return out
    } catch (e) {
      try {
        const search = String((globalThis as any)?.location?.search ?? '')
        if (/(?:\?|&)fg_debug_exec_plan=(?:1|true|yes|on)(?:&|$)/i.test(search)) {
          console.warn('[lodRewrite] failed; leaving SQL unchanged', e)
        }
      } catch { }
      return sql
    }
  }

  const coerceDateTruncLiteralAnchors = (sqlIn: string): string => {
    const sql = String(sqlIn ?? '')
    // DuckDB requires the second arg to be DATE/TIMESTAMP, not a string literal.
    // Rewrite date_trunc('<unit>', '<literal>') -> date_trunc('<unit>', TRY_CAST('<literal>' AS TIMESTAMP))
    return sql.replace(
      /\bdate_trunc\s*\(\s*'([^']+)'\s*,\s*'([^']*)'\s*\)/gi,
      (_m, unit, literal) => `date_trunc('${unit}', TRY_CAST('${String(literal).replace(/'/g, "''")}' AS TIMESTAMP))`
    )
  }

  const coerceDateLiteralArithmetic = (sqlIn: string): string => {
    let sql = String(sqlIn ?? '')
    try {
      // Cast date-like string literals before arithmetic to avoid "+(STRING_LITERAL, INTEGER_LITERAL)" errors.
      // Examples:
      //   '2026-01-29' + 1  -> TRY_CAST('2026-01-29' AS DATE) + 1
      //   1 + '2026-01-29'  -> 1 + TRY_CAST('2026-01-29' AS DATE)
      const dateLit = "(\\d{4}-\\d{2}-\\d{2}(?:[ T][^']+)?)"
      const reLeft = new RegExp(String.raw`'${dateLit}'\s*([+-])\s*(\d+)`, 'g')
      const reRight = new RegExp(String.raw`(\d+)\s*([+-])\s*'${dateLit}'`, 'g')
      sql = sql.replace(reLeft, (_m, dt, op, n) => `TRY_CAST('${dt}' AS DATE) ${op} ${n}`)
      sql = sql.replace(reRight, (_m, n, op, dt) => `${n} ${op} TRY_CAST('${dt}' AS DATE)`)
    } catch { }
    return sql
  }

  const coerceDateDiffOperands = (sqlIn: string): string => {
    const sql = String(sqlIn ?? '')
    if (!sql || !/\bdate_diff\s*\(/i.test(sql)) return sql
    try {
      const splitTopLevelArgs = (src: string): string[] => {
        const s = String(src ?? '')
        const out: string[] = []
        let buf = ''
        let depth = 0
        let inSq = false
        let inDq = false
        for (let i = 0; i < s.length; i++) {
          const ch = s[i]
          if (!inDq && ch === "'" && s[i - 1] !== '\\') {
            inSq = !inSq
            buf += ch
            continue
          }
          if (!inSq && ch === '"' && s[i - 1] !== '\\') {
            inDq = !inDq
            buf += ch
            continue
          }
          if (inSq || inDq) {
            buf += ch
            continue
          }
          if (ch === '(') {
            depth += 1
            buf += ch
            continue
          }
          if (ch === ')') {
            depth = Math.max(0, depth - 1)
            buf += ch
            continue
          }
          if (ch === ',' && depth === 0) {
            out.push(buf.trim())
            buf = ''
            continue
          }
          buf += ch
        }
        if (buf.trim()) out.push(buf.trim())
        return out
      }
      const findMatchingParen = (src: string, openIdx: number): number => {
        let depth = 0
        let inSq = false
        let inDq = false
        for (let i = openIdx; i < src.length; i++) {
          const ch = src[i]
          if (!inDq && ch === "'" && src[i - 1] !== '\\') {
            inSq = !inSq
            continue
          }
          if (!inSq && ch === '"' && src[i - 1] !== '\\') {
            inDq = !inDq
            continue
          }
          if (inSq || inDq) continue
          if (ch === '(') depth += 1
          else if (ch === ')') {
            depth -= 1
            if (depth === 0) return i
          }
        }
        return -1
      }
      const coerceDateDiffArg = (argIn: string): string => {
        const arg = String(argIn ?? '').trim()
        if (!arg) return arg
        if (/^(TRY_CAST|CAST|TO_TIMESTAMP)\s*\(/i.test(arg)) return arg
        if (/^NULL$/i.test(arg)) return 'CAST(NULL AS TIMESTAMP)'
        const num = Number(arg)
        if (/^[+-]?\d+(?:\.\d+)?$/.test(arg) && Number.isFinite(num) && Math.abs(num) >= 1e12) {
          return `CAST(TO_TIMESTAMP((CAST(${arg} AS DOUBLE) / 1000.0)) AS TIMESTAMP)`
        }
        return `TRY_CAST(${arg} AS TIMESTAMP)`
      }
      let out = ''
      let i = 0
      let inSq = false
      let inDq = false
      while (i < sql.length) {
        const ch = sql[i]
        if (!inDq && ch === "'" && sql[i - 1] !== '\\') {
          inSq = !inSq
          out += ch
          i += 1
          continue
        }
        if (!inSq && ch === '"' && sql[i - 1] !== '\\') {
          inDq = !inDq
          out += ch
          i += 1
          continue
        }
        if (inSq || inDq) {
          out += ch
          i += 1
          continue
        }
        if (i + 9 <= sql.length && sql.slice(i, i + 9).toUpperCase() === 'DATE_DIFF') {
          const before = sql[i - 1]
          const afterToken = sql[i + 9]
          if ((before && /[A-Za-z0-9_]/.test(before)) || (afterToken && /[A-Za-z0-9_]/.test(afterToken))) {
            out += ch
            i += 1
            continue
          }
          let j = i + 9
          while (j < sql.length && /\s/.test(sql[j])) j += 1
          if (sql[j] !== '(') {
            out += ch
            i += 1
            continue
          }
          const end = findMatchingParen(sql, j)
          if (end < 0) {
            out += ch
            i += 1
            continue
          }
          const inner = sql.slice(j + 1, end)
          const args = splitTopLevelArgs(inner)
          if (args.length === 3) {
            const rebuilt = `${String(args[0] ?? '').trim()}, ${coerceDateDiffArg(String(args[1] ?? ''))}, ${coerceDateDiffArg(String(args[2] ?? ''))}`
            out += `DATE_DIFF(${rebuilt})`
            i = end + 1
            continue
          }
        }
        out += ch
        i += 1
      }
      return out
    } catch {
      return sql
    }
  }

  const reportUnresolved = (sql: string): string => {
    if (traceUnresolved && typeof window !== 'undefined') {
      try {
        ;(window as any).__FG_UNRESOLVED_FIELDS__ = {
          worksheetName: options.worksheetName ?? null,
          count: unresolvedEvents.length,
          fields: unresolvedEvents.slice(0, 50),
          sqlPreview: String(sql ?? '').slice(0, 2000),
          ts: Date.now(),
        }
      } catch { }
    }
    return sql
  }

  const coerceAggregateWindowOrderColumns = (sqlIn: string): string => {
    // Deprecated: this pass blindly wrapped window ORDER BY refs with MIN/MAX and
    // could inject aggregate scope into SELECT lists that are not grouped, causing
    // DuckDB binder failures. Group-safe ORDER rewrites are handled by
    // rewriteNestedWindowInGroupedSelect with SELECT/GROUP BY awareness.
    return String(sqlIn ?? '')
  }
  const SQL_TRANSFORM_CACHE_MAX = 24
  const nestedWindowRewriteResultCache = new Map<string, string>()
  const windowOverDedupeResultCache = new Map<string, string>()
  const writeSqlTransformCache = (cache: Map<string, string>, key: string, value: string): string => {
    if (!cache.has(key) && cache.size >= SQL_TRANSFORM_CACHE_MAX) {
      const oldest = cache.keys().next()
      if (!oldest.done) cache.delete(oldest.value)
    }
    cache.set(key, value)
    return value
  }
  const rewriteNestedWindowInGroupedSelectCached = (sqlIn: string, rwDepth = 0): string => {
    const key = String(sqlIn ?? '')
    const cached = nestedWindowRewriteResultCache.get(key)
    if (cached !== undefined) return cached
    return writeSqlTransformCache(
      nestedWindowRewriteResultCache,
      key,
      rewriteNestedWindowInGroupedSelect(sqlIn, rwDepth),
    )
  }
  const dedupeWindowOverClausesInSelectCached = (sqlIn: string): string => {
    const key = String(sqlIn ?? '')
    const cached = windowOverDedupeResultCache.get(key)
    if (cached !== undefined) return cached
    return writeSqlTransformCache(
      windowOverDedupeResultCache,
      key,
      dedupeWindowOverClausesInSelect(sqlIn),
    )
  }

  const splitTopLevelCsvForFinalSanitizer = (src: string): string[] => {
    const out: string[] = []
    let depth = 0
    let inStr = false
    let inIdent = false
    let start = 0
    for (let i = 0; i < src.length; i += 1) {
      const ch = src[i]
      if (inStr) {
        if (ch === "'" && src[i + 1] === "'") { i += 1; continue }
        if (ch === "'") inStr = false
        continue
      }
      if (inIdent) {
        if (ch === '"' && src[i + 1] === '"') { i += 1; continue }
        if (ch === '"') inIdent = false
        continue
      }
      if (ch === "'") { inStr = true; continue }
      if (ch === '"') { inIdent = true; continue }
      if (ch === '(') { depth += 1; continue }
      if (ch === ')') { depth = Math.max(0, depth - 1); continue }
      if (ch === ',' && depth === 0) {
        out.push(src.slice(start, i).trim())
        start = i + 1
      }
    }
    const tail = src.slice(start).trim()
    if (tail) out.push(tail)
    return out
  }

  const findTopLevelKeywordForFinalSanitizer = (src: string, keyword: string): number => {
    const upper = String(src ?? '').toUpperCase()
    const needle = String(keyword ?? '').trim().toUpperCase()
    if (!upper || !needle) return -1
    let depth = 0
    let inStr = false
    let inIdent = false
    for (let i = 0; i <= upper.length - needle.length; i += 1) {
      const ch = upper[i]
      if (inStr) {
        if (ch === "'" && upper[i + 1] === "'") { i += 1; continue }
        if (ch === "'") inStr = false
        continue
      }
      if (inIdent) {
        if (ch === '"' && upper[i + 1] === '"') { i += 1; continue }
        if (ch === '"') inIdent = false
        continue
      }
      if (ch === "'") { inStr = true; continue }
      if (ch === '"') { inIdent = true; continue }
      if (ch === '(') { depth += 1; continue }
      if (ch === ')') { depth = Math.max(0, depth - 1); continue }
      if (depth !== 0) continue
      if (upper.slice(i, i + needle.length) !== needle) continue
      const prev = i > 0 ? upper[i - 1] : ''
      const next = i + needle.length < upper.length ? upper[i + needle.length] : ''
      if ((prev && /[A-Z0-9_]/.test(prev)) || (next && /[A-Z0-9_]/.test(next))) continue
      return i
    }
    return -1
  }

  const stripOrderTermModifiersForFinalSanitizer = (termRaw: string): string => {
    let term = String(termRaw ?? '').trim()
    if (!term) return ''
    const nullsMatch = term.match(/\s+NULLS\s+(?:FIRST|LAST)\s*$/i)
    if (nullsMatch) term = term.slice(0, nullsMatch.index).trim()
    const dirMatch = term.match(/\s+(?:ASC|DESC)\s*$/i)
    if (dirMatch) term = term.slice(0, dirMatch.index).trim()
    return term
  }

  const finalizeOrderTerms = (terms: string[]): string[] => {
    const raw = Array.isArray(terms) ? terms.filter(Boolean).join(', ') : ''
    if (!raw) return []
    const sanitized = sanitizeNullArtifacts(raw)
    return splitTopLevelCsvForFinalSanitizer(sanitized).filter((item) => {
      const expr = stripOrderTermModifiersForFinalSanitizer(item)
      return !isNullishExpr(expr) && !isScalarLiteralExpr(expr)
    })
  }

  const pruneNullishTopLevelGroupOrderTerms = (sqlIn: string): string => {
    let sql = String(sqlIn ?? '')
    const rewriteClause = (keyword: 'GROUP BY' | 'ORDER BY', stripMods: boolean): void => {
      const clauseIdx = findTopLevelKeywordForFinalSanitizer(sql, keyword)
      if (clauseIdx < 0) return
      const bodyStart = clauseIdx + keyword.length
      const nextIdxCandidates = [
        findTopLevelKeywordForFinalSanitizer(sql, 'HAVING'),
        findTopLevelKeywordForFinalSanitizer(sql, 'QUALIFY'),
        findTopLevelKeywordForFinalSanitizer(sql, 'ORDER BY'),
        findTopLevelKeywordForFinalSanitizer(sql, 'LIMIT'),
      ].filter((idx) => idx > clauseIdx)
      const bodyEnd = nextIdxCandidates.length ? Math.min(...nextIdxCandidates) : sql.length
      const clauseBody = sql.slice(bodyStart, bodyEnd).trim()
      if (!clauseBody) return
      const items = splitTopLevelCsvForFinalSanitizer(clauseBody)
      const kept = items.filter((item) => {
        const expr = stripMods ? stripOrderTermModifiersForFinalSanitizer(item) : item
        return !isNullishExpr(expr) && !isScalarLiteralExpr(expr)
      })
      if (kept.length === items.length) return
      const before = sql.slice(0, clauseIdx).trimEnd()
      const after = sql.slice(bodyEnd).trimStart()
      sql = kept.length > 0
        ? `${before} ${keyword} ${kept.join(', ')}${after ? ` ${after}` : ''}`
        : `${before}${after ? ` ${after}` : ''}`
    }
    rewriteClause('GROUP BY', false)
    rewriteClause('ORDER BY', true)
    return sql.replace(/\s+/g, ' ').trim()
  }

  const pruneInvalidGroupTermsEverywhere = (sqlIn: string): string => {
    const sql = String(sqlIn ?? '')
    if (!/\bGROUP\s+BY\b/i.test(sql)) return sql
    type Edit = { start: number; end: number; newText: string }
    const edits: Edit[] = []
    const isWord = (c: string | undefined): boolean => Boolean(c && /[A-Za-z0-9_]/.test(c))
    const matchKeywordAt = (idx: number, keyword: string): boolean => {
      const needle = keyword.toUpperCase()
      const slice = sql.slice(idx, idx + needle.length).toUpperCase()
      if (slice !== needle) return false
      const prev = idx > 0 ? sql[idx - 1] : ''
      const next = idx + needle.length < sql.length ? sql[idx + needle.length] : ''
      return !isWord(prev) && !isWord(next)
    }
    const matchGroupByAt = (idx: number): { len: number } | null => {
      if (!matchKeywordAt(idx, 'GROUP')) return null
      let j = idx + 'GROUP'.length
      while (j < sql.length && /\s/.test(sql[j])) j += 1
      if (!matchKeywordAt(j, 'BY')) return null
      return { len: (j + 'BY'.length) - idx }
    }
    const findClauseEnd = (start: number, groupDepth: number): number => {
      let depth = groupDepth
      let inStr = false
      let inIdent = false
      const terminators = ['HAVING', 'QUALIFY', 'ORDER BY', 'LIMIT', 'UNION', 'INTERSECT', 'EXCEPT']
      for (let i = start; i < sql.length; i += 1) {
        const ch = sql[i]
        if (inStr) {
          if (ch === "'" && sql[i + 1] === "'") { i += 1; continue }
          if (ch === "'") inStr = false
          continue
        }
        if (inIdent) {
          if (ch === '"' && sql[i + 1] === '"') { i += 1; continue }
          if (ch === '"') inIdent = false
          continue
        }
        if (ch === "'") { inStr = true; continue }
        if (ch === '"') { inIdent = true; continue }
        if (ch === '(') { depth += 1; continue }
        if (ch === ')') {
          if (depth === groupDepth) return i
          depth = Math.max(groupDepth, depth - 1)
          continue
        }
        if (depth !== groupDepth) continue
        for (const kw of terminators) {
          if (matchKeywordAt(i, kw)) return i
        }
      }
      return sql.length
    }
    const findProjectionTextForGroup = (groupIdx: number, groupDepth: number): string => {
      let depth = 0
      let inStr = false
      let inIdent = false
      let selectEnd = -1
      let fromIdx = -1
      for (let i = 0; i < groupIdx; i += 1) {
        const ch = sql[i]
        if (inStr) {
          if (ch === "'" && sql[i + 1] === "'") { i += 1; continue }
          if (ch === "'") inStr = false
          continue
        }
        if (inIdent) {
          if (ch === '"' && sql[i + 1] === '"') { i += 1; continue }
          if (ch === '"') inIdent = false
          continue
        }
        if (ch === "'") { inStr = true; continue }
        if (ch === '"') { inIdent = true; continue }
        if (ch === '(') { depth += 1; continue }
        if (ch === ')') { depth = Math.max(0, depth - 1); continue }
        if (depth !== groupDepth) continue
        if (matchKeywordAt(i, 'SELECT')) {
          selectEnd = i + 'SELECT'.length
          fromIdx = -1
          i += 'SELECT'.length - 1
          continue
        }
        if (selectEnd >= 0 && matchKeywordAt(i, 'FROM')) {
          fromIdx = i
          i += 'FROM'.length - 1
        }
      }
      if (selectEnd < 0 || fromIdx <= selectEnd) return ''
      return sql.slice(selectEnd, fromIdx)
    }
    const normalizeGroupTerm = (raw: string): string => String(raw ?? '').replace(/\s+/g, '').toLowerCase()

    let depth = 0
    let inStr = false
    let inIdent = false
    for (let i = 0; i < sql.length; i += 1) {
      const ch = sql[i]
      if (inStr) {
        if (ch === "'" && sql[i + 1] === "'") { i += 1; continue }
        if (ch === "'") inStr = false
        continue
      }
      if (inIdent) {
        if (ch === '"' && sql[i + 1] === '"') { i += 1; continue }
        if (ch === '"') inIdent = false
        continue
      }
      if (ch === "'") { inStr = true; continue }
      if (ch === '"') { inIdent = true; continue }
      if (ch === '(') { depth += 1; continue }
      if (ch === ')') { depth = Math.max(0, depth - 1); continue }
      const groupBy = matchGroupByAt(i)
      if (!groupBy) continue
      const bodyStart = i + groupBy.len
      const bodyEnd = findClauseEnd(bodyStart, depth)
      const clauseBody = sql.slice(bodyStart, bodyEnd).trim()
      if (!clauseBody || /^GROUPING\s+SETS\b/i.test(clauseBody) || /^ALL\b/i.test(clauseBody)) {
        i = bodyEnd - 1
        continue
      }
      const items = splitTopLevelCsvForFinalSanitizer(clauseBody)
      const kept = items.filter((item) => canSqlExpressionAppearInGroupBy(item))
      let groupChanged = kept.length !== items.length
      if (/\bOVER\s*\(/i.test(sql.slice(Math.max(0, i - 8000), bodyEnd))) {
        const existing = new Set(kept.map((item) => normalizeGroupTerm(item)))
        const projectionText = findProjectionTextForGroup(i, depth)
        const syntheticAggRefs = Array.from(
          projectionText.matchAll(/(?:"[^"]+"|\b\w+\b)\s*\.\s*"__fg_agg\d+"/gi),
          (match) => String(match[0] ?? '').trim(),
        ).filter(Boolean)
        for (const ref of syntheticAggRefs) {
          const key = normalizeGroupTerm(ref)
          if (!key || existing.has(key) || !canSqlExpressionAppearInGroupBy(ref)) continue
          kept.push(ref)
          groupChanged = true
          existing.add(key)
        }
      }
      if (groupChanged) {
        edits.push({
          start: i,
          end: bodyEnd,
          newText: kept.length > 0 ? `GROUP BY ${kept.join(', ')}` : '',
        })
      }
      i = bodyEnd - 1
    }
    if (!edits.length) return sql
    edits.sort((a, b) => b.start - a.start)
    let out = sql
    for (const ed of edits) {
      const before = out.slice(0, ed.start).trimEnd()
      const after = out.slice(ed.end).trimStart()
      out = `${before}${ed.newText ? ` ${ed.newText}` : ''}${after ? ` ${after}` : ''}`
    }
    return out.replace(/\s+/g, ' ').trim()
  }

  const aggregateCallContainsWindowExpression = (exprRaw: string): boolean => {
    const expr = String(exprRaw ?? '')
    if (!expr || !/\bOVER\s*\(/i.test(expr)) return false
    const aggregateNames = new Set(['MIN', 'MAX', 'SUM', 'AVG', 'COUNT', 'ANY_VALUE', 'MEDIAN', 'STDDEV', 'VARIANCE'])
    const readWordBeforeParen = (openIdx: number): string => {
      let j = openIdx - 1
      while (j >= 0 && /\s/.test(expr[j])) j -= 1
      const end = j + 1
      while (j >= 0 && /[A-Za-z0-9_]/.test(expr[j])) j -= 1
      return expr.slice(j + 1, end).toUpperCase()
    }
    for (let i = 0; i < expr.length; i += 1) {
      if (expr[i] !== '(') continue
      const fn = readWordBeforeParen(i)
      if (!aggregateNames.has(fn)) continue
      let depth = 0
      let inStr = false
      let inIdent = false
      for (let j = i; j < expr.length; j += 1) {
        const ch = expr[j]
        if (inStr) {
          if (ch === "'" && expr[j + 1] === "'") { j += 1; continue }
          if (ch === "'") inStr = false
          continue
        }
        if (inIdent) {
          if (ch === '"' && expr[j + 1] === '"') { j += 1; continue }
          if (ch === '"') inIdent = false
          continue
        }
        if (ch === "'") { inStr = true; continue }
        if (ch === '"') { inIdent = true; continue }
        if (ch === '(') { depth += 1; continue }
        if (ch === ')') {
          depth -= 1
          if (depth === 0) {
            const body = expr.slice(i + 1, j)
            if (/\bOVER\s*\(/i.test(body)) return true
            i = j
            break
          }
        }
      }
    }
    return false
  }

  const pruneInvalidOrderTermsEverywhere = (sqlIn: string): string => {
    const sql = String(sqlIn ?? '')
    if (!/\bORDER\s+BY\b/i.test(sql) || !/\bOVER\s*\(/i.test(sql)) return sql
    type Edit = { start: number; end: number; newText: string }
    const edits: Edit[] = []
    const isWord = (c: string | undefined): boolean => Boolean(c && /[A-Za-z0-9_]/.test(c))
    const matchKeywordAt = (idx: number, keyword: string): boolean => {
      const needle = keyword.toUpperCase()
      const slice = sql.slice(idx, idx + needle.length).toUpperCase()
      if (slice !== needle) return false
      const prev = idx > 0 ? sql[idx - 1] : ''
      const next = idx + needle.length < sql.length ? sql[idx + needle.length] : ''
      return !isWord(prev) && !isWord(next)
    }
    const matchOrderByAt = (idx: number): { len: number } | null => {
      if (!matchKeywordAt(idx, 'ORDER')) return null
      let j = idx + 'ORDER'.length
      while (j < sql.length && /\s/.test(sql[j])) j += 1
      if (!matchKeywordAt(j, 'BY')) return null
      return { len: (j + 'BY'.length) - idx }
    }
    const findClauseEnd = (start: number, orderDepth: number): number => {
      let depth = orderDepth
      let inStr = false
      let inIdent = false
      const terminators = ['LIMIT', 'OFFSET', 'FETCH', 'UNION', 'INTERSECT', 'EXCEPT']
      for (let i = start; i < sql.length; i += 1) {
        const ch = sql[i]
        if (inStr) {
          if (ch === "'" && sql[i + 1] === "'") { i += 1; continue }
          if (ch === "'") inStr = false
          continue
        }
        if (inIdent) {
          if (ch === '"' && sql[i + 1] === '"') { i += 1; continue }
          if (ch === '"') inIdent = false
          continue
        }
        if (ch === "'") { inStr = true; continue }
        if (ch === '"') { inIdent = true; continue }
        if (ch === '(') { depth += 1; continue }
        if (ch === ')') {
          if (depth === orderDepth) return i
          depth = Math.max(orderDepth, depth - 1)
          continue
        }
        if (depth !== orderDepth) continue
        for (const kw of terminators) {
          if (matchKeywordAt(i, kw)) return i
        }
      }
      return sql.length
    }
    const functionNameBefore = (idx: number): string => {
      let j = idx - 1
      while (j >= 0 && /\s/.test(sql[j])) j -= 1
      const end = j + 1
      while (j >= 0 && /[A-Za-z0-9_]/.test(sql[j])) j -= 1
      return sql.slice(j + 1, end).toUpperCase()
    }

    let depth = 0
    let inStr = false
    let inIdent = false
    const parenFns: Array<{ depth: number; fn: string }> = []
    for (let i = 0; i < sql.length; i += 1) {
      const ch = sql[i]
      if (inStr) {
        if (ch === "'" && sql[i + 1] === "'") { i += 1; continue }
        if (ch === "'") inStr = false
        continue
      }
      if (inIdent) {
        if (ch === '"' && sql[i + 1] === '"') { i += 1; continue }
        if (ch === '"') inIdent = false
        continue
      }
      if (ch === "'") { inStr = true; continue }
      if (ch === '"') { inIdent = true; continue }
      if (ch === '(') {
        depth += 1
        parenFns.push({ depth, fn: functionNameBefore(i) })
        continue
      }
      if (ch === ')') {
        while (parenFns.length && parenFns[parenFns.length - 1]!.depth >= depth) parenFns.pop()
        depth = Math.max(0, depth - 1)
        continue
      }
      const orderBy = matchOrderByAt(i)
      if (!orderBy) continue
      const insideOverClause = parenFns.some((p) => p.fn === 'OVER')
      if (insideOverClause) {
        i += orderBy.len - 1
        continue
      }
      const bodyStart = i + orderBy.len
      const bodyEnd = findClauseEnd(bodyStart, depth)
      const clauseBody = sql.slice(bodyStart, bodyEnd).trim()
      if (!clauseBody) {
        i = bodyEnd - 1
        continue
      }
      const items = splitTopLevelCsvForFinalSanitizer(clauseBody)
      const kept = items.filter((item) => {
        const expr = stripOrderTermModifiersForFinalSanitizer(item)
        return !isNullishExpr(expr) &&
          !isScalarLiteralExpr(expr) &&
          !isSqlNullPlaceholderReference(expr) &&
          !aggregateCallContainsWindowExpression(expr) &&
          !/\bOVER\s*\(/i.test(expr)
      })
      if (kept.length !== items.length) {
        edits.push({
          start: i,
          end: bodyEnd,
          newText: kept.length > 0 ? `ORDER BY ${kept.join(', ')}` : '',
        })
      }
      i = bodyEnd - 1
    }
    if (!edits.length) return sql
    edits.sort((a, b) => b.start - a.start)
    let out = sql
    for (const ed of edits) {
      const before = out.slice(0, ed.start).trimEnd()
      const after = out.slice(ed.end).trimStart()
      out = `${before}${ed.newText ? ` ${ed.newText}` : ''}${after ? ` ${after}` : ''}`
    }
    return out.replace(/\s+/g, ' ').trim()
  }

  const rewriteTableauAttrFunctionCalls = (sqlIn: string): string => {
    const sql = String(sqlIn ?? '')
    if (!/\bATTR\s*\(/i.test(sql)) return sql
    type Edit = { start: number; end: number; newText: string }
    const edits: Edit[] = []
    const isWord = (c: string | undefined): boolean => Boolean(c && /[A-Za-z0-9_]/.test(c))
    const attrSql = (exprRaw: string): string => {
      const expr = String(exprRaw ?? '').trim() || 'NULL'
      return `CASE WHEN COUNT(DISTINCT CAST(${expr} AS VARCHAR)) = 1 THEN MIN(CAST(${expr} AS VARCHAR)) ELSE '*' END`
    }
    const findMatchingParen = (openIdx: number): number => {
      let depth = 0
      let inStr = false
      let inIdent = false
      for (let i = openIdx; i < sql.length; i += 1) {
        const ch = sql[i]
        if (inStr) {
          if (ch === "'" && sql[i + 1] === "'") { i += 1; continue }
          if (ch === "'") inStr = false
          continue
        }
        if (inIdent) {
          if (ch === '"' && sql[i + 1] === '"') { i += 1; continue }
          if (ch === '"') inIdent = false
          continue
        }
        if (ch === "'") { inStr = true; continue }
        if (ch === '"') { inIdent = true; continue }
        if (ch === '(') { depth += 1; continue }
        if (ch === ')') {
          depth -= 1
          if (depth === 0) return i
        }
      }
      return -1
    }
    const readFunctionCallAt = (idx: number, name: string): { open: number; close: number; body: string } | null => {
      const n = String(name || '').trim()
      if (!n) return null
      if (sql.slice(idx, idx + n.length).toUpperCase() !== n.toUpperCase()) return null
      const prev = idx > 0 ? sql[idx - 1] : ''
      const next = idx + n.length < sql.length ? sql[idx + n.length] : ''
      if (isWord(prev) || isWord(next)) return null
      let j = idx + n.length
      while (j < sql.length && /\s/.test(sql[j])) j += 1
      if (sql[j] !== '(') return null
      const close = findMatchingParen(j)
      if (close < 0) return null
      return { open: j, close, body: sql.slice(j + 1, close) }
    }
    const bodyIsSingleAttrCall = (bodyRaw: string): { arg: string } | null => {
      const body = String(bodyRaw ?? '')
      const leading = body.match(/^\s*/)?.[0]?.length ?? 0
      const fakeSql = body
      const idx = leading
      if (fakeSql.slice(idx, idx + 4).toUpperCase() !== 'ATTR') return null
      let j = idx + 4
      while (j < fakeSql.length && /\s/.test(fakeSql[j])) j += 1
      if (fakeSql[j] !== '(') return null
      let depth = 0
      let inStr = false
      let inIdent = false
      let close = -1
      for (let i = j; i < fakeSql.length; i += 1) {
        const ch = fakeSql[i]
        if (inStr) {
          if (ch === "'" && fakeSql[i + 1] === "'") { i += 1; continue }
          if (ch === "'") inStr = false
          continue
        }
        if (inIdent) {
          if (ch === '"' && fakeSql[i + 1] === '"') { i += 1; continue }
          if (ch === '"') inIdent = false
          continue
        }
        if (ch === "'") { inStr = true; continue }
        if (ch === '"') { inIdent = true; continue }
        if (ch === '(') { depth += 1; continue }
        if (ch === ')') {
          depth -= 1
          if (depth === 0) { close = i; break }
        }
      }
      if (close < 0) return null
      if (fakeSql.slice(close + 1).trim()) return null
      return { arg: fakeSql.slice(j + 1, close) }
    }

    let inStr = false
    let inIdent = false
    for (let i = 0; i < sql.length; i += 1) {
      const ch = sql[i]
      if (inStr) {
        if (ch === "'" && sql[i + 1] === "'") { i += 1; continue }
        if (ch === "'") inStr = false
        continue
      }
      if (inIdent) {
        if (ch === '"' && sql[i + 1] === '"') { i += 1; continue }
        if (ch === '"') inIdent = false
        continue
      }
      if (ch === "'") { inStr = true; continue }
      if (ch === '"') { inIdent = true; continue }

      const anyValue = readFunctionCallAt(i, 'ANY_VALUE')
      if (anyValue) {
        const attr = bodyIsSingleAttrCall(anyValue.body)
        if (attr) {
          edits.push({ start: i, end: anyValue.close + 1, newText: attrSql(attr.arg) })
          i = anyValue.close
          continue
        }
      }

      const attr = readFunctionCallAt(i, 'ATTR')
      if (attr) {
        edits.push({ start: i, end: attr.close + 1, newText: attrSql(attr.body) })
        i = attr.close
      }
    }
    if (!edits.length) return sql
    edits.sort((a, b) => b.start - a.start)
    let out = sql
    for (const ed of edits) out = out.slice(0, ed.start) + ed.newText + out.slice(ed.end)
    return out
  }

  const coerceUnaryMinusNumericStringLiterals = (sqlIn: string): string =>
    String(sqlIn ?? '').replace(
      /-\s*'([+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?)'/gi,
      (match, raw) => {
        const n = Number(String(raw ?? '').trim())
        return Number.isFinite(n) ? String(-n) : match
      },
    )

  const rewriteNumericDateLikeInPredicates = (sqlIn: string): string => {
    const parseNumericLiteralList = (rawList: string): number[] | null => {
      const items = splitTopLevelCsvForFinalSanitizer(rawList)
      if (items.length === 0) return null
      const out: number[] = []
      for (const itemRaw of items) {
        const item = String(itemRaw ?? '').trim()
        const quoted = item.match(/^'((?:''|[^'])*)'$/)
        const unwrapped = quoted ? quoted[1].replace(/''/g, "'") : item
        if (!/^\d+$/.test(unwrapped)) return null
        const num = Number(unwrapped)
        if (!Number.isFinite(num)) return null
        out.push(num)
      }
      return out
    }
    const looksDateLikeExpr = (exprRaw: string): boolean => {
      const expr = String(exprRaw ?? '')
      if (!expr) return false
      if (/\bDATE_TRUNC\s*\(/i.test(expr)) return true
      if (/\bTRY_CAST\s*\([\s\S]*\bAS\s+(?:TIMESTAMP|DATE)\b/i.test(expr)) return true
      if (/"[^"]*(?:date|time)[^"]*"/i.test(expr)) return true
      if (/\breportenddate\b/i.test(expr)) return true
      return false
    }
    const hasBalancedParentheses = (exprRaw: string): boolean => {
      const expr = String(exprRaw ?? '')
      let depth = 0
      let inSingle = false
      let inDouble = false
      for (let i = 0; i < expr.length; i++) {
        const ch = expr[i]
        if (inSingle) {
          if (ch === "'" && expr[i + 1] === "'") {
            i++
            continue
          }
          if (ch === "'") inSingle = false
          continue
        }
        if (inDouble) {
          if (ch === '"' && expr[i + 1] === '"') {
            i++
            continue
          }
          if (ch === '"') inDouble = false
          continue
        }
        if (ch === "'") {
          inSingle = true
          continue
        }
        if (ch === '"') {
          inDouble = true
          continue
        }
        if (ch === '(') depth++
        if (ch === ')') {
          depth--
          if (depth < 0) return false
        }
      }
      return depth === 0 && !inSingle && !inDouble
    }
    const maybeRewrite = (exprRaw: string, operatorRaw: string, listRaw: string): string | null => {
      const expr = String(exprRaw ?? '').trim()
      const operator = String(operatorRaw ?? '').trim().toUpperCase()
      const values = parseNumericLiteralList(listRaw)
      if (!expr || !operator || !values || values.length === 0) return null
      if (!hasBalancedParentheses(expr)) return null
      if (!looksDateLikeExpr(expr)) return null
      let rewrittenExpr: string | null = null
      if (values.every((value) => Number.isInteger(value) && value >= 1 && value <= 12)) {
        rewrittenExpr = `EXTRACT(MONTH FROM TRY_CAST(${expr} AS TIMESTAMP))`
      } else if (values.every((value) => Number.isInteger(value) && value >= 1000 && value <= 9999)) {
        rewrittenExpr = `EXTRACT(YEAR FROM TRY_CAST(${expr} AS TIMESTAMP))`
      } else if (values.every((value) => Number.isInteger(value) && value >= 100001 && value <= 999912)) {
        rewrittenExpr = `((EXTRACT(YEAR FROM TRY_CAST(${expr} AS TIMESTAMP)) * 100) + EXTRACT(MONTH FROM TRY_CAST(${expr} AS TIMESTAMP)))`
      }
      if (!rewrittenExpr) return null
      return `${rewrittenExpr} ${operator} (${values.join(', ')})`
    }

    let sql = String(sqlIn ?? '')
    const patterns = [
      /\((DATE_TRUNC\([\s\S]*?\))\)\s+(NOT\s+IN|IN)\s*\(([^)]+)\)/gi,
      /\(("(?:""|[^"])+?"(?:\s*\.\s*"(?:""|[^"])+?")?)\)\s+(NOT\s+IN|IN)\s*\(([^)]+)\)/gi,
    ]
    for (const pattern of patterns) {
      sql = sql.replace(pattern, (fullMatch, exprRaw, operatorRaw, listRaw) => {
        const rewritten = maybeRewrite(exprRaw, operatorRaw, listRaw)
        return rewritten || fullMatch
      })
    }
    return sql
  }

  const stripGroupedBaseDirectPassthroughAggregates = (sqlIn: string): string =>
    String(sqlIn ?? '').replace(
      /\b(?:ANY_VALUE|MIN|MAX)\s*\(\s*"__fg_grouped_base"\."([^"]+)"\s*\)\s+AS\s+"\1"/gi,
      (_, col: string) => `"__fg_grouped_base"."${String(col || '').trim()}" AS "${String(col || '').trim()}"`,
    )

  // Removes `ANY_VALUE(<expr>) AS "<alias>"` projection wrappers when `<expr>` is already
  // guaranteed by appearing verbatim in the same SELECT's GROUP BY list. DuckDB treats
  // grouping-key columns as deterministic per row, so `ANY_VALUE(t."k")` over a group-by
  // key is semantically equivalent to `t."k"` but costs an extra per-row aggregate in the
  // executor. Staged MNV / window / LOD rewrites add these wrappers defensively to keep
  // later projections aggregate-safe, but leaving them in place doubles per-column work
  // and bloats plan text on heterogeneous measure chunks (e.g. Monthly Comparison: 1.4M
  // rows × N deferred layers). Stripping them preserves correctness while shrinking the
  // compiled SQL and the spill budget.
  const stripRedundantAnyValueAroundGroupKeys = (sqlIn: string): string => {
    const src = String(sqlIn ?? '')
    if (!src || !/\bANY_VALUE\s*\(/i.test(src) || !/\bGROUP\s+BY\b/i.test(src)) return src
    const __fgDebug = Boolean(
      (typeof globalThis !== 'undefined' && (globalThis as any).__FG_DEBUG_STRIP_ANY_VALUE__) ||
      (typeof process !== 'undefined' && (process as any)?.env?.FG_DEBUG_STRIP_ANY_VALUE)
    )
    type MarkerKind =
      | 'SELECT'
      | 'FROM'
      | 'WHERE'
      | 'GROUP_BY'
      | 'HAVING'
      | 'ORDER_BY'
      | 'LIMIT'
      | 'UNION'
      | 'LPAREN'
      | 'RPAREN'
    const markers: Array<{ pos: number; endPos: number; depth: number; kind: MarkerKind }> = []
    {
      let depth = 0
      let inStr = false
      let inIdent = false
      const len = src.length
      for (let i = 0; i < len; i += 1) {
        const ch = src[i]
        if (inStr) {
          if (ch === "'" && src[i + 1] === "'") { i += 1; continue }
          if (ch === "'") inStr = false
          continue
        }
        if (inIdent) {
          if (ch === '"' && src[i + 1] === '"') { i += 1; continue }
          if (ch === '"') inIdent = false
          continue
        }
        if (ch === "'") { inStr = true; continue }
        if (ch === '"') { inIdent = true; continue }
        if (ch === '(') {
          depth += 1
          markers.push({ pos: i, endPos: i + 1, depth, kind: 'LPAREN' })
          continue
        }
        if (ch === ')') {
          markers.push({ pos: i, endPos: i + 1, depth, kind: 'RPAREN' })
          depth = Math.max(0, depth - 1)
          continue
        }
        const prev = i > 0 ? src[i - 1] : ''
        if (prev && /[A-Za-z0-9_]/.test(prev)) continue
        const rest = src.slice(i, i + 12)
        const m = /^(SELECT|FROM|WHERE|GROUP\s+BY|HAVING|ORDER\s+BY|LIMIT|UNION\s+ALL|UNION)\b/i.exec(rest)
        if (!m) continue
        const endIdx = i + m[0].length
        const after = endIdx < len ? src[endIdx] : ''
        if (after && /[A-Za-z0-9_]/.test(after)) continue
        const kwUp = m[1].toUpperCase().replace(/\s+/g, '_')
        const kindMap: Record<string, MarkerKind> = {
          SELECT: 'SELECT',
          FROM: 'FROM',
          WHERE: 'WHERE',
          GROUP_BY: 'GROUP_BY',
          HAVING: 'HAVING',
          ORDER_BY: 'ORDER_BY',
          LIMIT: 'LIMIT',
          UNION: 'UNION',
          UNION_ALL: 'UNION',
        }
        const kind = kindMap[kwUp]
        if (kind) markers.push({ pos: i, endPos: endIdx, depth, kind })
        i = endIdx - 1
      }
    }
    const len = src.length
    const edits: Array<{ start: number; end: number; newText: string }> = []
    for (let mi = 0; mi < markers.length; mi += 1) {
      const mk = markers[mi]
      if (mk.kind !== 'GROUP_BY') continue
      let selectIdx = -1
      let fromIdx = -1
      for (let j = mi - 1; j >= 0; j -= 1) {
        const mm = markers[j]
        if (mm.depth < mk.depth) break
        if (mm.depth !== mk.depth) continue
        if (mm.kind === 'FROM' && fromIdx < 0) { fromIdx = j; continue }
        if (mm.kind === 'SELECT') { selectIdx = j; break }
      }
      if (selectIdx < 0 || fromIdx < 0) continue
      let groupEnd = len
      for (let j = mi + 1; j < markers.length; j += 1) {
        const mm = markers[j]
        if (mm.depth < mk.depth) { groupEnd = mm.pos; break }
        if (mm.depth === mk.depth) {
          if (
            mm.kind === 'HAVING' ||
            mm.kind === 'ORDER_BY' ||
            mm.kind === 'LIMIT' ||
            mm.kind === 'UNION' ||
            mm.kind === 'RPAREN'
          ) { groupEnd = mm.pos; break }
        }
      }
      const projectionStart = markers[selectIdx].endPos
      const projectionEnd = markers[fromIdx].pos
      const groupByStart = mk.endPos
      const projectionText = src.slice(projectionStart, projectionEnd)
      const groupByText = src.slice(groupByStart, groupEnd)
      if (/\bGROUPING\s+SETS\b/i.test(groupByText)) continue
      if (/\bROLLUP\b|\bCUBE\b/i.test(groupByText)) continue
      const groupExprs = splitTopLevelCsvForFinalSanitizer(groupByText)
        .map((g) => String(g || '').trim())
        .filter(Boolean)
      if (groupExprs.length === 0) continue
      // Normalize forms of a column reference so `"t"."col"`, `t."col"`, `"col"` all
      // match a GROUP BY list entry written in any of those shapes. Strips any
      // single leading <alias>. prefix (either a simple identifier or a quoted
      // identifier) and collapses whitespace.
      const normalizeColumnRef = (exprRaw: string): string => {
        const s = String(exprRaw || '').trim().replace(/\s+/g, '')
        return s.replace(/^(?:[A-Za-z_][A-Za-z0-9_]*|"(?:""|[^"])+")\./, '')
      }
      const groupExprSet = new Set<string>()
      for (const g of groupExprs) {
        const compact = g.replace(/\s+/g, '')
        groupExprSet.add(compact)
        const stripped = normalizeColumnRef(g)
        if (stripped) groupExprSet.add(stripped)
      }
      const projItems = splitTopLevelCsvForFinalSanitizer(projectionText)
      if (projItems.length === 0) continue
      let changed = false
      const rewrittenItems = projItems.map((item) => {
        const raw = String(item || '')
        const leadingMatch = /^(\s*)/.exec(raw)
        const leading = leadingMatch ? leadingMatch[1] : ''
        const body = raw.slice(leading.length)
        if (!/^ANY_VALUE\s*\(/i.test(body)) return raw
        const openParenIdx = body.indexOf('(')
        if (openParenIdx < 0) return raw
        let d = 0
        let ident = false
        let str = false
        let closeParenIdx = -1
        for (let k = openParenIdx; k < body.length; k += 1) {
          const ck = body[k]
          if (str) {
            if (ck === "'" && body[k + 1] === "'") { k += 1; continue }
            if (ck === "'") str = false
            continue
          }
          if (ident) {
            if (ck === '"' && body[k + 1] === '"') { k += 1; continue }
            if (ck === '"') ident = false
            continue
          }
          if (ck === "'") { str = true; continue }
          if (ck === '"') { ident = true; continue }
          if (ck === '(') { d += 1; continue }
          if (ck === ')') {
            d -= 1
            if (d === 0) { closeParenIdx = k; break }
          }
        }
        if (closeParenIdx < 0) return raw
        const innerExpr = body.slice(openParenIdx + 1, closeParenIdx).trim()
        const afterClose = body.slice(closeParenIdx + 1).trim()
        const asMatch = /^AS\s+("[^"]+"|[A-Za-z_][A-Za-z0-9_]*)\s*$/i.exec(afterClose)
        if (!asMatch) return raw
        const aliasPart = asMatch[1]
        const innerCompact = innerExpr.replace(/\s+/g, '')
        const innerStripped = normalizeColumnRef(innerExpr)
        if (!groupExprSet.has(innerCompact) && !groupExprSet.has(innerStripped)) return raw
        changed = true
        return `${leading}${innerExpr} AS ${aliasPart}`
      })
      if (changed) {
        const newProjection = rewrittenItems.join(', ')
        // The original projection text includes whitespace that buffers the SELECT keyword
        // from the first item and the final item from FROM. `splitTopLevelCsvForFinalSanitizer`
        // trims each split piece, so recombining without preserving outer spaces would
        // produce `SELECTt.` or `...colFROM` on some inputs. Re-insert a single leading +
        // trailing space when the surrounding characters aren't whitespace already.
        const origProjection = src.slice(projectionStart, projectionEnd)
        const leadMatch = /^(\s+)/.exec(origProjection)
        const tailMatch = /(\s+)$/.exec(origProjection)
        const safeLead = leadMatch ? leadMatch[1] : ' '
        const safeTail = tailMatch ? tailMatch[1] : ' '
        edits.push({ start: projectionStart, end: projectionEnd, newText: `${safeLead}${newProjection}${safeTail}` })
      }
    }
    if (__fgDebug) {
      try {
        console.log('[FG-STRIP-AV-DEBUG]', JSON.stringify({
          markers: markers.length,
          edits: edits.length,
          srcLen: src.length,
        }))
      } catch {}
    }
    if (edits.length === 0) return src
    edits.sort((a, b) => b.start - a.start)
    let out = src
    for (const ed of edits) {
      out = out.slice(0, ed.start) + ed.newText + out.slice(ed.end)
    }
    return out
  }

  const finalizeSql = (sql: string): string =>
    reportUnresolved(
      (() => {
        const literalCoerced = coerceDateTruncLiteralAnchors(sql)
        const diffCoerced = coerceDateLiteralArithmetic(
          coerceDateDiffOperands(
            literalCoerced
          )
        )
        const lodRewritten = rewriteLodWindowsForGroupedQuery(diffCoerced)
        const hasFactoredWindowCorrelates =
          /\b__fg_win_corr\b/i.test(lodRewritten) || /\b__fg_win_src_\d+\b/i.test(lodRewritten)
        // Advanced/window-factored SQL is already normalized. Re-running grouped nested-window
        // rewrite here can degrade correlated simple-window subqueries back to stale `t.__fg_*`
        // references.
        const nestedRewritten = hasFactoredWindowCorrelates
          ? lodRewritten
          : rewriteNestedWindowInGroupedSelectCached(lodRewritten)
        const normalized = rewriteNumericDateLikeInPredicates(
          pruneInvalidGroupTermsEverywhere(
            pruneInvalidOrderTermsEverywhere(
              pruneNullishTopLevelGroupOrderTerms(
                scrubUnresolvedFieldRefs(
                  stripRedundantAnyValueAroundGroupKeys(
                    stripGroupedBaseDirectPassthroughAggregates(
                      sanitizeNullArtifacts(
                        coerceAggregateWindowOrderColumns(
                          coerceUnaryMinusNumericStringLiterals(
                            rewriteTableauAttrFunctionCalls(nestedRewritten)
                          )
                        )
                      )
                    )
                  )
                )
              )
            )
          )
        )
        // Tableau special fields sometimes appear with a leading ':' token.
        // Normalize quoted identifiers so downstream clauses refer to the same column.
        return normalized
          .replaceAll('":Measure Names"', '"Measure Names"')
          .replaceAll('":Measure Values"', '"Measure Values"')
          // General form: strip leading ':' inside quoted identifiers.
          .replace(/"\s*:/g, '"')
      })()
    )

  function rewriteNestedWindowInGroupedSelect(sqlIn: string, _rwDepth = 0): string {
    let sql = String(sqlIn ?? '')
    if (skipNestedWindowRewrite) return sql
    // Fast path: no window clauses means nothing to de-nest.
    // Support both forms:
    // - OVER (...)
    // - OVER window_name
    if (!/\bover\b/i.test(sql)) return sql
    if (traceUnresolved && /__lod_\d+/.test(sql)) {
      console.log(`[windowRewrite] ENTRY depth=${_rwDepth} — SQL contains __lod refs. Length=${sql.length}`)
    }
    const traceRewrite = traceUnresolved
    const traceTarget: any = (() => {
      try { return globalThis as any } catch { return {} as any }
    })()
    const noteRewrite = (patch: Record<string, any>) => {
      if (!traceRewrite) return
      try {
        const prev = (traceTarget.__FG_WINDOW_REWRITE_STATS__ && typeof traceTarget.__FG_WINDOW_REWRITE_STATS__ === 'object')
          ? traceTarget.__FG_WINDOW_REWRITE_STATS__
          : {}
        const next: any = { ...prev, ...patch, ts: Date.now() }
        if (typeof prev.calls === 'number' && typeof patch.calls !== 'number') {
          // preserve caller-controlled increment semantics below
          next.calls = prev.calls
        }
        if (typeof patch.hasWindowItems === 'boolean') {
          next.sawWindowItems = Boolean(prev.sawWindowItems) || patch.hasWindowItems
        }
        if (typeof patch.windowItemCount === 'number') {
          next.maxWindowItemCount = Math.max(Number(prev.maxWindowItemCount || 0), patch.windowItemCount)
        }
        if (typeof patch.innerExtraCount === 'number') {
          next.maxInnerExtraCount = Math.max(Number(prev.maxInnerExtraCount || 0), patch.innerExtraCount)
        }
        traceTarget.__FG_WINDOW_REWRITE_STATS__ = next
      } catch { }
    }
    const isWordChar = (c: string | undefined): boolean => Boolean(c && /[A-Za-z0-9_]/.test(c))
    const hasSetOp = (s: string, topLevelOnly: boolean): boolean => {
      let depth = 0
      let inStr = false
      let inIdent = false
      const kws = ['UNION', 'INTERSECT', 'EXCEPT']
      for (let i = 0; i < s.length; i++) {
        const ch = s[i]
        if (ch === "'") {
          if (inStr && s[i + 1] === "'") { i++; continue }
          inStr = !inStr
          continue
        }
        if (!inStr && ch === '"') {
          if (inIdent && s[i + 1] === '"') { i++; continue }
          inIdent = !inIdent
          continue
        }
        if (inStr || inIdent) continue
        if (ch === '(') { depth++; continue }
        if (ch === ')') { depth = Math.max(0, depth - 1); continue }
        if (topLevelOnly && depth !== 0) continue
        for (const kw of kws) {
          if (!s.slice(i, i + kw.length).toUpperCase().startsWith(kw)) continue
          const before = s[i - 1]
          const after = s[i + kw.length]
          if (isWordChar(before) || isWordChar(after)) continue
          return true
        }
      }
      return false
    }
    const hasTopLevelSetOp = (s: string): boolean => hasSetOp(s, true)
    const hasAnySetOp = (s: string): boolean => hasSetOp(s, false)
    const splitTopLevelCsv = (src: string): string[] => {
      const out: string[] = []
      let buf = ''
      let depth = 0
      let inStr = false
      let inIdent = false
      for (let i = 0; i < src.length; i++) {
        const ch = src[i]
        if (inStr) {
          if (ch === "'" && src[i + 1] === "'") { buf += "''"; i++; continue }
          if (ch === "'") inStr = false
          buf += ch
          continue
        }
        if (inIdent) {
          if (ch === '"' && src[i + 1] === '"') { buf += '""'; i++; continue }
          if (ch === '"') inIdent = false
          buf += ch
          continue
        }
        if (ch === "'") { inStr = true; buf += ch; continue }
        if (ch === '"') { inIdent = true; buf += ch; continue }
        if (ch === '(') { depth++; buf += ch; continue }
        if (ch === ')') { depth = Math.max(0, depth - 1); buf += ch; continue }
        if (ch === ',' && depth === 0) {
          const item = buf.trim()
          if (item) out.push(item)
          buf = ''
          continue
        }
        buf += ch
      }
      const tail = buf.trim()
      if (tail) out.push(tail)
      return out
    }
    const findMatchingParen = (src: string, openIdx: number): number => {
      if (openIdx < 0 || openIdx >= src.length || src[openIdx] !== '(') return -1
      let depth = 0
      let inStr = false
      let inIdent = false
      for (let i = openIdx; i < src.length; i++) {
        const ch = src[i]
        if (inStr) {
          if (ch === "'" && src[i + 1] === "'") { i++; continue }
          if (ch === "'") inStr = false
          continue
        }
        if (inIdent) {
          if (ch === '"' && src[i + 1] === '"') { i++; continue }
          if (ch === '"') inIdent = false
          continue
        }
        if (ch === "'") { inStr = true; continue }
        if (ch === '"') { inIdent = true; continue }
        if (ch === '(') depth++
        else if (ch === ')') {
          depth--
          if (depth === 0) return i
        }
      }
      return -1
    }
    const dedupeGroupingSetsTuples = (input: string): { sql: string; sawGroupingSets: boolean; allSingleSet: boolean } => {
      const marker = 'GROUP BY GROUPING SETS'
      const markerLen = marker.length
      let cursor = 0
      let out = ''
      let sawGroupingSets = false
      let allSingleSet = true
      while (cursor < input.length) {
        const up = input.toUpperCase()
        const idx = up.indexOf(marker, cursor)
        if (idx < 0) {
          out += input.slice(cursor)
          break
        }
        let open = idx + markerLen
        while (open < input.length && /\s/.test(input[open])) open++
        if (open >= input.length || input[open] !== '(') {
          out += input.slice(cursor, idx + markerLen)
          cursor = idx + markerLen
          continue
        }
        const close = findMatchingParen(input, open)
        if (close < 0) {
          out += input.slice(cursor)
          break
        }
        sawGroupingSets = true
        const rawSets = input.slice(open + 1, close)
        const setItems = splitTopLevelCsv(rawSets)
        const dedupedSets: string[] = []
        const seenSet = new Set<string>()
        for (const rawSet of setItems) {
          const trimmed = String(rawSet || '').trim()
          if (!trimmed) continue
          if (trimmed === '()') {
            if (!seenSet.has('()')) {
              seenSet.add('()')
              dedupedSets.push('()')
            }
            continue
          }
          if (!(trimmed.startsWith('(') && trimmed.endsWith(')'))) {
            const key = trimmed.replace(/\s+/g, ' ').trim().toLowerCase()
            if (!seenSet.has(key)) {
              seenSet.add(key)
              dedupedSets.push(trimmed)
            }
            continue
          }
          const inner = trimmed.slice(1, -1)
          const innerItems = splitTopLevelCsv(inner)
          const seenExpr = new Set<string>()
          const dedupedExprs: string[] = []
          for (const exprRaw of innerItems) {
            const expr = String(exprRaw || '').trim()
            if (!expr) continue
            const key = expr.replace(/\s+/g, ' ').trim().toLowerCase()
            if (seenExpr.has(key)) continue
            seenExpr.add(key)
            dedupedExprs.push(expr)
          }
          const rebuilt = `(${dedupedExprs.join(', ')})`
          const setKey = rebuilt.replace(/\s+/g, ' ').trim().toLowerCase()
          if (seenSet.has(setKey)) continue
          seenSet.add(setKey)
          dedupedSets.push(rebuilt)
        }
        if (dedupedSets.length !== 1) {
          allSingleSet = false
        }
        const rewritten = dedupedSets.length > 0 ? dedupedSets.join(', ') : rawSets
        out += input.slice(cursor, open + 1)
        out += rewritten
        out += input.slice(close, close + 1)
        cursor = close + 1
      }
      return { sql: out, sawGroupingSets, allSingleSet: sawGroupingSets && allSingleSet }
    }
    sql = rewriteNestedSubqueries(sql)
    // Keep the current SELECT scope stable when it contains set operations. Rewriting the
    // scope itself across UNION/INTERSECT/EXCEPT can change branch column shapes and break
    // binder alignment, but recursive subquery rewrites above are still safe and necessary.
    if (hasTopLevelSetOp(sql)) return sql
    if (traceRewrite) {
      noteRewrite({ calls: ((traceTarget.__FG_WINDOW_REWRITE_STATS__?.calls ?? 0) + 1) })
    }
    sql = sql.replace(/\bGROUP\s+BY\s+GROUPING\s+SETS\b/ig, 'GROUP BY GROUPING SETS')
    const groupingSetsNormalized = dedupeGroupingSetsTuples(sql)
    sql = groupingSetsNormalized.sql
    if (groupingSetsNormalized.sawGroupingSets && groupingSetsNormalized.allSingleSet) {
      // Single grouping-set queries are semantically equivalent to plain GROUP BY.
      // Replace GROUPING(...) probes with 0 so date-dimension CASE expressions do not
      // trigger binder ambiguity in DuckDB when no subtotal/grand-total rows exist.
      sql = sql.replace(/\bGROUPING\s*\([^)]*\)/ig, '0')
    }

    const findTopLevelFrom = (s: string): number => {
      let depth = 0
      let inStr = false
      let inIdent = false
      for (let i = 0; i < s.length; i++) {
        const ch = s[i]
        if (ch === "'") {
          if (inStr && s[i + 1] === "'") {
            i++
            continue
          }
          inStr = !inStr
          continue
        }
        if (!inStr && ch === '"') {
          if (inIdent && s[i + 1] === '"') {
            i++
            continue
          }
          inIdent = !inIdent
          continue
        }
        if (inStr || inIdent) continue
        if (ch === '(') depth++
        else if (ch === ')') depth = Math.max(0, depth - 1)
        if (depth !== 0) continue
        if (s.startsWith('FROM', i) || s.startsWith('from', i) || s.startsWith('From', i)) {
          const before = s[i - 1]
          const after = s[i + 4]
          if ((before && isWordChar(before)) || (after && isWordChar(after))) continue
          return i
        }
      }
      return -1
    }
    const findTopLevelSelect = (s: string): number => {
      let depth = 0
      let inStr = false
      let inIdent = false
      for (let i = 0; i < s.length; i++) {
        const ch = s[i]
        if (ch === "'") {
          if (inStr && s[i + 1] === "'") {
            i++
            continue
          }
          inStr = !inStr
          continue
        }
        if (!inStr && ch === '"') {
          if (inIdent && s[i + 1] === '"') {
            i++
            continue
          }
          inIdent = !inIdent
          continue
        }
        if (inStr || inIdent) continue
        if (ch === '(') depth++
        else if (ch === ')') depth = Math.max(0, depth - 1)
        if (depth !== 0) continue
        if (s.startsWith('SELECT', i) || s.startsWith('select', i) || s.startsWith('Select', i)) {
          const before = s[i - 1]
          const after = s[i + 6]
          if ((before && isWordChar(before)) || (after && isWordChar(after))) continue
          return i
        }
      }
      return -1
    }

    const splitSelectList = (s: string): string[] => {
      const out: string[] = []
      let depth = 0
      let inStr = false
      let inIdent = false
      let start = 0
      for (let i = 0; i < s.length; i++) {
        const ch = s[i]
        if (ch === "'") {
          if (inStr && s[i + 1] === "'") {
            i++
            continue
          }
          inStr = !inStr
          continue
        }
        if (!inStr && ch === '"') {
          if (inIdent && s[i + 1] === '"') {
            i++
            continue
          }
          inIdent = !inIdent
          continue
        }
        if (inStr || inIdent) continue
        if (ch === '(') depth++
        else if (ch === ')') depth = Math.max(0, depth - 1)
        if (depth === 0 && ch === ',') {
          out.push(s.slice(start, i).trim())
          start = i + 1
        }
      }
      const tail = s.slice(start).trim()
      if (tail) out.push(tail)
      return out
    }

    const parseSelectItem = (item: string, idx: number): { expr: string; alias: string; hasAlias: boolean } => {
      const asMatch = item.match(/\s+AS\s+("[^"]+"|[A-Za-z_][\w$]*)\s*$/i)
      if (asMatch && asMatch.index !== undefined) {
        return { expr: item.slice(0, asMatch.index).trim(), alias: asMatch[1], hasAlias: true }
      }
      const tailMatch = item.match(/\s+("[^"]+"|[A-Za-z_][\w$]*)\s*$/)
      if (tailMatch && tailMatch.index !== undefined) {
        return { expr: item.slice(0, tailMatch.index).trim(), alias: tailMatch[1], hasAlias: true }
      }
      const expr = item.trim()
      // Preserve direct source column names for unaliased simple refs so downstream
      // GROUP BY / ORDER BY refs (which often use the original column name) keep binding.
      const directQuoted = expr.match(/^(?:"t"|t)\s*\.\s*"([^"]+)"$/i)
      if (directQuoted && String(directQuoted[1] ?? '').trim()) {
        return { expr, alias: quoteIdent(String(directQuoted[1]).trim()), hasAlias: false }
      }
      const directBare = expr.match(/^(?:"t"|t)\s*\.\s*([A-Za-z_][A-Za-z0-9_]*)$/i)
      if (directBare && String(directBare[1] ?? '').trim()) {
        return { expr, alias: quoteIdent(String(directBare[1]).trim()), hasAlias: false }
      }
      return { expr, alias: quoteIdent(`__fg_expr_${idx}`), hasAlias: false }
    }

    function findWindowCalls(s: string): Array<{ start: number; end: number; full: string }> {
      return __test_findWindowCalls(s)
    }
    function hasWindowCallExpr(exprRaw: string): boolean {
      const expr = String(exprRaw || '')
      if (!/\bover\b/i.test(expr)) return false
      return findWindowCalls(expr).length > 0
    }

    const findAggregateCallSpans = (s: string): Array<{ start: number; end: number; full: string }> => {
      const out: Array<{ start: number; end: number; full: string }> = []
      // Fast path: skip full parser when no aggregate call tokens exist.
      if (!/\b(?:sum|avg|min|max|count)\s*\(/i.test(s)) return out
      const seen = new Set<string>()
      const up = s.toUpperCase()
      const fns = ['SUM', 'AVG', 'MIN', 'MAX', 'COUNT']
      let inStr = false
      for (let i = 0; i < s.length; i++) {
        const ch = s[i]
        if (ch === "'") {
          if (inStr && s[i + 1] === "'") {
            i++
            continue
          }
          inStr = !inStr
          continue
        }
        if (inStr) continue
        for (const fn of fns) {
          if (!up.startsWith(fn, i)) continue
          const before = s[i - 1]
          const after = s[i + fn.length]
          if ((before && isWordChar(before)) || (after && isWordChar(after))) continue
          let j = i + fn.length
          while (j < s.length && /\s/.test(s[j])) j++
          if (s[j] !== '(') continue
          let depth = 0
          let k = j
          let inStr2 = false
          for (; k < s.length; k++) {
            const c = s[k]
            if (c === "'") {
              if (inStr2 && s[k + 1] === "'") {
                k++
                continue
              }
              inStr2 = !inStr2
              continue
            }
            if (inStr2) continue
            if (c === '(') depth++
            else if (c === ')') {
              depth--
              if (depth === 0) break
            }
          }
          if (k >= s.length) continue
          let t = k + 1
          while (t < s.length && /\s/.test(s[t])) t++
          const full = s.slice(i, k + 1)
          if (!seen.has(full)) {
            seen.add(full)
            out.push({ start: i, end: k + 1, full })
          }
          // Keep scanning through the aggregate body so nested aggregates like
          // `MAX(SUM(...)) OVER (...)` surface alongside the outer span.
          break
        }
      }
      return out
    }

    const selectIdx = findTopLevelSelect(sql)
    if (selectIdx < 0) return sql
    const fromIdx = findTopLevelFrom(sql)
    if (fromIdx < 0) return sql
    const selectBodyRaw = sql.slice(selectIdx + 6, fromIdx)
    const tail = sql.slice(fromIdx).trim()
    if (!selectBodyRaw || !tail) return sql
    const selectBodyTrim = selectBodyRaw.trim()
    let selectPrefix = ''
    let selectListRaw = selectBodyTrim
    const prefixMatch = selectBodyTrim.match(/^(DISTINCT|ALL)\b/i)
    if (prefixMatch) {
      selectPrefix = prefixMatch[0].toUpperCase()
      selectListRaw = selectBodyTrim.slice(prefixMatch[0].length).trimStart()
    }
    if (!selectListRaw) return sql

    const itemsRaw = splitSelectList(selectListRaw)
    let items = itemsRaw.map((it, i) => parseSelectItem(it, i))
    function rewriteNestedSubqueries(input: string): string {
      const hasSubquery = /\(\s*(select|with)\b/i.test(input)
      if (!hasSubquery) return input
      // Fast single-pass parenthesis matcher to find "(select ...)" regions.
      // This avoids O(n^2) behavior on large SQL strings.
      const isWs = (c: string) => c === ' ' || c === '\n' || c === '\t' || c === '\r' || c === '\f'
      const ranges: Array<{ start: number; end: number }> = []
      const stack: number[] = []
      let inStr = false
      let inIdent = false
      for (let i = 0; i < input.length; i++) {
        if ((i & 0x3fff) === 0) guard('rewrite-nested-windows:scan')
        const ch = input[i]
        if (inStr) {
          if (ch === "'" && input[i + 1] === "'") {
            i++
            continue
          }
          if (ch === "'") inStr = false
          continue
        }
        if (inIdent) {
          if (ch === '"' && input[i + 1] === '"') {
            i++
            continue
          }
          if (ch === '"') inIdent = false
          continue
        }
        if (ch === "'") {
          inStr = true
          continue
        }
        if (ch === '"') {
          inIdent = true
          continue
        }
        if (ch === '(') {
          stack.push(i)
          continue
        }
        if (ch === ')') {
          const start = stack.pop()
          if (start === undefined) continue
          // Does the paren content start with SELECT?
            let j = start + 1
            while (j < i && isWs(input[j])) j++
            const isSelect = j + 6 <= i && input.slice(j, j + 6).toUpperCase() === 'SELECT'
            const isWith = j + 4 <= i && input.slice(j, j + 4).toUpperCase() === 'WITH'
            if (isSelect || isWith) {
            ranges.push({ start, end: i })
          }
          continue
        }
      }
      if (!ranges.length) return input
      // Important: avoid rewriting nested ranges in the same pass.
      // Rewriting an inner range can change string length and invalidate
      // precomputed indices for its enclosing range, which can corrupt SQL
      // by splicing replacement text into unrelated tokens.
      // We rewrite only outermost ranges here; recursion handles nested ones.
      const sorted = ranges.slice().sort((a, b) => {
        if (a.start !== b.start) return a.start - b.start
        return b.end - a.end
      })
      const outerRanges: Array<{ start: number; end: number }> = []
      for (const r of sorted) {
        const prev = outerRanges[outerRanges.length - 1]
        if (prev && r.start >= prev.start && r.end <= prev.end) continue
        outerRanges.push(r)
      }

      let out = input
      let changed = false
      for (let idx = outerRanges.length - 1; idx >= 0; idx--) {
        guard('rewrite-nested-windows:rewrite-subqueries')
        const r = outerRanges[idx]
        const inner = out.slice(r.start + 1, r.end)
        if (!hasWindowCallExpr(inner)) continue
        const rewritten = rewriteNestedWindowInGroupedSelectCached(inner, _rwDepth + 1)
        if (rewritten !== inner) {
          out = `${out.slice(0, r.start + 1)}${rewritten}${out.slice(r.end)}`
          changed = true
        }
      }
      return changed ? out : input
    }

    const innerExtraMap = new Map<string, { expr: string; alias: string; kind: 'win' | 'agg' | 'ord' }>()
    const precomputedAliasSet = new Set<string>()
    const nextSyntheticAliasIndex = (kind: 'win' | 'agg' | 'ord'): number => {
      const re =
        kind === 'win'
          ? /\b__fg_w(\d+)\b/ig
          : (kind === 'agg'
            ? /\b__fg_agg(\d+)\b/ig
            : /\b__fg_ord(\d+)\b/ig)
      let maxIdx = -1
      let match: RegExpExecArray | null
      while ((match = re.exec(sql))) {
        const idx = Number(match[1] ?? '')
        if (Number.isFinite(idx)) maxIdx = Math.max(maxIdx, idx)
      }
      return maxIdx + 1
    }
    // Nested rewrite passes can revisit subqueries that were only partially normalized earlier.
    // Seed counters from existing synthetic aliases so subsequent lifts do not collide.
    let winIdx = nextSyntheticAliasIndex('win')
    let aggIdx = nextSyntheticAliasIndex('agg')
    let ordIdx = nextSyntheticAliasIndex('ord')

    const normalizeAliasName = (raw: string): string => {
      const s = String(raw ?? '').trim()
      const unquoted = s.startsWith('"') && s.endsWith('"') ? s.slice(1, -1) : s
      return unquoted.trim().toLowerCase()
    }
    const stripOuterParens = (input: string): string => {
      let s = String(input ?? '').trim()
      const unwrapOnce = (raw: string): string | null => {
        if (!(raw.startsWith('(') && raw.endsWith(')'))) return null
        let depth = 0
        let inStr = false
        let inIdent = false
        for (let i = 0; i < raw.length; i++) {
          const ch = raw[i]
          if (ch === "'" && !inIdent) {
            if (inStr && raw[i + 1] === "'") {
              i++
              continue
            }
            inStr = !inStr
            continue
          }
          if (!inStr && ch === '"') {
            if (inIdent && raw[i + 1] === '"') {
              i++
              continue
            }
            inIdent = !inIdent
            continue
          }
          if (inStr || inIdent) continue
          if (ch === '(') depth++
          else if (ch === ')') {
            depth--
            if (depth === 0 && i < raw.length - 1) return null
          }
          if (depth < 0) return null
        }
        if (depth !== 0) return null
        return raw.slice(1, -1).trim()
      }
      for (let i = 0; i < 6; i++) {
        const next = unwrapOnce(s)
        if (!next || next === s) break
        s = next
      }
      return s
    }
    const deriveImplicitAlias = (expr: string): string => {
      const s = String(expr ?? '').trim()
      if (!s) return ''
      const core = stripOuterParens(s)
      let m = core.match(/^"([^"]+)"$/)
      if (m) return m[1]
      m = core.match(/^(?:"t"|t)\s*\.\s*"([^"]+)"$/)
      if (m) return m[1]
      m = core.match(/^(?:"t"|t)\s*\.\s*([A-Za-z_][A-Za-z0-9_]*)$/)
      if (m) return m[1]
      m = core.match(/^([A-Za-z_][A-Za-z0-9_]*)$/)
      if (m) return m[1]
      return ''
    }
    const qualifySimpleColumnExpr = (expr: string): string => {
      const s = String(expr ?? '').trim()
      if (!s) return s
      const core = stripOuterParens(s)
      let m = core.match(/^(?:"t"|t)\s*\.\s*"([^"]+)"$/)
      if (m) return `t.${quoteIdent(m[1])}`
      m = core.match(/^(?:"t"|t)\s*\.\s*([A-Za-z_][A-Za-z0-9_]*)$/)
      if (m) return `t.${quoteIdent(m[1])}`
      m = core.match(/^"([^"]+)"$/)
      if (m) return quoteIdent(m[1])
      m = core.match(/^([A-Za-z_][A-Za-z0-9_]*)$/)
      if (m) return quoteIdent(m[1])
      return s
    }
    const collectColumnRefs = (s: string): Array<{ ref: string; col: string }> => {
      const out: Array<{ ref: string; col: string }> = []
      const seen = new Set<string>()
      const aliasForms = ['"t"', 't']
      let inStr = false
      for (let i = 0; i < s.length; i++) {
        const ch = s[i]
        if (ch === "'") {
          if (inStr && s[i + 1] === "'") {
            i++
            continue
          }
          inStr = !inStr
          continue
        }
        if (inStr) continue
        let matched: string | null = null
        for (const form of aliasForms) {
          if (s.startsWith(form, i)) {
            const before = s[i - 1]
            if (before && isWordChar(before)) continue
            matched = form
            break
          }
        }
        if (!matched) continue
        let j = i + matched.length
        while (j < s.length && /\s/.test(s[j])) j++
        if (s[j] !== '.') continue
        j++
        while (j < s.length && /\s/.test(s[j])) j++
        let col = ''
        if (s[j] === '"') {
          j++
          for (; j < s.length; j++) {
            const c = s[j]
            if (c === '"') {
              if (s[j + 1] === '"') { col += '"'; j++; continue }
              break
            }
            col += c
          }
          if (!col) continue
          const ref = `${matched}.${quoteIdent(col)}`
          const key = ref.toLowerCase()
          if (!seen.has(key)) {
            seen.add(key)
            out.push({ ref, col })
          }
          i = j
        } else {
          const m = s.slice(j).match(/^([A-Za-z_][A-Za-z0-9_]*)/)
          if (!m) continue
          col = m[1]
          const ref = `${matched}.${col}`
          const key = ref.toLowerCase()
          if (!seen.has(key)) {
            seen.add(key)
            out.push({ ref, col })
          }
          i = j + col.length - 1
        }
      }
      return out
    }
    const aliasByKey = new Map<string, string>()
    const aliasExprByKey = new Map<string, string>()
    const sourceColByKey = new Map<string, string>()
    const sourceColByCompactKey = new Map<string, string>()
    const extractColByKey = new Map<string, string>()
    const extractColByCompactKey = new Map<string, string>()
    const compactFieldKey = (raw: string): string =>
      normalizeAliasName(raw).replace(/[^a-z0-9]+/g, '')
    if (Array.isArray(extractColumns)) {
      for (const col of extractColumns) {
        const raw = String(col ?? '').trim()
        const key = normalizeAliasName(raw)
        if (!raw || !key) continue
        if (!extractColByKey.has(key)) extractColByKey.set(key, raw)
        const compact = compactFieldKey(raw)
        if (compact && !extractColByCompactKey.has(compact)) {
          extractColByCompactKey.set(compact, raw)
        }
      }
    }
    if (traceRewrite) {
      try {
        traceTarget.__FG_QSRC_DEBUG__ = {
          extractHasCityStateZip: extractColByKey.has('city state zip'),
          extractCityStateZip: extractColByKey.get('city state zip') ?? null,
          extractHasReportEndDate: extractColByKey.has('reportenddate'),
          extractReportEndDate: extractColByKey.get('reportenddate') ?? null,
        }
        console.log('[QSRC] extract-map', traceTarget.__FG_QSRC_DEBUG__)
      } catch { }
    }
    for (const it of items.filter((it) => !hasWindowCallExpr(it.expr))) {
      const rawAlias = it.hasAlias ? it.alias : deriveImplicitAlias(it.expr)
      const key = normalizeAliasName(rawAlias)
      if (!key || key === 't') continue
      if (!aliasByKey.has(key)) {
        const aliasLabel = it.hasAlias ? it.alias : quoteIdent(rawAlias)
        aliasByKey.set(key, aliasLabel)
      }
      if (!aliasExprByKey.has(key)) {
        aliasExprByKey.set(key, qualifySimpleColumnExpr(it.expr))
      }
    }
    for (const it of items) {
      const refs = collectColumnRefs(it.expr)
      for (const r of refs) {
        const key = normalizeAliasName(r.col)
        if (!key) continue
        if (!sourceColByKey.has(key)) sourceColByKey.set(key, r.col)
        const compact = compactFieldKey(r.col)
        if (compact && !sourceColByCompactKey.has(compact)) {
          sourceColByCompactKey.set(compact, r.col)
        }
      }
    }
    if (traceRewrite) {
      try {
        traceTarget.__FG_QSRC_DEBUG__ = {
          ...(traceTarget.__FG_QSRC_DEBUG__ ?? {}),
          sourceHasCityStateZip: sourceColByKey.has('city state zip'),
          sourceCityStateZip: sourceColByKey.get('city state zip') ?? null,
          sourceHasReportEndDate: sourceColByKey.has('reportenddate'),
          sourceReportEndDate: sourceColByKey.get('reportenddate') ?? null,
        }
        console.log('[QSRC] source-map', traceTarget.__FG_QSRC_DEBUG__)
      } catch { }
    }
    const aliasKeySet = new Set(aliasByKey.keys())
    const resolveKnownSourceColumn = (rawKey: string): string | null => {
      const key = normalizeAliasName(rawKey)
      if (!key) return null
      const direct = sourceColByKey.get(key) || extractColByKey.get(key)
      if (direct) return direct
      const compact = compactFieldKey(rawKey)
      if (!compact) return null
      return sourceColByCompactKey.get(compact) || extractColByCompactKey.get(compact) || null
    }
    const hasKnownSourceColumn = (rawKey: string): boolean => Boolean(resolveKnownSourceColumn(rawKey))

    const inlineAliasRefs = (expr: string, selfKey: string | null, depth = 0, seen?: Set<string>): string => {
      if (!expr) return expr
      if (depth > 6) return expr
      const localSeen = new Set(seen ?? [])
      if (selfKey) localSeen.add(selfKey)
      let out = String(expr)
      const refs = collectColumnRefs(out)
      for (const r of refs) {
        const key = normalizeAliasName(r.col)
        if (!key || key === 't' || !aliasKeySet.has(key)) continue
        if (/^__fg_ord\d+$/i.test(key)) continue
        if (hasKnownSourceColumn(key)) continue
        if (selfKey && key === selfKey) continue
        if (localSeen.has(key)) continue
        const aliasExpr = aliasExprByKey.get(key)
        if (!aliasExpr || String(aliasExpr).trim().length === 0) continue
        const expanded = inlineAliasRefs(aliasExpr, key, depth + 1, new Set([...localSeen, key]))
        if (String(expanded).trim().length === 0) continue
        out = out.split(r.ref).join(`(${expanded})`)
      }
      // Replace unqualified quoted identifiers that refer to select aliases.
      let rebuilt = ''
      let inStr = false
      for (let i = 0; i < out.length; i++) {
        const ch = out[i]
        if (ch === "'") {
          if (inStr && out[i + 1] === "'") {
            rebuilt += "''"
            i++
            continue
          }
          inStr = !inStr
          rebuilt += ch
          continue
        }
        if (inStr) {
          rebuilt += ch
          continue
        }
        if (ch !== '"') {
          rebuilt += ch
          continue
        }
        let k = i + 1
        let buf = ''
        for (; k < out.length; k++) {
          const c = out[k]
          if (c === '"') {
            if (out[k + 1] === '"') {
              buf += '"'
              k++
              continue
            }
            break
          }
          buf += c
        }
        if (k >= out.length) {
          rebuilt += out.slice(i)
          break
        }
        let p = i - 1
        while (p >= 0 && /\s/.test(out[p])) p--
        const prev = p >= 0 ? out[p] : ''
        const key = normalizeAliasName(buf)
        if (
          prev !== '.'
          && key
          && key !== 't'
          && aliasKeySet.has(key)
          && !/^__fg_ord\d+$/i.test(key)
          && !hasKnownSourceColumn(key)
          && !(selfKey && key === selfKey)
          && !localSeen.has(key)
        ) {
          const aliasExpr = aliasExprByKey.get(key)
          if (aliasExpr && String(aliasExpr).trim().length > 0) {
            const expanded = inlineAliasRefs(aliasExpr, key, depth + 1, new Set([...localSeen, key]))
            if (String(expanded).trim().length > 0) {
              rebuilt += `(${expanded})`
              i = k
              continue
            }
          }
        }
        rebuilt += out.slice(i, k + 1)
        i = k
      }
      return rebuilt
    }

    items = items.map((it) => {
      const key = normalizeAliasName(it.alias)
      const rewritten = inlineAliasRefs(it.expr, key || null)
      if (rewritten === it.expr) return it
      return { ...it, expr: rewritten }
    })

    const windowItems = items.filter((it) => hasWindowCallExpr(it.expr))
    const hasWindowItems = windowItems.length > 0
    noteRewrite({
      hasWindowItems,
      selectListHead: selectListRaw.slice(0, 80),
      selectListHasRowNumber: /\bROW_NUMBER\s*\(\)\s*OVER\s*\(/i.test(selectListRaw),
      tailHead: tail.slice(0, 60),
    })

    const renderSelectItem = (it: { expr: string; alias: string; hasAlias: boolean }): string => {
      return it.hasAlias ? `${it.expr} AS ${it.alias}` : it.expr
    }
    const selectListRewritten = items.map(renderSelectItem).join(', ')
    const head = sql.slice(0, selectIdx + 6)
    const prefix = selectPrefix ? `${selectPrefix} ` : ''
    sql = `${head} ${prefix}${selectListRewritten} ${tail}`

    const addExtra = (expr: string, kind: 'win' | 'agg' | 'ord', aliasOverride?: string): string => {
      const key = normalizePrecomputeExpr(expr)
      const precomputed = precomputedExtrasMap?.get(key)
      if (precomputed) {
        const exprSql = String(expr ?? '').trim()
        if (exprSql && !innerExtraMap.has(key)) {
          innerExtraMap.set(key, { expr: exprSql, alias: precomputed, kind })
        }
        return precomputed
      }
      const existing = innerExtraMap.get(key)
      if (existing) return existing.alias
      const exprSql = String(expr ?? '').trim()
      if (!exprSql) return ''
      const alias = aliasOverride
        ? aliasOverride
        : (kind === 'win'
          ? `__fg_w${winIdx++}`
          : (kind === 'agg' ? `__fg_agg${aggIdx++}` : `__fg_ord${ordIdx++}`))
      innerExtraMap.set(key, { expr: exprSql, alias, kind })
      if (precomputeExtrasOut) {
        precomputeExtrasOut.set(key, { alias, kind })
      }
      return alias
    }
    const findTopLevelKeywordInTail = (input: string, keyword: string): number => {
      const kw = String(keyword ?? '').trim().toUpperCase()
      if (!kw) return -1
      let depth = 0
      let inStr = false
      let inIdent = false
      for (let i = 0; i < input.length; i++) {
        const ch = input[i]
        if (ch === "'") {
          if (inStr && input[i + 1] === "'") {
            i++
            continue
          }
          if (!inIdent) inStr = !inStr
          continue
        }
        if (!inStr && ch === '"') {
          if (inIdent && input[i + 1] === '"') {
            i++
            continue
          }
          inIdent = !inIdent
          continue
        }
        if (inStr || inIdent) continue
        if (ch === '(') {
          depth += 1
          continue
        }
        if (ch === ')') {
          depth = Math.max(0, depth - 1)
          continue
        }
        if (depth !== 0) continue
        if (input.slice(i, i + kw.length).toUpperCase() !== kw) continue
        const prev = input[i - 1]
        const next = input[i + kw.length]
        if (prev && /[A-Za-z0-9_]/.test(prev)) continue
        if (next && /[A-Za-z0-9_]/.test(next)) continue
        return i
      }
      return -1
    }
    const splitTopLevelWindowDefs = (input: string): string[] => {
      const out: string[] = []
      let buf = ''
      let depth = 0
      let inStr = false
      let inIdent = false
      for (let i = 0; i < input.length; i++) {
        const ch = input[i]
        if (ch === "'") {
          buf += ch
          if (inStr && input[i + 1] === "'") {
            buf += "'"
            i++
            continue
          }
          if (!inIdent) inStr = !inStr
          continue
        }
        if (!inStr && ch === '"') {
          buf += ch
          if (inIdent && input[i + 1] === '"') {
            buf += '"'
            i++
            continue
          }
          inIdent = !inIdent
          continue
        }
        if (!inStr && !inIdent) {
          if (ch === '(') depth += 1
          else if (ch === ')') depth = Math.max(0, depth - 1)
          else if (ch === ',' && depth === 0) {
            const item = buf.trim()
            if (item) out.push(item)
            buf = ''
            continue
          }
        }
        buf += ch
      }
      const tailItem = buf.trim()
      if (tailItem) out.push(tailItem)
      return out
    }
    const namedWindowDefsFromTail = (() => {
      const out = new Map<string, string>()
      const windowIdx = findTopLevelKeywordInTail(tail, 'WINDOW')
      if (windowIdx < 0) return out
      const defsBody = tail.slice(windowIdx + 'WINDOW'.length).trim()
      if (!defsBody) return out
      for (const defRaw of splitTopLevelWindowDefs(defsBody)) {
        const def = String(defRaw || '').trim()
        if (!def) continue
        const match = def.match(/^(?:"([^"]+)"|([A-Za-z_][A-Za-z0-9_$]*))\s+AS\s*\(([\s\S]+)\)\s*$/i)
        const name = String(match?.[1] ?? match?.[2] ?? '').trim()
        const clause = String(match?.[3] ?? '').trim()
        if (!name || !clause) continue
        out.set(normalizeAliasName(name), clause)
      }
      return out
    })()
    const inlineNamedWindowCall = (windowCallRaw: string): string => {
      const windowCall = String(windowCallRaw ?? '').trim()
      if (!windowCall || !namedWindowDefsFromTail.size) return windowCall
      const match = windowCall.match(
        /^([\s\S]*?\bOVER\s*)\(\s*(?:"([^"]+)"|([A-Za-z_][A-Za-z0-9_$]*))(?:\s+([\s\S]*?))?\s*\)$/i,
      )
      if (!match) return windowCall
      const windowName = normalizeAliasName(String(match[1 + 1] ?? match[2 + 1] ?? '').trim())
      const baseClause = namedWindowDefsFromTail.get(windowName)
      if (!baseClause) return windowCall
      const prefix = String(match[1] ?? '').trimEnd()
      const suffix = String(match[4] ?? '').trim()
      return `${prefix}(${baseClause}${suffix ? ` ${suffix}` : ''})`
    }

    const splitTopLevelList = (s: string): string[] => {
      const out: string[] = []
      let buf = ''
      let depth = 0
      let inStr = false
      let inIdent = false
      for (let i = 0; i < s.length; i++) {
        const ch = s[i]
        if (inStr) {
          if (ch === "'" && s[i + 1] === "'") {
            buf += "''"
            i++
            continue
          }
          if (ch === "'") {
            inStr = false
          }
          buf += ch
          continue
        }
        if (inIdent) {
          if (ch === '"' && s[i + 1] === '"') {
            buf += '""'
            i++
            continue
          }
          if (ch === '"') {
            inIdent = false
          }
          buf += ch
          continue
        }
        if (ch === "'") {
          inStr = true
          buf += ch
          continue
        }
        if (ch === '"') {
          inIdent = true
          buf += ch
          continue
        }
        if (ch === '(') {
          depth += 1
          buf += ch
          continue
        }
        if (ch === ')') {
          depth = Math.max(0, depth - 1)
          buf += ch
          continue
        }
        if (ch === ',' && depth === 0) {
          const item = buf.trim()
          if (item) out.push(item)
          buf = ''
          continue
        }
        buf += ch
      }
      const tail = buf.trim()
      if (tail) out.push(tail)
      return out
    }

    const findTopLevelKeyword = (s: string, keyword: string): number => {
      const kw = keyword.toUpperCase()
      let depth = 0
      let inStr2 = false
      let inIdent2 = false
      for (let i = 0; i < s.length; i++) {
        const ch = s[i]
        if (inStr2) {
          if (ch === "'" && s[i + 1] === "'") { i++; continue }
          if (ch === "'") inStr2 = false
          continue
        }
        if (inIdent2) {
          if (ch === '"' && s[i + 1] === '"') { i++; continue }
          if (ch === '"') inIdent2 = false
          continue
        }
        if (ch === "'") { inStr2 = true; continue }
        if (ch === '"') { inIdent2 = true; continue }
        if (ch === '(') depth++
        else if (ch === ')') depth = Math.max(0, depth - 1)
        if (depth !== 0) continue
        if (s.slice(i, i + kw.length).toUpperCase() === kw) {
          const prev = s[i - 1]
          const next = s[i + kw.length]
          if (prev && isWordChar(prev)) continue
          if (next && isWordChar(next)) continue
          return i
        }
      }
      return -1
    }

    const isSimpleOrderExpr = (raw: string): boolean => {
      const s = String(raw ?? '').trim()
      if (!s) return true
      if (/^"[^"]+"$/.test(s)) return true
      if (/^[A-Za-z_][A-Za-z0-9_]*$/.test(s)) return true
      if (/^(?:"t"|t)\s*\.\s*(?:"[^"]+"|[A-Za-z_][A-Za-z0-9_]*)$/.test(s)) return true
      return false
    }
    const orderExprHasSyntheticDependency = (raw: string): boolean => {
      const s = String(raw ?? '')
      if (!s) return false
      return /(?:(?:"t"|t)\s*\.\s*)?"(?:__lod_\d+|__fg_(?:w|agg)\d+)"/i.test(s) ||
        /(?:(?:t)\s*\.\s*)?\b(?:__lod_\d+|__fg_(?:w|agg)\d+)\b/i.test(s)
    }

    const referencesSelectAlias = (raw: string): boolean => {
      const refs = collectColumnRefs(raw)
      for (const ref of refs) {
        const key = normalizeAliasName(ref.col)
        if (key && aliasKeySet.has(key)) return true
      }
      // Also catch unqualified quoted identifiers that match select aliases.
      try {
        let inStr = false
        for (let i = 0; i < raw.length; i++) {
          const ch = raw[i]
          if (ch === "'") {
            if (inStr && raw[i + 1] === "'") { i++; continue }
            inStr = !inStr
            continue
          }
          if (inStr) continue
          if (ch !== '"') continue
          let k = i + 1
          let buf = ''
          for (; k < raw.length; k++) {
            const c = raw[k]
            if (c === '"') {
              if (raw[k + 1] === '"') {
                buf += '"'
                k++
                continue
              }
              break
            }
            buf += c
          }
          if (k >= raw.length) break
          const key = normalizeAliasName(buf)
          if (key && aliasKeySet.has(key)) return true
          i = k
        }
      } catch { }
      return false
    }
    const hasTopLevelGroupBy = findTopLevelKeyword(tail, 'GROUP BY') >= 0
    const hasGroupingSetsClause = /\bGROUP\s+BY\s+GROUPING\s+SETS\b/i.test(tail)
    const hasGroupedProjectionScope = hasTopLevelGroupBy || hasGroupingSetsClause
    const normalizeSqlExpr = (s: string): string => String(s || '').replace(/\s+/g, ' ').trim().toLowerCase()
    const extractSimpleSourceColumnKey = (exprRaw: string): string | null => {
      const expr = String(exprRaw || '').trim()
      if (!expr) return null
      const quoted = expr.match(/^(?:(?:"[^"]+"|[A-Za-z_][A-Za-z0-9_]*)\s*\.\s*)?"((?:[^"]|"")+)"$/i)
      if (quoted) return String(quoted[1] || '').replace(/""/g, '"').trim().toLowerCase() || null
      const bare = expr.match(/^(?:(?:"[^"]+"|[A-Za-z_][A-Za-z0-9_]*)\s*\.\s*)?([A-Za-z_][A-Za-z0-9_]*)$/)
      if (bare) return String(bare[1] || '').trim().toLowerCase() || null
      return null
    }
    const groupedSourceColumnKeys = new Set<string>()
    const groupedExprNormalized = new Set<string>()
    if (hasTopLevelGroupBy) {
      const groupIdx = findTopLevelKeyword(tail, 'GROUP BY')
      if (groupIdx >= 0) {
        const start = groupIdx + 'GROUP BY'.length
        const endCandidates = [
          findTopLevelKeyword(tail, 'HAVING'),
          findTopLevelKeyword(tail, 'QUALIFY'),
          findTopLevelKeyword(tail, 'ORDER BY'),
          findTopLevelKeyword(tail, 'LIMIT'),
          findTopLevelKeyword(tail, 'WINDOW'),
        ].filter((idx) => idx > start)
        const end = endCandidates.length ? Math.min(...endCandidates) : tail.length
        const groupClause = tail.slice(start, end).trim()
        if (groupClause) {
          const groupItems = /^GROUPING\s+SETS\b/i.test(groupClause)
            ? (() => {
                const marker = 'GROUPING SETS'
                const openIdx = groupClause.toUpperCase().indexOf(marker) + marker.length
                const parenIdx = groupClause.indexOf('(', openIdx)
                if (parenIdx < 0) return splitTopLevelList(groupClause)
                const closeIdx = findMatchingParen(groupClause, parenIdx)
                if (closeIdx < 0) return splitTopLevelList(groupClause)
                const rawSets = groupClause.slice(parenIdx + 1, closeIdx)
                return splitTopLevelList(rawSets).flatMap((setRaw) => {
                  const trimmed = String(setRaw ?? '').trim()
                  if (!trimmed || trimmed === '()') return []
                  if (trimmed.startsWith('(') && trimmed.endsWith(')')) {
                    return splitTopLevelList(trimmed.slice(1, -1))
                  }
                  return [trimmed]
                })
              })()
            : splitTopLevelList(groupClause)
          for (const item of groupItems) {
            const itemTrim = String(item ?? '').trim()
            if (!itemTrim) continue
            const ordinalMatch = itemTrim.match(/^(\d+)$/)
            if (ordinalMatch) {
              const ordinal = Number(ordinalMatch[1])
              if (Number.isFinite(ordinal) && ordinal >= 1 && ordinal <= items.length) {
                const byPos = String(items[ordinal - 1]?.expr ?? '').trim()
                if (byPos) {
                  const byPosNorm = normalizeSqlExpr(byPos)
                  if (byPosNorm) groupedExprNormalized.add(byPosNorm)
                  const byPosCol = extractSimpleSourceColumnKey(byPos)
                  if (byPosCol) groupedSourceColumnKeys.add(byPosCol)
                }
              }
              continue
            }
            const norm = normalizeSqlExpr(itemTrim)
            if (norm) groupedExprNormalized.add(norm)
            const colKey = extractSimpleSourceColumnKey(itemTrim)
            if (colKey) groupedSourceColumnKeys.add(colKey)
          }
        }
      }
    }
    const appendTopLevelGroupByExprs = (
      tailSql: string,
      exprsRaw: string[],
      options?: { createIfMissing?: boolean },
    ): string => {
      const exprs = Array.from(new Set(
        exprsRaw
          .map((expr) => String(expr || '').trim())
          .filter(Boolean)
      ))
      if (exprs.length === 0) return tailSql
      const groupIdx = findTopLevelKeyword(tailSql, 'GROUP BY')
      if (groupIdx < 0) {
        if (!options?.createIfMissing) return tailSql
        const insertCandidates = [
          findTopLevelKeyword(tailSql, 'HAVING'),
          findTopLevelKeyword(tailSql, 'QUALIFY'),
          findTopLevelKeyword(tailSql, 'ORDER BY'),
          findTopLevelKeyword(tailSql, 'LIMIT'),
          findTopLevelKeyword(tailSql, 'WINDOW'),
        ].filter((idx) => idx >= 0)
        const insertIdx = insertCandidates.length > 0 ? Math.min(...insertCandidates) : tailSql.length
        const prefix = tailSql.slice(0, insertIdx).trimEnd()
        const suffix = tailSql.slice(insertIdx).trimStart()
        return `${prefix} GROUP BY ${exprs.join(', ')}${suffix ? ` ${suffix}` : ''}`
      }
      const start = groupIdx + 'GROUP BY'.length
      const endCandidates = [
        findTopLevelKeyword(tailSql, 'HAVING'),
        findTopLevelKeyword(tailSql, 'QUALIFY'),
        findTopLevelKeyword(tailSql, 'ORDER BY'),
        findTopLevelKeyword(tailSql, 'LIMIT'),
        findTopLevelKeyword(tailSql, 'WINDOW'),
      ].filter((idx) => idx > start)
      const end = endCandidates.length ? Math.min(...endCandidates) : tailSql.length
      const groupClause = tailSql.slice(start, end).trim()
      if (!groupClause) return tailSql
      const mergeExprs = (itemsRaw: string[]): string[] => {
        const out: string[] = []
        const seen = new Set<string>()
        for (const raw of [...itemsRaw, ...exprs]) {
          const item = String(raw || '').trim()
          if (!item) continue
          const key = normalizeSqlExpr(item)
          if (!key || seen.has(key)) continue
          seen.add(key)
          out.push(item)
        }
        return out
      }
      let rebuiltClause = groupClause
      if (/^GROUPING\s+SETS\b/i.test(groupClause)) {
        const marker = 'GROUPING SETS'
        const openIdx = groupClause.toUpperCase().indexOf(marker) + marker.length
        const parenIdx = groupClause.indexOf('(', openIdx)
        if (parenIdx < 0) return tailSql
        const closeIdx = findMatchingParen(groupClause, parenIdx)
        if (closeIdx < 0) return tailSql
        const rawSets = groupClause.slice(parenIdx + 1, closeIdx)
        const rebuiltSets = splitTopLevelList(rawSets)
          .map((setRaw) => {
            const trimmed = String(setRaw || '').trim()
            if (!trimmed) return ''
            const innerItems =
              trimmed === '()'
                ? []
                : (
                  trimmed.startsWith('(') && trimmed.endsWith(')')
                    ? splitTopLevelList(trimmed.slice(1, -1))
                    : [trimmed]
                )
            return `(${mergeExprs(innerItems).join(', ')})`
          })
          .filter(Boolean)
        if (rebuiltSets.length === 0) return tailSql
        rebuiltClause = `${marker} (${rebuiltSets.join(', ')})`
      } else {
        rebuiltClause = mergeExprs(splitTopLevelList(groupClause)).join(', ')
      }
      const suffix = tailSql.slice(end)
      const spacer = suffix && !/^\s/.test(suffix) ? ' ' : ''
      return `${tailSql.slice(0, start)} ${rebuiltClause}${spacer}${suffix}`
    }
    const detectTopLevelFromAlias = (s: string): string => {
      const fromIdx = findTopLevelKeyword(s, 'FROM')
      if (fromIdx < 0) return ''
      let i = fromIdx + 4
      while (i < s.length && /\s/.test(s[i])) i++
      if (i >= s.length) return ''
      let depth = 0
      let inStr = false
      let inIdent = false
      for (; i < s.length; i++) {
        const ch = s[i]
        if (inStr) {
          if (ch === "'" && s[i + 1] === "'") { i++; continue }
          if (ch === "'") inStr = false
          continue
        }
        if (inIdent) {
          if (ch === '"' && s[i + 1] === '"') { i++; continue }
          if (ch === '"') inIdent = false
          continue
        }
        if (ch === "'") { inStr = true; continue }
        if (ch === '"') { inIdent = true; continue }
        if (ch === '(') { depth++; continue }
        if (ch === ')') {
          if (depth > 0) { depth--; continue }
          break
        }
        if (depth === 0 && /\s/.test(ch)) break
      }
      let j = i
      while (j < s.length && /\s/.test(s[j])) j++
      if (
        s.slice(j, j + 2).toUpperCase() === 'AS'
        && !isWordChar(s[j - 1] || '')
        && !isWordChar(s[j + 2] || '')
      ) {
        j += 2
        while (j < s.length && /\s/.test(s[j])) j++
      }
      if (j >= s.length) return ''
      if (s[j] === '"') {
        let k = j + 1
        let buf = ''
        for (; k < s.length; k++) {
          const c = s[k]
          if (c === '"') {
            if (s[k + 1] === '"') { buf += '"'; k++; continue }
            break
          }
          buf += c
        }
        return normalizeAliasName(buf)
      }
      const m = s.slice(j).match(/^([A-Za-z_][A-Za-z0-9_]*)/)
      if (!m) return ''
      const cand = normalizeAliasName(m[1])
      if (!cand) return ''
      const blocked = new Set([
        'where', 'group', 'order', 'having', 'qualify', 'window', 'limit',
        'join', 'left', 'right', 'inner', 'full', 'cross', 'on',
        'union', 'intersect', 'except',
      ])
      return blocked.has(cand) ? '' : cand
    }
    const topLevelFromAliasKey = detectTopLevelFromAlias(tail)
    const topLevelFromAliasIsT = topLevelFromAliasKey === 't'
    const readSqlIdentifierAt = (s: string, start: number): { text: string; end: number } | null => {
      if (start < 0 || start >= s.length) return null
      if (s[start] === '"') {
        let i = start + 1
        let out = ''
        for (; i < s.length; i++) {
          const c = s[i]
          if (c === '"') {
            if (s[i + 1] === '"') {
              out += '"'
              i++
              continue
            }
            return { text: out, end: i + 1 }
          }
          out += c
        }
        return null
      }
      if (s[start] === '[') {
        let i = start + 1
        let out = ''
        for (; i < s.length; i++) {
          const c = s[i]
          if (c === ']') {
            if (s[i + 1] === ']') {
              out += ']'
              i++
              continue
            }
            return { text: out, end: i + 1 }
          }
          out += c
        }
        return null
      }
      const m = s.slice(start).match(/^([A-Za-z_][A-Za-z0-9_]*)/)
      if (!m) return null
      return { text: m[1], end: start + m[1].length }
    }
    const rewriteQualifiedSourceRefs = (input: string): string => {
      if (!input) return input
      let out = ''
      let i = 0
      let inStr = false
      while (i < input.length) {
        const ch = input[i]
        if (ch === "'") {
          if (inStr && input[i + 1] === "'") {
            out += "''"
            i += 2
            continue
          }
          inStr = !inStr
          out += ch
          i++
          continue
        }
        if (inStr) {
          out += ch
          i++
          continue
        }
        const left = readSqlIdentifierAt(input, i)
        if (!left) {
          out += ch
          i++
          continue
        }
        let j = left.end
        while (j < input.length && /\s/.test(input[j])) j++
        if (input[j] !== '.') {
          out += input.slice(i, left.end)
          i = left.end
          continue
        }
        j++
        while (j < input.length && /\s/.test(input[j])) j++
        const right = readSqlIdentifierAt(input, j)
        if (!right) {
          out += input.slice(i, left.end)
          i = left.end
          continue
        }
        const qualifierKey = normalizeAliasName(left.text)
        const srcCol = resolveKnownSourceColumn(right.text)
        if (qualifierKey && qualifierKey !== 't' && srcCol) {
          out += `t.${quoteIdent(srcCol)}`
          i = right.end
          continue
        }
        out += input.slice(i, left.end)
        i = left.end
      }
      return out
    }
    const qualifyKnownSourceCols = (raw: string): string => {
      if (!raw) return raw
      // Only rewrite to `t.<col>` when this SELECT scope actually aliases
      // its FROM source as `t`.
      if (!topLevelFromAliasIsT) return raw
      const input = rewriteQualifiedSourceRefs(raw)
      let out = ''
      let inStr = false
      for (let i = 0; i < input.length; i++) {
        const ch = input[i]
        if (ch === "'") {
          if (inStr && input[i + 1] === "'") {
            out += "''"
            i++
            continue
          }
          inStr = !inStr
          out += ch
          continue
        }
        if (inStr) {
          out += ch
          continue
        }
        if (ch !== '"') {
          out += ch
          continue
        }
        let k = i + 1
        let buf = ''
        for (; k < input.length; k++) {
          const c = input[k]
          if (c === '"') {
            if (input[k + 1] === '"') {
              buf += '"'
              k++
              continue
            }
            break
          }
          buf += c
        }
        if (k >= input.length) {
          out += input.slice(i)
          break
        }
        let p = i - 1
        while (p >= 0 && /\s/.test(input[p])) p--
        const prev = p >= 0 ? input[p] : ''
        // Never rewrite alias declarations: `... AS "Some Name"`.
        let prevWord = ''
        if (p >= 0) {
          let wEnd = p
          while (wEnd >= 0 && /\s/.test(input[wEnd])) wEnd--
          let wStart = wEnd
          while (wStart >= 0 && /[A-Za-z_]/.test(input[wStart])) wStart--
          if (wEnd >= 0) {
            prevWord = input.slice(wStart + 1, wEnd + 1).toUpperCase()
          }
        }
        let nextNonWs = ''
        let n = k + 1
        while (n < input.length && /\s/.test(input[n])) n++
        if (n < input.length) nextNonWs = input[n]
        const key = normalizeAliasName(buf)
        if (prev !== '.' && nextNonWs !== '.' && prevWord !== 'AS' && key) {
          const srcCol = resolveKnownSourceColumn(buf)
          if (srcCol) {
            if (traceRewrite && (key === 'city state zip' || key === 'reportenddate')) {
              try {
                const arr = Array.isArray(traceTarget.__FG_QSRC_REWRITES__) ? traceTarget.__FG_QSRC_REWRITES__ : []
                const entry = {
                  key,
                  srcCol,
                  rawHead: input.slice(0, 220),
                }
                arr.push(entry)
                traceTarget.__FG_QSRC_REWRITES__ = arr.slice(-20)
                console.log('[QSRC] rewrite', entry)
              } catch { }
            }
            out += `t.${quoteIdent(srcCol)}`
            i = k
            continue
          }
        }
        out += input.slice(i, k + 1)
        i = k
      }
      return out
    }
    items = items.map((it) => {
      const rewritten = qualifyKnownSourceCols(it.expr)
      if (rewritten === it.expr) return it
      return { ...it, expr: rewritten }
    })
    const hasAggregateProjectionScope = items.some((it) => (
      /\b(?:SUM|COUNT|AVG|MIN|MAX|ANY_VALUE)\s*\(/i.test(String(it?.expr ?? ''))
      && !/\bOVER\s*\(/i.test(String(it?.expr ?? ''))
    ))
    const hasAggregateTokenInSelectList = /\b(?:SUM|COUNT|AVG|MIN|MAX|ANY_VALUE)\s*\(/i.test(selectListRaw)

    const rewriteWindowOrderBy = (expr: string): string => {
      if (!/\bover\s*\(/i.test(expr)) return expr
      const needsGroupedOrderSurrogate =
        hasGroupedProjectionScope || hasAggregateProjectionScope || hasAggregateTokenInSelectList
      const out: string[] = []
      let cursor = 0
      const upper = expr.toUpperCase()
      const findMatchingParen = (s: string, start: number): number => {
        let depth = 0
        let inStr2 = false
        for (let i = start; i < s.length; i++) {
          const ch = s[i]
          if (ch === "'") {
            if (inStr2 && s[i + 1] === "'") { i++; continue }
            inStr2 = !inStr2
            continue
          }
          if (inStr2) continue
          if (ch === '(') depth++
          else if (ch === ')') {
            depth--
            if (depth === 0) return i
          }
        }
        return -1
      }
      for (let i = 0; i < upper.length; i++) {
        if (upper.slice(i, i + 4) !== 'OVER') continue
        const prev = expr[i - 1]
        const next = expr[i + 4]
        if (prev && isWordChar(prev)) continue
        if (next && isWordChar(next)) continue
        let j = i + 4
        while (j < expr.length && /\s/.test(expr[j])) j++
        if (expr[j] !== '(') continue
        const end = findMatchingParen(expr, j)
        if (end < 0) continue
        const clause = expr.slice(j + 1, end)
        const orderIdx = findTopLevelKeyword(clause, 'ORDER BY')
        if (orderIdx < 0) {
          i = end
          continue
        }
        const before = clause.slice(0, orderIdx).trim()
        const afterOrder = clause.slice(orderIdx + 8)
        const frameIdx = (() => {
          const rowsIdx = findTopLevelKeyword(afterOrder, 'ROWS')
          const rangeIdx = findTopLevelKeyword(afterOrder, 'RANGE')
          const groupsIdx = findTopLevelKeyword(afterOrder, 'GROUPS')
          const candidates = [rowsIdx, rangeIdx, groupsIdx].filter((v) => v >= 0)
          return candidates.length ? Math.min(...candidates) : -1
        })()
        const orderPart = (frameIdx >= 0 ? afterOrder.slice(0, frameIdx) : afterOrder).trim()
        const framePart = frameIdx >= 0 ? afterOrder.slice(frameIdx).trim() : ''
        const orderItems = splitTopLevelList(orderPart).map((raw) => {
          let item = raw.trim()
          if (!item) return item
          let nulls = ''
          let dir = ''
          const nullsMatch = item.match(/\s+NULLS\s+(FIRST|LAST)\s*$/i)
          if (nullsMatch) {
            nulls = nullsMatch[0].trim()
            item = item.slice(0, nullsMatch.index).trim()
          }
          const dirMatch = item.match(/\s+(ASC|DESC)\s*$/i)
          if (dirMatch) {
            dir = dirMatch[1].toUpperCase()
            item = item.slice(0, dirMatch.index).trim()
          }
          item = qualifyKnownSourceCols(item)
          // Window ORDER BY cannot reliably reference sibling SELECT aliases in DuckDB.
          // Expand alias refs first; then, if still complex, lift to an inner precomputed column.
          if (referencesSelectAlias(item)) {
            const expanded = inlineAliasRefs(item, null)
            if (String(expanded).trim().length > 0) {
              item = expanded
            }
          }
          // Outer rewritten windows read from `FROM (<inner>) AS t`. If ORDER BY references a
          // raw source column (e.g. t."reportenddate") that is not projected by the inner
          // select-list, DuckDB binder fails. Prefer an already-projected select alias when
          // possible; otherwise force-lift this ORDER expression into the inner projection.
          const liftSourceOrderItem = (sourceColRaw: string): string => {
            const sourceCol = String(sourceColRaw ?? '').trim()
            if (!sourceCol) return item
            if (/^__fg_ord\d+$/i.test(sourceCol)) {
              return `t.${quoteIdent(sourceCol)}`
            }
            const sourceKey = normalizeAliasName(sourceCol)
            const projectedAlias = sourceKey ? aliasByKey.get(sourceKey) : null
            if (projectedAlias) {
              // Prefer existing projected aliases over injecting inline aggregate wrappers
              // into window ORDER BY clauses. Aggregate/group-safety is handled in the
              // inner projection rewrite path (ANY_VALUE/MIN/MAX over lifted extras).
              return `t.${projectedAlias}`
            }
            const sourceRef = `t.${quoteIdent(sourceCol)}`
            const alias = addExtra(sourceRef, 'ord')
            return alias ? `t.${quoteIdent(alias)}` : sourceRef
          }
          const sourceRefMatch = item.match(
            /^(?:"t"|t)\s*\.\s*(?:"([^"]+)"|([A-Za-z_][A-Za-z0-9_]*))$/i
          )
          if (sourceRefMatch) {
            item = liftSourceOrderItem(String(sourceRefMatch[1] ?? sourceRefMatch[2] ?? '').trim())
          } else {
            const bareSourceRefMatch = item.match(
              /^(?:"([^"]+)"|([A-Za-z_][A-Za-z0-9_]*))$/i
            )
            const bareSourceCol = String(bareSourceRefMatch?.[1] ?? bareSourceRefMatch?.[2] ?? '').trim()
            if (bareSourceCol) {
              const resolvedBareSourceCol = resolveKnownSourceColumn(bareSourceCol)
              item = liftSourceOrderItem(resolvedBareSourceCol || bareSourceCol)
            }
          }
          if (
            !isSimpleOrderExpr(item) &&
            (!referencesSelectAlias(item) || orderExprHasSyntheticDependency(item))
          ) {
            const alias = addExtra(item, 'ord')
            item = `t.${quoteIdent(alias)}`
          }
          if (needsGroupedOrderSurrogate && !/\b(?:MIN|MAX|SUM|AVG|COUNT|ANY_VALUE)\s*\(/i.test(item)) {
            const stripOuterParens = (rawIn: string): string => {
              let raw = String(rawIn ?? '').trim()
              const isWrappedOnce = (s: string): boolean => {
                if (!(s.startsWith('(') && s.endsWith(')'))) return false
                let depth = 0
                let inStr = false
                for (let idx = 0; idx < s.length; idx++) {
                  const ch = s[idx]
                  if (ch === "'") {
                    if (inStr && s[idx + 1] === "'") { idx++; continue }
                    inStr = !inStr
                    continue
                  }
                  if (inStr) continue
                  if (ch === '(') depth++
                  else if (ch === ')') {
                    depth--
                    if (depth === 0 && idx < s.length - 1) return false
                  }
                }
                return depth === 0
              }
              while (isWrappedOnce(raw)) raw = raw.slice(1, -1).trim()
              return raw
            }
            const compact = stripOuterParens(item)
            const compactRef = compact.match(
              /^(?:(?:"t"|t)\s*\.\s*)?(?:"([^"]+)"|([A-Za-z_][A-Za-z0-9_]*))$/i
            )
            if (compactRef) {
              const refCol = String(compactRef[1] ?? compactRef[2] ?? '').trim()
              if (refCol && /^__fg_ord\d+$/i.test(refCol)) {
                // __fg_ord* aliases are pre-lifted order surrogates; keep ORDER BY as a
                // plain reference to avoid injecting aggregate context into non-grouped queries.
                item = `t.${quoteIdent(refCol)}`
              } else if (refCol) {
                const resolvedRefCol = resolveKnownSourceColumn(refCol) || refCol
                item = liftSourceOrderItem(resolvedRefCol)
              }
            }
          }
          return [item, dir, nulls].filter(Boolean).join(' ')
        }).filter(Boolean)
        if (!orderItems.length) {
          i = end
          continue
        }
        const newClause = [
          before,
          `ORDER BY ${orderItems.join(', ')}`,
          framePart,
        ].filter(Boolean).join(' ').trim()
        out.push(expr.slice(cursor, i))
        out.push(`OVER (${newClause})`)
        cursor = end + 1
        i = end
      }
      out.push(expr.slice(cursor))
      return out.join('')
    }

    const nonWindowItems = items.filter((it) => !hasWindowCallExpr(it.expr))
    const windowItems2 = items.filter((it) => hasWindowCallExpr(it.expr))
    const unquoteSqlAlias = (raw: string): string => {
      const s = String(raw ?? '').trim()
      if (s.startsWith('"') && s.endsWith('"')) return s.slice(1, -1).replace(/""/g, '"')
      return s
    }
    const windowExprByAliasKey = new Map<string, { alias: string; expr: string }>()
    for (const it of windowItems2) {
      const alias = unquoteSqlAlias(it.alias)
      const key = normalizeAliasName(alias)
      const expr = String(it.expr || '').trim()
      if (key && alias && expr && !windowExprByAliasKey.has(key)) {
        windowExprByAliasKey.set(key, { alias, expr })
      }
    }
    const isWindowedAggregateSpan = (
      sourceExpr: string,
      span: { start: number; end: number; full: string },
    ): boolean => {
      let t = span.end
      while (t < sourceExpr.length && /\s/.test(sourceExpr[t])) t++
      if (t + 4 > sourceExpr.length) return false
      const kw = sourceExpr.slice(t, t + 4)
      const before = sourceExpr[t - 1]
      const after = sourceExpr[t + 4]
      if ((before && isWordChar(before)) || (after && isWordChar(after))) return false
      return kw.toUpperCase() === 'OVER'
    }

    // Debug: check which items contain __lod refs
    for (const it of nonWindowItems) {
      if (traceRewrite && /__lod_\d+/.test(it.expr)) {
        console.log(`[windowRewrite] NON-WINDOW item "${it.alias}" contains __lod ref. expr preview:`, it.expr.slice(0, 200))
      }
    }
    for (const it of windowItems2) {
      if (traceRewrite && /__lod_\d+/.test(it.expr)) {
        console.log(`[windowRewrite] WINDOW item "${it.alias}" contains __lod ref. expr preview:`, it.expr.slice(0, 200))
      }
    }

    // Build a reverse lookup: for each non-window item, map aggregated column refs back to their alias.
    // This lets us resolve __lod_N refs that appear inside window expressions by finding the non-window
    // item that aggregates them (e.g. MAX("t"."__lod_1") AS "measure").
    const aggColToAlias = new Map<string, string>()
    for (const nw of nonWindowItems) {
      // Match patterns like MAX("t"."__lod_1") or MIN("t"."col") in the non-window expression.
      const aggMatch = nw.expr.match(/^\s*(MAX|MIN|AVG|SUM|COUNT)\s*\(\s*(?:"t"|t)\s*\.\s*"([^"]+)"\s*\)\s*$/i)
      if (aggMatch) {
        const colKey = normalizeAliasName(aggMatch[2])
        if (colKey && !aggColToAlias.has(colKey)) {
          aggColToAlias.set(colKey, nw.alias)
        }
      }
    }
    const stageSiblingWindowAliasRefs = (exprIn: string, selfAliasRaw?: string): string => {
      let expr = String(exprIn ?? '')
      if (!expr || windowExprByAliasKey.size === 0) return expr
      const selfKey = normalizeAliasName(unquoteSqlAlias(String(selfAliasRaw ?? '')))
      for (const [key, info] of windowExprByAliasKey.entries()) {
        if (!key || (selfKey && key === selfKey)) continue
        const alias = String(info.alias || '').trim()
        const aliasExpr = String(info.expr || '').trim()
        if (!alias || !aliasExpr) continue
        const quoted = quoteIdent(alias)
        const unqualified = new RegExp(`(?<![.])${quoted.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'g')
        const qualified = new RegExp(`(?:"t"|t)\\s*\\.\\s*${quoted.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'gi')
        if (!unqualified.test(expr) && !qualified.test(expr)) continue
        addExtra(aliasExpr, 'win', alias)
        expr = expr.replace(qualified, `t.${quoted}`)
        expr = expr.replace(unqualified, `t.${quoted}`)
      }
      return expr
    }

    const rewriteWindowProjectionExpr = (exprIn: string, selfAliasRaw?: string): string => {
      let expr = normalizeResidualTaggedLodWindowAggregates(String(exprIn ?? ''))
      expr = stageSiblingWindowAliasRefs(expr, selfAliasRaw)
      const traceDaysWorkedExpr =
        traceRewrite &&
        /MAX\s*\(\s*(?:"t"|t)\s*\.\s*"daysworked"\s*\)/i.test(expr)
      if (traceDaysWorkedExpr) {
        console.log('[windowRewrite] daysworked expr enter', expr.slice(0, 400))
      }
      const refs = collectColumnRefs(expr)
      for (const r of refs) {
        const key = normalizeAliasName(r.col)
        if (key && aliasKeySet.has(key)) {
          const alias = aliasByKey.get(key)
          if (alias) {
            expr = expr.split(r.ref).join(`t.${alias}`)
            continue
          }
        }
        // If an unresolved column ref matches an LOD-generated column (__lod_N),
        // do NOT replace it here. The enclosing aggregate (e.g. MAX("t"."__lod_N"))
        // will be discovered by the aggregate-lifting pass below (findAggregateCallSpans),
        // which properly lifts the entire MAX(...) call into the inner SELECT and
        // replaces it with an alias reference. Replacing just the column ref here
        // would create a mismatched inner reference (unquoted t vs quoted "t") that
        // breaks the subsequent aggregate lifting.
        if (key && /^__lod_\d+$/.test(key) && !aliasKeySet.has(key)) {
          if (traceRewrite) {
            console.log(`[windowRewrite] Skipping LOD column ref ${r.col} — deferring to aggregate-lifting pass`)
          }
          continue
        }
      }
      for (let windowLiftPass = 0; windowLiftPass < 8; windowLiftPass += 1) {
        const calls = findWindowCalls(expr)
        const aggSpans =
          /\b(?:sum|avg|min|max|count)\s*\(/i.test(expr)
            ? findAggregateCallSpans(expr)
            : []
        // Debug: log all found aggregates and whether they contain __lod
        const lodAggs = aggSpans.filter(a => /__lod_\d+/.test(a.full))
        if (traceRewrite && lodAggs.length > 0) {
          console.log(`[windowRewrite] Found ${lodAggs.length} LOD-related aggregate spans:`, lodAggs.map(a => a.full))
        }
        if (traceRewrite && /__lod_\d+/.test(expr) && lodAggs.length === 0) {
          console.log(`[windowRewrite] WARNING: expr contains __lod but no LOD aggregates found. expr preview:`, expr.slice(0, 300))
        }
        const needsLift = (c: { start: number; end: number; full: string }): boolean => {
          // Window call nested in another window call.
          if (calls.some((o) => o.start < c.start && o.end > c.end)) return true
          // Window call nested inside an aggregate call (DuckDB rejects this as "nested window").
          if (aggSpans.some((a) => a.start < c.start && a.end > c.end)) return true
          return false
        }
        const lift = calls.filter(needsLift)
        if (lift.length === 0) break
        const uniqLift = Array.from(new Map(lift.map((n) => [n.full, n])).values())
        let changed = false
        for (const n of uniqLift) {
          const alias = addExtra(n.full, 'win')
          const nextExpr = expr.split(n.full).join(`t.${quoteIdent(alias)}`)
          if (nextExpr !== expr) {
            expr = nextExpr
            changed = true
          }
        }
        if (!changed) break
      }

      // Nested window lifting can change aggregate text (e.g. by replacing nested OVER() calls
      // with synthetic aliases). Re-scan after that rewrite so aggregate lifting runs on the
      // current expression shape rather than stale pre-rewrite spans.
      const aggSpansPostWindowLift =
        /\b(?:sum|avg|min|max|count)\s*\(/i.test(expr)
          ? findAggregateCallSpans(expr)
          : []

      // Lift non-windowed aggregates inside window expressions into the inner SELECT,
      // so window ORDER BY / PARTITION BY clauses refer to columns (not aggregates).
      const aggToLift = aggSpansPostWindowLift.filter((a) => !isWindowedAggregateSpan(expr, a))
      if (traceDaysWorkedExpr) {
        console.log('[windowRewrite] daysworked agg spans', {
          all: aggSpansPostWindowLift.map((a) => a.full).slice(0, 8),
          lift: aggToLift.map((a) => a.full).slice(0, 8),
        })
      }
      if (aggToLift.length > 0) {
        const uniqAgg = Array.from(new Map(aggToLift.map((a) => [a.full, a])).values())
        for (const a of uniqAgg) {
          if (!ALLOW_FALLBACKS) {
            const aggExpr = String(a.full ?? '').trim()
            const nullAgg =
              /\bSUM\s*\(\s*TRY_CAST\(\s*NULL\s+AS\s+DOUBLE\s*\)\s*\)/i.test(aggExpr) ||
              /\b(?:MIN|MAX|AVG)\s*\(\s*TRY_CAST\(\s*NULL\s+AS\s+DOUBLE\s*\)\s*\)/i.test(aggExpr) ||
              /\b(?:MIN|MAX|SUM|AVG)\s*\(\s*NULL\s*\)/i.test(aggExpr)
            if (nullAgg) {
              // Some table-calc members legitimately collapse to NULL under the active
              // Measure Names selection. Keep strict mode deterministic by rewriting the
              // aggregate to NULL in place, rather than aborting the whole worksheet query.
              if (traceRewrite) {
                console.log(`[windowRewrite] Rewriting aggregate "${aggExpr}" -> NULL`)
              }
              expr = expr.split(a.full).join('NULL')
              continue
            }
          }
          const alias = addExtra(a.full, 'agg')
          if (traceRewrite) {
            console.log(`[windowRewrite] Lifting aggregate "${a.full}" → inner alias "${alias}"`)
          }
          expr = expr.split(a.full).join(`t.${quoteIdent(alias)}`)
        }
      }
      expr = normalizeResidualTaggedLodWindowAggregates(expr)
      if (traceDaysWorkedExpr) {
        console.log('[windowRewrite] daysworked expr exit', expr.slice(0, 400))
      }

      // Reuse identical top-level window calls across the rewritten measure expression.
      // This catches LOOKUP/FIRST-driven diffs that repeat the same FIRST_VALUE(...)
      // multiple times inside CASE/IIF chains even after nested-window lifting.
      const repeatedWindowCalls = Array.from(
        findWindowCalls(expr).reduce((acc, call) => {
          const key = String(call?.full ?? '').trim()
          if (!key) return acc
          acc.set(key, (acc.get(key) ?? 0) + 1)
          return acc
        }, new Map<string, number>()).entries(),
      )
        .filter(([full, count]) =>
          count >= 2 &&
          /\bOVER\s*\(\s*(?:"?__fg_win_\d+"?|\b__fg_win_\d+\b)/i.test(full) &&
          /\b__fg_(?:agg|w|ord)\d+\b/i.test(full),
        )
        .map(([full]) => full)
      for (const windowCall of repeatedWindowCalls) {
        const alias = addExtra(inlineNamedWindowCall(windowCall), 'win')
        const nextExpr = expr.split(windowCall).join(`t.${quoteIdent(alias)}`)
        if (nextExpr !== expr) {
          expr = nextExpr
        }
      }

      // If the (remaining) outer window expression repeats a complex projected expression
      // (e.g. a CASE dim), replace it with the projected column so outer scopes don't need
      // base-table-only columns.
      const normalizeWs = (s: string) => s.replace(/\s+/g, ' ').trim()
      const unwrapGroupingAllExpr = (s: string): string | null => {
        // Generated form:
        // CASE WHEN GROUPING(<E>) = 1 THEN 'All' ELSE CAST(<E> AS VARCHAR) END
        const m = s.match(/^CASE\s+WHEN\s+GROUPING\s*\(\s*([\s\S]+?)\s*\)\s*=\s*1\s+THEN\s+'All'\s+ELSE\s+CAST\s*\(\s*([\s\S]+?)\s+AS\s+VARCHAR\s*\)\s*END$/i)
        if (!m) return null
        const a = normalizeWs(String(m[1] ?? ''))
        const b = normalizeWs(String(m[2] ?? ''))
        if (!a || !b) return null
        if (a !== b) return null
        return String(m[1] ?? '').trim()
      }
      const unwrapDimNullSentinelExpr = (s: string): string | null => {
        const src = String(s ?? '')
        if (!src || !src.includes('__FG_DIM_NULL_SENTINEL')) return null
        const lower = src.toLowerCase()
        const marker = /coalesce\s*\(\s*cast\s*\(/ig
        const rawExprs: string[] = []
      while (marker.exec(src) !== null) {
          const start = marker.lastIndex
          let i = start
          let depth = 0
          let inSingle = false
          while (i < src.length) {
            const ch = src[i]
            const next = src[i + 1]
            if (inSingle) {
              if (ch === "'" && next === "'") {
                i += 2
                continue
              }
              if (ch === "'") inSingle = false
              i += 1
              continue
            }
            if (ch === "'") {
              inSingle = true
              i += 1
              continue
            }
            if (ch === '(') {
              depth += 1
              i += 1
              continue
            }
            if (ch === ')') {
              if (depth === 0) break
              depth -= 1
              i += 1
              continue
            }
            if (depth === 0) {
              const tail = lower.slice(i)
              const castEnd = tail.match(/^\s+as\s+varchar\s*\)/i)
              if (castEnd) {
                const rawExpr = src.slice(start, i).trim()
                if (rawExpr) rawExprs.push(rawExpr)
                i += castEnd[0].length
                break
              }
            }
            i += 1
          }
        }
        if (rawExprs.length >= 2) {
          const exprs = rawExprs.map((part) => normalizeWs(part)).filter(Boolean)
          if (!exprs.length) return null
          const first = exprs[0]
          if (exprs.every((part) => part === first)) return rawExprs[0] ?? null
        }
        return null
      }
      const replaceExactOrWhitespaceStable = (sqlIn: string, rawNeedle: string, replacement: string): string => {
        const source = String(sqlIn ?? '')
        const needle = String(rawNeedle ?? '').trim()
        if (!source || !needle) return source
        if (source.includes(needle)) return source.split(needle).join(replacement)
        const escaped = needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '\\s+')
        if (!escaped) return source
        try {
          return source.replace(new RegExp(escaped, 'g'), replacement)
        } catch {
          return source
        }
      }
      for (const nw of nonWindowItems) {
        const raw0 = String(nw.expr || '').trim()
        if (!raw0) continue
        // Heuristic: this substitution is only intended for complex projected expressions.
        // Avoid expensive pattern building for simple column refs and aggregates.
        const rawIsComplex = /\bCASE\b/i.test(raw0) || /\bGROUPING\s*\(/i.test(raw0)
        if (!rawIsComplex) continue
        const cands = [raw0]
        const unwrapped = unwrapGroupingAllExpr(raw0)
        if (unwrapped) cands.push(unwrapped)
        const unwrappedNullSentinel = unwrapDimNullSentinelExpr(raw0)
        if (unwrappedNullSentinel) cands.push(unwrappedNullSentinel)
        if (unwrapped) {
          const nestedNullSentinel = unwrapDimNullSentinelExpr(unwrapped)
          if (nestedNullSentinel) cands.push(nestedNullSentinel)
        }
        for (const raw of cands) {
          if (!raw) continue
          if (raw.length < 24) continue
          // Avoid generating huge regexes (can get slow on big SQL).
          if (raw.length > 80_000) continue
          const nextExpr = replaceExactOrWhitespaceStable(expr, raw, `t.${nw.alias}`)
          if (nextExpr === expr) continue
          expr = nextExpr
        }
      }
      expr = rewriteWindowOrderBy(expr)
      return expr
    }
    const stabilizeWindowProjectionExpr = (exprIn: string, selfAliasRaw?: string): string => {
      let expr = String(exprIn ?? '')
      for (let pass = 0; pass < 4; pass += 1) {
        const nextExpr = rewriteWindowProjectionExpr(expr, selfAliasRaw)
        if (nextExpr === expr) break
        expr = nextExpr
      }
      return expr
    }
    const rewrittenWindowItems = windowItems2.map((it) => ({
      ...it,
      expr: stabilizeWindowProjectionExpr(it.expr, it.alias),
    }))
    noteRewrite({
      windowItemCount: windowItems.length,
      innerExtraCount: innerExtraMap.size,
    })
    if (precomputeOut && precomputeExtrasOut) {
      ;(precomputeOut as any).extras = Array.from(precomputeExtrasOut.entries()).map(([expr, info]) => ({
        expr,
        alias: info.alias,
        kind: info.kind,
      }))
    }

    const rewriteTailWindowExprs = (input: string): string => {
      let out = String(input ?? '')
      if (!out || !windowItems.length || rewrittenWindowItems.length !== windowItems.length) return out
      const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      const replaceLoose = (src: string, fromExpr: string, toExpr: string): string => {
        let next = src.split(fromExpr).join(toExpr)
        if (next !== src) return next
        const toks = fromExpr.split(/\s+/).filter(Boolean)
        if (!toks.length || toks.length > 5000 || fromExpr.length > 80_000) return src
        try {
          const pat = toks.map(escapeRe).join('\\s+')
          next = src.replace(new RegExp(pat, 'g'), toExpr)
          return next
        } catch {
          return src
        }
      }
      for (let i = 0; i < windowItems.length; i++) {
        const beforeExpr = String(windowItems[i]?.expr ?? '').trim()
        const afterExpr = String(rewrittenWindowItems[i]?.expr ?? '').trim()
        if (!beforeExpr || !afterExpr || beforeExpr === afterExpr) continue
        const innerTailExpr = afterExpr
          .replace(/(?:"t"|t)\s*\.\s*"(__lod_\d+|__fg_[A-Za-z0-9_]+)"/ig, (_m, a) => {
            return quoteIdent(String(a || ''))
          })
          .replace(/(?:"t"|t)\s*\.\s*(__lod_\d+|__fg_[A-Za-z0-9_]+)/ig, (_m, a) => {
            return quoteIdent(String(a || '').trim())
          })
        out = replaceLoose(out, beforeExpr, innerTailExpr)
      }
      return out
    }
    let tailAdjusted = rewriteTailWindowExprs(tail)
    let outerQualifyClause = ''
    let outerLimitClause = ''
    let outerWindowClause = ''
    const qualifyIdx = findTopLevelKeyword(tailAdjusted, 'QUALIFY')
    if (qualifyIdx >= 0) {
      const qualifyStart = qualifyIdx + 'QUALIFY'.length
      const orderIdx = findTopLevelKeyword(tailAdjusted, 'ORDER BY')
      const limitIdx = findTopLevelKeyword(tailAdjusted, 'LIMIT')
      let qualifyEnd = tailAdjusted.length
      if (orderIdx > qualifyStart && orderIdx < qualifyEnd) qualifyEnd = orderIdx
      if (limitIdx > qualifyStart && limitIdx < qualifyEnd) qualifyEnd = limitIdx
      const qualifyExpr = tailAdjusted.slice(qualifyStart, qualifyEnd).trim()
      const usesSyntheticWindowAlias = /(?:^|[^A-Za-z0-9_])(?:__lod_\d+|__fg_[A-Za-z0-9_]+)\b/i.test(qualifyExpr)
      if (qualifyExpr && usesSyntheticWindowAlias) {
        // QUALIFY that references synthetic window aliases must run in the outer scope,
        // where those aliases are plain projected columns (not same-level window outputs).
        const beforeQualify = tailAdjusted.slice(0, qualifyIdx).trimEnd()
        const afterQualify = tailAdjusted.slice(qualifyEnd).trimStart()
        tailAdjusted = `${beforeQualify}${afterQualify ? ` ${afterQualify}` : ''}`.trim()
        const outerQualified = qualifyExpr
          .replace(/(^|[^A-Za-z0-9_."'])"(__lod_\d+|__fg_[A-Za-z0-9_]+)"/ig, (_m, p1, a) => `${p1}t.${quoteIdent(String(a || ''))}`)
          .replace(/(^|[^A-Za-z0-9_."'])(__lod_\d+|__fg_[A-Za-z0-9_]+)/ig, (_m, p1, a) => `${p1}t.${quoteIdent(String(a || '').trim())}`)
        outerQualifyClause = outerQualified
        const movedLimitIdx = findTopLevelKeyword(tailAdjusted, 'LIMIT')
        if (movedLimitIdx >= 0) {
          outerLimitClause = tailAdjusted.slice(movedLimitIdx).trim()
          tailAdjusted = tailAdjusted.slice(0, movedLimitIdx).trimEnd()
        }
      }
    }
    const windowIdx = findTopLevelKeyword(tailAdjusted, 'WINDOW')
    if (windowIdx >= 0) {
      const windowStart = windowIdx
      const qualifyIdxAfterWindow = findTopLevelKeyword(tailAdjusted, 'QUALIFY')
      const orderIdxAfterWindow = findTopLevelKeyword(tailAdjusted, 'ORDER BY')
      const limitIdxAfterWindow = findTopLevelKeyword(tailAdjusted, 'LIMIT')
      let windowEnd = tailAdjusted.length
      if (qualifyIdxAfterWindow > windowStart && qualifyIdxAfterWindow < windowEnd) windowEnd = qualifyIdxAfterWindow
      if (orderIdxAfterWindow > windowStart && orderIdxAfterWindow < windowEnd) windowEnd = orderIdxAfterWindow
      if (limitIdxAfterWindow > windowStart && limitIdxAfterWindow < windowEnd) windowEnd = limitIdxAfterWindow
      const extracted = tailAdjusted.slice(windowStart, windowEnd).trim()
      if (extracted) outerWindowClause = extracted
      const beforeWindow = tailAdjusted.slice(0, windowStart).trimEnd()
      const afterWindow = tailAdjusted.slice(windowEnd).trimStart()
      tailAdjusted = `${beforeWindow}${afterWindow ? ` ${afterWindow}` : ''}`.trim()
    }

    const innerSelects: string[] = []
    const innerExtraByAlias = new Map<string, { alias: string; expr: string; kind: 'win' | 'agg' | 'ord' }>()
    const innerAliasSet = new Set<string>()
    const innerExtraAliasSet = new Set<string>()
    const innerExtraExprByAliasKey = new Map<string, string>()
    const isSyntheticOrderAlias = (raw: string | null | undefined): boolean => /^__fg_ord\d+$/i.test(String(raw || '').trim())
    const isGroupedKeyAliasRef = (raw: string | null | undefined): boolean => {
      const expr = String(raw || '').trim()
      return /^(?:(?:"t"|t)\s*\.\s*)?(?:"__fg_group_key_\d+"|__fg_group_key_\d+)$/i.test(expr)
    }
    const collapseDuplicateAnyValueExpr = (rawIn: string): string => {
      const input = String(rawIn ?? '').trim()
      const findMatchingParenLocal = (src: string, openIdx: number): number => {
        if (openIdx < 0 || src[openIdx] !== '(') return -1
        let depth = 0
        let inStr = false
        let inIdent = false
        for (let i = openIdx; i < src.length; i += 1) {
          const ch = src[i]
          if (inStr) {
            if (ch === "'" && src[i + 1] === "'") { i += 1; continue }
            if (ch === "'") inStr = false
            continue
          }
          if (inIdent) {
            if (ch === '"' && src[i + 1] === '"') { i += 1; continue }
            if (ch === '"') inIdent = false
            continue
          }
          if (ch === "'") { inStr = true; continue }
          if (ch === '"') { inIdent = true; continue }
          if (ch === '(') depth += 1
          else if (ch === ')') {
            depth -= 1
            if (depth === 0) return i
          }
        }
        return -1
      }
      const stripOuterParensLocal = (exprRaw: string): string => {
        let expr = String(exprRaw ?? '').trim()
        while (expr.startsWith('(') && expr.endsWith(')')) {
          const end = findMatchingParenLocal(expr, 0)
          if (end !== expr.length - 1) break
          expr = expr.slice(1, -1).trim()
        }
        return expr
      }
      const parseWholeAggregateCall = (
        exprRaw: string,
      ): { fn: string; inner: string } | null => {
        const expr = String(exprRaw ?? '').trim()
        const match = expr.match(/^([A-Za-z_][A-Za-z0-9_]*)\s*\(/)
        if (!match) return null
        const fn = String(match[1] ?? '').trim().toUpperCase()
        if (!fn) return null
        const openIdx = expr.indexOf('(', match.index ?? 0)
        if (openIdx < 0) return null
        const closeIdx = findMatchingParenLocal(expr, openIdx)
        if (closeIdx !== expr.length - 1) return null
        return {
          fn,
          inner: expr.slice(openIdx + 1, closeIdx),
        }
      }
      const outer = parseWholeAggregateCall(input)
      if (!outer) return input
      const strippedInner = stripOuterParensLocal(outer.inner)
      if (
        /^(?:MIN|MAX|ANY_VALUE)$/i.test(outer.fn)
        && /^CASE\s*\(\s*ANY_VALUE\s*\(/i.test(strippedInner)
      ) {
        return strippedInner
      }
      const inner = parseWholeAggregateCall(strippedInner)
      if (!inner || inner.fn !== 'ANY_VALUE') return input
      const normalizedInnerArg = stripOuterParensLocal(inner.inner)
      if (outer.fn === 'ANY_VALUE') {
        return `ANY_VALUE(${normalizedInnerArg})`
      }
      if (outer.fn === 'MIN' || outer.fn === 'MAX') {
        return `${outer.fn}(${normalizedInnerArg})`
      }
      return input
    }
    const resolveProjectedAliasExpr = (raw: string | null | undefined): string => {
      const expr = String(raw || '').trim()
      if (!expr) return ''
      const match = expr.match(/^(?:(?:"t"|t)\s*\.\s*)?(?:"([^"]+)"|([A-Za-z_][A-Za-z0-9_]*))$/i)
      const ref = String(match?.[1] ?? match?.[2] ?? '').trim()
      const key = normalizeAliasName(ref)
      if (!key) return ''
      return String(aliasExprByKey.get(key) ?? '').trim()
    }
    const exprHasNonWindowAggregate = (exprRaw: string): boolean => {
      const expr = String(exprRaw || '')
      const spans = findAggregateCallSpans(expr)
      if (spans.some((agg) => !isWindowedAggregateSpan(expr, agg))) return true
      // Fallback heuristic: parser can miss some deeply nested expressions.
      // If we see plain aggregate calls in a non-window projection, treat passthrough
      // columns as aggregate-sensitive in this scope.
      return /\b(?:SUM|COUNT|AVG|MIN|MAX|ANY_VALUE)\s*\(/i.test(expr) && !/\bOVER\s*\(/i.test(expr)
    }
    const exprDependsOnAggregateAlias = (exprRaw: string, seen?: Set<string>): boolean => {
      const expr = String(exprRaw || '').trim()
      if (!expr) return false
      const localSeen = new Set(seen ?? [])
      const candidateAliasKeys = new Set<string>()
      for (const ref of collectColumnRefs(expr)) {
        const key = normalizeAliasName(ref.col)
        if (key && aliasExprByKey.has(key)) candidateAliasKeys.add(key)
      }
      try {
        let inStr = false
        for (let i = 0; i < expr.length; i++) {
          const ch = expr[i]
          if (ch === "'") {
            if (inStr && expr[i + 1] === "'") { i++; continue }
            inStr = !inStr
            continue
          }
          if (inStr || ch !== '"') continue
          let j = i + 1
          let quoted = ''
          while (j < expr.length) {
            const next = expr[j]
            if (next === '"') {
              if (expr[j + 1] === '"') { quoted += '"'; j += 2; continue }
              break
            }
            quoted += next
            j++
          }
          const key = normalizeAliasName(quoted)
          if (key && aliasExprByKey.has(key)) candidateAliasKeys.add(key)
          i = j
        }
      } catch {
        // Ignore alias token scan issues and fall back to direct column refs.
      }
      for (const key of candidateAliasKeys) {
        if (!key || localSeen.has(key)) continue
        if (aggregateSensitiveProjectedAliasKeys.has(key)) return true
        localSeen.add(key)
        const aliasExpr = String(aliasExprByKey.get(key) ?? '').trim()
        if (!aliasExpr) continue
        if (exprHasNonWindowAggregate(aliasExpr)) return true
        if (exprDependsOnAggregateAlias(aliasExpr, localSeen)) return true
      }
      return false
    }
    let rewroteInnerProjection = false
    const hasSelectAggregateExpr = nonWindowItems.some((it) => exprHasNonWindowAggregate(String(it.expr || '')))
    const hasLiftedAggExtras = Array.from(innerExtraMap.values()).some((entry) => String(entry?.kind || '') === 'agg')
    const needsAggSafePassthrough =
      hasGroupedProjectionScope ||
      hasLiftedAggExtras ||
      hasSelectAggregateExpr ||
      hasAggregateTokenInSelectList
    const aggregateSensitiveProjectedAliasKeys = new Set<string>()
    const noteAggregateSensitiveProjectedAlias = (aliasRaw: string | null | undefined): void => {
      const key = normalizeAliasName(aliasRaw)
      if (key) aggregateSensitiveProjectedAliasKeys.add(key)
    }
    for (const [, entry] of innerExtraMap.entries()) {
      const key = normalizeAliasName(entry.alias)
      if (key) {
        innerExtraAliasSet.add(key)
        if (!innerExtraExprByAliasKey.has(key)) {
          innerExtraExprByAliasKey.set(key, String(entry?.expr ?? '').trim())
        }
      }
    }
    for (const it of nonWindowItems) {
      const originalInnerExpr = String(it.expr || '').trim()
      let innerExpr = originalInnerExpr
      const originalHadPlainAggregate = exprHasNonWindowAggregate(originalInnerExpr)
      if (needsAggSafePassthrough && innerExpr) {
        const exprNorm = normalizeSqlExpr(innerExpr)
        const colKey = extractSimpleSourceColumnKey(innerExpr)
        const groupedKeyExpr =
          isGroupedKeyAliasRef(innerExpr) ||
          Boolean(colKey && isGroupedKeyAliasRef(colKey))
        const groupedByExpr = groupedExprNormalized.has(exprNorm)
        const groupedByColumn = Boolean(colKey && groupedSourceColumnKeys.has(colKey))
        const needsGroupWrap = hasGroupedProjectionScope && !groupedByExpr && !groupedByColumn
        const needsAggWrap = hasLiftedAggExtras || hasSelectAggregateExpr
        const hasWindowCall = /\bOVER\s*\(/i.test(innerExpr)
        const aggregateSpans = findAggregateCallSpans(innerExpr)
        const hasPlainAggregate =
          aggregateSpans.some((agg) => !isWindowedAggregateSpan(innerExpr, agg)) ||
          (/\b(?:SUM|COUNT|AVG|MIN|MAX|ANY_VALUE)\s*\(/i.test(innerExpr) && !hasWindowCall)
        const hasSourceColumnRefs =
          Boolean(colKey) ||
          collectColumnRefs(innerExpr).some((ref) => Boolean(normalizeAliasName(String(ref?.col ?? ''))))
        const needsComplexExprWrap =
          (needsGroupWrap || needsAggWrap) &&
          !colKey &&
          !groupedByExpr &&
          hasSourceColumnRefs &&
          !hasPlainAggregate &&
          !hasWindowCall
        const dependsOnAggregateAlias = exprDependsOnAggregateAlias(innerExpr)
        // Wrapping passthroughs with `ANY_VALUE` keeps deferred aggregate layers well-formed
        // when extras lift plain aggregate calls into the SELECT list (every passthrough source
        // ref normally has to be wrapped to satisfy SQL grouping rules). But columns that are
        // already in the source GROUP BY (or are `__fg_group_key_*` synthesized grouping
        // aliases that always participate in the layer's GROUP BY) are functionally constant
        // per group, so wrapping them in `ANY_VALUE` is redundant — and worse, hides the column
        // from `stripRedundantAnyValueAroundGroupKeys` because the stripper only matches
        // GROUP BY expressions verbatim. Leaving the redundant wraps in place forces every
        // Monthly / Weekly Comparison MNV chunk to re-aggregate the grouped time keys at every
        // deferred layer, which is exactly the OOM regression the canonical-base refactor was
        // meant to prevent. Treat `groupedKeyExpr` the same way `groupedByExpr` /
        // `groupedByColumn` already are: skip the wrap entirely for both the
        // grouped-projection-scope path (`needsGroupWrap`) and the lifted-aggregate path
        // (`aggWrapNeeded`).
        const groupWrapNeeded =
          needsGroupWrap && !groupedKeyExpr
        const aggWrapNeeded =
          needsAggWrap && !groupedByExpr && !groupedByColumn && !groupedKeyExpr
        if (
          !hasPlainAggregate &&
          (
            ((groupWrapNeeded || aggWrapNeeded) && colKey) ||
            needsComplexExprWrap ||
            (dependsOnAggregateAlias && !groupedKeyExpr)
          )
        ) {
          innerExpr = `ANY_VALUE(${innerExpr})`
        }
      }
      if (!originalHadPlainAggregate && exprHasNonWindowAggregate(innerExpr)) {
        noteAggregateSensitiveProjectedAlias(it.alias)
      }
      if (innerExpr !== originalInnerExpr) rewroteInnerProjection = true
      innerSelects.push(`${innerExpr} AS ${it.alias}`)
      const key = normalizeAliasName(it.alias)
      if (key) innerAliasSet.add(key)
    }
    for (const alias of precomputedAliasSet) {
      const key = normalizeAliasName(alias)
      if (key && innerAliasSet.has(key)) continue
      const groupedKeyAlias =
        isGroupedKeyAliasRef(alias) ||
        Boolean(key && isGroupedKeyAliasRef(key))
      const projectedExpr =
        (isSyntheticOrderAlias(key))
          ? `t.${quoteIdent(alias)}`
          : (needsAggSafePassthrough)
          ? `ANY_VALUE(t.${quoteIdent(alias)})`
          : `t.${quoteIdent(alias)}`
      if (/^\s*ANY_VALUE\s*\(/i.test(projectedExpr)) {
        noteAggregateSensitiveProjectedAlias(alias)
      }
      if (projectedExpr !== `t.${quoteIdent(alias)}`) rewroteInnerProjection = true
      innerSelects.push(`${projectedExpr} AS ${quoteIdent(alias)}`)
      if (key) innerAliasSet.add(key)
    }
    // Rewritten outer window expressions can still reference source columns (e.g. partition keys,
    // CASE predicates). Ensure those columns are projected by the inner query.
    const requiredSourceCols = new Map<string, string>()
    const noteRequiredSourceCol = (rawColCandidate: string): void => {
      const rawCol = String(rawColCandidate ?? '').trim()
      const resolved = resolveKnownSourceColumn(rawCol) || rawCol
      const key = normalizeAliasName(resolved)
      if (!resolved || !key) return
      if (innerAliasSet.has(key)) return
      if (innerExtraAliasSet.has(key)) return
      if (!requiredSourceCols.has(key)) requiredSourceCols.set(key, resolved)
    }
    const collectRequiredSourceColsFromAliasDeps = (exprRaw: string, seenAliasKeys?: Set<string>) => {
      const refs = collectColumnRefs(String(exprRaw ?? ''))
      const localSeen = new Set(seenAliasKeys ?? [])
      for (const r of refs) {
        const rawCol = String(r?.col ?? '').trim()
        const key = normalizeAliasName(rawCol)
        if (!rawCol || !key) continue
        const resolved = resolveKnownSourceColumn(rawCol)
        if (resolved) {
          noteRequiredSourceCol(resolved)
          continue
        }
        const aliasExpr = String(aliasExprByKey.get(key) ?? innerExtraExprByAliasKey.get(key) ?? '').trim()
        if (!aliasExpr || localSeen.has(key)) continue
        localSeen.add(key)
        collectRequiredSourceColsFromAliasDeps(aliasExpr, localSeen)
      }
    }
    const stripNonWindowAggregateRefs = (exprRaw: string): string => {
      const expr = String(exprRaw ?? '')
      if (!expr) return expr
      const spans = findAggregateCallSpans(expr)
        .filter((agg) => !isWindowedAggregateSpan(expr, agg))
        .sort((left, right) => right.start - left.start)
      if (spans.length <= 0) return expr
      let next = expr
      for (const span of spans) {
        const start = Math.max(0, Number(span?.start ?? -1))
        const end = Math.max(start, Number(span?.end ?? -1))
        if (end <= start) continue
        next = `${next.slice(0, start)}__fg_agg_ref__${next.slice(end)}`
      }
      return next
    }
    const collectRequiredSourceColsFrom = (exprRaw: string) => {
      const refs = collectColumnRefs(stripNonWindowAggregateRefs(String(exprRaw ?? '')))
      for (const r of refs) {
        const rawCol = String(r?.col ?? '').trim()
        const key = normalizeAliasName(rawCol)
        if (!rawCol || !key) continue
        const aliasExpr = String(aliasExprByKey.get(key) ?? innerExtraExprByAliasKey.get(key) ?? '').trim()
        if (aliasExpr) {
          collectRequiredSourceColsFromAliasDeps(aliasExpr, new Set([key]))
        }
        noteRequiredSourceCol(rawCol)
      }
    }
    const stripTopLevelFromSourceSql = (s: string): string => {
      const src = String(s || '')
      const fromIdx = findTopLevelKeyword(src, 'FROM')
      if (fromIdx < 0) return src
      let i = fromIdx + 4
      while (i < src.length && /\s/.test(src[i])) i++
      if (i >= src.length) return src
      if (src[i] === '(') {
        let depth = 0
        let inStr = false
        let inIdent = false
        for (let j = i; j < src.length; j++) {
          const ch = src[j]
          if (ch === "'" && !inIdent) {
            if (inStr && src[j + 1] === "'") { j++; continue }
            inStr = !inStr
            continue
          }
          if (!inStr && ch === '"') {
            if (inIdent && src[j + 1] === '"') { j++; continue }
            inIdent = !inIdent
            continue
          }
          if (inStr || inIdent) continue
          if (ch === '(') depth++
          else if (ch === ')') {
            depth--
            if (depth === 0) return src.slice(j + 1)
          }
        }
        return src
      }
      let j = i
      while (j < src.length) {
        const ch = src[j]
        if (/\s/.test(ch) || ch === ',' || ch === ')') break
        j++
      }
      return src.slice(j)
    }
    for (const it of rewrittenWindowItems) {
      collectRequiredSourceColsFrom(String(it?.expr ?? ''))
    }
    for (const [, entry] of innerExtraMap.entries()) {
      collectRequiredSourceColsFrom(String(entry?.expr ?? ''))
    }
    collectRequiredSourceColsFrom(stripTopLevelFromSourceSql(tailAdjusted))
    collectRequiredSourceColsFrom(String(outerWindowClause || ''))
    collectRequiredSourceColsFrom(String(outerQualifyClause || ''))
    const requiredSourceGroupExprs = Array.from(requiredSourceCols.values()).map((rawCol) => `t.${quoteIdent(rawCol)}`)
    if (requiredSourceGroupExprs.length > 0) {
      // If the existing GROUP BY uses GROUPING SETS, merging raw source columns into
      // every set tuple explodes group cardinality (each raw value becomes a distinct
      // group member) and defeats aggregation — causing long DuckDB timeouts on the
      // aggregate-base source. Skip adding these expressions to the GROUP BY in that
      // case; the passthrough loop below will wrap the raw columns with ANY_VALUE()
      // so outer window functions still see per-group values without disturbing the
      // grouping cardinality.
      const groupByIdx = findTopLevelKeyword(tailAdjusted, 'GROUP BY')
      let existingGroupByIsGroupingSets = false
      if (groupByIdx >= 0) {
        const afterGroupBy = tailAdjusted.slice(groupByIdx + 'GROUP BY'.length).trimStart()
        existingGroupByIsGroupingSets = /^GROUPING\s+SETS\b/i.test(afterGroupBy)
      }
      if (!existingGroupByIsGroupingSets) {
        tailAdjusted = appendTopLevelGroupByExprs(tailAdjusted, requiredSourceGroupExprs)
        for (const rawCol of requiredSourceCols.values()) {
          const key = normalizeAliasName(rawCol)
          if (key) groupedSourceColumnKeys.add(key)
          const groupedExpr = normalizeSqlExpr(`t.${quoteIdent(rawCol)}`)
          if (groupedExpr) groupedExprNormalized.add(groupedExpr)
        }
      }
    }
    for (const [, rawCol] of requiredSourceCols.entries()) {
      const rawKey = normalizeAliasName(rawCol)
      const groupedKeyAlias =
        isGroupedKeyAliasRef(rawCol) ||
        Boolean(rawKey && isGroupedKeyAliasRef(rawKey))
      const passthroughExpr =
        (isSyntheticOrderAlias(rawKey))
          ? `t.${quoteIdent(rawCol)}`
          : ((needsAggSafePassthrough && !(rawKey && groupedSourceColumnKeys.has(rawKey))) || groupedKeyAlias)
          ? `ANY_VALUE(t.${quoteIdent(rawCol)})`
          : `t.${quoteIdent(rawCol)}`
      if (/^\s*ANY_VALUE\s*\(/i.test(passthroughExpr)) {
        noteAggregateSensitiveProjectedAlias(rawCol)
      }
      if (passthroughExpr !== `t.${quoteIdent(rawCol)}`) rewroteInnerProjection = true
      innerSelects.push(`${passthroughExpr} AS ${quoteIdent(rawCol)}`)
      const key = normalizeAliasName(rawCol)
      if (key) innerAliasSet.add(key)
    }
    for (const [, entry] of innerExtraMap.entries()) {
      const key = normalizeAliasName(entry.alias)
      if (!key) continue
      const extraKind: 'win' | 'agg' | 'ord' =
        String(entry.kind) === 'win'
          ? 'win'
          : (String(entry.kind) === 'agg' ? 'agg' : 'ord')
      let extraExpr = String(entry.expr ?? '').trim()
      extraExpr = collapseDuplicateAnyValueExpr(extraExpr)
      if (extraExpr && extraKind === 'win') {
        extraExpr = stabilizeWindowProjectionExpr(extraExpr)
      }
      if (extraExpr && extraKind === 'ord' && needsAggSafePassthrough) {
        const hasAggregateWrapper = /\b(?:MIN|MAX|SUM|AVG|COUNT|ANY_VALUE)\s*\(/i.test(extraExpr)
        const hasWindowCall = /\bOVER\s*\(/i.test(extraExpr)
        const projectedAliasExpr = resolveProjectedAliasExpr(extraExpr)
        const projectedAliasRef = Boolean(projectedAliasExpr)
        const dependsOnAggregateAlias = exprDependsOnAggregateAlias(extraExpr)
        const isSimplePassthroughExpr = /^(?:(?:"t"|t)\s*\.\s*)?(?:"[^"]+"|[A-Za-z_][A-Za-z0-9_]*)$/i.test(extraExpr)
        if (!hasAggregateWrapper && !hasWindowCall && !projectedAliasRef && !dependsOnAggregateAlias && isSimplePassthroughExpr) {
          const dirHint = (() => {
            // Synthetic order aliases can be reused across rewritten windows.
            // If all usages agree on direction, preserve ordering semantics with MIN/MAX;
            // otherwise fall back to ANY_VALUE for binder-safe projection.
            const lowerAlias = String(entry.alias ?? '').trim().toLowerCase()
            if (!lowerAlias) return null as 'ASC' | 'DESC' | null
            let seenAsc = false
            let seenDesc = false
            const scanExpr = (rawExpr: string) => {
              const reQuoted = /(?:ORDER\s+BY|,)\s*(?:"t"|t)\s*\.\s*"(__fg_ord\d+)"\s+(ASC|DESC)\b/ig
              const reBare = /(?:ORDER\s+BY|,)\s*(?:"t"|t)\s*\.\s*(__fg_ord\d+)\s+(ASC|DESC)\b/ig
              let m: RegExpExecArray | null
              while ((m = reQuoted.exec(rawExpr))) {
                const aliasHit = String(m[1] ?? '').trim().toLowerCase()
                if (aliasHit !== lowerAlias) continue
                const dirHit = String(m[2] ?? '').toUpperCase()
                if (dirHit === 'ASC') seenAsc = true
                else if (dirHit === 'DESC') seenDesc = true
              }
              while ((m = reBare.exec(rawExpr))) {
                const aliasHit = String(m[1] ?? '').trim().toLowerCase()
                if (aliasHit !== lowerAlias) continue
                const dirHit = String(m[2] ?? '').toUpperCase()
                if (dirHit === 'ASC') seenAsc = true
                else if (dirHit === 'DESC') seenDesc = true
              }
            }
            for (const it of rewrittenWindowItems) {
              scanExpr(String(it?.expr ?? ''))
              if (seenAsc && seenDesc) break
            }
            if (seenAsc && !seenDesc) return 'ASC'
            if (seenDesc && !seenAsc) return 'DESC'
            return null
          })()
          if (dirHint === 'DESC') {
            extraExpr = `MAX(${extraExpr})`
          } else if (dirHint === 'ASC') {
            extraExpr = `MIN(${extraExpr})`
          } else {
            extraExpr = `ANY_VALUE(${extraExpr})`
          }
          extraExpr = collapseDuplicateAnyValueExpr(extraExpr)
          rewroteInnerProjection = true
        }
      }
      innerExtraByAlias.set(key, {
        alias: String(entry.alias),
        expr: extraExpr,
        kind: extraKind,
      })
    }

    const outerSelects: string[] = []
    // Critical: preserve original SELECT item order.
    // UNION branches rely on positional column alignment; reordering window items to the end
    // will silently corrupt downstream columns (e.g. "Measure Values" becomes a dim string).
    const rewrittenByAlias = new Map<string, string>()
    for (const it of rewrittenWindowItems) {
      rewrittenByAlias.set(it.alias, it.expr)
    }
    for (const it of items) {
      const rewritten = rewrittenByAlias.get(it.alias)
      if (rewritten) {
        if (traceRewrite && /__lod_\d+/.test(rewritten)) {
          console.log(`[windowRewrite] OUTER SELECT (rewritten) "${it.alias}" still has __lod ref! preview:`, rewritten.slice(0, 200))
        }
        outerSelects.push(`${rewritten} AS ${it.alias}`)
      } else {
        outerSelects.push(`t.${it.alias} AS ${it.alias}`)
      }
    }

    // Keep only extras that the rewritten outer projection actually references.
    const requiredInnerAliases = new Set<string>()
    const refReQuoted = /(?:^|[^A-Za-z0-9_])(?:"t"|t)\s*\.\s*"([^"]+)"/ig
    const refReBare = /(?:^|[^A-Za-z0-9_])(?:t)\s*\.\s*([A-Za-z_][A-Za-z0-9_]*)/ig
    const refReSyntheticQuoted = /"(__lod_\d+|__fg_[A-Za-z0-9_]+)"/ig
    const refReSyntheticBare = /\b(__lod_\d+|__fg_[A-Za-z0-9_]+)\b/ig
    const collectRequiredAliasesFrom = (rawIn: string) => {
      const raw = String(rawIn || '')
      let m: RegExpExecArray | null
      while ((m = refReQuoted.exec(raw))) {
        const k = normalizeAliasName(String(m[1] || ''))
        if (k) requiredInnerAliases.add(k)
      }
      while ((m = refReBare.exec(raw))) {
        const k = normalizeAliasName(String(m[1] || ''))
        if (k) requiredInnerAliases.add(k)
      }
      while ((m = refReSyntheticQuoted.exec(raw))) {
        const k = normalizeAliasName(String(m[1] || ''))
        if (k) requiredInnerAliases.add(k)
      }
      while ((m = refReSyntheticBare.exec(raw))) {
        const k = normalizeAliasName(String(m[1] || ''))
        if (k) requiredInnerAliases.add(k)
      }
    }
    for (const sel of outerSelects) {
      collectRequiredAliasesFrom(sel)
    }
    collectRequiredAliasesFrom(tailAdjusted)
    collectRequiredAliasesFrom(outerWindowClause)
    collectRequiredAliasesFrom(outerQualifyClause)
    collectRequiredAliasesFrom(outerLimitClause)
    const collectReferencedProjectedAliasKeys = (exprIn: string, knownAliasKeys: Set<string>): Set<string> => {
      const out = new Set<string>()
      const refs = collectColumnRefs(String(exprIn || ''))
      for (const ref of refs) {
        const key = normalizeAliasName(String(ref?.col ?? ''))
        if (key && knownAliasKeys.has(key)) out.add(key)
      }
      return out
    }
    const projectedAliasKeysBeforeExtras = new Set(innerAliasSet)
    const allExtraAliasKeys = new Set<string>(innerExtraByAlias.keys())
    const allDependencyAliasKeys = new Set<string>([
      ...Array.from(projectedAliasKeysBeforeExtras),
      ...Array.from(allExtraAliasKeys),
    ])
    const requiredRetainedExtraAliasKeys = new Set<string>()
    const requiredProjectedAliasKeys = new Set<string>(
      Array.from(requiredInnerAliases).filter((key) => projectedAliasKeysBeforeExtras.has(key))
    )
    const pendingRequiredExtraKeys = Array.from(requiredInnerAliases).filter((key) => allExtraAliasKeys.has(key))
    while (pendingRequiredExtraKeys.length > 0) {
      const key = String(pendingRequiredExtraKeys.pop() || '').trim().toLowerCase()
      if (!key || requiredRetainedExtraAliasKeys.has(key)) continue
      requiredRetainedExtraAliasKeys.add(key)
      const extra = innerExtraByAlias.get(key)
      if (!extra) continue
      const refs = collectReferencedProjectedAliasKeys(String(extra.expr || ''), allDependencyAliasKeys)
      for (const refKey of refs) {
        if (projectedAliasKeysBeforeExtras.has(refKey)) {
          requiredProjectedAliasKeys.add(refKey)
          continue
        }
        if (allExtraAliasKeys.has(refKey) && !requiredRetainedExtraAliasKeys.has(refKey)) {
          pendingRequiredExtraKeys.push(refKey)
        }
      }
    }
    const retainedExtraAliasKeys = new Set<string>()
    const retainedExtras: Array<{ key: string; alias: string; expr: string; kind: 'win' | 'agg' | 'ord' }> = []
    for (const [key, extra] of innerExtraByAlias.entries()) {
      const isSyntheticOrder = /^__fg_ord\d+$/i.test(String(extra.alias || '').trim())
      if (!isSyntheticOrder && !requiredRetainedExtraAliasKeys.has(key)) continue
      if (innerAliasSet.has(key)) continue
      const extraExpr = String(extra.expr || '').trim()
      const extraDependsOnAggregateAlias = exprDependsOnAggregateAlias(extraExpr)
      const groupedSyntheticOrderExpr =
        needsAggSafePassthrough &&
        isSyntheticOrder &&
        extraExpr &&
        !extraDependsOnAggregateAlias &&
        !/^\s*(?:ANY_VALUE|MIN|MAX|SUM|AVG|COUNT)\s*\(/i.test(extraExpr)
          ? `ANY_VALUE(${extraExpr})`
          : extraExpr
      retainedExtraAliasKeys.add(key)
      retainedExtras.push({
        key,
        alias: String(extra.alias || '').trim(),
        expr: collapseDuplicateAnyValueExpr(groupedSyntheticOrderExpr),
        kind: extra.kind,
      })
    }
    const stagedAliasKeys = new Set<string>([
      ...Array.from(projectedAliasKeysBeforeExtras),
      ...Array.from(retainedExtraAliasKeys),
    ])
    const baseStageExtras: Array<{ key: string; alias: string; expr: string; kind: 'win' | 'agg' | 'ord' }> = []
    const deferredStageExtras: Array<{ key: string; alias: string; expr: string; kind: 'win' | 'agg' | 'ord' }> = []
    for (const extra of retainedExtras) {
      const refs = collectReferencedProjectedAliasKeys(extra.expr, stagedAliasKeys)
      if (refs.size === 0) baseStageExtras.push(extra)
      else deferredStageExtras.push(extra)
    }
    const extractProjectedAliasName = (selectExpr: string): string => {
      const raw = String(selectExpr || '').trim()
      if (!raw) return ''
      const quoted = raw.match(/\bAS\s+"([^"]+)"\s*$/i)
      if (quoted) return String(quoted[1] || '').trim()
      const bare = raw.match(/\bAS\s+([A-Za-z_][A-Za-z0-9_]*)\s*$/i)
      if (bare) return String(bare[1] || '').trim()
      return ''
    }
    const baseInnerSelects = [...innerSelects]
    for (const extra of baseStageExtras) {
      if (!extra.alias) continue
      baseInnerSelects.push(`${extra.expr} AS ${quoteIdent(extra.alias)}`)
      innerAliasSet.add(extra.key)
    }
    const baseInnerProjectedAliasNames = new Set<string>(
      baseInnerSelects
        .map((selectExpr) => extractProjectedAliasName(selectExpr))
        .filter(Boolean)
    )
    const requiredExactProjectedAliases = new Set<string>()
    const noteRequiredExactProjectedAliasesFrom = (rawIn: string): void => {
      const raw = String(rawIn || '')
      const refReQuoted = /(?:^|[^A-Za-z0-9_])(?:"t"|t)\s*\.\s*"([^"]+)"/ig
      let m: RegExpExecArray | null
      while ((m = refReQuoted.exec(raw))) {
        const alias = String(m[1] || '').trim()
        if (alias) requiredExactProjectedAliases.add(alias)
      }
    }
    for (const sel of outerSelects) noteRequiredExactProjectedAliasesFrom(sel)
    noteRequiredExactProjectedAliasesFrom(tailAdjusted)
    noteRequiredExactProjectedAliasesFrom(outerWindowClause)
    noteRequiredExactProjectedAliasesFrom(outerQualifyClause)
    noteRequiredExactProjectedAliasesFrom(outerLimitClause)
    for (const extra of retainedExtras) {
      noteRequiredExactProjectedAliasesFrom(String(extra?.expr ?? ''))
    }
    const projectedExprBodyByAliasKey = new Map<string, string>()
    for (const selectExpr of baseInnerSelects) {
      const raw = String(selectExpr || '').trim()
      const quoted = raw.match(/^(.*)\s+AS\s+"([^"]+)"$/i)
      const bare = quoted ? null : raw.match(/^(.*)\s+AS\s+([A-Za-z_][A-Za-z0-9_]*)$/i)
      const exprBody = String(quoted?.[1] ?? bare?.[1] ?? '').trim()
      const aliasName = String(quoted?.[2] ?? bare?.[2] ?? '').trim()
      const key = normalizeAliasName(aliasName)
      if (!exprBody || !aliasName || !key || projectedExprBodyByAliasKey.has(key)) continue
      projectedExprBodyByAliasKey.set(key, exprBody)
    }
    for (const aliasName of requiredExactProjectedAliases) {
      const key = normalizeAliasName(aliasName)
      if (!aliasName || !key || baseInnerProjectedAliasNames.has(aliasName)) continue
      const exprBody = projectedExprBodyByAliasKey.get(key)
      if (!exprBody) continue
      // Preserve the exact grouped alias spelling (`"Field Supervisor"`, `"Location"`, etc.)
      // when later deferred window stages reference it, even if this stage only carried the
      // normalized/raw alias form (`"field supervisor"`, `"location"`).
      baseInnerSelects.push(`${exprBody} AS ${quoteIdent(aliasName)}`)
      baseInnerProjectedAliasNames.add(aliasName)
      rewroteInnerProjection = true
    }
    if (needsAggSafePassthrough) {
      for (let i = 0; i < baseInnerSelects.length; i++) {
        const raw = String(baseInnerSelects[i] || '').trim()
        const quoted = raw.match(/^(?:"t"|t)\s*\.\s*"(__fg_ord\d+)"\s+AS\s+"([^"]+)"$/i)
        if (quoted) {
          baseInnerSelects[i] = `ANY_VALUE(t.${quoteIdent(String(quoted[1] || '').trim())}) AS ${quoteIdent(String(quoted[2] || '').trim())}`
          continue
        }
        const bare = raw.match(/^(?:"t"|t)\s*\.\s*(__fg_ord\d+)\s+AS\s+"([^"]+)"$/i)
        if (bare) {
          baseInnerSelects[i] = `ANY_VALUE(t.${quoteIdent(String(bare[1] || '').trim())}) AS ${quoteIdent(String(bare[2] || '').trim())}`
          continue
        }
        const aliasMatch = raw.match(/^(.*)\s+AS\s+"([^"]+)"$/i)
        const exprBody = String(aliasMatch?.[1] || '').trim()
        const aliasName = String(aliasMatch?.[2] || '').trim()
        const hasAggregateWrapper = /\b(?:MIN|MAX|SUM|AVG|COUNT|ANY_VALUE)\s*\(/i.test(exprBody)
        const hasWindowCall = /\bOVER\s*\(/i.test(exprBody)
        const dependsOnInternalAggregateAlias =
          /__(?:fg_(?:agg|win)\d+|lod_\d+)/i.test(exprBody) ||
          exprDependsOnAggregateAlias(exprBody)
        if (exprBody && aliasName && !hasAggregateWrapper && !hasWindowCall && dependsOnInternalAggregateAlias) {
          baseInnerSelects[i] = `ANY_VALUE(${exprBody}) AS ${quoteIdent(aliasName)}`
        }
      }
    }
    const baseInnerHasPlainAggregates = baseInnerSelects.some((selectExpr) => {
      const raw = String(selectExpr || '').trim()
      return /\b(?:MIN|MAX|SUM|AVG|COUNT|ANY_VALUE)\s*\(/i.test(raw) && !/\bOVER\s*\(/i.test(raw)
    })
    if (baseInnerHasPlainAggregates) {
      for (let i = 0; i < baseInnerSelects.length; i++) {
        const raw = String(baseInnerSelects[i] || '').trim()
        const quotedLod = raw.match(/^(?:"t"|t)\s*\.\s*"(__lod_\d+)"\s+AS\s+"([^"]+)"$/i)
        if (quotedLod) {
          baseInnerSelects[i] = `ANY_VALUE(t.${quoteIdent(String(quotedLod[1] || '').trim())}) AS ${quoteIdent(String(quotedLod[2] || '').trim())}`
          continue
        }
        const bareLod = raw.match(/^(?:"t"|t)\s*\.\s*(__lod_\d+)\s+AS\s+([A-Za-z_][A-Za-z0-9_]*)$/i)
        if (bareLod) {
          baseInnerSelects[i] = `ANY_VALUE(t.${quoteIdent(String(bareLod[1] || '').trim())}) AS ${quoteIdent(String(bareLod[2] || '').trim())}`
        }
      }
      const baseInnerGroupExprs = Array.from(new Set(
        baseInnerSelects
          .map((selectExpr) => {
            const raw = String(selectExpr || '').trim()
            if (!raw) return ''
            const quoted = raw.match(/^(?:"t"|t)\s*\.\s*"([^"]+)"\s+AS\s+"([^"]+)"$/i)
            if (quoted) return `t.${quoteIdent(String(quoted[1] || '').trim())}`
            const bare = raw.match(/^(?:"t"|t)\s*\.\s*([A-Za-z_][A-Za-z0-9_]*)\s+AS\s+([A-Za-z_][A-Za-z0-9_]*)$/i)
            if (bare) return `t.${quoteIdent(String(bare[1] || '').trim())}`
            return ''
          })
          .filter(Boolean)
      ))
      if (baseInnerGroupExprs.length > 0) {
        // Same GROUPING SETS protection as above: avoid injecting raw passthrough
        // columns into every grouping-set tuple, which would explode cardinality.
        const groupByIdx = findTopLevelKeyword(tailAdjusted, 'GROUP BY')
        let existingGroupByIsGroupingSets = false
        if (groupByIdx >= 0) {
          const afterGroupBy = tailAdjusted.slice(groupByIdx + 'GROUP BY'.length).trimStart()
          existingGroupByIsGroupingSets = /^GROUPING\s+SETS\b/i.test(afterGroupBy)
        }
        if (!existingGroupByIsGroupingSets) {
          tailAdjusted = appendTopLevelGroupByExprs(tailAdjusted, baseInnerGroupExprs, { createIfMissing: true })
        }
      }
    }

    const hasExtras = retainedExtras.length > 0
    if (!hasWindowItems && !hasExtras && !rewroteInnerProjection) {
      return sql
    }

    const projectedLayerAliasByKey = new Map<string, { key: string; alias: string }>()
    const projectedLayerExprByKey = new Map<string, string>()
    const preferredProjectedLayerAliasByKey = new Map<string, string>()
    for (const aliasName of requiredExactProjectedAliases) {
      const key = normalizeAliasName(aliasName)
      if (aliasName && key) preferredProjectedLayerAliasByKey.set(key, aliasName)
    }
    const noteProjectedLayerAlias = (aliasRaw: string): void => {
      const alias = String(aliasRaw || '').trim()
      const key = normalizeAliasName(alias)
      if (!alias || !key) return
      const preferredAlias = preferredProjectedLayerAliasByKey.get(key)
      if (
        !projectedLayerAliasByKey.has(key) ||
        (preferredAlias && alias === preferredAlias)
      ) {
        projectedLayerAliasByKey.set(key, { key, alias })
      }
    }
    for (const sel of baseInnerSelects) {
      const alias = extractProjectedAliasName(sel)
      const key = normalizeAliasName(alias)
      if (!alias || !key) continue
      noteProjectedLayerAlias(alias)
      const preferredAlias = preferredProjectedLayerAliasByKey.get(key)
      if (
        !projectedLayerExprByKey.has(key) ||
        (preferredAlias && alias === preferredAlias)
      ) {
        projectedLayerExprByKey.set(key, String(sel || '').trim())
      }
    }
    const initialProjectedLayerAliases = Array.from(projectedLayerAliasByKey.values())

    const inner = `SELECT ${baseInnerSelects.join(', ')} ${tailAdjusted}`
    const directSyntheticSourceAliases = (() => {
      const out = new Set<string>()
      const re = /(?:^|[^A-Za-z0-9_])(?:"t"|t)\s*\.\s*"(__lod_\d+|__fg_[A-Za-z0-9_]+)"/ig
      for (const sel of baseInnerSelects) {
        const raw = String(sel || '')
        let m: RegExpExecArray | null
        while ((m = re.exec(raw))) {
          const alias = String(m[1] || '').trim().toLowerCase()
          if (alias) out.add(alias)
        }
      }
      return out
    })()
    const extractTopLevelFromSourceSql = (s: string): string => {
      const fromIdx = findTopLevelKeyword(s, 'FROM')
      if (fromIdx < 0) return ''
      let i = fromIdx + 4
      while (i < s.length && /\s/.test(s[i])) i++
      if (i >= s.length) return ''
      if (s[i] === '(') {
        let depth = 0
        let inStr = false
        let inIdent = false
        for (let j = i; j < s.length; j++) {
          const ch = s[j]
          if (ch === "'" && !inIdent) {
            if (inStr && s[j + 1] === "'") { j++; continue }
            inStr = !inStr
            continue
          }
          if (!inStr && ch === '"') {
            if (inIdent && s[j + 1] === '"') { j++; continue }
            inIdent = !inIdent
            continue
          }
          if (inStr || inIdent) continue
          if (ch === '(') depth++
          else if (ch === ')') {
            depth--
            if (depth === 0) return s.slice(i, j + 1)
          }
        }
        return ''
      }
      let j = i
      while (j < s.length) {
        const ch = s[j]
        if (/\s/.test(ch) || ch === ',' || ch === ')') break
        j++
      }
      return s.slice(i, j)
    }
    const topLevelFromSourceSql = extractTopLevelFromSourceSql(tailAdjusted)
    const sourceDefinedSyntheticAliases = (() => {
      const src = String(topLevelFromSourceSql || '').trim()
      if (!(src.startsWith('(') && src.endsWith(')'))) return new Set<string>()
      const inner = src.slice(1, -1)
      const out = new Set<string>()
      const quoted = /\bAS\s+"(__lod_\d+|__fg_[A-Za-z0-9_]+)"/ig
      const bare = /\bAS\s+(__lod_\d+|__fg_[A-Za-z0-9_]+)\b/ig
      let m: RegExpExecArray | null
      while ((m = quoted.exec(inner))) {
        const alias = String(m[1] || '').trim().toLowerCase()
        if (alias) out.add(alias)
      }
      while ((m = bare.exec(inner))) {
        const alias = String(m[1] || '').trim().toLowerCase()
        if (alias) out.add(alias)
      }
      return out
    })()
    const sourceProjectsWildcard = (() => {
      const src = String(topLevelFromSourceSql || '').trim()
      if (!(src.startsWith('(') && src.endsWith(')'))) return false
      const inner = src.slice(1, -1).trim()
      const selectIdx = findTopLevelKeyword(inner, 'SELECT')
      const fromIdx = findTopLevelKeyword(inner, 'FROM')
      if (selectIdx < 0 || fromIdx <= selectIdx) return false
      const selectBodyRaw = inner.slice(selectIdx + 6, fromIdx).trim()
      if (!selectBodyRaw) return false
      const prefixMatch = selectBodyRaw.match(/^(DISTINCT|ALL)\b/i)
      const selectListRaw = prefixMatch
        ? selectBodyRaw.slice(prefixMatch[0].length).trimStart()
        : selectBodyRaw
      if (!selectListRaw) return false
      return splitTopLevelList(selectListRaw).some((itemRaw) => {
        const item = String(itemRaw ?? '').trim()
        if (!item) return false
        if (item === '*') return true
        return /^(?:(?:"[^"]+"|[A-Za-z_][A-Za-z0-9_]*)\s*\.\s*)?\*$/.test(item)
      })
    })()
    const locallyDefinedSyntheticAliases = new Set<string>([
      ...Array.from(retainedExtraAliasKeys),
      ...Array.from(innerAliasSet),
    ])
    const missingSyntheticSourceAliases = Array.from(directSyntheticSourceAliases).filter((a) => {
      if (/^__fg_ord\d+$/i.test(String(a || ''))) return false
      if (sourceProjectsWildcard) return false
      return !sourceDefinedSyntheticAliases.has(a) && !locallyDefinedSyntheticAliases.has(a)
    })
    if (missingSyntheticSourceAliases.length > 0) {
      // Keep the pre-rewrite SQL if this rewrite would introduce synthetic source refs
      // that the immediate FROM source does not project.
      if (traceRewrite) {
        console.log('[windowRewrite] abort missing synthetic source aliases', {
          aliases: missingSyntheticSourceAliases.slice(0, 8),
          tailHead: tailAdjusted.slice(0, 240),
        })
      }
      noteRewrite({ abortedMissingLodSource: true, missingSyntheticSourceAliases: missingSyntheticSourceAliases.slice(0, 8) })
      return sql
    }
    let rewrittenSourceSql = inner
    if (deferredStageExtras.length > 0) {
      const groupedProjectedLayerAliasKeys = new Set<string>(
        initialProjectedLayerAliases
          .filter(({ key, alias }) => {
            if (isSyntheticOrderAlias(alias) || isSyntheticOrderAlias(key)) return false
            if (isGroupedKeyAliasRef(alias) || isGroupedKeyAliasRef(key)) return true
            const aliasExpr = String(aliasExprByKey.get(key) ?? '').trim()
            if (!aliasExpr) return false
            if (isGroupedKeyAliasRef(aliasExpr)) return true
            const exprNorm = normalizeSqlExpr(aliasExpr)
            if (exprNorm && groupedExprNormalized.has(exprNorm)) return true
            const colKey = extractSimpleSourceColumnKey(aliasExpr)
            return Boolean(colKey && groupedSourceColumnKeys.has(colKey))
          })
          .map(({ key }) => key)
      )
      // Deferred aggregate layers must preserve any alias still needed by later windows/outer
      // projections. Collapsing those aliases with ANY_VALUE makes LOD-partition inputs like
      // `reportenddate` / `__fg_agg0` nondeterministic, which can null out derived measures.
      for (const { key, alias } of initialProjectedLayerAliases) {
        if (isSyntheticOrderAlias(alias) || isSyntheticOrderAlias(key)) continue
        if (!requiredProjectedAliasKeys.has(key)) continue
        groupedProjectedLayerAliasKeys.add(key)
      }
      let pendingExtras = [...deferredStageExtras]
      const allProjectedAliasKeys = new Set<string>([
        ...Array.from(projectedAliasKeysBeforeExtras),
        ...Array.from(retainedExtraAliasKeys),
      ])
      const availableAliasKeys = new Set<string>(innerAliasSet)
      const hasPlainAggregateExpr = (exprRaw: string): boolean => {
        const expr = String(exprRaw || '').trim()
        return /\b(?:MIN|MAX|SUM|AVG|COUNT|ANY_VALUE)\s*\(/i.test(expr) && !/\bOVER\s*\(/i.test(expr)
      }
      while (pendingExtras.length > 0) {
        const readyExtras = pendingExtras.filter((extra) => {
          const refs = collectReferencedProjectedAliasKeys(extra.expr, allProjectedAliasKeys)
          return Array.from(refs).every((key) => availableAliasKeys.has(key))
        })
        if (readyExtras.length === 0) {
          if (traceRewrite) {
            console.log('[windowRewrite] abort deferred extra layer', {
              pendingExtraAliases: pendingExtras.map((extra) => extra.alias).slice(0, 8),
              tailHead: tailAdjusted.slice(0, 240),
            })
          }
          noteRewrite({
            abortedDeferredExtraLayer: true,
            pendingExtraAliases: pendingExtras.map((extra) => extra.alias).slice(0, 8),
          })
          return sql
        }
        const readyPlainAggregateExtras = readyExtras.filter((extra) => hasPlainAggregateExpr(extra.expr))
        const readyNonAggregateExtras = readyExtras.filter((extra) => !hasPlainAggregateExpr(extra.expr))
        const extrasForLayer =
          readyPlainAggregateExtras.length > 0 && readyNonAggregateExtras.length > 0
            ? readyPlainAggregateExtras
            : readyExtras
        const projectedLayerAliases = Array.from(projectedLayerAliasByKey.values())
        const layerHasProjectedPlainAggregates = projectedLayerAliases.some(({ key }) =>
          hasPlainAggregateExpr(String(projectedLayerExprByKey.get(key) || ''))
          || aggregateSensitiveProjectedAliasKeys.has(key)
        )
        const layerHasPlainAggregates =
          layerHasProjectedPlainAggregates
          || extrasForLayer.some((extra) => hasPlainAggregateExpr(extra.expr))
        const groupedLayerAliasKeys = (() => {
          if (!layerHasPlainAggregates) return groupedProjectedLayerAliasKeys
          const out = new Set<string>(groupedProjectedLayerAliasKeys)
          for (const extra of pendingExtras) {
            const refs = collectReferencedProjectedAliasKeys(extra.expr, allProjectedAliasKeys)
            for (const refKey of refs) {
              if (
                /^__fg_agg\d+$/i.test(refKey)
                && innerExtraExprByAliasKey.has(refKey)
                && !requiredProjectedAliasKeys.has(refKey)
              ) continue
              out.add(refKey)
            }
          }
          return out
        })()
        const groupedLayerAliasExprs = layerHasPlainAggregates
          ? projectedLayerAliases
            .filter(({ key }) => groupedLayerAliasKeys.has(key))
            .map(({ alias }) => `t.${quoteIdent(alias)}`)
          : []
        const groupedLayerExtraExprs = layerHasPlainAggregates
          ? extrasForLayer
            .map((extra) => {
              const expr = String(extra?.expr || '').trim()
              if (!expr) return ''
              if (hasPlainAggregateExpr(expr)) return ''
              if (/\bOVER\s*\(/i.test(expr)) return ''
              if (/^t\.(?:"[^"]+"|[A-Za-z_][A-Za-z0-9_]*)$/i.test(expr)) return expr
              return ''
            })
            .filter(Boolean)
          : []
        const groupedLayerSelectExprs = Array.from(new Set<string>([
          ...groupedLayerAliasExprs,
          ...groupedLayerExtraExprs,
        ]))
        const directGroupedPassthroughExprs: string[] = []
        const deferredLayerExprByAliasKey = new Map<string, string>()
        const layerSelects = [
          ...projectedLayerAliases.map(({ key, alias }) => {
            const groupedKeyAlias =
              isGroupedKeyAliasRef(alias) ||
              isGroupedKeyAliasRef(key)
            const directRef = `t.${quoteIdent(alias)}`
            // An alias that is part of the layer's GROUP BY is functionally constant per group
            // and must be projected directly. Wrapping it in ANY_VALUE is both redundant (same
            // value per group) and actively wrong when later passes pattern-match on the raw
            // `t."<alias>" AS "<alias>"` shape (e.g. strip-passes, reuse detectors).
            const aliasInGrouping =
              layerHasPlainAggregates &&
              (groupedKeyAlias || groupedLayerAliasKeys.has(key))
            const passthroughExpr =
              layerHasPlainAggregates
                ? (aliasInGrouping ? directRef : `ANY_VALUE(t.${quoteIdent(alias)})`)
                : directRef
            if (layerHasPlainAggregates && passthroughExpr === directRef) {
              directGroupedPassthroughExprs.push(directRef)
            }
            return `${passthroughExpr} AS ${quoteIdent(alias)}`
          }),
          ...extrasForLayer.map((extra) => {
            const rawExpr = String(extra?.expr || '').trim()
            const dependsOnInternalAggregateAlias =
              /__fg_(?:agg|win)\d+/i.test(rawExpr) ||
              exprDependsOnAggregateAlias(rawExpr)
            const wrappedExpr =
              layerHasPlainAggregates &&
              rawExpr &&
              !hasPlainAggregateExpr(rawExpr) &&
              !/\bOVER\s*\(/i.test(rawExpr) &&
              dependsOnInternalAggregateAlias
                ? `ANY_VALUE(${rawExpr})`
                : rawExpr
            if (wrappedExpr) deferredLayerExprByAliasKey.set(extra.key, wrappedExpr)
            return `${wrappedExpr} AS ${quoteIdent(extra.alias)}`
          }),
        ]
        const rawLayerSelectGroupExprs = layerHasPlainAggregates
          ? layerSelects
            .map((selectExpr) => {
              const raw = String(selectExpr || '').trim()
              if (!raw) return ''
              const quoted = raw.match(/^(?:"t"|t)\s*\.\s*"([^"]+)"\s+AS\s+"([^"]+)"$/i)
              if (quoted) return `t.${quoteIdent(String(quoted[1] || '').trim())}`
              const bare = raw.match(/^(?:"t"|t)\s*\.\s*([A-Za-z_][A-Za-z0-9_]*)\s+AS\s+([A-Za-z_][A-Za-z0-9_]*)$/i)
              if (bare) return `t.${quoteIdent(String(bare[1] || '').trim())}`
              return ''
            })
            .filter(Boolean)
          : []
        const layerGroupByExprs = Array.from(new Set<string>([
          ...groupedLayerSelectExprs,
          ...directGroupedPassthroughExprs,
          ...rawLayerSelectGroupExprs,
        ]))
        rewrittenSourceSql = `SELECT ${layerSelects.join(', ')} FROM (${rewrittenSourceSql}) AS ${quoteIdent('t')}${
          layerHasPlainAggregates && layerGroupByExprs.length > 0
            ? ` GROUP BY ${layerGroupByExprs.join(', ')}`
            : ''
        }`
        for (const extra of extrasForLayer) {
          availableAliasKeys.add(extra.key)
          noteProjectedLayerAlias(extra.alias)
          const extraExpr = String(deferredLayerExprByAliasKey.get(extra.key) ?? extra?.expr ?? '').trim()
          if (extraExpr) projectedLayerExprByKey.set(extra.key, extraExpr)
        }
        const readyKeys = new Set<string>(extrasForLayer.map((extra) => extra.key))
        pendingExtras = pendingExtras.filter((extra) => !readyKeys.has(extra.key))
      }
    }
    let outer = `SELECT ${outerSelects.join(', ')} FROM (${rewrittenSourceSql}) AS ${quoteIdent('t')}`
    if (outerWindowClause) outer += ` ${outerWindowClause}`
    if (outerQualifyClause) outer += ` QUALIFY ${outerQualifyClause}`
    if (outerLimitClause) outer += ` ${outerLimitClause}`
    return outer
  }

  // For Tableau calc compilation we use a stable base alias ("t") throughout this file.
  // The DuckDB calc compiler uses `scope.source_id` to qualify column refs.
  const CALC_BASE_ALIAS = 't'
  const resolveColCache = new Map<string, string>()
  const fieldExprCache = new Map<string, string>()
  const bindingIndex = (() => {
    const raw = (options as any)?.bindingMap
    if (!raw || typeof raw !== 'object') return null
    const datasources = Array.isArray((raw as any)?.datasources) ? (raw as any).datasources : []
    if (!datasources.length) return null
    const indexLoose = new Map<string, Map<string, string>>()
    const indexExact = new Map<string, Map<string, string>>()
    const getMap = (index: Map<string, Map<string, string>>, dsKey: string) => {
      if (!dsKey) return null
      const key = normalizeTableauDatasourceKey(dsKey)
      if (!key) return null
      if (!index.has(key)) index.set(key, new Map())
      return index.get(key) ?? null
    }
    const normalizeBindingKeyExact = (rawKey: any): string => {
      const keyRaw = String(rawKey ?? '').trim()
      if (!keyRaw) return ''
      const cleaned = cleanBracketedFieldRef0(keyRaw) || keyRaw
      const token = normalizeTableauFieldToken(cleaned)
      const out = String(token || cleaned || keyRaw).trim().toLowerCase()
      return out
    }
    const normalizeBindingKeyLoose = (rawKey: any): string => {
      const keyRaw = String(rawKey ?? '').trim()
      if (!keyRaw) return ''
      return normalizeTableauFieldKey(keyRaw) || keyRaw.toLowerCase()
    }
    const pushKeyExact = (map: Map<string, string> | null, rawKey: any, column: string) => {
      const key = normalizeBindingKeyExact(rawKey)
      if (!map || !key) return
      if (!map.has(key)) map.set(key, column)
    }
    const pushKeyLoose = (map: Map<string, string> | null, rawKey: any, column: string) => {
      const key = normalizeBindingKeyLoose(rawKey)
      if (!map || !key) return
      if (!map.has(key)) map.set(key, column)
    }
    for (const ds of datasources) {
      const dsKeyRaw = String(ds?.datasource_key ?? ds?.datasource_name ?? '').trim()
      const mapLoose = getMap(indexLoose, dsKeyRaw)
      const mapExact = getMap(indexExact, dsKeyRaw)
      const bindings = Array.isArray(ds?.bindings) ? ds.bindings : []
      for (const entry of bindings) {
        const column = String(entry?.physical_column ?? '').trim()
        if (!column) continue
        const aliases = Array.isArray(entry?.aliases) ? entry.aliases : []
        const exactFields = [
          entry?.tableau_token,
          entry?.field_id,
        ]
        for (const f of exactFields) {
          pushKeyExact(mapExact, f, column)
        }
        const looseFields = [
          entry?.tableau_token,
          entry?.tableau_token_raw,
          entry?.field_id,
          entry?.caption,
          ...aliases,
        ]
        for (const f of looseFields) {
          pushKeyLoose(mapLoose, f, column)
        }
      }
    }
    return { loose: indexLoose, exact: indexExact }
  })()
  const extractColumnKeySet = (() => {
    const out = new Set<string>()
    const cols = Array.isArray(extractColumns) ? extractColumns : []
    for (const c of cols) {
      const raw = String(c ?? '').trim()
      if (!raw) continue
      const key = normalizeTableauFieldKey(raw) || raw.toLowerCase()
      if (key) out.add(key)
    }
    return out
  })()
  const hasExtractSchema = extractColumnKeySet.size > 0
  const isPresentInExtractSchema = (columnName: string): boolean => {
    if (!hasExtractSchema) return !localDatasetId
    const raw = String(columnName ?? '').trim()
    if (!raw) return false
    if (sourceSchema && !isColumnInSourceSchema(sourceSchema, raw)) return false
    const key = normalizeTableauFieldKey(raw) || raw.toLowerCase()
    return Boolean(key) && extractColumnKeySet.has(key)
  }
  type SqlPhysicalRefValidationResult = {
    ok: boolean
    missingPhysicalRefs: string[]
  }
  const physicalRefKey = (raw: any): string => {
    const value = String(raw ?? '').trim()
    if (!value) return ''
    return normalizeTableauFieldKey(value) || value.toLowerCase()
  }
  const extractSqlPhysicalRefs = (
    sqlRaw: string,
    opts?: { sourceAliases?: Iterable<string>; includeUnqualified?: boolean },
  ): string[] => {
    const sql = String(sqlRaw ?? '')
    if (!sql) return []
    const sourceAliases = new Set<string>(
      Array.from(opts?.sourceAliases ?? [CALC_BASE_ALIAS, 't'])
        .map((v) => String(v ?? '').trim().replace(/^"|"$/g, '').toLowerCase())
        .filter(Boolean),
    )
    const out: string[] = []
    const seen = new Set<string>()
    let inString = false
    for (let i = 0; i < sql.length; i += 1) {
      const ch = sql[i]
      if (ch === "'") {
        if (inString && sql[i + 1] === "'") {
          i += 1
          continue
        }
        inString = !inString
        continue
      }
      if (inString) continue

      const before = sql[i - 1]
      if (before && /[A-Za-z0-9_]/.test(before)) continue

      let alias = ''
      let j = i
      if (sql[j] === '"') {
        j += 1
        let value = ''
        for (; j < sql.length; j += 1) {
          const c = sql[j]
          if (c === '"') {
            if (sql[j + 1] === '"') {
              value += '"'
              j += 1
              continue
            }
            break
          }
          value += c
        }
        if (sql[j] !== '"') continue
        alias = value
        j += 1
      } else {
        const m = sql.slice(j).match(/^([A-Za-z_][A-Za-z0-9_]*)/)
        if (!m) continue
        alias = String(m[1] ?? '')
        j += alias.length
      }
      if (!sourceAliases.has(alias.toLowerCase())) continue
      while (j < sql.length && /\s/.test(sql[j])) j += 1
      if (sql[j] !== '.') continue
      j += 1
      while (j < sql.length && /\s/.test(sql[j])) j += 1

      let col = ''
      if (sql[j] === '"') {
        j += 1
        for (; j < sql.length; j += 1) {
          const c = sql[j]
          if (c === '"') {
            if (sql[j + 1] === '"') {
              col += '"'
              j += 1
              continue
            }
            break
          }
          col += c
        }
      } else {
        const m = sql.slice(j).match(/^([A-Za-z_][A-Za-z0-9_]*)/)
        if (!m) continue
        col = String(m[1] ?? '')
      }
      const key = physicalRefKey(col)
      if (!key || seen.has(key)) continue
      seen.add(key)
      out.push(col)
    }
    if (opts?.includeUnqualified) {
      inString = false
      for (let i = 0; i < sql.length; i += 1) {
        const ch = sql[i]
        if (ch === "'") {
          if (inString && sql[i + 1] === "'") {
            i += 1
            continue
          }
          inString = !inString
          continue
        }
        if (inString) continue
        if (ch !== '"') continue

        let j = i + 1
        let col = ''
        for (; j < sql.length; j += 1) {
          const c = sql[j]
          if (c === '"') {
            if (sql[j + 1] === '"') {
              col += '"'
              j += 1
              continue
            }
            break
          }
          col += c
        }
        if (sql[j] !== '"') continue

        let prev = i - 1
        while (prev >= 0 && /\s/.test(sql[prev] || '')) prev -= 1
        let next = j + 1
        while (next < sql.length && /\s/.test(sql[next] || '')) next += 1
        // Alias-qualified refs are handled above; quoted relation/alias names adjacent to a dot
        // are not unqualified physical-column refs.
        if (sql[prev] === '.' || sql[next] === '.') {
          i = j
          continue
        }

        const key = physicalRefKey(col)
        if (!key || seen.has(key)) {
          i = j
          continue
        }
        seen.add(key)
        out.push(col)
        i = j
      }
    }
    return out
  }
  const validateSqlPhysicalRefs = (
    sqlRaw: string,
    opts?: {
      sourceAliases?: Iterable<string>
      allowedDerivedAliases?: Iterable<string>
      includeUnqualified?: boolean
    },
  ): SqlPhysicalRefValidationResult => {
    if (!hasExtractSchema) return { ok: true, missingPhysicalRefs: [] }
    const allowedDerivedAliasKeys = new Set<string>(
      Array.from(opts?.allowedDerivedAliases ?? [])
        .map(physicalRefKey)
        .filter(Boolean),
    )
    const knownPlannerSyntheticRefKeys = (() => {
      const out = new Set<string>()
      const add = (value: any): void => {
        const raw = String(value ?? '').trim()
        if (!raw) return
        const candidates = [
          raw,
          cleanBracketedFieldRef0(raw),
          normalizeTableauFieldToken(raw),
          normalizeTableauFieldToken(cleanBracketedFieldRef0(raw) || raw),
        ]
        for (const candidate of candidates) {
          const key = physicalRefKey(candidate)
          if (key) out.add(key)
        }
      }
      try {
        const fd = (workbookIr as any)?.field_dictionary
        const entries: any[] = [
          ...(Array.isArray(fd?.columns) ? fd.columns : []),
          ...(Array.isArray(fd?.column_instances) ? fd.column_instances : []),
        ]
        for (const entry of entries) {
          if (!entry || typeof entry !== 'object') continue
          const attrs = (entry as any).attrs && typeof (entry as any).attrs === 'object' ? (entry as any).attrs : {}
          const calc = (entry as any).calculation && typeof (entry as any).calculation === 'object' ? (entry as any).calculation : null
          const calcAttrs = calc?.attrs && typeof calc.attrs === 'object' ? calc.attrs : {}
          const classRaw = String(calc?.class ?? calcAttrs.class ?? attrs.class ?? '').trim().toLowerCase()
          const names = [
            (entry as any).name,
            (entry as any).caption,
            (entry as any).column,
            attrs.name,
            attrs.caption,
            attrs.column,
            calcAttrs.name,
            calcAttrs.caption,
          ]
          const isBin =
            classRaw === 'bin' ||
            names.some((name) => /\(\s*bin\s*\)/i.test(String(name ?? '')))
          if (!isBin) continue
          for (const name of names) add(name)
        }
      } catch { }
      return out
    })()
    const missingPhysicalRefs: string[] = []
    const seenMissing = new Set<string>()
    for (const col of extractSqlPhysicalRefs(sqlRaw, opts)) {
      const key = physicalRefKey(col)
      if (!key) continue
      if (allowedDerivedAliasKeys.has(key)) continue
      if (/^__fg_group_key_\d+$/i.test(String(col ?? '').trim())) continue
      if (knownPlannerSyntheticRefKeys.has(key)) continue
      if (isPresentInExtractSchema(col)) continue
      if (seenMissing.has(key)) continue
      seenMissing.add(key)
      missingPhysicalRefs.push(col)
    }
    return { ok: missingPhysicalRefs.length === 0, missingPhysicalRefs }
  }
  const canonicalizePhysicalColumnId = (rawId: string): string => {
    const raw = String(rawId ?? '').trim()
    if (!raw) return ''
    const candidates = [
      raw,
      cleanBracketedFieldRef0(raw),
      normalizeTableauFieldToken(raw),
    ]
      .map((v) => String(v ?? '').trim())
      .filter(Boolean)
    if (!candidates.length) return raw
    if (!hasExtractSchema) return candidates[0] || raw
    const cols = Array.isArray(extractColumns) ? extractColumns : []
    for (const cand of candidates) {
      const key = normalizeTableauFieldKey(cand) || cand.toLowerCase()
      if (!key) continue
      for (const colRaw of cols) {
        const col = String(colRaw ?? '').trim()
        if (!col) continue
        const colKey = normalizeTableauFieldKey(col) || col.toLowerCase()
        if (colKey === key) return col
      }
    }
    return candidates[0] || raw
  }
  const preferredDatasourceKeys = (() => {
    const out = new Set<string>()
    const push = (rawKey: any) => {
      const k = normalizeTableauDatasourceKey(String(rawKey ?? '').trim())
      if (k) out.add(k)
    }
    const optionsAny = options as any
    push(optionsAny?.datasourceKey)
    const keys = Array.isArray(optionsAny?.datasourceKeys) ? optionsAny.datasourceKeys : []
    for (const k of keys) push(k)
    push(optionsAny?.fieldManifest?.datasource_name)
    const relTables = Array.isArray(optionsAny?.relationshipModel?.tables)
      ? optionsAny.relationshipModel.tables
      : []
    for (const t of relTables) {
      push((t as any)?.name)
      push((t as any)?.fieldManifest?.datasource_name)
    }
    if (!out.size && bindingIndex && bindingIndex.exact.size === 1) {
      for (const k of bindingIndex.exact.keys()) {
        if (k) out.add(k)
      }
    }
    return out
  })()

  const resolveColFromBinding = (fieldOrId: string): string | null => {
    if (!bindingIndex) return null
    const raw = String(fieldOrId ?? '').trim()
    if (!raw) return null
    const wantsGeneratedLatLon =
      /\(\s*generated\s*\)/i.test(raw) &&
      (/\blatitude\b/i.test(raw) || /\blongitude\b/i.test(raw))
    if (wantsGeneratedLatLon) {
      const geom = Array.isArray(extractColumns)
        ? extractColumns.find((colRaw) => {
            const col = String(colRaw ?? '').trim()
            if (!col) return false
            const key = normalizeTableauFieldKey(col) || col.toLowerCase()
            return key === 'geometry' || key === 'geom'
          })
        : null
      if (geom) return String(geom)
    }
    const parsed = parseTableauFieldRef(raw)
    const dsKey = parsed?.datasource ? normalizeTableauDatasourceKey(parsed.datasource) : ''
    const cleaned = cleanBracketedFieldRef0(raw) || raw
    const token = normalizeTableauFieldToken(cleaned || raw)
    const generatedValueTokenCandidates = (rawToken: any): string[] => {
      const out: string[] = []
      const s = String(rawToken ?? '').trim()
      if (!s) return out
      const cleanedToken = cleanBracketedFieldRef0(s) || s
      const normalizedToken = normalizeTableauFieldToken(cleanedToken)
      for (const candidateRaw of [cleanedToken, normalizedToken]) {
        const candidate = String(candidateRaw ?? '').trim()
        if (!candidate) continue
        const stripped = candidate.replace(/^fval:/i, '').trim()
        if (stripped && stripped !== candidate) {
          out.push(stripped)
          out.push(`[${stripped}]`)
        }
      }
      return out
    }
    const generatedValueFallbacks = [
      ...generatedValueTokenCandidates(raw),
      ...generatedValueTokenCandidates(cleaned),
      ...generatedValueTokenCandidates(token),
    ]
    const exactCandidates = [raw, cleaned, token, ...generatedValueFallbacks]
      .map((v) => String(v ?? '').trim())
      .filter(Boolean)
    const looseCandidates = [raw, cleaned, token, ...generatedValueFallbacks, stripTableauAutoGeneratedSuffixes(token)]
      .map((v) => String(v ?? '').trim())
      .filter(Boolean)

    const exactKeys = exactCandidates.map((cand) => {
      const cleanedKey = cleanBracketedFieldRef0(cand) || cand
      const tok = normalizeTableauFieldToken(cleanedKey)
      return String(tok || cleanedKey || cand).trim().toLowerCase()
    }).filter(Boolean)
    const looseKeys = looseCandidates.map((cand) => normalizeTableauFieldKey(cand) || cand.toLowerCase())

    const tryResolveExact = (map: Map<string, string> | undefined): string | null => {
      if (!map) return null
      for (const key of exactKeys) {
        if (!map.has(key)) continue
        const hit = String(map.get(key) ?? '').trim()
        if (!hit) continue
        if (!isPresentInExtractSchema(hit)) continue
        return hit
      }
      return null
    }
    const tryResolveLoose = (map: Map<string, string> | undefined): string | null => {
      if (!map) return null
      for (const key of looseKeys) {
        if (!map.has(key)) continue
        const hit = String(map.get(key) ?? '').trim()
        if (!hit) continue
        if (!isPresentInExtractSchema(hit)) continue
        return hit
      }
      return null
    }
    const tryResolveForDatasource = (keyRaw: string): string | null => {
      const key = normalizeTableauDatasourceKey(String(keyRaw ?? '').trim())
      if (!key) return null
      const mapExact = bindingIndex.exact.get(key)
      const exactHit = tryResolveExact(mapExact)
      if (exactHit) return exactHit
      const mapLoose = bindingIndex.loose.get(key)
      const looseHit = tryResolveLoose(mapLoose)
      if (looseHit) return looseHit
      return null
    }
    if (dsKey) {
      // Strict binding contract: never resolve through a datasource outside the active query scope.
      if (preferredDatasourceKeys.size > 0 && !preferredDatasourceKeys.has(dsKey)) return null
      const dsHit = tryResolveForDatasource(dsKey)
      if (dsHit) return dsHit
      return null
    }
    if (preferredDatasourceKeys.size > 0) {
      for (const key of preferredDatasourceKeys.values()) {
        const hit = tryResolveForDatasource(key)
        if (hit) return hit
      }
      return null
    }
    return null
  }

  const resolveCol = (fieldOrId: string): string => {
    const raw = String(fieldOrId ?? '').trim()
    if (!raw) return ''
    if (resolveColCache.has(raw)) return resolveColCache.get(raw) ?? ''
    if (!bindingIndex) {
      throw new Error('binding map missing: cannot resolve fields without semantic bindings')
    }
    const bindingResolved = resolveColFromBinding(raw)
    if (bindingResolved) {
      resolveColCache.set(raw, bindingResolved)
      return bindingResolved
    }
    const out = UNRESOLVED_FIELD
    resolveColCache.set(raw, out)
    return out
  }
  const resolveCompiledSqlPhysicalColumn = (columnRaw: string): string | null => {
    const raw = String(columnRaw ?? '').trim()
    if (!raw) return null
    const needsSemanticRewrite =
      /^\[[\s\S]+\]$/.test(raw) ||
      looksTableauToken(raw) ||
      raw.includes(':')
    if (!needsSemanticRewrite) return null
    const candidates = Array.from(new Set([
      raw,
      cleanBracketedFieldRef0(raw),
      normalizeTableauFieldToken(raw),
      raw.startsWith('[') && raw.endsWith(']') ? '' : `[${raw}]`,
    ].map((value) => String(value ?? '').trim()).filter(Boolean)))

    for (const candidate of candidates) {
      const bindingHit = resolveColFromBinding(candidate)
      if (!bindingHit) continue
      const canonical = canonicalizePhysicalColumnId(bindingHit)
      if (canonical && isPresentInExtractSchema(canonical)) return canonical
    }

    for (const candidate of candidates) {
      const canonical = canonicalizePhysicalColumnId(candidate)
      if (canonical && isPresentInExtractSchema(canonical)) return canonical
    }

    return null
  }
  const rewriteCompiledSqlPhysicalRefs = (
    sqlRaw: string,
    opts?: { sourceAliases?: Iterable<string> },
  ): string => {
    const sql = String(sqlRaw ?? '')
    if (!sql) return sql
    const sourceAliases = new Set<string>(
      Array.from(opts?.sourceAliases ?? [CALC_BASE_ALIAS, 't'])
        .map((value) => String(value ?? '').trim().replace(/^"|"$/g, '').toLowerCase())
        .filter(Boolean),
    )
    if (sourceAliases.size === 0) return sql

    const readIdentifier = (offset: number): { value: string; end: number } | null => {
      if (offset < 0 || offset >= sql.length) return null
      if (sql[offset] === '"') {
        let value = ''
        let i = offset + 1
        for (; i < sql.length; i += 1) {
          const ch = sql[i]
          if (ch === '"') {
            if (sql[i + 1] === '"') {
              value += '"'
              i += 1
              continue
            }
            return { value, end: i + 1 }
          }
          value += ch
        }
        return null
      }
      const m = sql.slice(offset).match(/^([A-Za-z_][A-Za-z0-9_]*)/)
      if (!m) return null
      const value = String(m[1] ?? '')
      return value ? { value, end: offset + value.length } : null
    }

    let out = ''
    let i = 0
    while (i < sql.length) {
      const ch = sql[i]
      if (ch === "'") {
        const start = i
        i += 1
        for (; i < sql.length; i += 1) {
          if (sql[i] !== "'") continue
          if (sql[i + 1] === "'") {
            i += 1
            continue
          }
          i += 1
          break
        }
        out += sql.slice(start, i)
        continue
      }

      const prev = sql[i - 1] ?? ''
      if (prev && /[A-Za-z0-9_]/.test(prev)) {
        out += ch
        i += 1
        continue
      }

      const alias = readIdentifier(i)
      if (!alias || !sourceAliases.has(alias.value.toLowerCase())) {
        out += ch
        i += 1
        continue
      }

      let j = alias.end
      while (j < sql.length && /\s/.test(sql[j] ?? '')) j += 1
      if (sql[j] !== '.') {
        out += ch
        i += 1
        continue
      }
      j += 1
      while (j < sql.length && /\s/.test(sql[j] ?? '')) j += 1
      const colStart = j
      const col = readIdentifier(colStart)
      if (!col) {
        out += ch
        i += 1
        continue
      }

      const resolved = resolveCompiledSqlPhysicalColumn(col.value)
      if (resolved && resolved !== col.value) {
        out += sql.slice(i, colStart)
        out += quoteIdent(resolved)
        i = col.end
        continue
      }

      out += sql.slice(i, col.end)
      i = col.end
    }
    return out
  }
  const resolveColOrRawWhenNoSchema = (fieldOrId: string): string => {
    const raw = String(fieldOrId ?? '').trim()
    if (!raw) return ''
    const resolved = resolveCol(raw)
    if (resolved && !isUnresolvedField(resolved)) return resolved
    return hasExtractSchema ? '' : raw
  }

  const looksTableauToken = (raw: string): boolean => {
    const s = String(raw ?? '').trim()
    if (!s) return false
    return (
      s.includes(':') &&
      (/^(none|usr|twk|win|attr|sum|avg|min|max|count|countd|ctd|tqr|yr|mn|wk|dy|qr|pcto):/i.test(s) ||
        /:(nk|ok|qk|sk)(:\d+)?$/i.test(s) ||
        /:\d+$/i.test(s))
    )
  }

  const resolveMeasureColumnForRewrite = (rawField: string): string | null => {
    const resolved = resolveCol(rawField)
    if (resolved && resolved !== UNRESOLVED_FIELD) return resolved
    return null
  }

  const tryRewriteMonthlyAvgDiff = (formula: string, baseRef: string): string | null => {
    const cleaned = String(formula ?? '').replace(/\s+/g, ' ').trim()
    if (!cleaned) return null
    const re = /SUM\s*\(\s*\[([^\]]+)\]\s*\)\s*-\s*SUM\s*\(\s*\{\s*(?:AVG|AVERAGE|MEAN)\s*\(\s*\{\s*FIXED\s+(?:DATETRUNC|DATEPART)\s*\(\s*['"](?:month|mm|mon)['"]\s*,\s*\[([^\]]+)\]\s*\)\s*:\s*SUM\s*\(\s*\[([^\]]+)\]\s*\)\s*\}\s*\)\s*\}\s*\)/i
    const m = cleaned.match(re)
    if (!m) return null
    const rawMeasure = String(m[1] ?? '').trim()
    const rawMeasureInner = String(m[3] ?? '').trim()
    const measureField = rawMeasureInner || rawMeasure
    const resolved = resolveMeasureColumnForRewrite(measureField)
    if (!resolved) return null
    const sumExpr = `SUM(TRY_CAST(${baseRef}.${quoteIdent(resolved)} AS DOUBLE))`
    const avgExpr = `AVG(${sumExpr}) OVER ()`
    return `(${sumExpr} - ${avgExpr})`
  }

  const isMeasureNames = (s: any) => normalizeTableauFieldKey(String(s ?? '')) === 'measure names'
  const isMultipleValues = (s: any) => {
    const k = normalizeTableauFieldKey(String(s ?? ''))
    return k === 'multiple values' || k === 'measure values'
  }

  const workbookDerived = (workbookIr && typeof workbookIr === 'object') ? getWorkbookIrDerived(workbookIr) : null
  const workbookSetDefs: SetDef[] = workbookDerived ? workbookDerived.sets : []

  // Build a minimal VisualSpec that lets `compileTableauExpressionToDuckdbSql()` resolve:
  // - calculated fields (from Tableau field dictionary)
  // - parameters (current values)
  // - base table scope (source id "t")
  const calcCompileSpec: VisualSpec | null = skipCalcs ? null : (() => {
    try {
      const fdCols: any[] = workbookDerived ? workbookDerived.fdCols : []

      const physicalCols = (() => {
        // In relationship-aware mode, calcs may reference columns from multiple tables.
        // Build a union of all known physical columns so the calc compiler can resolve ids.
        try {
          const rel = (options as any)?.relationshipModel
          const tables = rel && typeof rel === 'object' && Array.isArray((rel as any).tables) ? ((rel as any).tables as any[]) : []
          if (tables.length >= 2) {
            const out: string[] = []
            const seen = new Set<string>()
            for (const t of tables) {
              const cols0 =
                Array.isArray((t as any)?.extractColumns) ? (t as any).extractColumns
                  : Array.isArray((t as any)?.fieldManifest?.extract_columns) ? (t as any).fieldManifest.extract_columns
                    : []
              for (const c of cols0) {
                const cc = String(c ?? '').trim()
                if (!cc || seen.has(cc)) continue
                seen.add(cc)
                out.push(cc)
              }
            }
            return out
          }
        } catch { }
        return Array.isArray(extractColumns) ? extractColumns : []
      })()
      const metaByName = workbookDerived ? workbookDerived.metaByName : new Map<string, any>()
      // Prefer the real extract schema when available.
      // Only fall back to non-calculated field-dictionary columns when schema probing is unavailable.
      const fdBaseCols = workbookDerived ? workbookDerived.fdBaseColNames : []
      const baseCols = physicalCols.length
        ? [...physicalCols]
        : fdBaseCols
      const fields = (() => {
        const out: Array<{ id: string; label: string }> = []
        const seen = new Set<string>()
        const add = (idRaw: any, labelRaw: any) => {
          const id = String(idRaw ?? '').trim()
          const label = String(labelRaw ?? '').trim()
          if (!id || !label) return
          const sig = `${id}::${normalizeTableauFieldKey(label) || label.toLowerCase()}`
          if (seen.has(sig)) return
          seen.add(sig)
          out.push({ id, label })
        }

        for (const raw of baseCols) {
          const c = String(raw ?? '').trim()
          if (!c) continue
          const meta =
            metaByName.get(c)
            || metaByName.get(cleanBracketedFieldRef0(c) || '')
            || metaByName.get(`[${cleanBracketedFieldRef0(c) || c}]`)
            || null
          const labels = [
            c,
            cleanBracketedFieldRef0(c),
            normalizeTableauFieldToken(c),
            String(meta?.name ?? '').trim(),
            cleanBracketedFieldRef0(String(meta?.name ?? '')),
            normalizeTableauFieldToken(String(meta?.name ?? '')),
            String(meta?.caption ?? '').trim(),
            normalizeTableauFieldToken(String(meta?.caption ?? '')),
          ]
          for (const label of labels) add(c, label)
        }
        return out
      })()

      const calculated_fields = workbookDerived ? workbookDerived.calcEntries : []

      const paramTypeByKey = buildParamTypeByKeyFromWorkbookIr(fdCols)

      const paramDefs = Object.entries(parameters || {}).map(([k, v]) => ({
        id: `param:${k}`,
        name: String(k),
        data_type: inferEffectiveParamType(String(k), paramTypeByKey.get(normalizeParamKey(k)) || 'unknown', v) as any,
        allow_list: false,
        current_value: (v === undefined ? null : v),
        template_sql: templateMode
          ? buildParamTemplatePlaceholder(String(k), paramTypeByKey.get(normalizeParamKey(k)) || 'unknown', v)
          : null,
      }))

      const datasourceRef = String((options as any)?.fieldManifest?.datasource_name ?? '').trim() || CALC_BASE_ALIAS
      return {
        version: 1,
        data_pane: {
          sources: [{ id: CALC_BASE_ALIAS, name: CALC_BASE_ALIAS, kind: 'extract', reference: datasourceRef, fields }],
          calculated_fields,
          parameters: paramDefs,
          sets: workbookSetDefs,
          field_overrides: [],
        },
        shelves: { rows: [], columns: [] },
        marks: { mark: 'text' as any },
        filters: [],
        limit: null,
      } as any
    } catch {
      return null
    }
  })()
  _markPhase('workbookDerived')
  guard('calc-compile')

  // Perf: cache calc compilation across builds (same workbook + params).
  // Workbooks frequently reference the same calculated fields many times (shelves, filters, tooltips, table calcs),
  // and rebuilding after a viz edit can otherwise recompile hundreds of formulas.
  //
  // NOTE: This cache lives per-workbook IR object (WeakMap-derived), so it resets when the worker reloads.
  // We key by a small "compile context signature" to reduce the risk of incorrect reuse when parameters/schema change.
  const compiledFormulaCache = (() => {
    const wbAny: any = workbookDerived as any
    const existing = wbAny && wbAny.__fg_compiledFormulaCache
    if (existing && existing instanceof Map) return existing as Map<string, { duckdbSql: string; errors: any[] }>
    const m = new Map<string, { duckdbSql: string; errors: any[] }>()
    if (wbAny) wbAny.__fg_compiledFormulaCache = m
    return m
  })()
  const listCalcSpecSources = (specLike: VisualSpec | null | undefined): Array<{ id: string; name: string; reference: string }> => {
    const rawSources = Array.isArray((specLike as any)?.data_pane?.sources)
      ? ((((specLike as any).data_pane.sources) as any[]) ?? [])
      : []
    return rawSources
      .map((src) => ({
        id: String(src?.id ?? '').trim(),
        name: String(src?.name ?? '').trim(),
        reference: String((src as any)?.reference ?? '').trim(),
      }))
      .filter((src) => src.id || src.name || src.reference)
  }
  const buildCalcSourceSignature = (specLike: VisualSpec | null | undefined): string =>
    listCalcSpecSources(specLike)
      .map((src) => `${src.id}\u0001${src.name}\u0001${src.reference}`)
      .join('\u0002')
  const normalizeCompiledSourceAliases = (
    sqlRaw: string,
    specLike: VisualSpec | null | undefined,
    expectedAliasRaw: string,
  ): string => {
    let sql = String(sqlRaw ?? '').trim()
    const expectedAlias = String(expectedAliasRaw ?? '').trim()
    if (!sql || !expectedAlias) return sql
    const suffix = (value: string): string => {
      const parts = String(value ?? '').split('.').map((part) => part.trim()).filter(Boolean)
      return parts.length > 0 ? parts[parts.length - 1] ?? '' : String(value ?? '').trim()
    }
    const aliases: string[] = []
    const seen = new Set<string>()
    const addAlias = (valueRaw: any) => {
      const value = String(valueRaw ?? '').trim()
      if (!value) return
      const aliasKey = normalizeTableauDatasourceKey(value) || value.toLowerCase()
      const expectedKey = normalizeTableauDatasourceKey(expectedAlias) || expectedAlias.toLowerCase()
      if (aliasKey === expectedKey) return
      if (seen.has(value)) return
      seen.add(value)
      aliases.push(value)
    }
    for (const source of listCalcSpecSources(specLike)) {
      addAlias(source.name)
      addAlias(source.id)
      addAlias(source.reference)
      addAlias(suffix(source.reference))
    }
    if (aliases.length === 0) return sql
    const expectedQualifier = `${quoteIdent(expectedAlias)}.`
    for (const alias of aliases) {
      const quotedAlias = quoteIdent(alias)
      sql = sql.replace(new RegExp(`${escapeRegExp(quotedAlias)}\\s*\\.`, 'g'), expectedQualifier)
      if (/^[A-Za-z_][A-Za-z0-9_]*$/.test(alias)) {
        sql = sql.replace(new RegExp(`\\b${escapeRegExp(alias)}\\s*\\.`, 'g'), expectedQualifier)
      }
    }
    return sql
  }
  const compiledFormulaContextSig = (() => {
    try {
      // Bump when calc compiler / parameter typing SQL semantics change.
      const COMPILED_FORMULA_CACHE_VERSION = 5
      const p: any = parameters && typeof parameters === 'object' ? parameters : {}
      const keys = Object.keys(p).sort()
      // Keep signature stable + compact; enough to distinguish common parameter changes.
      const parts = keys.slice(0, 50).map((k) => `${k}=${String(p[k])}`)
      const paramSig = parts.join('|')
      const physCount = Array.isArray(extractColumns) ? extractColumns.length : 0
      const extractSchemaSig = Array.isArray(extractColumns)
        ? hashString((extractColumns as any[]).map((col) => String(col ?? '').trim()).join('\u0001'))
        : 0
      const calcSourceSig = hashString(buildCalcSourceSignature(calcCompileSpec))
      const relCount = (() => {
        try {
          const rel = (options as any)?.relationshipModel
          const tables = rel && typeof rel === 'object' && Array.isArray((rel as any).tables) ? (rel as any).tables : []
          return Array.isArray(tables) ? tables.length : 0
        } catch {
          return 0
        }
      })()
      return `v:${COMPILED_FORMULA_CACHE_VERSION}|p:${paramSig}|c:${physCount}|s:${extractSchemaSig}|src:${calcSourceSig}|t:${relCount}`
    } catch {
      return ''
    }
  })()

  const compileTableauFormulaToSql = (
    formula: string,
    opts?: { overClause?: string | null },
  ): { sql: string | null; hasErrors: boolean; errors?: any[] } => {
    const raw = String(formula ?? '').trim()
    if (!raw || !calcCompileSpec) return { sql: null, hasErrors: false, errors: [] }
    try {
      const cacheKey = compiledFormulaContextSig ? `${compiledFormulaContextSig}::${raw}` : raw
      const compiled = compiledFormulaCache.get(cacheKey) ?? (() => {
        const res = compileTableauExpressionToDuckdbSql({
          spec: calcCompileSpec,
          scope: { kind: 'source', source_id: CALC_BASE_ALIAS } as any,
          expression: raw,
        })
        const rawErrors = Array.isArray((res as any)?.errors) ? ((res as any).errors as any[]) : []
        const errors = rawErrors.filter((e: any) => {
          const msg = String(e?.message ?? '')
          // Best-effort: allow mixed-aggregate warnings to proceed when SQL is still generated.
          if (msg.includes('Cannot mix aggregate and non-aggregate')) return false
          return true
        })
        const duckdbSql = String((res as any)?.duckdb_sql ?? '').trim()
        const out = { duckdbSql, errors }
        // Simple bounded cache (insertion order as an approximation of LRU).
        try {
          compiledFormulaCache.set(cacheKey, out)
          const max = 5000
          if (compiledFormulaCache.size > max) {
            let n = compiledFormulaCache.size - max
            for (const k of compiledFormulaCache.keys()) {
              compiledFormulaCache.delete(k)
              n--
              if (n <= 0) break
            }
          }
        } catch {
          // ignore
        }
        return out
      })()
      const hasErrors = compiled.errors.length > 0
      let sql = String(compiled.duckdbSql ?? '').trim()
      if (!sql) return { sql: null, hasErrors, errors: compiled.errors }
      const overClauseRaw = String(opts?.overClause ?? '').trim()
      if (overClauseRaw) {
        const tableCalcPartitionOnly = (() => {
          const s = String(overClauseRaw || '').trim()
          if (!s) return ''
          const upper = s.toUpperCase()
          const idxOrder = upper.indexOf('ORDER BY')
          const idxRows = upper.indexOf('ROWS')
          const idxRange = upper.indexOf('RANGE')
          const cuts = [idxOrder, idxRows, idxRange].filter((i) => i >= 0)
          const end = cuts.length ? Math.min(...cuts) : s.length
          return s.slice(0, end).trim()
        })()
        const optimizePreviousValueRowSequence = (src: string): string => {
          if (!/\bROW_NUMBER\s*\(\)\s*OVER\s*\(\s*__TBLCALC__\s*\)/i.test(src)) return src
          const parseSingleOrder = (): { expr: string; direction: 'ASC' | 'DESC' } | null => {
            const s = String(overClauseRaw || '').trim()
            const upper = s.toUpperCase()
            const idxOrder = upper.indexOf('ORDER BY')
            if (idxOrder < 0) return null
            const orderStart = idxOrder + 'ORDER BY'.length
            const tail = s.slice(orderStart)
            const tailUpper = tail.toUpperCase()
            const idxRows = tailUpper.indexOf(' ROWS ')
            const idxRange = tailUpper.indexOf(' RANGE ')
            const end = [idxRows, idxRange].filter((i) => i >= 0).reduce((a, b) => Math.min(a, b), tail.length)
            let orderExpr = tail.slice(0, end).trim()
            if (!orderExpr) return null
            let depth = 0
            let inSingle = false
            let inDouble = false
            for (let i = 0; i < orderExpr.length; i++) {
              const ch = orderExpr[i]
              const prev = i > 0 ? orderExpr[i - 1] : ''
              if (ch === "'" && !inDouble && prev !== '\\') inSingle = !inSingle
              else if (ch === '"' && !inSingle && prev !== '\\') inDouble = !inDouble
              else if (!inSingle && !inDouble) {
                if (ch === '(') depth++
                else if (ch === ')') depth = Math.max(0, depth - 1)
                else if (ch === ',' && depth === 0) return null
              }
            }
            const dirMatch = orderExpr.match(/\s+(ASC|DESC)(?:\s+NULLS\s+(?:FIRST|LAST))?\s*$/i)
            const direction = (dirMatch?.[1]?.toUpperCase() === 'DESC' ? 'DESC' : 'ASC') as 'ASC' | 'DESC'
            if (dirMatch) orderExpr = orderExpr.slice(0, dirMatch.index).trim()
            orderExpr = orderExpr.replace(/\s+NULLS\s+(?:FIRST|LAST)\s*$/i, '').trim()
            if (!orderExpr) return null
            return { expr: orderExpr, direction }
          }
          const order = parseSingleOrder()
          if (!order) return src
          const overPartition = tableCalcPartitionOnly ? `OVER (${tableCalcPartitionOnly})` : 'OVER ()'
          const orderCast = `TRY_CAST(${order.expr} AS DOUBLE)`
          const anchor = order.direction === 'DESC'
            ? `MAX(${orderCast}) ${overPartition}`
            : `MIN(${orderCast}) ${overPartition}`
          const distance = order.direction === 'DESC'
            ? `(TRY_CAST((${anchor}) AS DOUBLE) - ${orderCast})`
            : `(${orderCast} - TRY_CAST((${anchor}) AS DOUBLE))`
          const pattern = /(?:TRY_CAST\(\s*\(*\s*)+ROW_NUMBER\(\)\s+OVER\s*\(\s*__TBLCALC__\s*\)\s*(?:\)*\s+AS\s+DOUBLE\s*\))+\s*\)*\s*\*\s*TRY_CAST\(\s*'?(?<delta>-?(?:(?:\d+(?:\.\d*)?)|(?:\.\d+))(?:[eE][+-]?\d+)?)'?\s+AS\s+DOUBLE\s*\)/gi
          return src.replace(pattern, (match: string) => {
            const delta = Number(match.match(/TRY_CAST\(\s*'?(-?(?:(?:\d+(?:\.\d*)?)|(?:\.\d+))(?:[eE][+-]?\d+)?)'?\s+AS\s+DOUBLE\s*\)\s*$/i)?.[1] ?? NaN)
            if (!Number.isFinite(delta) || delta === 0) return match
            const deltaSql = String(Math.abs(delta))
            const sequenceIndex = `(ROUND(TRY_CAST(${distance} AS DOUBLE) / NULLIF(TRY_CAST(${deltaSql} AS DOUBLE), 0), 8) + 1)`
            return `${sequenceIndex} * TRY_CAST(${delta} AS DOUBLE)`
          })
        }
        sql = optimizePreviousValueRowSequence(sql)
        if (sql.includes('__TBLCALC__')) {
          sql = sql.replaceAll('__TBLCALC__', overClauseRaw)
        }
        if (sql.includes('__TBLCALC_PARTITION__')) {
          sql = sql.replaceAll('__TBLCALC_PARTITION__', tableCalcPartitionOnly)
        }
      } else {
        // Make table calc placeholders syntactically valid even when we don't yet have an explicit OVER clause.
        sql = sql.replaceAll('__TBLCALC__', '')
        sql = sql.replaceAll('__TBLCALC_PARTITION__', '')
      }
      sql = normalizeCompiledSourceAliases(sql, calcCompileSpec, CALC_BASE_ALIAS)
      sql = rewriteCompiledSqlPhysicalRefs(sql, { sourceAliases: [CALC_BASE_ALIAS, 't'] })
      const preScrubSql = sql
      sql = scrubUnresolvedFieldRefs(sql)
      try {
        const numericRef = String.raw`((?:(?:"[^"]+"|[A-Za-z_][A-Za-z0-9_]*)\s*\.\s*)?"[^"]+")`
        const numericLiteral = String.raw`(-?\d+(?:\.\d+)?)`
        const castArm = (expr: string): string => (
          /\bTRY_CAST\s*\(/i.test(expr) ? expr : `TRY_CAST(${expr} AS DOUBLE)`
        )
        sql = sql.replace(
          new RegExp(String.raw`\bTHEN\s+${numericRef}\s+ELSE\s+${numericLiteral}\s+END`, 'gi'),
          (_m, ref, literal) => `THEN ${castArm(String(ref))} ELSE ${literal} END`,
        )
        sql = sql.replace(
          new RegExp(String.raw`\bTHEN\s+${numericLiteral}\s+ELSE\s+${numericRef}\s+END`, 'gi'),
          (_m, literal, ref) => `THEN ${literal} ELSE ${castArm(String(ref))} END`,
        )
      } catch { }
      if (debugFieldMapping) {
        const hadUnresolved = String(preScrubSql).includes(UNRESOLVED_FIELD)
        const hasNullifiedTryCast = /\bTRY_CAST\s*\(\s*NULL\s+AS\s+DOUBLE\s*\)/i.test(String(sql))
        if (hadUnresolved || hasNullifiedTryCast) {
          logFieldMapping('compileTableauFormulaToSql nullification', {
            worksheetName: worksheetNameDebug,
            formula: raw,
            hadUnresolved,
            hasNullifiedTryCast,
            sqlPreview: String(sql).slice(0, 500),
          })
        }
      }

      // If compilation yields aggregates of NULL, simplify them away. This is not a "fallback"—it's
      // just algebraic cleanup that avoids generating expressions DuckDB/our harness will choke on.
      // Examples we see in the wild:
      // - MAX(NULL) OVER (...) → NULL
      // - SUM(TRY_CAST(NULL AS DOUBLE)) → NULL
      // - SUM(TRY_CAST(NULL AS DOUBLE)) OVER (...) → NULL
      try {
        const nullAtom = String.raw`(?:\(+\s*)?NULL(?:\s*\)+)?`
        const step = (s: string): string => {
          let out = s
          // Collapse nested aggregates-of-NULL first (e.g. MAX(MAX(NULL)) OVER (...) → NULL)
          out = out.replace(
            new RegExp(String.raw`\\b(MAX|MIN|AVG|SUM|COUNT)\\s*\\(\\s*(?:\\(+\\s*)?(MAX|MIN|AVG|SUM|COUNT)\\s*\\(\\s*${nullAtom}\\s*\\)(?:\\s*\\)+)?\\s*\\)\\s*OVER\\s*\\([^)]*\\)`, 'gi'),
            'NULL'
          )
          out = out.replace(
            new RegExp(String.raw`\\b(MAX|MIN|AVG|SUM|COUNT)\\s*\\(\\s*${nullAtom}\\s*\\)\\s*OVER\\s*\\([^)]*\\)`, 'gi'),
            'NULL'
          )
          out = out.replace(
            new RegExp(String.raw`\\b(MAX|MIN|AVG|SUM|COUNT)\\s*\\(\\s*TRY_CAST\\s*\\(\\s*${nullAtom}[^)]*\\)\\s*\\)\\s*OVER\\s*\\([^)]*\\)`, 'gi'),
            'NULL'
          )
          // Non-window aggregates of NULL
          out = out.replace(new RegExp(String.raw`\\b(MAX|MIN|AVG|SUM|COUNT)\\s*\\(\\s*(?:\\(+\\s*)?(MAX|MIN|AVG|SUM|COUNT)\\s*\\(\\s*${nullAtom}\\s*\\)(?:\\s*\\)+)?\\s*\\)\\s*`, 'gi'), 'NULL')
          out = out.replace(new RegExp(String.raw`\\b(MAX|MIN|AVG|SUM|COUNT)\\s*\\(\\s*${nullAtom}\\s*\\)\\s*`, 'gi'), 'NULL')
          out = out.replace(/\bSUM\s*\(\s*TRY_CAST\s*\(\s*(?:\(+\s*)?NULL(?:\s*\)+)?[^)]*\)\s*\)\s*/gi, 'NULL')
          return out
        }
        for (let i = 0; i < 6; i++) {
          const prev = sql
          sql = step(sql)
          if (sql === prev) break
        }
      } catch { }

      // DATE_DIFF with NULL args yields binder errors in DuckDB; cast NULLs to a date-like type
      // so the function can resolve, or collapse fully-null calls to NULL.
      try {
        const fixDateDiffNulls = (s: string): string => {
          let out = s
          out = out.replace(/DATE_DIFF\(\s*('[^']+'|"[^"]+")\s*,\s*NULL\s*,\s*NULL\s*\)/gi, 'NULL')
          out = out.replace(/DATE_DIFF\(\s*('[^']+'|"[^"]+")\s*,\s*NULL\s*,/gi, 'DATE_DIFF($1, CAST(NULL AS TIMESTAMP),')
          out = out.replace(/DATE_DIFF\(\s*('[^']+'|"[^"]+")\s*,([^,]+),\s*NULL\s*\)/gi, 'DATE_DIFF($1,$2, CAST(NULL AS TIMESTAMP))')
          return out
        }
        sql = fixDateDiffNulls(sql)
      } catch { }

      if (!sql) return { sql: null, hasErrors, errors: compiled.errors }
      const validation = validateSqlPhysicalRefs(sql)
      if (!validation.ok) {
        if (debugFieldMapping || validation.missingPhysicalRefs.some((ref) => shouldTracePlannerColumn(ref))) {
          logFieldMapping('compileTableauFormulaToSql missing physical refs', {
            worksheetName: worksheetNameDebug,
            formula: raw,
            missingPhysicalRefs: validation.missingPhysicalRefs.slice(0, 8),
            sqlPreview: sql.slice(0, 500),
          })
        }
        return {
          sql: null,
          hasErrors: true,
          errors: [
            ...compiled.errors,
            {
              message: 'compiled_missing_physical_refs',
              missingPhysicalRefs: validation.missingPhysicalRefs,
            },
          ],
        }
      }
      return { sql, hasErrors, errors: compiled.errors }
    } catch {
      return { sql: null, hasErrors: true, errors: [{ message: 'compile_exception' }] }
    }
  }

  _markPhase('setup+helpers')
  // Fast index: calculated field lookup keys -> formula.
  // Many workbooks have thousands of fields; scanning the whole field dictionary on every lookup
  // can make query building take tens of seconds.
  const AMBIGUOUS_CALC_FORMULA = '__fg_ambiguous_calc_formula__'
  const putCalcFormulaIndexValue = (
    out: Map<string, string>,
    kind: 'exact' | 'token' | 'loose',
    keyRaw: string,
    formula: string,
  ) => {
    const k = String(keyRaw ?? '').trim()
    if (!k) return
    const idxKey = `${kind}:${k}`
    const prev = out.get(idxKey)
    if (prev == null) {
      out.set(idxKey, formula)
      return
    }
    if (prev === formula) return
    out.set(idxKey, AMBIGUOUS_CALC_FORMULA)
  }
  const addCalcFormulaIndexAliases = (out: Map<string, string>, nameLike: any, formula: string) => {
    const v = String(nameLike ?? '').trim()
    if (!v) return
    const norm = (s: any) => String(s ?? '').trim().toLowerCase()
    const normDisplay = (s: any) => {
      const cleaned = cleanBracketedFieldRef0(String(s ?? '')) || String(s ?? '')
      return norm(normalizeTableauFieldToken(cleaned))
        .replace(/^"(.*)"$/s, '$1')
        .replace(/^'(.*)'$/s, '$1')
        .replace(/\s+/g, ' ')
        .trim()
    }
    const normExact = (s: any) => norm(cleanBracketedFieldRef0(String(s ?? '')) || String(s ?? ''))
    const normToken = (s: any) => norm(normalizeTableauFieldToken(cleanBracketedFieldRef0(String(s ?? '')) || String(s ?? '')))
    const normKey = (s: any) => norm(normalizeTableauFieldKey(cleanBracketedFieldRef0(String(s ?? '')) || String(s ?? '')))
    putCalcFormulaIndexValue(out, 'exact', normExact(v), formula)
    putCalcFormulaIndexValue(out, 'token', normToken(v), formula)
    putCalcFormulaIndexValue(out, 'loose', normKey(v), formula)
    putCalcFormulaIndexValue(out, 'loose', normDisplay(v), formula)
  }
  const calcFormulaIndex = (() => {
    const out = new Map<string, string>()
    if (!workbookIr || typeof workbookIr !== 'object') return out
    try {
      const fd = (workbookIr as any)?.field_dictionary
      const cols: any[] = Array.isArray(fd?.columns) ? fd.columns : []
      const cis: any[] = Array.isArray(fd?.column_instances) ? fd.column_instances : []
      const calcFormulaByColumn = new Map<string, string>()
      for (const c of cols) {
        const formula = String(c?.calculation?.formula ?? '').trim()
        if (!formula) continue
        const colId = String((c as any)?.name ?? (c as any)?.id ?? '').trim()
        if (colId && !calcFormulaByColumn.has(colId)) calcFormulaByColumn.set(colId, formula)
      }
      for (const c of cols) {
        const formula = String(c?.calculation?.formula ?? '').trim()
        if (!formula) continue
        addCalcFormulaIndexAliases(out, c?.name, formula)
        addCalcFormulaIndexAliases(out, c?.caption, formula)
      }
      for (const ci of cis) {
        const base = String(ci?.column ?? '').trim()
        const formula = String(ci?.calculation?.formula ?? '').trim() || (base ? (calcFormulaByColumn.get(base) ?? '') : '')
        if (!formula) continue
        addCalcFormulaIndexAliases(out, ci?.name, formula)
        addCalcFormulaIndexAliases(out, ci?.caption, formula)
      }
      const samples: any[] = Array.isArray((workbookIr as any)?.sample_calculations)
        ? (workbookIr as any).sample_calculations
        : []
      for (const s of samples) {
        const nm = String((s as any)?.name ?? '').trim()
        const formula = String((s as any)?.formula ?? '').trim()
        if (!nm || !formula) continue
        addCalcFormulaIndexAliases(out, nm, formula)
      }
    } catch { }
    return out
  })()
  const manifestCalcFormulaIndex = (() => {
    const out = new Map<string, string>()
    try {
      const fields: any[] = Array.isArray((options as any)?.fieldManifest?.fields)
        ? (((options as any).fieldManifest.fields) as any[])
        : []
      for (const f of fields) {
        const formula = String((f as any)?.calc_formula_raw ?? (f as any)?.calc_formula ?? (f as any)?.formula ?? '').trim()
        if (!formula) continue
        addCalcFormulaIndexAliases(out, (f as any)?.field_id, formula)
        addCalcFormulaIndexAliases(out, (f as any)?.caption, formula)
        addCalcFormulaIndexAliases(out, (f as any)?.tableau_token, formula)
        addCalcFormulaIndexAliases(out, (f as any)?.tableau_token_raw, formula)
        addCalcFormulaIndexAliases(out, (f as any)?.name, formula)
      }
    } catch { }
    return out
  })()
  _markPhase('calcFormulaIndex')

  const lookupCalculatedFormula = (fieldOrId: string): string | null => {
    const raw = String(fieldOrId ?? '').trim()
    if (!raw) return null
    const hasWorkbookIr = Boolean(workbookIr && typeof workbookIr === 'object')
    const fd = hasWorkbookIr ? (workbookIr as any)?.field_dictionary : null
    const cols: any[] = Array.isArray(fd?.columns) ? fd.columns : []
    const cis: any[] = Array.isArray(fd?.column_instances) ? fd.column_instances : []

    const rawClean = cleanBracketedFieldRef0(raw) || raw
    const rawToken = normalizeTableauFieldToken(rawClean)
    const rawKey = normalizeTableauFieldKey(rawClean)
    const debugTarget =
      worksheetDebugEnabled &&
      (
        /diff from monthly avg/i.test(rawClean) ||
        /calculation_2084392578417565712/i.test(rawClean) ||
        /calculation_2084392578415947789/i.test(rawClean)
      )
    const norm = (s: any) => String(s ?? '').trim().toLowerCase()
    const normToken = (s: any) => norm(normalizeTableauFieldToken(cleanBracketedFieldRef0(String(s ?? '')) || String(s ?? '')))
    const normKey = (s: any) => norm(normalizeTableauFieldKey(cleanBracketedFieldRef0(String(s ?? '')) || String(s ?? '')))
    const normDisplay = (s: any) => normToken(s)
      .replace(/^"(.*)"$/s, '$1')
      .replace(/^'(.*)'$/s, '$1')
      .replace(/\s+/g, ' ')
      .trim()
    const rawStrictKey = norm(normalizeTableauFieldKeyStrict(rawClean))
    const hasCopyToken = /\(copy\b/i.test(rawClean) || /\(copy\b/i.test(rawToken)
    const targetStrict = new Set<string>([norm(rawClean), norm(rawToken)].filter(Boolean))
    const targetLoose = hasCopyToken
      ? targetStrict
      : new Set<string>([...targetStrict, norm(rawKey), normDisplay(rawClean), normDisplay(rawToken)].filter(Boolean))
    if (targetLoose.size === 0) return null
    const lookupIndexedFormula = (index: Map<string, string>, keys: Set<string>, includeLoose: boolean): string | null => {
      for (const k0 of keys) {
        const k = String(k0 ?? '').trim()
        if (!k) continue
        const exactHit = index.get(`exact:${k}`)
        if (exactHit && exactHit !== AMBIGUOUS_CALC_FORMULA) return exactHit
        const tokenHit = index.get(`token:${k}`)
        if (tokenHit && tokenHit !== AMBIGUOUS_CALC_FORMULA) return tokenHit
        if (includeLoose) {
          const looseHit = index.get(`loose:${k}`)
          if (looseHit && looseHit !== AMBIGUOUS_CALC_FORMULA) return looseHit
        }
      }
      return null
    }

    // Prefer manifest-backed raw formulas for exact field refs. Those preserve the
    // original Tableau parameter identities (`[Parameters].[Foo]`) better than the
    // normalized workbook formulas, which may collapse distinct parameters to a
    // generic caption such as `[Date Parameter]`.
    const strictManifestFirstHit = lookupIndexedFormula(manifestCalcFormulaIndex, targetStrict, false)
    if (strictManifestFirstHit) return strictManifestFirstHit
    const looseManifestPreferredHit = lookupIndexedFormula(manifestCalcFormulaIndex, targetLoose, true)
    if (looseManifestPreferredHit) return looseManifestPreferredHit

    const normStrict = (s: any) => norm(normalizeTableauFieldKeyStrict(cleanBracketedFieldRef0(String(s ?? '')) || String(s ?? '')))
    if (rawStrictKey) {
      const exactCol =
        cols.find((c) => normStrict(c?.name) === rawStrictKey) ||
        null
      const exactColFormula = String((exactCol as any)?.calculation?.formula ?? '').trim()
      if (exactColFormula) return exactColFormula

      const exactCi =
        cis.find((ci) => normStrict(ci?.name) === rawStrictKey) ||
        null
      if (exactCi) {
        const ciFormula = String((exactCi as any)?.calculation?.formula ?? '').trim()
        if (ciFormula) return ciFormula
        const base = String((exactCi as any)?.column ?? '').trim()
        if (base) {
          const baseCol = cols.find((c) => normStrict(c?.name) === normStrict(base)) || null
          const baseFormula = String((baseCol as any)?.calculation?.formula ?? '').trim()
          if (baseFormula) return baseFormula
        }
      }
    }

    // Fast path via precomputed index.
    // Priority is important for parity:
    // 1) exact caption/name matches,
    // 2) tokenized aliases,
    // 3) loose normalized keys (last resort, can be ambiguous).
    const lookupIndexed = lookupIndexedFormula
    const strictHit = lookupIndexed(calcFormulaIndex, targetStrict, false)
    if (strictHit) return strictHit
    const looseHit = lookupIndexed(calcFormulaIndex, targetLoose, true)
    if (looseHit) return looseHit
    const strictManifestHit = strictManifestFirstHit
    if (strictManifestHit) return strictManifestHit

    const matchByAnyKey = (val: any, targetSet: Set<string>, allowKeyFallback: boolean): boolean => {
      const v = String(val ?? '').trim()
      if (!v) return false
      if (targetSet.has(norm(v))) return true
      if (targetSet.has(normToken(v))) return true
      if (allowKeyFallback && targetSet.has(normKey(v))) return true
      return false
    }

    const findMatch = (targetSet: Set<string>, allowKeyFallback: boolean): { kind: 'col' | 'ci'; value: any } | null => {
      const hitCol =
        cols.find((c) => matchByAnyKey(c?.caption, targetSet, allowKeyFallback)) ||
        cols.find((c) => matchByAnyKey(c?.name, targetSet, allowKeyFallback)) ||
        null
      if (hitCol) return { kind: 'col', value: hitCol }
      const hitCi =
        cis.find((ci) => matchByAnyKey(ci?.caption, targetSet, allowKeyFallback)) ||
        cis.find((ci) => matchByAnyKey(ci?.name, targetSet, allowKeyFallback)) ||
        null
      if (hitCi) return { kind: 'ci', value: hitCi }
      return null
    }

    let hit = findMatch(targetStrict, false)
    if (!hit) hit = findMatch(targetLoose, !hasCopyToken)

    const hitCol = (hit && hit.kind === 'col') ? hit.value : null
    const formula0 = String(hitCol?.calculation?.formula ?? '').trim()
    if (formula0) {
      if (debugTarget) debugLog('lookupCalculatedFormula hit col', { raw, rawClean, rawToken, rawKey, formula: formula0 })
      return formula0
    }

    // Column-instance tokens often appear in view specs.
    let hitCi = (hit && hit.kind === 'ci') ? hit.value : null
    if (!hitCi) {
      const strictCi =
        cis.find((ci) => matchByAnyKey(ci?.caption, targetStrict, false)) ||
        cis.find((ci) => matchByAnyKey(ci?.name, targetStrict, false)) ||
        null
      if (strictCi) {
        hitCi = strictCi
      } else if (!hasCopyToken) {
        hitCi =
          cis.find((ci) => matchByAnyKey(ci?.caption, targetLoose, true)) ||
          cis.find((ci) => matchByAnyKey(ci?.name, targetLoose, true)) ||
          null
      }
    }
    if (hitCi) {
      const ciFormula = String((hitCi as any)?.calculation?.formula ?? '').trim()
      if (ciFormula) {
        if (debugTarget) debugLog('lookupCalculatedFormula hit ci', { raw, rawClean, rawToken, rawKey, formula: ciFormula })
        return ciFormula
      }
      const base = String(hitCi?.column ?? '').trim()
      if (base) {
        const baseCol = cols.find((c) => norm(c?.name) === norm(base)) || null
        const f = String(baseCol?.calculation?.formula ?? '').trim()
        if (f) {
          if (debugTarget) debugLog('lookupCalculatedFormula hit base', { raw, rawClean, rawToken, rawKey, formula: f })
          return f
        }
      }
    }

    if (ALLOW_FALLBACKS && hasWorkbookIr) {
      // Final fallback: workbook sample calculations (best-effort parser capture).
      try {
        const samples: any[] = Array.isArray((workbookIr as any)?.sample_calculations)
          ? (workbookIr as any).sample_calculations
          : []
        for (const s of samples) {
          const nm = String((s as any)?.name ?? '').trim()
          const formula = String((s as any)?.formula ?? '').trim()
          if (!nm || !formula) continue
          if (
            matchByAnyKey(nm, targetStrict, false) ||
            matchByAnyKey(nm, targetLoose, !hasCopyToken)
          ) {
            return formula
          }
        }
      } catch { }
    }
    if (debugTarget) debugLog('lookupCalculatedFormula miss', { raw, rawClean, rawToken, rawKey })
    return null
  }

  const preferredCalculatedFormula = (...fieldCandidates: any[]): string => {
    for (const candidate of fieldCandidates) {
      const raw = String(candidate ?? '').trim()
      if (!raw) continue
      const hit = lookupCalculatedFormula(raw)
      if (hit) return hit
    }
    return ''
  }

  type BinSpec = {
    baseField: string | null
    size: number | null
    peg: number | null
    decimals: number | null
  }

  const decimalPlacesForNumber = (valueRaw: any): number => {
    const text = String(valueRaw ?? '').trim().toLowerCase()
    if (!text) return 0
    const expanded = text.includes('e')
      ? (() => {
          const n = Number(text)
          return Number.isFinite(n) ? n.toFixed(12).replace(/0+$/, '').replace(/\.$/, '') : text
        })()
      : text
    const part = expanded.split('.')[1] ?? ''
    return Math.max(0, part.length)
  }
  const effectiveBinDecimals = (sizeRaw: any, decimalsRaw: any): number | null => {
    const sizeDecimals = decimalPlacesForNumber(sizeRaw)
    const decimals = Number.isFinite(Number(decimalsRaw)) ? Math.max(0, Math.floor(Number(decimalsRaw))) : null
    if (sizeDecimals <= 0) return decimals
    return Math.max(decimals ?? 0, sizeDecimals)
  }

  const parameterValueForTableauRef = (rawRef: any): any => {
    const raw = String(rawRef ?? '').trim()
    if (!raw) return undefined
    const candidates: string[] = []
    const add = (value: any) => {
      const s0 = String(value ?? '').trim()
      if (!s0) return
      const s = stripBracketsAroundFieldName(s0) || cleanBracketedFieldRef0(s0) || s0
      if (s && !candidates.includes(s)) candidates.push(s)
    }
    add(raw)
    const qualified = raw.match(/\[Parameters\]\s*\.\s*\[([^\]]+)\]/i)
    if (qualified?.[1]) add(qualified[1])
    const trailing = raw.match(/\[([^\]]+)\]\s*$/)
    if (trailing?.[1]) add(trailing[1])
    const cleaned = cleanBracketedFieldRef0(raw)
    if (cleaned) add(cleaned)
    for (const candidate of candidates) {
      if (Object.prototype.hasOwnProperty.call(parameters, candidate)) return (parameters as any)[candidate]
    }
    const wantedKeys = candidates
      .map((candidate) => normalizeParamKey(candidate))
      .filter(Boolean)
    if (!wantedKeys.length) return undefined
    for (const [key, value] of Object.entries(parameters)) {
      const keyNorm = normalizeParamKey(key)
      if (keyNorm && wantedKeys.includes(keyNorm)) return value
    }
    return undefined
  }

  const lookupBinSpec = (fieldOrId: string): BinSpec | null => {
    const raw = String(fieldOrId ?? '').trim()
    if (!raw || !workbookIr || typeof workbookIr !== 'object') return null
    const fd = (workbookIr as any)?.field_dictionary
    const cols: any[] = Array.isArray(fd?.columns) ? fd.columns : []
    const cis: any[] = Array.isArray(fd?.column_instances) ? fd.column_instances : []
    const rawClean = cleanBracketedFieldRef0(raw) || raw
    const rawToken = normalizeTableauFieldToken(rawClean)
    const rawKey = normalizeTableauFieldKey(rawClean)
    const norm = (s: any) => String(s ?? '').trim().toLowerCase()
    const normToken = (s: any) => norm(normalizeTableauFieldToken(cleanBracketedFieldRef0(String(s ?? '')) || String(s ?? '')))
    const normKey = (s: any) => norm(normalizeTableauFieldKey(cleanBracketedFieldRef0(String(s ?? '')) || String(s ?? '')))
    const hasCopyToken = /\(copy\b/i.test(rawClean) || /\(copy\b/i.test(rawToken)
    const targetStrict = new Set<string>([norm(rawClean), norm(rawToken)].filter(Boolean))
    const targetLoose = hasCopyToken
      ? targetStrict
      : new Set<string>([...targetStrict, norm(rawKey)].filter(Boolean))
    if (targetLoose.size === 0) return null
    const matchByAnyKey = (val: any, targetSet: Set<string>, allowKeyFallback: boolean): boolean => {
      const v = String(val ?? '').trim()
      if (!v) return false
      if (targetSet.has(norm(v))) return true
      if (targetSet.has(normToken(v))) return true
      if (allowKeyFallback && targetSet.has(normKey(v))) return true
      return false
    }
    const findMatch = (targetSet: Set<string>, allowKeyFallback: boolean): { kind: 'col' | 'ci'; value: any } | null => {
      const hitCol =
        cols.find((c) => matchByAnyKey(c?.caption, targetSet, allowKeyFallback)) ||
        cols.find((c) => matchByAnyKey(c?.name, targetSet, allowKeyFallback)) ||
        null
      if (hitCol) return { kind: 'col', value: hitCol }
      const hitCi =
        cis.find((ci) => matchByAnyKey(ci?.caption, targetSet, allowKeyFallback)) ||
        cis.find((ci) => matchByAnyKey(ci?.name, targetSet, allowKeyFallback)) ||
        null
      if (hitCi) return { kind: 'ci', value: hitCi }
      return null
    }
    let hit = findMatch(targetStrict, false)
    if (!hit) hit = findMatch(targetLoose, !hasCopyToken)
    const numberOrNull = (rawValue: any): number | null => {
      if (rawValue === null || rawValue === undefined) return null
      if (typeof rawValue === 'string' && rawValue.trim() === '') return null
      const n = Number(rawValue)
      return Number.isFinite(n) ? n : null
    }
    const sizeFromAttrs = (attrs: Record<string, any>): number | null => {
      const direct = numberOrNull(
        attrs?.size ??
          attrs?.['bin-size'] ??
          attrs?.bin_size ??
          attrs?.binSize ??
          null,
      )
      if (direct !== null) return direct
      const sizeParam =
        attrs?.['size-parameter'] ??
        attrs?.size_parameter ??
        attrs?.sizeParameter ??
        null
      return numberOrNull(parameterValueForTableauRef(sizeParam))
    }
    const hitCol = (hit && hit.kind === 'col') ? hit.value : null
    const colCalc = hitCol?.calculation
    if (colCalc && String(colCalc?.class ?? '').trim().toLowerCase() === 'bin') {
      const attrs = {
        ...((hitCol?.attrs && typeof hitCol.attrs === 'object') ? hitCol.attrs : {}),
        ...((colCalc?.attrs && typeof colCalc.attrs === 'object') ? colCalc.attrs : {}),
      }
      const size = sizeFromAttrs(attrs)
      const peg = numberOrNull(attrs?.peg ?? null)
      const decimals = numberOrNull(attrs?.decimals ?? null)
      const formula = String(colCalc?.formula ?? '').trim()
      return {
        baseField: formula ? stripBracketsAroundFieldName(formula) : null,
        size,
        peg: peg !== null ? peg : 0,
        decimals,
      }
    }
    const hitCi = (hit && hit.kind === 'ci') ? hit.value : null
    const ciCalc = hitCi?.calculation
    if (ciCalc && String(ciCalc?.class ?? '').trim().toLowerCase() === 'bin') {
      const attrs = {
        ...((hitCi?.attrs && typeof hitCi.attrs === 'object') ? hitCi.attrs : {}),
        ...((ciCalc?.attrs && typeof ciCalc.attrs === 'object') ? ciCalc.attrs : {}),
      }
      const size = sizeFromAttrs(attrs)
      const peg = numberOrNull(attrs?.peg ?? null)
      const decimals = numberOrNull(attrs?.decimals ?? null)
      const formula = String(ciCalc?.formula ?? '').trim()
      return {
        baseField: formula ? stripBracketsAroundFieldName(formula) : null,
        size,
        peg: peg !== null ? peg : 0,
        decimals,
      }
    }
    return null
  }

  const numericLiteralFromFormula = (formula: string | null): string | null => {
    const s = String(formula ?? '').trim()
    if (!s) return null
    // Most constant calcs are just `0` / `1` / `-3.14`
    if (/^[+-]?\d+(\.\d+)?$/.test(s)) return s
    // Tableau sometimes wraps numeric constants in parentheses
    const p = s.replace(/^\((.*)\)$/s, '$1').trim()
    if (/^[+-]?\d+(\.\d+)?$/.test(p)) return p
    return null
  }

  const parseAggFieldName = (rawField: string): { agg: string; inner: string } | null => {
    const s = String(rawField ?? '').trim()
    // Examples: "AVG(0)" "SUM([Sales])" "COUNTD([Customer ID])"
    const m = s.match(/^([A-Za-z_][A-Za-z0-9_]*)\s*\(\s*(.*?)\s*\)\s*$/)
    if (!m) return null
    const fn = String(m[1] ?? '').trim().toUpperCase()
    const inner = String(m[2] ?? '').trim()
    if (!fn) return null
    const allowed = new Set(['SUM', 'AVG', 'AVERAGE', 'MEAN', 'MIN', 'MAX', 'COUNT', 'COUNTD', 'COUNT_DISTINCT', 'COUNTDISTINCT'])
    if (!allowed.has(fn)) return null
    return { agg: fn, inner }
  }

  // Shared helpers used by multiple branches (relationship + viz + crosstab).
  // NOTE: Some branches define their own specialized versions; these are best-effort fallbacks.
  const inferTimeGrainFromField = (rawField: string): string | null => {
    const cleaned = cleanBracketedFieldRef0(String(rawField ?? '').trim()) || String(rawField ?? '').trim()
    if (!cleaned) return null
    const tok = cleaned.match(/^(t?yr|t?mn|t?wk|t?dy|t?qr)\s*:\s*/i)
    if (tok) {
      const t = String(tok[1] ?? '').trim().toLowerCase()
      if (t.endsWith('yr')) return 'year'
      if (t.endsWith('mn')) return 'month'
      if (t.endsWith('wk')) return 'week'
      if (t.endsWith('dy')) return 'day'
      if (t.endsWith('qr')) return 'quarter'
    }
    const fn = cleaned.match(/^(year|quarter|month|week|day|hour|minute|second)\s*\(/i)
    if (fn) return String(fn[1] ?? '').trim().toLowerCase() || null
    return null
  }
  const inferTimeGrainFromPill = (pill: any): string | null => {
    if (!pill || typeof pill !== 'object') return null
    const candidates = [
      (pill as any)?.field,
      (pill as any)?.field_name,
      (pill as any)?.display_name,
      (pill as any)?.displayName,
      (pill as any)?.field_display_name,
      (pill as any)?.fieldDisplayName,
      (pill as any)?.label,
      (pill as any)?.caption,
      (pill as any)?.name,
    ]
    for (const candidate of candidates) {
      const inferred = inferTimeGrainFromField(String(candidate ?? ''))
      if (inferred) return inferred
    }
    return null
  }

  const timeGrainFromMeta = (_rawField: string): string | null => null

  const normalizeAggTokenSimple = (raw: any, fieldName: string): string | null => {
    const s = String(raw ?? '').trim().toLowerCase()
    if (s) {
      if (s === 'average' || s === 'mean') return 'avg'
      if (s === 'ctd') return 'countd'
      if (s === 'count distinct' || s === 'count_distinct' || s === 'countd' || s === 'distinct_count') return 'countd'
      if (s === 'sum' || s === 'avg' || s === 'min' || s === 'max' || s === 'count' || s === 'countd') return s
    }
    const inline = parseAggFieldName(fieldName)
    if (inline) {
      const fn = String(inline.agg ?? '').trim().toLowerCase()
      if (fn === 'average' || fn === 'mean') return 'avg'
      if (fn === 'countd' || fn === 'count_distinct' || fn === 'countdistinct') return 'countd'
      if (fn === 'sum' || fn === 'avg' || fn === 'min' || fn === 'max' || fn === 'count') return fn
    }
    return null
  }

  const isMeasurePill = (p: any, forceDimension = false): boolean => {
    if (!p || typeof p !== 'object') return false
    if (forceDimension) return false
    const agg = String((p as any)?.aggregation ?? '').trim()
    if (agg) return true
    const rawField = String((p as any)?.field ?? (p as any)?.field_name ?? '').trim()
    const tg = String((p as any)?.time_grain ?? (p as any)?.timeGrain ?? '').trim().toLowerCase()
    if (tg) return false
    if (inferTimeGrainFromPill(p)) return false
    if (rawField && inferTimeGrainFromField(rawField)) return false
    const dt = String((p as any)?.data_type ?? (p as any)?.dataType ?? '').trim().toLowerCase()
    if (dt && (dt.includes('date') || dt.includes('time'))) return false
    return Boolean((p as any)?.is_measure)
  }

  const fieldExprFor = (rawField: string, baseRef: string): string => {
    const f = String(rawField ?? '').trim()
    const cacheKey = `${baseRef}::${f}`
    if (fieldExprCache.has(cacheKey)) return fieldExprCache.get(cacheKey) ?? ''
    const finish = (result: string, reason: string, extra?: Record<string, any>): string => {
      if (result === UNRESOLVED_FIELD) {
        noteUnresolved(f, reason, extra)
      }
      if (shouldTracePlannerColumn(f, result, extra?.resolved, extra?.colId, extra?.missingPhysicalRefs?.join?.(','))) {
        logPlannerRootTrace('field_expr', {
          worksheet: worksheetNameDebug,
          field: f,
          result,
          reason,
          source_schema_count: sourceSchemaColumns.length,
          source_schema_has_result: isColumnInSourceSchema(sourceSchema, String(extra?.colId ?? '').trim() || result.replace(/^[^.]+\./, '').replace(/^"|"$/g, '')),
          extra,
        })
      }
      if (debugFieldMapping) {
        pushFieldMappingTrace({
          phase: 'field_expr',
          message: reason,
          data: {
            worksheetName: worksheetNameDebug,
            field: f,
            result,
            reason,
            ...extra,
          },
        })
        if (reason === 'unresolved' || reason === 'compiled_error' || reason === 'compiled_nullish') {
          logFieldMapping('fieldExprFor fallback', { worksheetName: worksheetNameDebug, field: f, reason, result, ...extra })
        }
      }
      fieldExprCache.set(cacheKey, result)
      return result
    }
    if (!f) return finish(UNRESOLVED_FIELD, 'empty_field')
    if (isMeasureNames(f)) return finish(escapeValue('Measure Names'), 'measure_names_literal')
    const measureValueKey = normalizeTableauFieldKey(f)
    if (measureValueKey === 'measure values') return finish(escapeValue('Measure Values'), 'measure_values_literal')
    if (measureValueKey === 'multiple values') return finish(escapeValue('Multiple Values'), 'multiple_values_literal')
    const fClean = stripBracketsAroundFieldName(f)
    // Tableau date-part shelf tokens like "mn:Created Date" / "yr:Created Date" aren't physical columns.
    // In many workbooks (incl. Visual Vocabulary) the field dictionary may be incomplete, so we
    // decompose these tokens by stripping the leading date-part prefix and referencing the base field.
    // (This is a best-effort fallback to avoid NULL dims when schema probing is disabled/slow.)
    const dateTok = (() => {
      const m = fClean.match(/^(t?yr|t?mn|t?wk|t?dy|t?qr)\s*:\s*(.+)$/i)
      if (!m) return null
      const base = String(m[2] ?? '').trim()
      return base || null
    })()
    const physical = (() => {
      const direct = Array.isArray(extractColumns) ? extractColumns : []
      const directCols = direct
        .map((v) => String(v ?? '').trim())
        .filter(Boolean)
      if (directCols.length) return Array.from(new Set(directCols))
      const manifestCols =
        Array.isArray((options as any)?.fieldManifest?.extract_columns)
          ? (((options as any).fieldManifest.extract_columns) as any[])
          : []
      const merged = manifestCols
        .map((v) => String(v ?? '').trim())
        .filter(Boolean)
      return merged.length ? Array.from(new Set(merged)) : null
    })()
    const findPhysicalCol = (name: string): string => {
      if (!physical) return ''
      const raw = String(name ?? '').trim()
      if (!raw) return ''
      const key = normalizeTableauFieldKey(raw) || raw.toLowerCase()
      for (const c of physical) {
        const col = String(c ?? '').trim()
        if (!col) continue
        if (col === raw) return col
        const colKey = normalizeTableauFieldKey(col) || col.toLowerCase()
        if (colKey && key && colKey === key) return col
      }
      return ''
    }
    const maybeWeeklyTimePeriodOverride = null
    if (dateTok) {
      const baseNameRaw = resolveColOrRawWhenNoSchema(dateTok)
      const baseName = (baseNameRaw && baseNameRaw !== UNRESOLVED_FIELD) ? baseNameRaw : ''
      if (baseName) {
        if (!physical) return finish(`${baseRef}.${quoteIdent(baseName)}`, 'date_token', { dateTok, baseName })
        const physicalMatch = findPhysicalCol(baseName)
        if (physicalMatch) {
          return finish(`${baseRef}.${quoteIdent(physicalMatch)}`, 'date_token', { dateTok, baseName: physicalMatch })
        }
      }
    }
    // If the field resolves to a real extract column, use it.
    const resolved = resolveCol(fClean)
    const resolvedSafe = (resolved && !isUnresolvedField(resolved)) ? resolved : ''
    const colId = resolvedSafe || fClean
    const physicalMatch = findPhysicalCol(colId)
    const wantsGeneratedLatLon =
      /\(\s*generated\s*\)/i.test(fClean) &&
      (/\blatitude\b/i.test(fClean) || /\blongitude\b/i.test(fClean))
    const generatedLatLonPhysical =
      wantsGeneratedLatLon
        ? (findPhysicalCol('geometry') || findPhysicalCol('geom'))
        : ''
    const generatedLatLonApproxExpr = (() => {
      if (!wantsGeneratedLatLon || generatedLatLonPhysical) return ''
      const isLongitude = /\blongitude\b/i.test(fClean)
      const postalCol =
        findPhysicalCol('postal code') ||
        findPhysicalCol('postal_code') ||
        findPhysicalCol('zip code') ||
        findPhysicalCol('zipcode') ||
        findPhysicalCol('zip') ||
        ''
      if (postalCol) {
        const postalRef = `${baseRef}.${quoteIdent(postalCol)}`
        const digits = `REGEXP_REPLACE(CAST(${postalRef} AS VARCHAR), '[^0-9]', '', 'g')`
        const zip3 = `TRY_CAST(SUBSTR(${digits}, 1, 3) AS DOUBLE)`
        const zip5 = `TRY_CAST(SUBSTR(${digits}, 1, 5) AS DOUBLE)`
        const z = `COALESCE(${zip5}, ${zip3})`
        const bucket = `CAST(FLOOR(${zip3} / 100) AS INTEGER)`
        const fracA = `((${z} - FLOOR(${z} / 100) * 100) / 100.0)`
        const fracB = `((FLOOR((${z} - FLOOR(${z} / 1000) * 1000) / 10)) / 100.0)`
        const regions: Array<[number, number, number, number, number]> = [
          // first ZIP digit, lon_min, lon_span, lat_min, lat_span
          [0, -76.5, 8.0, 40.0, 6.0],
          [1, -80.5, 8.5, 39.0, 5.0],
          [2, -83.5, 9.0, 33.5, 6.5],
          [3, -92.5, 12.5, 25.0, 11.5],
          [4, -90.5, 10.5, 37.5, 8.5],
          [5, -97.5, 9.5, 40.0, 8.5],
          [6, -96.5, 10.0, 36.0, 7.5],
          [7, -106.0, 16.5, 29.0, 9.5],
          [8, -116.5, 15.5, 31.0, 14.5],
          [9, -124.5, 10.5, 32.0, 17.5],
        ]
        const clauses = regions
          .map(([digit, lonMin, lonSpan, latMin, latSpan]) => {
            const expr = isLongitude
              ? `${lonMin} + (${fracA} * ${lonSpan})`
              : `${latMin} + (${fracB} * ${latSpan})`
            return `WHEN ${digit} THEN ${expr}`
          })
          .join(' ')
        return `CASE WHEN ${zip3} IS NULL THEN NULL ELSE CASE ${bucket} ${clauses} ELSE NULL END END`
      }

      const stateCol =
        findPhysicalCol('state') ||
        findPhysicalCol('province') ||
        findPhysicalCol('state province') ||
        ''
      if (stateCol) {
        const stateRef = `LOWER(TRIM(CAST(${baseRef}.${quoteIdent(stateCol)} AS VARCHAR)))`
        const states: Array<[string[], number, number]> = [
          [['alabama', 'al'], -86.9023, 32.3182],
          [['arizona', 'az'], -111.0937, 34.0489],
          [['arkansas', 'ar'], -92.3731, 34.9697],
          [['california', 'ca'], -119.4179, 36.7783],
          [['colorado', 'co'], -105.7821, 39.5501],
          [['connecticut', 'ct'], -72.7554, 41.6032],
          [['delaware', 'de'], -75.5277, 38.9108],
          [['district of columbia', 'dc'], -77.0369, 38.9072],
          [['florida', 'fl'], -81.5158, 27.6648],
          [['georgia', 'ga'], -82.9001, 32.1656],
          [['idaho', 'id'], -114.7420, 44.0682],
          [['illinois', 'il'], -89.3985, 40.6331],
          [['indiana', 'in'], -86.1349, 40.2672],
          [['iowa', 'ia'], -93.0977, 41.8780],
          [['kansas', 'ks'], -98.4842, 39.0119],
          [['kentucky', 'ky'], -84.2700, 37.8393],
          [['louisiana', 'la'], -91.9623, 30.9843],
          [['maine', 'me'], -69.4455, 45.2538],
          [['maryland', 'md'], -76.6413, 39.0458],
          [['massachusetts', 'ma'], -71.3824, 42.4072],
          [['michigan', 'mi'], -85.6024, 44.3148],
          [['minnesota', 'mn'], -94.6859, 46.7296],
          [['mississippi', 'ms'], -89.3985, 32.3547],
          [['missouri', 'mo'], -91.8318, 37.9643],
          [['montana', 'mt'], -110.3626, 46.8797],
          [['nebraska', 'ne'], -99.9018, 41.4925],
          [['nevada', 'nv'], -116.4194, 38.8026],
          [['new hampshire', 'nh'], -71.5724, 43.1939],
          [['new jersey', 'nj'], -74.4057, 40.0583],
          [['new mexico', 'nm'], -105.8701, 34.5199],
          [['new york', 'ny'], -75.0000, 43.0000],
          [['north carolina', 'nc'], -79.0193, 35.7596],
          [['north dakota', 'nd'], -101.0020, 47.5515],
          [['ohio', 'oh'], -82.9071, 40.4173],
          [['oklahoma', 'ok'], -97.0929, 35.0078],
          [['oregon', 'or'], -120.5542, 43.8041],
          [['pennsylvania', 'pa'], -77.1945, 41.2033],
          [['rhode island', 'ri'], -71.4774, 41.5801],
          [['south carolina', 'sc'], -81.1637, 33.8361],
          [['south dakota', 'sd'], -99.9018, 43.9695],
          [['tennessee', 'tn'], -86.5804, 35.5175],
          [['texas', 'tx'], -99.9018, 31.9686],
          [['utah', 'ut'], -111.0937, 39.3210],
          [['vermont', 'vt'], -72.5778, 44.5588],
          [['virginia', 'va'], -78.6569, 37.4316],
          [['washington', 'wa'], -120.7401, 47.7511],
          [['west virginia', 'wv'], -80.4549, 38.5976],
          [['wisconsin', 'wi'], -89.6165, 43.7844],
          [['wyoming', 'wy'], -107.2903, 43.0760],
        ]
        const clauses = states
          .flatMap(([names, lon, lat]) =>
            names.map((name) => `WHEN '${String(name).replace(/'/g, "''")}' THEN ${isLongitude ? lon : lat}`),
          )
          .join(' ')
        return `CASE ${stateRef} ${clauses} ELSE NULL END`
      }
      return ''
    })()
    // If we don't have extractColumns (schema probe disabled/slow), but the workbook IR alias map
    // deterministically resolved this token to a base column id, trust it.
    if (ALLOW_FALLBACKS && !physical && resolvedSafe && resolvedSafe !== fClean) {
      return finish(`${baseRef}.${quoteIdent(resolvedSafe)}`, 'resolved_alias_no_schema', { resolved: resolvedSafe, colId })
    }
    const binSpec = lookupBinSpec(fClean) || lookupBinSpec(colId)
    if (binSpec && Number.isFinite(binSpec.size) && Number(binSpec.size) > 0) {
      const baseField = String(binSpec.baseField || '').trim()
      if (baseField && baseField !== fClean) {
        const baseExpr = fieldExprFor(baseField, baseRef)
        const baseExprTrim = String(baseExpr || '').trim()
        if (baseExprTrim && !/^NULL$/i.test(baseExprTrim)) {
          const size = Number(binSpec.size)
          const peg = Number.isFinite(binSpec.peg) ? Number(binSpec.peg) : 0
          const numericBaseExpr = `TRY_CAST(${baseExprTrim} AS DOUBLE)`
          const rawExpr = `FLOOR(((${numericBaseExpr}) - ${peg}) / ${size}) * ${size} + ${peg}`
          const decimals = effectiveBinDecimals(size, binSpec.decimals)
          const expr = (decimals !== null) ? `ROUND(${rawExpr}, ${decimals})` : rawExpr
          return finish(expr, 'bin_calc', { baseField, size, peg, decimals })
        }
      }
    }
    if (generatedLatLonPhysical) {
      return finish(`${baseRef}.${quoteIdent(generatedLatLonPhysical)}`, 'generated_latlon_geometry', {
        colId,
        physical: generatedLatLonPhysical,
      })
    }
    if (generatedLatLonApproxExpr) {
      return finish(generatedLatLonApproxExpr, 'generated_latlon_geographic_dimensions', {
        colId,
        source: generatedLatLonApproxExpr.includes('REGEXP_REPLACE') ? 'postal_code' : 'state',
      })
    }
    // Otherwise, attempt a calculated-field fallback.
    const formula = lookupCalculatedFormula(fClean) || lookupCalculatedFormula(colId)
    const debugTarget = debugFieldMapping && /x_path/i.test(normalizeTableauFieldKey(fClean) || fClean)
    if (debugTarget) {
      try { console.log('[FG-FIELD-MAP] fieldExprFor debug', { field: f, resolved, colId, hasFormula: Boolean(formula) }) } catch { }
    }
    const lit = numericLiteralFromFormula(formula)
    if (lit != null) return finish(lit, 'literal_from_formula', { formula })
    let compiledInfo: Record<string, any> | null = null
    if (formula) {
      const compiled = compileTableauFormulaToSql(formula || '')
      const compiledSql = compiled?.sql ? String(compiled.sql).trim() : ''
      const hasFieldRef = /"t"\s*\./i.test(compiledSql) || /\bt\./i.test(compiledSql)
      const looksNullish = isNullishExpr(compiledSql)
      compiledInfo = {
        formula,
        compiledSql,
        hasErrors: compiled.hasErrors,
        hasFieldRef,
        looksNullish,
        errors: compiled.errors,
      }
      if (debugTarget) {
        try {
          const errs = Array.isArray(compiled.errors) ? compiled.errors : []
          const errMessages = errs.map((e) => (e && typeof e === 'object' && 'message' in e ? (e as any).message : String(e)))
          console.log('[FG-FIELD-MAP] fieldExprFor compiled', {
            field: f,
            compiledSql: compiledSql.slice(0, 400),
            hasErrors: compiled.hasErrors,
            looksNullish,
            error_count: errMessages.length,
            errors: errMessages.slice(0, 10),
          })
          if (errMessages.length) {
            console.log(`[FG-FIELD-MAP] fieldExprFor errors ${JSON.stringify({ field: f, errors: errMessages })}`)
          }
        } catch { }
      }
      if (compiledSql && !/^null$/i.test(compiledSql) && !/^\(\s*null\s*\)$/i.test(compiledSql) && !compiled.hasErrors && !looksNullish) {
        if (hasExtractSchema) {
          const validation = validateSqlPhysicalRefs(compiledSql)
          if (!validation.ok) {
            if (!physicalMatch) {
              return finish(UNRESOLVED_FIELD, 'compiled_missing_physical_refs', {
                formula,
                missingPhysicalRefs: validation.missingPhysicalRefs.slice(0, 8),
                compiledSql: compiledSql.slice(0, 400),
              })
            }
          } else {
            return finish(compiledSql, 'compiled_formula', { formula, compiledSql })
          }
        } else {
          return finish(compiledSql, 'compiled_formula', { formula, compiledSql })
        }
      }
      if (compiled.hasErrors) {
        if (!physicalMatch) return finish(UNRESOLVED_FIELD, 'compiled_error', compiledInfo)
      }
      if (looksNullish || /^null$/i.test(compiledSql) || /^\(\s*null\s*\)$/i.test(compiledSql)) {
        if (!physicalMatch) return finish(UNRESOLVED_FIELD, 'compiled_nullish', compiledInfo)
      }
    }
    if (physicalMatch) {
      return finish(`${baseRef}.${quoteIdent(physicalMatch)}`, 'physical_column', { resolved, colId: physicalMatch, hasFormula: Boolean(formula) })
    }
    // If schema probing is disabled, still try a direct column reference so physical columns render.
    if (!physical) {
      if (ALLOW_FALLBACKS && !isUnresolvedField(colId)) {
        return finish(`${baseRef}.${quoteIdent(colId)}`, 'no_schema_fallback', { colId, compiledInfo })
      }
      return finish(UNRESOLVED_FIELD, 'unresolved', { colId, formula, compiledInfo })
    }
    return finish(UNRESOLVED_FIELD, 'unresolved', { colId, formula, compiledInfo })
  }

  function isNullishExpr(expr: string): boolean {
    const raw = String(expr ?? '').trim()
    if (!raw) return true
    const unwrap = (v: string): string => {
      let s = v.trim()
      while (s.startsWith('(') && s.endsWith(')')) {
        s = s.slice(1, -1).trim()
      }
      return s
    }
    const s = unwrap(raw)
    if (/^NULL$/i.test(s)) return true
    if (/^(?:TRY_)?CAST\s*\(\s*NULL\s+AS\s+[^)]+\)$/i.test(s)) return true
    if (/^CASE\b/i.test(s)) {
      const thenExprs: string[] = []
      const thenRe = /\bTHEN\b([\s\S]*?)(?=\bWHEN\b|\bELSE\b|\bEND\b)/gi
      let match: RegExpExecArray | null
      while ((match = thenRe.exec(s))) {
        thenExprs.push(String(match[1] ?? '').trim())
      }
      const elseMatch = s.match(/\bELSE\b([\s\S]*?)\bEND\b/i)
      if (elseMatch) thenExprs.push(String(elseMatch[1] ?? '').trim())
      if (thenExprs.length && thenExprs.every((t) => isNullishExpr(t))) return true
    }
    return false
  }

  function isScalarLiteralExpr(expr: string): boolean {
    const raw = String(expr ?? '').trim()
    if (!raw) return false
    const unwrap = (v: string): string => {
      let s = v.trim()
      while (s.startsWith('(') && s.endsWith(')')) {
        s = s.slice(1, -1).trim()
      }
      return s
    }
    const s = unwrap(raw)
    if (/^'(?:''|[^'])*'$/.test(s)) return true
    if (/^(?:DATE|TIME|TIMESTAMP)\s+'(?:''|[^'])*'$/i.test(s)) return true
    if (/^(?:TRUE|FALSE)$/i.test(s)) return true
    // DuckDB treats integer ORDER BY literals as positional ordinals. Preserve those.
    if (/^[+-]?\d+\.\d+(?:e[+-]?\d+)?$/i.test(s)) return true
    return false
  }

  const stripTableauDateLiteral = (raw: string): string => {
    const s = String(raw ?? '').trim()
    if (s.startsWith('#') && s.endsWith('#') && s.length >= 2) {
      const inner = s.slice(1, -1).trim()
      if (/^\d{4}-\d{2}-\d{2}(?:[ T].+)?$/.test(inner)) return inner
    }
    return s
  }

  const canonicalizeFilterDateTimeLiteral = (raw: any): string | null => {
    const s = stripTableauDateLiteral(String(raw ?? '').trim())
    if (!s) return null
    if (/^-?\d{12,}$/.test(s)) {
      const ms = Number(s)
      if (Number.isFinite(ms)) {
        const d = new Date(ms)
        if (Number.isFinite(d.getTime())) {
          const pad2 = (n: number): string => String(n).padStart(2, '0')
          return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())} ${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}:${pad2(d.getUTCSeconds())}`
        }
      }
    }
    const isoDate = s.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (isoDate) return `${isoDate[1]}-${isoDate[2]}-${isoDate[3]} 00:00:00`
    const isoDateTime = s.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/)
    if (isoDateTime) return `${isoDateTime[1]} ${isoDateTime[2]}:${isoDateTime[3]}:${isoDateTime[4] || '00'}`
    const m = s.match(
      /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?)?$/i,
    )
    if (!m) return null
    const month = Number(m[1] ?? 0)
    const day = Number(m[2] ?? 0)
    const year = Number(m[3] ?? 0)
    let hour = Number(m[4] ?? 0)
    const minute = Number(m[5] ?? 0)
    const second = Number(m[6] ?? 0)
    const meridiem = String(m[7] ?? '').trim().toUpperCase()
    if (!Number.isInteger(month) || month < 1 || month > 12) return null
    if (!Number.isInteger(day) || day < 1 || day > 31) return null
    if (!Number.isInteger(year) || year < 1000 || year > 9999) return null
    if (!Number.isInteger(hour) || hour < 0 || hour > 23) return null
    if (!Number.isInteger(minute) || minute < 0 || minute > 59) return null
    if (!Number.isInteger(second) || second < 0 || second > 59) return null
    if (meridiem) {
      if (hour < 1 || hour > 12) return null
      if (meridiem === 'AM') hour = hour % 12
      else if (meridiem === 'PM') hour = (hour % 12) + 12
      else return null
    }
    const dt = new Date(Date.UTC(year, month - 1, day, hour, minute, second))
    if (
      dt.getUTCFullYear() !== year ||
      dt.getUTCMonth() + 1 !== month ||
      dt.getUTCDate() !== day ||
      dt.getUTCHours() !== hour ||
      dt.getUTCMinutes() !== minute ||
      dt.getUTCSeconds() !== second
    ) {
      return null
    }
    const pad2 = (n: number): string => String(n).padStart(2, '0')
    return `${year}-${pad2(month)}-${pad2(day)} ${pad2(hour)}:${pad2(minute)}:${pad2(second)}`
  }

  // Escape SQL values
  //
  // IMPORTANT: dashboard actions and some workbook filters can surface values as objects
  // (e.g. { value, label } or { v }, or even Date-ish wrappers). If we stringify those,
  // DuckDB sees "[object Object]" and fails conversions. Normalize aggressively.
  const escapeValue = (vRaw: any): string => {
    const unwrap = (v: any): any => {
      if (v === null || v === undefined) return v
      if (v instanceof Date) return v.toISOString()
      if (Array.isArray(v)) return v.map(unwrap)
      if (typeof v !== 'object') return v

      // Common wrappers
      // Use `in` (not hasOwnProperty): values may be on prototypes (e.g. class instances).
      if ('value' in (v as any)) return unwrap((v as any).value)
      if ('v' in (v as any)) return unwrap((v as any).v)
      if ('raw' in (v as any)) return unwrap((v as any).raw)
      if ('key' in (v as any)) return unwrap((v as any).key)

      // Prefer stable identifiers when present
      const id = (v as any).id
      if (typeof id === 'string' || typeof id === 'number') return id

      // Human-friendly fallbacks
      const nm = (v as any).name
      if (typeof nm === 'string' || typeof nm === 'number') return nm
      const cap = (v as any).caption
      if (typeof cap === 'string' || typeof cap === 'number') return cap
      const lbl = (v as any).label
      if (typeof lbl === 'string' || typeof lbl === 'number') return lbl
      const txt = (v as any).text
      if (typeof txt === 'string' || typeof txt === 'number') return txt

      // Last-resort: if it's a small object with exactly one primitive-ish field, use it.
      try {
        const entries = Object.entries(v as any)
        if (entries.length === 1) {
          const vv = entries[0]?.[1]
          if (typeof vv === 'string' || typeof vv === 'number' || typeof vv === 'boolean') return vv
        }
      } catch { }

      return v
    }

    const v = unwrap(vRaw)
    if (v === null || v === undefined) return 'NULL'
    if (typeof v === 'string') {
      const cleaned = stripTableauDateLiteral(v)
      return `'${cleaned.replace(/'/g, "''")}'`
    }
    if (typeof v === 'number') return String(v)
    if (typeof v === 'boolean') return v ? 'TRUE' : 'FALSE'
    return `'${String(v).replace(/'/g, "''")}'`
  }

  const unwrapFilterValueForSql = (vRaw: any): any => {
    if (vRaw === null || vRaw === undefined) return vRaw
    if (vRaw instanceof Date) return vRaw
    if (Array.isArray(vRaw)) return vRaw.map(unwrapFilterValueForSql)
    if (typeof vRaw !== 'object') return vRaw
    if ('value' in (vRaw as any)) return unwrapFilterValueForSql((vRaw as any).value)
    if ('v' in (vRaw as any)) return unwrapFilterValueForSql((vRaw as any).v)
    if ('raw' in (vRaw as any)) return unwrapFilterValueForSql((vRaw as any).raw)
    if ('key' in (vRaw as any)) return unwrapFilterValueForSql((vRaw as any).key)
    const id = (vRaw as any).id
    if (typeof id === 'string' || typeof id === 'number') return id
    const name = (vRaw as any).name
    if (typeof name === 'string' || typeof name === 'number') return name
    const caption = (vRaw as any).caption
    if (typeof caption === 'string' || typeof caption === 'number') return caption
    const label = (vRaw as any).label
    if (typeof label === 'string' || typeof label === 'number') return label
    return vRaw
  }

  const parseEpochMillisDate = (valueRaw: any): Date | null => {
    const value = unwrapFilterValueForSql(valueRaw)
    if (value instanceof Date) {
      const ms = value.getTime()
      return Number.isFinite(ms) ? value : null
    }
    if (typeof value !== 'number' && typeof value !== 'string') return null
    const text = String(value).trim()
    if (!/^[+-]?\d+(?:\.\d+)?$/.test(text)) return null
    const n = Number(text)
    if (!Number.isFinite(n)) return null
    // Tableau range sliders commonly serialize date domains as JavaScript epoch
    // milliseconds. Keep ordinary numeric filters numeric; only temporal-sized
    // domains become timestamp literals.
    if (Math.abs(n) < 100_000_000_000) return null
    const dt = new Date(n)
    if (!Number.isFinite(dt.getTime())) return null
    const year = dt.getUTCFullYear()
    if (year < 1900 || year > 2300) return null
    return dt
  }

  const formatDuckdbTimestampLiteral = (dt: Date): string => {
    const pad2 = (n: number): string => String(n).padStart(2, '0')
    const year = dt.getUTCFullYear()
    const month = pad2(dt.getUTCMonth() + 1)
    const day = pad2(dt.getUTCDate())
    const hour = pad2(dt.getUTCHours())
    const minute = pad2(dt.getUTCMinutes())
    const second = pad2(dt.getUTCSeconds())
    return `TIMESTAMP '${year}-${month}-${day} ${hour}:${minute}:${second}'`
  }

  const looksTemporalFilterTarget = (exprSqlRaw: any, filterRaw?: any, fieldNameRaw?: any): boolean => {
    const exprSql = String(exprSqlRaw ?? '')
    const filter = filterRaw && typeof filterRaw === 'object' ? filterRaw : {}
    const fieldName = String(
      fieldNameRaw ??
      (filter as any)?.field_name ??
      (filter as any)?.field ??
      '',
    )
    const typeHints = [
      (filter as any)?.data_type,
      (filter as any)?.datatype,
      (filter as any)?.type,
      (filter as any)?.kind,
      (filter as any)?.time_grain,
      (filter as any)?.timeGrain,
      (filter as any)?.date_grain,
      (filter as any)?.dateGrain,
      fieldName,
      exprSql,
    ]
      .map((v) => String(v ?? '').trim().toLowerCase())
      .filter(Boolean)
      .join(' ')
    return /\b(?:date|datetime|timestamp|time_grain|date_grain)\b/.test(typeHints) ||
      /\b(?:date_trunc|to_timestamp|strftime)\s*\(/i.test(exprSql) ||
      /try_cast\s*\([\s\S]*\bas\s+(?:timestamp|date)\b/i.test(exprSql)
  }

  const buildBetweenPredicate = (exprSql: string, lo: any, hi: any, filter?: any, fieldName?: any): string => {
    if (looksTemporalFilterTarget(exprSql, filter, fieldName)) {
      const loDate = parseEpochMillisDate(lo)
      const hiDate = parseEpochMillisDate(hi)
      if (loDate && hiDate) {
        const castExpr = `TRY_CAST(${exprSql} AS TIMESTAMP)`
        return `${castExpr} BETWEEN ${formatDuckdbTimestampLiteral(loDate)} AND ${formatDuckdbTimestampLiteral(hiDate)}`
      }
    }
    return `${exprSql} BETWEEN ${escapeValue(lo)} AND ${escapeValue(hi)}`
  }

  // For IN/NOT IN style filters, we must avoid emitting values that would stringify to "[object Object]".
  // Normalize to primitive-ish scalars, otherwise return null to indicate "drop this atom".
  const normalizeFilterAtom = (vRaw: any): any => {
    const unwrap = (v: any): any => {
      if (v === null || v === undefined) return v
      if (v instanceof Date) return v.toISOString()
      if (Array.isArray(v)) return v.map(unwrap)
      if (typeof v !== 'object') return v

      if ('value' in (v as any)) return unwrap((v as any).value)
      if ('v' in (v as any)) return unwrap((v as any).v)
      if ('raw' in (v as any)) return unwrap((v as any).raw)
      if ('key' in (v as any)) return unwrap((v as any).key)

      const id = (v as any).id
      if (typeof id === 'string' || typeof id === 'number') return id
      const nm = (v as any).name
      if (typeof nm === 'string' || typeof nm === 'number') return nm
      const cap = (v as any).caption
      if (typeof cap === 'string' || typeof cap === 'number') return cap
      const lbl = (v as any).label
      if (typeof lbl === 'string' || typeof lbl === 'number') return lbl
      const txt = (v as any).text
      if (typeof txt === 'string' || typeof txt === 'number') return txt

      try {
        const entries = Object.entries(v as any)
        if (entries.length === 1) {
          const vv = entries[0]?.[1]
          if (typeof vv === 'string' || typeof vv === 'number' || typeof vv === 'boolean') return vv
        }
      } catch { }

      return v
    }

    const v = unwrap(vRaw)
    if (v === null || v === undefined) return null
    if (typeof v === 'string' && v.trim() === '[object Object]') return null
    if (typeof v === 'string') return stripTableauDateLiteral(v)
    if (typeof v === 'number' || typeof v === 'boolean') return v
    // Unknown object shape → drop (better than silently filtering to "[object Object]").
    return null
  }

  const normalizeBooleanFilterAtom = (vRaw: any): boolean | null => {
    const atom = normalizeFilterAtom(vRaw)
    if (typeof atom === 'boolean') return atom
    if (typeof atom === 'number') {
      if (atom === 1) return true
      if (atom === 0) return false
      return null
    }
    if (typeof atom !== 'string') return null
    const normalized = atom
      .trim()
      .replace(/^"(.*)"$/s, '$1')
      .replace(/^'(.*)'$/s, '$1')
      .trim()
      .toLowerCase()
    if (!normalized) return null
    if (['true', 't', 'yes', 'y', '1'].includes(normalized)) return true
    if (['false', 'f', 'no', 'n', '0'].includes(normalized)) return false
    return null
  }

  const looksBooleanLikeExpr = (exprRaw: string): boolean => {
    const expr = String(exprRaw ?? '').trim()
    if (!expr) return false
    if (/\bas\s+boolean\b/i.test(expr)) return true
    if (/\btrue\b|\bfalse\b/i.test(expr)) return true
    if (/^\(*\s*case\s+when[\s\S]+end\s*\)*$/i.test(expr)) {
      return /\bthen\s+(?:true|false)\b/i.test(expr) || /\belse\s+(?:true|false)\b/i.test(expr)
    }
    return /^\(*\s*(?:not\s+)?[\s\S]+?\s*(?:=|<>|!=|<=|>=|<|>)\s*[\s\S]+\)*\s*$/i.test(expr)
  }

  const coerceFilterAtomsForExpr = (exprSql: string, values: any[]): any[] => {
    if (!looksBooleanLikeExpr(exprSql) || values.length <= 0) return values
    const coerced: boolean[] = []
    for (const value of values) {
      const boolAtom = normalizeBooleanFilterAtom(value)
      if (boolAtom === null) return values
      coerced.push(boolAtom)
    }
    return coerced
  }

  // Tableau uses "%null%" as a sentinel in filter member lists.
  const isNullSentinel = (vRaw: any): boolean => {
    if (vRaw === null || vRaw === undefined) return false
    const s = String(vRaw ?? '').trim()
    if (!s) return false
    const unquoted = s
      .replace(/^"(.*)"$/s, '$1')
      .replace(/^'(.*)'$/s, '$1')
      .trim()
      .toLowerCase()
    if (!unquoted) return false
    return unquoted === '%null%' || unquoted.includes('%null%')
    return false
  }

  const splitInFilterValues = (raw: any[]): { values: any[]; includeNull: boolean } => {
    const values: any[] = []
    let includeNull = false
    for (const vRaw of raw) {
      if (vRaw === null || vRaw === undefined) {
        includeNull = true
        continue
      }
      if (isNullSentinel(vRaw)) {
        includeNull = true
        continue
      }
      const atom = normalizeFilterAtom(vRaw)
      if (atom === null || atom === undefined) continue
      if (isNullSentinel(atom)) {
        includeNull = true
        continue
      }
      values.push(atom)
    }
    return { values, includeNull }
  }

  const buildInPredicate = (
    exprSql: string,
    values: any[],
    includeNull: boolean,
    not: boolean,
    opts?: { includeNullsOnNot?: boolean },
  ): string | null => {
    const exprIsExtractYear = /\bEXTRACT\s*\(\s*YEAR\s+FROM\b/i.test(String(exprSql || ''))
    const exprIsExtractMonth = /\bEXTRACT\s*\(\s*MONTH\s+FROM\b/i.test(String(exprSql || ''))
    const buildLiteralInPredicate = (literalValues: any[]): string | null => {
      const includeNullsOnNot = Boolean(opts?.includeNullsOnNot)
      if (!literalValues.length) {
        if (!includeNull) return null
        return not ? `${exprSql} IS NOT NULL` : `${exprSql} IS NULL`
      }
      const valuesSql = literalValues.map(escapeValue).join(', ')
      if (includeNull) {
        return not
          ? `(${exprSql} IS NOT NULL AND ${exprSql} NOT IN (${valuesSql}))`
          : `(${exprSql} IS NULL OR ${exprSql} IN (${valuesSql}))`
      }
      if (not) {
        return includeNullsOnNot
          ? `(${exprSql} IS NULL OR ${exprSql} NOT IN (${valuesSql}))`
          : `${exprSql} NOT IN (${valuesSql})`
      }
      return `${exprSql} IN (${valuesSql})`
    }
    const literalStringValue = (() => {
      let s = String(exprSql ?? '').trim()
      while (s.startsWith('(') && s.endsWith(')')) s = s.slice(1, -1).trim()
      const m = /^'(.*)'$/s.exec(s)
      if (!m) return null
      return String(m[1] ?? '').replace(/''/g, "'")
    })()
    if (literalStringValue !== null) {
      const normalizeMember = (raw: any) => String(raw ?? '')
        .trim()
        .replace(/^"(.*)"$/s, '$1')
        .replace(/^'(.*)'$/s, '$1')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase()
      const literalKey = normalizeMember(literalStringValue)
      const matches = values.some((value) => normalizeMember(value) === literalKey)
      if (matches) return not ? 'FALSE' : null
      return not ? null : 'FALSE'
    }
    const isYearAtom = (v: any): boolean => {
      if (typeof v !== 'string') return false
      return /^\d{4}$/.test(String(v).trim())
    }
    const isMonthAtom = (v: any): boolean => {
      if (typeof v === 'number') return Number.isInteger(v) && v >= 1 && v <= 12
      if (typeof v !== 'string') return false
      const s = String(v).trim()
      if (!/^\d{1,2}$/.test(s)) return false
      const n = Number(s)
      return Number.isFinite(n) && n >= 1 && n <= 12
    }
    const looksDateLikeExpr = (expr: string): boolean => {
      const s = String(expr || '')
      if (!s) return false
      if (/\bdate_trunc\s*\(/i.test(s)) return true
      if (/\bto_timestamp\s*\(/i.test(s)) return true
      if (/\bcurrent_date\b/i.test(s)) return true
      if (/try_cast\s*\([\s\S]*\bas\s+(?:timestamp|date)\b/i.test(s)) return true
      if (/"[^"]*(?:date|time)[^"]*"/i.test(s)) return true
      if (/\b[a-z_][a-z0-9_]*\s*\.\s*"[^"]*(?:date|time)[^"]*"/i.test(s)) return true
      if (/\breportenddate\b/i.test(s)) return true
      return false
    }
    const datePartFromAtom = (v: any): { year: number | null; month: number | null } | null => {
      if (v instanceof Date) {
        const ms = v.getTime()
        if (!Number.isFinite(ms)) return null
        return { year: v.getUTCFullYear(), month: v.getUTCMonth() + 1 }
      }
      if (typeof v === 'number') {
        if (!Number.isFinite(v)) return null
        if (v > 1000000) {
          const d = new Date(v)
          if (!Number.isFinite(d.getTime())) return null
          return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1 }
        }
        if (Number.isInteger(v) && v >= 1000 && v <= 9999) return { year: v, month: null }
        if (Number.isInteger(v) && v >= 1 && v <= 12) return { year: null, month: v }
        return null
      }
      if (typeof v !== 'string') return null
      const s = String(v).trim()
      if (!s) return null
      if (/^\d{4}$/.test(s)) return { year: Number(s), month: null }
      if (/^\d{1,2}$/.test(s)) {
        const n = Number(s)
        return Number.isFinite(n) && n >= 1 && n <= 12 ? { year: null, month: n } : null
      }
      if (/^\d+$/.test(s)) {
        const n = Number(s)
        if (Number.isFinite(n) && n > 1000000) {
          const d = new Date(n)
          if (!Number.isFinite(d.getTime())) return null
          return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1 }
        }
        return null
      }
      const d = new Date(s)
      if (!Number.isFinite(d.getTime())) return null
      return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1 }
    }
    const coercedValues = coerceFilterAtomsForExpr(exprSql, values)
    if (coercedValues.length > 0 && (exprIsExtractYear || exprIsExtractMonth)) {
      const partValues = coercedValues.map(datePartFromAtom)
      if (partValues.every((v): v is { year: number | null; month: number | null } => Boolean(v))) {
        const extractedValues = partValues
          .map((v) => exprIsExtractYear ? v.year : v.month)
          .filter((v): v is number => Number.isFinite(v))
        if (extractedValues.length === coercedValues.length) {
          return buildLiteralInPredicate(extractedValues)
        }
      }
    }
    if (coercedValues.length > 0 && coercedValues.every(isYearAtom) && looksDateLikeExpr(exprSql) && !exprIsExtractYear) {
      const years = coercedValues
        .map((v) => Number(String(v).trim()))
        .filter((n) => Number.isFinite(n))
      if (years.length === coercedValues.length) {
        const yearExpr = `EXTRACT(YEAR FROM TRY_CAST(${exprSql} AS TIMESTAMP))`
        return buildInPredicate(yearExpr, years, includeNull, not, opts)
      }
    }
    if (
      coercedValues.length > 0
      && coercedValues.every(isMonthAtom)
      && looksDateLikeExpr(exprSql)
      && !exprIsExtractMonth
    ) {
      const months = coercedValues
        .map((v) => Number(String(v).trim()))
        .filter((n) => Number.isFinite(n) && n >= 1 && n <= 12)
      if (months.length === coercedValues.length) {
        const monthExpr = `EXTRACT(MONTH FROM TRY_CAST(${exprSql} AS TIMESTAMP))`
        return buildInPredicate(monthExpr, months, includeNull, not, opts)
      }
    }
    if (coercedValues.length > 0 && looksDateLikeExpr(exprSql)) {
      const canonicalDateTimes = coercedValues.map((v) => canonicalizeFilterDateTimeLiteral(v))
      if (canonicalDateTimes.every((v): v is string => typeof v === 'string' && v.length > 0)) {
        const includeNullsOnNot = Boolean(opts?.includeNullsOnNot)
        const exprAsTimestamp = `TRY_CAST(${exprSql} AS TIMESTAMP)`
        const valuesSql = canonicalDateTimes
          .map((v) => `TRY_CAST(${escapeValue(v)} AS TIMESTAMP)`)
          .join(', ')
        if (includeNull) {
          return not
            ? `(${exprAsTimestamp} IS NOT NULL AND ${exprAsTimestamp} NOT IN (${valuesSql}))`
            : `(${exprAsTimestamp} IS NULL OR ${exprAsTimestamp} IN (${valuesSql}))`
        }
        if (not) {
          return includeNullsOnNot
            ? `(${exprAsTimestamp} IS NULL OR ${exprAsTimestamp} NOT IN (${valuesSql}))`
            : `${exprAsTimestamp} NOT IN (${valuesSql})`
        }
        return `${exprAsTimestamp} IN (${valuesSql})`
      }
    }
    const includeNullsOnNot = Boolean(opts?.includeNullsOnNot)
    if (!values.length) {
      if (!includeNull) return null
      return not ? `${exprSql} IS NOT NULL` : `${exprSql} IS NULL`
    }
    const valuesSql = coercedValues.map(escapeValue).join(', ')
    if (includeNull) {
      return not
        ? `(${exprSql} IS NOT NULL AND ${exprSql} NOT IN (${valuesSql}))`
        : `(${exprSql} IS NULL OR ${exprSql} IN (${valuesSql}))`
    }
    if (not) {
      return includeNullsOnNot
        ? `(${exprSql} IS NULL OR ${exprSql} NOT IN (${valuesSql}))`
        : `${exprSql} NOT IN (${valuesSql})`
    }
    return `${exprSql} IN (${valuesSql})`
  }

  const buildInPredicateFromValue = (
    exprSql: string,
    value: any,
    not: boolean,
    opts?: { includeNullsOnNot?: boolean },
  ): string | null => {
    if (Array.isArray(value)) {
      const { values, includeNull } = splitInFilterValues(value)
      return buildInPredicate(exprSql, values, includeNull, not, opts)
    }
    if (value === null || value === undefined) return null
    if (isNullSentinel(value)) return not ? `${exprSql} IS NOT NULL` : `${exprSql} IS NULL`
    const atom = normalizeFilterAtom(value)
    if (atom === null || atom === undefined) return null
    if (isNullSentinel(atom)) return not ? `${exprSql} IS NOT NULL` : `${exprSql} IS NULL`
    return buildInPredicate(exprSql, [atom], false, not, opts)
  }

  const isExplicitEmptyFilter = (f: any): boolean => {
    if (!f || typeof f !== 'object') return false
    const explicit =
      (f as any).explicit_empty ??
      (f as any).explicit_none ??
      (f as any).explicitEmpty ??
      (f as any).explicitNone
    if (typeof explicit === 'boolean') return explicit
    return false
  }

  // Tableau `kind='hide'` filters are display/domain rules that the presentation layer
  // enforces after execution. Applying them in SQL prunes rows needed by densification,
  // table-calc windows, and shared aggregate-base reuse.
  const shouldSkipSqlFilter = (f: any): boolean => isHideDisplayFilter(f)

  const buildRelativeDatePredicate = (fieldSql: string, v: any): string | null => {
    const obj = (v && typeof v === 'object') ? (v as any) : null
    if (!obj) return null

    const num0 = (raw: any, fallback: number): number => {
      const n = Number(raw)
      return Number.isFinite(n) ? Math.trunc(n) : fallback
    }
    const bool0 = (raw: any, fallback: boolean): boolean => {
      if (raw === true) return true
      if (raw === false) return false
      const s = String(raw ?? '').trim().toLowerCase()
      if (s === 'true') return true
      if (s === 'false') return false
      return fallback
    }

    const first = num0(obj.first_period ?? obj.firstPeriod ?? obj['first-period'], 0)
    const last = num0(obj.last_period ?? obj.lastPeriod ?? obj['last-period'], 0)
    const unitRaw = String(
      obj.period_type ??
      obj.periodType ??
      obj['period-type-v2'] ??
      obj['period-type'] ??
      ''
    ).trim().toLowerCase()
    const unit =
      (unitRaw === 'minute' || unitRaw === 'hour' || unitRaw === 'day' || unitRaw === 'week' || unitRaw === 'month' || unitRaw === 'quarter' || unitRaw === 'year')
        ? unitRaw
        : ''
    if (!unit) return null

    const includeFuture = bool0(obj.include_future ?? obj.includeFuture ?? obj['include-future'], false)
    const includeNull = bool0(obj.include_null ?? obj.includeNull ?? obj['include-null'], false)
    const anchorRaw = String(obj.period_anchor ?? obj.periodAnchor ?? obj['period-anchor'] ?? '').trim()
    const anchorClean =
      (anchorRaw.startsWith('#') && anchorRaw.endsWith('#') && anchorRaw.length >= 2)
        ? anchorRaw.slice(1, -1)
        : anchorRaw
    const anchorValue = anchorClean
      ? `'${anchorClean.replace(/'/g, "''")}'`
      : 'NOW()'
    // Prefer a typed timestamp literal when the anchor is an explicit value (stable + matches Tableau semantics).
    // Fallback to TRY_CAST for NOW() / other expressions.
    const anchorExpr = anchorClean
      ? `TIMESTAMP ${anchorValue}`
      : `TRY_CAST(${anchorValue} AS TIMESTAMP)`

    const base = `date_trunc('${unit}', ${anchorExpr})`
    const intervalUnit = (unit === 'quarter') ? 'month' : unit
    const mul = (unit === 'quarter') ? 3 : 1
    const add = (offset: number): string => {
      const n = offset * mul
      if (n === 0) return base
      return `(${base} + INTERVAL '${n} ${intervalUnit}')`
    }

    const lower = add(first)
    const upperRange = add(last + 1)
    // Tableau "include future values": when false, cap the upper bound to the anchor timestamp.
    // This prevents including future dates within the current (or future) period.
    const upper = includeFuture ? upperRange : `LEAST(${upperRange}, ${anchorExpr})`
    const fieldExpr = `TRY_CAST(${fieldSql} AS TIMESTAMP)`
    const core = `(${fieldExpr} >= ${lower} AND ${fieldExpr} < ${upper})`
    return includeNull ? `(${fieldExpr} IS NULL OR ${core})` : core
  }

  const buildRelativeDatePredicateTemplate = (fieldSql: string, filterIndex: number): string => {
    const bind = (part: string): string => `/*__FG_FILTER:${filterIndex}:${part}__*/ NULL`

    const unit = `LOWER(CAST(${bind('unit')} AS VARCHAR))`
    const first = `CAST(${bind('first')} AS BIGINT)`
    const last = `CAST(${bind('last')} AS BIGINT)`
    const includeFuture = `CAST(${bind('include_future')} AS BOOLEAN)`
    const includeNull = `CAST(${bind('include_null')} AS BOOLEAN)`
    const anchorExpr = `COALESCE(TRY_CAST(${bind('anchor')} AS TIMESTAMP), NOW())`

    const trunc = (u: string): string => `date_trunc('${u}', ${anchorExpr})`
    const add = (offsetExpr: string): string => {
      const off = `(${offsetExpr})`
      return `CASE ${unit}
        WHEN 'minute' THEN (${trunc('minute')} + (${off} * INTERVAL '1 minute'))
        WHEN 'hour' THEN (${trunc('hour')} + (${off} * INTERVAL '1 hour'))
        WHEN 'day' THEN (${trunc('day')} + (${off} * INTERVAL '1 day'))
        WHEN 'week' THEN (${trunc('week')} + (${off} * INTERVAL '1 week'))
        WHEN 'month' THEN (${trunc('month')} + (${off} * INTERVAL '1 month'))
        WHEN 'quarter' THEN (${trunc('quarter')} + (((${off}) * 3) * INTERVAL '1 month'))
        WHEN 'year' THEN (${trunc('year')} + (${off} * INTERVAL '1 year'))
        ELSE (${trunc('day')} + (${off} * INTERVAL '1 day'))
      END`
    }

    const lower = add(first)
    const upperRange = add(`(${last} + 1)`)
    const upper = `CASE WHEN ${includeFuture} THEN ${upperRange} ELSE LEAST(${upperRange}, ${anchorExpr}) END`
    const fieldExpr = `TRY_CAST(${fieldSql} AS TIMESTAMP)`
    const core = `(${fieldExpr} >= ${lower} AND ${fieldExpr} < ${upper})`
    return `((${includeNull} AND ${fieldExpr} IS NULL) OR ${core})`
  }

  const buildTopNPredicate = (
    fieldSql: string,
    dimColId: string,
    dimFieldRaw: string,
    v: any,
    contextFilters: any[] | null,
    templateBindings?: { filterIndex: number; filterIndexByRef: Map<any, number> } | null,
  ): string | null => {
    const obj = (v && typeof v === 'object') ? (v as any) : null
    if (!obj) return null
    if (isNullishExpr(fieldSql)) return null
    const dimId = String(dimColId ?? '').trim()
    if (!dimId || dimId === UNRESOLVED_FIELD) return null

    if (templateMode) {
      const topFilterIndex = templateBindings?.filterIndex
      const indexByRef = templateBindings?.filterIndexByRef
      if (!(Number.isFinite(topFilterIndex) && (topFilterIndex as number) >= 0) || !(indexByRef instanceof Map)) {
        throw new Error('templateMode: TOP_N requires {filterIndex, filterIndexByRef}')
      }

      const bindScalar = (idx: number): string => `/*__FG_FILTER:${idx}__*/ NULL`
      const bindPart = (idx: number, part: string): string => `/*__FG_FILTER:${idx}:${part}__*/ NULL`
      const bindTable = (idx: number): string => `__FG_FILTER_TABLE__${idx}__`

      const buildInPredicateFromValueTemplateLocal = (exprSql: string, not: boolean, idx: number): string => {
        const tbl = bindTable(idx)
        const any = `EXISTS (SELECT 1 FROM ${tbl})`
        const hasNull = `EXISTS (SELECT 1 FROM ${tbl} WHERE value IS NULL)`
        const exprAsVarchar = `CAST(${exprSql} AS VARCHAR)`
        const exprAsDouble = `TRY_CAST(${exprSql} AS DOUBLE)`
        const exprAsBoolean = `TRY_CAST(${exprSql} AS BOOLEAN)`
        const exprAsDate = `TRY_CAST(${exprSql} AS DATE)`
        const exprAsTimestamp = `TRY_CAST(${exprSql} AS TIMESTAMP)`
        const nonNullMatch = `EXISTS (
          SELECT 1
          FROM ${tbl}
          WHERE value IS NOT NULL
            AND (
              ${exprAsVarchar} = CAST(value AS VARCHAR)
              OR (${exprAsDouble} IS NOT NULL AND TRY_CAST(value AS DOUBLE) IS NOT NULL AND ${exprAsDouble} = TRY_CAST(value AS DOUBLE))
              OR (${exprAsBoolean} IS NOT NULL AND TRY_CAST(value AS BOOLEAN) IS NOT NULL AND ${exprAsBoolean} = TRY_CAST(value AS BOOLEAN))
              OR (${exprAsDate} IS NOT NULL AND TRY_CAST(value AS DATE) IS NOT NULL AND ${exprAsDate} = TRY_CAST(value AS DATE))
              OR (${exprAsTimestamp} IS NOT NULL AND TRY_CAST(value AS TIMESTAMP) IS NOT NULL AND ${exprAsTimestamp} = TRY_CAST(value AS TIMESTAMP))
            )
        )`

        if (not) {
          // Match existing default semantics: if the IN-list contains NULL, "NOT IN" excludes NULL rows.
          return `(CASE
            WHEN NOT ${any} THEN TRUE
            WHEN ${hasNull} THEN (${exprSql} IS NOT NULL AND NOT ${nonNullMatch})
            ELSE (${exprSql} IS NULL OR NOT ${nonNullMatch})
          END)`
        }
        return `(CASE
          WHEN NOT ${any} THEN TRUE
          WHEN ${hasNull} THEN (${nonNullMatch} OR ${exprSql} IS NULL)
          ELSE (${nonNullMatch})
        END)`
      }

      const nExpr = `LEAST(5000, GREATEST(1, CAST(${bindPart(topFilterIndex as number, 'n')} AS INTEGER)))`
      const endRaw = String(obj.end ?? obj.direction ?? obj.mode ?? 'top').trim().toLowerCase()
      const dir = (endRaw === 'bottom' || endRaw === 'asc') ? 'ASC' : 'DESC'

      const topAlias = '__topn'
      const topRef = quoteIdent(topAlias)
      const dimExpr = `${topRef}.${quoteIdent(dimId)}`

      const ctxWhereParts: string[] = []
      const dimKey = normalizeTableauFieldKey(String(dimFieldRaw ?? '')) || String(dimFieldRaw ?? '').trim().toLowerCase()
      const isMeasureFilter = (f: any): boolean => {
        const ft = String(f?.filter_type ?? '').trim().toLowerCase()
        if (ft === 'measure') return true
        if (Boolean(f?.is_measure)) return true
        // Also treat explicit aggregation tokens like SUM(Foo) as measure-ish.
        const fn = String(f?.field_name ?? '').trim()
        return /^[A-Za-z_][A-Za-z0-9_]*\s*\(.*\)\s*$/.test(fn)
      }
      if (Array.isArray(contextFilters) && contextFilters.length > 0) {
        for (const f of contextFilters) {
          if (!f) continue
          if (isMeasureFilter(f)) continue
          const rawField = String(f?.field_name ?? '').trim()
          if (!rawField) continue
          if (isMeasureNames(rawField) || isMultipleValues(rawField)) continue
          const k = normalizeTableauFieldKey(rawField) || rawField.toLowerCase()
          if (k && dimKey && k === dimKey) continue

          const ctxIdx = indexByRef.get(f)
          if (!(Number.isFinite(ctxIdx) && (ctxIdx as number) >= 0)) {
            throw new Error(`templateMode: TOP_N context filter index missing for field "${rawField}"`)
          }

          const op = String(f?.operator ?? '=').trim().toUpperCase()
          if (!op) continue
          if (op === 'TOP_N') continue

          const colId = resolveColOrRawWhenNoSchema(rawField)
          if (!colId || colId === UNRESOLVED_FIELD) continue
          const colSql = `${topRef}.${quoteIdent(colId)}`

          if (op === 'RELATIVE_DATE') {
            ctxWhereParts.push(buildRelativeDatePredicateTemplate(colSql, ctxIdx as number))
            continue
          }
          if (op === 'IN' || op === 'NOT IN') {
            const not = op.startsWith('NOT')
            if (!not && isExplicitEmptyFilter(f)) {
              ctxWhereParts.push('FALSE')
              continue
            }
            ctxWhereParts.push(buildInPredicateFromValueTemplateLocal(colSql, not, ctxIdx as number))
            continue
          }
          if (op === 'IS NULL' || op === 'IS NOT NULL') {
            ctxWhereParts.push(`${colSql} ${op}`)
            continue
          }
          if (op === 'BETWEEN') {
            ctxWhereParts.push(`${colSql} BETWEEN ${bindPart(ctxIdx as number, 'lo')} AND ${bindPart(ctxIdx as number, 'hi')}`)
            continue
          }
          if (op === 'LIKE' || op === 'NOT LIKE') {
            ctxWhereParts.push(`${colSql} ${op} ${bindScalar(ctxIdx as number)}`)
            continue
          }
          if (op === '!=' || op === '<>') {
            ctxWhereParts.push(`(${colSql} IS NULL OR ${colSql} ${op} ${bindScalar(ctxIdx as number)})`)
            continue
          }
          ctxWhereParts.push(`${colSql} ${op} ${bindScalar(ctxIdx as number)}`)
        }
      }

      const byObj = (obj.by && typeof obj.by === 'object') ? (obj.by as any) : null
      const aggRaw = byObj ? String(byObj.aggregation ?? byObj.agg ?? '').trim().toUpperCase() : ''
      const byFieldRaw = byObj ? String(byObj.field ?? '').trim() : ''
      const byColId = byFieldRaw ? resolveColOrRawWhenNoSchema(byFieldRaw) : ''
      const byColSafe = (byColId && byColId !== UNRESOLVED_FIELD) ? byColId : ''
      const byInner = byColSafe ? `${topRef}.${quoteIdent(byColSafe)}` : ''

      const orderExpr = (() => {
        if (!byObj || !aggRaw || !byInner) return 'COUNT(*)'
        if (aggRaw === 'SUM') return `SUM(TRY_CAST(${byInner} AS DOUBLE))`
        if (aggRaw === 'AVG') return `AVG(TRY_CAST(${byInner} AS DOUBLE))`
        if (aggRaw === 'MIN') return `MIN(${byInner})`
        if (aggRaw === 'MAX') return `MAX(${byInner})`
        if (aggRaw === 'COUNT') return 'COUNT(*)'
        return 'COUNT(*)'
      })()

      const where = [ `${dimExpr} IS NOT NULL`, ...ctxWhereParts ].filter(Boolean).join(' AND ')
      const sub = [
        `SELECT ${dimExpr} AS v`,
        `FROM ${parquetRef} AS ${topRef}`,
        `WHERE ${where}`,
        `GROUP BY 1`,
        `ORDER BY ${orderExpr} ${dir} NULLS LAST, v ASC NULLS LAST`,
        `LIMIT ${nExpr}`,
      ].join(' ')

      return `${fieldSql} IN (${sub})`
    }

    const int0 = (raw: any): number | null => {
      const n = Number(raw)
      if (!Number.isFinite(n)) return null
      const i = Math.trunc(n)
      return i > 0 ? i : null
    }

    const n0 =
      int0(obj.n ?? obj.count ?? obj.top_n ?? obj.topN ?? obj.limit ?? obj.records) ||
      (() => {
        const p = String(obj.count_param ?? obj.countParam ?? obj.param ?? '').trim()
        if (!p) return null
        const pv = (parameters as any)[p]
        return int0(pv)
      })() ||
      10
    const n = Math.max(1, Math.min(5000, Math.trunc(Number(n0) || 10)))

    const endRaw = String(obj.end ?? obj.direction ?? obj.mode ?? 'top').trim().toLowerCase()
    const dir = (endRaw === 'bottom' || endRaw === 'asc') ? 'ASC' : 'DESC'

    const topAlias = '__topn'
    const topRef = quoteIdent(topAlias)
    const dimExpr = `${topRef}.${quoteIdent(dimId)}`

    const ctxWhereParts: string[] = []
    const dimKey = normalizeTableauFieldKey(String(dimFieldRaw ?? '')) || String(dimFieldRaw ?? '').trim().toLowerCase()
    const isMeasureFilter = (f: any): boolean => {
      const ft = String(f?.filter_type ?? '').trim().toLowerCase()
      if (ft === 'measure') return true
      if (Boolean(f?.is_measure)) return true
      // Also treat explicit aggregation tokens like SUM(Foo) as measure-ish.
      const fn = String(f?.field_name ?? '').trim()
      return /^[A-Za-z_][A-Za-z0-9_]*\s*\(.*\)\s*$/.test(fn)
    }
    if (Array.isArray(contextFilters) && contextFilters.length > 0) {
      for (const f of contextFilters) {
        if (!f) continue
        if (isMeasureFilter(f)) continue
        const rawField = String(f?.field_name ?? '').trim()
        if (!rawField) continue
        if (isMeasureNames(rawField) || isMultipleValues(rawField)) continue
        const k = normalizeTableauFieldKey(rawField) || rawField.toLowerCase()
        if (k && dimKey && k === dimKey) continue

        const op = String(f?.operator ?? '=').trim().toUpperCase()
        const value = (f as any)?.value
        if (!op) continue
        if (op === 'TOP_N') continue

        const colId = resolveColOrRawWhenNoSchema(rawField)
        if (!colId || colId === UNRESOLVED_FIELD) continue
        const colSql = `${topRef}.${quoteIdent(colId)}`

        if (op === 'RELATIVE_DATE') {
          const pred = buildRelativeDatePredicate(colSql, value)
          if (pred) ctxWhereParts.push(pred)
          continue
        }
        if (op === 'IN' || op === 'NOT IN') {
          const not = op.startsWith('NOT')
          // Tableau "All" selection may be encoded as IN with value=null.
          if (value === null || value === undefined) continue
          if (Array.isArray(value)) {
            if (value.length === 0) {
              if (!not && isExplicitEmptyFilter(f)) ctxWhereParts.push('FALSE')
              continue
            } else {
              const pred = buildInPredicateFromValue(colSql, value, not)
              if (pred) ctxWhereParts.push(pred)
            }
          } else {
            const pred = buildInPredicateFromValue(colSql, value, not)
            if (pred) ctxWhereParts.push(pred)
          }
          continue
        }
        if (op === 'IS NULL' || op === 'IS NOT NULL') {
          ctxWhereParts.push(`${colSql} ${op}`)
          continue
        }
        if (op === 'BETWEEN') {
          if (Array.isArray(value) && value.length >= 2) {
            ctxWhereParts.push(buildBetweenPredicate(colSql, value[0], value[1], f, rawField))
          }
          continue
        }
        if (op === 'LIKE' || op === 'NOT LIKE') {
          ctxWhereParts.push(`${colSql} ${op} ${escapeValue(value)}`)
          continue
        }
        if (op === '!=' || op === '<>') {
          // Match worker semantics: exclude comparisons do not implicitly drop NULLs.
          ctxWhereParts.push(`(${colSql} IS NULL OR ${colSql} ${op} ${escapeValue(value)})`)
          continue
        }
        ctxWhereParts.push(`${colSql} ${op} ${escapeValue(value)}`)
      }
    }

    const byObj = (obj.by && typeof obj.by === 'object') ? (obj.by as any) : null
    const aggRaw = byObj ? String(byObj.aggregation ?? byObj.agg ?? '').trim().toUpperCase() : ''
    const byFieldRaw = byObj ? String(byObj.field ?? '').trim() : ''
    const byColId = byFieldRaw ? resolveColOrRawWhenNoSchema(byFieldRaw) : ''
    const byColSafe = (byColId && byColId !== UNRESOLVED_FIELD) ? byColId : ''
    const byInner = byColSafe ? `${topRef}.${quoteIdent(byColSafe)}` : ''

    const orderExpr = (() => {
      if (!byObj || !aggRaw || !byInner) return 'COUNT(*)'
      if (aggRaw === 'SUM') return `SUM(TRY_CAST(${byInner} AS DOUBLE))`
      if (aggRaw === 'AVG') return `AVG(TRY_CAST(${byInner} AS DOUBLE))`
      if (aggRaw === 'MIN') return `MIN(${byInner})`
      if (aggRaw === 'MAX') return `MAX(${byInner})`
      if (aggRaw === 'COUNT') return 'COUNT(*)'
      return 'COUNT(*)'
    })()

    const where = [ `${dimExpr} IS NOT NULL`, ...ctxWhereParts ].filter(Boolean).join(' AND ')
    const sub = [
      `SELECT ${dimExpr} AS v`,
      `FROM ${parquetRef} AS ${topRef}`,
      `WHERE ${where}`,
      `GROUP BY 1`,
      `ORDER BY ${orderExpr} ${dir} NULLS LAST, v ASC NULLS LAST`,
      `LIMIT ${n}`,
    ].join(' ')

    return `${fieldSql} IN (${sub})`
  }

  // Build parquet reference
  let parquetRef: string
  if (options.baseFromSql && String(options.baseFromSql || '').trim()) {
    parquetRef = `(${String(options.baseFromSql).trim().replace(/;+\s*$/, '')})`
  } else if (extractUri.startsWith('s3://') || extractUri.startsWith('https://')) {
    // Remote extract - DuckDB can read from S3 or HTTP
    parquetRef = `read_parquet('${extractUri.replace(/'/g, "''")}')`
  } else {
    // Local path
    parquetRef = `read_parquet('${extractUri.replace(/'/g, "''")}')`
  }

  // Relationship-aware query builder (shared by viz + crosstab relationship modes).
  // This emits "late materialization" SQL:
  // - Aggregate each table to viz grain (+ relationship keys)
  // - Join aggregates via a spanning tree from the primary table
  // - Compute cross-table calcs in a post-join projection
  const buildRelationshipAwareTidyQueryShared = (args: {
    dimFields: Array<{ field: string; time_grain?: string | null; is_discrete?: boolean | null }>
    measures: Array<{ field: string; aggregation: string; alias: string }>
  }): string => {
    const model = (options as any)?.relationshipModel
    const tablesRaw: any[] = (model && typeof model === 'object' && Array.isArray((model as any).tables)) ? (model as any).tables : []
    const relsRaw: any[] = (model && typeof model === 'object' && Array.isArray((model as any).relationships)) ? (model as any).relationships : []
    const primaryName = String((model as any)?.primary_table ?? '').trim()
    const quote = quoteIdent

    const normTable = (s: any) => normalizeTableauDatasourceKey(String(s ?? '').trim())
    const tableByKey = new Map<string, { name: string; key: string; fromSql: string; extractColumns: string[]; fieldManifest: any | null }>()
    for (const t0 of tablesRaw) {
      const t = (t0 && typeof t0 === 'object') ? t0 : null
      if (!t) continue
      const name = String((t as any).name ?? '').trim()
      const key = normTable(name)
      const fromSql = String((t as any).fromSql ?? '').trim()
      if (!name || !key || !fromSql) continue
      const cols0 =
        Array.isArray((t as any).extractColumns) ? (t as any).extractColumns
          : Array.isArray((t as any).fieldManifest?.extract_columns) ? (t as any).fieldManifest.extract_columns
            : []
      const cols = cols0.map((c: any) => String(c ?? '').trim()).filter(Boolean)
      tableByKey.set(key, { name, key, fromSql, extractColumns: cols, fieldManifest: ((t as any).fieldManifest ?? null) })
    }

    const primaryKey = (() => {
      const k = normTable(primaryName)
      if (k && tableByKey.has(k)) return k
      return Array.from(tableByKey.keys())[0] || ''
    })()

    if (!primaryKey || tableByKey.size < 2) {
      return `SELECT * FROM ${parquetRef} AS ${quote('t')}`
    }

    const resolveColIn = (_tblKey: string, fieldRaw: any): string => {
      const field = String(fieldRaw ?? '').trim()
      if (!field) return ''
      return String(resolveColFromBinding(field) ?? '').trim()
    }

    const extractFieldRefsFromFormula = (formulaRaw: any): string[] => {
      const formula = String(formulaRaw ?? '')
      if (!formula) return []
      const out: string[] = []
      for (const m of formula.matchAll(/\[([^\]]+)\](?:\s*\.\s*\[([^\]]+)\])?/g)) {
        const tok = String(m?.[2] ?? m?.[1] ?? '').trim()
        if (!tok) continue
        const cleaned = cleanBracketedFieldRef0(tok) || tok
        if (!cleaned) continue
        const k = normalizeTableauFieldKey(cleaned)
        if (!k) continue
        if (k === 'measure names' || k === 'measure values' || k === 'multiple values') continue
        out.push(cleaned)
      }
      return out
    }

    const compileFormulaForAlias = (formula: string, baseAlias: string): string | null => {
      const raw = String(formula ?? '').trim()
      if (!raw) return null
      const compiled = compileTableauFormulaToSql(raw)
      const compiledSql = compiled?.sql ? String(compiled.sql).trim() : ''
      if (!compiledSql || compiled.hasErrors) return null
      let sql = compiledSql
      try { sql = sql.replaceAll('"t".', `${baseAlias}.`) } catch { }
      try { sql = sql.replaceAll('t.', `${baseAlias}.`) } catch { }
      return sql
    }

    const chooseTableForField = (fieldRaw: any): { tableKey: string; kind: 'physical' | 'calc' | 'calc_cross' | 'unknown' } => {
      const field = String(fieldRaw ?? '').trim()
      if (!field) return { tableKey: primaryKey, kind: 'unknown' }
      for (const k of tableByKey.keys()) {
        if (resolveColIn(k, field)) return { tableKey: k, kind: 'physical' }
      }
      const formula = lookupCalculatedFormula(field)
      if (formula) {
        const refs = extractFieldRefsFromFormula(formula)
        if (refs.length) {
          for (const k of tableByKey.keys()) {
            const ok = refs.every((r) => Boolean(resolveColIn(k, r)))
            if (ok) return { tableKey: k, kind: 'calc' }
          }
          return { tableKey: primaryKey, kind: 'calc_cross' }
        }
      }
      return { tableKey: primaryKey, kind: formula ? 'calc' : 'unknown' }
    }

    const fieldExprInTable = (tblKey: string, fieldRaw: any): string => {
      const field = String(fieldRaw ?? '').trim()
      if (!field) return 'NULL'
      const col = resolveColIn(tblKey, field)
      const baseAlias = quote(`t_${tblKey}`)
      if (col) return `${baseAlias}.${quote(col)}`
      const formula = lookupCalculatedFormula(field)
      if (!formula) return 'NULL'
      try {
        const refs = extractFieldRefsFromFormula(formula)
        if (refs.length) {
          const ok = refs.every((r) => Boolean(resolveColIn(tblKey, r)))
          if (!ok) return 'NULL'
        }
      } catch { }
      const lit = numericLiteralFromFormula(formula)
      if (lit != null) return String(lit)
      const compiled = compileFormulaForAlias(formula, baseAlias)
      return compiled ? compiled : 'NULL'
    }

    const aggExpr = (aggRaw: any, colExpr: string): string => {
      const a = String(aggRaw ?? '').trim().toLowerCase()
      if (a === 'avg' || a === 'average' || a === 'mean') return `AVG(TRY_CAST(${colExpr} AS DOUBLE))`
      if (a === 'min') return `MIN(${colExpr})`
      if (a === 'max') return `MAX(${colExpr})`
      if (a === 'count') return `COUNT(${colExpr})`
      if (a === 'countd' || a === 'count distinct' || a === 'count_distinct') return `COUNT(DISTINCT ${colExpr})`
      return `SUM(TRY_CAST(${colExpr} AS DOUBLE))`
    }

    const applyTimeGrain = (expr: string, grain: string | null, isDiscrete?: boolean | null): string => {
      const g = String(grain ?? '').trim().toLowerCase()
      if (!g) return expr
      const ts = `TRY_CAST(${expr} AS TIMESTAMP)`
      if (isDiscrete) {
        if (g === 'year') return `EXTRACT(year FROM ${ts})`
        if (g === 'quarter' || g === 'month' || g === 'week' || g === 'day' || g === 'hour' || g === 'minute' || g === 'second') {
          return `DATE_TRUNC('${g}', ${ts})`
        }
        return expr
      }
      if (g === 'quarter' || g === 'month' || g === 'week' || g === 'day' || g === 'hour' || g === 'minute' || g === 'second' || g === 'year') {
        return `DATE_TRUNC('${g}', ${ts})`
      }
      return expr
    }

    const normalizeAggTok0 = (raw: any, fallbackFieldName: string): string => {
      const s = String(raw ?? '').trim().toLowerCase()
      if (!s) return 'sum'
      if (s === 'average' || s === 'mean') return 'avg'
      if (s === 'ctd') return 'countd'
      if (s === 'count distinct' || s === 'count_distinct' || s === 'countd' || s === 'distinct_count') return 'countd'
      if (s === 'sum' || s === 'avg' || s === 'min' || s === 'max' || s === 'count' || s === 'countd') return s
      const inline = parseAggFieldName(fallbackFieldName)
      if (inline) {
        const fn = String(inline.agg ?? '').trim().toLowerCase()
        if (fn === 'average' || fn === 'mean') return 'avg'
        if (fn === 'countd' || fn === 'count_distinct' || fn === 'countdistinct') return 'countd'
        if (fn === 'sum' || fn === 'avg' || fn === 'min' || fn === 'max' || fn === 'count') return fn
      }
      return 'sum'
    }

    const aggTokFromFn = (fnRaw: any): string => {
      const fn = String(fnRaw ?? '').trim().toUpperCase()
      if (!fn) return 'sum'
      if (fn === 'AVG' || fn === 'AVERAGE' || fn === 'MEAN') return 'avg'
      if (fn === 'MIN') return 'min'
      if (fn === 'MAX') return 'max'
      if (fn === 'COUNT') return 'count'
      if (fn === 'COUNTD' || fn === 'COUNT_DISTINCT' || fn === 'COUNTDISTINCT') return 'countd'
      if (fn === 'SUM') return 'sum'
      return 'sum'
    }

    const inferAggTokForRefInFormula = (formulaRaw: string, refField: string, defaultAgg: string): string => {
      const formula = String(formulaRaw ?? '')
      const ref = String(refField ?? '').trim()
      if (!formula || !ref) return defaultAgg
      const esc = escapeRegExp(ref)
      const atom = String.raw`(?:\[[^\]]+\]\s*\.\s*)?\[${esc}\]`
      const patterns: Array<{ fn: string; re: RegExp }> = [
        { fn: 'COUNTD', re: new RegExp(String.raw`\b(COUNTD|COUNT_DISTINCT|COUNTDISTINCT)\s*\(\s*${atom}\s*\)`, 'i') },
        { fn: 'COUNT', re: new RegExp(String.raw`\bCOUNT\s*\(\s*${atom}\s*\)`, 'i') },
        { fn: 'AVG', re: new RegExp(String.raw`\b(AVG|AVERAGE|MEAN)\s*\(\s*${atom}\s*\)`, 'i') },
        { fn: 'SUM', re: new RegExp(String.raw`\bSUM\s*\(\s*${atom}\s*\)`, 'i') },
        { fn: 'MIN', re: new RegExp(String.raw`\bMIN\s*\(\s*${atom}\s*\)`, 'i') },
        { fn: 'MAX', re: new RegExp(String.raw`\bMAX\s*\(\s*${atom}\s*\)`, 'i') },
      ]
      for (const p of patterns) {
        if (p.re.test(formula)) return aggTokFromFn(p.fn)
      }
      return defaultAgg
    }

    const mergeAggTok = (a: string, b: string): string => {
      const rank: Record<string, number> = { countd: 60, count: 50, avg: 40, sum: 30, min: 20, max: 20 }
      const aa = String(a ?? '').trim().toLowerCase() || 'sum'
      const bb = String(b ?? '').trim().toLowerCase() || 'sum'
      return (rank[bb] ?? 0) > (rank[aa] ?? 0) ? bb : aa
    }

    const edges: Array<{ a: string; b: string; clauses: Array<{ left: string; right: string }> }> = []
    for (const r0 of relsRaw) {
      const r = (r0 && typeof r0 === 'object') ? r0 : null
      if (!r) continue
      const a = normTable((r as any).a)
      const b = normTable((r as any).b)
      if (!a || !b || a === b) continue
      if (!tableByKey.has(a) || !tableByKey.has(b)) continue
      const clauses0 = Array.isArray((r as any).clauses) ? (r as any).clauses : []
      const clauses = clauses0
        .map((c0: any) => {
          const c = (c0 && typeof c0 === 'object') ? c0 : null
          if (!c) return null
          const l = resolveColIn(a, c.left)
          const rr = resolveColIn(b, c.right)
          if (!l || !rr) return null
          return { left: l, right: rr }
        })
        .filter(Boolean) as Array<{ left: string; right: string }>
      if (!clauses.length) continue
      edges.push({ a, b, clauses })
    }

    const adjacency = new Map<string, Array<{ to: string; edge: any; dir: 'a_to_b' | 'b_to_a' }>>()
    const addAdj = (from: string, to: string, edge: any, dir: 'a_to_b' | 'b_to_a') => {
      const arr = adjacency.get(from) || []
      arr.push({ to, edge, dir })
      adjacency.set(from, arr)
    }
    for (const e of edges) {
      addAdj(e.a, e.b, e, 'a_to_b')
      addAdj(e.b, e.a, e, 'b_to_a')
    }

    const dimByTable = new Map<string, Array<{ field: string; expr: string; groupExpr: string }>>()
    const measByTable = new Map<string, Array<{ field: string; alias: string; expr: string }>>()
    const dimOwner = new Map<string, string>()
    const measOwner = new Map<string, string>()
    const usedTables = new Set<string>([primaryKey])

    const claimDim = (tblKey: string, entry: { field: string; expr: string; groupExpr: string }) => {
      const arr = dimByTable.get(tblKey) || []
      arr.push(entry)
      dimByTable.set(tblKey, arr)
      dimOwner.set(entry.field, tblKey)
      usedTables.add(tblKey)
    }
    const claimMeas = (tblKey: string, entry: { field: string; alias: string; expr: string }) => {
      const aliasKey = String(entry.alias ?? '').trim()
      if (!aliasKey) return
      const existing = measByTable.get(tblKey) || []
      if (existing.some((x) => String((x as any)?.alias ?? '').trim() === aliasKey)) {
        usedTables.add(tblKey)
        measOwner.set(aliasKey, tblKey)
        return
      }
      const arr = measByTable.get(tblKey) || []
      arr.push(entry)
      measByTable.set(tblKey, arr)
      measOwner.set(aliasKey, tblKey)
      usedTables.add(tblKey)
    }

    const crossCalcs: Array<{ alias: string; formula: string }> = []
    const depAggsByTable = new Map<string, Map<string, string>>()

    for (const d of args.dimFields) {
      const f = String(d?.field ?? '').trim()
      if (!f) continue
      const chosenInfo = chooseTableForField(f)
      const chosen = chosenInfo.tableKey
      const rawExpr = fieldExprInTable(chosen, f)
      const expr = applyTimeGrain(rawExpr, (d as any)?.time_grain ?? null, (d as any)?.is_discrete ?? null)
      claimDim(chosen, { field: f, expr, groupExpr: expr })
    }

    for (const m of args.measures) {
      const f = String(m?.field ?? '').trim()
      const a = String(m?.aggregation ?? '').trim()
      const aliasName = String(m?.alias ?? '').trim()
      if (!f || !aliasName) continue
      const chosenInfo = chooseTableForField(f)
      const chosen = chosenInfo.tableKey
      if (chosenInfo.kind === 'calc_cross') {
        const formula = lookupCalculatedFormula(f)
        if (!formula) continue
        crossCalcs.push({ alias: aliasName, formula })
        const defaultAgg = normalizeAggTok0(a || 'sum', f)
        const refs = extractFieldRefsFromFormula(formula)
        for (const r of refs) {
          for (const k of tableByKey.keys()) {
            const col = resolveColIn(k, r)
            if (!col) continue
            const aggTok = inferAggTokForRefInFormula(formula, r, defaultAgg)
            const m0 = depAggsByTable.get(k) || new Map<string, string>()
            const prev = m0.get(col) || ''
            m0.set(col, mergeAggTok(prev, aggTok))
            depAggsByTable.set(k, m0)
            usedTables.add(k)
            break
          }
        }
        continue
      }
      const baseExpr = fieldExprInTable(chosen, f)
      const looksAgg = /\b(SUM|AVG|MIN|MAX|COUNT)\s*\(/i.test(baseExpr)
      const looksWin = /\bOVER\s*\(/i.test(baseExpr)
      const expr = (looksAgg || looksWin) ? baseExpr : aggExpr(a || 'sum', baseExpr)
      claimMeas(chosen, { field: f, alias: aliasName, expr })
    }

    for (const [k, cols] of depAggsByTable.entries()) {
      const tAlias = quote(`t_${k}`)
      for (const [col, aggTok] of cols.entries()) {
        const c = String(col ?? '').trim()
        if (!c) continue
        claimMeas(k, { field: c, alias: c, expr: aggExpr(aggTok || 'sum', `${tAlias}.${quote(c)}`) })
      }
    }

    const parent = new Map<string, { prev: string; edge: any; dir: 'a_to_b' | 'b_to_a' }>()
    const reachable = new Set<string>()
    const q: string[] = []
    reachable.add(primaryKey)
    q.push(primaryKey)
    while (q.length) {
      const cur = q.shift()!
      const neigh = adjacency.get(cur) || []
      for (const it of neigh) {
        const nx = it.to
        if (reachable.has(nx)) continue
        reachable.add(nx)
        parent.set(nx, { prev: cur, edge: it.edge, dir: it.dir })
        q.push(nx)
      }
    }

    const needed = new Set<string>(Array.from(usedTables).filter((k) => reachable.has(k)))
    needed.add(primaryKey)
    const unreachable = Array.from(usedTables).filter((k) => !reachable.has(k))
    if (unreachable.length) {
      try { if (worksheetDebugEnabled) console.warn('[worksheetQueryBuilder] relationship graph disconnected; nulling fields from tables:', unreachable) } catch { }
    }

    const depth = (n: string): number => {
      let d = 0
      let cur = n
      const seen = new Set<string>()
      while (cur !== primaryKey && parent.has(cur) && !seen.has(cur)) {
        seen.add(cur)
        d++
        cur = parent.get(cur)!.prev
      }
      return d
    }

    const joinSteps: Array<{ node: string; prev: string; edge: any; dir: 'a_to_b' | 'b_to_a' }> = []
    for (const node of needed) {
      if (node === primaryKey) continue
      const p = parent.get(node)
      if (!p) continue
      joinSteps.push({ node, prev: p.prev, edge: p.edge, dir: p.dir })
    }
    joinSteps.sort((a, b) => depth(a.node) - depth(b.node) || a.node.localeCompare(b.node))

    const joinColsByTable = new Map<string, Set<string>>()
    const needJoinCol = (tblKey: string, col: string) => {
      const set = joinColsByTable.get(tblKey) || new Set<string>()
      set.add(col)
      joinColsByTable.set(tblKey, set)
    }
    for (const st of joinSteps) {
      const e = st.edge
      for (const c of e.clauses) {
        needJoinCol(e.a, c.left)
        needJoinCol(e.b, c.right)
      }
    }

    const whereByTable = new Map<string, string[]>()
    const pushWhere = (tblKey: string, cond: string) => {
      if (!cond) return
      const arr = whereByTable.get(tblKey) || []
      arr.push(cond)
      whereByTable.set(tblKey, arr)
    }

    const filterList: any[] = Array.isArray(filters) ? (filters as any[]) : []
    for (const f0 of filterList) {
      const f = (f0 && typeof f0 === 'object') ? f0 : null
      if (!f) continue
      const field = String((f as any)?.field_name ?? '').trim()
      if (!field) continue
      const opRaw = String((f as any)?.operator ?? '').trim()
      const op = opRaw ? opRaw.toUpperCase() : ''
      if (shouldSkipSqlFilter(f)) continue
      const value = (f as any)?.value
      for (const k of needed) {
        const col = resolveColIn(k, field)
        if (!col) continue
        const tAlias = quote(`t_${k}`)
        const colSql = `${tAlias}.${quote(col)}`
        if (!op || op === '=') pushWhere(k, `${colSql} = ${escapeValue(value)}`)
        else if (op === '!=' || op === '<>') pushWhere(k, `(${colSql} IS NULL OR ${colSql} ${op} ${escapeValue(value)})`)
        else if (op === '>' || op === '>=' || op === '<' || op === '<=') pushWhere(k, `${colSql} ${op} ${escapeValue(value)}`)
        else if (op === 'IS NULL') pushWhere(k, `${colSql} IS NULL`)
        else if (op === 'IS NOT NULL') pushWhere(k, `${colSql} IS NOT NULL`)
        else if (op === 'LIKE' || op === 'NOT LIKE') pushWhere(k, `${colSql} ${op} ${escapeValue(value)}`)
        else if (op === 'IN' || op === 'NOT IN') {
          const arr = Array.isArray(value) ? value : [value]
          if (arr.length) pushWhere(k, `${colSql} ${op} (${arr.map((v: any) => escapeValue(v)).join(', ')})`)
        } else if (op === 'BETWEEN' && Array.isArray(value) && value.length >= 2) {
          pushWhere(k, buildBetweenPredicate(colSql, value[0], value[1], f, k))
        } else {
          pushWhere(k, `${colSql} ${op} ${escapeValue(value)}`)
        }
      }
    }

    const subByKey = new Map<string, { sql: string; alias: string }>()
    for (const k of needed) {
      const tbl = tableByKey.get(k)
      if (!tbl) continue
      const tAlias = quote(`t_${k}`)
      const keyCols = Array.from(joinColsByTable.get(k) || new Set<string>())
      const dims0 = dimByTable.get(k) || []
      const meas0 = measByTable.get(k) || []
      const selectParts: string[] = []
      const groupBy: string[] = []
      for (const c of keyCols) {
        selectParts.push(`${tAlias}.${quote(c)} AS ${quote(c)}`)
        groupBy.push(`${tAlias}.${quote(c)}`)
      }
      for (const d of dims0) {
        selectParts.push(`${d.expr} AS ${quote(d.field)}`)
        if (canSqlExpressionAppearInGroupBy(d.expr)) groupBy.push(d.groupExpr)
      }
      for (const m of meas0) selectParts.push(`${m.expr} AS ${quote(m.alias)}`)
      if (!selectParts.length) continue
      let sql = `SELECT ${selectParts.join(', ')} FROM ${String(tbl.fromSql).trim()} AS ${tAlias}`
      const where = whereByTable.get(k) || []
      if (where.length) sql += ` WHERE ${where.join(' AND ')}`
      if (groupBy.length && meas0.length) sql += ` GROUP BY ${groupBy.join(', ')}`
      subByKey.set(k, { sql: `(${sql})`, alias: quote(`q_${k}`) })
    }

    const base = subByKey.get(primaryKey)
    if (!base) return `SELECT * FROM ${parquetRef} AS ${quote('t')}`

    let from = `${base.sql} AS ${base.alias}`
    for (const st of joinSteps) {
      const child = subByKey.get(st.node)
      if (!child) continue
      const parentAlias = subByKey.get(st.prev)?.alias || base.alias
      const e = st.edge
      const on = e.clauses.map((c: any) => {
        const lcol = (st.dir === 'a_to_b') ? c.left : c.right
        const rcol = (st.dir === 'a_to_b') ? c.right : c.left
        return `${parentAlias}.${quote(String(lcol))} = ${child.alias}.${quote(String(rcol))}`
      }).join(' AND ')
      from = `${from} LEFT JOIN ${child.sql} AS ${child.alias} ON ${on || 'TRUE'}`
    }

    const dimNames = args.dimFields
      .map((d) => String(d?.field ?? '').trim().replace(/^:+/, ''))
      .filter(Boolean)
    const measureAliases = args.measures.map((m) => String(m?.alias ?? '').trim()).filter(Boolean)

    const innerSelect: string[] = []
    for (const f of dimNames) {
      const owner = dimOwner.get(f) || primaryKey
      if (!reachable.has(owner)) {
        innerSelect.push(`NULL AS ${quote(f)}`)
        continue
      }
      const alias = subByKey.get(owner)?.alias || base.alias
      innerSelect.push(`${alias}.${quote(f)} AS ${quote(f)}`)
    }
    for (const a0 of measureAliases) {
      if (crossCalcs.some((c) => c.alias === a0)) continue
      const owner = measOwner.get(a0) || primaryKey
      if (!reachable.has(owner)) {
        innerSelect.push(`NULL AS ${quote(a0)}`)
        continue
      }
      const alias = subByKey.get(owner)?.alias || base.alias
      innerSelect.push(`${alias}.${quote(a0)} AS ${quote(a0)}`)
    }

    const depAggAll = new Map<string, string>()
    const depOwner = new Map<string, string>()
    for (const [k, cols] of depAggsByTable.entries()) {
      for (const [col, aggTok] of cols.entries()) {
        const key = String(col ?? '').trim()
        if (!key) continue
        const prevAgg = depAggAll.get(key) || ''
        depAggAll.set(key, mergeAggTok(prevAgg, String(aggTok ?? '').trim()))
        const prevOwner = depOwner.get(key) || ''
        if (!prevOwner) {
          depOwner.set(key, k)
          continue
        }
        // Prefer the primary table first, then any reachable table. This avoids
        // assigning lifted dependency columns to disconnected datasource copies.
        if (k === primaryKey && prevOwner !== primaryKey) {
          depOwner.set(key, k)
          continue
        }
        if (!reachable.has(prevOwner) && reachable.has(k)) depOwner.set(key, k)
      }
    }
    for (const col of Array.from(depAggAll.keys())) {
      const owner = depOwner.get(col) || ''
      if (!owner || !reachable.has(owner)) {
        innerSelect.push(`NULL AS ${quote(col)}`)
        continue
      }
      const alias = subByKey.get(owner)?.alias || base.alias
      innerSelect.push(`${alias}.${quote(col)} AS ${quote(col)}`)
    }

    const limRaw = Number.isFinite(Number(limit)) ? Math.floor(Number(limit)) : 10_000
    const lim = limRaw > 0 ? Math.min(250_000, limRaw) : 0
    const joinedSql = `SELECT ${innerSelect.length ? innerSelect.join(', ') : '*'} FROM ${from}`

    const rewriteCrossCalcToUseLiftedDeps = (sqlIn: string): string => {
      let sql = String(sqlIn ?? '')
      for (const [col, aggTok] of depAggAll.entries()) {
        const esc = escapeRegExp(col)
        const atom = String.raw`${quote('t')}\.${quote(col)}`
        if (aggTok === 'sum') {
          sql = sql.replace(new RegExp(String.raw`\bSUM\s*\(\s*TRY_CAST\s*\(\s*${atom}\s+AS\s+DOUBLE\s*\)\s*\)\s*`, 'gi'), `${atom}`)
          sql = sql.replace(new RegExp(String.raw`\bSUM\s*\(\s*${atom}\s*\)\s*`, 'gi'), `${atom}`)
        } else if (aggTok === 'countd') {
          sql = sql.replace(new RegExp(String.raw`\bCOUNTD\s*\(\s*${atom}\s*\)\s*`, 'gi'), `${atom}`)
          sql = sql.replace(new RegExp(String.raw`\bCOUNT\s*\(\s*DISTINCT\s+${atom}\s*\)\s*`, 'gi'), `${atom}`)
        } else if (aggTok === 'count') {
          sql = sql.replace(new RegExp(String.raw`\bCOUNT\s*\(\s*${atom}\s*\)\s*`, 'gi'), `${atom}`)
        } else if (aggTok === 'avg') {
          sql = sql.replace(new RegExp(String.raw`\bAVG\s*\(\s*TRY_CAST\s*\(\s*${atom}\s+AS\s+DOUBLE\s*\)\s*\)\s*`, 'gi'), `${atom}`)
          sql = sql.replace(new RegExp(String.raw`\bAVG\s*\(\s*${atom}\s*\)\s*`, 'gi'), `${atom}`)
        } else if (aggTok === 'min') {
          sql = sql.replace(new RegExp(String.raw`\bMIN\s*\(\s*${atom}\s*\)\s*`, 'gi'), `${atom}`)
        } else if (aggTok === 'max') {
          sql = sql.replace(new RegExp(String.raw`\bMAX\s*\(\s*${atom}\s*\)\s*`, 'gi'), `${atom}`)
        }
        sql = sql.replace(new RegExp(String.raw`("t"\s*\.\s*"${esc}")`, 'g'), `${quote('t')}.${quote(col)}`)
      }
      return sql
    }

    const tAlias = quote('t')
    const outerSelect: string[] = []
    for (const f of dimNames) outerSelect.push(`${tAlias}.${quote(f)} AS ${quote(f)}`)
    for (const a0 of measureAliases) {
      const cross = crossCalcs.find((c) => c.alias === a0) || null
      if (!cross) {
        outerSelect.push(`${tAlias}.${quote(a0)} AS ${quote(a0)}`)
        continue
      }
      const compiled = compileTableauFormulaToSql(String(cross.formula ?? ''))
      let expr = compiled?.sql && !compiled.hasErrors ? String(compiled.sql).trim() : 'NULL'
      expr = rewriteCrossCalcToUseLiftedDeps(expr)
      outerSelect.push(`${expr} AS ${quote(a0)}`)
    }

    let sql = `SELECT ${outerSelect.length ? outerSelect.join(', ') : '*'} FROM (${joinedSql}) AS ${tAlias}`

    if (densify && dimNames.length > 0 && dimNames.length <= 4) {
      const doms = dimNames.map((nm, idx) => {
        const d = quote(`d${idx}`)
        return `(SELECT DISTINCT ${d}.${quote(nm)} AS ${quote(nm)} FROM (${sql}) AS ${d} WHERE ${d}.${quote(nm)} IS NOT NULL)`
      })
      const domainFrom = doms.map((d, idx) => `${d} AS ${quote(`dom${idx}`)}`).join(' CROSS JOIN ')
      const baseRef = quote('b')
      const on = dimNames.map((nm, idx) => `${quote(`dom${idx}`)}.${quote(nm)} = ${baseRef}.${quote(nm)}`).join(' AND ')
      const sel = [
        ...dimNames.map((nm, idx) => `${quote(`dom${idx}`)}.${quote(nm)} AS ${quote(nm)}`),
        ...measureAliases.map((nm) => `${baseRef}.${quote(nm)} AS ${quote(nm)}`),
      ].join(', ')
      sql = `SELECT ${sel} FROM ${domainFrom} LEFT JOIN (${sql}) AS ${baseRef} ON ${on || 'TRUE'}`
    }

    if (includeTotals && dimNames.length && measureAliases.length) {
      const b = quote('b')
      const totSelect = [
        ...dimNames.map((nm) => `${escapeValue('All')} AS ${quote(nm)}`),
        ...measureAliases.map((nm) => `SUM(TRY_CAST(${b}.${quote(nm)} AS DOUBLE)) AS ${quote(nm)}`),
      ].join(', ')
      const totalSql = `SELECT ${totSelect} FROM (${sql}) AS ${b}`
      sql = `(${sql}) UNION ALL (${totalSql})`
    }

    if (dimNames.length) {
      const o = quote('o')
      const order = dimNames.map((nm) => `${o}.${quote(nm)} ASC NULLS LAST`).join(', ')
      sql = `SELECT * FROM (${sql}) AS ${o} ORDER BY ${order}`
    }

    if (limRaw > 0) sql += ` LIMIT ${lim}`
    return sql
  }

  const dedupeWindowOverClausesInSelect = (sqlIn: string): string => {
    const sql = String(sqlIn ?? '')
    if (!/\bover\s*\(/i.test(sql)) return sql
    const overTokenCount = sql.match(/\bover\b/ig)?.length ?? 0
    if (overTokenCount < 2) return sql
    const beforeLen = sql.length
    let replaceCount = 0

    const isWordChar = (c: string) => /[A-Za-z0-9_]/.test(c)
    const findMatchingParen = (s: string, start: number): number => {
      let depth = 0
      let inStr = false
      let inIdent = false
      for (let i = start; i < s.length; i++) {
        const ch = s[i]
        if (ch === "'") {
          if (!inIdent && inStr && s[i + 1] === "'") {
            i++
            continue
          }
          if (!inIdent) inStr = !inStr
          continue
        }
        if (!inStr && ch === '"') {
          if (inIdent && s[i + 1] === '"') {
            i++
            continue
          }
          inIdent = !inIdent
          continue
        }
        if (inStr || inIdent) continue
        if (ch === '(') depth++
        else if (ch === ')') {
          depth--
          if (depth === 0) return i
        }
      }
      return -1
    }

    const normalizeClause = (s: string) => s.replace(/\s+/g, ' ').trim()
    const findClauseKeyword = (s: string, keyword: string): number => {
      const kw = keyword.toUpperCase()
      let depth = 0
      let inStr2 = false
      let inIdent2 = false
      let inLine2 = false
      let inBlock2 = false
      for (let i = 0; i < s.length; i++) {
        const c = s[i]
        const n = s[i + 1]
        if (inLine2) {
          if (c === '\n') inLine2 = false
          continue
        }
        if (inBlock2) {
          if (c === '*' && n === '/') {
            inBlock2 = false
            i++
          }
          continue
        }
        if (!inStr2 && !inIdent2 && c === '-' && n === '-') {
          inLine2 = true
          i++
          continue
        }
        if (!inStr2 && !inIdent2 && c === '/' && n === '*') {
          inBlock2 = true
          i++
          continue
        }
        if (c === "'") {
          if (inStr2 && n === "'") {
            i++
            continue
          }
          inStr2 = !inStr2
          continue
        }
        if (!inStr2 && c === '"') {
          if (inIdent2 && n === '"') {
            i++
            continue
          }
          inIdent2 = !inIdent2
          continue
        }
        if (inStr2 || inIdent2) continue
        if (c === '(') depth++
        else if (c === ')') depth = Math.max(0, depth - 1)
        if (depth !== 0) continue
        if (s.slice(i, i + kw.length).toUpperCase() === kw) {
          const prev2 = s[i - 1]
          const next2 = s[i + kw.length]
          if (prev2 && isWordChar(prev2)) continue
          if (next2 && isWordChar(next2)) continue
          return i
        }
      }
      return -1
    }
    const splitWindowClause = (clause: string) => {
      const trimmed = clause.trim()
      if (!trimmed) return null
      const fullKey = normalizeClause(trimmed)
      const orderIdx = findClauseKeyword(trimmed, 'ORDER BY')
      const rowsIdx = findClauseKeyword(trimmed, 'ROWS')
      const rangeIdx = findClauseKeyword(trimmed, 'RANGE')
      const groupsIdx = findClauseKeyword(trimmed, 'GROUPS')
      const excludeIdx = findClauseKeyword(trimmed, 'EXCLUDE')
      const frameCandidates = [rowsIdx, rangeIdx, groupsIdx, excludeIdx].filter((v) => v >= 0)
      const frameIdx = frameCandidates.length ? Math.min(...frameCandidates) : -1
      const baseClause = frameIdx >= 0 ? trimmed.slice(0, frameIdx).trim() : trimmed
      const tailClause = frameIdx >= 0 ? trimmed.slice(frameIdx).trim() : ''
      const baseKey = normalizeClause(baseClause)
      return { fullKey, baseClause, baseKey, tailClause, hasOrder: orderIdx >= 0 }
    }
    const normalizeAliasName = (raw: string): string => {
      const trimmed = String(raw ?? '').trim()
      if (!trimmed) return ''
      if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
        return trimmed.slice(1, -1).replace(/""/g, '"').trim().toLowerCase()
      }
      return trimmed.toLowerCase()
    }
    const readSqlIdentifierAt = (
      s: string,
      start: number,
    ): { text: string; end: number } | null => {
      if (start < 0 || start >= s.length) return null
      if (s[start] === '"') {
        let i = start + 1
        let out = ''
        for (; i < s.length; i++) {
          const c = s[i]
          if (c === '"') {
            if (s[i + 1] === '"') {
              out += '"'
              i++
              continue
            }
            return { text: out, end: i + 1 }
          }
          out += c
        }
        return null
      }
      const m = s.slice(start).match(/^([A-Za-z_][A-Za-z0-9_]*)/)
      if (!m) return null
      return { text: m[1], end: start + m[1].length }
    }
    const splitTopLevelList = (s: string): string[] => {
      const out: string[] = []
      let buf = ''
      let depth = 0
      let inStr = false
      let inIdent = false
      for (let i = 0; i < s.length; i++) {
        const ch = s[i]
        if (inStr) {
          if (ch === "'" && s[i + 1] === "'") {
            buf += "''"
            i++
            continue
          }
          if (ch === "'") inStr = false
          buf += ch
          continue
        }
        if (inIdent) {
          if (ch === '"' && s[i + 1] === '"') {
            buf += '""'
            i++
            continue
          }
          if (ch === '"') inIdent = false
          buf += ch
          continue
        }
        if (ch === "'") {
          inStr = true
          buf += ch
          continue
        }
        if (ch === '"') {
          inIdent = true
          buf += ch
          continue
        }
        if (ch === '(') {
          depth += 1
          buf += ch
          continue
        }
        if (ch === ')') {
          depth = Math.max(0, depth - 1)
          buf += ch
          continue
        }
        if (ch === ',' && depth === 0) {
          const item = buf.trim()
          if (item) out.push(item)
          buf = ''
          continue
        }
        buf += ch
      }
      const tail = buf.trim()
      if (tail) out.push(tail)
      return out
    }
    const parseProjectedAlias = (itemRaw: string): string | null => {
      const item = String(itemRaw ?? '').trim()
      if (!item || item === '*' || /(?:^|[^A-Za-z0-9_])\*(?:$|[^A-Za-z0-9_])/i.test(item)) {
        return null
      }
      const explicit = item.match(/\s+AS\s+("(?:(?:""|[^"])*)"|[A-Za-z_][A-Za-z0-9_]*)\s*$/i)
      if (explicit) return String(explicit[1] ?? '').trim()
      const quoted = item.match(/^(?:(?:"[^"]+"|[A-Za-z_][A-Za-z0-9_]*)\s*\.\s*)?"((?:""|[^"])*)"$/)
      if (quoted) return `"${String(quoted[1] ?? '').trim()}"`
      const bare = item.match(/^(?:(?:"[^"]+"|[A-Za-z_][A-Za-z0-9_]*)\s*\.\s*)?([A-Za-z_][A-Za-z0-9_]*)$/)
      if (bare) return String(bare[1] ?? '').trim()
      return null
    }
    const collectQualifiedAliasRefs = (expr: string, aliasKey: string): Set<string> => {
      const out = new Set<string>()
      if (!expr || !aliasKey) return out
      let inStr = false
      for (let i = 0; i < expr.length; i++) {
        const ch = expr[i]
        if (ch === "'") {
          if (inStr && expr[i + 1] === "'") {
            i++
            continue
          }
          inStr = !inStr
          continue
        }
        if (inStr) continue
        let matched: string | null = null
        if (expr.startsWith(`"${aliasKey}"`, i)) {
          const before = expr[i - 1]
          const after = expr[i + aliasKey.length + 2]
          if ((!before || !isWordChar(before)) && (!after || !isWordChar(after))) {
            matched = `"${aliasKey}"`
          }
        }
        if (!matched && expr.startsWith(aliasKey, i)) {
          const before = expr[i - 1]
          const after = expr[i + aliasKey.length]
          if ((!before || !isWordChar(before)) && (!after || !isWordChar(after))) {
            matched = aliasKey
          }
        }
        if (!matched) continue
        let j = i + matched.length
        while (j < expr.length && /\s/.test(expr[j])) j++
        if (expr[j] !== '.') continue
        j++
        while (j < expr.length && /\s/.test(expr[j])) j++
        const ident = readSqlIdentifierAt(expr, j)
        if (!ident) continue
        const key = normalizeAliasName(ident.text)
        if (key) out.add(key)
        i = ident.end - 1
      }
      return out
    }
    const scopeTokens = (() => {
      const selects: Array<{ pos: number; depth: number }> = []
      const froms: Array<{ pos: number; depth: number }> = []
      let depth = 0
      let inStr = false
      let inIdent = false
      let inLine = false
      let inBlock = false
      for (let i = 0; i < sql.length; i++) {
        const ch = sql[i]
        const next = sql[i + 1]
        if (inLine) {
          if (ch === '\n') inLine = false
          continue
        }
        if (inBlock) {
          if (ch === '*' && next === '/') {
            inBlock = false
            i++
          }
          continue
        }
        if (!inStr && !inIdent && ch === '-' && next === '-') {
          inLine = true
          i++
          continue
        }
        if (!inStr && !inIdent && ch === '/' && next === '*') {
          inBlock = true
          i++
          continue
        }
        if (ch === "'") {
          if (inStr && next === "'") {
            i++
            continue
          }
          inStr = !inStr
          continue
        }
        if (!inStr && ch === '"') {
          if (inIdent && next === '"') {
            i++
            continue
          }
          inIdent = !inIdent
          continue
        }
        if (inStr || inIdent) continue
        if (ch === '(') {
          depth += 1
          continue
        }
        if (ch === ')') {
          depth = Math.max(0, depth - 1)
          continue
        }
        if (sql.slice(i, i + 6).toUpperCase() === 'SELECT') {
          const prev = sql[i - 1]
          const after = sql[i + 6]
          if ((!prev || !isWordChar(prev)) && (!after || !isWordChar(after))) {
            selects.push({ pos: i, depth })
            i += 5
            continue
          }
        }
        if (sql.slice(i, i + 4).toUpperCase() === 'FROM') {
          const prev = sql[i - 1]
          const after = sql[i + 4]
          if ((!prev || !isWordChar(prev)) && (!after || !isWordChar(after))) {
            froms.push({ pos: i, depth })
            i += 3
            continue
          }
        }
      }
      return { selects, froms }
    })()
    const selectScopes = scopeTokens.selects
      .map((token) => {
        const from = scopeTokens.froms.find((candidate) => candidate.depth === token.depth && candidate.pos > token.pos)
        if (!from) return null
        return {
          selectPos: token.pos,
          fromPos: from.pos,
        }
      })
      .filter((value): value is { selectPos: number; fromPos: number } => Boolean(value))
    const hoistTargetSelectPos = findClauseKeyword(sql, 'SELECT')
    const hoistTargetScope = selectScopes.find((scope) => scope.selectPos === hoistTargetSelectPos) ?? null
    const selectScopeProjectionCache = new Map<number, { aliasKey: string; projectedAliases: Set<string> | null }>()
    const getSelectScopeProjection = (
      scope: { selectPos: number; fromPos: number },
    ): { aliasKey: string; projectedAliases: Set<string> | null } => {
      const cached = selectScopeProjectionCache.get(scope.selectPos)
      if (cached) return cached
      let i = scope.fromPos + 4
      while (i < sql.length && /\s/.test(sql[i])) i++
      if (i >= sql.length || sql[i] !== '(') {
        const empty = { aliasKey: '', projectedAliases: null as Set<string> | null }
        selectScopeProjectionCache.set(scope.selectPos, empty)
        return empty
      }
      const end = findMatchingParen(sql, i)
      if (end < 0) {
        const empty = { aliasKey: '', projectedAliases: null as Set<string> | null }
        selectScopeProjectionCache.set(scope.selectPos, empty)
        return empty
      }
      let j = end + 1
      while (j < sql.length && /\s/.test(sql[j])) j++
      if (
        sql.slice(j, j + 2).toUpperCase() === 'AS'
        && !isWordChar(sql[j - 1] || '')
        && !isWordChar(sql[j + 2] || '')
      ) {
        j += 2
        while (j < sql.length && /\s/.test(sql[j])) j++
      }
      const aliasIdent = readSqlIdentifierAt(sql, j)
      const aliasKey = normalizeAliasName(aliasIdent?.text ?? '')
      const subquerySql = sql.slice(i + 1, end).trim()
      const selectIdx = findClauseKeyword(subquerySql, 'SELECT')
      const subFromIdx = findClauseKeyword(subquerySql, 'FROM')
      if (selectIdx < 0 || subFromIdx <= selectIdx) {
        const out = { aliasKey, projectedAliases: null as Set<string> | null }
        selectScopeProjectionCache.set(scope.selectPos, out)
        return out
      }
      const selectBodyRaw = subquerySql.slice(selectIdx + 6, subFromIdx).trim()
      if (!selectBodyRaw) {
        const out = { aliasKey, projectedAliases: null as Set<string> | null }
        selectScopeProjectionCache.set(scope.selectPos, out)
        return out
      }
      const prefixMatch = selectBodyRaw.match(/^(DISTINCT|ALL)\b/i)
      const selectListRaw = prefixMatch
        ? selectBodyRaw.slice(prefixMatch[0].length).trimStart()
        : selectBodyRaw
      if (!selectListRaw) {
        const out = { aliasKey, projectedAliases: null as Set<string> | null }
        selectScopeProjectionCache.set(scope.selectPos, out)
        return out
      }
      const projectedAliases = new Set<string>()
      for (const item of splitTopLevelList(selectListRaw)) {
        const alias = parseProjectedAlias(item)
        if (!alias) {
          const out = { aliasKey, projectedAliases: null as Set<string> | null }
          selectScopeProjectionCache.set(scope.selectPos, out)
          return out
        }
        const key = normalizeAliasName(alias)
        if (key) projectedAliases.add(key)
      }
      const out = { aliasKey, projectedAliases: projectedAliases.size ? projectedAliases : null }
      selectScopeProjectionCache.set(scope.selectPos, out)
      return out
    }
    const getContainingSelectScope = (
      pos: number,
    ): { selectPos: number; fromPos: number } | null => {
      for (let idx = selectScopes.length - 1; idx >= 0; idx--) {
        const scope = selectScopes[idx]
        if (scope.selectPos >= pos) continue
        if (pos >= scope.fromPos) continue
        return scope
      }
      return null
    }

    const occurrences: Array<{
      start: number
      end: number
      clause: string
      fullKey: string
      baseKey: string
      baseClause: string
      tailClause: string
      hasOrder: boolean
      safeToHoist: boolean
    }> = []
    const fullCounts = new Map<string, number>()
    const baseCounts = new Map<string, number>()
    let inStr = false
    let inIdent = false
    let inLineComment = false
    let inBlockComment = false
    for (let i = 0; i < sql.length; i++) {
      const ch = sql[i]
      const next = sql[i + 1]
      if (inLineComment) {
        if (ch === '\n') inLineComment = false
        continue
      }
      if (inBlockComment) {
        if (ch === '*' && next === '/') {
          inBlockComment = false
          i++
        }
        continue
      }
      if (!inStr && !inIdent && ch === '-' && next === '-') {
        inLineComment = true
        i++
        continue
      }
      if (!inStr && !inIdent && ch === '/' && next === '*') {
        inBlockComment = true
        i++
        continue
      }
      if (ch === "'") {
        if (inStr && next === "'") {
          i++
          continue
        }
        inStr = !inStr
        continue
      }
      if (!inStr && ch === '"') {
        if (inIdent && next === '"') {
          i++
          continue
        }
        inIdent = !inIdent
        continue
      }
      if (inStr || inIdent) continue
      if (sql.slice(i, i + 4).toUpperCase() !== 'OVER') continue
      const prev = sql[i - 1]
      const nextAfter = sql[i + 4]
      if (prev && isWordChar(prev)) continue
      if (nextAfter && isWordChar(nextAfter)) continue
      let j = i + 4
      while (j < sql.length && /\s/.test(sql[j])) j++
      if (sql[j] !== '(') continue
      const end = findMatchingParen(sql, j)
      if (end < 0) continue
      const clause = sql.slice(j + 1, end).trim()
      if (!clause || !/\border\s+by\b/i.test(clause)) {
        i = end
        continue
      }
      const split = splitWindowClause(clause)
      if (!split?.fullKey) {
        i = end
        continue
      }
      const safeToHoist = (() => {
        const containingScope = getContainingSelectScope(i)
        // This optimizer only knows how to insert WINDOW definitions at the top-level
        // query scope. Never hoist nested SELECT windows into that outer scope.
        if (!containingScope || !hoistTargetScope || containingScope.selectPos !== hoistTargetScope.selectPos) {
          return false
        }
        const scopeProjection = getSelectScopeProjection(containingScope)
        const sourceAliasKey = String(scopeProjection.aliasKey ?? '').trim()
        const projectedAliases = scopeProjection.projectedAliases
        if (!sourceAliasKey) {
          return true
        }
        const refs = collectQualifiedAliasRefs(clause, sourceAliasKey)
        // When projection recovery fails, be conservative: any qualified source-alias
        // reference could become invalid if hoisted into a WINDOW clause.
        if (!projectedAliases || !projectedAliases.size) {
          return refs.size === 0
        }
        for (const refKey of refs) {
          if (!projectedAliases.has(refKey)) return false
        }
        return true
      })()
      occurrences.push({
        start: i,
        end,
        clause,
        fullKey: split.fullKey,
        baseKey: split.baseKey,
        baseClause: split.baseClause,
        tailClause: split.tailClause,
        hasOrder: split.hasOrder,
        safeToHoist,
      })
      if (safeToHoist) {
        fullCounts.set(split.fullKey, (fullCounts.get(split.fullKey) || 0) + 1)
        if (split.baseKey) baseCounts.set(split.baseKey, (baseCounts.get(split.baseKey) || 0) + 1)
      }
      i = end
    }
    if (!occurrences.length) return sql

    const minLen = 80
    const baseWindows = new Map<string, { name: string; clause: string }>()
    const fullWindows = new Map<string, { name: string; clause: string }>()
    let winIdx = 0
    for (const occ of occurrences) {
      if (!occ.safeToHoist) continue
      if (!occ.hasOrder) continue
      if ((baseCounts.get(occ.baseKey) || 0) < 2) continue
      if (occ.baseClause.length < minLen) continue
      if (!baseWindows.has(occ.baseKey)) {
        baseWindows.set(occ.baseKey, { name: `__fg_win_${winIdx++}`, clause: occ.baseClause })
      }
    }
    for (const occ of occurrences) {
      if (!occ.safeToHoist) continue
      if (baseWindows.has(occ.baseKey)) continue
      if ((fullCounts.get(occ.fullKey) || 0) < 2) continue
      if (occ.clause.length < minLen) continue
      if (!fullWindows.has(occ.fullKey)) {
        fullWindows.set(occ.fullKey, { name: `__fg_win_${winIdx++}`, clause: occ.clause })
      }
    }
    if (!baseWindows.size && !fullWindows.size) return sql

    const sorted = [...occurrences].sort((a, b) => a.start - b.start)
    let cursor = 0
    const out: string[] = []
    for (const occ of sorted) {
      if (!occ.safeToHoist) continue
      const baseHit = occ.baseKey ? baseWindows.get(occ.baseKey) : undefined
      const fullHit = fullWindows.get(occ.fullKey)
      if (!baseHit && !fullHit) continue
      if (occ.start < cursor) continue
      out.push(sql.slice(cursor, occ.start))
      if (baseHit) {
        const tail = occ.tailClause ? ` ${occ.tailClause}` : ''
        if (tail) {
          out.push(`OVER (${baseHit.name}${tail})`)
        } else {
          out.push(`OVER ${baseHit.name}`)
        }
        replaceCount++
      } else if (fullHit) {
        out.push(`OVER ${fullHit.name}`)
        replaceCount++
      }
      cursor = occ.end + 1
    }
    out.push(sql.slice(cursor))
    let rewritten = out.join('')

    const defs = [
      ...Array.from(baseWindows.values()).map((w) => `${w.name} AS (${w.clause})`),
      ...Array.from(fullWindows.values()).map((w) => `${w.name} AS (${w.clause})`),
    ]
    if (!defs.length) return rewritten

    const findTopLevelKeyword = (s: string, keyword: string): number => {
      const kw = keyword.toUpperCase()
      let depth = 0
      let inStr2 = false
      let inIdent2 = false
      let inLine2 = false
      let inBlock2 = false
      for (let i = 0; i < s.length; i++) {
        const c = s[i]
        const n = s[i + 1]
        if (inLine2) {
          if (c === '\n') inLine2 = false
          continue
        }
        if (inBlock2) {
          if (c === '*' && n === '/') {
            inBlock2 = false
            i++
          }
          continue
        }
        if (!inStr2 && !inIdent2 && c === '-' && n === '-') {
          inLine2 = true
          i++
          continue
        }
        if (!inStr2 && !inIdent2 && c === '/' && n === '*') {
          inBlock2 = true
          i++
          continue
        }
        if (c === "'") {
          if (inStr2 && n === "'") {
            i++
            continue
          }
          inStr2 = !inStr2
          continue
        }
        if (!inStr2 && c === '"') {
          if (inIdent2 && n === '"') {
            i++
            continue
          }
          inIdent2 = !inIdent2
          continue
        }
        if (inStr2 || inIdent2) continue
        if (c === '(') depth++
        else if (c === ')') depth = Math.max(0, depth - 1)
        if (depth !== 0) continue
        if (s.slice(i, i + kw.length).toUpperCase() === kw) {
          const prev2 = s[i - 1]
          const next2 = s[i + kw.length]
          if (prev2 && isWordChar(prev2)) continue
          if (next2 && isWordChar(next2)) continue
          return i
        }
      }
      return -1
    }

    if (findTopLevelKeyword(rewritten, 'WINDOW') >= 0) return rewritten

    const trimmed = rewritten.replace(/;+\s*$/, '')
    const insertAt = (() => {
      const orderIdx = findTopLevelKeyword(trimmed, 'ORDER BY')
      const limitIdx = findTopLevelKeyword(trimmed, 'LIMIT')
      const qualifyIdx = findTopLevelKeyword(trimmed, 'QUALIFY')
      const candidates = [orderIdx, limitIdx, qualifyIdx].filter((v) => v >= 0)
      return candidates.length ? Math.min(...candidates) : -1
    })()
    const windowClause = `WINDOW ${defs.join(', ')}`
    if (insertAt >= 0) {
      rewritten = `${trimmed.slice(0, insertAt)} ${windowClause} ${trimmed.slice(insertAt)}`
    } else {
      rewritten = `${trimmed} ${windowClause}`
    }
    try {
      const root: any =
        (typeof window !== 'undefined' && window) ||
        (typeof self !== 'undefined' && self) ||
        null
      if (root) {
        const prev = (root.__FG_WINDOW_DEDUPE_STATS__ && typeof root.__FG_WINDOW_DEDUPE_STATS__ === 'object')
          ? root.__FG_WINDOW_DEDUPE_STATS__
          : {}
        const saved = Math.max(0, beforeLen - rewritten.length)
        root.__FG_WINDOW_DEDUPE_STATS__ = {
          ...prev,
          ts: Date.now(),
          calls: Number(prev.calls ?? 0) + 1,
          replaced: replaceCount,
          window_defs: defs.length,
          last_saved_chars: saved,
          max_saved_chars: Math.max(Number(prev.max_saved_chars ?? 0), saved),
        }
      }
    } catch { }
    return rewritten
  }

  // --- Viz path (v0): build a tidy query for VisualSpec charts (scatter/bar/etc) ---
  //
  // This is a minimal implementation to support Milestone 1 (Circle scatter) end-to-end:
  // - Build a "tidy" SELECT with dimension columns + aggregated measure columns
  // - Use GROUP BY on all dimension fields (including marks.detail and marks.color)
  // - Alias aggregate headers in a Tableau-like way (e.g. "SUM(Profit)") so the worker can map pills via deriveVizKeysFromHeader()
  const buildVizQueryV0 = (visualSpec: VisualSpec): string => {
    const baseAlias = 't'
    const baseRef = quoteIdent(baseAlias)
    const fd = (workbookIr as any)?.field_dictionary
    const fdCols: any[] = Array.isArray(fd?.columns) ? fd.columns : []
    const fdCis: any[] = Array.isArray(fd?.column_instances) ? fd.column_instances : []

    const layerSpecsRaw: any[] = Array.isArray((visualSpec as any)?.layers)
      ? ((visualSpec as any).layers as any[])
      : []
    const baseLayer = {
      shelves: (visualSpec as any)?.shelves,
      marks: (visualSpec as any)?.marks,
      view: (visualSpec as any)?.view,
    }
    const layerSpecs: any[] = layerSpecsRaw.length
      ? [baseLayer, ...layerSpecsRaw]
      : [baseLayer]

    const withShelf = (pill: any, shelf: string): any => {
      if (!pill || typeof pill !== 'object') return pill
      const existingShelf = String((pill as any)?.shelf ?? '').trim().toLowerCase()
      if (existingShelf) return pill
      return { ...(pill as any), shelf }
    }
    const pillsForLayer = (layer: any): any[] => {
      const cols = Array.isArray(layer?.shelves?.columns) ? layer.shelves.columns : []
      const rows = Array.isArray(layer?.shelves?.rows) ? layer.shelves.rows : []
      const mk = (layer?.marks && typeof layer.marks === 'object') ? layer.marks : {}
      const encL = (k: string): any[] => (Array.isArray(mk?.[k]) ? mk[k] as any[] : [])
      return [
        ...cols.map((p: any) => withShelf(p, 'columns')),
        ...rows.map((p: any) => withShelf(p, 'rows')),
        ...encL('color').map((p: any) => withShelf(p, 'color')),
        ...encL('size').map((p: any) => withShelf(p, 'size')),
        ...encL('text').map((p: any) => withShelf(p, 'text')),
        ...encL('detail').map((p: any) => withShelf(p, 'detail')),
        ...encL('tooltip').map((p: any) => withShelf(p, 'tooltip')),
        ...encL('shape').map((p) => withShelf(p, 'shape')),
        ...encL('path').map((p) => withShelf(p, 'path')),
        ...encL('angle').map((p) => withShelf(p, 'angle')),
      ].filter(Boolean)
    }

    const pills: any[] = layerSpecs.flatMap(pillsForLayer).filter(Boolean)
    type PathBinDensificationSpec = {
      field: string
      alias: string
      baseColumn: string
      size: number
      peg: number
      decimals: number | null
    }
    const pathBinDensificationSpecs: PathBinDensificationSpec[] = (() => {
      const out: PathBinDensificationSpec[] = []
      const seen = new Set<string>()
      for (const pill of pills) {
        const shelf = String((pill as any)?.shelf ?? '').trim().toLowerCase()
        if (shelf !== 'path') continue
        const field = String((pill as any)?.field ?? (pill as any)?.field_name ?? '').trim()
        if (!field) continue
        const spec = lookupBinSpec(field)
        const size = Number(spec?.size ?? NaN)
        if (!spec || !Number.isFinite(size) || size <= 0) continue
        const baseField = String(spec.baseField ?? '').trim()
        if (!baseField) continue
        const baseFieldKey = normalizeTableauFieldKey(stripBracketsAroundFieldName(baseField) || baseField)
        const baseColumnFromExtract = (Array.isArray(extractColumns) ? extractColumns : [])
          .map((col) => String(col ?? '').trim())
          .find((col) => {
            const colKey = normalizeTableauFieldKey(col) || col.toLowerCase()
            return Boolean(baseFieldKey && colKey === baseFieldKey)
          }) || ''
        const baseColumnRaw = baseColumnFromExtract || resolveCol(baseField) || resolveCol(stripBracketsAroundFieldName(baseField)) || ''
        const baseColumn = String(baseColumnRaw ?? '').trim()
        if (!baseColumn || isUnresolvedField(baseColumn)) continue
        const key = normalizeTableauFieldKey(field) || field.toLowerCase()
        if (!key || seen.has(key)) continue
        seen.add(key)
        out.push({
          field,
          alias: dimensionProjectionAlias(field, null),
          baseColumn,
          size,
          peg: Number.isFinite(Number(spec.peg)) ? Number(spec.peg) : 0,
          decimals: effectiveBinDecimals(size, spec.decimals),
        })
      }
      return out
    })()
    const pathBinDensifiedFieldExpr = (fieldRaw: any): string | null => {
      const key = normalizeTableauFieldKey(String(fieldRaw ?? '').trim())
      if (!key) return null
      const hit = pathBinDensificationSpecs.find((spec) => {
        const specKey = normalizeTableauFieldKey(spec.field) || spec.field.toLowerCase()
        return specKey === key
      })
      return hit ? `${baseRef}.${quoteIdent(hit.alias)}` : null
    }
    const vizSourceSql = (() => {
      if (!pathBinDensificationSpecs.length) return parquetRef
      const sourceColumns = Array.isArray(extractColumns) ? extractColumns.map((c) => String(c ?? '').trim()).filter(Boolean) : []
      if (!sourceColumns.length) return parquetRef
      const sourceAlias = quoteIdent('__fg_path_src')
      const domainAlias = quoteIdent('__fg_path_domain')
      let fromSql = `${parquetRef} AS ${sourceAlias}`
      const selectParts: string[] = []
      const denseAliases = new Set(pathBinDensificationSpecs.map((spec) => (normalizeTableauFieldKey(spec.alias) || spec.alias.toLowerCase())))
      for (const col of sourceColumns) {
        const key = normalizeTableauFieldKey(col) || col.toLowerCase()
        if (denseAliases.has(key)) continue
        selectParts.push(`${sourceAlias}.${quoteIdent(col)} AS ${quoteIdent(col)}`)
      }
      for (const [idx, spec] of pathBinDensificationSpecs.entries()) {
        const denseAlias = quoteIdent(`__fg_path_bin_${idx}`)
        const boundsAlias = quoteIdent('__fg_bounds')
        const baseExpr = `TRY_CAST(${domainAlias}.${quoteIdent(spec.baseColumn)} AS DOUBLE)`
        const rawBinExpr = `FLOOR(((${baseExpr}) - ${spec.peg}) / ${spec.size}) * ${spec.size} + ${spec.peg}`
        const binExpr = spec.decimals !== null ? `ROUND(${rawBinExpr}, ${spec.decimals})` : rawBinExpr
        const lo = `${boundsAlias}.${quoteIdent('__fg_lo')}`
        const hi = `${boundsAlias}.${quoteIdent('__fg_hi')}`
        const idxRef = `${denseAlias}.${quoteIdent('__fg_idx')}`
        const generatedRaw = `(${lo} + (${idxRef} * ${spec.size}))`
        const generated = spec.decimals !== null ? `ROUND(${generatedRaw}, ${spec.decimals})` : generatedRaw
        fromSql += ` CROSS JOIN (
          SELECT ${generated} AS ${quoteIdent(spec.alias)}
          FROM (
            SELECT MIN(${binExpr}) AS ${quoteIdent('__fg_lo')}, MAX(${binExpr}) AS ${quoteIdent('__fg_hi')}
            FROM ${parquetRef} AS ${domainAlias}
            WHERE ${baseExpr} IS NOT NULL
          ) AS ${boundsAlias}
          CROSS JOIN UNNEST(generate_series(0, CAST(LEAST(10000, GREATEST(0, COALESCE(FLOOR(((${hi} - ${lo}) / ${spec.size}) + 0.5), 0))) AS BIGINT))) AS ${denseAlias}(${quoteIdent('__fg_idx')})
        ) AS ${denseAlias}`
        selectParts.push(`${denseAlias}.${quoteIdent(spec.alias)} AS ${quoteIdent(spec.alias)}`)
      }
      return `(SELECT ${selectParts.join(', ')} FROM ${fromSql})`
    })()

    const dimPills: Array<{ field: string; time_grain?: string | null; is_discrete?: boolean | null }> = []
    const measurePills: any[] = []
    const seenDim = new Set<string>()
    const seenMeas = new Set<string>()

    const normKey = (s: any) => normalizeTableauFieldKey(String(s ?? ''))
    const isMeasureNames = (s: any) => normKey(s) === 'measure names'
    const isMultipleValues = (s: any) => {
      const k = normKey(s)
      return k === 'multiple values' || k === 'measure values'
    }
    const looksAutoCalculation = (s: string): boolean => {
      const raw = String(s ?? '').trim()
      if (!raw) return false
      if (/^calculation_\d{6,}$/i.test(raw)) return true
      if (/^calculation_[0-9a-f-]{8,}$/i.test(raw)) return true
      return false
    }
    const normalizeAliasKey = (s: string): string =>
      normalizeTableauFieldKey(String(s ?? '').replace(/^[A-Z]+\s*\((.*)\)$/i, '$1'))
    const measureAliasForPill = (field: string, fn: string, rawAlias: any): string => {
      if (looksAutoCalculation(field)) return field
      const canonical = `${fn}(${field})`
      const aliasBase = String(rawAlias ?? '').trim()
      if (!aliasBase) return canonical
      const aliasKey = normalizeAliasKey(aliasBase)
      const fieldKey = normalizeAliasKey(field)
      const canonicalKey = normalizeTableauFieldKey(canonical)
      const hasUnbalancedParens = (aliasBase.includes(')') && !aliasBase.includes('(')) || (aliasBase.includes('(') && !aliasBase.includes(')'))
      const isTruncatedAggregateSuffix =
        aliasKey.length > 0 &&
        canonicalKey.endsWith(aliasKey) &&
        aliasKey !== canonicalKey &&
        (!fieldKey || !aliasKey.includes(fieldKey))
      if (hasUnbalancedParens || isTruncatedAggregateSuffix) return canonical
      return aliasBase
    }
    const looksWindowExpr = (expr: string): boolean =>
      /\bOVER\s*\(/i.test(expr) || /\bROW_NUMBER\s*\(/i.test(expr) || /\bLAG\s*\(/i.test(expr) || /\bLEAD\s*\(/i.test(expr)
    const looksAggExpr = (expr: string): boolean =>
      /\b(SUM|AVG|MIN|MAX|COUNT)\s*\(/i.test(expr)
    const hasNonWindowAggregateExpr = (expr: string): boolean => {
      const src = String(expr ?? '')
      if (!src) return false
      const aggFns = ['SUM', 'AVG', 'MIN', 'MAX', 'COUNT']
      const isWordChar = (c: string | undefined): boolean => Boolean(c && /[A-Za-z0-9_]/.test(c))
      const skipWs = (idx: number): number => {
        let i = idx
        while (i < src.length && /\s/.test(src[i])) i++
        return i
      }
      const findCallClose = (openParenIdx: number): number => {
        let depth = 0
        let inStr = false
        let inIdent = false
        for (let i = openParenIdx; i < src.length; i++) {
          const ch = src[i]
          if (inStr) {
            if (ch === "'" && src[i + 1] === "'") {
              i++
              continue
            }
            if (ch === "'") inStr = false
            continue
          }
          if (inIdent) {
            if (ch === '"') inIdent = false
            continue
          }
          if (ch === "'") {
            inStr = true
            continue
          }
          if (ch === '"') {
            inIdent = true
            continue
          }
          if (ch === '(') {
            depth++
            continue
          }
          if (ch === ')') {
            depth--
            if (depth === 0) return i
          }
        }
        return -1
      }

      for (let i = 0; i < src.length; i++) {
        for (const agg of aggFns) {
          const slice = src.slice(i, i + agg.length)
          if (slice.length !== agg.length || slice.toUpperCase() !== agg) continue
          const before = src[i - 1]
          const after = src[i + agg.length]
          if (isWordChar(before) || isWordChar(after)) continue

          let j = skipWs(i + agg.length)
          if (src[j] !== '(') continue
          const closeIdx = findCallClose(j)
          if (closeIdx < 0) return true

          const tailIdx = skipWs(closeIdx + 1)
          const overTok = src.slice(tailIdx, tailIdx + 4).toUpperCase()
          const hasWindowOver = overTok === 'OVER' && !isWordChar(src[tailIdx + 4])
          if (!hasWindowOver) return true
        }
      }
      return false
    }
    const inferTimeGrainFromField = (rawField: string): string | null => {
      const cleaned = cleanBracketedFieldRef0(String(rawField ?? '').trim()) || String(rawField ?? '').trim()
      if (!cleaned) return null
      const tok = cleaned.match(/^(t?yr|t?mn|t?wk|t?dy|t?qr)\s*:\s*/i)
      if (tok) {
        const t = String(tok[1] ?? '').trim().toLowerCase()
        if (t.endsWith('yr')) return 'year'
        if (t.endsWith('mn')) return 'month'
        if (t.endsWith('wk')) return 'week'
        if (t.endsWith('dy')) return 'day'
        if (t.endsWith('qr')) return 'quarter'
      }
      const fn = cleaned.match(/^(year|quarter|month|week|day|hour|minute|second)\s*\(/i)
      if (fn) return String(fn[1] ?? '').trim().toLowerCase() || null
      return null
    }
    const inferTimeGrainFromPill = (pill: any): string | null => {
      if (!pill || typeof pill !== 'object') return null
      const candidates = [
        (pill as any)?.field,
        (pill as any)?.field_name,
        (pill as any)?.display_name,
        (pill as any)?.displayName,
        (pill as any)?.field_display_name,
        (pill as any)?.fieldDisplayName,
        (pill as any)?.label,
        (pill as any)?.caption,
        (pill as any)?.name,
      ]
      for (const candidate of candidates) {
        const inferred = inferTimeGrainFromField(String(candidate ?? ''))
        if (inferred) return inferred
      }
      return null
    }
    const fieldMetaLookup = (() => {
      try {
        if (!workbookIr || typeof workbookIr !== 'object') return null
        const fd = (workbookIr as any)?.field_dictionary
        const cols: any[] = Array.isArray(fd?.columns) ? fd.columns : []
        const cis: any[] = Array.isArray(fd?.column_instances) ? fd.column_instances : []
        if (!cols.length && !cis.length) return null
        const normKeyMeta = (s: any) =>
          normalizeTableauFieldKey(cleanBracketedFieldRef0(String(s ?? '')) || String(s ?? '')) || ''
        const byKey = new Map<string, any>()
        const put = (k: string, meta: any) => {
          if (!k || byKey.has(k)) return
          byKey.set(k, meta)
        }
        for (const c of cols) {
          put(normKeyMeta(c?.name), c)
          put(normKeyMeta(c?.caption), c)
        }
        for (const ci of cis) {
          const base = String(ci?.column ?? '').trim()
          const baseKey = normKeyMeta(base)
          const baseCol = baseKey ? (cols.find((c) => normKeyMeta(c?.name) === baseKey) || null) : null
          // Prefer column_instance metadata (derivation/type) while preserving base column fields.
          const meta = baseCol ? { ...baseCol, ...ci } : ci
          put(normKeyMeta(ci?.name), meta)
          put(normKeyMeta(ci?.caption), meta)
        }
        return (rawField: string): any | null => {
          const key = normKeyMeta(rawField)
          if (!key) return null
          return byKey.get(key) || null
        }
      } catch {
        return null
      }
    })()
    const normalizeTimeGrainLabel = (raw: string): string | null => {
      const s = String(raw ?? '').trim().toLowerCase()
      if (!s) return null
      if (['year', 'yy', 'yyyy'].includes(s)) return 'year'
      if (['quarter', 'qq', 'q'].includes(s)) return 'quarter'
      if (['month', 'mm', 'mon'].includes(s)) return 'month'
      if (['week', 'wk'].includes(s)) return 'week'
      if (['day', 'dd'].includes(s)) return 'day'
      if (['date'].includes(s)) return 'day'
      if (['hour', 'hr'].includes(s)) return 'hour'
      if (['minute', 'min'].includes(s)) return 'minute'
      if (['second', 'sec'].includes(s)) return 'second'
      if (s.includes('year')) return 'year'
      if (s.includes('quarter')) return 'quarter'
      if (s.includes('month')) return 'month'
      if (s.includes('week')) return 'week'
      if (s.includes('day') || s.includes('date')) return 'day'
      if (s.includes('hour')) return 'hour'
      if (s.includes('minute')) return 'minute'
      if (s.includes('second')) return 'second'
      return null
    }
    const timeGrainFromMeta = (rawField: string): string | null => {
      if (!fieldMetaLookup) return null
      const meta = fieldMetaLookup(rawField)
      if (!meta || typeof meta !== 'object') return null
      const derivation = String(
        (meta as any)?.time_grain ??
          (meta as any)?.date_grain ??
          (meta as any)?.derivation ??
          (meta as any)?.attrs?.derivation ??
          (meta as any)?.attrs?.['date-derivation'] ??
          (meta as any)?.attrs?.date_derivation ??
          ''
      ).trim()
      if (!derivation) return null
      return normalizeTimeGrainLabel(derivation)
    }
    const isDimensionByMeta = (rawField: string): boolean => {
      if (!fieldMetaLookup) return false
      const meta = fieldMetaLookup(rawField)
      if (!meta) return false
      const role = String(meta?.role ?? '').trim().toLowerCase()
      if (role === 'dimension') return true
      const typ = String(
        meta?.data_type ??
          meta?.type ??
          meta?.datatype ??
          meta?.user_datatype ??
          (meta as any)?.['r#type'] ??
          ''
      )
        .trim()
        .toLowerCase()
      if (!typ) return false
      return typ.includes('date') || typ.includes('time')
    }
    const isDateLikePill = (p: any): boolean => {
      const rawField = String((p as any)?.field ?? (p as any)?.field_name ?? '').trim()
      const tg = String((p as any)?.time_grain ?? (p as any)?.timeGrain ?? '').trim().toLowerCase()
      if (tg) return true
      if (inferTimeGrainFromPill(p)) return true
      if (rawField && timeGrainFromMeta(rawField)) return true
      if (rawField && inferTimeGrainFromField(rawField)) return true
      const dt = String((p as any)?.data_type ?? (p as any)?.dataType ?? '').trim().toLowerCase()
      if (dt && (dt.includes('date') || dt.includes('time'))) return true
      if (rawField && isDimensionByMeta(rawField)) return true
      return false
    }
    const isMeasurePill = (p: any, forceDimension = false): boolean => {
      if (!p || typeof p !== 'object') return false
      if (forceDimension) return false
      const agg = String((p as any)?.aggregation ?? '').trim()
      if (agg) return true
      if (isDateLikePill(p)) return false
      return Boolean((p as any)?.is_measure)
    }
    const aggTokFromFieldName = (raw: string): string | null => {
      const cleaned = cleanBracketedFieldRef0(String(raw ?? '').trim())
      if (!cleaned) return null
      const parts = cleaned.split(':').map((p) => p.trim().toLowerCase()).filter(Boolean)
      for (const t of parts) {
        if (t === 'ctd') return 'countd'
        if (t === 'sum' || t === 'avg' || t === 'min' || t === 'max' || t === 'count' || t === 'countd') return t
      }
      return null
    }
    const normalizeAggTokenSimple = (raw: any, fieldName: string): string | null => {
      const s = String(raw ?? '').trim().toLowerCase()
      if (s) {
        if (s === 'average' || s === 'mean') return 'avg'
        if (s === 'ctd') return 'countd'
        if (s === 'count distinct' || s === 'count_distinct' || s === 'countd' || s === 'distinct_count') return 'countd'
        if (s === 'sum' || s === 'avg' || s === 'min' || s === 'max' || s === 'count' || s === 'countd') return s
      }
      return aggTokFromFieldName(fieldName)
    }
    const applyTimeGrain = (expr: string, grain: string | null, isDiscrete?: boolean | null): string => {
      const g = String(grain ?? '').trim().toLowerCase()
      if (!g) return expr
      const ts = `TRY_CAST(${expr} AS TIMESTAMP)`
      if (isDiscrete) {
        if (g === 'year') return `EXTRACT(year FROM ${ts})`
        if (g === 'quarter' || g === 'month' || g === 'week' || g === 'day' || g === 'hour' || g === 'minute' || g === 'second') {
          return `DATE_TRUNC('${g}', ${ts})`
        }
        return expr
      }
      if (g === 'quarter' || g === 'month' || g === 'week' || g === 'day' || g === 'hour' || g === 'minute' || g === 'second' || g === 'year') {
        return `DATE_TRUNC('${g}', ${ts})`
      }
      return expr
    }
    const usesMeasureNames = pills.some((p) => isMeasureNames((p as any)?.field ?? (p as any)?.field_name ?? ''))
    const usesMultipleValues = pills.some((p) => isMultipleValues((p as any)?.field ?? (p as any)?.field_name ?? ''))

    const buildMeasureNamesValuesQuery = (): string | null => {
      if (!workbookIr || typeof workbookIr !== 'object') return null
      const fdCols: any[] = workbookDerived ? workbookDerived.fdCols : []
      const fdCis: any[] = workbookDerived ? workbookDerived.fdCis : []
      if (!fdCols.length || !fdCis.length) return null
      const metaByCaptionKey = workbookDerived ? workbookDerived.metaByCaptionKey : new Map<string, any>()
      const metaByNameKey = workbookDerived ? workbookDerived.metaByNameKey : new Map<string, any>()
      const metaByNameKeyStrict = workbookDerived ? workbookDerived.metaByNameKeyStrict : new Map<string, any>()
      const metaByName = workbookDerived ? workbookDerived.metaByName : new Map<string, any>()
      const ciByCaptionLower = new Map<string, any>()
      const ciByNameLower = new Map<string, any>()
      for (const ci of fdCis) {
        const cap = String((ci as any)?.caption ?? '').trim().toLowerCase()
        const nm = String((ci as any)?.name ?? '').trim().toLowerCase()
        if (cap && !ciByCaptionLower.has(cap)) ciByCaptionLower.set(cap, ci)
        if (nm && !ciByNameLower.has(nm)) ciByNameLower.set(nm, ci)
      }

      const normToken = (s: any) =>
        normalizeTableauFieldToken(cleanBracketedFieldRef0(String(s ?? '') || '')).trim().toLowerCase()
      const normKey = (s: any) =>
        normalizeTableauFieldKey(cleanBracketedFieldRef0(String(s ?? '') || '')).trim().toLowerCase()
      const normKeyStrict = (s: any) =>
        normalizeTableauFieldKeyStrict(cleanBracketedFieldRef0(String(s ?? '') || '')).trim().toLowerCase()
      const cleanName = (s: any) => cleanBracketedFieldRef0(String(s ?? '') || '') || String(s ?? '')
      const colByCleanName = new Map<string, any>()
      for (const c of fdCols) {
        const k = cleanName((c as any)?.name ?? '')
        if (!k) continue
        if (!colByCleanName.has(k)) colByCleanName.set(k, c)
      }
      const manifestFields: any[] = Array.isArray((options as any)?.fieldManifest?.fields)
        ? ((options as any).fieldManifest.fields as any[])
        : []
      const manifestCaptionByToken = new Map<string, string>()
      const manifestTokenByCaption = new Map<string, string>()
      const manifestTokenKeysByCaption = new Map<string, string[]>()
      const captionKey = (raw: any): string => {
        const key = normalizeTableauFieldKey(String(raw ?? '').trim())
        return key || String(raw ?? '').trim().toLowerCase()
      }
      const manifestTokenKey = (rawId: any): string => {
        const cleaned = cleanBracketedFieldRef0(String(rawId ?? '') || '')
        if (!cleaned) return ''
        return normalizeTableauFieldToken(cleaned).trim().toLowerCase()
      }
      const noteManifest = (rawId: any, captionRaw: any, opts?: { primary?: boolean }) => {
        const cap = String(captionRaw ?? '').trim()
        if (!cap) return
        const rawText = String(rawId ?? '').trim()
        const tailToken = rawText.match(/\[([^\]]+)\]\s*$/)?.[1] ?? ''
        const keys = [
          manifestTokenKey(rawId),
          manifestTokenKey(tailToken),
        ].filter((key, idx, arr): key is string => Boolean(key) && arr.indexOf(key) === idx)
        if (!keys.length) return
        for (const key of keys) {
          if (opts?.primary !== false && !manifestCaptionByToken.has(key)) manifestCaptionByToken.set(key, cap)
        }
        const capKey = captionKey(cap)
        if (!capKey) return
        if (!manifestTokenByCaption.has(capKey)) manifestTokenByCaption.set(capKey, keys[0]!)
        const existing = manifestTokenKeysByCaption.get(capKey) ?? []
        for (const key of keys) {
          if (!existing.includes(key)) existing.push(key)
        }
        manifestTokenKeysByCaption.set(capKey, existing)
      }
      for (const f of manifestFields) {
        noteManifest((f as any)?.field_id, (f as any)?.caption)
        noteManifest((f as any)?.base_field_id, (f as any)?.caption, { primary: false })
        noteManifest((f as any)?.column_name, (f as any)?.caption, { primary: false })
        const aliases = Array.isArray((f as any)?.aliases) ? ((f as any).aliases as any[]) : []
        for (const alias of aliases) {
          noteManifest((f as any)?.field_id, alias, { primary: false })
          noteManifest((f as any)?.base_field_id, alias, { primary: false })
          noteManifest((f as any)?.column_name, alias, { primary: false })
        }
      }
      const bindingDatasources: any[] = Array.isArray((options as any)?.bindingMap?.datasources)
        ? (((options as any).bindingMap.datasources) as any[])
        : []
      for (const ds of bindingDatasources) {
        const bindings = Array.isArray((ds as any)?.bindings) ? ((ds as any).bindings as any[]) : []
        for (const entry of bindings) {
          const labels = [
            (entry as any)?.caption,
            ...(Array.isArray((entry as any)?.aliases) ? ((entry as any).aliases as any[]) : []),
          ]
          const ids = [
            (entry as any)?.field_id,
            (entry as any)?.tableau_token_raw,
            (entry as any)?.tableau_token,
          ]
          for (const label of labels) {
            for (const id of ids) noteManifest(id, label, { primary: false })
          }
        }
      }

      const aggTokFromCiName = (name: string): string | null => {
        const parts = String(name ?? '').split(':').map((p) => p.trim()).filter(Boolean)
        for (const p of parts) {
          const t = p.toLowerCase()
          if (t === 'ctd') return 'countd'
          if (t === 'sum' || t === 'avg' || t === 'min' || t === 'max' || t === 'count' || t === 'countd') return t
        }
        return null
      }
      const normalizeAggToken = (raw: any): string | null => {
        const s = String(raw ?? '').trim().toLowerCase()
        if (!s) return null
        if (s === 'average' || s === 'mean') return 'avg'
        if (s === 'ctd') return 'countd'
        if (s === 'count distinct' || s === 'count_distinct' || s === 'countd' || s === 'distinct_count') return 'countd'
        if (s === 'agg') return 'agg'
        if (s === 'sum' || s === 'avg' || s === 'min' || s === 'max' || s === 'count' || s === 'countd') return s
        return null
      }
      type MeasureFieldRef = { fieldId: string; columnId: string | null }
      const manifestMeasureRefByCaptionKey = new Map<string, MeasureFieldRef>()
      const manifestMeasureRefByTokenKey = new Map<string, MeasureFieldRef>()
      const manifestAggByFieldId = new Map<string, string | null>()
      const rememberManifestMeasureRef = (fieldIdRaw: any, columnRaw: any, captionRaw: any, aggRaw: any) => {
        const fieldId = String(fieldIdRaw ?? '').trim()
        const columnId = String(columnRaw ?? '').trim()
        if (!fieldId || !columnId) return
        const aggTok = normalizeAggToken(aggRaw) || aggTokFromCiName(fieldId)
        const ref: MeasureFieldRef = { fieldId, columnId }
        const capKey = captionKey(captionRaw)
        if (capKey && !manifestMeasureRefByCaptionKey.has(capKey)) {
          manifestMeasureRefByCaptionKey.set(capKey, ref)
        }
        const tokenKey = manifestTokenKey(fieldIdRaw)
        if (tokenKey && !manifestMeasureRefByTokenKey.has(tokenKey)) {
          manifestMeasureRefByTokenKey.set(tokenKey, ref)
        }
        if (aggTok) {
          if (!manifestAggByFieldId.has(fieldId)) manifestAggByFieldId.set(fieldId, aggTok)
          if (!manifestAggByFieldId.has(columnId)) manifestAggByFieldId.set(columnId, aggTok)
        }
      }
      for (const f of manifestFields) {
        const fieldId = (f as any)?.field_id
        const columnId = (f as any)?.column_name ?? (f as any)?.base_field_id
        const caption = (f as any)?.caption
        const derivation = (f as any)?.derivation
        rememberManifestMeasureRef(fieldId, columnId, caption, derivation)
        const aliases = Array.isArray((f as any)?.aliases) ? ((f as any).aliases as any[]) : []
        for (const alias of aliases) rememberManifestMeasureRef(fieldId, columnId, alias, derivation)
      }

      const ciByToken = new Map<string, { fieldId: string; columnId: string; aggTok: string | null; caption?: string }>()
      const aggByField = new Map<string, string | null>()
      for (const ci of fdCis) {
        const nm = String(ci?.name ?? '').trim()
        const col = String(ci?.column ?? '').trim()
        const cap = String(ci?.caption ?? '').trim()
        if (!nm || !col) continue
        const key = normToken(nm)
        const aggTok = aggTokFromCiName(nm)
        if (key) ciByToken.set(key, { fieldId: nm, columnId: col, aggTok, caption: cap || undefined })
        if (aggTok) {
          if (!aggByField.has(nm)) aggByField.set(nm, aggTok)
          if (!aggByField.has(col)) aggByField.set(col, aggTok)
        }
      }
      for (const [fieldId, aggTok] of manifestAggByFieldId) {
        if (aggTok && !aggByField.has(fieldId)) aggByField.set(fieldId, aggTok)
      }

      const findColumnByField = (raw: string): MeasureFieldRef | null => {
        const rawTrim = String(raw ?? '').trim()
        const rawKey = normKey(rawTrim)
        if (rawKey) {
          const manifestByCaption = manifestMeasureRefByCaptionKey.get(rawKey) || null
          if (manifestByCaption) return manifestByCaption
        }
        const keyTok0 = normToken(rawTrim)
        if (keyTok0) {
          const manifestByToken = manifestMeasureRefByTokenKey.get(keyTok0) || null
          if (manifestByToken) return manifestByToken
        }
        const rawLower = rawTrim.toLowerCase()
        if (rawLower) {
          const byExactName = ciByNameLower.get(rawLower) || null
          const byExactCaption = ciByCaptionLower.get(rawLower) || null
          const exact = byExactName || byExactCaption
          if (exact) {
            const baseFieldId = String((exact as any)?.base_field_id ?? (exact as any)?.baseFieldId ?? '').trim()
            const baseClean = baseFieldId ? (cleanBracketedFieldRef0(baseFieldId) || baseFieldId) : ''
            const baseKey = normalizeTableauFieldKey(baseClean)
            const colFallback = String((exact as any)?.column ?? '').trim()
            const col =
              (baseClean && baseKey && (baseKey === 'calculation' || baseKey.startsWith('calculation_')))
                ? baseClean
                : colFallback
            const fieldId = String(exact?.name ?? rawTrim).trim()
            return col ? { fieldId, columnId: col } : null
          }
        }
        if (rawKey) {
          const tokenCandidates = manifestTokenKeysByCaption.get(rawKey) ?? []
          const tokenFromCap = tokenCandidates.find((tokenKey) => ciByToken.has(tokenKey)) || manifestTokenByCaption.get(rawKey)
          if (tokenFromCap && ciByToken.has(tokenFromCap)) {
            const hit = ciByToken.get(tokenFromCap)!
            return { fieldId: hit.fieldId, columnId: hit.columnId }
          }
        }
        const keyTok = normToken(raw)
        if (keyTok && ciByToken.has(keyTok)) {
          const hit = ciByToken.get(keyTok)!
          return { fieldId: hit.fieldId, columnId: hit.columnId }
        }
        const keyStrict = normKeyStrict(raw)
        if (keyStrict) {
          const byNameStrict = metaByNameKeyStrict.get(keyStrict) || null
          if (byNameStrict) {
            const name = String(byNameStrict?.name ?? '').trim()
            return name ? { fieldId: name, columnId: name } : null
          }
        }
        const key = normKey(raw)
        if (key) {
          const byName = metaByNameKey.get(key) || null
          if (byName) {
            const name = String(byName?.name ?? '').trim()
            return name ? { fieldId: name, columnId: name } : null
          }
          const byCap = metaByCaptionKey.get(key) || null
          if (byCap) {
            const name = String(byCap?.name ?? '').trim()
            return name ? { fieldId: name, columnId: name } : null
          }
        }
        const rawClean = cleanName(raw)
        if (rawClean) {
          const byName = colByCleanName.get(rawClean) || null
          if (byName) {
            const name = String(byName?.name ?? '').trim()
            return name ? { fieldId: name, columnId: name } : null
          }
        }
        return null
      }
      const isAvailableCol = (colId: string | null): boolean => {
        if (!colId) return false
        const colMeta = metaByName.get(colId) || null
        const hasCalc = Boolean(String(colMeta?.calculation?.formula ?? '').trim())
        if (hasCalc) return true
        if (!Array.isArray(extractColumns) || extractColumns.length === 0) return true
        const key = normKey(colId)
        return extractColumns.some((c) => {
          const name = String(c ?? '').trim()
          if (!name) return false
          if (key && normKey(name) === key) return true
          return name === colId
        })
      }

      const explodeMeasureFilterValues = (rawVal: any): any[] => {
        if (rawVal === null || rawVal === undefined) return []
        if (Array.isArray(rawVal)) return rawVal.flatMap(explodeMeasureFilterValues)
        if (typeof rawVal === 'string') {
          const s = rawVal.trim()
          if (!s) return []
          if (s.includes('|')) return s.split('|').map((v) => v.trim()).filter(Boolean)
          if (s.includes(',')) return s.split(',').map((v) => v.trim()).filter(Boolean)
          return [s]
        }
        if (typeof rawVal === 'object') {
          const obj = rawVal as any
          if (Array.isArray(obj.values)) return explodeMeasureFilterValues(obj.values)
          if (obj.value !== undefined) return explodeMeasureFilterValues(obj.value)
          if (obj.label !== undefined) return explodeMeasureFilterValues(obj.label)
          if (obj.caption !== undefined) return explodeMeasureFilterValues(obj.caption)
          if (obj.name !== undefined) return explodeMeasureFilterValues(obj.name)
        }
        return [rawVal]
      }
      const normalizeMeasureFilterKeys = (raw: any): string[] => {
        const cleanedRaw = cleanBracketedFieldRef0(String(raw ?? '').trim()) || String(raw ?? '').trim()
        if (!cleanedRaw) return []
        const cleaned = cleanedRaw.replace(/^"(.*)"$/, '$1').replace(/^'(.*)'$/, '$1').trim()
        if (!cleaned) return []
        const out = new Set<string>()
        const addKey = (k: string | null | undefined) => {
          const v = String(k ?? '').trim().toLowerCase()
          if (v) out.add(v)
        }
        if (looksTableauToken(cleaned)) {
          const tok = normalizeTableauFieldToken(cleaned).trim().toLowerCase()
          addKey(tok)
          const cap = tok ? manifestCaptionByToken.get(tok) : undefined
          if (cap) addKey(captionKey(cap))
        } else {
          const capKey = captionKey(cleaned)
          addKey(capKey)
          const tokenFromCap = capKey ? manifestTokenByCaption.get(capKey) : undefined
          if (tokenFromCap) addKey(tokenFromCap)
        }
        return Array.from(out)
      }

      const layerFilters: any[] = layerSpecs.flatMap((l) => (Array.isArray((l as any)?.filters) ? ((l as any).filters as any[]) : []))
      const measureFilterSources: any[] = [
        ...(Array.isArray(filters) ? filters : []),
        ...layerFilters,
      ]
      const hasMeasureNamesFilter = measureFilterSources.some((f) => {
        const rawField = String((f as any)?.field_name ?? '').trim()
        return rawField && isMeasureNames(rawField)
      })
      const measureFilterValueKeys = new Set<string>()
      const addMeasureFilterKeys = (raw: any) => {
        for (const k of normalizeMeasureFilterKeys(raw)) {
          if (k) measureFilterValueKeys.add(k)
        }
      }
      const measureFilterCols: MeasureFieldRef[] = []
      for (const f of measureFilterSources) {
        const rawField = String((f as any)?.field_name ?? '').trim()
        if (!rawField) continue
        if (!isMeasureNames(rawField)) continue
        const op = String((f as any)?.operator ?? '').trim().toUpperCase()
        if (op && op !== 'IN' && op !== 'NOT IN' && op !== '=' && op !== '==' && op !== 'EQUALS') continue
        const val = (f as any)?.value
        const vals = explodeMeasureFilterValues(val)
        for (const v of vals) {
          addMeasureFilterKeys(v)
          const ref = findColumnByField(String(v ?? ''))
          if (ref && isAvailableCol(ref.columnId)) measureFilterCols.push(ref)
        }
      }

      const rawOverride = Array.isArray(options.measureValuesOverride) ? options.measureValuesOverride : []
      if (measureFilterCols.length === 0 && rawOverride.length === 0) {
        for (const p of pills) {
          const field = String((p as any)?.field ?? (p as any)?.field_name ?? '').trim()
          if (!field || !isMeasureNames(field)) continue
          const sort = (p as any)?.sort ?? null
          const customOrder = Array.isArray(sort?.custom_order ?? sort?.customOrder)
            ? (sort?.custom_order ?? sort?.customOrder) as any[]
            : []
          for (const v of customOrder) {
            const ref = findColumnByField(String(v ?? ''))
            if (ref && isAvailableCol(ref.columnId)) measureFilterCols.push(ref)
          }
        }
      }

      const overrideMeasureCols: MeasureFieldRef[] = []
      for (const ov of rawOverride) {
        const rawName = typeof ov === 'string'
          ? ov
          : String((ov as any)?.field_name ?? (ov as any)?.field ?? '').trim()
        if (!rawName) continue
        addMeasureFilterKeys(rawName)
        const ref = findColumnByField(rawName)
        if (ref && isAvailableCol(ref.columnId)) overrideMeasureCols.push(ref)
      }

      const extraMeasureCols: MeasureFieldRef[] = []
      const extraAggByFieldId = new Map<string, string>()
      for (const layer of layerSpecs) {
        const cols = Array.isArray(layer?.shelves?.columns) ? layer.shelves.columns : []
        const rows = Array.isArray(layer?.shelves?.rows) ? layer.shelves.rows : []
        const pills = [...cols, ...rows]
        for (const p of pills) {
          const field = String(p?.field ?? p?.field_name ?? '').trim()
          if (!field) continue
          if (isMeasureNames(field) || isMultipleValues(field)) continue
          const isMeasure = isMeasurePill(p)
          if (!isMeasure) continue
          const aggTok = normalizeAggToken((p as any)?.aggregation ?? (p as any)?.agg ?? null)
          const ref = findColumnByField(field)
          if (ref && isAvailableCol(ref.columnId)) {
            extraMeasureCols.push(ref)
            if (aggTok) {
              extraAggByFieldId.set(ref.fieldId, aggTok)
              if (ref.columnId) extraAggByFieldId.set(ref.columnId, aggTok)
            }
          }
        }
      }

      const measureColKey = (fieldId: string): string => {
        const raw = String(fieldId ?? '').trim()
        if (!raw) return ''
        if (/:\d+$/i.test(raw) || /:qk(\b|:)/i.test(raw)) return raw.toLowerCase()
        return normKey(raw) || raw.toLowerCase()
      }
      const measureCols: string[] = []
      const baseColByFieldId = new Map<string, string>()
      const pushCol = (fieldId: string, baseCol?: string | null) => {
        const key = measureColKey(fieldId)
        if (!key) return
        if (!measureCols.some((x) => measureColKey(x) === key)) {
          measureCols.push(fieldId)
          if (baseCol) baseColByFieldId.set(fieldId, baseCol)
        }
      }
      const measureFilterKeys = (() => {
        const out = new Set<string>()
        for (const ref of measureFilterCols) {
          for (const k of normalizeMeasureFilterKeys(ref.fieldId)) out.add(k)
          for (const k of normalizeMeasureFilterKeys(ref.columnId || '')) out.add(k)
        }
        for (const ref of overrideMeasureCols) {
          for (const k of normalizeMeasureFilterKeys(ref.fieldId)) out.add(k)
          for (const k of normalizeMeasureFilterKeys(ref.columnId || '')) out.add(k)
        }
        for (const k of measureFilterValueKeys) out.add(k)
        return out
      })()
      for (const ref of measureFilterCols) pushCol(ref.fieldId, ref.columnId)
      for (const ref of overrideMeasureCols) pushCol(ref.fieldId, ref.columnId)
      for (const ref of extraMeasureCols) {
        const hasAggOverride = extraAggByFieldId.has(ref.fieldId) || (ref.columnId ? extraAggByFieldId.has(ref.columnId) : false)
        if (!hasAggOverride && (measureFilterCols.length > 0 || overrideMeasureCols.length > 0 || measureFilterValueKeys.size > 0)) {
          const keys = [
            ...normalizeMeasureFilterKeys(ref.fieldId),
            ...normalizeMeasureFilterKeys(ref.columnId || ''),
          ]
          if (!keys.some((k) => measureFilterKeys.has(k))) continue
        }
        pushCol(ref.fieldId, ref.columnId)
      }
      if (measureCols.length === 0) {
        if (hasMeasureNamesFilter || overrideMeasureCols.length > 0) return null
        for (const col of fdCols) {
          const name = String(col?.name ?? '').trim()
          if (!name) continue
          if (isMeasureNames(name) || isMultipleValues(name)) continue
          const role = String(col?.role ?? '').trim().toLowerCase()
          const typ = String(col?.type ?? '').trim().toLowerCase()
          const isMeasure = role === 'measure' || typ === 'quantitative'
          if (!isMeasure) continue
          if (!isAvailableCol(name)) continue
          pushCol(name, name)
        }
      }
      if (measureCols.length === 0) return null

      const defaultAggByColumn = new Map<string, string | null>()
      for (const col of fdCols) {
        const name = String(col?.name ?? '').trim()
        if (!name) continue
        let agg = String(col?.aggregation ?? '').trim().toLowerCase()
        if (agg) {
          if (agg === 'average') agg = 'avg'
          if (agg === 'ctd') agg = 'countd'
          if (agg === 'count_distinct' || agg === 'countdistinct') agg = 'countd'
          defaultAggByColumn.set(name, agg)
          continue
        }
        const role = String(col?.role ?? '').trim().toLowerCase()
        const typ = String(col?.type ?? '').trim().toLowerCase()
        if (role === 'measure' || typ === 'quantitative') {
          defaultAggByColumn.set(name, 'sum')
        }
      }

      const dimFields: Array<{ field: string; time_grain?: string | null; is_discrete?: boolean | null }> = []
      const seenDim = new Set<string>()
      for (const p of pills) {
        const field = String(p?.field ?? p?.field_name ?? '').trim()
        if (!field) continue
        if (isMeasureNames(field) || isMultipleValues(field)) continue
        const shelf = String((p as any)?.shelf ?? '').trim().toLowerCase()
        const isVisualGroupingShelf =
          shelf === 'rows' ||
          shelf === 'columns' ||
          shelf === 'color' ||
          shelf === 'shape'
        if (!isVisualGroupingShelf) continue
        const forceDimension = shelf === 'detail' || shelf === 'path'
        const isMeasure = isMeasurePill(p, forceDimension)
        if (isMeasure) continue
        const timeGrainRaw = (p as any)?.time_grain ?? (p as any)?.timeGrain ?? null
        const timeGrain =
          String(timeGrainRaw ?? '').trim().toLowerCase() ||
          inferTimeGrainFromPill(p) ||
          (field ? timeGrainFromMeta(field) : null) ||
          (field ? inferTimeGrainFromField(field) : null)
        const isDiscreteRaw = (p as any)?.is_discrete
        const typeRaw = String((p as any)?.type ?? '').trim().toLowerCase()
        const isDiscrete = (typeof isDiscreteRaw === 'boolean')
          ? isDiscreteRaw
          : (typeRaw ? typeRaw === 'discrete' : null)
        const discreteKey = (isDiscrete === true) ? 'disc' : (isDiscrete === false ? 'cont' : 'auto')
        const k = `${normKey(field) || field.toLowerCase()}::${timeGrain || 'raw'}::${discreteKey}`
        if (seenDim.has(k)) continue
        seenDim.add(k)
        dimFields.push({ field, time_grain: timeGrain, is_discrete: isDiscrete })
      }

      const dimSelects: string[] = []
      const dimExprs: string[] = []
      const groupByExprs: string[] = []
      for (const d of dimFields) {
        const f = String(d?.field ?? '').trim()
        if (!f) continue
        let exprRaw = pathBinDensifiedFieldExpr(f) || fieldExprFor(f, baseRef)
        if (isNullishExpr(exprRaw)) {
          const ref = findColumnByField(f)
          if (ref?.columnId) {
            const exprAlt = fieldExprFor(ref.columnId, baseRef)
            if (!isNullishExpr(exprAlt)) exprRaw = exprAlt
          }
        }
        if (isNullishExpr(exprRaw)) {
          const formula = lookupCalculatedFormula(f)
          if (formula) {
            const compiled = compileTableauFormulaToSql(formula)
            const compiledSql = compiled?.sql ? String(compiled.sql).trim() : ''
            if (compiledSql && !isNullishExpr(compiledSql)) {
              exprRaw = compiledSql
            }
          }
        }
        if (isNullishExpr(exprRaw)) continue
        const expr = applyTimeGrain(exprRaw, d?.time_grain ?? null, (d as any)?.is_discrete ?? null)
        if (isNullishExpr(expr)) continue
        dimExprs.push(expr)
        dimSelects.push(`${expr} AS ${quoteIdent(dimensionProjectionAlias(f, d?.time_grain))}`)
        if (canSqlExpressionAppearInGroupBy(expr)) groupByExprs.push(expr)
      }
      const defaultOverClause = (() => {
        if (dimExprs.length === 0) return ''
        const orderIdx = Math.max(0, dimExprs.length - 1)
        const partitionExprs = dimExprs.slice(0, orderIdx)
        const orderExpr = dimExprs[orderIdx]
        const parts: string[] = []
        if (partitionExprs.length) parts.push(`PARTITION BY ${partitionExprs.join(', ')}`)
        if (orderExpr) parts.push(`ORDER BY ${orderExpr} ASC`)
        return parts.join(' ')
      })()

      const dimExprByKey = new Map<string, string>()
      for (let i = 0; i < dimFields.length; i++) {
        const f = String(dimFields[i]?.field ?? '').trim()
        const expr = String(dimExprs[i] ?? '').trim()
        if (!f || !expr) continue
        const keyBase = normKey(f) || f.toLowerCase()
        const grain = String(dimFields[i]?.time_grain ?? '').trim().toLowerCase() || 'raw'
        const isDiscrete = (dimFields[i] as any)?.is_discrete
        const discreteKey = (isDiscrete === true) ? 'disc' : (isDiscrete === false ? 'cont' : 'auto')
        if (keyBase) {
          dimExprByKey.set(`${keyBase}::${grain}::${discreteKey}`, expr)
          if (!dimExprByKey.has(keyBase)) dimExprByKey.set(keyBase, expr)
        }
      }

      const tableCalcDims: TableCalcDim[] = []
      const seenTableCalcDim = new Set<string>()
      for (const p of pills) {
        const field = String(p?.field ?? p?.field_name ?? '').trim()
        if (!field) continue
        if (isMeasureNames(field) || isMultipleValues(field)) continue
        const fieldKey = normalizeTableauFieldKey(field)
        if (fieldKey && hideDisplayFilterFieldKeys.has(fieldKey)) continue
        const shelfRaw = String((p as any)?.shelf ?? '').trim().toLowerCase()
        const forceDimension = shelfRaw === 'detail' || shelfRaw === 'path'
        const isMeasure = isMeasurePill(p, forceDimension)
        if (isMeasure) continue
        const timeGrainRaw = (p as any)?.time_grain ?? (p as any)?.timeGrain ?? null
        const timeGrain =
          String(timeGrainRaw ?? '').trim().toLowerCase() ||
          inferTimeGrainFromPill(p) ||
          (field ? timeGrainFromMeta(field) : null) ||
          (field ? inferTimeGrainFromField(field) : null) ||
          null
        const isDiscreteRaw = (p as any)?.is_discrete
        const typeRaw = String((p as any)?.type ?? '').trim().toLowerCase()
        const isDiscrete = (typeof isDiscreteRaw === 'boolean')
          ? isDiscreteRaw
          : (typeRaw ? typeRaw === 'discrete' : null)
        const discreteKey = (isDiscrete === true) ? 'disc' : (isDiscrete === false ? 'cont' : 'auto')
        const keyBase = normKey(field) || field.toLowerCase()
        const lookupKey = `${keyBase}::${timeGrain || 'raw'}::${discreteKey}`
        const expr = dimExprByKey.get(lookupKey) || dimExprByKey.get(keyBase) || null
        if (!expr) continue
        const shelf = (shelfRaw === 'rows' || shelfRaw === 'columns') ? shelfRaw : 'marks'
        const dedupeKey = `${keyBase}::${shelf}::${timeGrain || 'raw'}::${discreteKey}`
        if (seenTableCalcDim.has(dedupeKey)) continue
        seenTableCalcDim.add(dedupeKey)
        tableCalcDims.push({ field, sql: expr, shelf: shelf as TableCalcDim['shelf'] })
      }

      const rowCalcDims = tableCalcDims.filter((d) => d.shelf === 'rows')
      const colCalcDims = tableCalcDims.filter((d) => d.shelf === 'columns')
      const otherCalcDims = tableCalcDims.filter((d) => d.shelf === 'marks' || d.shelf === 'other')

      const resolveTableCalcsForMeasure = (fieldId: string, baseCol: string): any[] | null => {
        const normalize = (arr: any[]): any[] =>
          (Array.isArray(arr) ? arr : [])
            .map((x) => {
              if (!x || typeof x !== 'object') return null
              if ((x as any).attrs && typeof (x as any).attrs === 'object') return x
              const hasTypedShape =
                Object.prototype.hasOwnProperty.call(x, 'compute_using')
                || Object.prototype.hasOwnProperty.call(x, 'ordering_type')
                || Object.prototype.hasOwnProperty.call(x, 'ordering_field')
                || Object.prototype.hasOwnProperty.call(x, 'addressing')
                || Object.prototype.hasOwnProperty.call(x, 'partitioning')
                || Object.prototype.hasOwnProperty.call(x, 'at_level')
                || Object.prototype.hasOwnProperty.call(x, 'restart_every')
              if (hasTypedShape) return x
              return { attrs: x }
            })
            .filter(Boolean) as any[]
        const fKey = normKey(fieldId)
        const bKey = normKey(baseCol)
        const ciHit =
          (fKey && fdCis.find((ci) => normKey(ci?.name ?? '') === fKey)) ||
          (bKey && fdCis.find((ci) => normKey(ci?.name ?? '') === bKey)) ||
          null
        const fromCi = Array.isArray(ciHit?.table_calcs) && ciHit.table_calcs.length > 0 ? ciHit.table_calcs : null
        if (fromCi) return normalize(fromCi)
        const meta = fdCols.find((c) => String(c?.name ?? '').trim() === String(baseCol || '').trim()) || null
        const fromCalc = Array.isArray(meta?.calculation?.table_calcs) && meta.calculation.table_calcs.length > 0
          ? meta.calculation.table_calcs
          : null
        return fromCalc ? normalize(fromCalc) : null
      }

      const tableCalcDimsForOrder: readonly TableCalcDim[] = [
        ...rowCalcDims,
        ...colCalcDims,
        ...otherCalcDims,
      ]
      const tableCalcAggregateSafeOrderPostProcessor =
        makeAggregateSafeOrderByPostProcessor(groupByExprs, { isNullishExpr })
      const tableCalcDimOrderResolver = (d: TableCalcDim): { expr: string; dir: 'ASC' | 'DESC' } | null => {
        const expr = String(d.sql ?? '').trim()
        if (!expr) return null
        if (isNullishExpr(expr)) return null
        return { expr, dir: 'ASC' }
      }
      const tableCalcFallbackOrderResolver = (expr: string): { expr: string; dir: 'ASC' | 'DESC' } => ({
        expr,
        dir: 'ASC',
      })
      const tableCalcOrderByForFieldRef = makeOrderByForFieldRef({
        dims: tableCalcDimsForOrder,
        dimOrderResolver: tableCalcDimOrderResolver,
        fallbackOrderResolver: tableCalcFallbackOrderResolver,
        postProcess: tableCalcAggregateSafeOrderPostProcessor,
        resolveCol,
        allowPhysicalFallback: !hasExtractSchema,
        baseRef,
        quoteIdent,
        fieldExprFor,
        resolvedWeeklyTimePeriodExprForField,
        isNullishExpr,
      })
      const tableCalcOverClause = (tableCalcs: any[] | null): string => {
        const first = Array.isArray(tableCalcs) && tableCalcs.length ? tableCalcs[0] : null
        const derived = deriveComputeUsingFromTableauAttrs({
          attrs: first || null,
          rowDims: rowCalcDims,
          colDims: colCalcDims,
          otherDims: otherCalcDims,
          useSql: true,
          orderExprForField: (d) => tableCalcDimOrderResolver(d)?.expr ?? null,
          directionForField: () => 'Asc',
          orderExprForFieldRef: (fieldRefRaw) => tableCalcOrderByForFieldRef(fieldRefRaw)?.expr ?? null,
          directionForFieldRef: (fieldRefRaw) => {
            const dir = tableCalcOrderByForFieldRef(fieldRefRaw)?.dir ?? 'ASC'
            return dir === 'DESC' ? 'Desc' : 'Asc'
          },
        })
        const over = renderOverClause(derived.config)
        if (worksheetDebugEnabled) {
          debugLog('tableCalc over', {
            attrs: first || null,
            config: derived.config,
            warnings: derived.warnings,
            over: over.over || null,
            rowDims: rowCalcDims.map((d) => ({ field: d.field, sql: d.sql })),
            colDims: colCalcDims.map((d) => ({ field: d.field, sql: d.sql })),
            otherDims: otherCalcDims.map((d) => ({ field: d.field, sql: d.sql })),
          })
        }
        return over.over || defaultOverClause || 'ORDER BY 1'
      }

      const resolveMeasureName = (fieldId: string): string => {
        const labelFromMeasureToken = (raw: any): string | null => {
          const cleaned = cleanName(raw).replace(/^\[/, '').replace(/\]$/, '').trim()
          if (!cleaned) return null
          const parts = cleaned.split(':').map((part) => part.trim()).filter(Boolean)
          if (parts.length < 2) return null
          const aggIdx = parts.findIndex((part) => {
            const t = part.toLowerCase()
            return t === 'sum' || t === 'avg' || t === 'average' || t === 'min' || t === 'max' || t === 'count' || t === 'countd' || t === 'ctd'
          })
          if (aggIdx < 0 || aggIdx + 1 >= parts.length) return null
          const candidate = parts[aggIdx + 1]?.trim()
          if (!candidate || candidate.toLowerCase() === 'qk') return null
          return stripTableauAutoGeneratedSuffixes(candidate) || candidate
        }
        const key = normKey(fieldId)
        if (key) {
          const hitCi =
            fdCis.find((ci) => normKey(ci?.caption ?? '') === key) ||
            fdCis.find((ci) => normKey(ci?.name ?? '') === key) ||
            null
          if (hitCi) {
            const cap = String(hitCi?.caption ?? '').trim()
            if (cap) return cap
            const ciName = String(hitCi?.name ?? '').trim()
            const ciBase = String(hitCi?.column ?? '').trim()
            const ciManifestCap = (() => {
              const k0 = manifestTokenKey(ciName)
              if (k0 && manifestCaptionByToken.has(k0)) return manifestCaptionByToken.get(k0) || null
              const k1 = manifestTokenKey(ciBase)
              if (k1 && manifestCaptionByToken.has(k1)) return manifestCaptionByToken.get(k1) || null
              const k2 = manifestTokenKey(fieldId)
              if (k2 && manifestCaptionByToken.has(k2)) return manifestCaptionByToken.get(k2) || null
              return null
            })()
            if (ciManifestCap) return ciManifestCap
            const ciTokenLabel = labelFromMeasureToken(ciName) || labelFromMeasureToken(ciBase)
            if (ciTokenLabel) return ciTokenLabel
            if (ciName) return cleanName(ciName)
          }
        }
        const baseId = baseColByFieldId.get(fieldId) ?? fieldId
        const manifestCap = (() => {
          const k0 = manifestTokenKey(fieldId)
          if (k0 && manifestCaptionByToken.has(k0)) return manifestCaptionByToken.get(k0) || null
          const k1 = manifestTokenKey(baseId)
          if (k1 && manifestCaptionByToken.has(k1)) return manifestCaptionByToken.get(k1) || null
          return null
        })()
        if (manifestCap) return manifestCap
        const tokenLabel = labelFromMeasureToken(fieldId) || labelFromMeasureToken(baseId)
        if (tokenLabel) return tokenLabel
        const meta = fdCols.find((c) => String(c?.name ?? '').trim() === String(baseId || '').trim()) || null
        const cap = meta?.caption != null ? String(meta.caption).trim() : ''
        if (cap) return cap
        const fallbackRaw = normalizeTableauFieldToken(cleanName(baseId)).trim()
        const fallback = stripTableauAutoGeneratedSuffixes(fallbackRaw)
        return fallback || cleanName(baseId)
      }

      const wrapAgg = (aggTok: string | null, expr: string): string => {
        const t = String(aggTok ?? '').trim().toLowerCase()
        if (!t) return expr
        if (t === 'countd') return `COUNT(DISTINCT ${expr})`
        if (t === 'count') return `COUNT(${expr})`
        if (t === 'avg') return `AVG(TRY_CAST(${expr} AS DOUBLE))`
        if (t === 'min') return `MIN(${expr})`
        if (t === 'max') return `MAX(${expr})`
        // default SUM
        return `SUM(TRY_CAST(${expr} AS DOUBLE))`
      }

      const buildExprPredicate = (exprSql: string, op: string, value: any): string | null => {
        const o = String(op ?? '').trim().toUpperCase()
        if (!o) return null
        if (o === 'IS NULL') return `${exprSql} IS NULL`
        if (o === 'IS NOT NULL') return `${exprSql} IS NOT NULL`
        if (o === 'IN' || o === 'NOT IN') {
          const not = o.startsWith('NOT')
          if (value === null || value === undefined) return null
          if (Array.isArray(value)) {
          const pred = buildInPredicateFromValue(exprSql, value, not)
          return pred
          }
        return buildInPredicateFromValue(exprSql, value, not)
        }
        if (o === 'BETWEEN') {
          if (Array.isArray(value) && value.length >= 2) {
            return buildBetweenPredicate(exprSql, value[0], value[1], null, null)
          }
          return null
        }
        if (o === 'LIKE' || o === 'NOT LIKE') return `${exprSql} ${o} ${escapeValue(value)}`
        if (o === '!=' || o === '<>') return `(${exprSql} IS NULL OR ${exprSql} ${o} ${escapeValue(value)})`
        return `${exprSql} ${o} ${escapeValue(value)}`
      }

      const whereConditions: string[] = []
      const qualifyConditions: string[] = []
      const pushFilterPredicate = (pred: string) => {
        if (!pred) return
        if (looksWindowExpr(pred)) qualifyConditions.push(pred)
        else whereConditions.push(pred)
      }
      for (const f of (filters || [])) {
        const rawFieldName = String((f as any)?.field_name ?? '').trim()
        if (!rawFieldName) continue
        if (isMeasureNames(rawFieldName) || isMultipleValues(rawFieldName)) continue
        const op = String((f as any)?.operator ?? '=').trim().toUpperCase()
        if (!op) continue
        const expr =
          resolvedWeeklyTimePeriodExprForField?.(rawFieldName) ||
          fieldExprFor(rawFieldName, baseRef)
        if (isNullishExpr(expr)) continue
        const pred = buildExprPredicate(expr, op, (f as any)?.value)
        if (pred) pushFilterPredicate(pred)
      }
      const whereSql = whereConditions.length ? `WHERE ${whereConditions.join(' AND ')}` : ''
      const qualifySql = qualifyConditions.length ? `QUALIFY ${qualifyConditions.join(' AND ')}` : ''

      const branches: string[] = []
      for (const col of measureCols) {
        const name = resolveMeasureName(col)
        let expr = fieldExprFor(col, baseRef)
        const baseCol = baseColByFieldId.get(col) ?? col
        if (isNullishExpr(expr) && baseCol !== col) {
          const exprAlt = fieldExprFor(baseCol, baseRef)
          if (!isNullishExpr(exprAlt)) expr = exprAlt
        }
        const tableCalcs = resolveTableCalcsForMeasure(col, baseCol)
        const overClause = tableCalcs ? tableCalcOverClause(tableCalcs) : defaultOverClause || null
        if (isNullishExpr(expr)) {
          const formula = lookupCalculatedFormula(baseCol) || lookupCalculatedFormula(col)
          let rewritten = false
          if (formula) {
            const rewrite = tryRewriteMonthlyAvgDiff(formula, baseRef)
            if (rewrite) {
              expr = rewrite
              rewritten = true
            }
          }
          if (!rewritten) {
            const compiled = formula ? compileTableauFormulaToSql(formula, { overClause }) : null
            const compiledSql = compiled?.sql ? String(compiled.sql).trim() : ''
            if (compiledSql && !isNullishExpr(compiledSql)) {
              expr = compiledSql
            }
          }
        }
        let aggTok = extraAggByFieldId.get(col)
          ?? extraAggByFieldId.get(baseCol)
          ?? aggByField.get(col)
          ?? aggByField.get(baseCol)
          ?? defaultAggByColumn.get(baseCol)
          ?? null
        if (!aggTok && !looksAggExpr(expr)) {
          aggTok = 'sum'
        }
        if (aggTok && !looksAggExpr(expr)) {
          expr = wrapAgg(aggTok, expr)
        }
        const selectParts = [
          ...dimSelects,
          `${escapeValue(name)} AS ${quoteIdent('Measure Names')}`,
          `${expr} AS ${quoteIdent('Measure Values')}`,
        ]
        let sql = `SELECT ${selectParts.join(', ')} FROM ${vizSourceSql} AS ${baseRef}`
        if (whereSql) sql += ` ${whereSql}`
        // Pure window branches should stay row-level (no GROUP BY), but if a window
        // expression contains non-window aggregates we must still GROUP BY dimensions.
        const branchHasWindow = looksWindowExpr(expr)
        const branchNeedsGroupBy = !branchHasWindow || hasNonWindowAggregateExpr(expr)
        if (groupByExprs.length && branchNeedsGroupBy) sql += ` GROUP BY ${groupByExprs.join(', ')}`
        if (qualifySql) sql += ` ${qualifySql}`
        branches.push(sql)
      }
      if (!branches.length) return null
      const unionSql = branches.join(' UNION ALL ')
      const orderTerms = dimFields
        .map((d) => dimensionProjectionAlias(String(d?.field ?? '').trim(), d?.time_grain ?? null))
        .filter(Boolean)
        .map((name) => quoteIdent(name))
      const measureOrderNames = measureCols.map((col) => resolveMeasureName(col))
      if (measureOrderNames.length) {
        const seenMeasureOrder = new Set<string>()
        const whens: string[] = []
        for (const name of measureOrderNames) {
          const key = String(name ?? '').trim().toLowerCase()
          if (!key || seenMeasureOrder.has(key)) continue
          seenMeasureOrder.add(key)
          whens.push(`WHEN ${escapeValue(name)} THEN ${whens.length}`)
        }
        if (whens.length) {
          orderTerms.push(`CASE ${quoteIdent('Measure Names')} ${whens.join(' ')} ELSE 999999 END`)
        } else {
          orderTerms.push(quoteIdent('Measure Names'))
        }
      } else {
        orderTerms.push(quoteIdent('Measure Names'))
      }
      const wrappedAlias = quoteIdent('__fg_mnv_union')
      const wrappedSql = `SELECT * FROM (${unionSql}) AS ${wrappedAlias}`
      const orderedSql = orderTerms.length
        ? `${wrappedSql} ORDER BY ${orderTerms.join(', ')}`
        : wrappedSql
      const limRaw = Number.isFinite(Number(limit)) ? Math.floor(Number(limit)) : 10_000
      const lim = limRaw > 0 ? Math.min(250_000, limRaw) : 0
      return lim > 0 ? `${orderedSql} LIMIT ${lim}` : orderedSql
    }

    if ((usesMeasureNames || usesMultipleValues) && workbookIr) {
      const mnSql = buildMeasureNamesValuesQuery()
      if (mnSql) return mnSql
    }

    for (const p of pills) {
      const field = String(p?.field ?? p?.field_name ?? '').trim()
      if (!field) continue
      if (isMeasureNames(field) || isMultipleValues(field)) continue
      const aggRaw = (p as any)?.aggregation
      const aggTok = normalizeAggTokenSimple(aggRaw, field)
      const agg = aggTok ? String(aggTok).trim() : ((aggRaw === null || aggRaw === undefined) ? '' : String(aggRaw).trim())
      const shelf = String((p as any)?.shelf ?? '').trim().toLowerCase()
      const forceDimension = shelf === 'detail' || shelf === 'path'
      const isMeasure = isMeasurePill(p, forceDimension) || Boolean(aggTok) || Boolean(agg)
      if (!isMeasure) {
        const timeGrainRaw = (p as any)?.time_grain ?? (p as any)?.timeGrain ?? null
        const timeGrain = String(timeGrainRaw ?? '').trim().toLowerCase() || inferTimeGrainFromPill(p) || null
        const isDiscreteRaw = (p as any)?.is_discrete
        const typeRaw = String((p as any)?.type ?? '').trim().toLowerCase()
        const isDiscrete = (typeof isDiscreteRaw === 'boolean')
          ? isDiscreteRaw
          : (typeRaw ? typeRaw === 'discrete' : null)
        const discreteKey = (isDiscrete === true) ? 'disc' : (isDiscrete === false ? 'cont' : 'auto')
        const k = `${normKey(field) || field.toLowerCase()}::${timeGrain || 'raw'}::${discreteKey}`
        if (seenDim.has(k)) continue
        seenDim.add(k)
        dimPills.push({ field, time_grain: timeGrain, is_discrete: isDiscrete })
        continue
      }

      // Dedup measures by (agg + field) so tooltip + shelves don't double-add.
      const k = `${String(aggTok || agg || 'SUM').toLowerCase()}|${normKey(field) || field.toLowerCase()}`
      if (seenMeas.has(k)) continue
      seenMeas.add(k)
      measurePills.push({ ...p, field, aggregation: aggTok || agg || (p as any)?.aggregation || 'SUM' })
    }

    const dimSelects: string[] = []
    const dimExprs: string[] = []
    const groupByExprs: string[] = []
    for (const d of dimPills) {
      const f = String(d?.field ?? '').trim()
      if (!f) continue
      // Tableau "Measure Names" is a virtual field (not a real column in extracts).
      // v0 viz queries don't fully support it yet, but we must not emit `t."Measure Names"`
      // which causes DuckDB binder errors. Emit a stable placeholder instead.
      if (isMeasureNames(f)) {
        dimSelects.push(`${escapeValue('Measure Names')} AS ${quoteIdent(f)}`)
        continue
      }
      // Dimensions can reference physical columns OR calc/instance tokens like "[Calculation_123]".
      // Use fieldExprFor so we don't emit bogus extract column names for calcs.
      let exprRaw = pathBinDensifiedFieldExpr(f) || fieldExprFor(f, baseRef)
      if (isNullishExpr(exprRaw)) {
        const ref = findColumnByField(f)
        if (ref?.columnId) {
          const exprAlt = fieldExprFor(ref.columnId, baseRef)
          if (!isNullishExpr(exprAlt)) exprRaw = exprAlt
        }
      }
      if (isNullishExpr(exprRaw)) {
        const formula = lookupCalculatedFormula(f)
        if (formula) {
          const compiled = compileTableauFormulaToSql(formula)
          const compiledSql = compiled?.sql ? String(compiled.sql).trim() : ''
          if (compiledSql && !isNullishExpr(compiledSql)) {
            exprRaw = compiledSql
          }
        }
      }
      const expr = applyTimeGrain(exprRaw, d?.time_grain ?? null, (d as any)?.is_discrete ?? null)
      if (isNullishExpr(expr)) continue
      dimSelects.push(`${expr} AS ${quoteIdent(dimensionProjectionAlias(f, d?.time_grain))}`)
      dimExprs.push(expr)
      if (canSqlExpressionAppearInGroupBy(expr)) groupByExprs.push(expr)
    }

    const dimExprByKey = new Map<string, string>()
    for (let i = 0; i < dimPills.length; i++) {
      const f = String(dimPills[i]?.field ?? '').trim()
      const expr = String(dimExprs[i] ?? '').trim()
      if (!f || !expr) continue
      const keyBase = normKey(f) || f.toLowerCase()
      const grain = String(dimPills[i]?.time_grain ?? '').trim().toLowerCase() || 'raw'
      const isDiscrete = (dimPills[i] as any)?.is_discrete
      const discreteKey = (isDiscrete === true) ? 'disc' : (isDiscrete === false ? 'cont' : 'auto')
      if (keyBase) {
        dimExprByKey.set(`${keyBase}::${grain}::${discreteKey}`, expr)
        if (!dimExprByKey.has(keyBase)) dimExprByKey.set(keyBase, expr)
      }
    }

    const tableCalcDims: TableCalcDim[] = []
    const seenTableCalcDim = new Set<string>()
    for (const p of pills) {
      const field = String(p?.field ?? p?.field_name ?? '').trim()
      if (!field) continue
      if (isMeasureNames(field) || isMultipleValues(field)) continue
      const fieldKey = normalizeTableauFieldKey(field)
      if (fieldKey && hideDisplayFilterFieldKeys.has(fieldKey)) continue
      const shelfRaw = String((p as any)?.shelf ?? '').trim().toLowerCase()
      const forceDimension = shelfRaw === 'detail' || shelfRaw === 'path'
      const isMeasure = isMeasurePill(p, forceDimension)
      if (isMeasure) continue
      const timeGrainRaw = (p as any)?.time_grain ?? (p as any)?.timeGrain ?? null
      const timeGrain =
        String(timeGrainRaw ?? '').trim().toLowerCase() ||
        inferTimeGrainFromPill(p) ||
        (field ? timeGrainFromMeta(field) : null) ||
        (field ? inferTimeGrainFromField(field) : null) ||
        null
      const isDiscreteRaw = (p as any)?.is_discrete
      const typeRaw = String((p as any)?.type ?? '').trim().toLowerCase()
      const isDiscrete = (typeof isDiscreteRaw === 'boolean')
        ? isDiscreteRaw
        : (typeRaw ? typeRaw === 'discrete' : null)
      const discreteKey = (isDiscrete === true) ? 'disc' : (isDiscrete === false ? 'cont' : 'auto')
      const keyBase = normKey(field) || field.toLowerCase()
      const lookupKey = `${keyBase}::${timeGrain || 'raw'}::${discreteKey}`
      const expr = dimExprByKey.get(lookupKey) || dimExprByKey.get(keyBase) || null
      if (!expr) continue
      const shelf = (shelfRaw === 'rows' || shelfRaw === 'columns') ? shelfRaw : 'marks'
      const dedupeKey = `${keyBase}::${shelf}::${timeGrain || 'raw'}::${discreteKey}`
      if (seenTableCalcDim.has(dedupeKey)) continue
      seenTableCalcDim.add(dedupeKey)
      tableCalcDims.push({ field, sql: expr, shelf: shelf as TableCalcDim['shelf'] })
    }

    const rowCalcDims = tableCalcDims.filter((d) => d.shelf === 'rows')
    const colCalcDims = tableCalcDims.filter((d) => d.shelf === 'columns')
    const otherCalcDims = tableCalcDims.filter((d) => d.shelf === 'marks' || d.shelf === 'other')

    const resolveTableCalcsForMeasure = (fieldId: string, baseCol: string): any[] | null => {
      const normalize = (arr: any[]): any[] =>
        (Array.isArray(arr) ? arr : [])
          .map((x) => {
            if (!x || typeof x !== 'object') return null
            if ((x as any).attrs && typeof (x as any).attrs === 'object') return x
            const hasTypedShape =
              Object.prototype.hasOwnProperty.call(x, 'compute_using')
              || Object.prototype.hasOwnProperty.call(x, 'ordering_type')
              || Object.prototype.hasOwnProperty.call(x, 'ordering_field')
              || Object.prototype.hasOwnProperty.call(x, 'addressing')
              || Object.prototype.hasOwnProperty.call(x, 'partitioning')
              || Object.prototype.hasOwnProperty.call(x, 'at_level')
              || Object.prototype.hasOwnProperty.call(x, 'restart_every')
            if (hasTypedShape) return x
            return { attrs: x }
          })
          .filter(Boolean) as any[]
      const fKey = normKey(fieldId)
      const bKey = normKey(baseCol)
      const ciHit =
        (fKey && fdCis.find((ci) => normKey(ci?.name ?? '') === fKey)) ||
        (bKey && fdCis.find((ci) => normKey(ci?.name ?? '') === bKey)) ||
        null
      const fromCi = Array.isArray(ciHit?.table_calcs) && ciHit.table_calcs.length > 0 ? ciHit.table_calcs : null
      if (fromCi) return normalize(fromCi)
      const meta = fdCols.find((c) => String(c?.name ?? '').trim() === String(baseCol || '').trim()) || null
      const fromCalc = Array.isArray(meta?.calculation?.table_calcs) && meta.calculation.table_calcs.length > 0
        ? meta.calculation.table_calcs
        : null
      return fromCalc ? normalize(fromCalc) : null
    }

    const tableCalcDimsForOrder: readonly TableCalcDim[] = [
      ...rowCalcDims,
      ...colCalcDims,
      ...otherCalcDims,
    ]
    const tableCalcAggregateSafeOrderPostProcessor =
      makeAggregateSafeOrderByPostProcessor(groupByExprs, { isNullishExpr })
    const tableCalcDimOrderResolver = (d: TableCalcDim): { expr: string; dir: 'ASC' | 'DESC' } | null => {
      const expr = String(d.sql ?? '').trim()
      if (!expr) return null
      if (isNullishExpr(expr)) return null
      return { expr, dir: 'ASC' }
    }
    const tableCalcFallbackOrderResolver = (expr: string): { expr: string; dir: 'ASC' | 'DESC' } => ({
      expr,
      dir: 'ASC',
    })
    const tableCalcOrderByForFieldRef = makeOrderByForFieldRef({
      dims: tableCalcDimsForOrder,
      dimOrderResolver: tableCalcDimOrderResolver,
      fallbackOrderResolver: tableCalcFallbackOrderResolver,
      postProcess: tableCalcAggregateSafeOrderPostProcessor,
      resolveCol,
      allowPhysicalFallback: !hasExtractSchema,
      baseRef,
      quoteIdent,
      fieldExprFor,
      resolvedWeeklyTimePeriodExprForField,
      isNullishExpr,
    })
    const deriveTableCalcConfigForTableCalcs = (tableCalcs: any[] | null): any => {
      const first = Array.isArray(tableCalcs) && tableCalcs.length ? tableCalcs[0] : null
      return deriveComputeUsingFromTableauAttrs({
        attrs: first || null,
        rowDims: rowCalcDims,
        colDims: colCalcDims,
        otherDims: otherCalcDims,
        useSql: true,
        orderExprForField: (d) => tableCalcDimOrderResolver(d)?.expr ?? null,
        directionForField: () => 'Asc',
        orderExprForFieldRef: (fieldRefRaw) => tableCalcOrderByForFieldRef(fieldRefRaw)?.expr ?? null,
        directionForFieldRef: (fieldRefRaw) => {
          const dir = tableCalcOrderByForFieldRef(fieldRefRaw)?.dir ?? 'ASC'
          return dir === 'DESC' ? 'Desc' : 'Asc'
        },
      })
    }

    const tableCalcOverClause = (tableCalcs: any[] | null): string => {
      const first = Array.isArray(tableCalcs) && tableCalcs.length ? tableCalcs[0] : null
      const derived = deriveTableCalcConfigForTableCalcs(tableCalcs)
      const config = derived.config || null
      const over = renderOverClause(config)
      if (worksheetDebugEnabled) {
        debugLog('tableCalc over', {
          attrs: first || null,
          config,
          warnings: derived.warnings,
          over: over.over || null,
          rowDims: rowCalcDims.map((d) => ({ field: d.field, sql: d.sql })),
          colDims: colCalcDims.map((d) => ({ field: d.field, sql: d.sql })),
          otherDims: otherCalcDims.map((d) => ({ field: d.field, sql: d.sql })),
        })
      }
      return over.over || 'ORDER BY 1'
    }

    const applyTableCalcMetadataToCalcCompileSpec = (): void => {
      const calcs = Array.isArray((calcCompileSpec as any)?.data_pane?.calculated_fields)
        ? (((calcCompileSpec as any).data_pane.calculated_fields) as any[])
        : []
      let changed = false
      for (const calc of calcs) {
        if (!calc || typeof calc !== 'object') continue
        if ((calc as any).table_calc?.enabled) continue
        const raw = Array.isArray((calc as any).tableau_table_calcs)
          ? ((calc as any).tableau_table_calcs as any[])
          : []
        if (!raw.length) continue
        const derived = deriveTableCalcConfigForTableCalcs(raw)
        const config = derived?.config || null
        if (!config) continue
        ;(calc as any).table_calc = {
          enabled: true,
          compute_using: config,
        }
        changed = true
      }
      if (changed) {
        try { compiledFormulaCache.clear() } catch {}
      }
    }
    applyTableCalcMetadataToCalcCompileSpec()

    const measSelects: string[] = []
    const measureAliases: Array<{ field: string; aggregation: string; alias: string }> = []
    for (const p of measurePills) {
      const field = String(p?.field ?? '').trim()
      if (!field) continue
      // Measures can reference physical columns OR Tableau calc/instance tokens like "[Calculation_123]".
      // Use the shared fieldExprFor() helper so we can:
      // - resolve bracketed/internal tokens to real extract columns when possible
      // - fall back to compiling the calc formula when the token isn't a physical column
      let colExpr = fieldExprFor(field, baseRef)
      const baseCol = resolveExtractColumnName({
        fieldName: field,
        workbookIr,
        availableColumns: extractColumns,
        fieldManifest: options.fieldManifest ?? null,
      }) || field
      const tableCalcs = resolveTableCalcsForMeasure(field, baseCol)
      if (tableCalcs) {
        const formula = lookupCalculatedFormula(baseCol) || lookupCalculatedFormula(field)
        const overClause = tableCalcOverClause(tableCalcs)
        const compiled = formula ? compileTableauFormulaToSql(formula, { overClause }) : null
        const compiledSql = compiled?.sql ? String(compiled.sql).trim() : ''
        if (compiledSql && !isNullishExpr(compiledSql)) {
          colExpr = compiledSql
        }
      }

      const aggRaw = String((p as any)?.aggregation ?? '').trim()
      const aggLower = aggRaw.toLowerCase()
      const isAgg = aggLower === 'agg'
      const fn = aggLower ? aggLower.toUpperCase() : 'SUM'

      // Prefer the internal calc token when Tableau auto-generates Calculation_* fields,
      // otherwise headers won't line up with pill field names during viz mapping.
      const alias = measureAliasForPill(field, fn, (p as any)?.alias)

      let expr = ''
      if (isAgg) {
        // Tableau AGG(): expression is already aggregated.
        expr = colExpr
      } else if (looksWindowExpr(colExpr) || looksAggExpr(colExpr)) {
        // Avoid illegal nested aggregates/window functions.
        expr = colExpr
      } else if (fn === 'COUNTD' || aggLower === 'countd' || aggLower === 'count_distinct') {
        expr = `COUNT(DISTINCT ${colExpr})`
      } else if (fn === 'COUNT') {
        expr = `COUNT(${colExpr})`
      } else if (fn === 'SUM' || fn === 'AVG' || fn === 'MIN' || fn === 'MAX') {
        expr = `${fn}(TRY_CAST(${colExpr} AS DOUBLE))`
      } else {
        // Best-effort pass-through (AGG, etc.)
        expr = `${fn}(${colExpr})`
      }

      measSelects.push(`${expr} AS ${quoteDisplayAlias(alias)}`)
      measureAliases.push({ field, aggregation: fn, alias })
    }

    const tableauAttrSql = (expr: string): string =>
      `CASE WHEN COUNT(DISTINCT CAST(${expr} AS VARCHAR)) = 1 THEN MIN(CAST(${expr} AS VARCHAR)) ELSE '*' END`

    // Tooltip template token extra columns (ATTR/aggregates) so worker tooltips can substitute real data
    // even when the referenced field isn't on an explicit shelf.
    const extraSelects: string[] = []
    const extraAliases: string[] = []
    const seenAlias = new Set<string>([
      ...dimPills.map((p) => normKey(p?.field) || String(p?.field ?? '').toLowerCase()),
      ...measurePills.map((p) => String(p?.alias ?? `${String((p as any)?.aggregation ?? 'SUM').toUpperCase()}(${String(p?.field ?? '').trim()})`).trim()).filter(Boolean).map((a) => a.toLowerCase()),
    ].filter(Boolean))
    const rememberAlias = (a: string) => { const k = String(a || '').trim().toLowerCase(); if (k) seenAlias.add(k) }

    const buildDatePartSql0 = (fnUpper: string, innerExpr: string): string | null => {
      const fn = String(fnUpper ?? '').trim().toUpperCase()
      if (!fn) return null
      const ts = `TRY_CAST(${innerExpr} AS TIMESTAMP)`
      if (fn === 'YEAR') return `EXTRACT(year FROM ${ts})`
      if (fn === 'QUARTER') return `EXTRACT(quarter FROM ${ts})`
      if (fn === 'MONTH') return `EXTRACT(month FROM ${ts})`
      if (fn === 'DAY') return `EXTRACT(day FROM ${ts})`
      if (fn === 'HOUR') return `EXTRACT(hour FROM ${ts})`
      if (fn === 'MINUTE') return `EXTRACT(minute FROM ${ts})`
      if (fn === 'SECOND') return `EXTRACT(second FROM ${ts})`
      return null
    }

    const tt0: any = ((visualSpec as any)?.view && typeof (visualSpec as any).view === 'object') ? (visualSpec as any).view.tooltip : null
    const ttEnabled = tt0?.enabled !== false
    const ttMode = String(tt0?.mode ?? 'auto').trim().toLowerCase()
    const ttTemplateSrc = (ttMode === 'rich') ? String(tt0?.template_html ?? '') : String(tt0?.template ?? '')
    if (ttEnabled && (ttMode === 'template' || ttMode === 'rich') && ttTemplateSrc) {
      for (const tok of extractTooltipTokensFromTemplate(ttTemplateSrc)) {
        const need = parseTooltipTokenNeed(tok)
        if (!need || need.kind === 'all_fields') continue

        if (need.kind === 'field') {
          const field = stripBracketsAroundFieldName(need.field)
          if (!field) continue
          const k = normKey(field) || field.toLowerCase()
          if (seenDim.has(k)) continue
          const alias = field
          if (seenAlias.has(alias.toLowerCase())) continue
          const colId = resolveColOrRawWhenNoSchema(field)
          if (!colId || colId === UNRESOLVED_FIELD) continue
          const inner = `${baseRef}.${quoteIdent(colId)}`
          extraSelects.push(`${tableauAttrSql(inner)} AS ${quoteIdent(alias)}`)
          extraAliases.push(alias)
          rememberAlias(alias)
          continue
        }

        const fn = String((need as any).fn ?? '').trim().toUpperCase()
        const argRaw = String((need as any).arg ?? '').trim()
        const arg = stripBracketsAroundFieldName(argRaw)
        if (!fn || !arg) continue
        const alias =
          (fn === 'COUNTDISTINCT' || fn === 'COUNT_DISTINCT') ? `COUNTD(${arg})` :
            `${fn}(${arg})`
        if (seenAlias.has(alias.toLowerCase())) continue

        const colId = resolveColOrRawWhenNoSchema(arg)
        if (!colId || colId === UNRESOLVED_FIELD) continue
        const inner = `${baseRef}.${quoteIdent(colId)}`
        if (fn === 'ATTR') {
          extraSelects.push(`${tableauAttrSql(inner)} AS ${quoteIdent(alias)}`)
          extraAliases.push(alias)
          rememberAlias(alias)
          continue
        }
        const dp = buildDatePartSql0(fn, inner)
        if (dp) {
          extraSelects.push(`${tableauAttrSql(dp)} AS ${quoteIdent(alias)}`)
          extraAliases.push(alias)
          rememberAlias(alias)
          continue
        }
        if (fn === 'COUNTD' || fn === 'COUNTDISTINCT' || fn === 'COUNT_DISTINCT') {
          extraSelects.push(`COUNT(DISTINCT CAST(${inner} AS VARCHAR)) AS ${quoteIdent(alias)}`)
          extraAliases.push(alias)
          rememberAlias(alias)
          continue
        }
        if (fn === 'COUNT') {
          extraSelects.push(`COUNT(${inner}) AS ${quoteIdent(alias)}`)
          extraAliases.push(alias)
          rememberAlias(alias)
          continue
        }
        if (fn === 'SUM') {
          extraSelects.push(`SUM(TRY_CAST(${inner} AS DOUBLE)) AS ${quoteIdent(alias)}`)
          extraAliases.push(alias)
          rememberAlias(alias)
          continue
        }
        if (fn === 'AVG' || fn === 'AVERAGE' || fn === 'MEAN') {
          extraSelects.push(`AVG(TRY_CAST(${inner} AS DOUBLE)) AS ${quoteIdent(alias)}`)
          extraAliases.push(alias)
          rememberAlias(alias)
          continue
        }
        if (fn === 'MIN') {
          extraSelects.push(`MIN(${inner}) AS ${quoteIdent(alias)}`)
          extraAliases.push(alias)
          rememberAlias(alias)
          continue
        }
        if (fn === 'MAX') {
          extraSelects.push(`MAX(${inner}) AS ${quoteIdent(alias)}`)
          extraAliases.push(alias)
          rememberAlias(alias)
          continue
        }
      }
    }

    const selectList = [...dimSelects, ...measSelects, ...extraSelects]

    if (debugFieldMapping) {
      try {
        pushFieldMappingTrace({
          phase: 'viz_query',
          message: 'viz_query_fields',
          data: {
            worksheetName: worksheetNameDebug,
            dims: dimPills.map((p) => p.field),
            measures: measureAliases,
            extra: extraAliases,
          },
        })
      } catch { }
    }
    if (selectList.length === 0) {
      // Nothing to query; still return a valid, empty result.
      return `SELECT 1 AS ${quoteIdent('__empty__')} FROM ${vizSourceSql} AS ${quoteIdent(baseAlias)} LIMIT 0`
    }

    const whereConditions: string[] = []
    const qualifyConditions: string[] = []
    const postSelectFilters: Array<{ field: string; operator: 'IS NULL' | 'IS NOT NULL' }> = []
    const pushFilterPredicate = (pred: string) => {
      if (!pred) return
      if (looksWindowExpr(pred)) qualifyConditions.push(pred)
      else whereConditions.push(pred)
    }
    const havingConditions: string[] = []

    const isContextFilter = (f: any): boolean => {
      const ft = String((f as any)?.filter_type ?? '').trim().toLowerCase()
      return ft === 'context' || Boolean((f as any)?.context)
    }
    const contextFilters = (Array.isArray(filters) ? filters : []).filter(isContextFilter)

    const parseAggToken = (raw: string): { agg: string; field: string } | null => {
      const s = String(raw ?? '').trim()
      if (!s) return null
      const m = s.match(/^([A-Za-z_][A-Za-z0-9_]*)\s*\(\s*(.*?)\s*\)\s*$/)
      if (!m) return null
      const fn0 = String(m[1] ?? '').trim().toUpperCase()
      const arg0 = stripBracketsAroundFieldName(String(m[2] ?? '').trim())
      if (!fn0 || !arg0) return null
      const fn = (fn0 === 'COUNTDISTINCT' || fn0 === 'COUNT_DISTINCT') ? 'COUNTD' : fn0
      if (!['SUM', 'AVG', 'MIN', 'MAX', 'COUNT', 'COUNTD'].includes(fn)) return null
      return { agg: fn, field: arg0 }
    }

    const buildAggExpr = (aggUpper: string, innerExpr: string): string => {
      const fn = String(aggUpper ?? '').trim().toUpperCase()
      if (fn === 'COUNTD') return `COUNT(DISTINCT CAST(${innerExpr} AS VARCHAR))`
      if (fn === 'COUNT') return `COUNT(${innerExpr})`
      if (fn === 'AVG') return `AVG(TRY_CAST(${innerExpr} AS DOUBLE))`
      if (fn === 'MIN') return `MIN(${innerExpr})`
      if (fn === 'MAX') return `MAX(${innerExpr})`
      // Default SUM
      return `SUM(TRY_CAST(${innerExpr} AS DOUBLE))`
    }

    const buildExprPredicate = (exprSql: string, op: string, value: any): string | null => {
      const o = String(op ?? '').trim().toUpperCase()
      if (!o) return null
      if (o === 'IS NULL') return `${exprSql} IS NULL`
      if (o === 'IS NOT NULL') return `${exprSql} IS NOT NULL`
      if (o === 'IN' || o === 'NOT IN') {
        const not = o.startsWith('NOT')
        if (value === null || value === undefined) return null
        if (Array.isArray(value)) {
          const pred = buildInPredicateFromValue(exprSql, value, not)
          return pred
        }
        return buildInPredicateFromValue(exprSql, value, not)
      }
      if (o === 'BETWEEN') {
        if (Array.isArray(value) && value.length >= 2) {
          return buildBetweenPredicate(exprSql, value[0], value[1], null, null)
        }
        return null
      }
      if (o === 'LIKE' || o === 'NOT LIKE') return `${exprSql} ${o} ${escapeValue(value)}`
      if (o === '!=' || o === '<>') return `(${exprSql} IS NULL OR ${exprSql} ${o} ${escapeValue(value)})`
      return `${exprSql} ${o} ${escapeValue(value)}`
    }

    const usesAggregation = Boolean(measSelects.length || extraSelects.length)

    for (const f of (filters || [])) {
      const rawFieldName = String((f as any)?.field_name ?? '').trim()
      if (!rawFieldName) continue
      const op = String((f as any)?.operator ?? '=').trim().toUpperCase()
      const value = (f as any)?.value
      const explicitEmpty = isExplicitEmptyFilter(f)
      const explicitEmptyInFilter = op === 'IN' && explicitEmpty && (
        templateMode ||
        (Array.isArray(value) && value.length === 0)
      )
      if (shouldSkipSqlFilter(f)) continue
      if (op === 'ANY_TUPLE' || op === 'NOT ANY_TUPLE') {
        const tuples = Array.isArray(value) ? value : []
        const tuplePredicates: string[] = []
        for (const tuple of tuples) {
          if (!tuple || typeof tuple !== 'object') continue
          const fieldPredicates: string[] = []
          for (const [tupleFieldRaw, tupleValue] of Object.entries(tuple)) {
            const tupleFieldName = String(tupleFieldRaw || '').trim()
            if (!tupleFieldName) continue
            const tupleKeyBase = normKey(tupleFieldName) || tupleFieldName.toLowerCase()
            let tupleFieldExprRaw: string | null = null
            if (dimExprByKey && dimExprByKey.size > 0) {
              tupleFieldExprRaw = dimExprByKey.get(tupleKeyBase) || null
            }
            if (!tupleFieldExprRaw) {
              tupleFieldExprRaw =
                resolvedWeeklyTimePeriodExprForField?.(tupleFieldName) ||
                fieldExprFor(tupleFieldName, baseRef)
            }
            if (isNullishExpr(tupleFieldExprRaw)) continue
            const tupleFieldSql = `(${tupleFieldExprRaw})`
            if (tupleValue === null) {
              fieldPredicates.push(`${tupleFieldSql} IS NULL`)
              continue
            }
            const pred = buildInPredicateFromValue(tupleFieldSql, tupleValue, false, { includeNullsOnNot: true })
            if (pred) fieldPredicates.push(pred)
          }
          if (fieldPredicates.length > 0) tuplePredicates.push(`(${fieldPredicates.join(' AND ')})`)
        }
        if (tuplePredicates.length > 0) {
          const tuplePred = `(${tuplePredicates.join(' OR ')})`
          // #region agent log
          try {
            postOptionalLocalDebugLog('http://127.0.0.1:7788/ingest/7b95b565-26ed-4f27-b984-d1a6c1d0ea54',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'c5cbac'},body:JSON.stringify({sessionId:'c5cbac',runId:'detail-empty-round5',hypothesisId:'H14',location:'worksheetQueryBuilder.ts:8948',message:'tuple predicate built',data:{operator:op,tupleCount:tuples.length,tupleFieldSamples:tuples.slice(0,2).map((tuple:any)=>tuple&&typeof tuple==='object'?Object.keys(tuple).sort():[]),predicateSnippet:tuplePred.slice(0,800)},timestamp:Date.now()})})
          } catch {}
          // #endregion
          pushFilterPredicate(op === 'NOT ANY_TUPLE' ? `(NOT ${tuplePred})` : tuplePred)
        } else if (op === 'ANY_TUPLE') {
          pushFilterPredicate('FALSE')
        }
        continue
      }
      const fieldKey = normalizeTableauFieldKey(rawFieldName)
      const hidePointsFilter = (() => {
        if (isHideDisplayFilter(f)) return true
        if (fieldKey !== 'violin/points' && fieldKey !== 'violin points') return false
        if (!['NOT IN', '!=', '<>'].includes(op)) return false
        const values = Array.isArray(value) ? value : [value]
        return values.some((v) => String(normalizeFilterAtom(v) ?? '').trim().toLowerCase() === 'points')
      })()
      if (hidePointsFilter) continue
      if (isMeasureNames(rawFieldName) || isMultipleValues(rawFieldName)) continue

      const ft = String((f as any)?.filter_type ?? '').trim().toLowerCase()
      const tok = parseAggToken(rawFieldName)
      const isMeasure =
        ft === 'measure' ||
        Boolean((f as any)?.is_measure) ||
        Boolean(tok)

      if (op === 'TOP_N') {
        const colId = resolveColOrRawWhenNoSchema(rawFieldName)
        if (!colId) continue
        const fieldSql = `${baseRef}.${quoteIdent(colId)}`
        const fi = filterIndexByRef.get(f)
        const pred = buildTopNPredicate(
          fieldSql,
          colId,
          rawFieldName,
          value,
          contextFilters,
          templateMode ? { filterIndex: (fi ?? -1), filterIndexByRef } : null,
        )
        if (pred) pushFilterPredicate(pred)
        continue
      }

      if (isMeasure && usesAggregation) {
        if (explicitEmptyInFilter) {
          havingConditions.push('FALSE')
          continue
        }
        // Tableau order-of-ops: measure filters apply after aggregation (HAVING), not at row-level (WHERE).
        const innerField = tok ? tok.field : rawFieldName
        const aggRaw = String((f as any)?.aggregation ?? '').trim()
        const aggUpper = (aggRaw ? aggRaw : (tok ? tok.agg : 'SUM')).toUpperCase()
        const colId = resolveColOrRawWhenNoSchema(innerField)
        if (!colId || colId === UNRESOLVED_FIELD) continue
        const innerExpr = `${baseRef}.${quoteIdent(colId)}`
        const aggExpr = buildAggExpr(aggUpper, innerExpr)
        const pred = (op === 'RELATIVE_DATE')
          ? null
          : buildExprPredicate(aggExpr, op, value)
        if (pred) havingConditions.push(pred)
        continue
      }

      // For filters, prefer the dimension expression with time grain applied (from dimExprByKey)
      // This ensures filters match the SELECT clause expressions (e.g., EXTRACT(month FROM ...))
      const keyBase = normKey(rawFieldName) || rawFieldName.toLowerCase()
      let fieldExprRaw: string | null = null
      const filterTimeGrain = String((f as any)?.time_grain ?? (f as any)?.timeGrain ?? (f as any)?.date_grain ?? '').trim().toLowerCase()
      const filterIsDiscrete = (f as any)?.is_discrete
      
      // Try to find the field in dimExprByKey (with time grain applied)
      // Check multiple keys: with grain, without grain, base key
      if (dimExprByKey && dimExprByKey.size > 0) {
        // Try exact match first (if filter has time_grain metadata)
        const filterDiscreteKey = (filterIsDiscrete === true) ? 'disc' : (filterIsDiscrete === false ? 'cont' : 'auto')
        if (filterTimeGrain) {
          const lookupKey = `${keyBase}::${filterTimeGrain}::${filterDiscreteKey}`
          fieldExprRaw = dimExprByKey.get(lookupKey) || null
        }
        // Fallback to base key (may have default grain)
        if (!fieldExprRaw) {
          fieldExprRaw = dimExprByKey.get(keyBase) || null
        }
        // Also try with just the time grain (no discrete key)
        if (!fieldExprRaw && filterTimeGrain) {
          const lookupKey2 = `${keyBase}::${filterTimeGrain}`
          fieldExprRaw = dimExprByKey.get(lookupKey2) || null
        }
      }
      
      // If not found in dimExprByKey, fall back to fieldExprFor
      if (!fieldExprRaw) {
        fieldExprRaw =
          resolvedWeeklyTimePeriodExprForField?.(rawFieldName) ||
          fieldExprFor(rawFieldName, baseRef)
      }
      if (filterTimeGrain && fieldExprRaw && !isNullishExpr(fieldExprRaw) && !/\bdate_trunc\s*\(/i.test(String(fieldExprRaw))) {
        fieldExprRaw = applyTimeGrain(fieldExprRaw, filterTimeGrain, typeof filterIsDiscrete === 'boolean' ? filterIsDiscrete : false)
      }
      
      // Debug logging for field expression lookup
      if (rawFieldName.toLowerCase().includes('order date') || rawFieldName.toLowerCase().includes('date')) {
        console.log('[worksheetQueryBuilder] Filter field expression lookup:', {
          field: rawFieldName,
          keyBase,
          foundInDimExprByKey: fieldExprRaw !== null && dimExprByKey && dimExprByKey.has(keyBase),
          fieldExpr: fieldExprRaw,
          dimExprByKeySize: dimExprByKey?.size || 0,
          dimExprByKeyKeys: dimExprByKey ? Array.from(dimExprByKey.keys()).filter(k => k.includes(keyBase)).slice(0, 5) : []
        })
      }
      
      if (isNullishExpr(fieldExprRaw)) continue
      const fieldSql = `(${fieldExprRaw})`
      if (explicitEmptyInFilter) {
        pushFilterPredicate('FALSE')
        continue
      }

      if (op === 'RELATIVE_DATE') {
        if (templateMode) {
          const fi = filterIndexByRef.get(f)
          if (!(Number.isFinite(fi) && (fi as number) >= 0)) {
            throw new Error(`templateMode: RELATIVE_DATE is missing filter index for field "${rawFieldName}"`)
          }
          pushFilterPredicate(buildRelativeDatePredicateTemplate(fieldSql, fi as number))
        } else {
          const pred = buildRelativeDatePredicate(fieldSql, value)
          if (pred) pushFilterPredicate(pred)
        }
        continue
      }
      if (op === 'IS NULL') {
        if (looksWindowExpr(fieldExprRaw)) {
          postSelectFilters.push({ field: rawFieldName, operator: 'IS NULL' })
          continue
        }
        pushFilterPredicate(`${fieldSql} IS NULL`)
      } else if (op === 'IS NOT NULL') {
        if (looksWindowExpr(fieldExprRaw)) {
          postSelectFilters.push({ field: rawFieldName, operator: 'IS NOT NULL' })
          continue
        }
        pushFilterPredicate(`${fieldSql} IS NOT NULL`)
      } else if (op === 'IN' || op === 'NOT IN') {
        const not = op.startsWith('NOT')
        // Tableau "All" selection for categorical filters should be a no-op.
        // We encode this as IN with value=null in some import paths.
        if (value === null || value === undefined) continue
        if (Array.isArray(value)) {
          const { values: cleaned, includeNull } = splitInFilterValues(value)
          if (cleaned.length === 0) {
            if (includeNull) {
              const pred = buildInPredicate(fieldSql, cleaned, includeNull, not, { includeNullsOnNot: true })
              if (pred) pushFilterPredicate(pred)
            } else if (!not && explicitEmpty) {
              pushFilterPredicate('FALSE')
            }
            continue
          }
          const pred = buildInPredicate(fieldSql, cleaned, includeNull, not, { includeNullsOnNot: true })
          if (pred) pushFilterPredicate(pred)
        } else {
          const pred = buildInPredicateFromValue(fieldSql, value, not, { includeNullsOnNot: true })
          if (pred) pushFilterPredicate(pred)
        }
      } else if (op === 'BETWEEN') {
        if (Array.isArray(value) && value.length >= 2) {
          pushFilterPredicate(buildBetweenPredicate(fieldSql, value[0], value[1], f, rawFieldName))
        }
      } else if (op === '=' || op === '!=' || op === '<>' || op === '>' || op === '>=' || op === '<' || op === '<=') {
        pushFilterPredicate(`${fieldSql} ${op} ${escapeValue(value)}`)
      } else {
        // Best-effort: treat as binary op (e.g. LIKE)
        pushFilterPredicate(`${fieldSql} ${op} ${escapeValue(value)}`)
      }
    }

    const limRaw = Number.isFinite(Number(limit)) ? Math.floor(Number(limit)) : 10_000
    const lim = limRaw > 0 ? Math.min(250_000, limRaw) : 0
    let sql = `SELECT ${selectList.join(', ')} FROM ${vizSourceSql} AS ${quoteIdent(baseAlias)}`
    if (whereConditions.length) sql += ` WHERE ${whereConditions.join(' AND ')}`
    // GROUP BY only when we have aggregated measures.
    if ((measSelects.length || extraSelects.length) && groupByExprs.length) sql += ` GROUP BY ${groupByExprs.join(', ')}`
    if (havingConditions.length) sql += ` HAVING ${havingConditions.join(' AND ')}`
    if (qualifyConditions.length) sql += ` QUALIFY ${qualifyConditions.join(' AND ')}`
    if (postSelectFilters.length) {
      const postAlias = quoteIdent('__fg_post')
      const postPred = postSelectFilters
        .map((f) => `${postAlias}.${quoteIdent(String(f.field || '').trim())} ${f.operator}`)
        .join(' AND ')
      sql = `SELECT * FROM (${sql}) AS ${postAlias} WHERE ${postPred}`
    }
    if (dimExprs.length) {
      const orderTerms = postSelectFilters.length
        ? dimPills
            .map((d) => String((d as any)?.field ?? '').trim())
            .filter(Boolean)
            .map((name) => `${quoteIdent(name)} ASC NULLS LAST`)
        : dimExprs
            .map((expr) => String(expr ?? '').trim())
            .filter((expr) => expr && !isNullishExpr(expr) && !isScalarLiteralExpr(expr) && !isSqlConstantLiteralExpression(expr))
            .map((expr) => `${expr} ASC NULLS LAST`)
      if (orderTerms.length) sql += ` ORDER BY ${orderTerms.join(', ')}`
    }
    if (limRaw > 0) sql += ` LIMIT ${lim}`
    return sql
  }

  if (spec.kind === 'viz') {
    if (options.relationshipModel && Array.isArray((options.relationshipModel as any)?.tables) && (options.relationshipModel as any).tables.length >= 2) {
      const buildRelationshipAwareTidyQuery = (args: {
        dimFields: Array<{ field: string; time_grain?: string | null; is_discrete?: boolean | null }>
        measures: Array<{ field: string; aggregation: string; alias: string }>
      }): string => {
        // NOTE: legacy implementation below remains for now; ensure behavior stays consistent.
        return buildRelationshipAwareTidyQueryShared(args)
        /*
        const model = options.relationshipModel as any
        const tablesRaw: any[] = Array.isArray(model?.tables) ? model.tables : []
        const relsRaw: any[] = Array.isArray(model?.relationships) ? model.relationships : []
        const primaryName = String(model?.primary_table ?? '').trim()

        const normTable = (s: any) => normalizeTableauDatasourceKey(String(s ?? '').trim())
        const tableByKey = new Map<string, any>()
        for (const t0 of tablesRaw) {
          const t = (t0 && typeof t0 === 'object') ? t0 : null
          if (!t) continue
          const name = String(t.name ?? '').trim()
          const key = normTable(name)
          const fromSql = String(t.fromSql ?? '').trim()
          if (!name || !key || !fromSql) continue
          tableByKey.set(key, {
            name,
            key,
            fromSql,
            extractColumns: (Array.isArray(t.extractColumns) ? t.extractColumns : null),
            fieldManifest: (t.fieldManifest ?? null),
          })
        }

        const primaryKey = (() => {
          const k = normTable(primaryName)
          if (k && tableByKey.has(k)) return k
          const first = Array.from(tableByKey.keys())[0] || ''
          return first
        })()

        const resolveColIn = (_tbl: any, fieldRaw: any): string => {
          const field = String(fieldRaw ?? '').trim()
          if (!field) return ''
          return String(resolveColFromBinding(field) ?? '').trim()
        }

        const compileFormulaForAlias = (formula: string, baseAlias: string): string | null => {
          const raw = String(formula ?? '').trim()
          if (!raw) return null
          const compiled = compileTableauFormulaToSql(raw)
          const compiledSql = compiled?.sql ? String(compiled.sql).trim() : ''
          if (!compiledSql || compiled.hasErrors) return null
          let sql = compiledSql
          try { sql = sql.replaceAll('"t".', `${baseAlias}.`) } catch { }
          try { sql = sql.replaceAll('t.', `${baseAlias}.`) } catch { }
          return sql
        }

        const extractFieldRefsFromFormula = (formulaRaw: any): string[] => {
          const formula = String(formulaRaw ?? '')
          if (!formula) return []
          const out: string[] = []
          for (const m of formula.matchAll(/\[([^\]]+)\](?:\s*\.\s*\[([^\]]+)\])?/g)) {
            const tok = String(m?.[2] ?? m?.[1] ?? '').trim()
            if (!tok) continue
            const cleaned = cleanBracketedFieldRef0(tok) || tok
            if (!cleaned) continue
            const k = normalizeTableauFieldKey(cleaned)
            if (!k) continue
            if (k === 'measure names' || k === 'measure values' || k === 'multiple values') continue
            out.push(cleaned)
          }
          return out
        }

        const chooseTableForField = (fieldRaw: any): { tableKey: string; kind: 'physical' | 'calc' | 'calc_cross' | 'unknown' } => {
          const field = String(fieldRaw ?? '').trim()
          if (!field) return { tableKey: primaryKey, kind: 'unknown' }
          for (const [k, t] of tableByKey.entries()) {
            if (resolveColIn(t, field)) return { tableKey: k, kind: 'physical' }
          }
          const formula = lookupCalculatedFormula(field)
          if (formula) {
            const refs = extractFieldRefsFromFormula(formula)
            if (refs.length) {
              for (const [k, t] of tableByKey.entries()) {
                const ok = refs.every((r) => Boolean(resolveColIn(t, r)))
                if (ok) return { tableKey: k, kind: 'calc' }
              }
              return { tableKey: primaryKey, kind: 'calc_cross' }
            }
          }
          return { tableKey: primaryKey, kind: formula ? 'calc' : 'unknown' }
        }

        const fieldExprInTable = (tblKey: string, fieldRaw: any): string => {
          const field = String(fieldRaw ?? '').trim()
          if (!field) return 'NULL'
          const tbl = tableByKey.get(tblKey)
          const baseAlias = quoteIdent(`t_${tblKey}`)
          const col = tbl ? resolveColIn(tbl, field) : ''
          if (col) return `${baseAlias}.${quoteIdent(col)}`
          const formula = lookupCalculatedFormula(field)
          if (!formula) return 'NULL'
          // Only compile table-local calcs here. Cross-table calcs must be computed after joins.
          try {
            const refs = extractFieldRefsFromFormula(formula)
            if (refs.length && tbl) {
              const ok = refs.every((r) => Boolean(resolveColIn(tbl, r)))
              if (!ok) return 'NULL'
            }
          } catch { }
          const lit = numericLiteralFromFormula(formula)
          if (lit != null) return String(lit)
          const compiled = compileFormulaForAlias(formula, baseAlias)
          return compiled ? compiled : 'NULL'
        }

        const normCardinality = (raw: any): 'one_to_one' | 'one_to_many' | 'many_to_one' | 'many_to_many' | 'unknown' => {
          const s = String(raw ?? '').trim().toLowerCase().replace(/\s+/g, '')
          if (!s) return 'unknown'
          if (s === '1:1' || s.includes('one-to-one') || s.includes('one_to_one')) return 'one_to_one'
          if (s === '1:m' || s === '1:n' || s.includes('one-to-many') || s.includes('one_to_many')) return 'one_to_many'
          if (s === 'm:1' || s === 'n:1' || s.includes('many-to-one') || s.includes('many_to_one')) return 'many_to_one'
          if (s === 'm:m' || s === 'n:n' || s.includes('many-to-many') || s.includes('many_to_many')) return 'many_to_many'
          return 'unknown'
        }

        const aggExpr = (aggRaw: any, colExpr: string): string => {
          const a = String(aggRaw ?? '').trim().toLowerCase()
          if (a === 'avg' || a === 'average' || a === 'mean') return `AVG(TRY_CAST(${colExpr} AS DOUBLE))`
          if (a === 'min') return `MIN(${colExpr})`
          if (a === 'max') return `MAX(${colExpr})`
          if (a === 'count') return `COUNT(${colExpr})`
          if (a === 'countd' || a === 'count distinct' || a === 'count_distinct') return `COUNT(DISTINCT ${colExpr})`
          return `SUM(TRY_CAST(${colExpr} AS DOUBLE))`
        }

        // Assign each dim/measure to a table (best-effort: first table that resolves).
        const dimByTable = new Map<string, Array<{ field: string; expr: string; groupExpr: string }>>()
        const measByTable = new Map<string, Array<{ field: string; alias: string; expr: string }>>()
        const usedTables = new Set<string>()
        const crossCalcs: Array<{ alias: string; formula: string }> = []
        const depAggsByTable = new Map<string, Map<string, string>>()

        const normalizeAggTok0 = (raw: any, fallbackFieldName: string): string => {
          const s = String(raw ?? '').trim().toLowerCase()
          if (!s) return 'sum'
          if (s === 'average' || s === 'mean') return 'avg'
          if (s === 'ctd') return 'countd'
          if (s === 'count distinct' || s === 'count_distinct' || s === 'countd' || s === 'distinct_count') return 'countd'
          if (s === 'sum' || s === 'avg' || s === 'min' || s === 'max' || s === 'count' || s === 'countd') return s
          const inline = parseAggFieldName(fallbackFieldName)
          if (inline) {
            const fn = String(inline.agg ?? '').trim().toLowerCase()
            if (fn === 'average' || fn === 'mean') return 'avg'
            if (fn === 'countd' || fn === 'count_distinct' || fn === 'countdistinct') return 'countd'
            if (fn === 'sum' || fn === 'avg' || fn === 'min' || fn === 'max' || fn === 'count') return fn
          }
          return 'sum'
        }

        const aggTokFromFn = (fnRaw: any): string => {
          const fn = String(fnRaw ?? '').trim().toUpperCase()
          if (!fn) return 'sum'
          if (fn === 'AVG' || fn === 'AVERAGE' || fn === 'MEAN') return 'avg'
          if (fn === 'MIN') return 'min'
          if (fn === 'MAX') return 'max'
          if (fn === 'COUNT') return 'count'
          if (fn === 'COUNTD' || fn === 'COUNT_DISTINCT' || fn === 'COUNTDISTINCT') return 'countd'
          if (fn === 'SUM') return 'sum'
          return 'sum'
        }

        const inferAggTokForRefInFormula = (formulaRaw: string, refField: string, defaultAgg: string): string => {
          const formula = String(formulaRaw ?? '')
          const ref = String(refField ?? '').trim()
          if (!formula || !ref) return defaultAgg
          const esc = escapeRegExp(ref)
          const atom = String.raw`(?:\[[^\]]+\]\s*\.\s*)?\[${esc}\]`
          const patterns: Array<{ fn: string; re: RegExp }> = [
            { fn: 'COUNTD', re: new RegExp(String.raw`\b(COUNTD|COUNT_DISTINCT|COUNTDISTINCT)\s*\(\s*${atom}\s*\)`, 'i') },
            { fn: 'COUNT', re: new RegExp(String.raw`\bCOUNT\s*\(\s*${atom}\s*\)`, 'i') },
            { fn: 'AVG', re: new RegExp(String.raw`\b(AVG|AVERAGE|MEAN)\s*\(\s*${atom}\s*\)`, 'i') },
            { fn: 'SUM', re: new RegExp(String.raw`\bSUM\s*\(\s*${atom}\s*\)`, 'i') },
            { fn: 'MIN', re: new RegExp(String.raw`\bMIN\s*\(\s*${atom}\s*\)`, 'i') },
            { fn: 'MAX', re: new RegExp(String.raw`\bMAX\s*\(\s*${atom}\s*\)`, 'i') },
          ]
          for (const p of patterns) {
            if (p.re.test(formula)) return aggTokFromFn(p.fn)
          }
          return defaultAgg
        }

        const mergeAggTok = (a: string, b: string): string => {
          const rank: Record<string, number> = { countd: 60, count: 50, avg: 40, sum: 30, min: 20, max: 20 }
          const aa = String(a ?? '').trim().toLowerCase() || 'sum'
          const bb = String(b ?? '').trim().toLowerCase() || 'sum'
          return (rank[bb] ?? 0) > (rank[aa] ?? 0) ? bb : aa
        }

        const claimDim = (tblKey: string, entry: { field: string; expr: string; groupExpr: string }) => {
          const arr = dimByTable.get(tblKey) || []
          arr.push(entry)
          dimByTable.set(tblKey, arr)
          usedTables.add(tblKey)
        }
        const claimMeas = (tblKey: string, entry: { field: string; alias: string; expr: string }) => {
          const aliasKey = String(entry?.alias ?? '').trim()
          if (!aliasKey) return
          const existing = measByTable.get(tblKey) || []
          if (existing.some((x) => String((x as any)?.alias ?? '').trim() === aliasKey)) {
            usedTables.add(tblKey)
            return
          }
          const arr = measByTable.get(tblKey) || []
          arr.push(entry)
          measByTable.set(tblKey, arr)
          usedTables.add(tblKey)
        }

        for (const d of args.dimFields) {
          const f = String(d?.field ?? '').trim()
          if (!f) continue
          const chosenInfo = chooseTableForField(f)
          const chosen = chosenInfo.tableKey
          const rawExpr = fieldExprInTable(chosen, f)
          const expr = applyTimeGrain(rawExpr, (d as any)?.time_grain ?? null, (d as any)?.is_discrete ?? null)
          claimDim(chosen, { field: f, expr, groupExpr: expr })
        }

        for (const m of args.measures) {
          const f = String(m?.field ?? '').trim()
          const a = String(m?.aggregation ?? '').trim()
          const aliasName = String(m?.alias ?? '').trim()
          if (!f || !aliasName) continue
          const chosenInfo = chooseTableForField(f)
          const chosen = chosenInfo.tableKey
          if (chosenInfo.kind === 'calc_cross') {
            const formula = lookupCalculatedFormula(f)
            if (!formula) continue
            crossCalcs.push({ alias: aliasName, formula })
            // Lift dependencies as aggregated inputs from their source tables so post-join compilation can bind them.
            const defaultAgg = normalizeAggTok0(a || 'sum', f)
            const refs = extractFieldRefsFromFormula(formula)
            for (const r of refs) {
              for (const [k, t] of tableByKey.entries()) {
                const col = resolveColIn(t, r)
                if (!col) continue
                const aggTok = inferAggTokForRefInFormula(formula, r, defaultAgg)
                const m0 = depAggsByTable.get(k) || new Map<string, string>()
                const prev = m0.get(col) || ''
                m0.set(col, mergeAggTok(prev, aggTok))
                depAggsByTable.set(k, m0)
                usedTables.add(k)
                break
              }
            }
            continue
          }
          const baseExpr = fieldExprInTable(chosen, f)
          // Wrap aggregation unless the expression already contains an aggregate/window.
          const looksAgg = /\b(SUM|AVG|MIN|MAX|COUNT)\s*\(/i.test(baseExpr)
          const looksWin = /\bOVER\s*\(/i.test(baseExpr)
          const expr = (looksAgg || looksWin) ? baseExpr : aggExpr(a || 'sum', baseExpr)
          claimMeas(chosen, { field: f, alias: aliasName, expr })
        }

        // Add dependency aggregates needed for cross-table calcs.
        for (const [k, cols] of depAggsByTable.entries()) {
          const tAlias = quoteIdent(`t_${k}`)
          for (const [col, aggTok] of cols.entries()) {
            const c = String(col ?? '').trim()
            if (!c) continue
            claimMeas(k, { field: c, alias: c, expr: aggExpr(aggTok || 'sum', `${tAlias}.${quoteIdent(c)}`) })
          }
        }

        // Relationship edges between known tables.
        const rels = relsRaw
          .map((r0) => {
            const r = (r0 && typeof r0 === 'object') ? r0 : null
            if (!r) return null
            const a = normTable(r.a)
            const b = normTable(r.b)
            if (!a || !b || !tableByKey.has(a) || !tableByKey.has(b)) return null
            const clauses0 = Array.isArray(r.clauses) ? r.clauses : []
            const clauses = clauses0
              .map((c0: any) => {
                const c = (c0 && typeof c0 === 'object') ? c0 : null
                if (!c) return null
                const l = resolveColIn(tableByKey.get(a), c.left)
                const rr = resolveColIn(tableByKey.get(b), c.right)
                if (!l || !rr) return null
                return { left: l, right: rr }
              })
              .filter(Boolean) as Array<{ left: string; right: string }>
            if (!clauses.length) return null
            return { a, b, clauses, cardinality: normCardinality(r.cardinality ?? null) }
          })
          .filter(Boolean) as Array<{ a: string; b: string; clauses: Array<{ left: string; right: string }>; cardinality: string }>

        // Ensure we include intermediate tables needed to connect to used tables.
        const needed = new Set<string>(usedTables.size ? Array.from(usedTables) : [primaryKey])
        needed.add(primaryKey)
        const included = new Set<string>([primaryKey])
        const joinOrder: Array<{ edge: any; next: string; dir: 'a_to_b' | 'b_to_a' }> = []
        for (let pass = 0; pass < 16; pass++) {
          let progressed = false
          for (const e of rels) {
            if (included.has(e.a) && !included.has(e.b)) {
              included.add(e.b)
              joinOrder.push({ edge: e, next: e.b, dir: 'a_to_b' })
              progressed = true
            } else if (included.has(e.b) && !included.has(e.a)) {
              included.add(e.a)
              joinOrder.push({ edge: e, next: e.a, dir: 'b_to_a' })
              progressed = true
            }
          }
          if (!progressed) break
          if (Array.from(needed).every((k) => included.has(k))) break
          // Expand needed with intermediates as we traverse.
          for (const k of included) needed.add(k)
        }

        // Per-table WHERE clauses: apply filters to tables where the filter field resolves.
        const whereByTable = new Map<string, string[]>()
        const pushWhere = (tblKey: string, cond: string) => {
          if (!cond) return
          const arr = whereByTable.get(tblKey) || []
          arr.push(cond)
          whereByTable.set(tblKey, arr)
        }

        const filterList: any[] = Array.isArray(filters) ? (filters as any[]) : []
        for (const f0 of filterList) {
          const f = (f0 && typeof f0 === 'object') ? f0 : null
          if (!f) continue
          const field = String((f as any)?.field_name ?? '').trim()
          if (!field) continue
          const opRaw = String((f as any)?.operator ?? '').trim()
          const op = opRaw ? opRaw.toUpperCase() : ''
          if (shouldSkipSqlFilter(f)) continue
          const value = (f as any)?.value
          for (const [k, t] of tableByKey.entries()) {
            const col = resolveColIn(t, field)
            if (!col) continue
            const tAlias = quoteIdent(`t_${k}`)
            const colSql = `${tAlias}.${quoteIdent(col)}`
            if (!op || op === '=') {
              const pred = buildInPredicateFromValue(colSql, value, false, { includeNullsOnNot: true })
              if (pred) pushWhere(k, pred)
            }
            else if (op === '!=' || op === '<>') {
              const pred = buildInPredicateFromValue(colSql, value, true, { includeNullsOnNot: true })
              if (pred) pushWhere(k, pred)
            }
            else if (op === '>' || op === '>=' || op === '<' || op === '<=') pushWhere(k, `${colSql} ${op} ${escapeValue(value)}`)
            else if (op === 'IS NULL') pushWhere(k, `${colSql} IS NULL`)
            else if (op === 'IS NOT NULL') pushWhere(k, `${colSql} IS NOT NULL`)
            else if (op === 'LIKE' || op === 'NOT LIKE') pushWhere(k, `${colSql} ${op} ${escapeValue(value)}`)
            else if (op === 'IN' || op === 'NOT IN') {
              const pred = buildInPredicateFromValue(colSql, value, op === 'NOT IN', { includeNullsOnNot: true })
              if (pred) pushWhere(k, pred)
            } else if (op === 'BETWEEN' && Array.isArray(value) && value.length >= 2) {
              pushWhere(k, buildBetweenPredicate(colSql, value[0], value[1], f, k))
            }
          }
        }

        // Join key columns needed for each table (so ON clauses can reference them).
        const joinColsByTable = new Map<string, Set<string>>()
        const needJoinCol = (tblKey: string, col: string) => {
          if (!tblKey || !col) return
          const set = joinColsByTable.get(tblKey) || new Set<string>()
          set.add(col)
          joinColsByTable.set(tblKey, set)
        }
        for (const e of rels) {
          if (!needed.has(e.a) && !needed.has(e.b)) continue
          for (const c of e.clauses) {
            needJoinCol(e.a, c.left)
            needJoinCol(e.b, c.right)
          }
        }

        // Build per-table aggregate subqueries.
        const subByKey = new Map<string, { sql: string; alias: string }>()
        for (const k of needed) {
          const tbl = tableByKey.get(k)
          if (!tbl) continue
          const tAlias = quoteIdent(`t_${k}`)
          const keyCols = Array.from(joinColsByTable.get(k) || new Set<string>())
          const dims = dimByTable.get(k) || []
          const meas = measByTable.get(k) || []

          const selectParts: string[] = []
          const groupBy: string[] = []

          for (const c of keyCols) {
            selectParts.push(`${tAlias}.${quoteIdent(c)} AS ${quoteIdent(c)}`)
            groupBy.push(`${tAlias}.${quoteIdent(c)}`)
          }
          for (const d of dims) {
            selectParts.push(`${d.expr} AS ${quoteIdent(d.field)}`)
            if (canSqlExpressionAppearInGroupBy(d.expr)) groupBy.push(d.groupExpr)
          }
          for (const m of meas) {
            selectParts.push(`${m.expr} AS ${quoteIdent(m.alias)}`)
          }
          if (!selectParts.length) {
            // Still emit join keys if we have them.
            if (keyCols.length) {
              // already included
            } else {
              // nothing to select; skip this table entirely
              continue
            }
          }

          let sql = `SELECT ${selectParts.join(', ')} FROM ${String(tbl.fromSql).trim()} AS ${tAlias}`
          const where = whereByTable.get(k) || []
          if (where.length) sql += ` WHERE ${where.join(' AND ')}`
          if (groupBy.length && meas.length) sql += ` GROUP BY ${groupBy.join(', ')}`
          subByKey.set(k, { sql: `(${sql})`, alias: quoteIdent(`q_${k}`) })
        }

        // Stitch joins (LEFT JOIN from primary outward).
        const base = subByKey.get(primaryKey)
        if (!base) {
          // Fallback: no subquery built; return a minimal parquet query.
          if (Number(limit) <= 0) {
            return `SELECT * FROM ${parquetRef} AS ${quoteIdent('t')}`
          }
          return `SELECT * FROM ${parquetRef} AS ${quoteIdent('t')} LIMIT ${Math.max(1, Math.min(250_000, Math.floor(Number(limit) || 10_000)))}`
        }
        let from = `${base.sql} AS ${base.alias}`
        const joined = new Set<string>([primaryKey])

        const joinEdge = (edge: any, next: string, dir: 'a_to_b' | 'b_to_a') => {
          const nextSub = subByKey.get(next)
          if (!nextSub) return
          const aKey = dir === 'a_to_b' ? edge.a : edge.b
          const bKey = dir === 'a_to_b' ? edge.b : edge.a
          const aAlias = subByKey.get(aKey)?.alias
          const bAlias = subByKey.get(bKey)?.alias
          if (!aAlias || !bAlias) return
          const on = edge.clauses.map((c: any) => {
            const l = dir === 'a_to_b' ? c.left : c.right
            const r = dir === 'a_to_b' ? c.right : c.left
            return `${aAlias}.${quoteIdent(String(l))} = ${bAlias}.${quoteIdent(String(r))}`
          }).join(' AND ')
          from = `${from} LEFT JOIN ${nextSub.sql} AS ${bAlias} ON ${on || 'TRUE'}`
          joined.add(next)
        }

        for (const step of joinOrder) {
          if (joined.has(step.next)) continue
          joinEdge(step.edge, step.next, step.dir)
        }

        // Build an inner joined subquery with all physical outputs needed for post-join calcs.
        const innerSelect: string[] = []
        for (const d of args.dimFields) {
          const f = String(d?.field ?? '').trim()
          if (!f) continue
          // Find which table produced this dim.
          let foundAlias: string | null = null
          for (const [k, arr] of dimByTable.entries()) {
            if (arr.some((x) => x.field === f)) {
              foundAlias = subByKey.get(k)?.alias || null
              break
            }
          }
          if (!foundAlias) foundAlias = base.alias
          innerSelect.push(`${foundAlias}.${quoteIdent(f)} AS ${quoteIdent(f)}`)
        }
        for (const m of args.measures) {
          const aliasName = String(m?.alias ?? '').trim()
          if (!aliasName) continue
          if (crossCalcs.some((c) => c.alias === aliasName)) continue
          let foundAlias: string | null = null
          for (const [k, arr] of measByTable.entries()) {
            if (arr.some((x) => x.alias === aliasName)) {
              foundAlias = subByKey.get(k)?.alias || null
              break
            }
          }
          if (!foundAlias) foundAlias = base.alias
          innerSelect.push(`${foundAlias}.${quoteIdent(aliasName)} AS ${quoteIdent(aliasName)}`)
        }
        // Include lifted dependency columns for cross-table calcs.
        for (const cols of depAggsByTable.values()) {
          for (const col of Array.from(cols.keys())) {
            const c = String(col ?? '').trim()
            if (!c) continue
            // Find which table produced this aggregate column.
            let foundAlias: string | null = null
            for (const [k, arr] of measByTable.entries()) {
              if (arr.some((x) => x.alias === c)) {
                foundAlias = subByKey.get(k)?.alias || null
                break
              }
            }
            if (!foundAlias) foundAlias = base.alias
            innerSelect.push(`${foundAlias}.${quoteIdent(c)} AS ${quoteIdent(c)}`)
          }
        }

        const limRaw = Number.isFinite(Number(limit)) ? Math.floor(Number(limit)) : 10_000
        const lim = limRaw > 0 ? Math.min(250_000, limRaw) : 0
        const joinedSql = `SELECT ${innerSelect.length ? innerSelect.join(', ') : '*'} FROM ${from}`
        const tAlias = quoteIdent('t')
        const outerSelect: string[] = []
        for (const d of args.dimFields) {
          const f = String(d?.field ?? '').trim()
          if (!f) continue
          outerSelect.push(`${tAlias}.${quoteIdent(f)} AS ${quoteIdent(f)}`)
        }
        for (const m of args.measures) {
          const aliasName = String(m?.alias ?? '').trim()
          if (!aliasName) continue
          const cross = crossCalcs.find((c) => c.alias === aliasName) || null
          if (cross) {
            const compiled = compileTableauFormulaToSql(String(cross.formula ?? ''))
            const expr = compiled?.sql && !compiled.hasErrors ? String(compiled.sql).trim() : 'NULL'
            outerSelect.push(`${expr} AS ${quoteIdent(aliasName)}`)
          } else {
            outerSelect.push(`${tAlias}.${quoteIdent(aliasName)} AS ${quoteIdent(aliasName)}`)
          }
        }
        let sql = `SELECT ${outerSelect.length ? outerSelect.join(', ') : '*'} FROM (${joinedSql}) AS ${tAlias}`
        if (limRaw > 0) sql += ` LIMIT ${lim}`
        return sql
        */
      }

      // Build dim/measures from the VisualSpec pills (same heuristics as v0).
      const v = spec.spec as VisualSpec
      const layerSpecsRaw: any[] = Array.isArray((v as any)?.layers) ? ((v as any).layers as any[]) : []
      const baseLayer = { shelves: (v as any)?.shelves, marks: (v as any)?.marks, view: (v as any)?.view }
      const layerSpecs: any[] = layerSpecsRaw.length ? [baseLayer, ...layerSpecsRaw] : [baseLayer]
      const withShelf = (pill: any, shelf: string): any => {
        if (!pill || typeof pill !== 'object') return pill
        const existingShelf = String((pill as any)?.shelf ?? '').trim().toLowerCase()
        if (existingShelf) return pill
        return { ...(pill as any), shelf }
      }
      const pillsForLayer = (layer: any): any[] => {
        const cols = Array.isArray(layer?.shelves?.columns) ? layer.shelves.columns : []
        const rows = Array.isArray(layer?.shelves?.rows) ? layer.shelves.rows : []
        const mk = (layer?.marks && typeof layer.marks === 'object') ? layer.marks : {}
        const encL = (k: string): any[] => (Array.isArray((mk as any)?.[k]) ? (mk as any)[k] as any[] : [])
        return [
          ...cols.map((p: any) => withShelf(p, 'columns')),
          ...rows.map((p: any) => withShelf(p, 'rows')),
          ...encL('color').map((p: any) => withShelf(p, 'color')),
          ...encL('size').map((p: any) => withShelf(p, 'size')),
          ...encL('text').map((p: any) => withShelf(p, 'text')),
          ...encL('detail').map((p: any) => withShelf(p, 'detail')),
          ...encL('tooltip').map((p: any) => withShelf(p, 'tooltip')),
          ...encL('shape').map((p) => withShelf(p, 'shape')),
          ...encL('path').map((p) => withShelf(p, 'path')),
          ...encL('angle').map((p) => withShelf(p, 'angle')),
        ].filter(Boolean)
      }
      const pills: any[] = layerSpecs.flatMap(pillsForLayer).filter(Boolean)
      const dimFields: Array<{ field: string; time_grain?: string | null; is_discrete?: boolean | null }> = []
      const measures: Array<{ field: string; aggregation: string; alias: string }> = []
      const seenDim = new Set<string>()
      const seenMeas = new Set<string>()
      const normKey = (s: any) => normalizeTableauFieldKey(String(s ?? ''))
      const isMeasureNames0 = (s: any) => normKey(s) === 'measure names'
      const isMultipleValues0 = (s: any) => {
        const k = normKey(s)
        return k === 'multiple values' || k === 'measure values'
      }
      const aliasKeyForMeasure0 = (s: string): string =>
        normalizeTableauFieldKey(String(s ?? '').replace(/^[A-Z]+\s*\((.*)\)$/i, '$1'))
      const measureAliasForPill0 = (field: string, aggFinal: string, rawAlias: any): string => {
        const fn = String(aggFinal || 'SUM').trim().toUpperCase()
        const canonical = `${fn}(${field})`
        const aliasBase = String(rawAlias ?? '').trim()
        if (!aliasBase) return canonical
        const aliasKey = aliasKeyForMeasure0(aliasBase)
        const fieldKey = aliasKeyForMeasure0(field)
        const canonicalKey = normalizeTableauFieldKey(canonical)
        const hasUnbalancedParens = (aliasBase.includes(')') && !aliasBase.includes('(')) || (aliasBase.includes('(') && !aliasBase.includes(')'))
        const isTruncatedAggregateSuffix =
          aliasKey.length > 0 &&
          canonicalKey.endsWith(aliasKey) &&
          aliasKey !== canonicalKey &&
          (!fieldKey || !aliasKey.includes(fieldKey))
        if (hasUnbalancedParens || isTruncatedAggregateSuffix) return canonical
        return aliasBase
      }
      for (const p of pills) {
        const field = String((p as any)?.field ?? (p as any)?.field_name ?? '').trim()
        if (!field) continue
        if (isMeasureNames0(field) || isMultipleValues0(field)) continue
        const aggRaw = (p as any)?.aggregation
        const aggTok = normalizeAggTokenSimple(aggRaw, field)
        const agg = aggTok ? String(aggTok).trim() : ((aggRaw === null || aggRaw === undefined) ? '' : String(aggRaw).trim())
        const shelf = String((p as any)?.shelf ?? '').trim().toLowerCase()
        const forceDimension = shelf === 'detail' || shelf === 'path'
        const isMeasure = isMeasurePill(p, forceDimension) || Boolean(aggTok) || Boolean(agg)
        if (!isMeasure) {
          const timeGrainRaw = (p as any)?.time_grain ?? (p as any)?.timeGrain ?? null
          const timeGrain =
            String(timeGrainRaw ?? '').trim().toLowerCase() ||
            inferTimeGrainFromPill(p) ||
            (field ? timeGrainFromMeta(field) : null) ||
            (field ? inferTimeGrainFromField(field) : null) ||
            null
          const isDiscreteRaw = (p as any)?.is_discrete
          const typeRaw = String((p as any)?.type ?? '').trim().toLowerCase()
          const isDiscrete = (typeof isDiscreteRaw === 'boolean') ? isDiscreteRaw : (typeRaw ? typeRaw === 'discrete' : null)
          const discreteKey = (isDiscrete === true) ? 'disc' : (isDiscrete === false ? 'cont' : 'auto')
          const k = `${normKey(field) || field.toLowerCase()}::${timeGrain || 'raw'}::${discreteKey}`
          if (seenDim.has(k)) continue
          seenDim.add(k)
          dimFields.push({ field, time_grain: timeGrain, is_discrete: isDiscrete })
          continue
        }
        const k = `${String(aggTok || agg || 'SUM').toLowerCase()}|${normKey(field) || field.toLowerCase()}`
        if (seenMeas.has(k)) continue
        seenMeas.add(k)
        const aggFinal = String(aggTok || agg || (p as any)?.aggregation || 'SUM').trim()
        const alias = measureAliasForPill0(field, aggFinal, (p as any)?.alias)
        measures.push({ field, aggregation: aggFinal, alias })
      }

      return finalizeSql(buildRelationshipAwareTidyQuery({ dimFields, measures }))
    }
    return finalizeSql(buildVizQueryV0(spec.spec as VisualSpec))
  }

  if (spec.kind !== 'crosstab') {
    throw new Error(`Unsupported worksheet kind: ${String((spec as any)?.kind ?? '')}`)
  }

  const crosstabSpec = spec.spec as CrosstabSpecV1
  const inferTimeGrainFromFormatString = (raw: unknown): string | null => {
    const format = String(raw ?? '').trim().toLowerCase()
    if (!format) return null
    const cleaned = format
      .replace(/\*+/g, '')
      .replace(/'[^']*'/g, '')
      .replace(/\[[^\]]*\]/g, '')
      .replace(/\s+/g, '')
    if (!cleaned) return null
    if (cleaned.includes('mmm')) return 'month'
    if (cleaned.includes('qq') || cleaned === 'q' || cleaned.includes('quarter')) return 'quarter'
    if (cleaned.includes('wk') || cleaned.includes('ww')) return 'week'
    if ((cleaned.includes('yyyy') || cleaned.includes('yy')) && !cleaned.includes('m') && !cleaned.includes('d')) return 'year'
    if (cleaned.includes('d') && (cleaned.includes('m') || cleaned.includes('y'))) return 'day'
    return null
  }
  const extractFormattingFieldKey = (raw: unknown): string => {
    const text = String(raw ?? '').trim()
    if (!text) return ''
    const bracketTokens = Array.from(text.matchAll(/\[([^\]]+)\]/g))
      .map((match) => String(match[1] ?? '').trim())
      .filter(Boolean)
    const lastToken = bracketTokens[bracketTokens.length - 1] || text
    const cleaned = cleanBracketedFieldRef0(lastToken) || lastToken
    const tokenParts = cleaned.split(':').map((part) => String(part ?? '').trim()).filter(Boolean)
    const baseField =
      tokenParts.length >= 2
        ? tokenParts[1] || ''
        : cleaned
    return normalizeTableauFieldKey(baseField) || String(baseField ?? '').trim().toLowerCase()
  }
  const extractFormattingFieldToken = (raw: unknown): string => {
    const text = String(raw ?? '').trim()
    if (!text) return ''
    const bracketTokens = Array.from(text.matchAll(/\[([^\]]+)\]/g))
      .map((match) => String(match[1] ?? '').trim())
      .filter(Boolean)
    const lastToken = bracketTokens[bracketTokens.length - 1] || text
    return cleanBracketedFieldRef0(lastToken) || lastToken
  }
  const crosstabDateTimeGrainHints = (() => {
    const rules = Array.isArray((spec as any)?.formatting?.rules)
      ? ((spec as any).formatting.rules as any[])
      : []
    const hinted = new Map<string, string>()
    const ambiguous = new Set<string>()
    const noteHint = (fieldKey: string, grainRaw: unknown) => {
      const key = String(fieldKey ?? '').trim()
      const grain = normalizeTableauDateTimeGrain(grainRaw)
      if (!key || !grain || ambiguous.has(key)) return
      const prev = hinted.get(key)
      if (!prev) {
        hinted.set(key, grain)
        return
      }
      if (prev !== grain) {
        hinted.delete(key)
        ambiguous.add(key)
      }
    }
    for (const rule of rules) {
      const formats = Array.isArray((rule as any)?.formats) ? ((rule as any).formats as any[]) : []
      for (const formatEntry of formats) {
        const fieldKey = extractFormattingFieldKey((formatEntry as any)?.field)
        if (!fieldKey) continue
        const grain =
          normalizeTableauDateTimeGrain(inferTimeGrainFromField(extractFormattingFieldToken((formatEntry as any)?.field))) ||
          normalizeTableauDateTimeGrain(inferTimeGrainFromFormatString((formatEntry as any)?.value))
        noteHint(fieldKey, grain)
      }
    }
    return hinted
  })()
  const applyCrosstabDateTimeGrainHints = (fields: any[] | null | undefined): any[] =>
    (Array.isArray(fields) ? fields : []).map((field) => {
      if (!field || typeof field !== 'object') return field
      const key = normalizeTableauFieldKey(String((field as any)?.field_name ?? '').trim())
      const explicitTimeGrain = normalizeTableauDateTimeGrain((field as any)?.time_grain ?? (field as any)?.timeGrain)
      const hintedTimeGrain =
        explicitTimeGrain ||
        (key ? (crosstabDateTimeGrainHints.get(key) || null) : null) ||
        normalizeTableauDateTimeGrain(inferTimeGrainFromFormatString((field as any)?.format))
      if (!hintedTimeGrain) return field
      const typeRaw = String((field as any)?.type ?? '').trim().toLowerCase()
      const isDiscrete =
        typeof (field as any)?.is_discrete === 'boolean'
          ? (field as any).is_discrete
          : (typeRaw === 'continuous' ? false : true)
      const next = {
        ...(field as any),
        time_grain: hintedTimeGrain,
        is_discrete: isDiscrete,
      } as any
      const displayName = String(next.display_name ?? '').trim()
      const fieldName = String(next.field_name ?? '').trim()
      if (!displayName || normalizeTableauFieldKey(displayName) === (key || '')) {
        next.display_name = buildTableauDatePillLabel(next) || fieldName
      }
      return next
    })

  // Relationship-aware crosstab (best-effort, physical columns only).
  // We intentionally bypass the advanced Measure Names/Values/table-calc pipeline for now.
  if (options.relationshipModel && Array.isArray((options.relationshipModel as any)?.tables) && (options.relationshipModel as any).tables.length >= 2) {
    const rows = applyCrosstabDateTimeGrainHints(Array.isArray(crosstabSpec.rows) ? crosstabSpec.rows : [])
    const cols = applyCrosstabDateTimeGrainHints(Array.isArray(crosstabSpec.columns) ? crosstabSpec.columns : [])
    const dims: Array<{ field: string; time_grain?: string | null; is_discrete?: boolean | null }> = []
    const meas: Array<{ field: string; aggregation: string; alias: string }> = []
    for (const f0 of [...rows, ...cols]) {
      const f = (f0 && typeof f0 === 'object') ? (f0 as any) : null
      const field = String(f?.field_name ?? '').trim()
      if (!field) continue
      const role = String(f?.role ?? '').trim().toLowerCase()
      const agg = String(f?.aggregation ?? '').trim()
      const isMeasure = role === 'measure' || (!!agg && agg.toLowerCase() !== 'none')
      if (!isMeasure) {
        const type = String(f?.type ?? '').trim().toLowerCase()
        const isDiscrete = type ? (type === 'discrete') : null
        const tg =
          inferTimeGrainFromPill(f) ||
          (field ? timeGrainFromMeta(field) : null) ||
          (field ? inferTimeGrainFromField(field) : null) ||
          null
        dims.push({ field, time_grain: tg, is_discrete: isDiscrete })
      } else {
        const aggFinal = agg ? agg : 'sum'
        const alias = `${String(aggFinal).toUpperCase()}(${field})`
        meas.push({ field, aggregation: aggFinal, alias })
      }
    }

    const hasMeasureNames = [...rows, ...cols].some((f: any) => isMeasureNames(String((f as any)?.field_name ?? '')))
    const hasMultipleValues = [...rows, ...cols].some((f: any) => isMultipleValues(String((f as any)?.field_name ?? '')))

    const addCrosstabDimRequest = (fieldRaw: any, fieldLike?: any) => {
      const field = String(fieldRaw ?? '').trim()
      if (!field) return
      const key = normalizeTableauFieldKey(field)
      if (!key || dims.some((d) => normalizeTableauFieldKey(d.field) === key)) return
      const tg =
        inferTimeGrainFromPill(fieldLike) ||
        timeGrainFromMeta(field) ||
        inferTimeGrainFromField(field) ||
        null
      const type = String(fieldLike?.type ?? '').trim().toLowerCase()
      const isDiscrete = typeof fieldLike?.is_discrete === 'boolean'
        ? fieldLike.is_discrete
        : (type ? type === 'discrete' : true)
      dims.push({ field, time_grain: tg, is_discrete: isDiscrete })
    }
    const addCrosstabMeasureRequest = (fieldRaw: any, aggRaw: any, aliasRaw?: any) => {
      const field = String(fieldRaw ?? '').trim()
      if (!field) return
      const agg = String(aggRaw ?? '').trim().toLowerCase() || 'sum'
      const fn = agg === 'countd' ? 'COUNTD' : agg.toUpperCase()
      const alias = String(aliasRaw ?? `${fn}(${field})`).trim()
      if (!alias || meas.some((m) => String(m.alias ?? '').trim() === alias)) return
      meas.push({ field, aggregation: agg, alias })
    }
    const addCrosstabMarkPillRequest = (pill: any, shelf: string) => {
      if (!pill || typeof pill !== 'object') return
      const field = String((pill as any)?.field_name ?? (pill as any)?.field ?? '').trim()
      if (!field) return
      const role = String((pill as any)?.role ?? '').trim().toLowerCase()
      const agg = String((pill as any)?.aggregation ?? '').trim().toLowerCase()
      const isMeasure = role === 'measure' || Boolean(agg && agg !== 'none') || (pill as any)?.is_measure === true
      if (isMeasure || shelf === 'size') {
        const aggTok = agg && agg !== 'none' ? agg : 'sum'
        const fn = aggTok === 'countd' ? 'COUNTD' : aggTok.toUpperCase()
        addCrosstabMeasureRequest(field, aggTok, `${fn}(${field})`)
      } else {
        addCrosstabDimRequest(field, pill)
      }
    }
    const addCrosstabMarkShelfRequests = () => {
      const marks = ((crosstabSpec as any)?.marks && typeof (crosstabSpec as any).marks === 'object') ? (crosstabSpec as any).marks : null
      const intentEncodings = Array.isArray((spec as any)?.tableau_intent?.encodings)
        ? ((spec as any).tableau_intent.encodings as any[])
        : []
      for (const shelf of ['color', 'shape', 'detail', 'tooltip', 'text', 'label', 'size']) {
        const raw = marks ? (marks as any)[shelf] : null
        const items = Array.isArray(raw) ? raw : (raw ? [raw] : [])
        for (const item of items) addCrosstabMarkPillRequest(item, shelf)
      }
      for (const enc of intentEncodings) {
        const kind0 = String((enc as any)?.kind ?? '').trim().toLowerCase()
        const shelf =
          kind0 === 'label' ? 'text' :
          kind0 === 'color' || kind0 === 'shape' || kind0 === 'detail' || kind0 === 'tooltip' || kind0 === 'text' || kind0 === 'size'
            ? kind0
            : ''
        if (!shelf) continue
        const field = String((enc as any)?.field ?? '').trim()
        if (!field) continue
        addCrosstabMarkPillRequest({ field, field_name: field, shelf, ...(shelf === 'size' ? { aggregation: 'sum', is_measure: true, role: 'measure' } : null) }, shelf)
      }
      if (worksheetDebugEnabled) {
        try {
          console.warn('[worksheetQueryBuilder] crosstab mark-card requests', {
            worksheetName: options.worksheetName ?? null,
            markShelves: marks ? Object.fromEntries(Object.entries(marks).map(([k, v]) => [k, Array.isArray(v) ? v.length : (v ? 1 : 0)])) : null,
            intentEncodingKinds: intentEncodings.map((enc: any) => String(enc?.kind ?? '').trim()).filter(Boolean),
            dimFields: dims.map((d) => d.field),
            measures: meas.map((m) => m.alias),
          })
        } catch { }
      }
    }
    addCrosstabMarkShelfRequests()

    // Reuse the relationship-aware tidy query builder embedded above (viz branch) via a tiny shim.
    // It is safe to call buildWorksheetQuery recursively here because we short-circuit at this branch.
    // Instead of recursion, inline a minimal call by constructing a fake VisualSpec path would be messy,
    // so we share the SQL generator by duplicating the small invocation.
    // eslint-disable-next-line no-inner-declarations
    const buildRelationshipAwareTidyQueryLocal = (args: {
      dimFields: Array<{ field: string; time_grain?: string | null; is_discrete?: boolean | null }>
      measures: Array<{ field: string; aggregation: string; alias: string }>
    }): string => {
      // NOTE: legacy implementation below remains for now; ensure behavior stays consistent.
      return buildRelationshipAwareTidyQueryShared(args)
      /*
      // Delegate by pretending this is a viz build; the implementation lives in the viz branch above.
      // We call the top-level builder with kind='viz' is not possible here; instead, rebuild the same SQL
      // by calling through the already-in-scope finalizeSql + relationship branch.
      // This helper is intentionally duplicated in the viz branch; keep the logic consistent.
      // As a safety fallback, return a basic parquet query when not available.
      try {
        const model = options.relationshipModel as any
        const tablesRaw: any[] = Array.isArray(model?.tables) ? model.tables : []
        const relsRaw: any[] = Array.isArray(model?.relationships) ? model.relationships : []
        const primaryName = String(model?.primary_table ?? '').trim()

        const normTable = (s: any) => normalizeTableauDatasourceKey(String(s ?? '').trim())
        const tableByKey = new Map<string, any>()
        for (const t0 of tablesRaw) {
          const t = (t0 && typeof t0 === 'object') ? t0 : null
          if (!t) continue
          const name = String(t.name ?? '').trim()
          const key = normTable(name)
          const fromSql = String(t.fromSql ?? '').trim()
          if (!name || !key || !fromSql) continue
          tableByKey.set(key, {
            name,
            key,
            fromSql,
            extractColumns: (Array.isArray(t.extractColumns) ? t.extractColumns : null),
            fieldManifest: (t.fieldManifest ?? null),
          })
        }
        const primaryKey = (() => {
          const k = normTable(primaryName)
          if (k && tableByKey.has(k)) return k
          const first = Array.from(tableByKey.keys())[0] || ''
          return first
        })()
        const resolveColIn = (_tbl: any, fieldRaw: any): string => {
          const field = String(fieldRaw ?? '').trim()
          if (!field) return ''
          return String(resolveColFromBinding(field) ?? '').trim()
        }
        const aggExpr = (aggRaw: any, colExpr: string): string => {
          const a = String(aggRaw ?? '').trim().toLowerCase()
          if (a === 'avg' || a === 'average' || a === 'mean') return `AVG(TRY_CAST(${colExpr} AS DOUBLE))`
          if (a === 'min') return `MIN(${colExpr})`
          if (a === 'max') return `MAX(${colExpr})`
          if (a === 'count') return `COUNT(${colExpr})`
          if (a === 'countd' || a === 'count distinct' || a === 'count_distinct') return `COUNT(DISTINCT ${colExpr})`
          return `SUM(TRY_CAST(${colExpr} AS DOUBLE))`
        }

        const dimByTable = new Map<string, Array<{ field: string; expr: string; groupExpr: string }>>()
        const measByTable = new Map<string, Array<{ field: string; alias: string; expr: string }>>()
        const usedTables = new Set<string>()
        const claimDim = (tblKey: string, entry: { field: string; expr: string; groupExpr: string }) => {
          const arr = dimByTable.get(tblKey) || []
          arr.push(entry)
          dimByTable.set(tblKey, arr)
          usedTables.add(tblKey)
        }
        const claimMeas = (tblKey: string, entry: { field: string; alias: string; expr: string }) => {
          const aliasKey = String(entry?.alias ?? '').trim()
          if (!aliasKey) return
          const existing = measByTable.get(tblKey) || []
          if (existing.some((x) => String((x as any)?.alias ?? '').trim() === aliasKey)) {
            usedTables.add(tblKey)
            return
          }
          const arr = measByTable.get(tblKey) || []
          arr.push(entry)
          measByTable.set(tblKey, arr)
          usedTables.add(tblKey)
        }

        for (const d of args.dimFields) {
          const f = String(d?.field ?? '').trim()
          if (!f) continue
          const chosenInfo = chooseTableForField(f)
          const chosen = chosenInfo.tableKey
          const rawExpr = fieldExprInTable(chosen, f)
          const expr = applyTimeGrain(rawExpr, (d as any)?.time_grain ?? null, (d as any)?.is_discrete ?? null)
          claimDim(chosen, { field: f, expr, groupExpr: expr })
        }

        for (const m of args.measures) {
          const f = String(m?.field ?? '').trim()
          const a = String(m?.aggregation ?? '').trim()
          const aliasName = String(m?.alias ?? '').trim()
          if (!f || !aliasName) continue
          const chosenInfo = chooseTableForField(f)
          const chosen = chosenInfo.tableKey
          if (chosenInfo.kind === 'calc_cross') {
            const formula = lookupCalculatedFormula(f)
            if (!formula) continue
            crossCalcs.push({ alias: aliasName, formula })
            const defaultAgg = normalizeAggTok0(a || 'sum', f)
            const refs = extractFieldRefsFromFormula(formula)
            for (const r of refs) {
              for (const [k, t] of tableByKey.entries()) {
                const col = resolveColIn(t, r)
                if (!col) continue
                const aggTok = inferAggTokForRefInFormula(formula, r, defaultAgg)
                const m0 = depAggsByTable.get(k) || new Map<string, string>()
                const prev = m0.get(col) || ''
                m0.set(col, mergeAggTok(prev, aggTok))
                depAggsByTable.set(k, m0)
                usedTables.add(k)
                break
              }
            }
            continue
          }
          const baseExpr = fieldExprInTable(chosen, f)
          const looksAgg = /\b(SUM|AVG|MIN|MAX|COUNT)\s*\(/i.test(baseExpr)
          const looksWin = /\bOVER\s*\(/i.test(baseExpr)
          const expr = (looksAgg || looksWin) ? baseExpr : aggExpr(a || 'sum', baseExpr)
          claimMeas(chosen, { field: f, alias: aliasName, expr })
        }

        for (const [k, cols] of depAggsByTable.entries()) {
          const tAlias = quoteIdent(`t_${k}`)
          for (const [col, aggTok] of cols.entries()) {
            const c = String(col ?? '').trim()
            if (!c) continue
            claimMeas(k, { field: c, alias: c, expr: aggExpr(aggTok || 'sum', `${tAlias}.${quoteIdent(c)}`) })
          }
        }

        const rels = relsRaw
          .map((r0) => {
            const r = (r0 && typeof r0 === 'object') ? r0 : null
            if (!r) return null
            const a = normTable(r.a)
            const b = normTable(r.b)
            if (!a || !b || !tableByKey.has(a) || !tableByKey.has(b)) return null
            const clauses0 = Array.isArray(r.clauses) ? r.clauses : []
            const clauses = clauses0
              .map((c0: any) => {
                const c = (c0 && typeof c0 === 'object') ? c0 : null
                if (!c) return null
                const l = resolveColIn(tableByKey.get(a), c.left)
                const rr = resolveColIn(tableByKey.get(b), c.right)
                if (!l || !rr) return null
                return { left: l, right: rr }
              })
              .filter(Boolean) as Array<{ left: string; right: string }>
            if (!clauses.length) return null
            return { a, b, clauses }
          })
          .filter(Boolean) as any[]

        const needed = new Set<string>(usedTables.size ? Array.from(usedTables) : [primaryKey])
        needed.add(primaryKey)

        const joinColsByTable = new Map<string, Set<string>>()
        const needJoinCol = (tblKey: string, col: string) => {
          const set = joinColsByTable.get(tblKey) || new Set<string>()
          set.add(col)
          joinColsByTable.set(tblKey, set)
        }
        for (const e of rels) {
          for (const c of e.clauses) {
            needJoinCol(e.a, c.left)
            needJoinCol(e.b, c.right)
          }
        }

        const whereByTable = new Map<string, string[]>()
        const pushWhere = (tblKey: string, cond: string) => {
          if (!cond) return
          const arr = whereByTable.get(tblKey) || []
          arr.push(cond)
          whereByTable.set(tblKey, arr)
        }
        const filterList: any[] = Array.isArray(filters) ? (filters as any[]) : []
        for (const f0 of filterList) {
          const f = (f0 && typeof f0 === 'object') ? f0 : null
          if (!f) continue
          const field = String((f as any)?.field_name ?? '').trim()
          if (!field) continue
          const opRaw = String((f as any)?.operator ?? '').trim()
          const op = opRaw ? opRaw.toUpperCase() : ''
          if (shouldSkipSqlFilter(f)) continue
          const value = (f as any)?.value
          for (const [k, t] of tableByKey.entries()) {
            const col = resolveColIn(t, field)
            if (!col) continue
            const tAlias = quoteIdent(`t_${k}`)
            const colSql = `${tAlias}.${quoteIdent(col)}`
            if (!op || op === '=') {
              const pred = buildInPredicateFromValue(colSql, value, false, { includeNullsOnNot: true })
              if (pred) pushWhere(k, pred)
            }
            else if (op === '!=' || op === '<>') {
              const pred = buildInPredicateFromValue(colSql, value, true, { includeNullsOnNot: true })
              if (pred) pushWhere(k, pred)
            }
            else if (op === '>' || op === '>=' || op === '<' || op === '<=') pushWhere(k, `${colSql} ${op} ${escapeValue(value)}`)
            else if (op === 'IS NULL') pushWhere(k, `${colSql} IS NULL`)
            else if (op === 'IS NOT NULL') pushWhere(k, `${colSql} IS NOT NULL`)
            else if (op === 'LIKE' || op === 'NOT LIKE') pushWhere(k, `${colSql} ${op} ${escapeValue(value)}`)
            else if (op === 'IN' || op === 'NOT IN') {
              const pred = buildInPredicateFromValue(colSql, value, op === 'NOT IN', { includeNullsOnNot: true })
              if (pred) pushWhere(k, pred)
            } else if (op === 'BETWEEN' && Array.isArray(value) && value.length >= 2) {
              pushWhere(k, buildBetweenPredicate(colSql, value[0], value[1], f, k))
            }
          }
        }

        const subByKey = new Map<string, { sql: string; alias: string }>()
        for (const k of needed) {
          const tbl = tableByKey.get(k)
          if (!tbl) continue
          const tAlias = quoteIdent(`t_${k}`)
          const keyCols = Array.from(joinColsByTable.get(k) || new Set<string>())
          const dims0 = dimByTable.get(k) || []
          const meas0 = measByTable.get(k) || []
          const selectParts: string[] = []
          const groupBy: string[] = []
          for (const c of keyCols) {
            selectParts.push(`${tAlias}.${quoteIdent(c)} AS ${quoteIdent(c)}`)
            groupBy.push(`${tAlias}.${quoteIdent(c)}`)
          }
          for (const d of dims0) {
            selectParts.push(`${d.expr} AS ${quoteIdent(d.field)}`)
            if (canSqlExpressionAppearInGroupBy(d.expr)) groupBy.push(d.groupExpr)
          }
          for (const m of meas0) selectParts.push(`${m.expr} AS ${quoteIdent(m.alias)}`)
          if (!selectParts.length) continue
          let sql = `SELECT ${selectParts.join(', ')} FROM ${String(tbl.fromSql).trim()} AS ${tAlias}`
          const where = whereByTable.get(k) || []
          if (where.length) sql += ` WHERE ${where.join(' AND ')}`
          if (groupBy.length && meas0.length) sql += ` GROUP BY ${groupBy.join(', ')}`
          subByKey.set(k, { sql: `(${sql})`, alias: quoteIdent(`q_${k}`) })
        }

        const base = subByKey.get(primaryKey)
        if (!base) return `SELECT * FROM ${parquetRef} AS ${quoteIdent('t')}`
        let from = `${base.sql} AS ${base.alias}`
        const joined = new Set<string>([primaryKey])
        for (const e of rels) {
          const aIn = joined.has(e.a)
          const bIn = joined.has(e.b)
          if (aIn && !bIn) {
            const bSub = subByKey.get(e.b)
            if (!bSub) continue
            const on = e.clauses.map((c: any) => `${subByKey.get(e.a)?.alias}.${quoteIdent(c.left)} = ${bSub.alias}.${quoteIdent(c.right)}`).join(' AND ')
            from = `${from} LEFT JOIN ${bSub.sql} AS ${bSub.alias} ON ${on || 'TRUE'}`
            joined.add(e.b)
          } else if (bIn && !aIn) {
            const aSub = subByKey.get(e.a)
            if (!aSub) continue
            const on = e.clauses.map((c: any) => `${subByKey.get(e.b)?.alias}.${quoteIdent(c.right)} = ${aSub.alias}.${quoteIdent(c.left)}`).join(' AND ')
            from = `${from} LEFT JOIN ${aSub.sql} AS ${aSub.alias} ON ${on || 'TRUE'}`
            joined.add(e.a)
          }
        }

        const innerSelect: string[] = []
        for (const d of args.dimFields) {
          const f = String(d?.field ?? '').trim()
          if (!f) continue
          let found: string | null = null
          for (const [k, arr] of dimByTable.entries()) {
            if (arr.some((x) => x.field === f)) { found = subByKey.get(k)?.alias || null; break }
          }
          if (!found) found = base.alias
          innerSelect.push(`${found}.${quoteIdent(f)} AS ${quoteIdent(f)}`)
        }
        for (const m of args.measures) {
          const a = String(m?.alias ?? '').trim()
          if (!a) continue
          if (crossCalcs.some((c) => c.alias === a)) continue
          let found: string | null = null
          for (const [k, arr] of measByTable.entries()) {
            if (arr.some((x) => x.alias === a)) { found = subByKey.get(k)?.alias || null; break }
          }
          if (!found) found = base.alias
          innerSelect.push(`${found}.${quoteIdent(a)} AS ${quoteIdent(a)}`)
        }
        for (const cols of depAggsByTable.values()) {
          for (const col of Array.from(cols.keys())) {
            const c = String(col ?? '').trim()
            if (!c) continue
            let found: string | null = null
            for (const [k, arr] of measByTable.entries()) {
              if (arr.some((x) => x.alias === c)) { found = subByKey.get(k)?.alias || null; break }
            }
            if (!found) found = base.alias
            innerSelect.push(`${found}.${quoteIdent(c)} AS ${quoteIdent(c)}`)
          }
        }

        const limRaw = Number.isFinite(Number(limit)) ? Math.floor(Number(limit)) : 10_000
        const lim = limRaw > 0 ? Math.min(250_000, limRaw) : 0
        const joinedSql = `SELECT ${innerSelect.length ? innerSelect.join(', ') : '*'} FROM ${from}`
        const tAlias = quoteIdent('t')
        const outerSelect: string[] = []
        for (const d of args.dimFields) {
          const f = String(d?.field ?? '').trim()
          if (!f) continue
          outerSelect.push(`${tAlias}.${quoteIdent(f)} AS ${quoteIdent(f)}`)
        }
        for (const m of args.measures) {
          const aliasName = String(m?.alias ?? '').trim()
          if (!aliasName) continue
          const cross = crossCalcs.find((c) => c.alias === aliasName) || null
          if (cross) {
            const compiled = compileTableauFormulaToSql(String(cross.formula ?? ''))
            const expr = compiled?.sql && !compiled.hasErrors ? String(compiled.sql).trim() : 'NULL'
            outerSelect.push(`${expr} AS ${quoteIdent(aliasName)}`)
          } else {
            outerSelect.push(`${tAlias}.${quoteIdent(aliasName)} AS ${quoteIdent(aliasName)}`)
          }
        }
        let sql = `SELECT ${outerSelect.length ? outerSelect.join(', ') : '*'} FROM (${joinedSql}) AS ${tAlias}`
        if (limRaw > 0) sql += ` LIMIT ${lim}`
        return sql
      } catch {
        return `SELECT * FROM ${parquetRef} AS ${quoteIdent('t')}`
      }
      */
    }

    const baseMeasures = (() => {
      // Prefer explicit override list (matches Tableau "Measure Values" selection).
      const ov = options.measureValuesOverride
      if (Array.isArray(ov) && ov.length) {
        const fields = ov
          .map((x: any) => (typeof x === 'string' ? x : (x?.field_name ?? x?.field ?? '')))
          .map((s: any) => String(s ?? '').trim())
          .filter(Boolean)
          .filter((field) => {
            if (!sourceSchema) return true
            const col = resolveColFromBinding(field) || field
            return isPresentInExtractSchema(col)
          })
        return fields
      }
      // Fallback: sample measures from field dictionary (best-effort, capped).
      try {
        const fd = (workbookIr as any)?.field_dictionary
        const cols: any[] = Array.isArray(fd?.columns) ? fd.columns : []
        const picks: string[] = []
        for (const c of cols) {
          const name = String(c?.caption ?? c?.name ?? '').trim()
          if (!name) continue
          const role = String(c?.role ?? '').trim().toLowerCase()
          const hidden = String(c?.hidden ?? '').trim().toLowerCase() === 'true'
          if (hidden) continue
          if (role && role !== 'measure') continue
          const k = normalizeTableauFieldKey(name)
          if (!k || k === 'measure names' || k === 'measure values') continue
          const col = resolveColFromBinding(name) || name
          if (sourceSchema && !isPresentInExtractSchema(col)) continue
          picks.push(name)
          if (picks.length >= 40) break
        }
        return picks
      } catch {
        return []
      }
    })()

    const tooltipMeasureRequests = (() => {
      const out: Array<{ field: string; agg: string; alias: string }> = []
      const add = (fieldRaw: any, aggRaw: any, aliasRaw?: any) => {
        const field = String(fieldRaw ?? '').trim()
        if (!field) return
        const agg = String(aggRaw ?? '').trim().toLowerCase() || 'sum'
        const fn = agg === 'countd' ? 'COUNTD' : agg.toUpperCase()
        const alias = String(aliasRaw ?? `${fn}(${field})`).trim()
        if (!alias) return
        out.push({ field, agg, alias })
      }

      try {
        const tt0 = (spec as any)?.view?.tooltip
        const tt = (tt0 && typeof tt0 === 'object') ? tt0 : null
        if (tt && tt.enabled === false) return []
        const mode = String(tt?.mode ?? 'auto').trim().toLowerCase()
        const tmpl = String(tt?.template ?? '')
        const tmplHtml = String(tt?.template_html ?? '')
        const baseTokens = (mode === 'rich' && tmplHtml) ? extractTooltipTokensFromTemplate(tmplHtml) : extractTooltipTokensFromTemplate(tmpl)
        const needs = baseTokens.map(parseTooltipTokenNeed).filter(Boolean) as any[]
        for (const n of needs) {
          if (n.kind === 'all_fields') continue
          if (n.kind === 'func') {
            const fn = String(n.fn ?? '').trim().toUpperCase()
            const agg = (fn === 'COUNTD' || fn === 'COUNT_DISTINCT' || fn === 'COUNTDISTINCT') ? 'countd'
              : (fn === 'COUNT') ? 'count'
                : (fn === 'AVG' || fn === 'AVERAGE' || fn === 'MEAN') ? 'avg'
                  : (fn === 'MIN') ? 'min'
                    : (fn === 'MAX') ? 'max'
                      : 'sum'
            add(n.arg, agg, n.token)
          } else if (n.kind === 'field') {
            // Heuristic: treat raw field tokens as measures (SUM) when used in tooltip tokens.
            add(n.field, 'sum', `SUM(${String(n.field ?? '').trim()})`)
          }
        }
      } catch { }

      // Marks tooltip shelf pills can introduce extra measure needs.
      try {
        const arr: any[] = Array.isArray((crosstabSpec as any)?.marks?.tooltip) ? ((crosstabSpec as any).marks.tooltip as any[]) : []
        for (const it of arr) {
          const field = String((it as any)?.field_name ?? '').trim()
          if (!field) continue
          const role = String((it as any)?.role ?? '').trim().toLowerCase()
          const agg = String((it as any)?.aggregation ?? '').trim().toLowerCase()
          const isMeasure = role === 'measure' || (!!agg && agg !== 'none')
          if (!isMeasure) continue
          const aggTok = agg ? agg : 'sum'
          const fn = aggTok === 'countd' ? 'COUNTD' : aggTok.toUpperCase()
          add(field, aggTok, `${fn}(${field})`)
        }
      } catch { }

      // Dedup by alias.
      const seen = new Set<string>()
      const dedup: Array<{ field: string; agg: string; alias: string }> = []
      for (const r of out) {
        const k = String(r.alias ?? '').trim().toLowerCase()
        if (!k || seen.has(k)) continue
        seen.add(k)
        dedup.push(r)
      }
      return dedup
    })()

    const hasWeeklyTimePeriodDims = dims.some((d) => {
      const f = String(d?.field ?? '').trim()
      const k = normalizeTableauFieldKey(stripBracketsAroundFieldName(f))
      return k === 'time period' || k === 'time period begin dates' || k === 'time period end dates'
    })
    if ((hasMeasureNames || hasMultipleValues) && baseMeasures.length && !hasWeeklyTimePeriodDims) {
      const baseReqs = baseMeasures.map((field) => ({ field, aggregation: 'sum', alias: `SUM(${field})` }))
      const tooltipReqs = tooltipMeasureRequests.map((r) => ({ field: r.field, aggregation: r.agg, alias: r.alias }))
      const seen = new Set<string>()
      const measures = [...baseReqs, ...tooltipReqs].filter((m) => {
        const k = String(m.alias ?? '').trim().toLowerCase()
        if (!k || seen.has(k)) return false
        seen.add(k)
        return true
      })
      const baseSql = buildRelationshipAwareTidyQueryLocal({ dimFields: dims, measures })
      const baseCte = quoteIdent('__fg_mnv_base')
      const baseRef = quoteIdent('b')
      const selectDims = dims
        .map((d) => {
          const f = String(d?.field ?? '').trim()
          if (!f) return ''
          return `${baseRef}.${quoteIdent(f)} AS ${quoteIdent(f)}`
        })
        .filter(Boolean)
        .join(', ')
      const structItems: string[] = []
      for (const m of measures) {
        const name = String(m?.alias ?? '').trim()
        if (!name) continue
        const valCol = `${baseRef}.${quoteIdent(name)}`
        // Keep the UNNEST list homogeneous for DuckDB binder stability.
        // Some measure aliases resolve to VARCHAR-ish ATTR expressions; coercing all
        // Measure Values payloads to DOUBLE avoids mixed-struct list types.
        structItems.push(`struct_pack(label := ${escapeValue(name)}, value := TRY_CAST(${valCol} AS DOUBLE))`)
      }
      if (structItems.length) {
        const unnestAlias = quoteIdent('__fg_mnv_u_simple')
        const unnestItemAlias = quoteIdent('__fg_mnv_item')
        const parts = [
          selectDims,
          `struct_extract(${unnestAlias}.${unnestItemAlias}, 'label') AS ${quoteIdent('Measure Names')}`,
          `struct_extract(${unnestAlias}.${unnestItemAlias}, 'value') AS ${quoteIdent('Measure Values')}`,
        ].filter(Boolean)
        const mnvSelect = `SELECT ${parts.join(', ')}
FROM ${baseCte} AS ${baseRef}
CROSS JOIN UNNEST([${structItems.join(', ')}]) AS ${unnestAlias}(${unnestItemAlias})`
        return finalizeSql(`WITH ${baseCte} AS MATERIALIZED (${baseSql}) ${mnvSelect}`)
      }
    }

    return finalizeSql(buildRelationshipAwareTidyQueryLocal({ dimFields: dims, measures: meas }))
  }

  // --- Advanced path: derive Measure Names/Values + compile calcs/table calcs from WorkbookIR ---
  //
  // This is the core plumbing needed to make "formulas working" from extracts:
  // - WorkbookIR.field_dictionary.columns[*].calculation.formula contains Tableau calc expressions
  // - column_instances carry which measures are used in the viz (qk = quantitative instances)
  //
  // If we can't build this safely, we fall back to the simple SELECT/GROUP BY query below.
  const requiresMeasureNames = (() => {
    const rows = applyCrosstabDateTimeGrainHints(Array.isArray(crosstabSpec.rows) ? crosstabSpec.rows : [])
    const cols = applyCrosstabDateTimeGrainHints(Array.isArray(crosstabSpec.columns) ? crosstabSpec.columns : [])
    const normKey = (s: any) => normalizeTableauFieldKey(String(s ?? ''))
    const isMeasureNames = (s: any) => normKey(s) === 'measure names'
    const hasMeasureNames = [...rows, ...cols].some((f) => isMeasureNames((f as any)?.field_name))
    const hasMultipleValues = [...rows, ...cols].some((f) => isMultipleValues((f as any)?.field_name))
    const hasMeasureNamesFilter = (Array.isArray(filters) ? filters : []).some((f: any) => isMeasureNames((f as any)?.field_name))
    return hasMeasureNames || hasMultipleValues || hasMeasureNamesFilter
  })()
  const tryBuildMeasureNamesValuesQuery = (): string | null => {
    if (!requiresMeasureNames) return null
    if (!workbookIr || typeof workbookIr !== 'object') {
      if (!ALLOW_FALLBACKS) {
        throw new Error('Measure Names/Values requested but workbook IR is missing')
      }
      return null
    }

    _markPhase('adv_entry')
    const rows = Array.isArray(crosstabSpec.rows) ? crosstabSpec.rows : []
    const cols = Array.isArray(crosstabSpec.columns) ? crosstabSpec.columns : []

    const norm = (s: any) => String(s ?? '').trim().toLowerCase().replace(/^:+/, '')
    const normKey = (s: any) => normalizeTableauFieldKey(String(s ?? ''))
    const isMeasureNames = (s: any) => normKey(s) === 'measure names'

    const hasMeasureNames = [...rows, ...cols].some((f) => isMeasureNames((f as any)?.field_name))
    const hasMultipleValues = [...rows, ...cols].some((f) => isMultipleValues((f as any)?.field_name))
    const hasMeasureNamesFilter = (Array.isArray(filters) ? filters : []).some((f: any) => isMeasureNames((f as any)?.field_name))
    if (!hasMeasureNames && !hasMultipleValues && !hasMeasureNamesFilter) return null

    // Tooltip needs (template tokens + Marks tooltip shelf → extra SELECT cols).
    // Tableau often references measures not present in the view via tooltip tokens like <SUM(Foo)>,
    // and it also implicitly adds fields placed on the Marks tooltip shelf.
    const tooltipNeeds: TooltipTokenNeed[] = (() => {
      try {
        const tt0 = (spec as any)?.view?.tooltip
        const tt = (tt0 && typeof tt0 === 'object') ? (tt0 as any) : null
        if (tt && tt.enabled === false) return []

        const mode = String(tt?.mode ?? 'auto').trim().toLowerCase()
        const tmpl = String(tt?.template ?? '')
        const tmplHtml = String(tt?.template_html ?? '')
        const allowTemplateTokens = !lazyTooltips || (mode !== 'auto' && (tmpl || tmplHtml))
        const baseTokens = allowTemplateTokens
          ? ((mode === 'rich' && tmplHtml)
            ? extractTooltipTokensFromTemplate(tmplHtml)
            : extractTooltipTokensFromTemplate(tmpl))
          : []

        const marksTokens: string[] = []
        try {
          const arr = Array.isArray((crosstabSpec as any)?.marks?.tooltip) ? ((crosstabSpec as any).marks.tooltip as any[]) : []
          const aggTok = (raw: any): string | null => {
            const a = String(raw ?? '').trim().toLowerCase()
            if (!a) return null
            if (a === 'average' || a === 'mean') return 'avg'
            if (a === 'ctd') return 'countd'
            if (a === 'count distinct' || a === 'count_distinct') return 'countd'
            if (a === 'sum' || a === 'avg' || a === 'min' || a === 'max' || a === 'count' || a === 'countd') return a
            return null
          }
          for (const it of arr) {
            const field = stripBracketsAroundFieldName(String(it?.field_name ?? ''))
            if (!field) continue
            const role = String(it?.role ?? '').trim().toLowerCase()
            const agg = aggTok(it?.aggregation) || (role === 'measure' ? 'sum' : null)
            if (agg) {
              const fn = agg === 'countd' ? 'COUNTD' : agg.toUpperCase()
              marksTokens.push(`${fn}(${field})`)
            } else {
              marksTokens.push(field)
            }
          }
        } catch { }

        const parsed = [...baseTokens, ...marksTokens].map(parseTooltipTokenNeed).filter(Boolean) as TooltipTokenNeed[]

        // Dedup (preserve order) by token string
        const seen = new Set<string>()
        const out: TooltipTokenNeed[] = []
        for (const p of parsed) {
          const k = p.kind === 'all_fields' ? 'all fields' : (p as any).token?.toLowerCase?.() || ''
          if (!k) continue
          if (seen.has(k)) continue
          seen.add(k)
          out.push(p)
        }
        return out
      } catch {
        return []
      }
    })()
    _markPhase('adv_tooltip')

    const fd = (workbookIr as any)?.field_dictionary
    const fdCols: any[] = Array.isArray(fd?.columns) ? fd.columns : []
    const fdCis: any[] = Array.isArray(fd?.column_instances) ? fd.column_instances : []
    // O(1) lookup indexes for fdCols — avoids O(n) fdCols.find() per measure
    const _fdColsByName = new Map<string, any>()
    const _fdColsByNameKey = new Map<string, any>()
    const _fdColsByNameKeyStrict = new Map<string, any>()
    const _fdColsByCaptionKey = new Map<string, any>()
    for (const c of fdCols) {
      const n = String(c?.name ?? '').trim()
      if (n && !_fdColsByName.has(n)) _fdColsByName.set(n, c)
      const nameKey = normalizeTableauFieldKey(n)
      if (nameKey && !_fdColsByNameKey.has(nameKey)) _fdColsByNameKey.set(nameKey, c)
      const nameKeyStrict = normalizeTableauFieldKeyStrict(n)
      if (nameKeyStrict && !_fdColsByNameKeyStrict.has(nameKeyStrict)) _fdColsByNameKeyStrict.set(nameKeyStrict, c)
      const capKey = normalizeTableauFieldKey(String(c?.caption ?? '').trim())
      if (capKey && !_fdColsByCaptionKey.has(capKey)) _fdColsByCaptionKey.set(capKey, c)
    }
    if (fdCols.length === 0 || fdCis.length === 0) {
      if (!ALLOW_FALLBACKS) {
        throw new Error('Measure Names/Values requested but field dictionary is incomplete')
      }
      return null
    }

    const columnCaptionById = new Map<string, string>()
    const columnCaptionByDsId = new Map<string, string>()
    const normalizeDsKey = (raw: any): string => {
      const out = normalizeTableauDatasourceKey(String(raw ?? '').trim())
      return out || ''
    }
    const dsIdKey = (dsRaw: any, idRaw: any): string | null => {
      const dsKey = normalizeDsKey(dsRaw)
      if (!dsKey) return null
      const id = String(idRaw ?? '').trim()
      if (!id) return null
      return `${dsKey}::${id}`
    }
    const noteColumnCaption = (dsRaw: any, idRaw: any, captionRaw: any) => {
      const id = String(idRaw ?? '').trim()
      const caption = String(captionRaw ?? '').trim()
      if (!id || !caption) return
      if (!columnCaptionById.has(id)) columnCaptionById.set(id, caption)
      const cleaned = cleanBracketedFieldRef0(id)
      if (cleaned && !columnCaptionById.has(cleaned)) columnCaptionById.set(cleaned, caption)
      const dsKeyRaw = dsIdKey(dsRaw, id)
      if (dsKeyRaw && !columnCaptionByDsId.has(dsKeyRaw)) columnCaptionByDsId.set(dsKeyRaw, caption)
      const dsKeyClean = cleaned ? dsIdKey(dsRaw, cleaned) : null
      if (dsKeyClean && !columnCaptionByDsId.has(dsKeyClean)) columnCaptionByDsId.set(dsKeyClean, caption)
    }
    const columnCaptionForId = (idRaw: any, dsRaw?: any): string | null => {
      const id = String(idRaw ?? '').trim()
      if (!id) return null
      const dsKey = dsIdKey(dsRaw, id)
      if (dsKey && columnCaptionByDsId.has(dsKey)) return columnCaptionByDsId.get(dsKey) || null
      const cleaned = cleanBracketedFieldRef0(id)
      const dsKeyClean = cleaned ? dsIdKey(dsRaw, cleaned) : null
      if (dsKeyClean && columnCaptionByDsId.has(dsKeyClean)) return columnCaptionByDsId.get(dsKeyClean) || null
      return columnCaptionById.get(id) || (cleaned ? columnCaptionById.get(cleaned) : null) || null
    }
    for (const c of fdCols) {
      noteColumnCaption((c as any)?.datasource, (c as any)?.name, (c as any)?.caption)
    }
    const normalizeCalcFormula = (raw: string): string =>
      String(raw ?? '').replace(/\s+/g, ' ').trim().toLowerCase()
    const stripOuterParens = (raw: string): string => {
      let s = String(raw ?? '').trim()
      while (s.startsWith('(') && s.endsWith(')')) {
        let depth = 0
        let balanced = true
        for (let i = 0; i < s.length; i++) {
          const ch = s[i]
          if (ch === '(') depth++
          else if (ch === ')') depth--
          if (depth === 0 && i < s.length - 1) {
            balanced = false
            break
          }
        }
        if (!balanced) break
        s = s.slice(1, -1).trim()
      }
      return s
    }
    const stripSafeDivideWrapper = (raw: string): string => {
      const s = String(raw ?? '').trim()
      if (!/^case\s+when/i.test(s)) return s
      if (!/then\s+null\s+else/i.test(s)) return s
      if (!/(is null|=\s*0)/i.test(s)) return s
      const match = s.match(/then\s+null\s+else\s+(.+)\s+end$/i)
      return match ? String(match[1] ?? '').trim() : s
    }
    const extractLikertKeyFromFormula = (raw: string): string | null => {
      const s = String(raw ?? '').toLowerCase()
      if (!s.includes('[response value]')) return null
      const valMatch = s.match(/\[response value\]\s*=\s*(\d+)/i)
      if (!valMatch) return null
      const value = valMatch[1]
      const sign = /then\s*-\s*1/i.test(s) ? '-1' : /then\s*1/i.test(s) ? '1' : ''
      if (!sign) return null
      const denom = /2\s*\*\s*countd/i.test(s) ? '2' : '1'
      return `${value}:${sign}:${denom}`
    }
    const extractLikertKeyFromSql = (raw: string): string | null => {
      const s = String(raw ?? '').toLowerCase()
      if (!s.includes('response value')) return null
      const valMatch = s.match(/response value"\s*=\s*(\d+)/i)
      if (!valMatch) return null
      const value = valMatch[1]
      const sign = /then\s*\(?\s*-\s*1/i.test(s) ? '-1' : /then\s*1/i.test(s) ? '1' : ''
      if (!sign) return null
      const compact = s.replace(/\s+/g, '')
      const denom = compact.includes('2*(count(distinct') ? '2' : '1'
      return `${value}:${sign}:${denom}`
    }
    const normalizeSqlExpr = (raw: string): string => {
      let s = stripSafeDivideWrapper(raw)
      s = stripOuterParens(s)
      return String(s ?? '').replace(/\s+/g, ' ').trim().toLowerCase()
    }
    const calcCaptionByFormula = new Map<string, string>()
    // Lazy: only compiled-SQL→caption map is built when first accessed (saves ~14s on cache miss).
    let _calcCaptionByCompiledSqlCache: Map<string, string> | null = null
    const calcCaptionByLikertKey = new Map<string, string>()
    for (const c of fdCols) {
      const formula = String((c as any)?.calculation?.formula ?? '').trim()
      const caption = String((c as any)?.caption ?? '').trim()
      if (!formula || !caption) continue
      const key = normalizeCalcFormula(formula)
      if (key && !calcCaptionByFormula.has(key)) calcCaptionByFormula.set(key, caption)
      const likertKey = extractLikertKeyFromFormula(formula)
      if (likertKey && !calcCaptionByLikertKey.has(likertKey)) calcCaptionByLikertKey.set(likertKey, caption)
    }

    const manifestFields: any[] = Array.isArray((options as any)?.fieldManifest?.fields)
      ? ((options as any).fieldManifest.fields as any[])
      : []
    const manifestCaptionByToken = new Map<string, string>()
    const manifestTokenByCaption = new Map<string, string>()
    const manifestTokenKeysByCaption = new Map<string, string[]>()
    const captionKey = (raw: any): string => {
      const key = normalizeTableauFieldKey(String(raw ?? '').trim())
      return key || String(raw ?? '').trim().toLowerCase()
    }
    // Canonicalization for Measure Names labels:
    // - Tableau XML can include unicode spaces/hyphens and percent escaping ("\%")
    // - Some workbooks intentionally use repeated spaces to create distinct aliases that
    //   still *look* identical in the UI (HTML collapses whitespace). Therefore:
    //   - We preserve internal whitespace for identity/matching.
    //   - We provide a separate "loose" variant that collapses whitespace for fallback matching,
    //     but only when unambiguous.
    const canonicalizeMeasureLabel = (raw: any): string => {
      let s = String(raw ?? '')
      if (!s) return ''
      // Unescape Tableau-style percent escapes ("\%" or "\\%").
      s = s.replace(/\\+%/g, '%')
      // Normalize unicode (NFKC helps with full-width percent, etc.).
      try { s = s.normalize('NFKC') } catch {}
      // Normalize common non-breaking spaces / thin spaces to ASCII space.
      s = s.replace(/[\u00A0\u2007\u202F]/g, ' ')
      // Normalize common unicode hyphens/minus to ASCII hyphen.
      s = s.replace(/[\u2010\u2011\u2012\u2013\u2014\u2015\u2212]/g, '-')
      // Preserve internal whitespace; only trim ends.
      return s.trim()
    }
    const canonicalizeMeasureLabelLoose = (raw: any): string =>
      canonicalizeMeasureLabel(raw).replace(/\s+/g, ' ').trim()
    const measureLabelLooseIdentity = (raw: any): string =>
      canonicalizeMeasureLabel(raw)
        .toLowerCase()
        .replace(/[^a-z0-9%]+/g, '')
    const measureLabelReadabilityScore = (raw: any): number => {
      const s = canonicalizeMeasureLabel(raw)
      if (!s) return 0
      const separators = (s.match(/[\s/_-]+/g) || []).length
      const punctuation = (s.match(/[()%]/g) || []).length
      return (separators * 10) + punctuation + s.length
    }
    const preferReadableMeasureLabel = (aRaw: any, bRaw: any): string => {
      const a = canonicalizeMeasureLabel(aRaw)
      const b = canonicalizeMeasureLabel(bRaw)
      if (!a) return b
      if (!b) return a
      return measureLabelReadabilityScore(a) >= measureLabelReadabilityScore(b) ? a : b
    }
    const upgradeReadableEquivalentMeasureLabel = (primaryRaw: any, candidateRaws: any[]): string => {
      const primary = canonicalizeMeasureLabel(primaryRaw)
      if (!primary) return ''
      const primaryLoose = measureLabelLooseIdentity(primary)
      if (!primaryLoose) return primary
      let best = primary
      for (const raw of candidateRaws) {
        const candidate = canonicalizeMeasureLabel(raw)
        if (!candidate) continue
        if (measureLabelLooseIdentity(candidate) !== primaryLoose) continue
        best = preferReadableMeasureLabel(best, candidate)
      }
      return best
    }
    const stripAlongSuffix = (s: string): string => String(s || '').replace(/\s+along\s+.+$/i, '').trim()
    const explicitMeasureLabelIdentityKey = (raw: any): string =>
      canonicalizeMeasureLabel(raw)
        .toLowerCase()
        .replace(/[^a-z0-9%]+/g, '')
    const measureCaptionKey = (raw: any): string => {
      const s = canonicalizeMeasureLabel(raw)
      if (!s) return ''
      const key = normalizeTableauFieldKey(s)
      return key || s.toLowerCase()
    }
    const measureCaptionStrictKey = (raw: any): string => {
      const s = canonicalizeMeasureLabel(raw)
      if (!s) return ''
      const key = normalizeTableauFieldKeyStrict(s)
      return (key || s.toLowerCase()).replace(/\s+/g, ' ').trim()
    }
    const manifestTokenKey = (rawId: any): string => {
      const cleaned = cleanBracketedFieldRef0(String(rawId ?? '') || '')
      if (!cleaned) return ''
      return normalizeTableauFieldToken(cleaned).trim().toLowerCase()
    }
    const noteManifest = (rawId: any, captionRaw: any, opts?: { primary?: boolean }) => {
      const cap = String(captionRaw ?? '').trim()
      if (!cap) return
      const key = manifestTokenKey(rawId)
      if (!key) return
      if (opts?.primary !== false && !manifestCaptionByToken.has(key)) manifestCaptionByToken.set(key, cap)
      const capKey = captionKey(cap)
      if (!capKey) return
      if (!manifestTokenByCaption.has(capKey)) manifestTokenByCaption.set(capKey, key)
      const existing = manifestTokenKeysByCaption.get(capKey) ?? []
      if (!existing.includes(key)) {
        existing.push(key)
        manifestTokenKeysByCaption.set(capKey, existing)
      }
    }
    for (const f of manifestFields) {
      noteManifest((f as any)?.field_id, (f as any)?.caption)
      noteManifest((f as any)?.base_field_id, (f as any)?.caption, { primary: false })
      noteManifest((f as any)?.column_name, (f as any)?.caption, { primary: false })
      const aliases = Array.isArray((f as any)?.aliases) ? ((f as any).aliases as any[]) : []
      for (const alias of aliases) {
        noteManifest((f as any)?.field_id, alias, { primary: false })
        noteManifest((f as any)?.base_field_id, alias, { primary: false })
        noteManifest((f as any)?.column_name, alias, { primary: false })
      }
    }
    const bindingDatasources: any[] = Array.isArray((options as any)?.bindingMap?.datasources)
      ? (((options as any).bindingMap.datasources) as any[])
      : []
    for (const ds of bindingDatasources) {
      const bindings = Array.isArray((ds as any)?.bindings) ? ((ds as any).bindings as any[]) : []
      for (const entry of bindings) {
        const labels = [
          (entry as any)?.caption,
          ...(Array.isArray((entry as any)?.aliases) ? ((entry as any).aliases as any[]) : []),
        ]
        const ids = [
          (entry as any)?.field_id,
          (entry as any)?.tableau_token_raw,
          (entry as any)?.tableau_token,
        ]
        for (const label of labels) {
          for (const id of ids) noteManifest(id, label, { primary: false })
        }
      }
    }

    const worksheetName =
      String(
        options.worksheetName ??
          (spec as any)?.source?.tableau?.worksheet_name ??
          (spec as any)?.source?.tableau?.worksheetName ??
          ''
      ).trim() || null

    // Attempt to infer the Tableau datasource "reference" used in qualified field refs: [sqlproxy....].[Field]
    const ws = worksheetName
      ? (Array.isArray((workbookIr as any)?.worksheets) ? (workbookIr as any).worksheets : []).find((w: any) => {
          const a = String(w?.worksheet_name ?? w?.name ?? '').trim()
          const b = String(w?.internal_name ?? '').trim()
          return a === worksheetName || b === worksheetName
        }) || null
      : null
    const wsSpecEntry = worksheetName
      ? (Array.isArray((workbookIr as any)?.worksheet_specs) ? (workbookIr as any).worksheet_specs : []).find((w: any) => {
          const a = String(w?.worksheet_name ?? w?.name ?? '').trim()
          const b = String(w?.internal_name ?? '').trim()
          return a === worksheetName || b === worksheetName
        }) || null
      : null

    const inferSourceRef = (): string | null => {
      const expr = String(ws?.rows?.expr ?? ws?.cols?.expr ?? '')
      if (!expr) return null
      const m = expr.match(/\[([^\]]+)\]\.\[/)
      return m && m[1] ? String(m[1]) : null
    }
    const inferredSourceRef = inferSourceRef()
    const manifestSourceRef = String((options as any)?.fieldManifest?.datasource_name ?? '').trim() || null
    const sourceRef = inferredSourceRef || manifestSourceRef || null
    const sourceRefKey = sourceRef ? normalizeTableauDatasourceKey(sourceRef) : ''
    const matchesSourceRef = (dsRaw: any): boolean => {
      if (!sourceRefKey) return true
      const dsStr = String(dsRaw ?? '').trim()
      // Be tolerant to incomplete WorkbookIR/test fixtures that omit datasource on columns/instances.
      // If we cannot determine the datasource, assume it matches the active worksheet datasource.
      if (!dsStr) return true
      const dsKey = normalizeTableauDatasourceKey(dsStr)
      return dsKey ? dsKey === sourceRefKey : false
    }
    if (worksheetDebugEnabled) {
      debugLog('measureNames context', {
        sheet: worksheetName,
        inferredSourceRef,
        manifestSourceRef,
        sourceRef,
        rowsExpr: String(ws?.rows?.expr ?? ''),
        colsExpr: String(ws?.cols?.expr ?? ''),
        wsSpecRows: wsSpecEntry?.rows ?? null,
        wsSpecCols: wsSpecEntry?.cols ?? null,
      })
    }

    const cleanBracketedFieldRef = (ref: string): string => {
      // Examples:
      // - "[Sales]" -> "Sales"
      // - "[ds].[Sales]" -> "Sales"
      const parts = String(ref || '')
        .split('.')
        .map((p) => p.trim())
        .filter(Boolean)
        .map((p) => p.replace(/^\[/, '').replace(/\]$/, ''))
      return (parts[parts.length - 1] || String(ref || '')).trim()
    }

    const extractTableauTokens = (raw: string, opts?: { dedupe?: boolean }): string[] => {
      // Capture `[Field]` or `[ds].[Field]` patterns.
      const s = String(raw || '')
      const matches = s.match(/\[[^\]]+\](?:\.\[[^\]]+\])?/g) || []
      const cleaned = matches.map(cleanBracketedFieldRef).map((x) => x.trim()).filter(Boolean)
      const dedupe = opts?.dedupe !== false
      if (!dedupe) return cleaned
      const out: string[] = []
      const seen = new Set<string>()
      for (const c of cleaned) {
        const k = norm(c)
        if (!k) continue
        if (seen.has(k)) continue
        seen.add(k)
        out.push(c)
      }
      return out
    }
    const collectWorksheetShelfTokens = (raw: any, opts?: { dedupe?: boolean }): string[] => {
      const dedupe = opts?.dedupe !== false
      const out: string[] = []
      const pushToken = (tokenRaw: any) => {
        const rawText = String(tokenRaw ?? '').trim()
        if (!rawText) return
        const extracted = extractTableauTokens(rawText, { dedupe: false })
        if (extracted.length > 0) {
          for (const token of extracted) out.push(token)
          return
        }
        const cleaned = cleanBracketedFieldRef(rawText)
        if (cleaned) out.push(cleaned)
      }
      const visit = (value: any) => {
        if (value === null || typeof value === 'undefined') return
        if (typeof value === 'string') {
          pushToken(value)
          return
        }
        if (Array.isArray(value)) {
          for (const item of value) visit(item)
          return
        }
        if (typeof value !== 'object') return
        const explicitRefs = [
          (value as any)?.expr,
          (value as any)?.field_id,
          (value as any)?.fieldId,
          (value as any)?.tableau_token,
          (value as any)?.tableauToken,
          (value as any)?.field_name,
          (value as any)?.field,
          (value as any)?.name,
          (value as any)?.column,
        ]
        for (const ref of explicitRefs) visit(ref)
      }
      visit(raw)
      if (!dedupe) return out
      const deduped: string[] = []
      const seen = new Set<string>()
      for (const token of out) {
        const key = norm(token)
        if (!key || seen.has(key)) continue
        seen.add(key)
        deduped.push(token)
      }
      return deduped
    }

    const viewTokens = (() => {
      const out: string[] = []
      const pushAll = (arr: string[]) => { for (const x of arr) out.push(x) }
      pushAll(collectWorksheetShelfTokens(ws?.rows?.expr))
      pushAll(collectWorksheetShelfTokens(ws?.cols?.expr))
      pushAll(collectWorksheetShelfTokens(wsSpecEntry?.rows))
      pushAll(collectWorksheetShelfTokens(wsSpecEntry?.cols))
      return out
    })()
    const rowShelfTokens = [
      ...collectWorksheetShelfTokens(ws?.rows?.expr, { dedupe: false }),
      ...collectWorksheetShelfTokens(wsSpecEntry?.rows, { dedupe: false }),
    ]
    const colShelfTokens = [
      ...collectWorksheetShelfTokens(ws?.cols?.expr, { dedupe: false }),
      ...collectWorksheetShelfTokens(wsSpecEntry?.cols, { dedupe: false }),
    ]
    if (worksheetDebugEnabled) {
      debugLog('measureNames view tokens', { sheet: worksheetName, viewTokens })
    }

    const ciByExactNameForWorksheet = new Map<string, any>()
    const ciByTokenKeyForWorksheet = new Map<string, any[]>()
    for (const ci of fdCis) {
      if (!matchesSourceRef((ci as any)?.datasource)) continue
      const nm = String((ci as any)?.name ?? '').trim()
      if (!nm || ciByExactNameForWorksheet.has(nm)) continue
      ciByExactNameForWorksheet.set(nm, ci)
      const tokenKey = normalizeTableauFieldToken(cleanBracketedFieldRef0(nm) || nm).trim().toLowerCase()
      if (!tokenKey) continue
      const existing = ciByTokenKeyForWorksheet.get(tokenKey) ?? []
      existing.push(ci)
      ciByTokenKeyForWorksheet.set(tokenKey, existing)
    }
    const fieldCaptionKey = (raw: any): string =>
      normalizeTableauFieldKey(String(raw ?? '').trim()) || String(raw ?? '').trim().toLowerCase()
    const resolveWorksheetFieldRefForCaptionKey = (captionKeyRaw: string): string => {
      const captionKey = String(captionKeyRaw ?? '').trim().toLowerCase()
      if (!captionKey) return ''
      const tokenKeys = manifestTokenKeysByCaption.get(captionKey) ?? []
      const candidateTokenKeys = tokenKeys.length ? tokenKeys : (manifestTokenByCaption.get(captionKey) ? [manifestTokenByCaption.get(captionKey)!] : [])
      if (!candidateTokenKeys.length) return ''
      const tokenKeySet = new Set(candidateTokenKeys)
      const tokenMatch = viewTokens.find((token) => {
        const tokenNorm = normalizeTableauFieldToken(cleanBracketedFieldRef0(token) || token).trim().toLowerCase()
        return tokenKeySet.has(tokenNorm)
      })
      if (tokenMatch) return tokenMatch
      for (const tokenKey of candidateTokenKeys) {
        const ciCandidates = ciByTokenKeyForWorksheet.get(tokenKey) ?? []
        for (const ci of ciCandidates) {
          const ciName = String((ci as any)?.name ?? '').trim()
          if (ciName) return ciName
        }
      }
      for (const tokenKey of candidateTokenKeys) {
        const colHit = fdCols.find((c) => {
          const name = String((c as any)?.name ?? '').trim()
          const tokenNorm = normalizeTableauFieldToken(cleanBracketedFieldRef0(name) || name).trim().toLowerCase()
          return tokenNorm === tokenKey
        }) || null
        const colName = String((colHit as any)?.name ?? '').trim()
        if (colName) return colName
      }
      return ''
    }
    const worksheetTokenMatchesCaptionKey = (tokenRaw: any, targetCaptionKeyRaw: string): boolean => {
      const targetCaptionKey = fieldCaptionKey(targetCaptionKeyRaw)
      if (!targetCaptionKey) return false
      const token = String(tokenRaw ?? '').trim()
      if (!token) return false
      if (isMeasureNames(token)) return targetCaptionKey === fieldCaptionKey('Measure Names')
      if (isMultipleValues(token)) return targetCaptionKey === fieldCaptionKey('Measure Values')
      const tokenKey = normalizeTableauFieldToken(cleanBracketedFieldRef0(token) || token).trim().toLowerCase()
      const manifestLabelKeys = new Set<string>()
      if (tokenKey) {
        const manifestCaption = manifestCaptionByToken.get(tokenKey)
        if (manifestCaption) manifestLabelKeys.add(fieldCaptionKey(manifestCaption))
      }
      if (tokenKey) {
        const manifestLabelCandidates = Array.from(manifestTokenKeysByCaption.entries())
          .filter(([, tokenKeys]) => Array.isArray(tokenKeys) && tokenKeys.includes(tokenKey))
          .map(([labelKey]) => labelKey)
        for (const labelKey of manifestLabelCandidates) manifestLabelKeys.add(labelKey)
      }
      if (manifestLabelKeys.has(targetCaptionKey)) return true
      const ci = ciByExactNameForWorksheet.get(token) || null
      const ciCaption = String(ci?.caption ?? '').trim()
      if (ciCaption && fieldCaptionKey(ciCaption) === targetCaptionKey) return true
      const baseFieldId = String(ci?.base_field_id ?? ci?.baseFieldId ?? '').trim()
      const baseFieldClean = baseFieldId ? (cleanBracketedFieldRef0(baseFieldId) || baseFieldId) : ''
      if (baseFieldClean) {
        const meta = fdCols.find((c) => String((c as any)?.name ?? '').trim() === baseFieldClean) || null
        const metaCaption = String((meta as any)?.caption ?? '').trim()
        if (metaCaption && fieldCaptionKey(metaCaption) === targetCaptionKey) return true
      }
      const tokenMeta = fdCols.find((c) => String((c as any)?.name ?? '').trim() === token) || null
      const tokenCaption = String((tokenMeta as any)?.caption ?? '').trim()
      if (tokenCaption && fieldCaptionKey(tokenCaption) === targetCaptionKey) return true
      const fallbackTokenCaptionKey = fieldCaptionKey(cleanBracketedFieldRef0(token) || token)
      if (manifestLabelKeys.size === 0 && fallbackTokenCaptionKey === targetCaptionKey) return true
      const matchingCis = tokenKey ? (ciByTokenKeyForWorksheet.get(tokenKey) ?? []) : []
      for (const candidate of matchingCis) {
        const candidateCaption = String((candidate as any)?.caption ?? '').trim()
        if (candidateCaption && fieldCaptionKey(candidateCaption) === targetCaptionKey) return true
        const candidateBaseFieldId = String((candidate as any)?.base_field_id ?? (candidate as any)?.baseFieldId ?? '').trim()
        const candidateBaseFieldClean =
          candidateBaseFieldId ? (cleanBracketedFieldRef0(candidateBaseFieldId) || candidateBaseFieldId) : ''
        if (!candidateBaseFieldClean) continue
        const candidateMeta = fdCols.find((c) => String((c as any)?.name ?? '').trim() === candidateBaseFieldClean) || null
        const candidateMetaCaption = String((candidateMeta as any)?.caption ?? '').trim()
        if (candidateMetaCaption && fieldCaptionKey(candidateMetaCaption) === targetCaptionKey) return true
      }
      return false
    }
    const attachWorksheetShelfTokenHints = (fields: any[], shelfTokens: string[]): any[] => {
      let cursor = 0
      return fields.map((field) => {
        if (!field || typeof field !== 'object') return field
        const explicitRef = [
          (field as any)?.tableau_token,
          (field as any)?.tableau_token_raw,
          (field as any)?.field_id,
          looksTableauToken(String((field as any)?.field_name ?? '').trim()) ? String((field as any)?.field_name ?? '').trim() : '',
        ]
          .map((v) => String(v ?? '').trim())
          .find(Boolean)
        if (explicitRef) return { ...(field as any), __fg_field_ref: explicitRef }
        const fieldName = String((field as any)?.field_name ?? (field as any)?.field ?? '').trim()
        const targetKey = fieldCaptionKey(fieldName)
        if (!targetKey) return field
        const matchFrom = (start: number): string => {
          for (let i = start; i < shelfTokens.length; i++) {
            const token = String(shelfTokens[i] ?? '').trim()
            if (!token) continue
            if (!worksheetTokenMatchesCaptionKey(token, targetKey)) continue
            cursor = i + 1
            return token
          }
          return ''
        }
        const token = matchFrom(cursor) || matchFrom(0)
        const resolvedFieldRef = token || resolveWorksheetFieldRefForCaptionKey(targetKey)
        if (!resolvedFieldRef) return field
        return { ...(field as any), __fg_field_ref: resolvedFieldRef }
      })
    }
    const rowsResolved = attachWorksheetShelfTokenHints(rows, rowShelfTokens)
    const colsResolved = attachWorksheetShelfTokenHints(cols, colShelfTokens)
    const shelfFieldRef = (field: any): string => {
      const hinted = String(
        (field as any)?.__fg_field_ref ??
        (field as any)?.tableau_token ??
        (field as any)?.tableau_token_raw ??
        (field as any)?.field_id ??
        ''
      ).trim()
      if (hinted) return hinted
      const rawName = String((field as any)?.field_name ?? (field as any)?.field ?? '').trim()
      return rawName
    }

    const baseAlias = 't'
    const baseRef = quoteIdent(baseAlias)

    // Build a minimal VisualSpec for calc compilation (field resolution + parameters).
    const isCalculatedField = (c: any): boolean => Boolean(String(c?.calculation?.formula ?? '').trim())

    // Physical fields only (calculated fields are inlined during compilation).
    const sourceFields = (() => {
      const out: Array<{ id: string; label: string }> = []
      const seen = new Set<string>()
      const add = (idRaw: any, labelRaw: any) => {
        const id = String(idRaw ?? '').trim()
        const label = String(labelRaw ?? '').trim()
        if (!id || !label) return
        const k = `${id}::${norm(label)}`
        if (seen.has(k)) return
        seen.add(k)
        out.push({ id, label })
      }

      const physical = fdCols.filter((c) => !isCalculatedField(c))
      const physicalIds = new Set<string>()
      for (const c of physical) {
        const baseId = String(c?.name ?? '').trim()
        if (baseId) physicalIds.add(baseId)
        const resolvedIdRaw = String(resolveCol(baseId) || (hasExtractSchema ? '' : baseId)).trim()
        const resolvedId = (!resolvedIdRaw || isUnresolvedField(resolvedIdRaw))
          ? ''
          : canonicalizePhysicalColumnId(resolvedIdRaw)
        if (resolvedId) {
          const nameRaw = String(c?.name ?? '').trim()
          const captionRaw = String(c?.caption ?? '').trim()
          add(resolvedId, nameRaw)
          add(resolvedId, cleanBracketedFieldRef0(nameRaw))
          add(resolvedId, normalizeTableauFieldToken(nameRaw))
          add(resolvedId, captionRaw)
          add(resolvedId, normalizeTableauFieldToken(captionRaw))
        }
      }

      // Critical: Tableau formulas often reference column-instance internal tokens like `none:Field:nk`.
      // Provide those tokens as *labels* that resolve to the underlying physical column id.
      for (const ci of fdCis) {
        const baseCol = String((ci as any)?.column ?? '').trim()
        if (!baseCol || !physicalIds.has(baseCol)) continue
        const resolvedBaseColRaw = String(resolveCol(baseCol) || (hasExtractSchema ? '' : baseCol)).trim()
        const resolvedBaseCol = (!resolvedBaseColRaw || isUnresolvedField(resolvedBaseColRaw))
          ? ''
          : canonicalizePhysicalColumnId(resolvedBaseColRaw)
        if (!resolvedBaseCol) continue
        const ciName = String((ci as any)?.name ?? '').trim()
        const ciCap = String((ci as any)?.caption ?? '').trim()
        add(resolvedBaseCol, baseCol)
        add(resolvedBaseCol, cleanBracketedFieldRef0(baseCol))
        add(resolvedBaseCol, normalizeTableauFieldToken(baseCol))
        if (ciName) add(resolvedBaseCol, ciName)
        if (ciName) add(resolvedBaseCol, cleanBracketedFieldRef0(ciName))
        if (ciName) add(resolvedBaseCol, normalizeTableauFieldToken(ciName))
        if (ciCap) add(resolvedBaseCol, ciCap)
        if (ciCap) add(resolvedBaseCol, normalizeTableauFieldToken(ciCap))
      }

      return out
    })()

    // Map calculated fields (by name/caption) -> formula for inlining.
    // This allows calcs to reference other calcs without resolving to non-existent extract columns.
    _markPhase('adv_sourceFields')
    const calcByKey = new Map<string, string>()
    for (const c of fdCols) {
      const formula = String(c?.calculation?.formula ?? '').trim()
      if (!formula) continue
      const name = String(c?.name ?? '').trim()
      const caption = String(c?.caption ?? '').trim()
      if (name) calcByKey.set(norm(name), formula)
      if (caption) calcByKey.set(norm(caption), formula)

      // Also map calculated field column-instances (e.g. `usr:Calculation_123:qk`) to the formula
      // so we can inline them during compilation (these tokens are common in Tableau XML).
      if (name) {
        for (const ci of fdCis as any[]) {
          const baseCol = String(ci?.column ?? '').trim()
          if (!baseCol || baseCol !== name) continue
          const ciName = String(ci?.name ?? '').trim()
          const ciCap = String(ci?.caption ?? '').trim()
          if (ciName) calcByKey.set(norm(ciName), formula)
          if (ciCap) calcByKey.set(norm(ciCap), formula)
        }
      }
    }

    _markPhase('adv_calcByKey')
    const _inlineCalcRefsCache = new Map<string, string>()
    const inlineCalculatedRefs = (expr: string, depth = 0, seen?: Set<string>): string => {
      const s = String(expr ?? '')
      if (!s) return s
      if (depth > 8) return s
      // Cache top-level (depth=0, no seen set) calls for repeated formulas
      if (depth === 0 && !seen) {
        const cached = _inlineCalcRefsCache.get(s)
        if (cached !== undefined) return cached
      }
      const localSeen = seen ?? new Set<string>()

      // First replace qualified refs: [Source].[Field]
      let out = s.replace(/\[([^\]]+)\]\s*\.\s*\[([^\]]+)\]/g, (m, _src, field) => {
        const key = norm(field)
        const f = calcByKey.get(key)
        if (!f) return m
        if (localSeen.has(key)) return m
        localSeen.add(key)
        const expanded = inlineCalculatedRefs(f, depth + 1, localSeen)
        localSeen.delete(key)
        return `(${expanded})`
      })

      // Then replace unqualified refs: [Field]
      out = out.replace(/\[([^\]]+)\]/g, (m, field) => {
        const key = norm(field)
        const f = calcByKey.get(key)
        if (!f) return m
        if (localSeen.has(key)) return m
        localSeen.add(key)
        const expanded = inlineCalculatedRefs(f, depth + 1, localSeen)
        localSeen.delete(key)
        return `(${expanded})`
      })

      // Cache the result for top-level calls
      if (depth === 0 && !seen) _inlineCalcRefsCache.set(s, out)
      return out
    }

    const paramTypeByKey = buildParamTypeByKeyFromWorkbookIr(fdCols)

    const paramDefs = Object.entries(parameters || {}).map(([k, v]) => ({
      id: k,
      name: k,
      data_type: inferEffectiveParamType(String(k), paramTypeByKey.get(normalizeParamKey(k)) || 'unknown', v) as any,
      allow_list: false,
      current_value: v ?? null,
      template_sql: templateMode
        ? buildParamTemplatePlaceholder(String(k), paramTypeByKey.get(normalizeParamKey(k)) || 'unknown', v)
        : null,
    }))

    _markPhase('adv_inlineCalcRefs+params')
    const vizSpec: VisualSpec = {
      data_pane: {
        sources: [
          {
            id: baseAlias,
            name: baseAlias,
            // Allows qualified refs like [sqlproxy....].[Field] to resolve to our `t` alias.
            reference: sourceRef || null,
            fields: sourceFields as any,
          } as any,
        ],
        // Populate calculated fields so the calc compiler can resolve [Calc] references
        // without inlining them into nested window SQL.
        calculated_fields: (() => {
          const out: any[] = []
          const seen = new Set<string>()
          const add = (nm: string, expr: string, tableCalcsRaw: any[] | null) => {
            const name = String(nm ?? '').trim()
            const expression = String(expr ?? '').trim()
            if (!name || !expression) return
            const key = norm(name)
            if (!key) return
            // Allow multiple aliases with different names, but keep unique (name, expr) pairs stable.
            const sig = `${key}::${norm(expression)}`
            if (seen.has(sig)) return
            seen.add(sig)
            out.push({
              id: `tableau_fd:${key}`,
              name,
              expression,
              language: 'tableau',
              scope: { kind: 'source', source_id: baseAlias },
              // Keep raw Tableau table-calc attrs for later OVER clause derivation / round-tripping.
              tableau_table_calcs: Array.isArray(tableCalcsRaw) ? (tableCalcsRaw as any[]) : undefined,
            })
          }

          const isCalc = (c: any): boolean => Boolean(String(c?.calculation?.formula ?? '').trim())
          // 1) Field-dictionary calcs by name/caption
          for (const c of fdCols) {
            if (!isCalc(c)) continue
            const formula = String(c?.calculation?.formula ?? '').trim()
            const name = String(c?.name ?? '').trim()
            const caption = String(c?.caption ?? '').trim()
            const tcs = Array.isArray(c?.calculation?.table_calcs) ? (c.calculation.table_calcs as any[]) : null
            if (name) add(name, formula, tcs)
            if (caption && caption !== name) add(caption, formula, tcs)
          }

          // 2) Column-instance aliases for calculated fields (common in Tableau XML: usr:Calc_xxx:qk)
          for (const ci of fdCis as any[]) {
            const baseCol = String(ci?.column ?? '').trim()
            if (!baseCol) continue
            const meta = fdCols.find((c) => String(c?.name ?? '').trim() === baseCol) || null
            const formula = String(meta?.calculation?.formula ?? '').trim()
            if (!formula) continue
            const tcs =
              Array.isArray(ci?.table_calcs) && ci.table_calcs.length
                ? (ci.table_calcs as any[])
                : (Array.isArray(meta?.calculation?.table_calcs) ? (meta!.calculation.table_calcs as any[]) : null)
            const ciName = String(ci?.name ?? '').trim()
            const ciCap = String(ci?.caption ?? '').trim()
            if (ciName) add(ciName, formula, tcs)
            if (ciCap && ciCap !== ciName) add(ciCap, formula, tcs)
          }

          return out
        })(),
        parameters: paramDefs as any,
        sets: workbookSetDefs,
        field_overrides: [],
      },
      shelves: { rows: [], columns: [] },
      marks: { mark: 'text' },
      filters: [],
      limit: null,
    }

    _markPhase('adv_vizSpec_build')
    // Provide viz-level dimensions for LOD compile heuristics.
    const pushShelf = (fieldName: string, shelf: 'rows' | 'columns') => {
      const id = `${shelf}:${fieldName}`
      ;(vizSpec.shelves as any)[shelf].push({
        id,
        source_id: baseAlias,
        field: fieldName,
        shelf,
        aggregation: null,
      })
    }
    for (const rf of rowsResolved) {
      if ((rf as any)?.aggregation) continue
      pushShelf(shelfFieldRef(rf), 'rows')
    }
    for (const cf of colsResolved) {
      if ((cf as any)?.aggregation) continue
      pushShelf(shelfFieldRef(cf), 'columns')
    }

    // Resolve a Tableau-visible field name to a physical extract column id and/or calc metadata.
    const columnMetaCache = new Map<string, any | null>()
    const findColumnMeta = (fieldName: string): any | null => {
      const raw = String(fieldName ?? '').trim()
      if (!raw) return null
      const rawClean = cleanBracketedFieldRef0(raw) || raw
      const strictKey = normalizeTableauFieldKeyStrict(rawClean)
      const key = normalizeTableauFieldKey(rawClean)
      const cacheKey = strictKey || key
      if (!cacheKey) return null
      if (columnMetaCache.has(cacheKey)) return columnMetaCache.get(cacheKey) || null

      const exact =
        (workbookDerived ? (workbookDerived.metaByName.get(rawClean) || null) : null)
        || (_fdColsByName.get(rawClean) || null)
      if (exact) {
        columnMetaCache.set(cacheKey, exact)
        return exact
      }

      if (!looksTableauToken(rawClean) && key) {
        const resolvedFieldRef = resolveWorksheetFieldRefForCaptionKey(key)
        if (resolvedFieldRef) {
          const resolvedClean = cleanBracketedFieldRef0(resolvedFieldRef) || resolvedFieldRef
          const resolvedExact =
            (workbookDerived ? (workbookDerived.metaByName.get(resolvedClean) || null) : null)
            || (_fdColsByName.get(resolvedClean) || null)
          if (resolvedExact) {
            columnMetaCache.set(cacheKey, resolvedExact)
            return resolvedExact
          }
          const resolvedCi = ciByExactNameForWorksheet.get(resolvedFieldRef) || null
          const resolvedBaseFieldId = String((resolvedCi as any)?.base_field_id ?? (resolvedCi as any)?.baseFieldId ?? (resolvedCi as any)?.column ?? '').trim()
          const resolvedBaseName = resolvedBaseFieldId ? (cleanBracketedFieldRef0(resolvedBaseFieldId) || resolvedBaseFieldId) : ''
          const resolvedBaseMeta =
            (resolvedBaseName ? ((workbookDerived ? (workbookDerived.metaByName.get(resolvedBaseName) || null) : null) || (_fdColsByName.get(resolvedBaseName) || null)) : null)
            || null
          if (resolvedBaseMeta) {
            columnMetaCache.set(cacheKey, resolvedBaseMeta)
            return resolvedBaseMeta
          }
        }
      }

      const byNameStrict = strictKey
        ? (
          (workbookDerived ? (workbookDerived.metaByNameKeyStrict.get(strictKey) || null) : null)
          || (_fdColsByNameKeyStrict.get(strictKey) || null)
        )
        : null
      if (byNameStrict) {
        columnMetaCache.set(cacheKey, byNameStrict)
        return byNameStrict
      }

      const byName = key
        ? (
          (workbookDerived ? (workbookDerived.metaByNameKey.get(key) || null) : null)
          || (_fdColsByNameKey.get(key) || null)
        )
        : null
      if (byName) {
        columnMetaCache.set(cacheKey, byName)
        return byName
      }

      const byCaption = key
        ? (
          (workbookDerived ? (workbookDerived.metaByCaptionKey.get(key) || null) : null)
          || (_fdColsByCaptionKey.get(key) || null)
        )
        : null
      columnMetaCache.set(cacheKey, byCaption || null)
      return byCaption || null
    }

    _markPhase('adv_findColumnMeta+shelves')
    const missingPhysicalRefsForCalcFormula = (formulaRaw: string): string[] => {
      if (!hasExtractSchema) return []
      const expanded = inlineCalculatedRefs(String(formulaRaw ?? ''))
      if (!expanded) return []
      const refs: string[] = []
      const qualifiedRanges: Array<{ start: number; end: number }> = []
      expanded.replace(/\[([^\]]+)\]\s*\.\s*\[([^\]]+)\]/g, (match, sourceRaw, fieldRaw, offset) => {
        qualifiedRanges.push({ start: Number(offset) || 0, end: (Number(offset) || 0) + String(match).length })
        const source = String(sourceRaw ?? '').trim()
        if (/^parameters$/i.test(source)) return match
        refs.push(String(fieldRaw ?? '').trim())
        return match
      })
      expanded.replace(/\[([^\]]+)\]/g, (match, fieldRaw, offset) => {
        const start = Number(offset) || 0
        if (qualifiedRanges.some((range) => start >= range.start && start < range.end)) return match
        refs.push(String(fieldRaw ?? '').trim())
        return match
      })
      const missing: string[] = []
      const seen = new Set<string>()
      for (const refRaw of refs) {
        const ref = cleanBracketedFieldRef0(refRaw) || stripBracketsAroundFieldName(refRaw) || String(refRaw ?? '').trim()
        if (!ref || isMeasureNames(ref) || isMultipleValues(ref)) continue
        const refKey = normalizeTableauFieldKey(ref) || ref.toLowerCase()
        if (!refKey || seen.has(refKey)) continue
        const meta = findColumnMeta(ref)
        const metaFormula = String((meta as any)?.calculation?.formula ?? '').trim()
        if (metaFormula) continue
        const candidates = [
          ref,
          resolveCol(ref),
          (meta as any)?.name,
          (meta as any)?.caption,
          (meta as any)?.column_name,
        ]
          .map((value) => String(value ?? '').trim())
          .filter(Boolean)
        if (candidates.some((candidate) => isPresentInExtractSchema(candidate))) continue
        seen.add(refKey)
        missing.push(ref)
      }
      return missing
    }
    type CompiledTableauCalcResult = {
      sql: string
      isAgg: boolean
      isTableCalc: boolean
      invalid?: boolean
      missingPhysicalRefs?: string[]
    }
    const _compileTableauCalcCache = (() => {
      const wbAny: any = workbookDerived as any
      const existing = wbAny && wbAny.__fg_compileTableauCalcCache
      if (existing && existing instanceof Map) return existing as Map<string, CompiledTableauCalcResult>
      const m = new Map<string, CompiledTableauCalcResult>()
      if (wbAny) wbAny.__fg_compileTableauCalcCache = m
      return m
    })()
    const compileTableauCalc = (expression: string, overClause: string): CompiledTableauCalcResult => {
      const prefix = compiledFormulaContextSig ? `${compiledFormulaContextSig}\0` : ''
      const _cacheKey = `${prefix}${expression}\0${overClause}`
      const _cached = _compileTableauCalcCache.get(_cacheKey)
      if (_cached) return _cached
      const _storeAndReturn = (r: CompiledTableauCalcResult) => {
        _compileTableauCalcCache.set(_cacheKey, r)
        try {
          const max = 5000
          if (_compileTableauCalcCache.size > max) {
            let n = _compileTableauCalcCache.size - max
            for (const k of _compileTableauCalcCache.keys()) {
              _compileTableauCalcCache.delete(k)
              n--
              if (n <= 0) break
            }
          }
        } catch {
          // ignore
        }
        return r
      }
      const preserveCalculatedRefsForTableCalcs =
        /\b(?:LOOKUP|WINDOW_[A-Z_]+|RUNNING_[A-Z_]+|FIRST\s*\(|LAST\s*\(|INDEX\s*\(|RANK(?:_(?:DENSE|MODIFIED))?\s*\()/i
          .test(expression)
      const expandedExpression = preserveCalculatedRefsForTableCalcs
        ? String(expression ?? '')
        : inlineCalculatedRefs(expression)
      const rewrite = tryRewriteMonthlyAvgDiff(expandedExpression, baseRef)
      if (rewrite) {
        if (worksheetDebugEnabled) {
          debugLog('tableCalc rewrite', {
            expression,
            expandedExpression,
            rewrittenSql: rewrite,
          })
        }
        return _storeAndReturn({ sql: rewrite, isAgg: true, isTableCalc: /\bOVER\s*\(/i.test(rewrite) })
      }
      const windowMaxMatch = expandedExpression.match(/^WINDOW_MAX\s*\(\s*(.+)\s*\)\s*$/i)
      if (windowMaxMatch) {
        const inner = String(windowMaxMatch[1] ?? '').trim()
        const innerRewrite = tryRewriteMonthlyAvgDiff(inner, baseRef)
        if (innerRewrite) {
          const frame = 'ROWS BETWEEN UNBOUNDED PRECEDING AND UNBOUNDED FOLLOWING'
          const over = String(overClause || '').trim()
          const overFull = over ? `${over} ${frame}` : frame
          const sql = `MAX(${innerRewrite}) OVER (${overFull})`
          if (worksheetDebugEnabled) {
            debugLog('tableCalc rewrite', {
              expression,
              expandedExpression,
              rewrittenSql: sql,
              windowed: true,
            })
          }
          return _storeAndReturn({ sql, isAgg: true, isTableCalc: true })
        }
      }
      const res = compileTableauExpressionToDuckdbSql({
        spec: vizSpec,
        scope: { kind: 'source', source_id: baseAlias },
        expression: expandedExpression,
      })
      let sql = String(res.duckdb_sql || '').trim() || 'NULL'
      if (sql.includes('__TBLCALC__')) {
        sql = sql.replaceAll('__TBLCALC__', overClause)
      }
      if (sql.includes('__TBLCALC_PARTITION__')) {
        const partitionOnly = (() => {
          const s = String(overClause || '').trim()
          if (!s) return ''
          const upper = s.toUpperCase()
          const idxOrder = upper.indexOf('ORDER BY')
          const idxRows = upper.indexOf('ROWS')
          const idxRange = upper.indexOf('RANGE')
          const cuts = [idxOrder, idxRows, idxRange].filter((i) => i >= 0)
          const end = cuts.length ? Math.min(...cuts) : s.length
          return s.slice(0, end).trim()
        })()
        sql = sql.replaceAll('__TBLCALC_PARTITION__', partitionOnly)
      }
      sql = normalizeCompiledSourceAliases(sql, vizSpec, baseAlias)
      sql = rewriteCompiledSqlPhysicalRefs(sql, { sourceAliases: [baseAlias, CALC_BASE_ALIAS, 't'] })
      sql = scrubUnresolvedFieldRefs(sql)
      // Algebraic cleanup: avoid window/aggregate-over-NULL artifacts that the compiler can emit
      // when upstream field resolution yields NULL. These should behave like NULL, not a window fn.
      try {
        const nullAtom = String.raw`(?:\(+\s*)?NULL(?:\s*\)+)?`
        const step = (s: string): string => {
          let out = s
          out = out.replace(
            new RegExp(String.raw`\\b(MAX|MIN|AVG|SUM|COUNT)\\s*\\(\\s*(?:\\(+\\s*)?(MAX|MIN|AVG|SUM|COUNT)\\s*\\(\\s*${nullAtom}\\s*\\)(?:\\s*\\)+)?\\s*\\)\\s*OVER\\s*\\([^)]*\\)`, 'gi'),
            'NULL'
          )
          out = out.replace(
            new RegExp(String.raw`\\b(MAX|MIN|AVG|SUM|COUNT)\\s*\\(\\s*${nullAtom}\\s*\\)\\s*OVER\\s*\\([^)]*\\)`, 'gi'),
            'NULL'
          )
          out = out.replace(
            new RegExp(String.raw`\\b(MAX|MIN|AVG|SUM|COUNT)\\s*\\(\\s*TRY_CAST\\s*\\(\\s*${nullAtom}[^)]*\\)\\s*\\)\\s*OVER\\s*\\([^)]*\\)`, 'gi'),
            'NULL'
          )
          out = out.replace(new RegExp(String.raw`\\b(MAX|MIN|AVG|SUM|COUNT)\\s*\\(\\s*(?:\\(+\\s*)?(MAX|MIN|AVG|SUM|COUNT)\\s*\\(\\s*${nullAtom}\\s*\\)(?:\\s*\\)+)?\\s*\\)\\s*`, 'gi'), 'NULL')
          out = out.replace(new RegExp(String.raw`\\b(MAX|MIN|AVG|SUM|COUNT)\\s*\\(\\s*${nullAtom}\\s*\\)\\s*`, 'gi'), 'NULL')
          out = out.replace(/\bSUM\s*\(\s*TRY_CAST\s*\(\s*NULL[^)]*\)\s*\)\s*/gi, 'NULL')
          return out
        }
        for (let i = 0; i < 6; i++) {
          const prev = sql
          sql = step(sql)
          if (sql === prev) break
        }
      } catch { }
      const validation = validateSqlPhysicalRefs(sql)
      if (!validation.ok) {
        if (debugFieldMapping || validation.missingPhysicalRefs.some((ref) => shouldTracePlannerColumn(ref))) {
          logFieldMapping('compileTableauCalc missing physical refs', {
            worksheetName: worksheetNameDebug,
            expression,
            missingPhysicalRefs: validation.missingPhysicalRefs.slice(0, 8),
            sqlPreview: sql.slice(0, 500),
          })
        }
        return _storeAndReturn({
          sql: 'NULL',
          isAgg: false,
          isTableCalc: false,
          invalid: true,
          missingPhysicalRefs: validation.missingPhysicalRefs,
        })
      }
      return _storeAndReturn({ sql, isAgg: Boolean(res.is_aggregate), isTableCalc: Boolean(res.is_table_calc) })
    }

    _markPhase('adv_compileTableauCalc_def')
    // Lazy compiled-SQL→caption map: only built on first access to avoid ~14s upfront cost.
    const getCalcCaptionByCompiledSql = (): Map<string, string> => {
      if (_calcCaptionByCompiledSqlCache) return _calcCaptionByCompiledSqlCache
      _calcCaptionByCompiledSqlCache = new Map<string, string>()
      for (const c of fdCols) {
        const formula = String((c as any)?.calculation?.formula ?? '').trim()
        const caption = String((c as any)?.caption ?? '').trim()
        if (!formula || !caption) continue
        try {
          const compiled = compileTableauCalc(formula, '')
          if (compiled.invalid) continue
          const key = normalizeSqlExpr(compiled.sql)
          if (key && !_calcCaptionByCompiledSqlCache.has(key)) _calcCaptionByCompiledSqlCache.set(key, caption)
        } catch {}
      }
      return _calcCaptionByCompiledSqlCache
    }
    _markPhase('adv_caption_precompile_loop')

    // Grouping dimensions: all row dims + all column dims except Measure Names (which we emit as a literal).
    // Keep the original shelf field objects when available so we can respect sort hints (e.g. authoring overrides).
    const groupDims: any[] = []
    let rowDimCount = 0
    for (const rf of rowsResolved) {
      const fn = String((rf as any)?.field_name ?? '')
      if (isMeasureNames(fn) || isMultipleValues(fn)) continue
      groupDims.push((rf && typeof rf === 'object') ? rf : { field_name: fn })
      rowDimCount++
    }
    for (const cf of colsResolved) {
      const fn = String((cf as any)?.field_name ?? '')
      if (isMeasureNames(fn) || isMultipleValues(fn)) continue
      groupDims.push((cf && typeof cf === 'object') ? cf : { field_name: fn })
    }
    const groupDimFieldRef = (dim: any): string => shelfFieldRef(dim)
    const measureExprProjectedRefKeys = (() => {
      const out = new Set<string>()
      const add = (raw: any) => {
        const value = String(raw ?? '').trim()
        if (!value) return
        const cleaned = cleanBracketedFieldRef0(value) || stripBracketsAroundFieldName(value) || value
        const key = normalizeTableauFieldKey(cleaned) || cleaned.toLowerCase()
        if (key) out.add(key)
      }
      for (const col of (Array.isArray(extractColumns) ? extractColumns : [])) add(col)
      for (const dim of groupDims) {
        add(dim?.field_name)
        add(dim?.display_name)
        add(dim?.field_display_name)
        add(dim?.label)
        add(dim?.caption)
        add(groupDimFieldRef(dim))
      }
      return out
    })()

    _markPhase('adv_vizSpec+setup')
    // Build dim select expressions (and keep the raw expressions for table-calc OVER clauses).
    const weeklyTimePeriodOverrides = null
    const rewriteWeeklyPhysicalRefs = (sqlIn: string): string => String(sqlIn ?? '')
    resolvedWeeklyTimePeriodExprForField = null
    const applyGroupDimTimeGrain = (expr: string, grain: string | null, isDiscrete?: boolean | null): string => {
      const g = String(grain ?? '').trim().toLowerCase()
      if (!g) return expr
      const ts = `TRY_CAST(${expr} AS TIMESTAMP)`
      if (isDiscrete) {
        if (g === 'year') return `EXTRACT(year FROM ${ts})`
        if (g === 'quarter' || g === 'month' || g === 'week' || g === 'day' || g === 'hour' || g === 'minute' || g === 'second') {
          return `DATE_TRUNC('${g}', ${ts})`
        }
        return expr
      }
      if (g === 'quarter' || g === 'month' || g === 'week' || g === 'day' || g === 'hour' || g === 'minute' || g === 'second' || g === 'year') {
        return `DATE_TRUNC('${g}', ${ts})`
      }
      return expr
    }

    const dimExprs: string[] = []
    const dimSelects: string[] = []
    for (const d of groupDims) {
      const fieldName = String(d.field_name || '')
      if (!fieldName) continue
      const fieldRef = groupDimFieldRef(d) || fieldName
      const timeGrainRaw = (d as any)?.time_grain ?? (d as any)?.timeGrain ?? null
      const timeGrain =
        String(timeGrainRaw ?? '').trim().toLowerCase() ||
        inferTimeGrainFromPill(d) ||
        null
      const isDiscreteRaw = (d as any)?.is_discrete
      const typeRaw = String((d as any)?.type ?? '').trim().toLowerCase()
      const isDiscrete =
        typeof isDiscreteRaw === 'boolean'
          ? isDiscreteRaw
          : (typeRaw === 'discrete' ? true : (typeRaw === 'continuous' ? false : null))
      let expr = fieldExprFor(fieldRef, baseRef)
      const fieldNameNorm = norm(fieldName)
      if (fieldNameNorm.includes('time period') && !weeklyTimePeriodOverrides) {
        const calcFormula = lookupCalculatedFormula(fieldRef) || lookupCalculatedFormula(fieldName)
        if (debugFieldMapping) {
          try {
            console.log('[FG-TP-DEBUG] dim loop pre-force', {
              fieldName,
              fieldRef,
              hasWeeklyOverride: Boolean(weeklyTimePeriodOverrides),
              hasCalcFormula: Boolean(calcFormula),
              exprPreview: String(expr ?? '').slice(0, 240),
            })
          } catch { }
        }
        if (calcFormula) {
          const forced = compileTableauCalc(calcFormula, '')
          const forcedSql = String(forced?.sql ?? '').trim()
          const hasForcedErrors = Boolean(forced.invalid) || (Array.isArray((forced as any)?.errors) && (forced as any).errors.length > 0)
          if (forcedSql && !isNullishExpr(forcedSql) && !hasForcedErrors) {
            expr = forcedSql
          } else if (debugFieldMapping) {
            try {
              console.log('[FG-FIELD-MAP] time-period compile fallback', {
                fieldName,
                hasForcedErrors,
                forcedSql: forcedSql.slice(0, 240),
              })
            } catch { }
          }
        }
        if (debugFieldMapping) {
          try {
            console.log('[FG-TP-DEBUG] dim loop final', {
              fieldName,
              exprPreview: String(expr ?? '').slice(0, 240),
            })
          } catch { }
          pushFieldMappingTrace({
            phase: 'tp_dim_expr',
            message: 'dim_loop_final',
            data: {
              worksheetName: worksheetNameDebug,
              fieldName,
              expr: String(expr ?? ''),
              hasWeeklyOverride: Boolean(weeklyTimePeriodOverrides),
            },
          })
        }
      }
      if (debugFieldMapping && fieldNameNorm.includes('time period')) {
        pushFieldMappingTrace({
          phase: 'tp_dim_expr',
          message: 'dim_loop_post_time_period',
          data: {
            worksheetName: worksheetNameDebug,
            fieldName,
            expr: String(expr ?? ''),
            hasWeeklyOverride: Boolean(weeklyTimePeriodOverrides),
          },
        })
      }
      if (isNullishExpr(expr)) {
        const meta = findColumnMeta(fieldRef) || findColumnMeta(fieldName)
        const colId = String(meta?.name ?? fieldName)
        const calcFormula = preferredCalculatedFormula(fieldRef, fieldName, colId)
        if (calcFormula) {
          const compiled = compileTableauCalc(calcFormula, '')
          if (!compiled.invalid) expr = compiled.sql
        } else {
          const resolved = resolveCol(fieldRef) || resolveCol(colId)
          if (resolved && resolved !== UNRESOLVED_FIELD) {
            expr = `${baseRef}.${quoteIdent(resolved)}`
          }
        }
      }
      if (isNullishExpr(expr)) {
        // Strict: surface unresolved fields immediately.
        expr = UNRESOLVED_FIELD
        noteUnresolved(fieldName, 'dim_unresolved', { expr })
      }
      if (!isUnresolvedField(expr)) {
        expr = applyGroupDimTimeGrain(expr, timeGrain, isDiscrete)
      }
      dimExprs.push(expr)
      dimSelects.push(`${expr} AS ${quoteIdent(fieldName)}`)
    }
    _markPhase('adv_dimExprs')
      let baseWideSourceSql = `${parquetRef} AS ${baseRef}`

    const hoistGroupedDimExprsToSource = (): void => {
      if (groupDims.length <= 0) return
      const bareBaseRef = String(baseRef).replace(/"/g, '')
      const simpleQualifiedRef = new RegExp(
        `^(?:(?:"${bareBaseRef}"|${bareBaseRef})\\s*\\.\\s*)?"[^"]+"$`,
        'i',
      )
      const simpleBareRef = /^(?:"[^"]+"|[A-Za-z_][A-Za-z0-9_]*)$/i
      const dependencyPattern = (fieldName: string): RegExp => {
        const quotedField = quoteIdent(fieldName).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        const escapedBaseRef = bareBaseRef.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        return new RegExp(
          `(?:^|[^A-Za-z0-9_])(?:${quotedField}|${escapedBaseRef}\\s*\\.\\s*${quotedField})(?:$|[^A-Za-z0-9_])`,
          'i',
        )
      }
      const candidateNames = groupDims
        .map((d) => String(d?.field_name ?? '').trim())
        .filter(Boolean)
      const isHoistCandidate = (idx: number): boolean => {
        const expr = String(dimExprs[idx] ?? '').trim()
        if (!expr) return false
        if (!canSqlExpressionAppearInGroupBy(expr)) return false
        if (simpleQualifiedRef.test(expr) || simpleBareRef.test(expr)) return false
        return /\b(?:CASE|POSITION|TRY_CAST|CAST|DATE_TRUNC|TO_TIMESTAMP|EXTRACT)\b/i.test(expr)
      }
      const remaining = new Set<number>()
      for (let i = 0; i < groupDims.length; i += 1) {
        if (isHoistCandidate(i)) remaining.add(i)
      }
      while (remaining.size > 0) {
        const ready = Array.from(remaining).filter((idx) => {
          const expr = String(dimExprs[idx] ?? '').trim()
          if (!expr) return false
          return candidateNames.every((fieldName, fieldIdx) => {
            if (fieldIdx === idx || !remaining.has(fieldIdx)) return true
            return !dependencyPattern(fieldName).test(expr)
          })
        })
        if (ready.length <= 0) break
        const projected = ready
          .map((idx) => {
            const fieldName = String(groupDims[idx]?.field_name ?? '').trim()
            const expr = String(dimExprs[idx] ?? '').trim()
            return `${expr} AS ${quoteIdent(fieldName)}`
          })
          .join(', ')
        baseWideSourceSql = `(SELECT ${baseRef}.*, ${projected} FROM ${baseWideSourceSql}) AS ${baseRef}`
        for (const idx of ready) {
          const fieldName = String(groupDims[idx]?.field_name ?? '').trim()
          const projectedExpr = `${baseRef}.${quoteIdent(fieldName)}`
          dimExprs[idx] = projectedExpr
          dimSelects[idx] = `${projectedExpr} AS ${quoteIdent(fieldName)}`
          remaining.delete(idx)
        }
      }
    }
    hoistGroupedDimExprsToSource()

    // Build a map from field name to dimension expression (for filter matching)
    // This ensures filters use the same time grain expressions as the SELECT clause
    const dimExprByKey = new Map<string, string>()
    for (let i = 0; i < groupDims.length; i++) {
      const fieldName = String(groupDims[i]?.field_name ?? '').trim()
      const expr = String(dimExprs[i] ?? '').trim()
      if (!fieldName || !expr) continue
      const keyBase = normKey(fieldName) || fieldName.toLowerCase()
      if (keyBase) {
        dimExprByKey.set(keyBase, expr)
      }
    }

    const hasGroupedTableCalcScope = dimExprs.some((expr) => Boolean(String(expr ?? '').trim()))
    const tableCalcDimExprs = groupDims.map((d, i) => {
      const fieldName = String(d?.field_name ?? '').trim()
      const rawExpr = String(dimExprs[i] ?? '').trim()
      if (!hasGroupedTableCalcScope || !fieldName) return rawExpr
      // Grouped table-calcs should partition and order by the materialized grouped-dimension
      // alias, not by reopening the raw source expression. Reopening calculated dims such as
      // Time Period* or East/West leaks expensive source CASE trees back into grouped windows.
      return `${baseRef}.${quoteIdent(fieldName)}`
    })
    const qualifyGroupedWindowScopeExpr = (rawExpr: string): string => {
      const expr = String(rawExpr ?? '').trim()
      if (!expr) return expr
      if (/^(?:"[^"]+"|[A-Za-z_][A-Za-z0-9_]*)$/i.test(expr)) {
        return `${baseRef}.${expr.startsWith('"') ? expr : quoteIdent(expr)}`
      }
      return expr
    }
    const groupedWindowScopeExprBySourceCol = new Map<string, string>()
    for (let i = 0; i < groupDims.length; i++) {
      const dim = groupDims[i]
      const fieldName = String(dim?.field_name ?? '').trim()
      const targetExpr = qualifyGroupedWindowScopeExpr(String(tableCalcDimExprs[i] ?? '').trim())
      if (!fieldName || !targetExpr) continue
      const fieldRef = groupDimFieldRef(dim) || fieldName
      const resolvedRaw = String(resolveCol(fieldRef) || resolveCol(fieldName) || '').trim()
      const resolved = (!resolvedRaw || isUnresolvedField(resolvedRaw)) ? '' : resolvedRaw
      if (!resolved) continue
      const sourceRef = `${baseRef}.${quoteIdent(resolved)}`
      if (normalizeSqlExpr(sourceRef) === normalizeSqlExpr(targetExpr)) continue
      groupedWindowScopeExprBySourceCol.set(resolved, targetExpr)
    }
    const rewriteCompiledWindowScopeToGroupedDims = (rawSql: string): string => {
      const sql = String(rawSql ?? '')
      if (!sql || groupedWindowScopeExprBySourceCol.size === 0) return sql
      const rewriteWindowScopeClause = (clauseRaw: string): string => {
        let clause = String(clauseRaw ?? '')
        for (const [sourceCol, targetExpr] of groupedWindowScopeExprBySourceCol.entries()) {
          const sourceEsc = escapeRegExp(sourceCol)
          clause = clause
            .replace(new RegExp(`"t"\\s*\\.\\s*"${sourceEsc}"`, 'gi'), targetExpr)
            .replace(new RegExp(`\\bt\\s*\\.\\s*"${sourceEsc}"`, 'gi'), targetExpr)
            .replace(new RegExp(`"t"\\s*\\.\\s*${sourceEsc}\\b`, 'gi'), targetExpr)
            .replace(new RegExp(`\\bt\\s*\\.\\s*${sourceEsc}\\b`, 'gi'), targetExpr)
        }
        return clause
      }
      let out = ''
      let cursor = 0
      while (cursor < sql.length) {
        const overIdx = sql.slice(cursor).search(/\bOVER\s*\(/i)
        if (overIdx < 0) {
          out += sql.slice(cursor)
          break
        }
        const absOverIdx = cursor + overIdx
        const openIdx = sql.indexOf('(', absOverIdx)
        if (openIdx < 0) {
          out += sql.slice(cursor)
          break
        }
        out += sql.slice(cursor, openIdx + 1)
        let depth = 1
        let idx = openIdx + 1
        while (idx < sql.length && depth > 0) {
          const ch = sql[idx]
          if (ch === '(') depth += 1
          else if (ch === ')') depth -= 1
          idx += 1
        }
        if (depth !== 0) {
          out += sql.slice(openIdx + 1)
          break
        }
        const clause = sql.slice(openIdx + 1, idx - 1)
        out += rewriteWindowScopeClause(clause)
        out += ')'
        cursor = idx
      }
      return out
    }
    // Default table-calc OVER clause:
    // - Partition by row dimensions (so we compute across Time Period columns per row)
    // - Order by Time Period End Dates (if present) else Time Period
    const rowDimExprs = tableCalcDimExprs.slice(0, rowDimCount)
    const colDimExprs = tableCalcDimExprs.slice(rowDimCount)

    // Optional ordering overrides (manual/semantic member ordering).
    // Source: `workbookIr.meta.fusegrid_overrides_v1.sheets[worksheetName].query.ordering.field_orders`
    const sheetOverride = (() => {
      try {
        const wsName0 = String(worksheetName ?? '').trim()
        const internal0 = String((ws as any)?.internal_name ?? (ws as any)?.internalName ?? '').trim()
        const name0 = String((ws as any)?.name ?? '').trim()
        const ov = (workbookIr as any)?.meta?.fusegrid_overrides_v1
        const sheets =
          ov && typeof ov === 'object' && (ov as any).sheets && typeof (ov as any).sheets === 'object'
            ? ((ov as any).sheets as Record<string, any>)
            : null
        if (!sheets) return null
        const keys = [wsName0, internal0, name0].map((x) => String(x || '').trim()).filter(Boolean)
        for (const k of keys) {
          if (Object.prototype.hasOwnProperty.call(sheets, k)) return (sheets as any)[k]
        }
        return null
      } catch {
        return null
      }
    })()
    const queryOv = (sheetOverride && typeof (sheetOverride as any).query === 'object') ? ((sheetOverride as any).query as any) : null
    const orderingOv =
      (queryOv && typeof queryOv === 'object' && typeof (queryOv as any).ordering === 'object') ? (queryOv as any).ordering
        : (queryOv && typeof queryOv === 'object' && typeof (queryOv as any).order === 'object') ? (queryOv as any).order
          : null
    const fieldOrdersRaw =
      (orderingOv && typeof orderingOv === 'object' && typeof (orderingOv as any).field_orders === 'object') ? (orderingOv as any).field_orders
        : (orderingOv && typeof orderingOv === 'object' && typeof (orderingOv as any).fieldOrders === 'object') ? (orderingOv as any).fieldOrders
          : (queryOv && typeof queryOv === 'object' && typeof (queryOv as any).field_orders === 'object') ? (queryOv as any).field_orders
            : (queryOv && typeof queryOv === 'object' && typeof (queryOv as any).fieldOrders === 'object') ? (queryOv as any).fieldOrders
              : null

    const manualOrderByFieldKey = (() => {
      const m = new Map<string, string[]>()
      const add = (fieldKeyRaw: any, valuesRaw: any) => {
        const fk = norm(fieldKeyRaw)
        if (!fk) return
        const arr0 = Array.isArray(valuesRaw) ? valuesRaw : null
        if (!arr0) return
        const vals = arr0.map((v) => String(v ?? '').trim()).filter((v) => v.length > 0)
        if (vals.length === 0) return
        if (!m.has(fk)) m.set(fk, vals)
      }
      if (fieldOrdersRaw && typeof fieldOrdersRaw === 'object') {
        for (const [k, v] of Object.entries(fieldOrdersRaw as any)) add(k, v)
      }
      return m
    })()

    const manualOrderForField = (fieldName: string): string[] | null => {
      const k = norm(fieldName)
      if (!k) return null
      const vals = manualOrderByFieldKey.get(k) || null
      if (vals && vals.length > 0) return vals

      // Prefer the XML-derived sort config (Tableau "Manual" member ordering).
      // This is the primary source of truth; overrides exist only for ad-hoc experimentation.
      const dim = groupDims.find((d) => norm((d as any)?.field_name) === k) as any
      const sortObj = dim?.sort ?? dim?.sort_config ?? dim?.sortConfig ?? null
      const arr0 =
        (sortObj && typeof sortObj === 'object' && Array.isArray((sortObj as any).custom_order))
          ? (sortObj as any).custom_order
          : (sortObj && typeof sortObj === 'object' && Array.isArray((sortObj as any).customOrder))
            ? (sortObj as any).customOrder
            : null
      const vals2 = Array.isArray(arr0) ? arr0.map((v) => String(v ?? '').trim()).filter(Boolean) : []
      return vals2.length ? vals2 : null
    }

    const manualOrderCaseExpr = (expr: string, orderedValues: string[]): string => {
      const whens = orderedValues.map((v, i) => `WHEN ${escapeValue(v)} THEN ${i}`).join(' ')
      // Add a deterministic fallback rank for unknown values.
      return `CASE ${expr} ${whens} ELSE ${orderedValues.length + 999999} END`.replace(/\s+/g, ' ').trim()
    }

    const dirForFieldName = (fieldName: string): 'ASC' | 'DESC' => {
      const k = norm(fieldName)
      if (!k) return 'ASC'
      const dim = groupDims.find((d) => norm((d as any)?.field_name) === k) as any
      const sortObj = dim?.sort ?? dim?.sort_config ?? dim?.sortConfig ?? null
      const sortRaw = String(
        (typeof sortObj === 'string')
          ? sortObj
          : (sortObj && typeof sortObj === 'object')
            ? (sortObj as any)?.direction ?? (sortObj as any)?.dir ?? (sortObj as any)?.order ?? ''
            : (dim?.sort_direction ?? dim?.sortDirection ?? '')
      ).trim().toLowerCase()
      if (sortRaw === 'desc') return 'DESC'
      if (sortRaw === 'asc') return 'ASC'
      if (k === 'reportenddate') return 'DESC'
      if (k.includes('end date')) return 'DESC'
      if (k.includes('end dates')) return 'DESC'
      // For "Time Period" label fields, Tableau ordering is usually a custom/manual order,
      // so direction alone is insufficient. The ORDER BY expression is handled separately.
      return 'ASC'
    }

    const orderByForField = (expr: string, fieldName: string): { expr: string; dir: 'ASC' | 'DESC' } => {
      const manual = manualOrderForField(fieldName)
      if (manual && manual.length) {
        return { expr: manualOrderCaseExpr(expr, manual), dir: 'ASC' }
      }
      return { expr, dir: dirForFieldName(fieldName) }
    }

    const pickOrder = (): { expr: string; idx: number } | null => {
      // Prefer end-date as an ordering key (more reliable than textual period labels).
      const idxEnd = groupDims.findIndex((d) => norm(d.field_name) === 'time period end dates')
      if (idxEnd >= 0) return { expr: tableCalcDimExprs[idxEnd], idx: idxEnd }
      const idxTp = groupDims.findIndex((d) => norm(d.field_name) === 'time period')
      if (idxTp >= 0) return { expr: tableCalcDimExprs[idxTp], idx: idxTp }
      // Fallback: last dim
      return tableCalcDimExprs.length ? { expr: tableCalcDimExprs[tableCalcDimExprs.length - 1], idx: tableCalcDimExprs.length - 1 } : null
    }
    const pickedOrder = pickOrder()
    const orderExpr = pickedOrder ? pickedOrder.expr : null
    const pickedFieldName = pickedOrder ? String(groupDims[pickedOrder.idx]?.field_name ?? '') : ''
    const order = orderExpr ? orderByForField(orderExpr, pickedFieldName) : null
    // When the default ordering dimension also lives on the row shelf, it is the table-calc
    // addressing dimension, not an additional partition key. Leaving it in both PARTITION BY
    // and ORDER BY collapses each partition to a single row (for example reportenddate in
    // LOOKUP(..., 12) views), which forces every offset lookup to NULL.
    const defaultPartitionExprs =
      pickedOrder && pickedOrder.idx >= 0 && pickedOrder.idx < rowDimCount
        ? rowDimExprs.filter((_expr, idx) => idx !== pickedOrder.idx)
        : rowDimExprs
    const overParts: string[] = []
    if (defaultPartitionExprs.length) overParts.push(`PARTITION BY ${defaultPartitionExprs.join(', ')}`)
    if (order) overParts.push(`ORDER BY ${order.expr} ${order.dir}`)
    const defaultOverClause = overParts.join(' ') || 'ORDER BY 1'

    let emitTotalsForMeasureOverClause = false
    let groupByExprsForMeasureOverClause: readonly string[] = []
    applyTableauTableCalcMetadataToVizSpec()

    const tableauAttrSql = (expr: string): string => {
      const e = String(expr || '').trim() || 'NULL'
      // Tableau ATTR: value if unique else "*"
      return `CASE WHEN COUNT(DISTINCT CAST(${e} AS VARCHAR)) = 1 THEN MIN(CAST(${e} AS VARCHAR)) ELSE '*' END`
    }

    const buildDatePartSql = (fnUpper: string, innerExpr: string): string | null => {
      const f = fnUpper.toUpperCase()
      const inner = String(innerExpr || '').trim()
      if (!inner) return null
      if (f === 'YEAR') return `EXTRACT(year FROM ${inner})`
      if (f === 'QUARTER') return `EXTRACT(quarter FROM ${inner})`
      if (f === 'MONTH') return `EXTRACT(month FROM ${inner})`
      if (f === 'DAY') return `EXTRACT(day FROM ${inner})`
      if (f === 'HOUR') return `EXTRACT(hour FROM ${inner})`
      if (f === 'MINUTE') return `EXTRACT(minute FROM ${inner})`
      if (f === 'SECOND') return `EXTRACT(second FROM ${inner})`
      return null
    }

    const isGroupedDim = (fieldName: string): boolean => {
      const k = normalizeTableauFieldKey(String(fieldName ?? ''))
      if (!k) return false
      return groupDims.some((d) => normalizeTableauFieldKey(String(d?.field_name ?? '')) === k)
    }

    const buildTooltipExtraSelects = (): Array<{ alias: string; expr: string }> => {
      if (!tooltipNeeds.length) return []
      const out: Array<{ alias: string; expr: string }> = []
      const seen = new Set<string>()

      // Helper to resolve a token arg to a physical column expr (or compiled calc expr)
      const resolveFieldExpr = (
        fieldRefRaw: string,
        overClause: string,
      ): { expr: string; isAgg: boolean; isTableCalc: boolean; meta: any; resolvedField: string } | null => {
        const fieldRef = stripBracketsAroundFieldName(fieldRefRaw)
        if (!fieldRef) return null
        const meta = findColumnMeta(fieldRef)
        const colId = String(meta?.name ?? fieldRef)
        const calcFormula = preferredCalculatedFormula(fieldRef, colId, meta?.caption, meta?.name)
        if (calcFormula) {
          const overClauseForCalc = Array.isArray(meta?.calculation?.table_calcs) && meta!.calculation!.table_calcs!.length
            ? overClauseForMeasure(meta!.calculation!.table_calcs as any, {
                source: 'tooltip_calc',
                fieldRef,
                columnId: colId,
                displayName: String(meta?.caption ?? '').trim() || null,
                ciName: String(meta?.name ?? '').trim() || null,
                ciCaption: String(meta?.caption ?? '').trim() || null,
              })
            : overClause
          const compiled = compileTableauCalc(calcFormula, overClauseForCalc)
          if (compiled.invalid) return null
          return { expr: compiled.sql, isAgg: compiled.isAgg, isTableCalc: compiled.isTableCalc, meta, resolvedField: colId }
        }
        const resolved = resolveCol(colId)
        if (!resolved || resolved === UNRESOLVED_FIELD) return null
        return { expr: `${baseRef}.${quoteIdent(resolved)}`, isAgg: false, isTableCalc: false, meta, resolvedField: resolved }
      }

      const aggFnToSql = (fnUpper: string): { kind: 'agg' | 'countd'; fn: string } | null => {
        const f = fnUpper.toUpperCase()
        if (f === 'SUM') return { kind: 'agg', fn: 'SUM' }
        if (f === 'AVG') return { kind: 'agg', fn: 'AVG' }
        if (f === 'MIN') return { kind: 'agg', fn: 'MIN' }
        if (f === 'MAX') return { kind: 'agg', fn: 'MAX' }
        if (f === 'COUNT') return { kind: 'agg', fn: 'COUNT' }
        if (f === 'COUNTD') return { kind: 'countd', fn: 'COUNT' }
        if (f === 'AGG') return { kind: 'agg', fn: 'AGG' } // special: don't wrap
        return null
      }
      const normalizeAggUpper = (raw: any): string | null => {
        const a0 = String(raw ?? '').trim().toLowerCase()
        if (!a0) return null
        if (a0 === 'count distinct' || a0 === 'count_distinct' || a0 === 'countdistinct' || a0 === 'ctd') return 'COUNTD'
        if (a0 === 'average' || a0 === 'mean') return 'AVG'
        if (a0 === 'sum' || a0 === 'avg' || a0 === 'min' || a0 === 'max' || a0 === 'count' || a0 === 'countd' || a0 === 'agg') {
          return a0.toUpperCase()
        }
        return null
      }
      const tooltipFieldKey = (raw: any): string => {
        const cleaned = cleanBracketedFieldRef0(String(raw ?? '').trim()) || String(raw ?? '').trim()
        return normalizeTableauFieldKey(cleaned)
      }
      const findSpecAggForTooltipField = (fieldNameRaw: string): string | null => {
        const key = tooltipFieldKey(fieldNameRaw)
        if (!key) return null
        const shelfFields = [...(crosstabSpec.rows || []), ...(crosstabSpec.columns || [])] as any[]
        for (const f of shelfFields) {
          const fk = tooltipFieldKey((f as any)?.field_name)
          if (!fk || fk !== key) continue
          const aggHit = normalizeAggUpper((f as any)?.aggregation)
          if (aggHit) return aggHit
        }
        return null
      }

      const add = (alias: string, expr: string) => {
        const a = String(alias || '').trim()
        const e = String(expr || '').trim()
        if (!a || !e) return
        const key = a.toLowerCase()
        if (seen.has(key)) return
        seen.add(key)
        out.push({ alias: a, expr: e })
      }

      for (const need of tooltipNeeds) {
        if (need.kind === 'all_fields') continue
        const token = (need as any).token ? String((need as any).token) : ''
        const tokNorm = norm(token)
        if (!tokNorm) continue
        // Built-in / already emitted
        if (tokNorm === 'measure names' || tokNorm === 'measure values') continue

        if (need.kind === 'field') {
          const fieldName = String(need.field ?? '').trim()
          if (!fieldName) continue
          if (isGroupedDim(fieldName)) continue // already in dim selects

          const resolved = resolveFieldExpr(fieldName, defaultOverClause)
          if (!resolved) continue
          const aggFromFieldToken = (() => {
            const token = cleanBracketedFieldRef0(fieldName) || fieldName
            const m = String(token).match(/^([A-Za-z_][A-Za-z0-9_]*)\s*:/)
            if (!m) return null
            return normalizeAggUpper(String(m[1] ?? '').trim())
          })()
          const aggFromSpec = findSpecAggForTooltipField(fieldName) || findSpecAggForTooltipField(resolved.resolvedField)
          const aggFromMeta = normalizeAggUpper(resolved?.meta?.aggregation ?? resolved?.meta?.attrs?.aggregation)
          const role = String(resolved?.meta?.role ?? resolved?.meta?.attrs?.role ?? '').trim().toLowerCase()
          const typ = String(resolved?.meta?.type ?? resolved?.meta?.attrs?.type ?? '').trim().toLowerCase()
          const inferredAgg =
            aggFromFieldToken ||
            aggFromSpec ||
            aggFromMeta ||
            ((role === 'measure' || typ === 'quantitative') ? 'SUM' : null)
          const expr = (() => {
            if (resolved.isAgg || resolved.isTableCalc) return resolved.expr
            if (!inferredAgg) return tableauAttrSql(resolved.expr)
            if (inferredAgg === 'AGG') return resolved.expr
            if (inferredAgg === 'COUNTD') return `COUNT(DISTINCT CAST(${resolved.expr} AS VARCHAR))`
            if (inferredAgg === 'COUNT') return `COUNT(${resolved.expr})`
            if (inferredAgg === 'SUM' || inferredAgg === 'AVG') {
              return `${inferredAgg}(TRY_CAST(${resolved.expr} AS DOUBLE))`
            }
            return `${inferredAgg}(${resolved.expr})`
          })()
          add(fieldName, expr)
          continue
        }

        // Func tokens
        const fn = String(need.fn ?? '').trim().toUpperCase()
        const argRaw = String(need.arg ?? '').trim()
        if (!fn) continue

        if (fn === 'ATTR') {
          if (!argRaw) continue
          const resolved = resolveFieldExpr(argRaw, defaultOverClause)
          if (!resolved) continue
          add(`ATTR(${stripBracketsAroundFieldName(argRaw)})`, tableauAttrSql(resolved.expr))
          continue
        }

        // Date parts: YEAR/QUARTER/MONTH/DAY/HOUR/MINUTE/SECOND
        const dp = argRaw ? buildDatePartSql(fn, resolveFieldExpr(argRaw, defaultOverClause)?.expr ?? '') : null
        if (dp) {
          // Not grouped => use ATTR semantics to keep GROUP BY stable.
          const expr = tableauAttrSql(dp)
          add(`${fn}(${stripBracketsAroundFieldName(argRaw)})`, expr)
          continue
        }

        const agg = aggFnToSql(fn)
        if (!agg) {
          // Fallback: compile the full token as a Tableau expression (supports IFNULL/ZN/WINDOW_*/RUNNING_*/LOOKUP/etc).
          if (!token) continue
          try {
            const compiled = compileTableauCalc(token, defaultOverClause)
            if (compiled.invalid) continue
            const expr = (compiled.isAgg || compiled.isTableCalc) ? compiled.sql : tableauAttrSql(compiled.sql)
            add(token, expr)
          } catch { }
          continue
        }

        if (!argRaw) continue
        const resolved = resolveFieldExpr(argRaw, defaultOverClause)
        if (!resolved) continue
        const inner = resolved.expr

        if (agg.fn === 'AGG') {
          // Tableau AGG(): argument is already aggregated
          add(`AGG(${stripBracketsAroundFieldName(argRaw)})`, inner)
          continue
        }

        // If the argument is already aggregate/table-calc, do not re-wrap.
        if (resolved.isAgg || resolved.isTableCalc) {
          add(`${fn}(${stripBracketsAroundFieldName(argRaw)})`, inner)
          continue
        }

        if (agg.kind === 'countd') {
          add(`${fn}(${stripBracketsAroundFieldName(argRaw)})`, `COUNT(DISTINCT ${inner})`)
          continue
        }
        if (agg.fn === 'COUNT') {
          add(`${fn}(${stripBracketsAroundFieldName(argRaw)})`, `COUNT(${inner})`)
          continue
        }
        // SUM/AVG/MIN/MAX: numeric-ish aggregates
        if (agg.fn === 'SUM' || agg.fn === 'AVG') {
          add(`${fn}(${stripBracketsAroundFieldName(argRaw)})`, `${agg.fn}(TRY_CAST(${inner} AS DOUBLE))`)
          continue
        }
        add(`${fn}(${stripBracketsAroundFieldName(argRaw)})`, `${agg.fn}(${inner})`)
      }

      return out
    }

    function deriveMeasureTableCalcResolution(
      tableCalcs: any[] | null | undefined,
      debugMeta?: {
        source?: string | null
        fieldRef?: string | null
        columnId?: string | null
        displayName?: string | null
        ciName?: string | null
        ciCaption?: string | null
      } | null,
    ): { over: string; config: any | null } {
      const tcs0 = Array.isArray(tableCalcs) ? tableCalcs : []
      const normalizeTc = (x: any): any => {
        if (!x || typeof x !== 'object') return null
        const attrs = (x as any).attrs
        return (attrs && typeof attrs === 'object') ? attrs : x
      }
      const tcs = tcs0.map(normalizeTc).filter(Boolean) as any[]
      const first = tcs.find((x) => x && typeof x === 'object') as any

      const dims: TableCalcDim[] = groupDims
        .map((d, i) => {
          const groupedKeyAlias = emitTotalsForMeasureOverClause ? quoteIdent(`__fg_group_key_${i}`) : ''
          // For calculated dimensions, derive a partition-safe SQL expression
          // bound to the base extract's physical columns so window PARTITION BY
          // does not depend on the derived alias (which may be dropped by a
          // downstream projection that re-aggregates the base layer).
          const fieldName = String(d?.field_name ?? '').trim()
          const partitionSql = (() => {
            if (!fieldName) return null
            const fieldRef = groupDimFieldRef(d) || fieldName
            const calcFormula = lookupCalculatedFormula(fieldRef) || lookupCalculatedFormula(fieldName)
            if (!calcFormula) return null
            let compiled: CompiledTableauCalcResult | null = null
            try { compiled = compileTableauCalc(calcFormula, '') } catch { compiled = null }
            const sql = String(compiled?.sql ?? '').trim()
            if (!sql || compiled?.invalid) return null
            if (compiled?.isAgg || compiled?.isTableCalc) return null
            // Reopening expensive CASE/conditional trees (Time Period* style)
            // into grouped windows is wasteful; restrict the partition override
            // to straightforward column-bound expressions.
            if (/\b(?:CASE|WHEN|COALESCE|IIF|IFNULL|ZN)\b/i.test(sql)) return null
            if (new RegExp(UNRESOLVED_FIELD, 'i').test(sql)) return null
            return sql
          })()
          return {
            field: fieldName,
            // Keep partition expressions on their original grouped-safe SQL, but
            // retain the synthetic grouped-key alias for order surrogates in totals
            // mode so ORDER BY does not fall back to display aliases that no longer
            // exist in the grouped source stage.
            sql: tableCalcDimExprs[i],
            partitionSql,
            groupedSql: groupedKeyAlias || tableCalcDimExprs[i],
            shelf: (i < rowDimCount) ? 'rows' : 'columns',
          } as TableCalcDim
        })
        .filter((dim) => {
          const fieldKey = normalizeTableauFieldKey(String(dim.field ?? ''))
          return !(fieldKey && hideDisplayFilterFieldKeys.has(fieldKey))
        })
      const rowDims = dims.filter((d) => d.shelf === 'rows')
      const colDims = dims.filter((d) => d.shelf === 'columns')
      const normalizeExprForGrouping = (rawExpr: string): string =>
        String(rawExpr ?? '').replace(/\s+/g, ' ').trim().toLowerCase()
      const isGroupedSelectAliasExpr = (rawExpr: string): boolean => {
        const expr = String(rawExpr ?? '')
          .trim()
          .replace(/^(?:"t"|t)\s*\.\s*/i, '')
          .trim()
        return /^"__fg_group_key_\d+"$/i.test(expr) || /^__fg_group_key_\d+$/i.test(expr)
      }
      const groupedExprSet = new Set(
        (Array.isArray(groupByExprsForMeasureOverClause) ? groupByExprsForMeasureOverClause : [])
          .map((expr) => normalizeExprForGrouping(String(expr ?? '')))
          .filter(Boolean)
      )
      const ensureGroupedOrderExpr = (
        order: { expr: string; dir: 'ASC' | 'DESC' } | null,
      ): { expr: string; dir: 'ASC' | 'DESC' } | null => {
        if (!order) return null
        const expr = String(order.expr ?? '').trim()
        if (!expr) return null
        if (isNullishExpr(expr)) return null
        if (groupByExprsForMeasureOverClause.length <= 0) return order
        if (/\b(?:MIN|MAX|SUM|AVG|COUNT|ANY_VALUE)\s*\(/i.test(expr)) return order
        if (isGroupedSelectAliasExpr(expr)) {
          return {
            expr: `${order.dir === 'DESC' ? 'MAX' : 'MIN'}(${expr})`,
            dir: order.dir,
          }
        }
        const exprNorm = normalizeExprForGrouping(expr)
        if (exprNorm && groupedExprSet.has(exprNorm)) return order
        const groupedExpr = `${order.dir === 'DESC' ? 'MAX' : 'MIN'}(${expr})`
        if (isNullishExpr(groupedExpr)) return null
        return { expr: groupedExpr, dir: order.dir }
      }

      const orderByForDim = (dim: TableCalcDim): { expr: string; dir: 'ASC' | 'DESC' } | null => {
        const expr = String(((dim as any).groupedSql ?? dim.sql) ?? '').trim()
        if (!expr) return null
        if (isNullishExpr(expr)) return null
        return ensureGroupedOrderExpr(orderByForField(expr, String(dim.field ?? '').trim()))
      }
      const orderByForFieldRef = makeOrderByForFieldRef({
        dims,
        dimOrderResolver: orderByForDim,
        fallbackOrderResolver: orderByForField,
        postProcess: ensureGroupedOrderExpr,
        resolveCol,
        allowPhysicalFallback: !hasExtractSchema,
        baseRef,
        quoteIdent,
        fieldExprFor,
        resolvedWeeklyTimePeriodExprForField,
        isNullishExpr,
      })
      const effectiveDefault = (() => {
        const emptyConfig = { addressing: [], partitioning: [], order_by: [], frame: null }
        if (dims.length === 0) {
          return {
            over: defaultOverClause,
            config: defaultOverClause ? { ...emptyConfig, order_by: [{ field: `sql:${defaultOverClause.replace(/^ORDER\s+BY\s+/i, '')}`, direction: 'Asc' }] } : emptyConfig,
          }
        }
        const pickOrderDim = (): TableCalcDim | null => {
          const idxEnd = dims.findIndex((d) => norm(String(d.field ?? '')) === 'time period end dates')
          if (idxEnd >= 0) return dims[idxEnd] || null
          const idxTp = dims.findIndex((d) => norm(String(d.field ?? '')) === 'time period')
          if (idxTp >= 0) return dims[idxTp] || null
          return dims.length ? dims[dims.length - 1] || null : null
        }
        const pickedOrderDim = pickOrderDim()
        const order = pickedOrderDim
          ? orderByForDim(pickedOrderDim)
          : null
        const partitionExprs = pickedOrderDim?.shelf === 'rows'
          ? rowDims
              .filter((d) => d !== pickedOrderDim)
              .map((d) => String(d.sql ?? '').trim())
              .filter(Boolean)
          : rowDims
              .map((d) => String(d.sql ?? '').trim())
              .filter(Boolean)
        const overParts: string[] = []
        if (partitionExprs.length) overParts.push(`PARTITION BY ${partitionExprs.join(', ')}`)
        if (order?.expr) overParts.push(`ORDER BY ${order.expr} ${order.dir}`)
        const config = {
          addressing: order?.expr ? [`sql:${order.expr}`] : [],
          partitioning: partitionExprs.map((expr) => `sql:${expr}`),
          order_by: order?.expr
            ? [{ field: `sql:${order.expr}`, direction: order.dir === 'DESC' ? 'Desc' : 'Asc' }]
            : [],
          frame: null,
        }
        return { over: overParts.join(' ') || 'ORDER BY 1', config }
      })()
      const effectiveDefaultOverClause = effectiveDefault.over

      const derived = deriveComputeUsingFromTableauAttrs({
        attrs: first || null,
        rowDims,
        colDims,
        otherDims: [],
        useSql: true,
        orderExprForField: (d) => orderByForDim(d)?.expr ?? null,
        directionForField: (d) => {
          const dir = orderByForDim(d)?.dir ?? 'ASC'
          return dir === 'DESC' ? 'Desc' : 'Asc'
        },
        orderExprForFieldRef: (fieldRefRaw) => orderByForFieldRef(fieldRefRaw)?.expr ?? null,
        directionForFieldRef: (fieldRefRaw) => {
          const dir = orderByForFieldRef(fieldRefRaw)?.dir ?? 'ASC'
          return dir === 'DESC' ? 'Desc' : 'Asc'
        },
      })

      const over = renderOverClause(derived.config)
      if (debugFieldMapping) {
        pushFieldMappingTrace({
          phase: 'table_calc_over_clause',
          message: 'measure_mnv_over_clause',
          data: {
            worksheetName: worksheetNameDebug,
            source: String(debugMeta?.source ?? '').trim() || null,
            fieldRef: String(debugMeta?.fieldRef ?? '').trim() || null,
            columnId: String(debugMeta?.columnId ?? '').trim() || null,
            displayName: String(debugMeta?.displayName ?? '').trim() || null,
            ciName: String(debugMeta?.ciName ?? '').trim() || null,
            ciCaption: String(debugMeta?.ciCaption ?? '').trim() || null,
            orderingType: String(first?.['ordering-type'] ?? first?.ordering_type ?? '').trim() || null,
            orderingField: String(first?.['ordering-field'] ?? first?.ordering_field ?? '').trim() || null,
            rawTableCalcs: tcs0,
            rowDims: rowDims.map((d) => ({ field: d.field, sql: String(d.sql ?? '') })),
            colDims: colDims.map((d) => ({ field: d.field, sql: String(d.sql ?? '') })),
            derivedConfig: derived.config,
            defaultOverClause: effectiveDefaultOverClause,
            renderedOverClause: over.over || null,
          },
        })
      }
      const defaultHasPartition = /\bPARTITION\s+BY\b/i.test(String(effectiveDefaultOverClause || ''))
      const hasExplicitComputeUsingDims =
        tcs.some((tc: any) => {
          const computeUsing = String(tc?.['compute-using'] ?? tc?.compute_using ?? '').trim()
          const orderingField = String(tc?.['ordering-field'] ?? tc?.ordering_field ?? '').trim()
          const addressingRaw = Array.isArray(tc?.addressing)
            ? tc.addressing
            : String(tc?.addressing ?? tc?.['compute-using-addressing'] ?? '').trim()
          const partitioningRaw = Array.isArray(tc?.partitioning)
            ? tc.partitioning
            : String(tc?.partitioning ?? tc?.['compute-using-partitioning'] ?? '').trim()
          return Boolean(
            computeUsing ||
            orderingField ||
            (Array.isArray(addressingRaw) ? addressingRaw.length > 0 : addressingRaw) ||
            (Array.isArray(partitioningRaw) ? partitioningRaw.length > 0 : partitioningRaw)
          )
        })
      // Imported Measure Names copy-calcs often carry only a bare ordering-type without explicit
      // compute-using dims. In that case, the view-derived row partition + time order is the
      // authoritative semantics for Weekly-style crosstabs; letting the generic derive step
      // reinterpret the axis label introduces wrong partitions/orderings.
      if (!hasExplicitComputeUsingDims && defaultHasPartition) return { over: effectiveDefaultOverClause, config: effectiveDefault.config }
      if (!over.over) return { over: effectiveDefaultOverClause, config: effectiveDefault.config }
      const derivedHasPartition =
        Array.isArray((derived as any)?.config?.partitioning) &&
        ((derived as any).config.partitioning as any[]).length > 0
      // Normalize incomplete table-calc metadata (common in imported workbooks):
      // if compute-using derivation produced an unpartitioned window but the view-level
      // window has explicit partitions, preserve the view partitioning.
      if (!derivedHasPartition && defaultHasPartition) return { over: effectiveDefaultOverClause, config: effectiveDefault.config }
      return { over: over.over, config: derived.config }
    }

    function overClauseForMeasure(
      tableCalcs: any[] | null | undefined,
      debugMeta?: {
        source?: string | null
        fieldRef?: string | null
        columnId?: string | null
        displayName?: string | null
        ciName?: string | null
        ciCaption?: string | null
      } | null,
    ): string {
      return deriveMeasureTableCalcResolution(tableCalcs, debugMeta).over
    }

    function applyTableauTableCalcMetadataToVizSpec(): void {
      const calcs = Array.isArray((vizSpec as any)?.data_pane?.calculated_fields)
        ? ((vizSpec as any).data_pane.calculated_fields as any[])
        : []
      let changed = false
      for (const calc of calcs) {
        if (!calc || typeof calc !== 'object') continue
        if ((calc as any).table_calc?.enabled) continue
        const raw = Array.isArray((calc as any).tableau_table_calcs)
          ? ((calc as any).tableau_table_calcs as any[])
          : []
        if (raw.length === 0) continue
        const resolved = deriveMeasureTableCalcResolution(raw, {
          source: 'calculated_field_metadata',
          fieldRef: String((calc as any).name ?? '').trim() || null,
          columnId: String((calc as any).id ?? '').trim() || null,
          displayName: String((calc as any).name ?? '').trim() || null,
        })
        if (!resolved.config) continue
        ;(calc as any).table_calc = {
          enabled: true,
          compute_using: resolved.config,
        }
        changed = true
      }
      if (changed) {
        try { _compileTableauCalcCache.clear() } catch {}
      }
    }

    // Derive measure list from quantitative column instances (qk).
    // This generally aligns with Tableau's Measure Values selection for crosstabs.
    const aggTokenToSql = (tok: string | null): { kind: 'agg' | 'countd'; fn: string } | null => {
      const t = tok ? String(tok).trim().toLowerCase() : ''
      if (!t) return null
      if (t === 'sum') return { kind: 'agg', fn: 'SUM' }
      if (t === 'avg') return { kind: 'agg', fn: 'AVG' }
      if (t === 'min') return { kind: 'agg', fn: 'MIN' }
      if (t === 'max') return { kind: 'agg', fn: 'MAX' }
      if (t === 'count') return { kind: 'agg', fn: 'COUNT' }
      if (t === 'ctd') return { kind: 'countd', fn: 'COUNT' }
      if (t === 'countd') return { kind: 'countd', fn: 'COUNT' }
      return null
    }

    const normalizeMeasureName = (raw: any): string => {
      const cleaned0 = cleanBracketedFieldRef0(String(raw ?? '').trim()) || String(raw ?? '').trim()
      const cleaned = cleaned0.replace(/^"(.*)"$/, '$1').replace(/^'(.*)'$/, '$1').trim()
      if (!cleaned) return ''
      const token = normalizeTableauFieldToken(cleaned)
      const base = token || cleaned
      const stripped = looksTableauToken(cleaned) ? base.trim() : stripTableauAutoGeneratedSuffixes(base).trim()
      return stripped || cleaned
    }
    const measureKeyPart = (raw: any): string => {
      const name = normalizeMeasureName(raw)
      if (!name) return ''
      const rawStr = String(raw ?? '').trim()
      if (looksTableauToken(rawStr)) return name.toLowerCase()
      const key = normalizeTableauFieldKey(name)
      return key || name.toLowerCase()
    }
    const makeMeasureKey = (columnId: string, aggTok: string | null, displayName?: string | null, token?: string | null): string => {
      const keyPart =
        measureKeyPart(displayName) ||
        measureKeyPart(token) ||
        measureKeyPart(columnId) ||
        `measure_${measureMap.size}`
      return `${aggTok || ''}::${columnId}::${keyPart}`
    }
    const measureMap = new Map<string, {
      columnId: string
      aggTok: string | null
      tableCalcs?: any[] | null
      token?: string | null
      ci?: any | null
      displayName?: string | null
      displayNameOverride?: boolean
    }>()
    let measureFilterLabelSet: Set<string> | null = null
    let measureFilterLabelOrder: string[] = []
    const resolveMeasureLabel = (m: { columnId: string; token?: string | null; ci?: any | null; displayName?: string | null }): string => {
      const explicit = String(m.displayName ?? '').trim()
      const explicitKey = measureCaptionKey(explicit)
      const explicitLooksGeneric =
        !explicit ||
        explicit.toLowerCase() === 'null' ||
        explicit.toLowerCase().startsWith('calculation') ||
        looksTableauToken(explicit)
      const ciCap = String(m.ci?.caption ?? '').trim()
      const colCap = columnCaptionForId(m.columnId, m.ci?.datasource)
      const baseFieldId = String(m.ci?.base_field_id ?? m.ci?.baseFieldId ?? '').trim()
      const baseClean = baseFieldId ? (cleanBracketedFieldRef0(baseFieldId) || baseFieldId) : ''
      const meta =
        _fdColsByName.get(String(m.columnId || '').trim()) ||
        (baseClean ? _fdColsByName.get(baseClean) : null) ||
        null
      const metaCap = meta?.caption != null ? String(meta.caption).trim() : ''
      const tryManifestCap = (rawId: any): string => {
        const k = manifestTokenKey(rawId)
        if (!k) return ''
        const hit = manifestCaptionByToken.get(k)
        return (typeof hit === 'string' && hit.trim()) ? hit.trim() : ''
      }
      const manifestCap =
        tryManifestCap(m.columnId) ||
        tryManifestCap(m.token) ||
        tryManifestCap(m.ci?.name) ||
        (baseClean ? tryManifestCap(baseClean) : '')
      const preferReadableEquivalentExplicit = () =>
        upgradeReadableEquivalentMeasureLabel(explicit, [ciCap, colCap, metaCap, manifestCap])
      // Highest priority: an explicit label chosen by the authoring layer.
      // This is critical for export/UI parity because Tableau may store the user-facing caption
      // on the Measure Names member label (not on the base column meta).
      if (explicit && (m as any).displayNameOverride) return preferReadableEquivalentExplicit()
      if (explicit && !explicitLooksGeneric) return preferReadableEquivalentExplicit()
      if (explicit && explicitKey && measureFilterLabelSet && measureFilterLabelSet.has(explicitKey)) return preferReadableEquivalentExplicit()
      if (ciCap) return ciCap
      if (colCap) return colCap
      if (metaCap) return metaCap
      const calcFormula = String((m as any).ci?.calculation?.formula ?? meta?.calculation?.formula ?? '').trim()
      if (calcFormula) {
        const key = normalizeCalcFormula(calcFormula)
        const cap = calcCaptionByFormula.get(key)
        if (cap) return cap
      }
      // Fallback: manifest captions (backend extract field manifest) – often better than raw tokens.
      if (manifestCap) return manifestCap

      if (explicit) return explicit
      const ciName = String(m.ci?.name ?? '').trim()
      if (ciName) return normalizeMeasureName(ciName) || ciName
      const tok = String(m.token ?? '').trim()
      if (tok) return normalizeMeasureName(tok) || tok
      return String(meta?.name ?? m.columnId)
    }
    const measureLabelCache = new WeakMap<object, string>()
    const resolveMeasureLabelCached = (m: { columnId: string; token?: string | null; ci?: any | null; displayName?: string | null }): string => {
      if (m && typeof m === 'object') {
        if (measureLabelCache.has(m as any)) return measureLabelCache.get(m as any) || ''
        const label = resolveMeasureLabel(m)
        measureLabelCache.set(m as any, label)
        return label
      }
      return resolveMeasureLabel(m)
    }
    const measureExprCache = (() => {
      const wbAny: any = workbookDerived as any
      const existing = wbAny && wbAny.__fg_mnvMeasureExprCache
      if (existing && existing instanceof Map) return existing as Map<string, string | null>
      const m = new Map<string, string | null>()
      if (wbAny) wbAny.__fg_mnvMeasureExprCache = m
      return m
    })()
    const measureExprCacheKey = (
      columnId: string,
      aggTok: string | null,
      calcFormula: string,
      over: string
    ): string => {
      const sig = compiledFormulaContextSig || ''
      return [sig, columnId || '', aggTok || '', calcFormula || '', over || ''].join('\u0001')
    }
    const aggTokens = new Set(['sum', 'avg', 'min', 'max', 'count', 'countd', 'ctd'])

    const isCalcToken = (raw: string): boolean => {
      const cleaned = cleanBracketedFieldRef0(String(raw ?? '').trim())
      const key = normalizeTableauFieldKey(cleaned)
      if (!key) return false
      return key === 'calculation' || key.startsWith('calculation_')
    }
    const resolveCiColumnId = (ci: any): string => {
      const baseFieldId = String((ci as any)?.base_field_id ?? (ci as any)?.baseFieldId ?? '').trim()
      if (baseFieldId && isCalcToken(baseFieldId)) {
        return cleanBracketedFieldRef0(baseFieldId) || baseFieldId
      }
      const col = String(ci?.column ?? '').trim()
      return col
    }

    const resolveTableCalcsForColumn = (ci: any | null, columnId: string): any[] | null => {
      const normalize = (arr: any[]): any[] =>
        (Array.isArray(arr) ? arr : [])
          .map((x) => {
            if (!x || typeof x !== 'object') return null
            if ((x as any).attrs && typeof (x as any).attrs === 'object') return x
            const hasTypedShape =
              Object.prototype.hasOwnProperty.call(x, 'compute_using')
              || Object.prototype.hasOwnProperty.call(x, 'ordering_type')
              || Object.prototype.hasOwnProperty.call(x, 'ordering_field')
              || Object.prototype.hasOwnProperty.call(x, 'addressing')
              || Object.prototype.hasOwnProperty.call(x, 'partitioning')
              || Object.prototype.hasOwnProperty.call(x, 'at_level')
              || Object.prototype.hasOwnProperty.call(x, 'restart_every')
            if (hasTypedShape) return x
            return { attrs: x }
          })
          .filter(Boolean) as any[]

      const fromCi = Array.isArray(ci?.table_calcs) && ci.table_calcs.length > 0 ? ci.table_calcs : null
      if (fromCi) return normalize(fromCi)
      const meta = _fdColsByName.get(String(columnId || '').trim()) || null
      const fromCalc = Array.isArray(meta?.calculation?.table_calcs) && meta.calculation.table_calcs.length > 0
        ? meta.calculation.table_calcs
        : null
      return fromCalc ? normalize(fromCalc) : null
    }
    const ciByColumnIdWithFormula = (() => {
      const out = new Map<string, any[]>()
      for (const ci of fdCis) {
        if (!ci) continue
        if (!matchesSourceRef((ci as any)?.datasource)) continue
        const col = resolveCiColumnId(ci)
        if (!col) continue
        const f = String((ci as any)?.calculation?.formula ?? '').trim()
        if (!f) continue
        const arr = out.get(col)
        if (arr) arr.push(ci)
        else out.set(col, [ci])
      }
      return out
    })()
    const isMeasureColumn = (c: any): boolean => {
      const role = String(c?.role ?? '').trim().toLowerCase()
      // Never treat explicit dimensions as Measure Names candidates.
      // Many Tableau workbooks implement dimensions as calculated fields (e.g. "Time Period"),
      // which would otherwise be incorrectly promoted into Measure Values.
      if (role === 'dimension') return false
      if (role === 'measure') return true
      const typ = String((c as any)?.type ?? (c as any)?.['r#type'] ?? '').trim().toLowerCase()
      if (typ === 'quantitative') return true
      const dt = String(c?.datatype ?? '').trim().toLowerCase()
      if (['int', 'integer', 'bigint', 'smallint', 'float', 'double', 'decimal', 'numeric', 'real', 'number'].includes(dt)) return true
      const agg = String(c?.aggregation ?? '').trim()
      if (agg) return true
      return Boolean(c?.calculation)
    }

    // Prefer measures referenced by the active worksheet shelves (more accurate than scanning all qk in the workbook).
    // We read raw `ws.rows.expr` / `ws.cols.expr` tokens because compiled specs often drop column-instance ids.
    const ciByName = new Map<string, any>()
    for (const ci of fdCis) {
      if (!matchesSourceRef((ci as any)?.datasource)) continue
      const nm = String(ci?.name ?? '').trim()
      if (nm) ciByName.set(nm, ci)
      const cleanNm = nm ? (cleanBracketedFieldRef0(nm) || nm).trim() : ''
      if (cleanNm && !ciByName.has(cleanNm)) ciByName.set(cleanNm, ci)
    }
    const isMeasureLikeCi = (ci: any, col: string, aggTok: string | null): boolean => {
      if (aggTok) return true
      const role = String(ci?.role ?? '').trim().toLowerCase()
      // A very common failure mode: column-shelf qk dimensions (Time Period, Begin/End dates)
      // get incorrectly pulled into the Measure Names measure set. Never treat explicit dimensions
      // as measures, even if their storage type is numeric (e.g., epoch ms).
      if (role === 'dimension') return false
      if (role === 'measure') return true
      const baseFieldId = String(ci?.base_field_id ?? ci?.baseFieldId ?? '').trim()
      const baseClean = baseFieldId ? (cleanBracketedFieldRef0(baseFieldId) || baseFieldId) : ''
      const meta =
        fdCols.find((c) => String(c?.name ?? '').trim() === String(col || '').trim()) ||
        (baseClean ? fdCols.find((c) => String(c?.name ?? '').trim() === baseClean) : null) ||
        null
      const metaRole = String((meta as any)?.role ?? '').trim().toLowerCase()
      if (metaRole === 'dimension') return false
      if (metaRole === 'measure') return true
      const dt = String((meta as any)?.datatype ?? (meta as any)?.data_type ?? (meta as any)?.['data_type'] ?? '').trim().toLowerCase()
      if (dt.includes('int') || dt.includes('decimal') || dt.includes('numeric') || dt.includes('double') || dt.includes('float') || dt.includes('real')) {
        return true
      }
      return false
    }
    _markPhase('adv_ordering+over')
    const viewMeasureTokens = viewTokens.filter((t) => /(^|:)qk(\b|:)/i.test(String(t)) && !/measure\s+values/i.test(String(t)))
    for (const tok of viewMeasureTokens) {
      const ci = ciByName.get(tok)
      if (!ci) continue
      const col = resolveCiColumnId(ci)
      if (!col) continue
      const parts = (cleanBracketedFieldRef0(String(ci?.name ?? tok).trim()) || String(ci?.name ?? tok))
        .split(':')
        .map((p) => p.trim().replace(/^\[/, '').replace(/\]$/, ''))
        .filter(Boolean)
      let aggTok: string | null = null
      for (const p of parts) {
        const t = p.toLowerCase()
        if (aggTokens.has(t)) { aggTok = t; break }
      }
      // Avoid incorrectly treating qk dimensions as measures (common on the Columns shelf).
      if (!isMeasureLikeCi(ci, col, aggTok)) continue
      const displayName = (() => {
        const cap = String(ci?.caption ?? '').trim()
        if (cap) return cap
        const colCap = columnCaptionForId(col, ci?.datasource)
        if (colCap) return colCap
        const tokenKey = normalizeTableauFieldToken(String(ci?.name ?? tok ?? '')).trim().toLowerCase()
        const manifestCap = tokenKey ? manifestCaptionByToken.get(tokenKey) : undefined
        if (manifestCap) return manifestCap
        const nm = String(ci?.name ?? tok ?? '').trim()
        return normalizeMeasureName(nm)
      })()
      const token = String(tok || '').trim() || null
      const key = makeMeasureKey(col, aggTok, displayName, token)
      if (!measureMap.has(key)) {
        measureMap.set(key, {
          columnId: col,
          aggTok,
          tableCalcs: resolveTableCalcsForColumn(ci, col),
          token,
          ci,
          displayName,
          displayNameOverride: false,
        })
      }
    }

    // Fallback: scan all qk column instances in the workbook.
    // (Only when we couldn't infer measure instances from worksheet shelf tokens.)
    if (measureMap.size === 0) {
      for (const ci of fdCis) {
        if (!matchesSourceRef((ci as any)?.datasource)) continue
        const nm = String(ci?.name ?? '')
        if (!nm.includes(':qk')) continue
        const col = resolveCiColumnId(ci)
        if (!col) continue

        const parts = (cleanBracketedFieldRef0(nm) || nm)
          .split(':')
          .map((p) => p.trim().replace(/^\[/, '').replace(/\]$/, ''))
          .filter(Boolean)
        let aggTok: string | null = null
        for (const p of parts) {
          const t = p.toLowerCase()
          if (aggTokens.has(t)) {
            aggTok = t
            break
          }
        }
        // Avoid pulling in qk dimensions here; they can be mislabeled by Measure Names filters/order.
        if (!isMeasureLikeCi(ci, col, aggTok)) continue

        const displayName = (() => {
          const cap = String(ci?.caption ?? '').trim()
          if (cap) return cap
          const colCap = columnCaptionForId(col, ci?.datasource)
          if (colCap) return colCap
          const tokenKey = normalizeTableauFieldToken(String(ci?.name ?? nm ?? '')).trim().toLowerCase()
          const manifestCap = tokenKey ? manifestCaptionByToken.get(tokenKey) : undefined
          if (manifestCap) return manifestCap
          const nmRaw = String(ci?.name ?? nm ?? '').trim()
          return normalizeMeasureName(nmRaw)
        })()
        const token = String(nm || '').trim() || null
        const key = makeMeasureKey(col, aggTok, displayName, token)
        if (!measureMap.has(key)) {
          measureMap.set(key, {
            columnId: col,
            aggTok,
            tableCalcs: resolveTableCalcsForColumn(ci as any, col),
            token,
            ci,
            displayName,
            displayNameOverride: false,
          })
        }
      }
    }
    _markPhase('adv_viewToken_measures')
    let measures = Array.from(measureMap.values())
    const measureAvailableInAuthoritativeSchema = (measure: any): boolean => {
      if (!sourceSchema) return true
      const columnId = String(measure?.columnId ?? '').trim()
      if (!columnId) return false
      if (isPresentInExtractSchema(columnId)) return true
      const resolved = resolveColFromBinding(columnId)
      if (resolved && isPresentInExtractSchema(resolved)) return true
      const calcFormula =
        preferredCalculatedFormula(
          columnId,
          measure?.token,
          measure?.displayName,
          measure?.ci?.name,
          measure?.ci?.caption,
        ) ||
        String(measure?.ci?.calculation?.formula ?? '').trim()
      return Boolean(calcFormula)
    }
    if (sourceSchema) {
      measures = measures.filter(measureAvailableInAuthoritativeSchema)
    }
    if (worksheetDebugEnabled) {
      debugLog('measureNames measures (raw)', measures.map((m) => ({
        columnId: m.columnId,
        aggTok: m.aggTok,
        token: m.token,
        displayName: (m as any).displayName ?? null,
        ciName: (m as any).ci?.name ?? null,
        ciCaption: (m as any).ci?.caption ?? null,
        baseFieldId: (m as any).ci?.base_field_id ?? (m as any).ci?.baseFieldId ?? null,
        tableCalcs: Array.isArray(m.tableCalcs) ? m.tableCalcs : null,
      })))
    }
    if (measures.length === 0) return null

    const explodeMeasureFilterValues = (rawVal: any): any[] => {
      if (rawVal === null || rawVal === undefined) return []
      if (Array.isArray(rawVal)) return rawVal.flatMap(explodeMeasureFilterValues)
      if (typeof rawVal === 'string') {
        const s = rawVal.trim()
        if (!s) return []
        if (s.includes('|')) return s.split('|').map((v) => v.trim()).filter(Boolean)
        if (s.includes(',')) return s.split(',').map((v) => v.trim()).filter(Boolean)
        return [s]
      }
      if (typeof rawVal === 'object') {
        const obj = rawVal as any
        if (Array.isArray(obj.values)) return explodeMeasureFilterValues(obj.values)
        if (obj.value !== undefined) return explodeMeasureFilterValues(obj.value)
        if (obj.label !== undefined) return explodeMeasureFilterValues(obj.label)
        if (obj.caption !== undefined) return explodeMeasureFilterValues(obj.caption)
        if (obj.name !== undefined) return explodeMeasureFilterValues(obj.name)
      }
      return [rawVal]
    }
    const normalizeMeasureKeys = (raw: any): string[] => {
      // For key matching (not alias identity), use the loose canonicalization.
      const cleanedRaw = cleanBracketedFieldRef0(canonicalizeMeasureLabelLoose(raw)) || canonicalizeMeasureLabelLoose(raw)
      const cleaned0 = cleanedRaw.replace(/^"(.*)"$/, '$1').replace(/^'(.*)'$/, '$1').trim()
      const cleaned = canonicalizeMeasureLabelLoose(cleaned0)
      if (!cleaned) return []
      const out = new Set<string>()
      const addKey = (k: string | null | undefined) => {
        const v = String(k ?? '').trim().toLowerCase()
        if (v) out.add(v)
      }
      const stripCopyTokens = (s: string): string =>
        s.replace(/\s*\(copy(?:\s*\d+)?\)\s*/gi, ' ').replace(/\s+/g, ' ').trim()
      const cleanedNoCopy = stripCopyTokens(cleaned)
      if (looksTableauToken(cleaned)) {
        const tok = normalizeTableauFieldToken(cleaned).trim().toLowerCase()
        addKey(tok)
        const tokNoCopy = stripCopyTokens(tok).toLowerCase()
        if (tokNoCopy && tokNoCopy !== tok) addKey(tokNoCopy)
        const cap = tok ? manifestCaptionByToken.get(tok) : undefined
        if (cap) addKey(captionKey(cap))
        if (tokNoCopy && tokNoCopy !== tok) {
          const capNoCopy = manifestCaptionByToken.get(tokNoCopy)
          if (capNoCopy) addKey(captionKey(capNoCopy))
        }
      } else {
        const capKey = captionKey(cleaned)
        addKey(capKey)
        if (cleanedNoCopy && cleanedNoCopy !== cleaned) {
          addKey(captionKey(cleanedNoCopy))
        }
        const tokenFromCap = capKey ? manifestTokenByCaption.get(capKey) : undefined
        if (tokenFromCap) addKey(tokenFromCap)
        if (cleanedNoCopy && cleanedNoCopy !== cleaned) {
          const capKeyNoCopy = captionKey(cleanedNoCopy)
          const tokenFromCapNoCopy = capKeyNoCopy ? manifestTokenByCaption.get(capKeyNoCopy) : undefined
          if (tokenFromCapNoCopy) addKey(tokenFromCapNoCopy)
        }
      }
      return Array.from(out)
    }
    const measureFilterKeyOrder: string[] = []
    const measureFilterLabelByKey = new Map<string, string>()
    const measureFilterKeySet = (() => {
      const out = new Set<string>()
      const addKeys = (keys: string[]) => {
        if (!keys.length) return
        const [primary, ...rest] = keys
        if (primary && !out.has(primary)) {
          out.add(primary)
          measureFilterKeyOrder.push(primary)
        }
        for (const k of rest) {
          if (!k) continue
          if (out.has(k)) continue
          out.add(k)
        }
      }
      for (const f of Array.isArray(filters) ? filters : []) {
        if (!f || !isMeasureNames(f.field_name)) continue
        const op = String(f.operator || '').trim().toUpperCase()
        const value = f.value
        if (op !== 'IN' && op !== '=' && op !== '==' && op !== '' && op !== 'EQUALS') continue
        const vals = explodeMeasureFilterValues(value)
        for (const v of vals) {
          const label = canonicalizeMeasureLabel(v)
          if (label) {
            const labelKey = measureCaptionKey(label)
            if (labelKey) {
              if (!measureFilterLabelSet) measureFilterLabelSet = new Set<string>()
              if (!measureFilterLabelSet.has(labelKey)) measureFilterLabelSet.add(labelKey)
            }
            if (!measureFilterLabelOrder.includes(label)) {
              measureFilterLabelOrder.push(label)
            }
            const keys = normalizeMeasureKeys(label)
            if (keys.length && !measureFilterLabelByKey.has(keys[0]!)) {
              measureFilterLabelByKey.set(keys[0]!, label)
            }
          }
          const keys = normalizeMeasureKeys(v)
          addKeys(keys)
        }
      }
      return out
    })()
    const measureOverrideKeyOrder: string[] = []
    const measureOverrideKeySet = (() => {
      const out = new Set<string>()
      const addKeys = (keys: string[]) => {
        if (!keys.length) return
        const [primary, ...rest] = keys
        if (primary && !out.has(primary)) {
          out.add(primary)
          measureOverrideKeyOrder.push(primary)
        }
        for (const k of rest) {
          if (!k) continue
          if (out.has(k)) continue
          out.add(k)
        }
      }
      const rawList = Array.isArray(options.measureValuesOverride) ? options.measureValuesOverride : []
      for (const raw of rawList) {
        const name =
          typeof raw === 'string'
            ? raw
            : String((raw as any)?.field_name ?? (raw as any)?.field ?? '').trim()
        if (!name) continue
        const keys = normalizeMeasureKeys(name)
        addKeys(keys)
      }
      return out
    })()
    const hasMeasureNameFilter = measureFilterKeySet.size > 0 || measureOverrideKeySet.size > 0
    // Stronger constraint: if Measure Names selection is present, prefer matching by label/caption
    // (not token heuristics) to avoid accidental inclusion of non-measure qk fields.
    const measureNamesCustomOrder: string[] = (() => {
      // Tableau often encodes the effective Measure Values set + order as a manual sort on Measure Names.
      // This can include table-calc "copy" measures (e.g. "% Diff") that do not appear in the Measure Names filter values.
      const fields = Array.isArray(cols) ? cols : []
      const mn = fields.find((f: any) => f && isMeasureNames((f as any).field_name) && Array.isArray((f as any)?.sort?.custom_order)) as any
      const raw = Array.isArray(mn?.sort?.custom_order) ? mn.sort.custom_order : []
      const clean = (v: any): string =>
        canonicalizeMeasureLabel(v)
      const out: string[] = []
      const seen = new Set<string>()
      for (const r of raw) {
        const s = clean(r)
        if (!s) continue
        const k = explicitMeasureLabelIdentityKey(s) || measureCaptionKey(s)
        if (!k) continue
        if (seen.has(k)) continue
        seen.add(k)
        out.push(s)
      }
      return out
    })()
    const desiredMeasureLabelOrder: string[] = []
    const desiredMeasureLabelKeySet = (() => {
      const out = new Set<string>()
      const hasExplicitSelection = measureFilterKeySet.size > 0 || measureOverrideKeySet.size > 0
      const labelIsExplicitlySelected = (raw: any): boolean => {
        if (!hasExplicitSelection) return true
        const keys = normalizeMeasureKeys(raw)
        if (keys.some((key) => measureFilterKeySet.has(key) || measureOverrideKeySet.has(key))) return true
        const labelKey = measureCaptionKey(raw)
        return Boolean(labelKey && measureFilterLabelSet?.has(labelKey))
      }
      const addLabel = (raw: any) => {
        const s = canonicalizeMeasureLabel(raw)
        if (!s) return
        const k = explicitMeasureLabelIdentityKey(s) || measureCaptionKey(s)
        if (!k) return
        if (!out.has(k)) {
          out.add(k)
          desiredMeasureLabelOrder.push(s)
        }
      }
      // Prefer explicit manual order on Measure Names (captures Tableau "copy" measures).
      // Manual order is an ordering hint, not a selection by itself. When an
      // explicit Measure Names filter/override exists, keep only ordered labels
      // that are part of that selection; Tableau often stores a workbook-wide
      // custom order that contains measures hidden by the current sheet filter.
      for (const lbl of measureNamesCustomOrder) {
        if (labelIsExplicitlySelected(lbl)) addLabel(lbl)
      }
      // Prefer explicit Measure Names filter value order when present.
      for (const lbl of measureFilterLabelOrder) addLabel(lbl)
      // Also allow explicit overrides to contribute labels.
      for (const raw of (Array.isArray(options.measureValuesOverride) ? options.measureValuesOverride : [])) {
        const name =
          typeof raw === 'string'
            ? raw
            : String((raw as any)?.field_name ?? (raw as any)?.field ?? '').trim()
        addLabel(name)
      }
      return out
    })()
    void desiredMeasureLabelKeySet
    const hasMeasureNameSelection =
      hasMeasureNameFilter ||
      desiredMeasureLabelOrder.length > 0 ||
      measureNamesCustomOrder.length > 0 ||
      measureFilterLabelOrder.length > 0
    _markPhase('adv_measure_filter_prep')
    if (hasMeasureNameSelection) {
      // When we have an explicit Measure Names selection (filter or override),
      // we should NOT infer measures by scanning all qk instances in the workbook:
      // that approach is prone to token collisions (e.g. generic [Calculation1]) and
      // can accidentally pull column-shelf dimensions into the Measure Names set.
      measureMap.clear()

      const measureMemberTokenIdentityKey = (raw: any): string => {
        let s = canonicalizeMeasureLabel(raw)
        if (!s) return ''
        s = cleanBracketedFieldRef0(s) || s
        s = s.replaceAll('\\%', '%')
        return s.trim().toLowerCase()
      }
      const selectedMeasureIdentityKey = (
        columnId: string,
        aggTok: string | null,
        token: string | null,
        ci: any | null,
        displayName?: string | null,
      ): string => {
        const tokenKey =
          measureMemberTokenIdentityKey(token) ||
          measureMemberTokenIdentityKey((ci as any)?.name) ||
          ''
        if (tokenKey) return `token::${tokenKey}`
        const keyPart =
          measureKeyPart(displayName) ||
          measureKeyPart(columnId) ||
          `measure_${measureMap.size}`
        return `column::${aggTok || ''}::${columnId}::${keyPart}`
      }
      const mergeSelectedMeasure = (
        prev: {
          columnId: string
          aggTok: string | null
          tableCalcs?: any[] | null
          token?: string | null
          ci?: any | null
          displayName?: string | null
          displayNameOverride?: boolean
        },
        next: {
          columnId: string
          aggTok: string | null
          tableCalcs?: any[] | null
          token?: string | null
          ci?: any | null
          displayName?: string | null
          displayNameOverride?: boolean
        },
      ) => {
        const prevLabel = String(prev.displayName ?? '').trim()
        const nextLabel = String(next.displayName ?? '').trim()
        let displayName = prevLabel || nextLabel || null
        if ((prev.displayNameOverride ?? false) === (next.displayNameOverride ?? false)) {
          displayName = preferReadableMeasureLabel(prevLabel, nextLabel) || displayName
        } else if (next.displayNameOverride) {
          displayName = nextLabel || displayName
        }
        return {
          columnId: prev.columnId || next.columnId,
          aggTok: prev.aggTok || next.aggTok,
          tableCalcs: (prev.tableCalcs && prev.tableCalcs.length > 0) ? prev.tableCalcs : next.tableCalcs,
          token: prev.token || next.token || null,
          ci: prev.ci || next.ci || null,
          displayName,
          displayNameOverride: Boolean(prev.displayNameOverride || next.displayNameOverride),
        }
      }

      const addMeasure = (
        columnId: string,
        aggTok: string | null,
        token: string | null,
        ci: any | null,
        displayName?: string | null,
        displayNameOverride = false,
      ) => {
        const keyBase = selectedMeasureIdentityKey(columnId, aggTok, token || null, ci, displayName || null)
        const explicitLabelKey = displayNameOverride
          ? (
            explicitMeasureLabelIdentityKey(displayName) ||
            measureCaptionKey(displayName) ||
            measureKeyPart(displayName) ||
            ''
          )
          : ''
        let key = keyBase
        if (explicitLabelKey) {
          const siblingPrefix = `${keyBase}::label::`
          const existingSiblingKeys = Array.from(measureMap.keys()).filter(
            (candidateKey) => candidateKey === keyBase || candidateKey.startsWith(siblingPrefix),
          )
          const hasDifferentExplicitSibling = existingSiblingKeys.some((candidateKey) => {
            if (candidateKey === keyBase) {
              const existing = measureMap.get(candidateKey)
              const existingLabelKey =
                explicitMeasureLabelIdentityKey((existing as any)?.displayName) ||
                measureCaptionKey((existing as any)?.displayName) ||
                measureKeyPart((existing as any)?.displayName) ||
                ''
              return Boolean(existingLabelKey && existingLabelKey !== explicitLabelKey)
            }
            return candidateKey !== `${siblingPrefix}${explicitLabelKey}`
          })
          if (hasDifferentExplicitSibling || measureMap.has(`${siblingPrefix}${explicitLabelKey}`)) {
            key = `${siblingPrefix}${explicitLabelKey}`
          }
        }
        const next = {
          columnId,
          aggTok,
          tableCalcs: resolveTableCalcsForColumn(ci, columnId),
          token: token || null,
          ci,
          displayName: displayName || null,
          displayNameOverride,
        }
        const prev = measureMap.get(key)
        measureMap.set(key, prev ? mergeSelectedMeasure(prev, next) : next)
      }

      // Tableau's Measure Names member labels are often defined via aliases:
      // the filter list can contain the *alias values* (human labels), while the true identity
      // is the member key (a column-instance token like `[usr:...:qk]`).
      //
      // Example from TWB:
      //   <alias key="...[usr:Hair Cut \% Diff (copy)...:qk:4]" value="Total Net % Diff" />
      //
      // If we match only by captions, we'll incorrectly fail for labels that are *pure aliases*
      // (i.e., no base column/CI caption exactly equals the label).
      const normalizeMeasureMemberTokenKey = (raw: any): string =>
        // IMPORTANT: preserve full member-token identity (including copy suffixes / qk indices).
        // Using normalizeTableauFieldKey here can collapse distinct Tableau copy members that share
        // a human caption, which then binds Measure Names aliases to the wrong calc instance.
        measureMemberTokenIdentityKey(raw)
      const ciByFieldKey = new Map<string, any>()
      for (const ci of fdCis) {
        if (!matchesSourceRef((ci as any)?.datasource)) continue
        const nm = String((ci as any)?.name ?? '').trim()
        if (!nm) continue
        // Important: canonicalize whitespace/escapes so CI keys match alias keys.
        // Tableau sometimes encodes multiple spaces in tokens; our label canonicalizer collapses them.
        const k = normalizeMeasureMemberTokenKey(nm)
        if (k && !ciByFieldKey.has(k)) ciByFieldKey.set(k, ci)
      }
      const firstTableCalcOrderingType = (ci: any | null): string => {
        if (!ci) return ''
        const tableCalcs = Array.isArray((ci as any)?.table_calcs) ? (ci as any).table_calcs : []
        const first = tableCalcs.length > 0 ? tableCalcs[0] : null
        if (!first) return ''
        const attrs = normalizeTableauTableCalcAttrs(first)
        return String(attrs['ordering-type'] || attrs.ordering_type || '').trim().toLowerCase()
      }
      const preferCanonicalMeasureCi = (ci: any | null): any | null => {
        if (!ci) return ci
        const nm = String((ci as any)?.name ?? '').trim()
        if (!nm) return ci
        // Indexed qk members are distinct Measure Names identities in the workbook.
        // Do not collapse them back to a base :qk token or we can bind a label to the
        // wrong copied measure / table-calc instance.
        if (/:qk:\d+\]$/i.test(nm)) return ci
        const m = nm.match(/^(.*:qk):\d+\]$/i)
        if (!m) return ci
        const canonicalName = `${m[1]}]`
        const canonicalCiRaw = ciByName.get(canonicalName) || null
        if (!canonicalCiRaw || !matchesSourceRef((canonicalCiRaw as any)?.datasource)) return ci
        const currentCol = String((ci as any)?.column ?? '').trim()
        const canonicalCol = String((canonicalCiRaw as any)?.column ?? '').trim()
        if (!currentCol || !canonicalCol || currentCol !== canonicalCol) return ci
        const currentOrdering = firstTableCalcOrderingType(ci)
        const canonicalOrdering = firstTableCalcOrderingType(canonicalCiRaw)
        if (canonicalOrdering === 'rows' && currentOrdering !== 'rows') return canonicalCiRaw
        return ci
      }
      const normalizeMeasureCiTokenForLabelMatch = (raw: any): string => {
        const cleaned = cleanBracketedFieldRef0(String(raw ?? '').trim()) || String(raw ?? '').trim()
        if (!cleaned) return ''
        const normalized = measureMemberTokenIdentityKey(cleaned)
        if (!normalized) return ''
        return normalized.replace(/:qk(?::\d+)?$/i, '').trim().toLowerCase()
      }
      const scoreMeasureCiForDisplayLabel = (ci: any, displayName: string): number => {
        const targetLabel = canonicalizeMeasureLabel(displayName).trim().toLowerCase()
        if (!targetLabel) return 0
        const tokenBase = normalizeMeasureCiTokenForLabelMatch((ci as any)?.name)
        if (tokenBase) {
          if (tokenBase === targetLabel) return 4
          if (targetLabel.endsWith(`:${tokenBase}`)) return 2
        }
        const captionKey = measureCaptionKey((ci as any)?.caption ?? '')
        const targetCaptionKey = measureCaptionKey(displayName)
        if (captionKey && targetCaptionKey && captionKey === targetCaptionKey) return 3
        return 0
      }
      const dedupeCiCandidatesByMeasureIdentity = (entries: any[], displayName: string): any[] => {
        const byIdentity = new Map<string, any>()
        for (const entry of (Array.isArray(entries) ? entries : [])) {
          const ci = preferCanonicalMeasureCi(entry) || entry
          const colId = resolveCiColumnId(ci)
          if (!colId) continue
          const aggTok = inferAggTokFromCiName(ci)
          const identity = selectedMeasureIdentityKey(
            colId,
            aggTok,
            String((ci as any)?.name ?? '').trim() || null,
            ci,
            displayName,
          )
          if (!identity) continue
          if (!byIdentity.has(identity)) byIdentity.set(identity, ci)
        }
        const uniqueEntries = byIdentity.size > 0 ? Array.from(byIdentity.values()) : entries
        if (uniqueEntries.length <= 1) return uniqueEntries
        let bestScore = 0
        let bestEntries: any[] = []
        for (const ci of uniqueEntries) {
          const score = scoreMeasureCiForDisplayLabel(ci, displayName)
          if (score <= 0) continue
          if (score > bestScore) {
            bestScore = score
            bestEntries = [ci]
            continue
          }
          if (score === bestScore) bestEntries.push(ci)
        }
        return bestScore > 0 ? bestEntries : uniqueEntries
      }
      const measureNamesAliasKeysByLabelKey = (() => {
        const out = new Map<string, string[]>()
        const put = (label: string, rawKey: string) => {
          const lk = measureCaptionKey(label)
          if (!lk) return
          const keyClean = canonicalizeMeasureLabel(rawKey).replaceAll('\\%', '%')
          if (!keyClean) return
          const arr = out.get(lk) || []
          if (!arr.includes(keyClean)) {
            arr.push(keyClean)
            out.set(lk, arr)
          }
        }
        const isMeasureNamesMeta = (c: any): boolean => {
          const name = String(c?.name ?? '').trim()
          const attrName = String(c?.attrs?.name ?? '').trim()
          return /measure\s+names/i.test(name) || /measure\s+names/i.test(attrName) || isMeasureNames(name) || isMeasureNames(attrName)
        }
        // There can be multiple `[:Measure Names]` metas across datasources in a workbook.
        // Prefer the one for the active worksheet datasource (sourceRef) and, if multiple,
        // the one with the richest alias/member table.
        const measureNamesMetas = fdCols.filter((c) => isMeasureNamesMeta(c) && matchesSourceRef((c as any)?.datasource))
        const score = (c: any): number =>
          (Array.isArray((c as any)?.aliases) ? (c as any).aliases.length : 0) +
          (Array.isArray((c as any)?.members) ? (c as any).members.length : 0)
        let mn = (measureNamesMetas.length > 0 ? measureNamesMetas : fdCols.filter((c) => isMeasureNamesMeta(c)))
          .slice()
          .sort((a, b) => score(b) - score(a))[0] as any
        if (!mn) mn = null
        const addPair = (rawKeyAny: any, rawValAny: any) => {
          const rawKey = String(rawKeyAny ?? '').trim()
          const rawVal = canonicalizeMeasureLabel(rawValAny ?? '')
          if (!rawKey || !rawVal) return
          put(rawVal, rawKey)
          const base = stripAlongSuffix(rawVal)
          if (base && base !== rawVal) put(base, rawKey)
        }
        // Tableau encodes Measure Names member label mappings in a few ways:
        // - `aliases`: classic alias table (key -> value)
        // - `members`: for [:Measure Names] often holds objects { key, value } (member token -> label)
        const aliases = Array.isArray(mn?.aliases) ? mn.aliases : []
        for (const a of aliases) addPair((a as any)?.key, (a as any)?.value)
        const members = Array.isArray(mn?.members) ? mn.members : []
        for (const m of members) {
          if (!m || typeof m !== 'object') continue
          if ((m as any).key !== undefined && (m as any).value !== undefined) {
            addPair((m as any).key, (m as any).value)
          }
        }
        if (worksheetDebugEnabled) {
          debugLog('measureNames aliasMap', { size: out.size })
        }
        return out
      })()

      // Resolve Measure Names selections (labels) -> concrete measure columns.
      // This avoids accidental inclusion of non-measure calculated dimensions (e.g. "Time Period")
      // and prevents token collisions from selecting the wrong field.
      const looseKey = (raw: any): string =>
        canonicalizeMeasureLabel(raw)
          .toLowerCase()
          .replace(/[^a-z0-9%]+/g, '')

      const byCaptionStrictKey = new Map<string, any[]>()
      const byCaptionKey = new Map<string, any[]>()
      const byLoose = new Map<string, any[]>()
      const put = (m: Map<string, any[]>, kRaw: any, meta: any) => {
        const k = String(kRaw ?? '').trim()
        if (!k) return
        const arr = m.get(k) || []
        const colId = String(meta?.name ?? '').trim()
        if (!colId) return
        if (!arr.some((x) => String(x?.name ?? '').trim() === colId)) {
          arr.push(meta)
          m.set(k, arr)
        }
      }
      for (const c of fdCols) {
        if (!matchesSourceRef((c as any)?.datasource)) continue
        if (!isMeasureColumn(c)) continue
        const colId = String(c?.name ?? '').trim()
        if (!colId) continue
        const cap = canonicalizeMeasureLabel(c?.caption)
        if (cap) {
          const cks = measureCaptionStrictKey(cap)
          if (cks) put(byCaptionStrictKey, cks, c)
          const ck = measureCaptionKey(cap)
          if (ck) put(byCaptionKey, ck, c)
          const capBase = stripAlongSuffix(cap)
          const cksBase = capBase && capBase !== cap ? measureCaptionStrictKey(capBase) : ''
          if (cksBase && cksBase !== cks) put(byCaptionStrictKey, cksBase, c)
          const ckBase = capBase && capBase !== cap ? measureCaptionKey(capBase) : ''
          if (ckBase && ckBase !== ck) put(byCaptionKey, ckBase, c)
          const lk = looseKey(cap)
          if (lk) put(byLoose, lk, c)
          const lkBase = capBase && capBase !== cap ? looseKey(capBase) : ''
          if (lkBase && lkBase !== lk) put(byLoose, lkBase, c)
        }
        const nameClean = canonicalizeMeasureLabel(cleanBracketedFieldRef0(colId) || colId)
        const cksName = measureCaptionStrictKey(nameClean)
        if (cksName) put(byCaptionStrictKey, cksName, c)
        const lkName = looseKey(nameClean)
        if (lkName) put(byLoose, lkName, c)
        const tok = normalizeTableauFieldToken(nameClean).trim().toLowerCase()
        const lkTok = looseKey(tok)
        if (lkTok) put(byLoose, lkTok, c)
      }
      // Some published datasource snapshots carry richer/cleaner measure captions in the
      // extraction manifest than the workbook field dictionary. Use manifest fields as a
      // conservative supplement for label -> measure resolution when fd metadata is missing.
      const manifestMeasureFields = Array.isArray((options as any)?.fieldManifest?.fields)
        ? (((options as any).fieldManifest.fields) as any[])
        : []
      for (const mf of manifestMeasureFields) {
        const role = String((mf as any)?.role ?? '').trim().toLowerCase()
        const sourceKind = String((mf as any)?.source_kind ?? '').trim().toLowerCase()
        const isCalc = Boolean((mf as any)?.is_calc)
        const looksMeasure =
          role === 'measure' ||
          isCalc ||
          sourceKind === 'calculation' ||
          sourceKind === 'column' ||
          sourceKind === 'column_instance' ||
          sourceKind === 'calculation_instance'
        if (!looksMeasure) continue
        const fieldId = String((mf as any)?.field_id ?? '').trim()
        const columnName = String((mf as any)?.column_name ?? '').trim()
        const tokenRaw = String((mf as any)?.token ?? '').trim()
        const colId = fieldId || (columnName ? `[${columnName}]` : '')
        if (!colId) continue
        const caption = canonicalizeMeasureLabel((mf as any)?.caption)
        const calcFormula = String((mf as any)?.calc_formula_raw ?? (mf as any)?.calc_formula ?? '').trim()
        const manifestMeta: any = {
          name: colId,
          caption: caption || undefined,
          role: 'measure',
          calculation: calcFormula ? { formula: calcFormula } : undefined,
        }
        if (caption) {
          const cks = measureCaptionStrictKey(caption)
          if (cks && !byCaptionStrictKey.has(cks)) put(byCaptionStrictKey, cks, manifestMeta)
          const ck = measureCaptionKey(caption)
          if (ck && !byCaptionKey.has(ck)) put(byCaptionKey, ck, manifestMeta)
          const captionBase = stripAlongSuffix(caption)
          const cksBase = captionBase && captionBase !== caption ? measureCaptionStrictKey(captionBase) : ''
          if (cksBase && cksBase !== cks && !byCaptionStrictKey.has(cksBase)) put(byCaptionStrictKey, cksBase, manifestMeta)
          const ckBase = captionBase && captionBase !== caption ? measureCaptionKey(captionBase) : ''
          if (ckBase && ckBase !== ck && !byCaptionKey.has(ckBase)) put(byCaptionKey, ckBase, manifestMeta)
          const lk = looseKey(caption)
          if (lk && !byLoose.has(lk)) put(byLoose, lk, manifestMeta)
          const lkBase = captionBase && captionBase !== caption ? looseKey(captionBase) : ''
          if (lkBase && lkBase !== lk && !byLoose.has(lkBase)) put(byLoose, lkBase, manifestMeta)
        }
        const colClean = canonicalizeMeasureLabel(cleanBracketedFieldRef0(colId) || colId)
        const cksName = measureCaptionStrictKey(colClean)
        if (cksName && !byCaptionStrictKey.has(cksName)) put(byCaptionStrictKey, cksName, manifestMeta)
        const lkName = looseKey(colClean)
        if (lkName && !byLoose.has(lkName)) put(byLoose, lkName, manifestMeta)
        const tokenNorm = normalizeTableauFieldToken(tokenRaw).trim().toLowerCase()
        const lkTok = looseKey(tokenNorm)
        if (lkTok && !byLoose.has(lkTok)) put(byLoose, lkTok, manifestMeta)
      }

      // Some Measure Names labels refer to Tableau "copy"/table-calc measures that exist only as
      // column instances (with captions), not as base field dictionary columns. Build a lookup
      // for measure-like CIs so label resolution can succeed without workbook-wide qk scans.
      const ciByCaptionStrictKey = new Map<string, any[]>()
      const ciByCaptionKey = new Map<string, any[]>()
      const ciByLoose = new Map<string, any[]>()
      const putCi = (m: Map<string, any[]>, kRaw: any, ci: any) => {
        const k = String(kRaw ?? '').trim()
        if (!k) return
        const arr = m.get(k) || []
        const nm = String(ci?.name ?? '').trim()
        if (!nm) return
        if (!arr.some((x) => String(x?.name ?? '').trim() === nm)) {
          arr.push(ci)
          m.set(k, arr)
        }
      }
      const inferAggTokFromCiName = (ci: any): string | null => {
        const raw = String(ci?.name ?? '').trim()
        const parts = (cleanBracketedFieldRef0(raw) || raw)
          .split(':')
          .map((p) => p.trim().replace(/^\[/, '').replace(/\]$/, ''))
          .filter(Boolean)
        for (const p of parts) {
          const t = p.toLowerCase()
          if (aggTokens.has(t)) return t
        }
        return null
      }
      for (const ci of fdCis) {
        if (!matchesSourceRef((ci as any)?.datasource)) continue
        const col = resolveCiColumnId(ci)
        if (!col) continue
        const aggTok = inferAggTokFromCiName(ci)
        if (!isMeasureLikeCi(ci, col, aggTok)) continue
        const cap = canonicalizeMeasureLabel((ci as any)?.caption)
        const nm = canonicalizeMeasureLabel((ci as any)?.name)
        const normalizedTokenLabel = normalizeMeasureName((ci as any)?.name)
        const labelCandidates = [
          cap,
          cap ? stripAlongSuffix(cap) : '',
          normalizedTokenLabel,
          normalizedTokenLabel ? stripAlongSuffix(normalizedTokenLabel) : '',
          nm,
        ].filter(Boolean) as string[]
        for (const s of labelCandidates) {
          const cks = measureCaptionStrictKey(s)
          if (cks) putCi(ciByCaptionStrictKey, cks, ci)
          const ck = measureCaptionKey(s)
          if (ck) putCi(ciByCaptionKey, ck, ci)
          const lk = looseKey(s)
          if (lk) putCi(ciByLoose, lk, ci)
        }
      }

      const findBestCiForColumn = (colId: string): any | null => {
        const want = String(colId ?? '').trim()
        if (!want) return null
        let best: any | null = null
        for (const ci of fdCis) {
          if (!matchesSourceRef((ci as any)?.datasource)) continue
          const c = String((ci as any)?.column ?? '').trim()
          if (c !== want) continue
          const nm = String((ci as any)?.name ?? '').trim()
          if (!nm.includes(':qk')) continue
          const candidate = preferCanonicalMeasureCi(ci) || ci
          if (!best) {
            best = candidate
            continue
          }
          const bestName = String((best as any)?.name ?? '').trim()
          const candName = String((candidate as any)?.name ?? '').trim()
          const bestCanonical = /:qk\]$/i.test(bestName)
          const candCanonical = /:qk\]$/i.test(candName)
          if (bestCanonical !== candCanonical) {
            if (candCanonical) best = candidate
            continue
          }
          const bestRows = firstTableCalcOrderingType(best) === 'rows'
          const candRows = firstTableCalcOrderingType(candidate) === 'rows'
          if (bestRows !== candRows) {
            if (candRows) best = candidate
            continue
          }
        }
        return best
      }
      const dedupeMeasureMetaCandidatesByMeasureIdentity = (entries: any[], displayName: string): any[] => {
        const byIdentity = new Map<string, any>()
        for (const entry of (Array.isArray(entries) ? entries : [])) {
          const colId = String(entry?.name ?? '').trim()
          if (!colId) continue
          const ci = findBestCiForColumn(colId)
          const identity = selectedMeasureIdentityKey(
            colId,
            null,
            ci ? (String((ci as any)?.name ?? '').trim() || null) : null,
            ci,
            displayName || String(entry?.caption ?? '').trim() || null,
          )
          if (!identity) continue
          if (!byIdentity.has(identity)) byIdentity.set(identity, entry)
        }
        return byIdentity.size > 0 ? Array.from(byIdentity.values()) : entries
      }

      const desiredLabels = desiredMeasureLabelOrder.length > 0
        ? desiredMeasureLabelOrder
        : (measureNamesCustomOrder.length > 0 ? measureNamesCustomOrder : measureFilterLabelOrder)

      for (const lblRaw of desiredLabels) {
        const lbl = canonicalizeMeasureLabel(lblRaw)
        if (!lbl) continue
        const lblBase = stripAlongSuffix(lbl)
        // First priority: Measure Names selections may already be the raw Tableau member token
        // (`[avg:Discount:qk]`, `[usr:Calculation_...:qk]`, etc.). Preserve that identity so the
        // aggregate token and copied/table-calc column instance survive normalization.
        const directMemberKey =
          normalizeMeasureMemberTokenKey(lblRaw) ||
          normalizeMeasureMemberTokenKey(lbl)
        const directCi = directMemberKey ? preferCanonicalMeasureCi(ciByFieldKey.get(directMemberKey) || null) : null
        if (directCi) {
          const colId = resolveCiColumnId(directCi)
          const aggTok = inferAggTokFromCiName(directCi)
          if (!colId) continue
          if (!isMeasureLikeCi(directCi, colId, aggTok)) {
            throw new Error(`Measure Names selection is not a measure: ${lbl}`)
          }
          const displayName =
            canonicalizeMeasureLabel((directCi as any)?.caption) ||
            columnCaptionForId(colId, (directCi as any)?.datasource) ||
            normalizeMeasureName(String((directCi as any)?.name ?? lbl ?? ''))
          addMeasure(colId, aggTok, String((directCi as any)?.name ?? '').trim() || null, directCi, displayName || null, false)
          continue
        }
        // First: if this label is a pure Measure Names alias, resolve it to the underlying
        // member key(s) and add the exact referenced column instance.
        const aliasKeyList =
          measureNamesAliasKeysByLabelKey.get(measureCaptionKey(lbl)) ||
          (lblBase && lblBase !== lbl ? measureNamesAliasKeysByLabelKey.get(measureCaptionKey(lblBase)) : undefined) ||
          undefined
        if (Array.isArray(aliasKeyList) && aliasKeyList.length > 0) {
          if (worksheetDebugEnabled) {
            debugLog('measureNames aliasHit', { label: lbl, keys: aliasKeyList.slice(0, 6) })
          }
          const ciCandidates: any[] = []
          const seen = new Set<string>()
          for (const rawKey of aliasKeyList) {
            const k = normalizeMeasureMemberTokenKey(rawKey)
            if (!k) continue
            const ci = preferCanonicalMeasureCi(ciByFieldKey.get(k) || null)
            if (!ci) continue
            const nm = String(ci?.name ?? '').trim()
            if (!nm) continue
            if (seen.has(nm)) continue
            seen.add(nm)
            ciCandidates.push(ci)
          }
          if (ciCandidates.length === 0) {
            throw new Error(`Measure Names alias could not be resolved: ${lbl}`)
          }
          const uniqueCiCandidates = dedupeCiCandidatesByMeasureIdentity(ciCandidates, lbl)
          if (uniqueCiCandidates.length > 1) {
            const caps = uniqueCiCandidates
              .map((m) => canonicalizeMeasureLabel(m?.caption) || canonicalizeMeasureLabel(m?.name))
              .filter(Boolean)
              .slice(0, 12)
            throw new Error(
              `Measure Names selection is ambiguous: ${lbl} matched ${uniqueCiCandidates.length} aliased column instances: ${caps.join(' | ')}`
            )
          }
          const ci = preferCanonicalMeasureCi(uniqueCiCandidates[0] || null)
          if (!ci) continue
          const colId = resolveCiColumnId(ci)
          if (!colId) continue
          const aggTok = inferAggTokFromCiName(ci)
          if (!isMeasureLikeCi(ci, colId, aggTok)) {
            throw new Error(`Measure Names selection is not a measure: ${lbl}`)
          }
          addMeasure(colId, aggTok, String((ci as any)?.name ?? '').trim() || null, ci, lbl, true)
          continue
        }
        const strictKeysToTry = [
          measureCaptionStrictKey(lbl),
          lblBase && lblBase !== lbl ? measureCaptionStrictKey(lblBase) : '',
        ].filter(Boolean) as string[]
        const keysToTry = [
          measureCaptionKey(lbl),
          lblBase && lblBase !== lbl ? measureCaptionKey(lblBase) : '',
        ].filter(Boolean) as string[]
        const looseToTry = [
          looseKey(lbl),
          lblBase && lblBase !== lbl ? looseKey(lblBase) : '',
        ].filter(Boolean) as string[]
        const candidates: any[] = []
        const addCiCandidates = (bucket: any[], arr: any[] | undefined) => {
          for (const x of (Array.isArray(arr) ? arr : [])) {
            const nm = String(x?.name ?? '').trim()
            if (!nm) continue
            if (!bucket.some((y) => String(y?.name ?? '').trim() === nm)) bucket.push(x)
          }
        }
        const addCandidates = (arr: any[] | undefined) => {
          for (const x of (Array.isArray(arr) ? arr : [])) {
            const id = String(x?.name ?? '').trim()
            if (!id) continue
            if (!candidates.some((y) => String(y?.name ?? '').trim() === id)) candidates.push(x)
          }
        }
        for (const k of strictKeysToTry) addCandidates(byCaptionStrictKey.get(k))
        const strictCiCandidates: any[] = []
        if (candidates.length === 0) {
          for (const k of strictKeysToTry) addCiCandidates(strictCiCandidates, ciByCaptionStrictKey.get(k))
        }
        if (strictCiCandidates.length > 0) {
          const uniqueCiCandidates = dedupeCiCandidatesByMeasureIdentity(strictCiCandidates, lbl)
          if (uniqueCiCandidates.length > 1) {
            const caps = uniqueCiCandidates
              .map((m) => canonicalizeMeasureLabel(m?.caption) || canonicalizeMeasureLabel(m?.name))
              .filter(Boolean)
              .slice(0, 12)
            throw new Error(
              `Measure Names selection is ambiguous: ${lbl} matched ${uniqueCiCandidates.length} exact column instances: ${caps.join(' | ')}`
            )
          }
          const ci = preferCanonicalMeasureCi(uniqueCiCandidates[0] || null)
          if (!ci) continue
          const colId = resolveCiColumnId(ci)
          if (!colId) continue
          const aggTok = inferAggTokFromCiName(ci)
          if (!isMeasureLikeCi(ci, colId, aggTok)) {
            throw new Error(`Measure Names selection is not a measure: ${lbl}`)
          }
          const displayName = lbl || canonicalizeMeasureLabel(ci?.caption)
          addMeasure(colId, aggTok, String((ci as any)?.name ?? '').trim() || null, ci, displayName, true)
          continue
        }
        if (candidates.length === 0) {
          for (const k of keysToTry) addCandidates(byCaptionKey.get(k))
          for (const k of looseToTry) addCandidates(byLoose.get(k))
        }

        if (candidates.length === 0) {
          // Try resolving to a measure-like column instance (table-calc/copy measures).
          const ciCandidates: any[] = []
          for (const k of strictKeysToTry) addCiCandidates(ciCandidates, ciByCaptionStrictKey.get(k))
          if (ciCandidates.length === 0) {
            for (const k of keysToTry) addCiCandidates(ciCandidates, ciByCaptionKey.get(k))
            for (const k of looseToTry) addCiCandidates(ciCandidates, ciByLoose.get(k))
          }

          if (ciCandidates.length === 0) {
            if (!ALLOW_FALLBACKS) {
              throw new Error(`Measure Names selection includes unknown measure: ${lbl}`)
            }
            continue
          }
          const uniqueCiCandidates = dedupeCiCandidatesByMeasureIdentity(ciCandidates, lbl)
          if (uniqueCiCandidates.length > 1) {
            const caps = uniqueCiCandidates
              .map((m) => canonicalizeMeasureLabel(m?.caption) || canonicalizeMeasureLabel(m?.name))
              .filter(Boolean)
              .slice(0, 12)
            throw new Error(
              `Measure Names selection is ambiguous: ${lbl} matched ${uniqueCiCandidates.length} column instances: ${caps.join(' | ')}`
            )
          }
          const ci = preferCanonicalMeasureCi(uniqueCiCandidates[0] || null)
          if (!ci) continue
          const colId = resolveCiColumnId(ci)
          if (!colId) continue
          const aggTok = inferAggTokFromCiName(ci)
          if (!isMeasureLikeCi(ci, colId, aggTok)) {
            throw new Error(`Measure Names selection is not a measure: ${lbl}`)
          }
          const displayName = lbl || canonicalizeMeasureLabel(ci?.caption)
          addMeasure(colId, aggTok, String((ci as any)?.name ?? '').trim() || null, ci, displayName, true)
          continue
        }
        const uniqueCandidates = dedupeMeasureMetaCandidatesByMeasureIdentity(candidates, lbl)
        if (uniqueCandidates.length > 1) {
          const caps = uniqueCandidates
            .map((m) => String(m?.caption ?? m?.name ?? '').trim())
            .filter(Boolean)
            .slice(0, 12)
          throw new Error(
            `Measure Names selection is ambiguous: ${lbl} matched ${uniqueCandidates.length} measures: ${caps.join(' | ')}`
          )
        }
        const meta = uniqueCandidates[0]
        const colId = String(meta?.name ?? '').trim()
        if (!colId) continue
        const ci = findBestCiForColumn(colId)
        // Prefer the Measure Names selection label (it can reflect shelf renames / aliases),
        // falling back to the base column caption only when the label is missing.
        const displayName = lbl || String(meta?.caption ?? '').trim()
        addMeasure(colId, null, ci ? String((ci as any)?.name ?? '').trim() || null : null, ci, displayName, true)
      }

      // Important: even with an explicit Measure Names selection, we still include measures explicitly
      // referenced on the worksheet shelves (qk tokens). This is the most reliable way to include
      // Tableau-generated "copy" measures (e.g. quick table calcs like % diff) that may not appear
      // in the Measure Names filter value list, while still avoiding workbook-wide qk scans.
      if (measureOverrideKeySet.size === 0) {
        for (const tok of viewMeasureTokens) {
          const ci = preferCanonicalMeasureCi(ciByName.get(tok) || null)
          if (!ci) continue
          const col = resolveCiColumnId(ci)
          if (!col) continue
          const parts = (cleanBracketedFieldRef0(String(ci?.name ?? tok).trim()) || String(ci?.name ?? tok))
            .split(':')
            .map((p) => p.trim().replace(/^\[/, '').replace(/\]$/, ''))
            .filter(Boolean)
          let aggTok: string | null = null
          for (const p of parts) {
            const t = p.toLowerCase()
            if (aggTokens.has(t)) { aggTok = t; break }
          }
          if (!isMeasureLikeCi(ci, col, aggTok)) continue
          const displayName = (() => {
            const cap = String(ci?.caption ?? '').trim()
            if (cap) return cap
            const colCap = columnCaptionForId(col, ci?.datasource)
            if (colCap) return colCap
            const tokenKey = normalizeTableauFieldToken(String(ci?.name ?? tok ?? '')).trim().toLowerCase()
            const manifestCap = tokenKey ? manifestCaptionByToken.get(tokenKey) : undefined
            if (manifestCap) return manifestCap
            const nm = String(ci?.name ?? tok ?? '').trim()
            return normalizeMeasureName(nm)
          })()
          addMeasure(col, aggTok, String((ci as any)?.name ?? tok ?? '').trim() || null, ci, displayName)
        }
      }
    }
    if (hasMeasureNameSelection) {
      measures = Array.from(measureMap.values())
      if (sourceSchema) {
        measures = measures.filter(measureAvailableInAuthoritativeSchema)
      }
    }
    const measureOrderKeys =
      measureOverrideKeyOrder.length > measureFilterKeyOrder.length
        ? measureOverrideKeyOrder
        : measureFilterKeyOrder
    // Prefer deterministic ordering by label when we have it (manual Measure Names order / filter label order).
    if (desiredMeasureLabelOrder.length > 0) {
      const orderMap = new Map<string, number>()
      for (let i = 0; i < desiredMeasureLabelOrder.length; i++) {
        const k = measureCaptionKey(desiredMeasureLabelOrder[i]!)
        if (k && !orderMap.has(k)) orderMap.set(k, i)
      }
      const orderIdx = (m: { columnId: string; token?: string | null; displayName?: string | null; ci?: any | null }): number => {
        const lbl = resolveMeasureLabelCached(m as any)
        const k = measureCaptionKey(lbl)
        if (k && orderMap.has(k)) return orderMap.get(k)!
        // Second chance: try explicit displayName / token.
        const candidates = [(m as any).displayName, m.token, m.columnId]
        for (const c of candidates) {
          const ck = measureCaptionKey(String(c ?? '').trim())
          if (ck && orderMap.has(ck)) return orderMap.get(ck)!
        }
        return 1_000_000
      }
      measures = measures
        .map((m) => ({ m, i: orderIdx(m as any) }))
        .sort((a, b) => (a.i - b.i))
        .map((x) => x.m)
    }
    if (desiredMeasureLabelOrder.length === 0 && measureOrderKeys.length > 0) {
      const orderMap = new Map<string, number>()
      for (let i = 0; i < measureOrderKeys.length; i++) {
        orderMap.set(measureOrderKeys[i]!, i)
      }
      const orderIdx = (m: { columnId: string; token?: string | null; displayName?: string | null; ci?: any | null }): number => {
        const candidates = [(m as any).displayName, m.token, m.columnId, resolveMeasureLabelCached(m as any)]
        for (const c of candidates) {
          for (const k of normalizeMeasureKeys(c)) {
            if (orderMap.has(k)) return orderMap.get(k)!
          }
        }
        return 1_000_000
      }
      measures = measures
        .map((m) => ({ m, i: orderIdx(m as any) }))
        .sort((a, b) => (a.i - b.i))
        .map((x) => x.m)
    }
    if (worksheetDebugEnabled) {
      debugLog('measureNames measures (filtered)', measures.map((m) => ({
        columnId: m.columnId,
        aggTok: m.aggTok,
        token: m.token,
        displayName: (m as any).displayName ?? null,
        displayNameOverride: (m as any).displayNameOverride ?? null,
        ciName: (m as any).ci?.name ?? null,
        ciCaption: (m as any).ci?.caption ?? null,
      })))
    }
    const search =
      (typeof window !== 'undefined' && window.location?.search) ||
      (typeof self !== 'undefined' && (self as any).location?.search) ||
      ''
    if (search) {
      const qp = new URLSearchParams(search)
      const dumpMeasures = qp.get('fgDumpMeasureNames')
      const dumpFilters = qp.get('fgDumpFilters')
      const shouldDump =
        (dumpMeasures && !['0', 'false', 'off', 'no'].includes(dumpMeasures.trim().toLowerCase())) ||
        (dumpFilters && !['0', 'false', 'off', 'no'].includes(dumpFilters.trim().toLowerCase()))
      if (shouldDump) {
        try {
          console.log('[FG-MEASURE-NAMES] filter keys', measureFilterKeyOrder)
          console.log('[FG-MEASURE-NAMES] filter labels', Array.from(measureFilterLabelByKey.entries()))
          console.log('[FG-MEASURE-NAMES] measures', measures.map((m) => ({
            columnId: m.columnId,
            aggTok: m.aggTok,
            token: m.token,
            displayName: (m as any).displayName ?? null,
            displayNameOverride: (m as any).displayNameOverride ?? null,
            ciName: (m as any).ci?.name ?? null,
            ciCaption: (m as any).ci?.caption ?? null,
          })))
        } catch {}
      }
    }
    const mnvOptimization = (() => {
      const defaults = {
        enabled: true,
        stagedProjection: false,
        stagedProjectionSheet: '',
        chunkSize: 8,
        chunkMinMeasures: 8,
        chunkMaxExprChars: 18000,
        windowCseEnabled: false,
        castCseEnabled: true,
        minCount: 2,
        minExprLen: 80,
        maxFactoredWindowExprs: 96,
        debug: false,
      }
      const readRaw = (qp: URLSearchParams | null, keys: string[]): string => {
        for (const k of keys) {
          const fromQp = qp?.get(k)
          if (fromQp != null && String(fromQp).trim() !== '') return String(fromQp).trim()
          if (typeof window !== 'undefined') {
            try {
              const fromLs = window.localStorage.getItem(k)
              if (fromLs != null && String(fromLs).trim() !== '') return String(fromLs).trim()
            } catch { }
          }
        }
        return ''
      }
      const toBool = (raw: string, fallback: boolean): boolean => {
        const v = String(raw || '').trim().toLowerCase()
        if (!v) return fallback
        return !['0', 'false', 'off', 'no'].includes(v)
      }
      const toInt = (raw: string, fallback: number, min: number, max: number): number => {
        if (String(raw || '').trim() === '') return fallback
        const n = Math.floor(Number(raw))
        if (!Number.isFinite(n)) return fallback
        return Math.max(min, Math.min(max, n))
      }
      const qp = search ? new URLSearchParams(search) : null
      const enabled = toBool(readRaw(qp, ['fg_mnv_cse', 'fg_mnv_opt']), defaults.enabled)
      const stagedProjection = toBool(readRaw(qp, ['fg_mnv_staged_projection']), defaults.stagedProjection)
      const stagedProjectionSheet = readRaw(qp, ['fg_mnv_staged_projection_sheet'])
      const rawChunkSize = readRaw(qp, ['fg_mnv_chunk_size'])
      const chunkSize = toInt(rawChunkSize, defaults.chunkSize, 2, 64)
      const rawChunkMinMeasures = readRaw(qp, ['fg_mnv_chunk_min_measures'])
      const chunkMinMeasures = toInt(rawChunkMinMeasures, defaults.chunkMinMeasures, 1, 256)
      const rawChunkMaxExprChars = readRaw(qp, ['fg_mnv_chunk_max_expr_chars'])
      const chunkMaxExprChars = toInt(rawChunkMaxExprChars, defaults.chunkMaxExprChars, 2000, 200000)
      const windowCseEnabled = toBool(readRaw(qp, ['fg_mnv_window_cse']), defaults.windowCseEnabled)
      const castCseEnabled = toBool(readRaw(qp, ['fg_mnv_cast_cse']), defaults.castCseEnabled)
      const minCount = toInt(readRaw(qp, ['fg_mnv_cse_min_count']), defaults.minCount, 2, 16)
      const minExprLen = toInt(readRaw(qp, ['fg_mnv_cse_min_expr_len']), defaults.minExprLen, 24, 2048)
      const maxFactoredWindowExprs = toInt(
        readRaw(qp, ['fg_mnv_cse_max_window_exprs']),
        defaults.maxFactoredWindowExprs,
        8,
        256,
      )
      const debug = toBool(readRaw(qp, ['fg_mnv_cse_debug']), defaults.debug)
      return {
        enabled: artifactExportMode ? true : enabled,
        stagedProjection: artifactExportMode ? true : stagedProjection,
        stagedProjectionSheet,
        chunkSize: artifactExportMode ? 1 : chunkSize,
        chunkMinMeasures: artifactExportMode ? 1 : chunkMinMeasures,
        chunkMaxExprChars,
        rawChunkSize,
        rawChunkMinMeasures,
        rawChunkMaxExprChars,
        search,
        windowCseEnabled,
        castCseEnabled,
        minCount,
        minExprLen,
        maxFactoredWindowExprs,
        debug,
      }
    })()

    const resolveDefaultAggTok = (columnId: string): string | null => {
      const meta = _fdColsByName.get(String(columnId || '').trim()) || null
      const a = String(meta?.aggregation ?? '').trim().toLowerCase()
      if (!a) return null
      if (a === 'sum') return 'sum'
      if (a === 'avg' || a === 'average') return 'avg'
      if (a === 'min') return 'min'
      if (a === 'max') return 'max'
      if (a === 'count') return 'count'
      if (a === 'countd' || a === 'ctd') return 'countd'
      return null
    }

    const isMeaningfulMeasureExpr = (expr: string): boolean => {
      if (isNullishExpr(expr)) return false
      const s = String(expr || '')
      const baseRefEsc = baseRef.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      const hasRef = new RegExp(`${baseRefEsc}\\.`).test(s) || /"[^"]+"/.test(s)
      if (!hasRef && /\bNULL\b/i.test(s)) return false
      return true
    }

    _markPhase('adv_measure_setup')
    const buildMeasureValueExpr = (m: { columnId: string; aggTok: string | null; tableCalcs?: any[] | null; ci?: any | null; displayName?: string | null; token?: string | null }): string | null => {
      const mAny = m as any
      const columnId = String(m.columnId || '').trim()
      const ciBaseField = String((m as any).ci?.base_field_id ?? (m as any).ci?.baseFieldId ?? '').trim()
      const ciBaseClean = ciBaseField ? (cleanBracketedFieldRef0(ciBaseField) || ciBaseField) : ''
      const meta =
        _fdColsByName.get(columnId) ||
        (ciBaseClean ? _fdColsByName.get(ciBaseClean) : null) ||
        null
      const aggTok = m.aggTok || resolveDefaultAggTok(m.columnId)
      const isMeasureMeta = (() => {
        const role = String((m as any).ci?.role ?? meta?.role ?? '').trim().toLowerCase()
        if (role === 'measure') return true
        const rType = String(meta?.['r#type'] ?? (meta as any)?.type ?? '').trim().toLowerCase()
        if (rType.includes('quant')) return true
        const dt = String((meta as any)?.datatype ?? (meta as any)?.data_type ?? '').trim().toLowerCase()
        return dt.includes('int') || dt.includes('decimal') || dt.includes('numeric') || dt.includes('double') || dt.includes('float') || dt.includes('real')
      })()
      const aggTokResolved = aggTok || (isMeasureMeta ? 'sum' : null)
      const agg = aggTokenToSql(aggTokResolved)

      let calcFormula = preferredCalculatedFormula(
        (m as any).ci?.name,
        (m as any).ci?.caption,
        columnId,
        meta?.name,
        meta?.caption,
        mAny.displayName,
      )
      if (!calcFormula) {
        // Some workbooks store calculated field formulas only on column instances (not column metas).
        // If we selected this measure from fdCols matching, recover a formula-bearing CI for the same column id.
        const bestCi = (() => {
          const candidates = ciByColumnIdWithFormula.get(columnId) || []
          if (candidates.length === 0) return null
          // Prefer a CI whose caption matches the current displayName (if present), otherwise stable pick.
          const wantCap = String(mAny.displayName ?? '').trim().toLowerCase()
          candidates.sort((a, b) => {
            const ac = String((a as any)?.caption ?? '').trim().toLowerCase()
            const bc = String((b as any)?.caption ?? '').trim().toLowerCase()
            const am = wantCap && ac === wantCap ? 0 : 1
            const bm = wantCap && bc === wantCap ? 0 : 1
            if (am !== bm) return am - bm
            const an = String((a as any)?.name ?? '').trim()
            const bn = String((b as any)?.name ?? '').trim()
            return an.localeCompare(bn)
          })
          return candidates[0]
        })()
        if (bestCi) {
          const f = String((bestCi as any)?.calculation?.formula ?? '').trim()
          if (f) {
            calcFormula = f
            // Also recover table-calc addressing/partitioning if missing.
            if (!Array.isArray(mAny.tableCalcs) || mAny.tableCalcs.length === 0) {
              try { mAny.tableCalcs = resolveTableCalcsForColumn(bestCi, columnId) } catch { }
            }
          }
        }
      }
      const over = calcFormula ? overClauseForMeasure(mAny.tableCalcs ?? null, {
        source: 'measure_expr',
        fieldRef: String(mAny.token ?? '').trim() || null,
        columnId,
        displayName: String(mAny.displayName ?? '').trim() || null,
        ciName: String(mAny.ci?.name ?? '').trim() || null,
        ciCaption: String(mAny.ci?.caption ?? '').trim() || null,
      }) : ''
      const cacheKey = measureExprCacheKey(columnId, aggTokResolved, calcFormula, over)
      if (measureExprCache.has(cacheKey)) return measureExprCache.get(cacheKey) ?? null
      if (calcFormula) {
        const missingFormulaRefs = missingPhysicalRefsForCalcFormula(calcFormula)
        if (missingFormulaRefs.length > 0) {
          if (worksheetDebugEnabled) {
            debugLog('measureNames skipped_missing_formula_physical_refs', {
              columnId: m.columnId,
              token: m.token ?? null,
              displayName: (m as any)?.displayName ?? null,
              missingPhysicalRefs: missingFormulaRefs.slice(0, 8),
            })
          }
          measureExprCache.set(cacheKey, null)
          return null
        }
        const compiled = compileTableauCalc(calcFormula, over)
        if (compiled.invalid) {
          measureExprCache.set(cacheKey, null)
          return null
        }
        let expr = rewriteCompiledWindowScopeToGroupedDims(compiled.sql)
        const sqlHasUnresolvedField = new RegExp(UNRESOLVED_FIELD, 'i').test(String(expr || ''))
        if (sqlHasUnresolvedField) {
          measureExprCache.set(cacheKey, null)
          return null
        }
        // If Tableau expects aggregation on a row-level calc, wrap it.
        if (agg && !compiled.isAgg && !compiled.isTableCalc) {
          if (agg.kind === 'countd') expr = `COUNT(DISTINCT ${expr})`
          else expr = `${agg.fn}(${expr})`
        } else if (!agg && !compiled.isAgg && !compiled.isTableCalc) {
          const hasAgg = /\b(SUM|AVG|MIN|MAX|COUNT)\s*\(/i.test(expr)
          // Non-aggregated calcs used as measures should behave like ATTR to keep GROUP BY valid.
          if (!hasAgg) expr = tableauAttrSql(expr)
        }
        if (!isMeaningfulMeasureExpr(expr)) {
          measureExprCache.set(cacheKey, null)
          return null
        }
        if (hasExtractSchema) {
          // Guard against compiled measure expressions that still reference physical columns
          // absent from the active extract/schema projection (common with stale Measure Names members).
          // Keep grouped aliases in scope here as well: exec-plan measure/table-calc SQL legitimately
          // references derived row/column dims (for example hidden worksheet headers used in OVER clauses).
          const validation = validateSqlPhysicalRefs(expr, {
            allowedDerivedAliases: measureExprProjectedRefKeys,
            includeUnqualified: true,
          })
          if (!validation.ok) {
            if (worksheetDebugEnabled) {
              debugLog('measureNames skipped_missing_physical_refs', {
                columnId: m.columnId,
                token: m.token ?? null,
                displayName: (m as any)?.displayName ?? null,
                missingPhysicalRefs: validation.missingPhysicalRefs.slice(0, 8),
              })
            }
            measureExprCache.set(cacheKey, null)
            return null
          }
        }
        if (worksheetDebugEnabled) {
          const label = resolveMeasureLabelCached(m as any)
          const labelKey = String(label || '').toLowerCase()
          const shouldLog = labelKey.includes('diff from monthly avg') || labelKey.includes('max') || /\bnull\b/i.test(expr)
          if (shouldLog) {
            debugLog('measureNames calc', {
              label,
              columnId: m.columnId,
              aggTok: aggTokResolved,
              token: m.token,
              displayName: (m as any).displayName ?? null,
              ciName: (m as any).ci?.name ?? null,
              ciCaption: (m as any).ci?.caption ?? null,
              baseFieldId: ciBaseField || null,
              tableCalcs: Array.isArray(m.tableCalcs) ? m.tableCalcs : null,
              over,
              formula: calcFormula,
              compiledSql: compiled.sql,
              isAgg: compiled.isAgg,
              isTableCalc: compiled.isTableCalc,
              finalExpr: expr,
            })
          }
        }
        measureExprCache.set(cacheKey, expr)
        return expr
      }

      let resolved = resolveCol(columnId)
      if ((!resolved || isUnresolvedField(resolved)) && hasExtractSchema) {
        // Manifest-sourced physical measures can arrive as bracketed field ids (e.g. "[productamount]")
        // even when semantic bindings only expose raw extract columns. Before dropping the measure,
        // retry against the active extract schema with cleaned physical column candidates.
        const directCandidates = [
          stripBracketsAroundFieldName(columnId),
          cleanBracketedFieldRef0(columnId),
          stripBracketsAroundFieldName(String((meta as any)?.column_name ?? '').trim()),
          stripBracketsAroundFieldName(String((m as any)?.ci?.column ?? '').trim()),
          stripBracketsAroundFieldName(ciBaseClean),
        ]
        const seenDirect = new Set<string>()
        for (const candRaw of directCandidates) {
          const cand = String(candRaw ?? '').trim()
          if (!cand) continue
          const candKey = normalizeTableauFieldKey(cand) || cand.toLowerCase()
          if (!candKey || seenDirect.has(candKey)) continue
          seenDirect.add(candKey)
          if (!isPresentInExtractSchema(cand)) continue
          resolved = cand
          break
        }
      }
      if (!resolved || isUnresolvedField(resolved)) {
        if (worksheetDebugEnabled) {
          const label = resolveMeasureLabelCached(m as any)
          const labelKey = String(label || '').toLowerCase()
          if (labelKey.includes('diff from monthly avg') || labelKey.includes('max')) {
            debugLog('measureNames unresolved base', {
              label,
              columnId: m.columnId,
              resolved,
              aggTok: aggTokResolved,
              token: m.token,
            })
          }
        }
        measureExprCache.set(cacheKey, null)
        return null
      }
      const base = `${baseRef}.${quoteIdent(resolved)}`
      if (!agg) {
        const expr = isMeaningfulMeasureExpr(base) ? tableauAttrSql(base) : null
        measureExprCache.set(cacheKey, expr)
        return expr
      }
      if (agg.kind === 'countd') {
        const expr = `COUNT(DISTINCT ${base})`
        measureExprCache.set(cacheKey, expr)
        return expr
      }
      if (agg.fn === 'COUNT') {
        const expr = `COUNT(${base})`
        measureExprCache.set(cacheKey, expr)
        return expr
      }
      // Numeric-ish aggregates
      const expr = `${agg.fn}(TRY_CAST(${base} AS DOUBLE))`
      const out = isMeaningfulMeasureExpr(expr) ? expr : null
      measureExprCache.set(cacheKey, out)
      return out
    }

    const seededWeeklyDateGroupDimExprByIdx = (() => {
      const out = new Map<number, string>()
      if (!densify) return out
      const timePeriodIdx = groupDims.findIndex((dim) => {
        const key = normKey(String(dim?.field_name ?? ''))
        return Boolean(key && key.includes('time period') && !key.includes('begin dates') && !key.includes('end dates'))
      })
      const timePeriodExpr = timePeriodIdx >= 0 ? String(dimExprs[timePeriodIdx] ?? '').trim() : ''
      if (!timePeriodExpr) return out
      const stableWeekAnchorIso = '2000-01-03 00:00:00'
      for (let idx = 0; idx < groupDims.length; idx += 1) {
        const dim = groupDims[idx]
        const fieldName = String(dim?.field_name ?? '').trim()
        const key = normKey(fieldName)
        if (!key) continue
        if (!key.includes('time period begin dates') && !key.includes('time period end dates')) continue
        const fieldRef = groupDimFieldRef(dim) || fieldName
        const formula = String(
          lookupCalculatedFormula(fieldRef) ||
          lookupCalculatedFormula(fieldName) ||
          '',
        ).trim()
        if (!formula) continue
        if (!/\[Parameters\]\.\[[^\]]+\]/i.test(formula) || !/dateadd\('week'/i.test(formula)) continue
        const whens: string[] = []
        const anchorExpr = `CAST(${escapeValue(stableWeekAnchorIso)} AS TIMESTAMP)`
        const rx = /when\s+"([^"]+)"\s+then\s+dateadd\('week',\s*(-?\d+)\s*,\s*datetrunc\('week',[\s\S]*?\)\)\s*(-\s*1)?/gi
        let match: RegExpExecArray | null
        while ((match = rx.exec(formula)) !== null) {
          const label = String(match[1] ?? '').trim()
          const weekDelta = Number(match[2] ?? 0)
          if (!label || !Number.isFinite(weekDelta)) continue
          const dayDelta = weekDelta * 7 + (match[3] ? -1 : 0)
          const offsetExpr =
            dayDelta === 0
              ? anchorExpr
              : `(${anchorExpr} + (${dayDelta}) * INTERVAL 1 DAY)`
          whens.push(`WHEN ${escapeValue(label)} THEN ${offsetExpr}`)
        }
        if (!whens.length) continue
        out.set(idx, `CASE ${timePeriodExpr} ${whens.join(' ')} END`)
      }
      return out
    })()
    const groupByExprs = dimExprs
      .map((expr, idx) => String(seededWeeklyDateGroupDimExprByIdx.get(idx) ?? expr ?? '').trim())
      .filter((expr) => canSqlExpressionAppearInGroupBy(expr))
      .filter(Boolean)
    groupByExprsForMeasureOverClause = groupByExprs

    // Tooltip extra columns (computed at the same LOD as the mark)
    const tooltipExtraSelects = buildTooltipExtraSelects()

    const totalsConfigRaw: any = (crosstabSpec as any)?.totals ?? null
    const rowSubtotalsConfig: boolean[] | null =
      Array.isArray(totalsConfigRaw?.rowSubtotals)
        ? ((totalsConfigRaw.rowSubtotals as any[]).map(Boolean) as boolean[])
        : null
    const includeGrandTotalConfig = Boolean(totalsConfigRaw?.includeGrandTotal)
    const hasConfiguredRowSubtotals = Boolean(rowSubtotalsConfig?.some(Boolean))
    // Only emit GROUPING SETS when totals are actually configured. Some workbooks
    // carry includeTotals=true metadata without subtotal/grand-total selections.
    // Emitting GROUPING(...) in that case can trigger DuckDB binder errors.
    const emitTotals = Boolean(includeTotals && (hasConfiguredRowSubtotals || includeGrandTotalConfig))
    emitTotalsForMeasureOverClause = emitTotals
    const interactionCubeSlicerDims = (() => {
      const raw = Array.isArray((options as any)?.interactionCubeSlicers)
        ? ((options as any).interactionCubeSlicers as any[])
        : []
      if (!raw.length || emitTotals) return [] as Array<{ fieldName: string; columnName: string; expr: string; select: string }>
      const out: Array<{ fieldName: string; columnName: string; expr: string; select: string }> = []
      const seen = new Set<string>()
      for (const entry of raw) {
        if (!entry || typeof entry !== 'object') continue
        const fieldName = String(
          (entry as any)?.fieldName ??
          (entry as any)?.field_name ??
          (entry as any)?.displayName ??
          (entry as any)?.display_name ??
          (entry as any)?.columnName ??
          (entry as any)?.column_name ??
          '',
        ).trim()
        const columnName = String(
          (entry as any)?.columnName ??
          (entry as any)?.column_name ??
          (entry as any)?.displayName ??
          (entry as any)?.display_name ??
          fieldName,
        ).trim()
        if (!fieldName || !columnName) continue
        const key = normalizeTableauFieldKey(columnName) || columnName.toLowerCase()
        if (!key || seen.has(key)) continue
        if (groupDims.some((dim) => {
          const dimName = String(dim?.field_name ?? '').trim()
          const dimKey = normalizeTableauFieldKey(dimName) || dimName.toLowerCase()
          return dimKey && dimKey === key
        })) continue
        let expr = fieldExprFor(fieldName, baseRef)
        if (isNullishExpr(expr) || isUnresolvedField(expr)) {
          const resolved = resolveCol(fieldName) || resolveCol(columnName)
          if (resolved && !isUnresolvedField(resolved)) {
            expr = `${baseRef}.${quoteIdent(resolved)}`
          }
        }
        if (isNullishExpr(expr) || isUnresolvedField(expr)) continue
        const slicerFormula = preferredCalculatedFormula(fieldName, columnName)
        if (
          slicerFormula &&
          /\b(?:SUM|AVG|MIN|MAX|COUNT|COUNTD|ATTR|TOTAL|WINDOW_[A-Z_]*|RUNNING_[A-Z_]*|LOOKUP|RANK[A-Z_]*)\s*\(/i.test(slicerFormula)
        ) {
          continue
        }
        if (/\b(?:SUM|AVG|MIN|MAX|COUNT|COUNTD|ANY_VALUE)\s*\(/i.test(expr) || /\bOVER\s*\(/i.test(expr)) continue
        if (sourceSchema) {
          const directRef = expr.match(/^(?:"[^"]+"|[A-Za-z_][A-Za-z0-9_]*)\."([^"]+)"$/)
          const directColumn = String(directRef?.[1] ?? '').trim()
          if (directColumn && !isColumnInSourceSchema(sourceSchema, directColumn)) continue
        }
        seen.add(key)
        out.push({
          fieldName,
          columnName,
          expr,
          select: `${expr} AS ${quoteIdent(columnName)}`,
        })
      }
      return out
    })()
    // DuckDB can reject GROUPING(...) when nested rewrites/window lifts reshape the grouped scope.
    // Encode null dimension members up front so subtotal rows can be detected by NULL grouping-set
    // outputs without relying on GROUPING().
    const totalsNullSentinel = '__FG_DIM_NULL_SENTINEL_A9E2D4__'
    const totalsGroupingExprs = emitTotals
      ? dimExprs.map((expr, idx) => {
          const trimmed = String(seededWeeklyDateGroupDimExprByIdx.get(idx) ?? expr ?? '').trim()
          if (!trimmed) return ''
          if (!canSqlExpressionAppearInGroupBy(trimmed)) return ''
          return `COALESCE(CAST(${trimmed} AS VARCHAR), '${totalsNullSentinel}')`
        })
      : []
    const baseOutRef = quoteIdent('b')
    const baseCte = quoteIdent('__fg_mnv_base')

    const buildMeasureFilterPred = (expr: string, operatorRaw: any, value: any, filter?: any): string | null => {
      const o = String(operatorRaw ?? '').trim().toUpperCase()
      if (!o) return null
      const e = String(expr ?? '').trim()
      if (!e) return null
      if (o === 'IN' || o === 'NOT IN') {
        const not = o === 'NOT IN'
        if (Array.isArray(value) && value.length === 0) {
          return (o === 'IN' && isExplicitEmptyFilter(filter)) ? 'FALSE' : null
        }
        return buildInPredicateFromValue(e, value, not, { includeNullsOnNot: true })
      }
      if (o === 'IS NULL' || o === 'IS NOT NULL') return `${e} ${o}`
      if (o === 'BETWEEN') {
        if (Array.isArray(value) && value.length === 2) {
          return buildBetweenPredicate(e, value[0], value[1], filter, null)
        }
        return null
      }
      if (o === 'LIKE' || o === 'NOT LIKE') return `${e} ${o} ${escapeValue(value)}`
      if (o === '!=' || o === '<>') return `(${e} IS NULL OR ${e} ${o} ${escapeValue(value)})`
      return `${e} ${o} ${escapeValue(value)}`
    }

    const selectDimsForBase = (() => {
      if (!emitTotals) {
        return dimSelects.map((part, idx) => {
          const seededExpr = String(seededWeeklyDateGroupDimExprByIdx.get(idx) ?? '').trim()
          if (!seededExpr) return part
          const fn = String(groupDims[idx]?.field_name ?? '').trim()
          if (!fn) return part
          return `${seededExpr} AS ${quoteIdent(fn)}`
        })
      }
      const out: string[] = []
      for (let i = 0; i < groupDims.length; i++) {
        const groupedExpr = String(totalsGroupingExprs[i] ?? '').trim()
        if (!groupedExpr) continue
        // GROUPING SETS already returns NULL for dimensions omitted by subtotal/grand-total
        // branches. Preserve that signal directly so totals rows do not inherit an arbitrary
        // leaf member via ANY_VALUE(...), which collapses visible row domains.
        out.push(`${groupedExpr} AS ${quoteIdent(`__fg_group_key_${i}`)}`)
      }
      return out
    })()

    type MnvMeasureOut = {
      label: string
      key: string
      valueExpr: string
      valueAlias: string
      filterPreds: string[]
    }

    const contextFilters = (Array.isArray(filters) ? filters : []).filter((ff: any) => {
      const ft = String((ff as any)?.filter_type ?? '').trim().toLowerCase()
      return ft === 'context' || Boolean((ff as any)?.context)
    })

    const visibleGroupDimKeys = new Set(
      groupDims
        .map((d) => normKey(String(d?.field_name ?? '')))
        .filter(Boolean)
    )
    const deferredSharedFilters: any[] = []
    const deferredFilterBindScalar = (idx: number): string => `/*__FG_FILTER:${idx}__*/ NULL`
    const deferredFilterBindPart = (idx: number, part: string): string => `/*__FG_FILTER:${idx}:${part}__*/ NULL`
    const deferredFilterBindTableIdent = (idx: number): string => `__FG_FILTER_TABLE__${idx}__`
    const buildDeferredWrapperInPredicateTemplate = (
      exprSql: string,
      not: boolean,
      filterIndex: number,
      opts?: { includeNullsOnNot?: boolean },
    ): string | null => {
      const includeNullsOnNot = Boolean(opts?.includeNullsOnNot)
      const tbl = deferredFilterBindTableIdent(filterIndex)
      const any = `EXISTS (SELECT 1 FROM ${tbl})`
      const hasNull = `EXISTS (SELECT 1 FROM ${tbl} WHERE value IS NULL)`
      const exprAsVarchar = `CAST(${exprSql} AS VARCHAR)`
      const exprAsDouble = `TRY_CAST(${exprSql} AS DOUBLE)`
      const exprAsBoolean = `TRY_CAST(${exprSql} AS BOOLEAN)`
      const exprAsDate = `TRY_CAST(${exprSql} AS DATE)`
      const exprAsTimestamp = `TRY_CAST(${exprSql} AS TIMESTAMP)`
      const rawValueAsVarchar = `CAST(value AS VARCHAR)`
      const canonicalValueAsVarchar = `CAST(value_canonical AS VARCHAR)`
      const valueAsDouble = `COALESCE(TRY_CAST(value_canonical AS DOUBLE), TRY_CAST(value AS DOUBLE))`
      const valueAsBoolean = `COALESCE(TRY_CAST(value_canonical AS BOOLEAN), TRY_CAST(value AS BOOLEAN))`
      const valueAsDate = `COALESCE(TRY_CAST(value_canonical AS DATE), TRY_CAST(value AS DATE))`
      const valueAsTimestamp = `COALESCE(TRY_CAST(value_canonical AS TIMESTAMP), TRY_CAST(value AS TIMESTAMP))`
      const nonNullMatch = `EXISTS (
        SELECT 1
        FROM ${tbl}
        WHERE value IS NOT NULL
          AND (
            ${exprAsVarchar} = ${rawValueAsVarchar}
            OR (value_canonical IS NOT NULL AND ${exprAsVarchar} = ${canonicalValueAsVarchar})
            OR (${exprAsDouble} IS NOT NULL AND ${valueAsDouble} IS NOT NULL AND ${exprAsDouble} = ${valueAsDouble})
            OR (${exprAsBoolean} IS NOT NULL AND ${valueAsBoolean} IS NOT NULL AND ${exprAsBoolean} = ${valueAsBoolean})
            OR (${exprAsDate} IS NOT NULL AND ${valueAsDate} IS NOT NULL AND ${exprAsDate} = ${valueAsDate})
            OR (${exprAsTimestamp} IS NOT NULL AND ${valueAsTimestamp} IS NOT NULL AND ${exprAsTimestamp} = ${valueAsTimestamp})
          )
      )`
      if (!not) {
        return `((NOT ${any}) OR ${nonNullMatch} OR (${hasNull} AND ${exprSql} IS NULL))`
      }
      return includeNullsOnNot
        ? `(CASE WHEN NOT ${any} THEN TRUE WHEN ${hasNull} THEN (${exprSql} IS NOT NULL AND NOT ${nonNullMatch}) ELSE (${exprSql} IS NULL OR NOT ${nonNullMatch}) END)`
        : `(CASE WHEN NOT ${any} THEN TRUE WHEN ${hasNull} THEN (${exprSql} IS NOT NULL AND NOT ${nonNullMatch}) ELSE (NOT ${nonNullMatch}) END)`
    }
    const buildTupleFilterPredicate = (args: {
      tuples: any
      not?: boolean
      resolveFieldSql: (fieldName: string) => string | null
    }): string | null => {
      const tuples = Array.isArray(args.tuples) ? args.tuples : []
      const tuplePredicates: string[] = []
    const tupleDebugSamples: Array<Record<string, any>> = []
      for (const tuple of tuples) {
        if (!tuple || typeof tuple !== 'object') continue
        const fieldPredicates: string[] = []
      const tupleDebugFields: Array<Record<string, any>> = []
        for (const [fieldRaw, value] of Object.entries(tuple)) {
          const fieldName = String(fieldRaw || '').trim()
          if (!fieldName) continue
        if (isMeasureNames(fieldName) || isMultipleValues(fieldName)) continue
          const fieldSql = args.resolveFieldSql(fieldName)
          if (!fieldSql) continue
          if (value === null) {
            fieldPredicates.push(`${fieldSql} IS NULL`)
          if (tupleDebugSamples.length < 2 && tupleDebugFields.length < 8) {
            tupleDebugFields.push({
              fieldName,
              value: null,
              fieldSqlSnippet: fieldSql.slice(0, 220),
              predicateSnippet: `${fieldSql} IS NULL`.slice(0, 220),
              looksSlashDate: false,
            })
          }
            continue
          }
          const pred = buildInPredicateFromValue(fieldSql, value, false, { includeNullsOnNot: true })
        if (pred) {
          fieldPredicates.push(pred)
          if (tupleDebugSamples.length < 2 && tupleDebugFields.length < 8) {
            const valueText = typeof value === 'string' ? value : JSON.stringify(value)
            tupleDebugFields.push({
              fieldName,
              value,
              fieldSqlSnippet: fieldSql.slice(0, 220),
              predicateSnippet: pred.slice(0, 220),
              looksSlashDate: typeof valueText === 'string' && /\b\d{1,2}\/\d{1,2}\/\d{4}\b/.test(valueText),
            })
          }
        }
        }
      if (fieldPredicates.length > 0) {
        tuplePredicates.push(`(${fieldPredicates.join(' AND ')})`)
        if (tupleDebugSamples.length < 2) {
          tupleDebugSamples.push({
            keys: Object.keys(tuple).sort(),
            fields: tupleDebugFields,
          })
        }
      }
      }
      if (tuplePredicates.length === 0) return args.not ? 'TRUE' : 'FALSE'
      const pred = `(${tuplePredicates.join(' OR ')})`
    // #region agent log
    try {
      if (String(worksheetName || '').toLowerCase().includes('detail')) {
        postOptionalLocalDebugLog('http://127.0.0.1:7788/ingest/7b95b565-26ed-4f27-b984-d1a6c1d0ea54',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'c5cbac'},body:JSON.stringify({sessionId:'c5cbac',runId:'detail-empty-round14',hypothesisId:'H32',location:'worksheetQueryBuilder.ts:14058',message:'shared tuple predicate analysis',data:{worksheetName:String(worksheetName || ''),tupleCount:tuples.length,not:Boolean(args.not),tupleDebugSamples,predicateSnippet:pred.slice(0,900)},timestamp:Date.now()})})
      }
    } catch {}
    // #endregion
      return args.not ? `(NOT ${pred})` : pred
    }
    const buildDeferredWrapperFilterPredicate = (filter: any): string | null => {
      if (!filter) return null
      const operator = String(filter.operator || '').trim().toUpperCase()
      const value = filter.value
      if (operator === 'ANY_TUPLE' || operator === 'NOT ANY_TUPLE') {
        return buildTupleFilterPredicate({
          tuples: value,
          not: operator === 'NOT ANY_TUPLE',
          resolveFieldSql: (fieldName: string) => {
            const rawFieldName = String(fieldName || '').trim()
            if (!rawFieldName) return null
            return `(${quoteIdent('__ws')}.${quoteIdent(rawFieldName)})`
          },
        })
      }
      const rawFieldName = String(filter.field_name || '').trim()
      if (!rawFieldName) return null
      const fieldSql = `(${quoteIdent('__ws')}.${quoteIdent(rawFieldName)})`
      if (!operator) return null
      const filterIndex = filterIndexByRef.get(filter)
      const explicitEmpty = isExplicitEmptyFilter(filter)
      if ((operator === 'IN' || operator === 'NOT IN') && templateMode) {
        if (!(Number.isFinite(filterIndex) && (filterIndex as number) >= 0)) {
          throw new Error(`templateMode: deferred wrapper IN filter index missing for field "${rawFieldName}"`)
        }
        const not = operator === 'NOT IN'
        if (!not && explicitEmpty) return 'FALSE'
        return buildDeferredWrapperInPredicateTemplate(fieldSql, not, filterIndex as number, { includeNullsOnNot: true })
      }
      if (operator === 'IN' || operator === 'NOT IN') {
        const not = operator === 'NOT IN'
        if (Array.isArray(value)) {
          if (value.length === 0) {
            if (!not && explicitEmpty) return 'FALSE'
            return null
          }
          const { values: cleaned, includeNull } = splitInFilterValues(value)
          return buildInPredicate(fieldSql, cleaned, includeNull, not, { includeNullsOnNot: true })
        }
        if (value !== null && value !== undefined) {
          return buildInPredicateFromValue(fieldSql, value, not, { includeNullsOnNot: true })
        }
        return null
      }
      if (operator === 'IS NULL' || operator === 'IS NOT NULL') {
        return `${fieldSql} ${operator}`
      }
      if (operator === 'BETWEEN') {
        if (templateMode) {
          if (!(Number.isFinite(filterIndex) && (filterIndex as number) >= 0)) {
            throw new Error(`templateMode: deferred wrapper BETWEEN filter index missing for field "${rawFieldName}"`)
          }
          return `${fieldSql} BETWEEN ${deferredFilterBindPart(filterIndex as number, 'lo')} AND ${deferredFilterBindPart(filterIndex as number, 'hi')}`
        }
        if (Array.isArray(value) && value.length === 2) {
          return buildBetweenPredicate(fieldSql, value[0], value[1], filter, rawFieldName)
        }
        return null
      }
      if (operator === 'LIKE' || operator === 'NOT LIKE') {
        if (templateMode) {
          if (!(Number.isFinite(filterIndex) && (filterIndex as number) >= 0)) {
            throw new Error(`templateMode: deferred wrapper LIKE filter index missing for field "${rawFieldName}"`)
          }
          return `${fieldSql} ${operator} ${deferredFilterBindScalar(filterIndex as number)}`
        }
        return `${fieldSql} ${operator} ${escapeValue(value)}`
      }
      if (operator === '!=' || operator === '<>') {
        if (templateMode) {
          if (!(Number.isFinite(filterIndex) && (filterIndex as number) >= 0)) {
            throw new Error(`templateMode: deferred wrapper != filter index missing for field "${rawFieldName}"`)
          }
          return `(${fieldSql} IS NULL OR ${fieldSql} ${operator} ${deferredFilterBindScalar(filterIndex as number)})`
        }
        return `(${fieldSql} IS NULL OR ${fieldSql} ${operator} ${escapeValue(value)})`
      }
      if (templateMode) {
        if (!(Number.isFinite(filterIndex) && (filterIndex as number) >= 0)) {
          throw new Error(`templateMode: deferred wrapper filter index missing for field "${rawFieldName}"`)
        }
        return `${fieldSql} ${operator} ${deferredFilterBindScalar(filterIndex as number)}`
      }
      return `${fieldSql} ${operator} ${escapeValue(value)}`
    }
    const shouldDeferSharedFilterToWrapper = (args: {
      filter: any
      rawFieldName: string
      fieldExprRaw: string
      filterType: string
      isMeasureFilter: boolean
      operator: string
    }): boolean => {
      const { filter, rawFieldName, fieldExprRaw, filterType, isMeasureFilter, operator } = args
      if (!rawFieldName || !operator) return false
      if (filterType === 'context' || Boolean((filter as any)?.context)) return false
      if (isMeasureFilter) return false
      if (operator === 'TOP_N' || operator === 'RELATIVE_DATE') return false
      if (looksBooleanLikeExpr(fieldExprRaw)) {
        // Boolean-like expressions often encode row-level include/exclude logic
        // (for example comparison CASE expressions). Deferring those to the wrapper
        // can change semantics and poison persisted window-input nodes.
        return false
      }
      const calcFormula = lookupCalculatedFormula(rawFieldName)
      if (calcFormula) {
        const looksDateLikeCalc =
          /\bdate_trunc\s*\(/i.test(String(fieldExprRaw || '')) ||
          /\bto_timestamp\s*\(/i.test(String(fieldExprRaw || '')) ||
          /try_cast\s*\([\s\S]*\bas\s+(?:timestamp|date)\b/i.test(String(fieldExprRaw || '')) ||
          /\breportenddate\b/i.test(String(fieldExprRaw || '')) ||
          /\b(?:date|time)\b/i.test(rawFieldName)
        // Non-date calculated dimensions like East/West must stay in the shared
        // base so their authored row-level CASE/IIF logic is applied before the
        // grouped/window wrapper stages. Date-bucket calculations are the
        // exception: once grouped, wrapper projection can safely filter on the
        // projected alias without reopening the expensive source expression.
        return looksDateLikeCalc
      }
      const keyBase = normKey(rawFieldName) || rawFieldName.toLowerCase()
      if (!keyBase || !visibleGroupDimKeys.has(keyBase)) return false
      if (!['IN', 'NOT IN', '=', '!=', '<>', 'LIKE', 'NOT LIKE'].includes(operator)) return false
      const rawValue = (filter as any)?.value
      if (operator === 'IN' || operator === 'NOT IN') {
        const values = Array.isArray(rawValue)
          ? rawValue
          : (rawValue === null || typeof rawValue === 'undefined')
            ? []
            : [rawValue]
        if (values.some((v) => v === null || typeof v === 'undefined')) return false
      }
      // Visible grouped dimensions are already materialized into the grouped base
      // under their field alias, including calculated date buckets. Keep their
      // interactive filters on the wrapper projection so we don't re-run raw
      // source expressions (for example reportenddate-based CASE trees) inside a
      // grouped SELECT where those source columns may no longer be valid.
      return true
    }
    const sharedWhereConditions: string[] = []
    for (const f of filters) {
      if (!f) continue
      if (isMeasureNames(f.field_name)) {
        pushFilterLoweringDebug(f, { route: 'skip', reason: 'measure_names' })
        continue
      }
      const rawFieldName = String(f.field_name || '').trim()
      const meta = findColumnMeta(rawFieldName)
      const operator = String(f.operator || '').trim().toUpperCase()
      const value = f.value
      if (shouldSkipSqlFilter(f)) {
        pushFilterLoweringDebug(f, { route: 'skip', reason: 'hide_display_filter' })
        continue
      }
      if (!operator) {
        pushFilterLoweringDebug(f, { route: 'skip', reason: 'missing_operator' })
        continue
      }
      if (operator === 'ANY_TUPLE' || operator === 'NOT ANY_TUPLE') {
        const pred = buildTupleFilterPredicate({
          tuples: value,
          not: operator === 'NOT ANY_TUPLE',
          resolveFieldSql: (fieldName: string) => {
            const tupleFieldName = String(fieldName || '').trim()
            if (!tupleFieldName) return null
            const tupleKeyBase = normKey(tupleFieldName) || tupleFieldName.toLowerCase()
            let tupleFieldExprRaw: string | null = null
            if (dimExprByKey && dimExprByKey.size > 0) {
              tupleFieldExprRaw = dimExprByKey.get(tupleKeyBase) || null
            }
            if (!tupleFieldExprRaw) {
              tupleFieldExprRaw =
                resolvedWeeklyTimePeriodExprForField?.(tupleFieldName) ||
                fieldExprFor(tupleFieldName, baseRef)
            }
            if (isNullishExpr(tupleFieldExprRaw)) return null
            return `(${tupleFieldExprRaw})`
          },
        })
        if (pred) {
          sharedWhereConditions.push(pred)
          pushFilterLoweringDebug(f, {
            route: 'shared_where',
            reason: 'tuple',
            predicatePreview: pred.slice(0, 500),
          })
        } else {
          pushFilterLoweringDebug(f, { route: 'skip', reason: 'tuple_no_predicate' })
        }
        continue
      }

      // Skip measure filters here; we apply them per-measure after we materialize the base.
      const filterType = String((f as any)?.filter_type ?? '').trim().toLowerCase()
      const isMeasureFilter =
        filterType === 'measure' ||
        Boolean((f as any)?.is_measure) ||
        String((meta as any)?.role ?? '').trim().toLowerCase() === 'measure'
      if (isMeasureFilter) {
        pushFilterLoweringDebug(f, { route: 'skip', reason: 'measure_filter_applied_later' })
        continue
      }

      // Prefer the dimension expression with time grain applied (from dimExprByKey)
      const keyBase = normKey(rawFieldName) || rawFieldName.toLowerCase()
      let fieldExprRaw: string | null = null
      const filterTimeGrain = String((f as any)?.time_grain ?? (f as any)?.timeGrain ?? (f as any)?.date_grain ?? '').trim().toLowerCase()
      const filterIsDiscrete = (f as any)?.is_discrete
      if (dimExprByKey && dimExprByKey.size > 0) {
        if (filterTimeGrain) {
          const filterDiscreteKey = (filterIsDiscrete === true) ? 'disc' : (filterIsDiscrete === false ? 'cont' : 'auto')
          fieldExprRaw =
            dimExprByKey.get(`${keyBase}::${filterTimeGrain}::${filterDiscreteKey}`) ||
            dimExprByKey.get(`${keyBase}::${filterTimeGrain}`) ||
            null
        }
        if (!fieldExprRaw) fieldExprRaw = dimExprByKey.get(keyBase) || null
      }
      if (!fieldExprRaw) {
        fieldExprRaw =
          resolvedWeeklyTimePeriodExprForField?.(rawFieldName) ||
          fieldExprFor(rawFieldName, baseRef)
      }
      if (filterTimeGrain && fieldExprRaw && !isNullishExpr(fieldExprRaw) && !/\bdate_trunc\s*\(/i.test(String(fieldExprRaw))) {
        fieldExprRaw = applyTimeGrain(fieldExprRaw, filterTimeGrain, typeof filterIsDiscrete === 'boolean' ? filterIsDiscrete : false)
      }
      if (isNullishExpr(fieldExprRaw)) {
        pushFilterLoweringDebug(f, {
          route: 'skip',
          reason: 'field_expr_nullish_or_unresolved',
          keyBase,
          fieldExprPreview: String(fieldExprRaw ?? '').slice(0, 500),
        })
        continue
      }
      if (shouldDeferSharedFilterToWrapper({
        filter: f,
        rawFieldName,
        fieldExprRaw,
        filterType,
        isMeasureFilter,
        operator,
      })) {
        deferredSharedFilters.push(f)
        pushFilterLoweringDebug(f, {
          route: 'deferred_wrapper',
          reason: 'visible_grouped_dimension',
          keyBase,
          fieldExprPreview: String(fieldExprRaw ?? '').slice(0, 500),
        })
        continue
      }
      const fieldSql = `(${fieldExprRaw})`
      const fi = filterIndexByRef.get(f)
      const templateFilterIndex =
        Number.isFinite(fi) && (fi as number) >= 0
          ? (fi as number)
          : null

      if (operator === 'RELATIVE_DATE') {
        if (templateMode) {
          if (templateFilterIndex === null) {
            throw new Error(`templateMode: RELATIVE_DATE is missing filter index for field "${rawFieldName}"`)
          }
          sharedWhereConditions.push(buildRelativeDatePredicateTemplate(fieldSql, templateFilterIndex))
        } else {
          const pred = buildRelativeDatePredicate(fieldSql, value)
          if (pred) sharedWhereConditions.push(pred)
        }
        continue
      }
      if (operator === 'TOP_N') {
        const dimId = resolveColOrRawWhenNoSchema(rawFieldName)
        if (!dimId) continue
        const fi = filterIndexByRef.get(f)
        const pred = buildTopNPredicate(
          fieldSql,
          dimId,
          rawFieldName,
          value,
          contextFilters as any,
          templateMode ? { filterIndex: (fi ?? -1), filterIndexByRef } : null,
        )
        if (pred) sharedWhereConditions.push(pred)
        continue
      }

      if (operator === 'IN' || operator === 'NOT IN') {
        const not = operator === 'NOT IN'
        if (Array.isArray(value)) {
          if (value.length === 0) {
            if (!not && isExplicitEmptyFilter(f)) {
              sharedWhereConditions.push('FALSE')
              continue
            }
            if (!templateMode) continue
          }
          const { values: cleaned, includeNull } = splitInFilterValues(value)
          const isExtractMonth = /EXTRACT\s*\(\s*month\s+FROM/i.test(fieldExprRaw)
          let processedValues: Array<string | number> = []
          if (isExtractMonth) {
            const monthSet = new Set<number>()
            for (const v of cleaned) {
              let monthNum: number | null = null
              if (typeof v === 'string' && /^\d+$/.test(v)) {
                const num = Number(v)
                if (num > 1000000) {
                  const d = new Date(num)
                  if (!isNaN(d.getTime()) && d.getUTCFullYear() >= 1970 && d.getUTCFullYear() <= 2100) {
                    monthNum = d.getUTCMonth() + 1
                  }
                } else if (num >= 1 && num <= 12) {
                  monthNum = num
                }
              } else if (typeof v === 'number') {
                if (v > 1000000) {
                  const d = new Date(v)
                  if (!isNaN(d.getTime()) && d.getUTCFullYear() >= 1970 && d.getUTCFullYear() <= 2100) {
                    monthNum = d.getUTCMonth() + 1
                  }
                } else if (v >= 1 && v <= 12) {
                  monthNum = v
                }
              }
              if (monthNum !== null && monthNum >= 1 && monthNum <= 12) {
                monthSet.add(monthNum)
              }
            }
            processedValues = Array.from(monthSet).sort((a, b) => a - b)
          } else {
            processedValues = cleaned
          }
          // #region agent log
          try {
            const looksSlashDateValues =
              processedValues.length > 0 &&
              processedValues.every((v) =>
                typeof v === 'string' &&
                /^\d{1,2}\/\d{1,2}\/\d{4}(?:\s+\d{1,2}:\d{2}:\d{2}\s*(?:AM|PM))?$/i.test(String(v).trim()),
              )
            const looksDateExpr =
              /\bdate_trunc\s*\(/i.test(String(fieldExprRaw || '')) ||
              /\bto_timestamp\s*\(/i.test(String(fieldExprRaw || '')) ||
              /try_cast\s*\([\s\S]*\bas\s+(?:timestamp|date)\b/i.test(String(fieldExprRaw || '')) ||
              /\breportenddate\b/i.test(String(fieldExprRaw || ''))
            if (looksSlashDateValues || (looksDateExpr && /time period (begin|end) dates/i.test(rawFieldName))) {
              postOptionalLocalDebugLog('http://127.0.0.1:7788/ingest/7b95b565-26ed-4f27-b984-d1a6c1d0ea54',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'0c8a1b'},body:JSON.stringify({sessionId:'0c8a1b',runId:'pre-fix',hypothesisId:'H3',location:'worksheetQueryBuilder.ts:14210',message:'shared filter date predicate input',data:{rawFieldName,operator,processedValues,includeNull,templateMode,fieldExprPreview:String(fieldExprRaw || '').slice(0, 240)},timestamp:Date.now()})})
            }
          } catch {}
          // #endregion
          const pred = templateMode && !isExtractMonth
            ? (() => {
                if (templateFilterIndex === null) {
                  throw new Error(`templateMode: IN filter index missing for field "${rawFieldName}"`)
                }
                return buildDeferredWrapperInPredicateTemplate(fieldSql, not, templateFilterIndex, { includeNullsOnNot: true })
              })()
            : buildInPredicate(fieldSql, processedValues, includeNull, not, { includeNullsOnNot: true })
          if (pred) {
            sharedWhereConditions.push(pred)
            pushFilterLoweringDebug(f, {
              route: 'shared_where',
              reason: 'in_predicate',
              keyBase,
              templateFilterIndex,
              cleanedValueCount: processedValues.length,
              includeNull,
              fieldExprPreview: String(fieldExprRaw ?? '').slice(0, 500),
              predicatePreview: pred.slice(0, 500),
            })
          } else {
            pushFilterLoweringDebug(f, {
              route: 'skip',
              reason: 'in_no_predicate',
              keyBase,
              templateFilterIndex,
              cleanedValueCount: processedValues.length,
              includeNull,
              fieldExprPreview: String(fieldExprRaw ?? '').slice(0, 500),
            })
          }
        } else if (value !== null && value !== undefined) {
          const pred = templateMode
            ? (() => {
                if (templateFilterIndex === null) {
                  throw new Error(`templateMode: IN filter index missing for field "${rawFieldName}"`)
                }
                return buildDeferredWrapperInPredicateTemplate(fieldSql, not, templateFilterIndex, { includeNullsOnNot: true })
            })()
            : buildInPredicateFromValue(fieldSql, value, not, { includeNullsOnNot: true })
          if (pred) {
            sharedWhereConditions.push(pred)
            pushFilterLoweringDebug(f, {
              route: 'shared_where',
              reason: 'in_scalar_predicate',
              keyBase,
              templateFilterIndex,
              fieldExprPreview: String(fieldExprRaw ?? '').slice(0, 500),
              predicatePreview: pred.slice(0, 500),
            })
          }
        }
      } else if (operator === 'IS NULL' || operator === 'IS NOT NULL') {
        sharedWhereConditions.push(`${fieldSql} ${operator}`)
        pushFilterLoweringDebug(f, {
          route: 'shared_where',
          reason: 'null_predicate',
          keyBase,
          fieldExprPreview: String(fieldExprRaw ?? '').slice(0, 500),
          predicatePreview: `${fieldSql} ${operator}`.slice(0, 500),
        })
      } else if (operator === 'BETWEEN') {
        if (Array.isArray(value) && value.length === 2) {
          const pred =
            templateMode
              ? (() => {
                  if (templateFilterIndex === null) {
                    throw new Error(`templateMode: BETWEEN filter index missing for field "${rawFieldName}"`)
                  }
                  return `${fieldSql} BETWEEN ${deferredFilterBindPart(templateFilterIndex, 'lo')} AND ${deferredFilterBindPart(templateFilterIndex, 'hi')}`
                })()
              : buildBetweenPredicate(fieldSql, value[0], value[1], f, rawFieldName)
          sharedWhereConditions.push(pred)
          pushFilterLoweringDebug(f, {
            route: 'shared_where',
            reason: 'between_predicate',
            keyBase,
            templateFilterIndex,
            fieldExprPreview: String(fieldExprRaw ?? '').slice(0, 500),
            predicatePreview: pred.slice(0, 500),
          })
        }
      } else if (operator === 'LIKE' || operator === 'NOT LIKE') {
        const pred =
          templateMode
            ? (() => {
                if (templateFilterIndex === null) {
                  throw new Error(`templateMode: LIKE filter index missing for field "${rawFieldName}"`)
                }
                return `${fieldSql} ${operator} ${deferredFilterBindScalar(templateFilterIndex)}`
              })()
            : `${fieldSql} ${operator} ${escapeValue(value)}`
        sharedWhereConditions.push(pred)
        pushFilterLoweringDebug(f, {
          route: 'shared_where',
          reason: 'like_predicate',
          keyBase,
          templateFilterIndex,
          fieldExprPreview: String(fieldExprRaw ?? '').slice(0, 500),
          predicatePreview: pred.slice(0, 500),
        })
      } else if (operator === '!=' || operator === '<>') {
        const pred =
          templateMode
            ? (() => {
                if (templateFilterIndex === null) {
                  throw new Error(`templateMode: != filter index missing for field "${rawFieldName}"`)
                }
                return `(${fieldSql} IS NULL OR ${fieldSql} ${operator} ${deferredFilterBindScalar(templateFilterIndex)})`
              })()
            : `(${fieldSql} IS NULL OR ${fieldSql} ${operator} ${escapeValue(value)})`
        sharedWhereConditions.push(pred)
        pushFilterLoweringDebug(f, {
          route: 'shared_where',
          reason: 'not_equal_predicate',
          keyBase,
          templateFilterIndex,
          fieldExprPreview: String(fieldExprRaw ?? '').slice(0, 500),
          predicatePreview: pred.slice(0, 500),
        })
      } else {
        const pred =
          templateMode
            ? (() => {
                if (templateFilterIndex === null) {
                  throw new Error(`templateMode: filter index missing for field "${rawFieldName}"`)
                }
                return `${fieldSql} ${operator} ${deferredFilterBindScalar(templateFilterIndex)}`
              })()
            : `${fieldSql} ${operator} ${escapeValue(value)}`
        sharedWhereConditions.push(pred)
        pushFilterLoweringDebug(f, {
          route: 'shared_where',
          reason: 'generic_predicate',
          keyBase,
          templateFilterIndex,
          fieldExprPreview: String(fieldExprRaw ?? '').slice(0, 500),
          predicatePreview: pred.slice(0, 500),
        })
      }
    }
    if (debugFilterLowering && (filtersArr.length > 0 || filterLoweringDebugRows.length > 0)) {
      try {
        console.log(`[FG-FILTER-LOWERING] ${JSON.stringify({
          worksheetName: worksheetNameDebug,
          templateMode,
          filtersCount: filtersArr.length,
          sharedWhereCount: sharedWhereConditions.length,
          deferredSharedCount: deferredSharedFilters.length,
          rows: filterLoweringDebugRows,
        })}`)
      } catch { }
    }

    const sharedHavingConditions: string[] = []
    if (emitTotals && totalsOnly) {
      const pred = totalsGroupingExprs
        .map((e) => String(e || '').trim())
        .filter(Boolean)
        .map((e) => `${e} IS NULL`)
        .join(' OR ')
      if (pred) sharedHavingConditions.push(`(${pred})`)
    }

    _markPhase('adv_filters+setup2')
    const measureFilterPredsByKey = (() => {
      const out = new Map<string, Array<{ operator: string; value: any; filter: any }>>()
      for (const f of filters) {
        if (!f) continue
        if (isMeasureNames(f.field_name)) continue
        const rawFieldName = String(f.field_name || '').trim()
        const metaF = findColumnMeta(rawFieldName)
        const operator = String(f.operator || '').trim().toUpperCase()
        const value = f.value
        if (!operator) continue
        const filterType = String((f as any)?.filter_type ?? '').trim().toLowerCase()
        const isMeasureFilter =
          filterType === 'measure' ||
          Boolean((f as any)?.is_measure) ||
          String((metaF as any)?.role ?? '').trim().toLowerCase() === 'measure'
        if (!isMeasureFilter) continue

        // Best-effort: only apply per-measure predicate when the filter targets that measure.
        const raw = String(f.field_name || '').trim()
        const mTok = raw.match(/^([A-Za-z_][A-Za-z0-9_]*)\s*\(\s*(.*?)\s*\)\s*$/)
        const inner = mTok ? stripBracketsAroundFieldName(String(mTok[2] ?? '').trim()) : raw
        const k = normalizeTableauFieldKey(inner) || inner.toLowerCase()
        if (!k) continue
        const arr = out.get(k)
        const entry = { operator, value, filter: f }
        if (arr) arr.push(entry)
        else out.set(k, [entry])
      }
      return out
    })()
    const mnvMeasures: MnvMeasureOut[] = []
    let mnvIdx = 0
    for (const m of measures) {
      let measureName = resolveMeasureLabelCached(m as any)
      const baseFieldId = String((m as any).ci?.base_field_id ?? (m as any).ci?.baseFieldId ?? '').trim()
      const baseClean = baseFieldId ? (cleanBracketedFieldRef0(baseFieldId) || baseFieldId) : ''
      const meta =
        _fdColsByName.get(String((m as any).columnId || '').trim()) ||
        (baseClean ? _fdColsByName.get(baseClean) : null) ||
        null
      const metaCaption = meta?.caption != null ? String(meta.caption).trim() : ''
      if (metaCaption) {
        const lower = String(measureName ?? '').trim().toLowerCase()
        const looksGeneric =
          !lower ||
          lower === 'null' ||
          lower.startsWith('calculation') ||
          looksTableauToken(String(measureName ?? ''))
        if (looksGeneric) measureName = metaCaption
      }
      const calcFormula = String((m as any).ci?.calculation?.formula ?? meta?.calculation?.formula ?? '').trim()
      if (calcFormula) {
        const lower = String(measureName ?? '').trim().toLowerCase()
        const looksGeneric =
          !lower ||
          lower === 'null' ||
          lower.startsWith('calculation') ||
          looksTableauToken(String(measureName ?? ''))
        if (looksGeneric) {
          const key = normalizeCalcFormula(calcFormula)
          const cap = calcCaptionByFormula.get(key)
          if (cap) measureName = cap
        }
      }
      const measureValueExpr = buildMeasureValueExpr(m)
      if (!measureValueExpr) continue

      const labelLower = String(measureName ?? '').trim().toLowerCase()
      const labelLooksGeneric =
        !labelLower ||
        labelLower === 'null' ||
        labelLower.startsWith('calculation') ||
        looksTableauToken(String(measureName ?? ''))
      if (labelLooksGeneric) {
        const compiledCap = getCalcCaptionByCompiledSql().get(normalizeSqlExpr(measureValueExpr))
        if (compiledCap) measureName = compiledCap
      }
      const likertKey = extractLikertKeyFromSql(measureValueExpr)
      const likertCap = likertKey ? calcCaptionByLikertKey.get(likertKey) : null
      if (likertCap) measureName = likertCap

      const label = String(measureName ?? '').trim()
      if (!label) continue
      const key = normalizeTableauFieldKey(label) || label.toLowerCase()
      const valueAlias = `__fg_mnv_val_${mnvIdx++}`
      const valRef = `(${baseOutRef}.${quoteIdent(valueAlias)})`
      const filterPreds: string[] = []
      const filterConfigs = (key && measureFilterPredsByKey.has(key)) ? measureFilterPredsByKey.get(key)! : null
      if (filterConfigs && filterConfigs.length) {
        for (const cfg of filterConfigs) {
          const pred = buildMeasureFilterPred(valRef, cfg.operator, cfg.value, cfg.filter)
          if (pred) filterPreds.push(pred)
        }
      }

      mnvMeasures.push({
        label,
        key,
        valueExpr: measureValueExpr,
        valueAlias,
        filterPreds,
      })
    }

    _markPhase('adv_measure_compile')
    if (!mnvMeasures.length) return null

    const orderExtraAliases = new Set<string>()
    const orderByCache = (() => {
      if (!trustSemanticPrecomputeAliases) return null
      const wbAny: any = workbookDerived as any
      if (!wbAny) return null
      if (!wbAny.__fg_mnvOrderCache) wbAny.__fg_mnvOrderCache = new Map()
      return wbAny.__fg_mnvOrderCache as Map<string, { orderBy: string; orderExtraAliases: string[]; ts: number }>
    })()
    const orderKey = (() => {
      if (!orderByCache) return ''
      try {
        const dimEntries = groupDims.map((d) => {
          const name = String(d?.field_name ?? '').trim()
          const sortObj = (d as any)?.sort ?? (d as any)?.sort_config ?? (d as any)?.sortConfig ?? (d as any)?.sort_direction ?? (d as any)?.sortDirection ?? null
          return [name, sortObj]
        })
        const manualEntries = Array.from(manualOrderByFieldKey.entries())
          .sort((a, b) => a[0].localeCompare(b[0]))
          .map(([k, v]) => [k, v])
        const measureLabels = mnvMeasures.map((m) => m.label)
        const precomputeSig = precomputedExtrasMap
          ? Array.from(precomputedExtrasMap.entries()).map(([k, v]) => `${k}\0${v}`).join('\n')
          : ''
        const payload = safeStableStringify({
          orderRewriteVersion: 2,
          dims: dimEntries,
          manual: manualEntries,
          measures: measureLabels,
          precomputeSig,
        })
        return String(hashString(payload || ''))
      } catch {
        return ''
      }
    })()
    let orderBy = ''
    if (orderByCache && orderKey) {
      const cached = orderByCache.get(orderKey)
      if (cached && cached.orderBy) {
        orderBy = cached.orderBy
        for (const alias of cached.orderExtraAliases || []) {
          const trimmed = String(alias ?? '').trim()
          if (trimmed) orderExtraAliases.add(trimmed)
        }
      }
    }

    const sortKindForField = (fieldName: string): 'date' | 'number' | 'string' => {
      const k = norm(fieldName)
      const meta = findColumnMeta(fieldName)
      const dt = String((meta as any)?.datatype ?? (meta as any)?.data_type ?? (meta as any)?.attrs?.datatype ?? '').trim().toLowerCase()
      const rType = String((meta as any)?.['r#type'] ?? (meta as any)?.type ?? '').trim().toLowerCase()

      const isDateLike =
        k.includes('date') ||
        k.includes('time') ||
        k === 'reportenddate' ||
        k.includes('end date') ||
        k.includes('end dates') ||
        dt.includes('date') ||
        dt.includes('time') ||
        rType.includes('date') ||
        rType.includes('temporal')
      if (isDateLike) return 'date'

      const isNumericLike =
        dt.includes('int') ||
        dt.includes('decimal') ||
        dt.includes('numeric') ||
        dt.includes('double') ||
        dt.includes('float') ||
        dt.includes('real') ||
        rType.includes('quantitative') ||
        rType === 'ordinal'
      return isNumericLike ? 'number' : 'string'
    }
    if (!orderBy) {
      const orderTerms: string[] = []
      for (let i = 0; i < dimSelects.length; i++) {
        const fn = String(groupDims[i]?.field_name ?? '')
        const groupedOrderExpr =
          (emitTotals || groupByExprs.length > 0)
            ? String(tableCalcDimExprs[i] ?? dimExprs[i] ?? '').trim()
            : ''
        // Keep Tableau-like rollup totals ("All") stable: always sort sentinel totals LAST.
        if (fn) {
          const col = quoteIdent(fn)
          pushOrderTerm(orderTerms, `CASE WHEN LOWER(CAST(${col} AS VARCHAR)) IN ('all','(all)') THEN 1 ELSE 0 END`, 'ASC', 'NULLS LAST', orderExtraAliases)
        }
        const col = quoteIdent(fn)
        const orderCol = groupedOrderExpr || col

        // Manual/semantic ordering override (if present).
        const manual = manualOrderForField(fn)
        if (manual && manual.length) {
          pushOrderTerm(orderTerms, manualOrderCaseExpr(orderCol, manual), 'ASC', 'NULLS LAST', orderExtraAliases)
          // Tie-break unknown values deterministically.
          pushOrderTerm(orderTerms, `CAST(${orderCol} AS VARCHAR)`, 'ASC', 'NULLS LAST', orderExtraAliases)
          continue
        }

        if (groupedOrderExpr) {
          pushOrderTerm(orderTerms, orderCol, dirForFieldName(fn), 'NULLS LAST', orderExtraAliases)
          continue
        }

        const dir = dirForFieldName(fn)
        const kind = sortKindForField(fn)
        if (kind === 'date') {
          // Preserve chronological ordering even when the SELECT emits strings (due to "All" sentinels).
          // Fallback to string ordering for non-castable values.
          pushOrderTerm(orderTerms, `TRY_CAST(${orderCol} AS TIMESTAMP)`, dir, 'NULLS LAST', orderExtraAliases)
          pushOrderTerm(orderTerms, `CAST(${orderCol} AS VARCHAR)`, dir, 'NULLS LAST', orderExtraAliases)
          continue
        }
        if (kind === 'number') {
          pushOrderTerm(orderTerms, `TRY_CAST(${orderCol} AS DOUBLE)`, dir, 'NULLS LAST', orderExtraAliases)
          pushOrderTerm(orderTerms, `CAST(${orderCol} AS VARCHAR)`, dir, 'NULLS LAST', orderExtraAliases)
          continue
        }
        orderTerms.push(`${i + 1} ${dir} NULLS LAST`)
      }
      const measureOrderCase = (() => {
        const seen = new Set<string>()
        const pairs: Array<{ name: string; idx: number }> = []
        for (const m of mnvMeasures) {
          const nm = m.label
          const key = norm(nm)
          if (!key) continue
          if (seen.has(key)) continue
          seen.add(key)
          pairs.push({ name: nm, idx: pairs.length })
        }
        if (pairs.length === 0) return null
        const whens = pairs.map((p) => `WHEN ${escapeValue(p.name)} THEN ${p.idx}`).join(' ')
        return `CASE ${quoteIdent('Measure Names')} ${whens} ELSE 999999 END`
      })()
      const measureNamePos = dimSelects.length + 1 // "Measure Names" follows dims in SELECT
      if (measureOrderCase) {
        pushOrderTerm(orderTerms, measureOrderCase, 'ASC', 'NULLS LAST', orderExtraAliases)
      } else {
        orderTerms.push(`${measureNamePos} ASC NULLS LAST`)
      }
      const finalizedOrderTerms = finalizeOrderTerms(orderTerms)
      if (typeof window !== 'undefined') {
        try {
          const qp = new URLSearchParams(window.location.search)
          const debugExecPlan = String(qp.get('fg_debug_exec_plan') ?? '').trim().toLowerCase()
          if (debugExecPlan && !['0', 'false', 'off', 'no'].includes(debugExecPlan)) {
            ;(window as any).__FG_ORDER_TERM_DEBUG__ = {
              worksheetName: options.worksheetName ?? null,
              rawOrderTerms: orderTerms.slice(0, 32),
              finalizedOrderTerms: finalizedOrderTerms.slice(0, 32),
              removedOrderTerms: orderTerms.filter((term) => !finalizedOrderTerms.includes(term)).slice(0, 32),
              ts: Date.now(),
            }
          }
        } catch { }
      }
      orderBy = finalizedOrderTerms.join(', ')
      if (orderByCache && orderKey) {
        orderByCache.set(orderKey, { orderBy, orderExtraAliases: Array.from(orderExtraAliases), ts: Date.now() })
        if (orderByCache.size > 50) {
          const entries = Array.from(orderByCache.entries()).sort((a, b) => (a[1]?.ts ?? 0) - (b[1]?.ts ?? 0))
          const excess = entries.length - 50
          for (let i = 0; i < excess; i++) orderByCache.delete(entries[i]![0])
        }
      }
    }
    _markPhase('adv_measure_compile.order')

    const stagedProjectionSheetAllowed = (() => {
      if (artifactExportMode) return true
      const raw = String(mnvOptimization.stagedProjectionSheet || '').trim()
      if (!raw) return true
      const current = String(worksheetNameDebug || worksheetName || '').trim().toLowerCase()
      if (!current) return false
      const wanted = raw
        .split(',')
        .map((v) => String(v || '').trim().toLowerCase())
        .filter(Boolean)
      if (!wanted.length) return true
      return wanted.some((v) => current === v || current.includes(v))
    })()
    const stagedProjectionMinMeasures = Math.max(1, mnvOptimization.chunkMinMeasures)
    const estimatedUnstagedProjectionWidth =
      selectDimsForBase.length +
      mnvMeasures.length +
      tooltipExtraSelects.length +
      orderExtraAliases.size
    const estimatedUnstagedExprChars = mnvMeasures.reduce(
      (acc, m) => acc + String(m?.valueExpr || '').length,
      0,
    )
    // "Monthly Comparison"-style plans embed a LOOKUP (LEAD/LAG/FIRST_VALUE)
    // whose `OVER (PARTITION BY ...)` references a *compiled* calculated
    // dimension formula (e.g. `("t"."haircutcount" > 0)`) instead of a plain
    // column alias. When present, we lift an `agg_base`/`agg_base_src` pair
    // into the exec plan so the derived formula is grouped exactly once per
    // plan run instead of re-evaluating across chunks. We do NOT force
    // staged projection here: the CTE-based materialized SQL must stay
    // intact (with `t.`-qualified aliases the downstream LOOKUP normalizer
    // depends on), so the split happens as a plan-only wrapper.
    const hasCompiledCalcDimPartitionSignal = mnvMeasures.some((m) =>
      /\bOVER\s*\(\s*PARTITION\s+BY\s+\(/i.test(String(m?.valueExpr ?? '')),
    )
    const autoStagedProjection =
      mnvOptimization.enabled &&
      stagedProjectionSheetAllowed &&
      mnvMeasures.length >= stagedProjectionMinMeasures &&
      (
        estimatedUnstagedProjectionWidth >= stagedProjectionMinMeasures + 4 ||
        estimatedUnstagedExprChars >= 2500
      )
    const useStagedMnvProjection =
      mnvOptimization.enabled &&
      stagedProjectionSheetAllowed &&
      mnvMeasures.length >= stagedProjectionMinMeasures &&
      (mnvOptimization.stagedProjection || autoStagedProjection)

    const needsAggregateSafeDimProjectionWithoutGroupBy =
      !emitTotals &&
      groupByExprs.length <= 0 &&
      mnvMeasures.some((m) => /\b(?:SUM|COUNT|AVG|MIN|MAX|ANY_VALUE)\s*\(/i.test(String(m?.valueExpr ?? '')))
    const dedupeGroupingExprs = (exprs: string[]): string[] => {
      const out: string[] = []
      const seen = new Set<string>()
      for (const raw of exprs) {
        const trimmed = String(raw || '').trim()
        if (!trimmed) continue
        const key = normalizeSqlExpr(trimmed)
        if (!key || seen.has(key)) continue
        seen.add(key)
        out.push(trimmed)
      }
      return out
    }
    const splitTopLevelSqlList = (src: string): string[] => {
      const out: string[] = []
      let buf = ''
      let depth = 0
      let inStr = false
      let inIdent = false
      for (let i = 0; i < src.length; i += 1) {
        const ch = src[i]
        if (inStr) {
          buf += ch
          if (ch === "'" && src[i + 1] === "'") {
            buf += src[i + 1]
            i += 1
            continue
          }
          if (ch === "'") inStr = false
          continue
        }
        if (inIdent) {
          buf += ch
          if (ch === '"' && src[i + 1] === '"') {
            buf += src[i + 1]
            i += 1
            continue
          }
          if (ch === '"') inIdent = false
          continue
        }
        if (ch === "'") {
          inStr = true
          buf += ch
          continue
        }
        if (ch === '"') {
          inIdent = true
          buf += ch
          continue
        }
        if (ch === '(') depth += 1
        else if (ch === ')' && depth > 0) depth -= 1
        if (ch === ',' && depth === 0) {
          const item = buf.trim()
          if (item) out.push(item)
          buf = ''
          continue
        }
        buf += ch
      }
      const tail = buf.trim()
      if (tail) out.push(tail)
      return out
    }
    const findTopLevelKeywordSql = (src: string, keyword: string): number => {
      const s = String(src ?? '')
      const needle = String(keyword ?? '').trim().toUpperCase()
      const needleParts = needle.split(/\s+/).filter(Boolean)
      if (!needle || needleParts.length <= 0) return -1
      let depth = 0
      let inStr = false
      let inIdent = false
      outer: for (let i = 0; i < s.length; i += 1) {
        const ch = s[i]
        if (inStr) {
          if (ch === "'" && s[i + 1] === "'") {
            i += 1
            continue
          }
          if (ch === "'") inStr = false
          continue
        }
        if (inIdent) {
          if (ch === '"' && s[i + 1] === '"') {
            i += 1
            continue
          }
          if (ch === '"') inIdent = false
          continue
        }
        if (ch === "'") {
          inStr = true
          continue
        }
        if (ch === '"') {
          inIdent = true
          continue
        }
        if (ch === '(') {
          depth += 1
          continue
        }
        if (ch === ')') {
          if (depth > 0) depth -= 1
          continue
        }
        if (depth > 0) continue
        if (i > 0 && /[A-Za-z0-9_]/.test(s[i - 1] || '')) continue
        let cursor = i
        for (let partIdx = 0; partIdx < needleParts.length; partIdx += 1) {
          const part = needleParts[partIdx]
          if (s.slice(cursor, cursor + part.length).toUpperCase() !== part) continue outer
          cursor += part.length
          if (partIdx >= needleParts.length - 1) break
          let sawWhitespace = false
          while (cursor < s.length && /\s/.test(s[cursor] || '')) {
            sawWhitespace = true
            cursor += 1
          }
          if (!sawWhitespace) continue outer
        }
        const after = s[cursor]
        if (after && /[A-Za-z0-9_]/.test(after)) continue
        return i
      }
      return -1
    }
    const factorRepeatedTopLevelWindowExprs = (sqlIn: string): string => {
      const sql = String(sqlIn ?? '').trim()
      if (!sql || !/\bOVER\b/i.test(sql)) return sql
      const selectIdx = findTopLevelKeywordSql(sql, 'SELECT')
      const fromIdx = findTopLevelKeywordSql(sql, 'FROM')
      if (selectIdx !== 0 || fromIdx <= selectIdx) return sql
      const selectBody = sql.slice(selectIdx + 6, fromIdx).trim()
      const fromTail = sql.slice(fromIdx).trim()
      if (!selectBody || !fromTail) return sql
      const items = splitTopLevelSqlList(selectBody)
      if (!items.length) return sql
      const sourceAliasRefs = new Set<string>()
      const windowCounts = new Map<string, number>()
      for (const item of items) {
        const aliasMatch = /^(.*)\s+AS\s+("[^"]+"|[A-Za-z_][A-Za-z0-9_]*)\s*$/i.exec(item)
        const expr = String(aliasMatch?.[1] ?? item).trim()
        const refMatches = expr.match(/(?:"[^"]+"|[A-Za-z_][A-Za-z0-9_]*)\s*\.\s*"[^"]+"/g) ?? []
        for (const ref of refMatches) sourceAliasRefs.add(String(ref))
        for (const call of __test_findWindowCalls(expr)) {
          const key = String(call.full ?? '').trim()
          if (!key) continue
          windowCounts.set(key, (windowCounts.get(key) ?? 0) + 1)
        }
      }
      const repeatedWindowExprs = Array.from(windowCounts.entries())
        .filter(([, count]) => count >= 2)
        .map(([expr]) => expr)
      if (sourceAliasRefs.size === 0 || windowCounts.size === 0) return sql
      const sourceAliasKeys = new Set<string>()
      let quotedSourceAlias = ''
      let bareSourceAlias = ''
      for (const ref of sourceAliasRefs) {
        const dotIdx = ref.indexOf('.')
        if (dotIdx <= 0) continue
        const alias = ref.slice(0, dotIdx).trim()
        const aliasKey = alias.replace(/^"|"$/g, '').trim().toLowerCase()
        if (!aliasKey) continue
        sourceAliasKeys.add(aliasKey)
        if (!quotedSourceAlias && /^".*"$/.test(alias)) quotedSourceAlias = alias
        if (!bareSourceAlias && !/^".*"$/.test(alias)) bareSourceAlias = alias
      }
      if (sourceAliasKeys.size !== 1) return sql
      const sourceAlias = quotedSourceAlias || bareSourceAlias || Array.from(sourceAliasKeys)[0]!
      const sourceAliasBare = sourceAlias.replace(/^"|"$/g, '').trim()
      const factoringAllowedDerivedAliases = [
        ...groupDims.map((d) => String(d?.field_name ?? '').trim()).filter(Boolean),
        ...Array.from(orderExtraAliases).map((c) => String(c ?? '').trim()).filter(Boolean),
        'Measure Names',
        'Measure Values',
      ]
      const sourceRefAvailableForFactoring = (exprRaw: string): boolean =>
        validateSqlPhysicalRefs(exprRaw, {
          sourceAliases: [sourceAliasBare],
          allowedDerivedAliases: factoringAllowedDerivedAliases,
        }).ok
      const availableSourceAliasRefs = Array.from(sourceAliasRefs)
        .filter((ref) => sourceRefAvailableForFactoring(ref))
      const replaceSqlAliasQualifier = (src: string, nextAlias: string): string => {
        const bare = sourceAliasBare
        return String(src ?? '')
          .replace(new RegExp(`${escapeRegExp(sourceAlias)}\\s*\\.`, 'g'), `${nextAlias}.`)
          .replace(new RegExp(`\\b${escapeRegExp(bare)}\\s*\\.`, 'g'), `${nextAlias}.`)
      }
      const parseSimplePartitionWindowExpr = (
        exprRaw: string,
      ): { aggFn: string; aggArg: string; partitionCols: string[] } | null => {
        const expr = String(exprRaw ?? '').trim()
        if (!expr || /\bORDER\s+BY\b/i.test(expr) || /\bROWS\b|\bRANGE\b|\bGROUPS\b/i.test(expr)) return null
        const match = expr.match(/^([A-Za-z_][A-Za-z0-9_]*)\s*\(([\s\S]+)\)\s*OVER\s*\(\s*PARTITION\s+BY\s+([\s\S]+)\)\s*$/i)
        if (!match) return null
        const aggFn = String(match[1] ?? '').trim().toUpperCase()
        const aggArg = String(match[2] ?? '').trim()
        const partitionCols = splitTopLevelSqlList(String(match[3] ?? '').trim())
        if (!aggFn || !aggArg || !partitionCols.length) return null
        return { aggFn, aggArg, partitionCols }
      }
      const refactorWindowSqlUntilStable = (sqlRaw: string): string => {
        let nextSql = String(sqlRaw ?? '').trim()
        if (!nextSql || nextSql === sql) return nextSql
        for (let pass = 0; pass < 4; pass += 1) {
          const factoredSql = factorRepeatedTopLevelWindowExprs(nextSql)
          if (!factoredSql || factoredSql === nextSql) break
          nextSql = factoredSql
        }
        return nextSql
      }
      const simpleOnlyWindowExprs = Array.from(windowCounts.keys()).filter((expr) => Boolean(parseSimplePartitionWindowExpr(expr)))
      if (simpleOnlyWindowExprs.length === windowCounts.size && simpleOnlyWindowExprs.length > 0) {
        const baseAlias = quoteIdent('__fg_win_base')
        const corrAlias = quoteIdent('__fg_win_corr')
        const nonWindowItems = items.filter((item) => {
          const aliasMatch = /^(.*)\s+AS\s+("[^"]+"|[A-Za-z_][A-Za-z0-9_]*)\s*$/i.exec(item)
          const expr = String(aliasMatch?.[1] ?? item).trim()
          return __test_findWindowCalls(expr).length === 0
        })
        if (nonWindowItems.length + simpleOnlyWindowExprs.length === items.length && nonWindowItems.length > 0) {
          const refAliasMap = new Map<string, string>()
          const projectedAliases: string[] = []
          const baseOuterSelects: string[] = []
          const cseAlias = quoteIdent('__fg_win_cse')
          const cseOuterSelects: string[] = []
          const cseGroupByExprs: string[] = []
          for (const item of nonWindowItems) {
            const aliasMatch = /^(.*)\s+AS\s+("[^"]+"|[A-Za-z_][A-Za-z0-9_]*)\s*$/i.exec(item)
            if (!aliasMatch) {
              baseOuterSelects.length = 0
              cseOuterSelects.length = 0
              cseGroupByExprs.length = 0
              break
            }
            const expr = String(aliasMatch[1] ?? '').trim()
            const alias = String(aliasMatch[2] ?? '').trim()
            projectedAliases.push(alias)
            baseOuterSelects.push(`${baseAlias}.${alias} AS ${alias}`)
            cseOuterSelects.push(`${cseAlias}.${alias} AS ${alias}`)
            cseGroupByExprs.push(`${cseAlias}.${alias}`)
            for (const ref of availableSourceAliasRefs) {
              if (normalizeSqlExpr(expr) === normalizeSqlExpr(ref)) {
                refAliasMap.set(ref, alias)
              }
            }
          }
          if (baseOuterSelects.length > 0) {
            const replaceRefWithBaseAlias = (src: string, aliasPrefix: string): string => {
                let out = replaceSqlAliasQualifier(String(src ?? ''), aliasPrefix)
              for (const ref of availableSourceAliasRefs) {
                const mappedAlias = refAliasMap.get(ref)
                const colMatch = /\.("([^"]+)")$/.exec(ref)
                const fallbackAlias = String(colMatch?.[1] ?? '').trim()
                  if (!fallbackAlias) continue
                  const nextRef = mappedAlias ? `${aliasPrefix}.${mappedAlias}` : `${aliasPrefix}.${fallbackAlias}`
                  out = out.replaceAll(`${aliasPrefix}.${fallbackAlias}`, nextRef)
                  out = out.replaceAll(ref, nextRef)
              }
              return out
            }
            const replaceRefWithProjectedAlias = (src: string): string => {
              let out = String(src ?? '')
              for (const alias of projectedAliases) {
                out = out.replaceAll(`${cseAlias}.${alias}`, alias)
                out = out.replaceAll(`${baseAlias}.${alias}`, alias)
              }
              for (const ref of availableSourceAliasRefs) {
                const mappedAlias = refAliasMap.get(ref)
                const colMatch = /\.("([^"]+)")$/.exec(ref)
                const fallbackAlias = String(colMatch?.[1] ?? '').trim()
                if (!fallbackAlias) continue
                const nextRef = mappedAlias || fallbackAlias
                out = out.replaceAll(`${cseAlias}.${fallbackAlias}`, nextRef)
                out = out.replaceAll(`${baseAlias}.${fallbackAlias}`, nextRef)
                out = out.replaceAll(ref, nextRef)
              }
              return out
            }
            const baseRelationSqlRaw = (() => {
              const parsedNonWindowItems = nonWindowItems.map((item) => {
                const aliasMatch = /^(.*)\s+AS\s+("[^"]+"|[A-Za-z_][A-Za-z0-9_]*)\s*$/i.exec(item)
                return {
                  item,
                  expr: String(aliasMatch?.[1] ?? '').trim(),
                }
              })
              const hasAggregateNonWindowItem = parsedNonWindowItems.some(({ expr }) =>
                /\b(?:SUM|COUNT|AVG|MIN|MAX|ANY_VALUE)\s*\(/i.test(expr),
              )
              const rawNonWindowGroupExprs = dedupeGroupingExprs(
                parsedNonWindowItems
                  .map(({ expr }) => expr)
                  .filter((expr) => expr && !/\b(?:SUM|COUNT|AVG|MIN|MAX|ANY_VALUE)\s*\(/i.test(expr)),
              )
              if (!hasAggregateNonWindowItem || rawNonWindowGroupExprs.length <= 0) {
                return `SELECT ${nonWindowItems.join(', ')} ${fromTail}`
              }
              const fromBody = fromTail.slice(4).trim()
              const groupByIdx = findTopLevelKeywordSql(fromBody, 'GROUP BY')
              const suffixStartCandidates = [
                findTopLevelKeywordSql(fromBody, 'HAVING'),
                findTopLevelKeywordSql(fromBody, 'QUALIFY'),
                findTopLevelKeywordSql(fromBody, 'ORDER BY'),
                findTopLevelKeywordSql(fromBody, 'LIMIT'),
                findTopLevelKeywordSql(fromBody, 'WINDOW'),
              ].filter((idx) => idx >= 0)
              const fromCoreEnd = suffixStartCandidates.length ? Math.min(...suffixStartCandidates) : fromBody.length
              const hasTopLevelGroupBy = groupByIdx >= 0 && groupByIdx < fromCoreEnd
              const fromCore = fromBody.slice(0, hasTopLevelGroupBy ? groupByIdx : fromCoreEnd).trim()
              const existingGroupByExprs = hasTopLevelGroupBy
                ? splitTopLevelSqlList(fromBody.slice(groupByIdx + 'GROUP BY'.length, fromCoreEnd).trim())
                : []
              const fromSuffix = fromBody.slice(fromCoreEnd).trim()
              const mergedGroupByExprs = dedupeGroupingExprs([...existingGroupByExprs, ...rawNonWindowGroupExprs])
              return `SELECT ${nonWindowItems.join(', ')} FROM ${fromCore} GROUP BY ${mergedGroupByExprs.join(', ')}${fromSuffix ? ` ${fromSuffix}` : ''}`
            })()
            const baseRelationSql =
              baseRelationSqlRaw !== sql
                ? refactorWindowSqlUntilStable(baseRelationSqlRaw)
                : baseRelationSqlRaw
            const loweredWindowSelects = items
              .filter((item) => {
                const aliasMatch = /^(.*)\s+AS\s+("[^"]+"|[A-Za-z_][A-Za-z0-9_]*)\s*$/i.exec(item)
                const expr = String(aliasMatch?.[1] ?? item).trim()
                return __test_findWindowCalls(expr).length > 0
              })
              .map((item) => {
                const aliasMatch = /^(.*)\s+AS\s+("[^"]+"|[A-Za-z_][A-Za-z0-9_]*)\s*$/i.exec(item)
                if (!aliasMatch) return null
                const expr = String(aliasMatch[1] ?? '').trim()
                const alias = String(aliasMatch[2] ?? '').trim()
                const simple = parseSimplePartitionWindowExpr(expr)
                if (!simple) return null
                const aggArg = replaceRefWithProjectedAlias(simple.aggArg)
                const partitionBy = simple.partitionCols
                  .map((part) => replaceRefWithProjectedAlias(part))
                  .join(', ')
                return `${simple.aggFn}(${aggArg}) OVER (PARTITION BY ${partitionBy}) AS ${alias}`
              })
              .filter((v): v is string => Boolean(v))
            if (loweredWindowSelects.length === simpleOnlyWindowExprs.length) {
              const factoredSql = [
                `SELECT ${[...cseOuterSelects, ...loweredWindowSelects].join(', ')}`,
                `FROM (SELECT ${baseOuterSelects.join(', ')} FROM (${baseRelationSql}) AS ${baseAlias}) AS ${cseAlias}`,
                `GROUP BY ${cseGroupByExprs.join(', ')}`,
              ].join(' ')
                .replaceAll(`${cseAlias}.`, '')
                .replaceAll(`${baseAlias}.`, '')
              return factoredSql
            }
          }
        }
      }
      const wrapFromSourceWithAlias = (baseSourceSql: string, alias: string): string => {
        const s0 = String(baseSourceSql ?? '').trim().replace(/;+\s*$/, '')
        const a = String(alias ?? '').trim()
        if (!s0) return a ? `(${''}) AS ${a}` : `(${''})`
        if (!a) return s0
        const lower = s0.toLowerCase()
        if (lower.startsWith('select ') || lower.startsWith('with ') || lower.startsWith('select\n') || lower.startsWith('with\n')) {
          return `(${s0}) AS ${a}`
        }
        if (s0.startsWith('(') && s0.endsWith(')')) {
          const inner = s0.slice(1, -1).trim()
          const innerLower = inner.toLowerCase()
          if (innerLower.startsWith('select ') || innerLower.startsWith('with ') || innerLower.startsWith('select\n') || innerLower.startsWith('with\n')) {
            return `${s0} AS ${a}`
          }
          return `${inner} AS ${a}`
        }
        return `${s0} AS ${a}`
      }
      const fromBody = fromTail.slice(4).trim()
      const fromSuffixStartCandidates = [
        findTopLevelKeywordSql(fromBody, 'GROUP BY'),
        findTopLevelKeywordSql(fromBody, 'HAVING'),
        findTopLevelKeywordSql(fromBody, 'QUALIFY'),
        findTopLevelKeywordSql(fromBody, 'ORDER BY'),
        findTopLevelKeywordSql(fromBody, 'LIMIT'),
        findTopLevelKeywordSql(fromBody, 'WINDOW'),
      ].filter((idx) => idx >= 0)
      const fromSourceEnd = fromSuffixStartCandidates.length ? Math.min(...fromSuffixStartCandidates) : fromBody.length
      const fromSourceSection = fromBody.slice(0, fromSourceEnd).trim()
      const fromSuffix = fromBody.slice(fromSourceEnd).trim()
      const sourceMatch = /^(.*)\s+AS\s+("[^"]+"|[A-Za-z_][A-Za-z0-9_]*)\s*$/i.exec(fromSourceSection)
      const sourceBaseSql = String(sourceMatch?.[1] ?? '').trim()
      const sourceMatchAlias = String(sourceMatch?.[2] ?? '').trim()
      const unwrapParenthesizedSelect = (src: string): { inner: string; wrapped: boolean } => {
        const trimmed = String(src ?? '').trim()
        if (!(trimmed.startsWith('(') && trimmed.endsWith(')'))) return { inner: trimmed, wrapped: false }
        const inner = trimmed.slice(1, -1).trim()
        const lower = inner.toLowerCase()
        if (lower.startsWith('select ') || lower.startsWith('with ') || lower.startsWith('select\n') || lower.startsWith('with\n')) {
          return { inner, wrapped: true }
        }
        return { inner: trimmed, wrapped: false }
      }
      const sourceBaseForRewrite = unwrapParenthesizedSelect(sourceBaseSql)
      const loweredSourceBaseInnerSql =
        sourceBaseForRewrite.inner && sourceBaseForRewrite.inner !== sql
          ? refactorWindowSqlUntilStable(sourceBaseForRewrite.inner)
          : sourceBaseForRewrite.inner
      const loweredSourceBaseSql =
        sourceBaseForRewrite.wrapped
          ? `(${loweredSourceBaseInnerSql})`
          : loweredSourceBaseInnerSql
      const effectiveFromTail =
        loweredSourceBaseSql && sourceMatchAlias
          ? `FROM ${wrapFromSourceWithAlias(loweredSourceBaseSql, sourceMatchAlias)}${fromSuffix ? ` ${fromSuffix}` : ''}`
          : fromTail
      const namedWindowDefs = (() => {
        const out = new Map<string, string>()
        const windowIdx = findTopLevelKeywordSql(fromSuffix, 'WINDOW')
        if (windowIdx < 0) return out
        const defsBody = fromSuffix.slice(windowIdx + 'WINDOW'.length).trim()
        if (!defsBody) return out
        for (const defRaw of splitTopLevelSqlList(defsBody)) {
          const def = String(defRaw || '').trim()
          if (!def) continue
          const match = def.match(/^(?:"([^"]+)"|([A-Za-z_][A-Za-z0-9_$]*))\s+AS\s*\(([\s\S]+)\)\s*$/i)
          const name = String(match?.[1] ?? match?.[2] ?? '').trim()
          const spec = String(match?.[3] ?? '').trim()
          if (!name || !spec) continue
          out.set(name.toLowerCase(), spec)
        }
        return out
      })()
      const parseCorrelatableWindowExpr = (
        exprRaw: string,
      ): { aggFn: string; aggArg: string; partitionCols: string[]; orderTerms: string[]; frameClause: string; canSeed: boolean } | null => {
        const expr = String(exprRaw ?? '').trim()
        if (!expr) return null
        const match = expr.match(/^([A-Za-z_][A-Za-z0-9_]*)\s*\(([\s\S]+)\)\s*OVER\s*\(([\s\S]+)\)\s*$/i)
        if (!match) return null
        const aggFn = String(match[1] ?? '').trim().toUpperCase()
        const aggArg = String(match[2] ?? '').trim()
        const rawOverBody = String(match[3] ?? '').trim()
        if (!aggFn || !aggArg || !rawOverBody) return null
        const namedWindowMatch = rawOverBody.match(/^(?:"([^"]+)"|([A-Za-z_][A-Za-z0-9_$]*))(?=\s|$)([\s\S]*)$/)
        let effectiveOverBody = rawOverBody
        if (namedWindowMatch) {
          const windowName = String(namedWindowMatch[1] ?? namedWindowMatch[2] ?? '').trim().toLowerCase()
          const baseSpec = namedWindowDefs.get(windowName)
          if (baseSpec) {
            const suffix = String(namedWindowMatch[3] ?? '').trim()
            effectiveOverBody = `${baseSpec}${suffix ? ` ${suffix}` : ''}`.trim()
          }
        }
        const partitionIdx = findTopLevelKeywordSql(effectiveOverBody, 'PARTITION BY')
        if (partitionIdx < 0) return null
        const orderIdx = findTopLevelKeywordSql(effectiveOverBody, 'ORDER BY')
        const frameIdxCandidates = [
          findTopLevelKeywordSql(effectiveOverBody, 'ROWS'),
          findTopLevelKeywordSql(effectiveOverBody, 'RANGE'),
          findTopLevelKeywordSql(effectiveOverBody, 'GROUPS'),
        ].filter((idx) => idx >= 0)
        const frameIdx = frameIdxCandidates.length ? Math.min(...frameIdxCandidates) : -1
        const partitionEndCandidates = [orderIdx, frameIdx].filter((idx) => idx >= 0 && idx > partitionIdx)
        const partitionEnd = partitionEndCandidates.length ? Math.min(...partitionEndCandidates) : effectiveOverBody.length
        const partitionBody = effectiveOverBody.slice(partitionIdx + 'PARTITION BY'.length, partitionEnd).trim()
        const partitionCols = splitTopLevelSqlList(partitionBody).map((part) => String(part || '').trim()).filter(Boolean)
        if (!partitionCols.length) return null
        const orderEnd = frameIdx >= 0 ? frameIdx : effectiveOverBody.length
        const orderBody =
          orderIdx >= 0
            ? effectiveOverBody.slice(orderIdx + 'ORDER BY'.length, orderEnd).trim()
            : ''
        const orderTerms = orderBody
          ? splitTopLevelSqlList(orderBody).map((part) => String(part || '').trim()).filter(Boolean)
          : []
        const frameClause = frameIdx >= 0 ? effectiveOverBody.slice(frameIdx).trim() : ''
        return {
          aggFn,
          aggArg,
          partitionCols,
          orderTerms,
          frameClause,
          canSeed: orderTerms.length === 0 && !frameClause,
        }
      }
      const hasTopLevelClause = (keyword: string): boolean => findTopLevelKeywordSql(fromSuffix, keyword) >= 0
      const canFactorTopLevelWindows =
        Boolean(loweredSourceBaseSql) &&
        Boolean(sourceMatchAlias) &&
        sourceMatchAlias.replace(/^"|"$/g, '').toLowerCase() === sourceAlias.replace(/^"|"$/g, '').toLowerCase() &&
        !hasTopLevelClause('LEFT JOIN') &&
        !hasTopLevelClause('RIGHT JOIN') &&
        !hasTopLevelClause('FULL JOIN') &&
        !hasTopLevelClause('INNER JOIN') &&
        !hasTopLevelClause('CROSS JOIN') &&
        !hasTopLevelClause('JOIN') &&
        !hasTopLevelClause('GROUP BY') &&
        !hasTopLevelClause('HAVING') &&
        !hasTopLevelClause('QUALIFY') &&
        !hasTopLevelClause('ORDER BY') &&
        !hasTopLevelClause('LIMIT')
      if (!canFactorTopLevelWindows) {
        if (effectiveFromTail !== fromTail) {
          return `SELECT ${selectBody} ${effectiveFromTail}`
        }
        return sql
      }
      const canLowerSimpleWindows =
        Boolean(loweredSourceBaseSql) &&
        Boolean(sourceMatchAlias) &&
        sourceMatchAlias.replace(/^"|"$/g, '').toLowerCase() === sourceAlias.replace(/^"|"$/g, '').toLowerCase() &&
        !hasTopLevelClause('LEFT JOIN') &&
        !hasTopLevelClause('RIGHT JOIN') &&
        !hasTopLevelClause('FULL JOIN') &&
        !hasTopLevelClause('INNER JOIN') &&
        !hasTopLevelClause('CROSS JOIN') &&
        !hasTopLevelClause('JOIN')
      const innerAlias = quoteIdent('__fg_win_cse')
      const projectedRefs = availableSourceAliasRefs
        .map((ref) => {
          const colMatch = /\.("([^"]+)")$/.exec(ref)
          const colName = String(colMatch?.[2] ?? '').trim()
          if (!colName) return null
          return `${ref} AS ${quoteIdent(colName)}`
        })
        .filter((v): v is string => Boolean(v))
      const projectedRefCols = Array.from(new Set(
        availableSourceAliasRefs
          .map((ref) => {
            const quoted = /\.("([^"]+)")$/.exec(ref)
            if (quoted?.[1]) return String(quoted[1]).trim()
            const bare = /\.([A-Za-z_][A-Za-z0-9_]*)$/.exec(ref)
            return String(bare?.[1] ?? '').trim() ? quoteIdent(String(bare?.[1] ?? '').trim()) : ''
          })
          .filter(Boolean),
      ))
      const replaceProjectedRefQualifiers = (src: string, nextAlias: string): string => {
        let out = replaceSqlAliasQualifier(src, nextAlias)
        for (const quotedCol of projectedRefCols) {
          out = out.replace(
            new RegExp(`(?:\"[^\"]+\"|[A-Za-z_][A-Za-z0-9_]*)\\s*\\.\\s*${escapeRegExp(quotedCol)}`, 'g'),
            `${nextAlias}.${quotedCol}`,
          )
        }
        return out
      }
      const simpleWindowSourceNeedsWrap =
        hasTopLevelClause('GROUP BY') ||
        hasTopLevelClause('HAVING') ||
        hasTopLevelClause('QUALIFY') ||
        hasTopLevelClause('ORDER BY') ||
        hasTopLevelClause('LIMIT') ||
        hasTopLevelClause('WINDOW')
      const simpleWindowWrappedSourceAlias = quoteIdent('__fg_win_base')
      const simpleWindowWrappedSourceSql = simpleWindowSourceNeedsWrap
        ? `SELECT * ${effectiveFromTail}`
        : ''
      const sourceShapeRequiresAggSafeProjectedRefs =
        /\bGROUP\s+BY\b/i.test(String(loweredSourceBaseSql || '')) ||
        /\bGROUPING\s+SETS\b/i.test(String(loweredSourceBaseSql || ''))
      const groupedSourceColumnKeysForFactoring = (() => {
        const out = new Set<string>()
        const src = String(loweredSourceBaseSql || '').trim()
        if (!src) return out
        const sqlText = src.startsWith('(') && src.endsWith(')') ? src.slice(1, -1).trim() : src
        const groupByIdx = findTopLevelKeywordSql(sqlText, 'GROUP BY')
        if (groupByIdx < 0) return out
        const suffixStartCandidates = [
          findTopLevelKeywordSql(sqlText, 'HAVING'),
          findTopLevelKeywordSql(sqlText, 'QUALIFY'),
          findTopLevelKeywordSql(sqlText, 'ORDER BY'),
          findTopLevelKeywordSql(sqlText, 'LIMIT'),
          findTopLevelKeywordSql(sqlText, 'WINDOW'),
        ].filter((idx) => idx >= 0 && idx > groupByIdx)
        const groupByEnd = suffixStartCandidates.length ? Math.min(...suffixStartCandidates) : sqlText.length
        const groupByBody = sqlText.slice(groupByIdx + 'GROUP BY'.length, groupByEnd).trim()
        if (!groupByBody) return out
        for (const exprRaw of splitTopLevelSqlList(groupByBody)) {
          const expr = String(exprRaw || '').trim()
          if (!expr) continue
          const quoted = expr.match(/(?:\"[^\"]+\"|[A-Za-z_][A-Za-z0-9_]*)\s*\.\s*\"([^\"]+)\"$/)
          if (quoted?.[1]) {
            const key = normalizeTableauFieldKey(String(quoted[1] || '').trim()) || String(quoted[1] || '').trim().toLowerCase()
            if (key) out.add(key)
            continue
          }
          const bare = expr.match(/([A-Za-z_][A-Za-z0-9_]*)$/)
          if (bare?.[1]) {
            const key = normalizeTableauFieldKey(String(bare[1] || '').trim()) || String(bare[1] || '').trim().toLowerCase()
            if (key) out.add(key)
          }
        }
        return out
      })()
      const isGroupedProjectedAliasForFactoring = (aliasRaw: string): boolean => {
        const alias = String(aliasRaw || '').trim()
        if (!alias) return false
        if (/^__fg_group_key_\d+$/i.test(alias)) return true
        const sourceColKey = normalizeTableauFieldKey(alias) || alias.toLowerCase()
        return Boolean(sourceColKey && groupedSourceColumnKeysForFactoring.has(sourceColKey))
      }
      const canCorrelateSimpleWindow = (windowExpr: { partitionCols: string[] } | null): boolean => {
        if (!windowExpr) return false
        if (!sourceShapeRequiresAggSafeProjectedRefs) return true
        return windowExpr.partitionCols.every((partRaw) => {
          const part = String(partRaw || '').trim()
          const quoted = part.match(/(?:\"[^\"]+\"|[A-Za-z_][A-Za-z0-9_]*)\s*\.\s*\"([^\"]+)\"$/)
          const bare = part.match(/([A-Za-z_][A-Za-z0-9_]*)$/)
          const alias = String(quoted?.[1] ?? bare?.[1] ?? '').trim()
          return isGroupedProjectedAliasForFactoring(alias)
        })
      }
      const projectedRefMeta = availableSourceAliasRefs
        .map((ref) => {
          const colMatch = /\.("([^"]+)")$/.exec(ref)
          const colName = String(colMatch?.[2] ?? '').trim()
          if (!colName) return null
          const projectedRef = simpleWindowSourceNeedsWrap
            ? `${simpleWindowWrappedSourceAlias}.${quoteIdent(colName)}`
            : ref
          const keepRaw =
            isGroupedProjectedAliasForFactoring(colName) || !sourceShapeRequiresAggSafeProjectedRefs
          const needsAggSafeLift =
            sourceShapeRequiresAggSafeProjectedRefs &&
            !keepRaw
          // `__fg_group_key_*` aliases are always `keepRaw` (via
          // `isGroupedProjectedAliasForFactoring`) and therefore always participate in the
          // factored inner `GROUP BY`. Wrapping them in `ANY_VALUE` is redundant when they
          // are already a grouping column, and actively breaks downstream pattern matching
          // that expects `t."<alias>" AS "<alias>"` passthrough shapes (strip passes, reuse
          // detectors, and per-layer grouping identity tracking). Use a single rule driven
          // by `needsAggSafeLift` so the SELECT projection and the GROUP BY list stay in
          // sync.
          const useAggSafeProjection = needsAggSafeLift
          const projectedExpr = useAggSafeProjection
            ? `ANY_VALUE(${projectedRef})`
            : projectedRef
          return {
            selectExpr: `${projectedExpr} AS ${quoteIdent(colName)}`,
            projectedRef,
            keepRaw,
            needsAggSafeLift,
          }
        })
        .filter((v): v is { selectExpr: string; projectedRef: string; keepRaw: boolean; needsAggSafeLift: boolean } => Boolean(v))
      const projectedRefsForFactoring = projectedRefMeta.map((entry) => entry.selectExpr)
      const correlatableWindowExprs = Array.from(windowCounts.keys()).filter((expr) =>
        canCorrelateSimpleWindow(parseCorrelatableWindowExpr(expr)),
      )
      const simpleWindowExprs = canLowerSimpleWindows
        ? correlatableWindowExprs.filter((expr) => Boolean(parseCorrelatableWindowExpr(expr)?.canSeed))
        : []
      const baseFactoredWindowExprs = canFactorTopLevelWindows
        ? Array.from(new Set([...repeatedWindowExprs, ...simpleWindowExprs]))
        : simpleWindowExprs
      const hasUnsafeWindowFactoringWithAggSafeLifts =
        projectedRefMeta.some((entry) => entry.needsAggSafeLift) &&
        baseFactoredWindowExprs.some((expr) => !canCorrelateSimpleWindow(parseCorrelatableWindowExpr(expr)))
      const factoredWindowExprs = (hasUnsafeWindowFactoringWithAggSafeLifts
        ? simpleWindowExprs
        : baseFactoredWindowExprs
      ).filter((expr) => sourceRefAvailableForFactoring(expr))
      const factorAliases = factoredWindowExprs.map((_expr, idx) => quoteIdent(`__fg_win_${idx}`))
      const factorSourceAlias = quoteIdent('__fg_win_seed')
      const useSeededSimpleWindowFactoring =
        factoredWindowExprs.length > 0 &&
        factoredWindowExprs.every((expr) => {
          const parsed = parseCorrelatableWindowExpr(expr)
          return Boolean(parsed?.canSeed && canCorrelateSimpleWindow(parsed))
        })
      if (!projectedRefsForFactoring.length || !factorAliases.length) {
        if (effectiveFromTail !== fromTail) {
          return `SELECT ${selectBody} ${effectiveFromTail}`
        }
        return sql
      }
      const factorExprs = factoredWindowExprs.map((expr, idx) => {
        const parsedSimpleWindow = canLowerSimpleWindows ? parseCorrelatableWindowExpr(expr) : null
        const simpleWindow =
          parsedSimpleWindow?.canSeed && canCorrelateSimpleWindow(parsedSimpleWindow)
            ? parsedSimpleWindow
            : null
        if (useSeededSimpleWindowFactoring) {
          if (!simpleWindow) return `${expr} AS ${factorAliases[idx]}`
          const aggArg = replaceProjectedRefQualifiers(simpleWindow.aggArg, factorSourceAlias)
          const partitionBy = simpleWindow.partitionCols
            .map((part) => replaceProjectedRefQualifiers(part, factorSourceAlias))
            .join(', ')
          return `${simpleWindow.aggFn}(${aggArg}) OVER (PARTITION BY ${partitionBy}) AS ${factorAliases[idx]}`
        }
        if (!simpleWindow) return `${expr} AS ${factorAliases[idx]}`
        const corrAlias = quoteIdent(`__fg_win_src_${idx}`)
        const aggArg = replaceProjectedRefQualifiers(simpleWindow.aggArg, corrAlias)
        const predicates = simpleWindow.partitionCols.map((part) => {
          const innerPart = replaceProjectedRefQualifiers(part, corrAlias)
          return `${innerPart} IS NOT DISTINCT FROM ${part}`
        })
        return `(
SELECT ${simpleWindow.aggFn}(${aggArg})
FROM ${wrapFromSourceWithAlias(simpleWindowSourceNeedsWrap ? simpleWindowWrappedSourceSql : loweredSourceBaseSql, corrAlias)}
WHERE ${predicates.join(' AND ')}
) AS ${factorAliases[idx]}`
      })
      const innerSelects = [
        ...projectedRefsForFactoring,
        ...factorExprs,
      ]
      const factorLayerProjectedRefs = projectedRefCols.map((quotedCol) => `${factorSourceAlias}.${quotedCol} AS ${quotedCol}`)
      const factorLayerSelects = [...factorLayerProjectedRefs, ...factorExprs]
      const rewriteRef = (exprRaw: string): string => {
        let out = String(exprRaw ?? '')
        for (let i = 0; i < factoredWindowExprs.length; i += 1) {
          out = out.replaceAll(factoredWindowExprs[i], `${factorAliases[i]}`)
        }
        for (const ref of availableSourceAliasRefs) {
          const colMatch = /\.("([^"]+)")$/.exec(ref)
          const colName = String(colMatch?.[2] ?? '').trim()
          if (!colName) continue
          out = out.replaceAll(ref, `${quoteIdent(colName)}`)
        }
        return out
      }
      const hasTopLevelPlainAggregateCall = (exprRaw: string): boolean => {
        const expr = String(exprRaw ?? '')
        if (!expr) return false
        let depth = 0
        let inStr = false
        let inIdent = false
        for (let i = 0; i < expr.length; i += 1) {
          const ch = expr[i]
          if (inStr) {
            if (ch === "'" && expr[i + 1] === "'") { i += 1; continue }
            if (ch === "'") inStr = false
            continue
          }
          if (inIdent) {
            if (ch === '"' && expr[i + 1] === '"') { i += 1; continue }
            if (ch === '"') inIdent = false
            continue
          }
          if (ch === "'") { inStr = true; continue }
          if (ch === '"') { inIdent = true; continue }
          if (ch === '(') { depth += 1; continue }
          if (ch === ')') { depth -= 1; continue }
          if (
            depth === 0 &&
            /^(?:SUM|COUNT|AVG|MIN|MAX|ANY_VALUE)\s*\(/i.test(expr.slice(i)) &&
            !/^OVER\s*\(/i.test(expr.slice(i))
          ) {
            return true
          }
        }
        return false
      }
      const outerItems = items.map((item) => {
        const aliasMatch = /^(.*)\s+AS\s+("[^"]+"|[A-Za-z_][A-Za-z0-9_]*)\s*$/i.exec(item)
        if (!aliasMatch) return rewriteRef(item)
        const expr = rewriteRef(String(aliasMatch[1] ?? '').trim())
        const alias = String(aliasMatch[2] ?? '').trim()
        return `${expr} AS ${alias}`
      })
      const outerProjectsGroupedKeys = outerItems.some((item) => /__fg_group_key_\d+/i.test(String(item ?? '')))
      const outerHasTopLevelAggregate = outerItems.some((item) => hasTopLevelPlainAggregateCall(item))
      if (outerProjectsGroupedKeys && outerHasTopLevelAggregate) {
        if (effectiveFromTail !== fromTail) {
          return `SELECT ${selectBody} ${effectiveFromTail}`
        }
        return sql
      }
      const innerFromTail =
        simpleWindowSourceNeedsWrap
          ? `FROM ${wrapFromSourceWithAlias(simpleWindowWrappedSourceSql, simpleWindowWrappedSourceAlias)}`
          : effectiveFromTail
      const factoredInnerGroupByExprs = Array.from(new Set(
        projectedRefMeta
          .filter((entry) => entry.keepRaw)
          .map((entry) => entry.projectedRef)
          .filter(Boolean),
      ))
      const factoredInnerGroupByClause =
        projectedRefMeta.some((entry) => entry.needsAggSafeLift) && factoredInnerGroupByExprs.length > 0
          ? ` GROUP BY ${factoredInnerGroupByExprs.join(', ')}`
          : ''
      const factoredSql = useSeededSimpleWindowFactoring
        ? (
            `SELECT ${outerItems.join(', ')} ` +
            `FROM (` +
            `SELECT ${factorLayerSelects.join(', ')} FROM (` +
            `SELECT ${projectedRefsForFactoring.join(', ')} ${innerFromTail}${factoredInnerGroupByClause}` +
            `) AS ${factorSourceAlias}` +
            `) AS ${innerAlias}`
          )
        : `SELECT ${outerItems.join(', ')} FROM (SELECT ${innerSelects.join(', ')} ${innerFromTail}${factoredInnerGroupByClause}) AS ${innerAlias}`
      return refactorWindowSqlUntilStable(factoredSql)
    }
    const makeBaseWideSelectParts = (
      measureSelectPairs: Array<{ alias: string; expr: string }>,
      opts?: {
        includeTooltipExtraSelects?: boolean
        includeOrderExtraAliases?: boolean
        includeInteractionCubeSlicers?: boolean
      },
    ): string[] => {
      const includeTooltipExtraSelects = opts?.includeTooltipExtraSelects ?? true
      const includeOrderExtraAliases = opts?.includeOrderExtraAliases ?? true
      const includeInteractionCubeSlicers = opts?.includeInteractionCubeSlicers === true
      const baseWideSelectParts = needsAggregateSafeDimProjectionWithoutGroupBy
        ? selectDimsForBase.map((rawPart) => {
            const raw = String(rawPart ?? '').trim()
            const m = raw.match(/^(.*)\s+AS\s+("[^"]+")\s*$/i)
            if (!m) return raw
            const expr = String(m[1] ?? '').trim()
            const alias = String(m[2] ?? '').trim()
            if (!expr || !alias) return raw
            if (/\b(?:SUM|COUNT|AVG|MIN|MAX|ANY_VALUE)\s*\(/i.test(expr)) return raw
            return `ANY_VALUE(${expr}) AS ${alias}`
          })
        : [...selectDimsForBase]
      const baseWideAliasSeen = new Set<string>()
      for (const part of selectDimsForBase) {
        const m = /\bAS\s+"([^"]+)"\s*$/i.exec(String(part || '').trim())
        const alias = String(m?.[1] || '').trim().toLowerCase()
        if (alias) baseWideAliasSeen.add(alias)
      }
      if (includeInteractionCubeSlicers) {
        for (const slicer of interactionCubeSlicerDims) {
          const alias = String(slicer?.columnName ?? '').trim()
          const select = String(slicer?.select ?? '').trim()
          if (!alias || !select) continue
          const key = alias.toLowerCase()
          if (baseWideAliasSeen.has(key)) continue
          baseWideAliasSeen.add(key)
          baseWideSelectParts.push(select)
        }
      }
      const pushBaseWideAliasSelect = (aliasRaw: string, exprRaw: string) => {
        const alias = String(aliasRaw || '').trim()
        const expr = normalizeResidualTaggedLodWindowAggregates(String(exprRaw || '').trim())
        if (!alias || !expr) return
        const key = alias.toLowerCase()
        if (baseWideAliasSeen.has(key)) return
        baseWideAliasSeen.add(key)
        baseWideSelectParts.push(`${expr} AS ${quoteIdent(alias)}`)
      }
      for (const pair of measureSelectPairs) {
        pushBaseWideAliasSelect(pair.alias, pair.expr)
      }
      if (includeTooltipExtraSelects) {
        for (const c of tooltipExtraSelects) {
          pushBaseWideAliasSelect(String(c.alias || '').trim(), String(c.expr || '').trim())
        }
      }
      if (includeOrderExtraAliases && orderExtraAliases.size) {
        const needsGroupedOrderPassthrough = emitTotals || groupByExprs.length > 0
        for (const alias of orderExtraAliases) {
          const trimmed = String(alias ?? '').trim()
          if (!trimmed) continue
          pushBaseWideAliasSelect(
            trimmed,
            needsGroupedOrderPassthrough
              ? `ANY_VALUE(${baseRef}.${quoteIdent(trimmed)})`
              : `${baseRef}.${quoteIdent(trimmed)}`,
          )
        }
      }
      return baseWideSelectParts
    }
    const buildBaseWideOptimizedSql = (
      measureSelectPairs: Array<{ alias: string; expr: string }>,
      opts?: {
        includeTooltipExtraSelects?: boolean
        includeOrderExtraAliases?: boolean
        // When true, return the inner pre-aggregated form (with __fg_group_key_N
        // aliases and raw SUM/etc. measure exprs) instead of wrapping it with the
        // outer CASE-WHEN totals projection. This is used by the chunked union
        // path to emit a single shared aggregate base that all chunks project
        // from — avoiding N-way re-aggregation of the same underlying data.
        skipTotalsOuterWrap?: boolean
        includeInteractionCubeSlicers?: boolean
      },
    ): string => {
      let baseWide = `SELECT ${makeBaseWideSelectParts(measureSelectPairs, opts).join(', ')} FROM ${baseWideSourceSql}`
      if (sharedWhereConditions.length > 0) {
        baseWide += ` WHERE ${sharedWhereConditions.join(' AND ')}`
      }
      if (emitTotals) {
        const sets: string[] = []
        const rowExprs = totalsGroupingExprs.slice(0, Math.max(0, rowDimCount))
        const colExprs = totalsGroupingExprs.slice(Math.max(0, rowDimCount))
        const rowSubtotals = rowSubtotalsConfig
        const includeGrandTotal = includeGrandTotalConfig
        const baseExprs = dedupeGroupingExprs([...rowExprs, ...colExprs].filter(Boolean))
        sets.push(baseExprs.length ? `(${baseExprs.join(', ')})` : '()')
        if (rowSubtotals && rowExprs.length > 0) {
          const maxLvl = Math.max(0, rowExprs.length - 1)
          for (let lvl = 0; lvl < maxLvl; lvl++) {
            if (!rowSubtotals[lvl]) continue
            const exprs = dedupeGroupingExprs([...rowExprs.slice(0, lvl + 1), ...colExprs].filter(Boolean))
            sets.push(exprs.length ? `(${exprs.join(', ')})` : '()')
          }
        }
        if (includeGrandTotal) {
          const exprs = dedupeGroupingExprs([...colExprs].filter(Boolean))
          sets.push(exprs.length ? `(${exprs.join(', ')})` : '()')
        }
        const uniq = Array.from(new Set(sets))
        baseWide += ` GROUP BY GROUPING SETS (${uniq.join(', ')})`
      } else if (groupByExprs.length || opts?.includeInteractionCubeSlicers === true) {
        const interactionCubeGroupByExprs = opts?.includeInteractionCubeSlicers === true
          ? interactionCubeSlicerDims.map((slicer) => String(slicer.expr ?? '').trim()).filter(Boolean)
          : []
        const dedupedGroupBy = dedupeGroupingExprs([...groupByExprs, ...interactionCubeGroupByExprs])
        if (dedupedGroupBy.length > 0) {
          baseWide += ` GROUP BY ${dedupedGroupBy.join(', ')}`
        }
      }
      if (sharedHavingConditions.length > 0) {
        baseWide += ` HAVING ${sharedHavingConditions.join(' AND ')}`
      }
      baseWide = rewriteWeeklyPhysicalRefs(baseWide)
      _markPhase('adv_measure_compile.groupby')
      _markPhase('adv_sql_assembly')
      if (debugFieldMapping && weeklyTimePeriodOverrides) {
        pushFieldMappingTrace({
          phase: 'tp_sql_stage',
          message: 'baseWide_pre_rewrite',
          data: {
            worksheetName: worksheetNameDebug,
            hasDateTruncWeek: baseWide.includes("DATE_TRUNC('week'"),
            hasLastWeekLiteral: baseWide.includes("'Last Week'"),
            sqlPreview: baseWide.slice(0, 1200),
          },
        })
      }
      const _lodRewritten = rewriteLodWindowsForGroupedQuery(baseWide)
      _markPhase('adv_lod_rewrite')
      if (debugFieldMapping && weeklyTimePeriodOverrides) {
        pushFieldMappingTrace({
          phase: 'tp_sql_stage',
          message: 'after_lod_rewrite',
          data: {
            worksheetName: worksheetNameDebug,
            hasDateTruncWeek: _lodRewritten.includes("DATE_TRUNC('week'"),
            hasLastWeekLiteral: _lodRewritten.includes("'Last Week'"),
            sqlPreview: _lodRewritten.slice(0, 1200),
          },
        })
      }
      const baseWideRewritten = rewriteNestedWindowInGroupedSelectCached(_lodRewritten)
      _markPhase('adv_window_rewrite')
      if (debugFieldMapping && weeklyTimePeriodOverrides) {
        pushFieldMappingTrace({
          phase: 'tp_sql_stage',
          message: 'after_window_rewrite',
          data: {
            worksheetName: worksheetNameDebug,
            hasDateTruncWeek: baseWideRewritten.includes("DATE_TRUNC('week'"),
            hasLastWeekLiteral: baseWideRewritten.includes("'Last Week'"),
            sqlPreview: baseWideRewritten.slice(0, 1200),
          },
        })
      }
      let baseWideFactored = foldLiftedReportEndMonthAliasExprs(
        liftRawSourceReportEndMonthExprInNestedSubquery(
          foldTemplateMonthAnchorDayOffsetExprs(
            foldTemplateMonthAnchorOffsetExprs(
            foldTemplateMonthAnchorParamExprs(foldConstantMonthAnchorExprs(baseWideRewritten)),
            ),
          ),
        ),
      )
      for (let factorPass = 0; factorPass < 4; factorPass += 1) {
        const next = factorRepeatedTopLevelWindowExprs(baseWideFactored)
        if (!next || next === baseWideFactored) break
        baseWideFactored = next
      }
      const baseWideOptimized = sanitizeNullArtifacts(dedupeWindowOverClausesInSelectCached(baseWideFactored))
      _markPhase('adv_window_dedupe')
      if (debugFieldMapping && weeklyTimePeriodOverrides) {
        pushFieldMappingTrace({
          phase: 'tp_sql_stage',
          message: 'after_window_dedupe',
          data: {
            worksheetName: worksheetNameDebug,
            hasDateTruncWeek: baseWideOptimized.includes("DATE_TRUNC('week'"),
            hasLastWeekLiteral: baseWideOptimized.includes("'Last Week'"),
            sqlPreview: baseWideOptimized.slice(0, 1200),
          },
        })
      }
      if (!emitTotals || opts?.skipTotalsOuterWrap) {
        return baseWideOptimized
      }
      const groupedBaseRef = quoteIdent('__fg_grouped_base')
      const projectedSelects: string[] = []
      for (let i = 0; i < groupDims.length; i += 1) {
        const fieldName = String(groupDims[i]?.field_name ?? '').trim()
        if (!fieldName) continue
        const groupKeyRef = `${groupedBaseRef}.${quoteIdent(`__fg_group_key_${i}`)}`
        const seededTemplate = String(seededWeeklyDateGroupDimExprByIdx.get(i) ?? '').trim()
        if (seededTemplate) {
          projectedSelects.push(
            `CASE WHEN ${groupKeyRef} IS NULL OR ${groupKeyRef} = '${totalsNullSentinel}' THEN NULL ELSE TRY_CAST(${groupKeyRef} AS TIMESTAMP) END AS ${quoteIdent(fieldName)}`
          )
          continue
        }
        projectedSelects.push(
          `CASE WHEN ${groupKeyRef} IS NULL THEN 'All' WHEN ${groupKeyRef} = '${totalsNullSentinel}' THEN NULL ELSE ${groupKeyRef} END AS ${quoteIdent(fieldName)}`
        )
      }
      for (const pair of measureSelectPairs) {
        const alias = String(pair.alias ?? '').trim()
        if (!alias) continue
        projectedSelects.push(`${groupedBaseRef}.${quoteIdent(alias)} AS ${quoteIdent(alias)}`)
      }
      if (opts?.includeTooltipExtraSelects ?? true) {
        for (const c of tooltipExtraSelects) {
          const alias = String(c.alias ?? '').trim()
          if (!alias) continue
          projectedSelects.push(`${groupedBaseRef}.${quoteIdent(alias)} AS ${quoteIdent(alias)}`)
        }
      }
      if (opts?.includeOrderExtraAliases ?? true) {
        for (const aliasRaw of orderExtraAliases) {
          const alias = String(aliasRaw ?? '').trim()
          if (!alias) continue
          projectedSelects.push(`${groupedBaseRef}.${quoteIdent(alias)} AS ${quoteIdent(alias)}`)
        }
      }
      let groupedBaseSql = `SELECT ${projectedSelects.join(', ')} FROM (${baseWideOptimized}) AS ${groupedBaseRef}`
      const stripGroupedBaseAggregatePassthrough = (aliasRaw: string) => {
        const alias = String(aliasRaw ?? '').trim()
        if (!alias) return
        const directRef = `${groupedBaseRef}.${quoteIdent(alias)}`
        const aliasRef = quoteIdent(alias)
        groupedBaseSql = groupedBaseSql.replace(
          new RegExp(`\\b(?:ANY_VALUE|MIN|MAX)\\s*\\(\\s*${escapeRegExp(directRef)}\\s*\\)\\s+AS\\s+${escapeRegExp(aliasRef)}`, 'gi'),
          `${directRef} AS ${aliasRef}`,
        )
      }
      for (const pair of measureSelectPairs) stripGroupedBaseAggregatePassthrough(String(pair.alias ?? ''))
      for (const c of tooltipExtraSelects) stripGroupedBaseAggregatePassthrough(String(c.alias ?? ''))
      for (const aliasRaw of orderExtraAliases) stripGroupedBaseAggregatePassthrough(String(aliasRaw ?? ''))
      return groupedBaseSql
    }
    const interactionCubeBuildSql = (() => {
      if (interactionCubeSlicerDims.length <= 0) return null
      const measurePairs = mnvMeasures.map((m) => ({
        alias: m.valueAlias,
        expr: String(m.valueExpr || ''),
      }))
      if (measurePairs.length <= 0) return null
      try {
        return buildBaseWideOptimizedSql(measurePairs, {
          includeTooltipExtraSelects: true,
          includeOrderExtraAliases: true,
          skipTotalsOuterWrap: true,
          includeInteractionCubeSlicers: true,
        })
      } catch {
        return null
      }
    })()
    const baseWideOptimized = buildBaseWideOptimizedSql(
      mnvMeasures.map((m) => ({ alias: m.valueAlias, expr: m.valueExpr })),
    )

    const dimSelectsFromBase: string[] = []
    for (const d of groupDims) {
      const fn = String(d?.field_name ?? '').trim()
      if (!fn) continue
      dimSelectsFromBase.push(`${baseOutRef}.${quoteIdent(fn)} AS ${quoteIdent(fn)}`)
    }
    const tooltipSelectsFromBase = tooltipExtraSelects.map((c) => `${baseOutRef}.${quoteIdent(c.alias)} AS ${quoteIdent(c.alias)}`)

    const orderExtraSelects = orderExtraAliases.size
      ? Array.from(orderExtraAliases).map((alias) => `${baseOutRef}.${quoteIdent(alias)} AS ${quoteIdent(alias)}`)
      : []
	    const outputColumns = [
	      ...groupDims.map((d) => String(d?.field_name ?? '').trim()).filter(Boolean),
	      'Measure Names',
	      'Measure Values',
	      ...tooltipExtraSelects.map((c) => String(c.alias ?? '').trim()).filter(Boolean),
	    ]
	    const interactionCubeAxisFields = groupDims
	      .map((d: any) => {
	        const columnName = String(d?.field_name ?? '').trim()
	        if (!columnName) return null
	        return {
	          fieldName: columnName,
	          displayName: String(d?.label ?? d?.caption ?? columnName).trim() || columnName,
	          columnName,
	          role: 'axis',
	        }
	      })
	      .filter((field): field is Record<string, any> => Boolean(field))
	    const interactionCubeMeasureComponents = mnvMeasures.map((m) => ({
	      fieldName: m.key || m.label,
	      displayName: m.label,
	      columnName: m.valueAlias,
	      resultColumnName: m.valueAlias,
	      aggregate: 'sum',
	    }))

    const chunkMeasures = <T,>(arr: T[], chunkSizeRaw: number): T[][] => {
      const size = Math.max(1, Math.floor(Number(chunkSizeRaw) || 1))
      const out: T[][] = []
      for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size))
      return out
    }
    const chunkMeasuresByExprBudget = (arr: any[], chunkSizeRaw: number, maxExprCharsRaw: number): any[][] => {
      const size = Math.max(1, Math.floor(Number(chunkSizeRaw) || 1))
      const maxExprChars = Math.max(0, Math.floor(Number(maxExprCharsRaw) || 0))
      if (arr.length <= 0) return []
      if (maxExprChars <= 0) return chunkMeasures(arr, size)
      const out: any[][] = []
      let current: any[] = []
      let currentExprChars = 0
      const flush = () => {
        if (current.length <= 0) return
        out.push(current)
        current = []
        currentExprChars = 0
      }
      for (const measure of arr) {
        const nextExprChars = normalizeMeasureExprShape(String((measure as any)?.valueExpr ?? '')).length
        const exceedsSize = current.length >= size
        const exceedsBudget = current.length > 0 && currentExprChars + nextExprChars > maxExprChars
        if (exceedsSize || exceedsBudget) flush()
        current.push(measure)
        currentExprChars += nextExprChars
      }
      flush()
      return out
    }
    const measureNeedsIsolatedChunk = (measure: any): boolean => {
      const expr = String((measure as any)?.valueExpr ?? '').trim()
      if (!expr) return false
      return /\b(?:LEAD|LAG|NTH_VALUE|ROW_NUMBER|RANK|DENSE_RANK|NTILE)\b/i.test(expr)
    }
    const measureChunkDependencySignatureCache = new Map<string, string>()
    const measureChunkDependencySignature = (measure: any): string => {
      const expr = String((measure as any)?.valueExpr ?? '').trim()
      if (!expr) return 'base'
      const alias = String((measure as any)?.valueAlias ?? '').trim()
      const cacheKey = `${alias}::${expr}`
      const cached = measureChunkDependencySignatureCache.get(cacheKey)
      if (cached) return cached
      const singleMeasureSql = buildBaseWideOptimizedSql(
        [{ alias, expr }],
        {
          includeTooltipExtraSelects: true,
          includeOrderExtraAliases: true,
        },
      )
      const extracted = extractDeepestChunkSharedStage(singleMeasureSql)
      const signature =
        canonicalizeSharedStageFromTailKey(extracted?.fromTail || '') ||
        normalizeSqlExpr(singleMeasureSql)
      measureChunkDependencySignatureCache.set(cacheKey, signature)
      return signature
    }
    const chunkMnvMeasuresForPlan = (arr: any[], chunkSizeRaw: number): any[][] => {
      const size = Math.max(1, Math.floor(Number(chunkSizeRaw) || 1))
      const singletonShapeChunkSize = Math.max(size, 8)
      const groupedBySignature = new Map<string, any[]>()
      const groupOrder: string[] = []
      const singletonByShape = new Map<string, any[]>()
      const singletonShapeOrder: string[] = []
      const out: any[][] = []
      const pushGroupedMeasure = (signature: string, measure: any) => {
        let bucket = groupedBySignature.get(signature)
        if (!bucket) {
          bucket = []
          groupedBySignature.set(signature, bucket)
          groupOrder.push(signature)
        }
        bucket.push(measure)
      }
      const pushSingletonMeasure = (shape: string, measure: any) => {
        let bucket = singletonByShape.get(shape)
        if (!bucket) {
          bucket = []
          singletonByShape.set(shape, bucket)
          singletonShapeOrder.push(shape)
        }
        bucket.push(measure)
      }
      for (const measure of arr) {
        const isolate = measureNeedsIsolatedChunk(measure)
        if (isolate) {
          out.push([measure])
          continue
        }
        pushGroupedMeasure(measureChunkDependencySignature(measure), measure)
      }
      for (const signature of groupOrder) {
        const measures = groupedBySignature.get(signature) || []
        if (measures.length === 1) {
          pushSingletonMeasure(
            normalizeMeasureExprShape(String(measures[0]?.valueExpr ?? '')) || signature,
            measures[0],
          )
          continue
        }
        const budgetedChunks = chunkMeasuresByExprBudget(measures, size, mnvOptimization.chunkMaxExprChars)
        mnvOptimizationStats.chunkBudgetGroups.push({
          signature,
          chunks: budgetedChunks.map((chunk) => ({
            measureNames: chunk.map((measure) => String(measure?.label ?? '').trim()).filter(Boolean),
            exprChars: chunk.reduce(
              (acc, measure) => acc + normalizeMeasureExprShape(String((measure as any)?.valueExpr ?? '')).length,
              0,
            ),
          })),
        })
        for (const chunk of budgetedChunks) {
          if (chunk.length > 0) out.push(chunk)
        }
      }
      const residualSingletonMeasures: any[] = []
      for (const shape of singletonShapeOrder) {
        const measures = singletonByShape.get(shape) || []
        if (measures.length === 1) {
          residualSingletonMeasures.push(measures[0])
          continue
        }
        for (const chunk of chunkMeasuresByExprBudget(measures, singletonShapeChunkSize, mnvOptimization.chunkMaxExprChars)) {
          if (chunk.length > 0) out.push(chunk)
        }
      }
      // Cap residual singleton chunks at the configured singleton-shape chunk
      // size (default 8). Previously this was `Math.max(singletonShapeChunkSize,
      // residualSingletonMeasures.length)`, which collapsed every residual
      // measure into a single chunk whenever residuals outnumbered the cap —
      // e.g. Monthly Comparison's 24 heterogeneous LOOKUP measures turned into
      // one 24-measure chunk, defeating staged projection and forcing the
      // monolithic `_agg_base_src` path that OOM'd DuckDB's OPFS temp quota.
      // Capping at the singleton shape size lets the unified-agg-base optimizer
      // share one aggregate across multiple smaller UNNEST chunks, which
      // materialize independently and release memory between statements.
      for (const chunk of chunkMeasuresByExprBudget(residualSingletonMeasures, singletonShapeChunkSize, mnvOptimization.chunkMaxExprChars)) {
        if (chunk.length > 0) out.push(chunk)
      }
      return out
    }

    const unnestAlias = quoteIdent('__fg_mnv_u')
    const unnestItemAlias = quoteIdent('__fg_mnv_item')
    const buildUnnestSelectParts = (sourceRef: string, extraSourceRef = sourceRef): string[] => [
      ...groupDims.map((d) => `${sourceRef}.${quoteIdent(String(d?.field_name ?? '').trim())} AS ${quoteIdent(String(d?.field_name ?? '').trim())}`).filter((v) => !/\.""\s+AS\s+""$/.test(v)),
      `struct_extract(${unnestAlias}.${unnestItemAlias}, 'label') AS ${quoteIdent('Measure Names')}`,
      `struct_extract(${unnestAlias}.${unnestItemAlias}, 'value') AS ${quoteIdent('Measure Values')}`,
      ...tooltipExtraSelects.map((c) => `${extraSourceRef}.${quoteIdent(c.alias)} AS ${quoteIdent(c.alias)}`),
      ...Array.from(orderExtraAliases).map((alias) => `${extraSourceRef}.${quoteIdent(alias)} AS ${quoteIdent(alias)}`),
    ]
    const baseStructListForMeasures = (list: MnvMeasureOut[], sourceRef = baseOutRef) => {
      const structItems: string[] = []
      for (const m of list) {
        const valCol = `${sourceRef}.${quoteIdent(m.valueAlias)}`
        const keepExpr =
          m.filterPreds.length > 0 ? `(${m.filterPreds.join(' AND ')})` : 'true'
        // Keep the UNNEST list homogeneous for DuckDB binder stability.
        // Some measure aliases resolve to VARCHAR-ish ATTR expressions; coercing all
        // Measure Values payloads to DOUBLE avoids mixed-struct list types.
        structItems.push(`struct_pack(label := ${escapeValue(m.label)}, value := TRY_CAST(${valCol} AS DOUBLE), keep := ${keepExpr})`)
      }
      return `list_filter([${structItems.join(', ')}], x -> x.keep)`
    }

    const mnvOptimizationStats: any = {
      ts: Date.now(),
      worksheetName: worksheetName || null,
      measureCount: mnvMeasures.length,
      stagedProjection: useStagedMnvProjection,
      stagedProjectionMinMeasures,
      estimatedUnstagedProjectionWidth,
      estimatedUnstagedExprChars,
      search,
      chunkSize: mnvOptimization.chunkSize,
      chunkMaxExprChars: mnvOptimization.chunkMaxExprChars,
      rawChunkSize: mnvOptimization.rawChunkSize,
      rawChunkMinMeasures: mnvOptimization.rawChunkMinMeasures,
      rawChunkMaxExprChars: mnvOptimization.rawChunkMaxExprChars,
      chunkCount: 0,
      totalExprCharsBefore: 0,
      totalExprCharsAfter: 0,
      windowCandidates: 0,
      windowFactored: 0,
      castCandidates: 0,
      castFactored: 0,
      reusableFamilyCount: 0,
      reusableFamilySizes: [] as number[],
      chunkDependencyGroups: [] as Array<{ measureNames: string[], signature: string }>,
      chunkBudgetGroups: [] as Array<{ signature: string, chunks: Array<{ measureNames: string[], exprChars: number }> }>,
      chunkReusableFamilies: [] as Array<{ measureNames: string[], familyKey: string }>,
      chunkStageFamilyKeys: [] as Array<{ measureNames: string[], stageKeys: string[] }>,
      measureExprShapes: [] as Array<{ measureName: string, shape: string }>,
    }
    const stagedChunkPlanParts: Array<{
      sql: string
      baseSql: string
      outSqlTemplate: string
      measureNames: string[]
    }> = []
    let stagedChunkSharedStages: Array<{
      placeholderName: string
      sql: string
      chunkIndexes: number[]
    }> = []
    let stagedChunkReusableAggBases: StagedChunkReusableAggBaseDefinition[] = []
    let stagedChunkReusableWrapperStages: StagedChunkSharedStageDefinition[] = []
    let stagedChunkReusableWrapperSourceSql = ''

    const extractDeepestChunkSharedStage = (sqlRaw: string): {
      innerSql: string
      selectItems: string[]
      fromTail: string
      innerStart: number
      innerEnd: number
    } | null => {
      const sql = String(sqlRaw || '')
      const sourceMarker = `FROM ${parquetRef} AS ${baseRef}`
      const sourcePos = sql.lastIndexOf(sourceMarker)
      if (sourcePos < 0) return null
      let balance = 0
      let openParen = -1
      for (let idx = sourcePos - 1; idx >= 0; idx -= 1) {
        const ch = sql[idx]
        if (ch === ')') {
          balance += 1
          continue
        }
        if (ch !== '(') continue
        if (balance > 0) {
          balance -= 1
          continue
        }
        const candidate = sql.slice(idx + 1).trimStart()
        if (!/^SELECT\b/i.test(candidate)) continue
        openParen = idx
        break
      }
      if (openParen < 0) return null
      let depth = 0
      let closeParen = -1
      for (let idx = openParen + 1; idx < sql.length; idx += 1) {
        const ch = sql[idx]
        if (ch === '(') {
          depth += 1
          continue
        }
        if (ch !== ')') continue
        if (depth > 0) {
          depth -= 1
          continue
        }
        closeParen = idx
        break
      }
      if (closeParen < 0 || closeParen <= openParen + 1) return null
      const innerSql = sql.slice(openParen + 1, closeParen).trim()
      if (!innerSql) return null
      const fromIdx = findTopLevelKeywordSql(innerSql, 'FROM')
      if (fromIdx <= 0) return null
      const selectItems = splitTopLevelSqlList(innerSql.slice('SELECT'.length, fromIdx).trim())
      const fromTail = innerSql.slice(fromIdx).trim()
      if (selectItems.length <= 0 || !fromTail) return null
      const dependsOnLiftedReportEndMonthAlias = selectItems.some((itemRaw) =>
        /__fg_reportenddate_month/i.test(String(itemRaw || '').trim()),
      )
      if (dependsOnLiftedReportEndMonthAlias) return null
      const hasHoistedGroupedDimProjection = String(baseWideSourceSql || '').trim() !== `${parquetRef} AS ${baseRef}`
      if (hasHoistedGroupedDimProjection && normalizeSqlExpr(fromTail) === normalizeSqlExpr(sourceMarker)) {
        const dependsOnProjectedGroupedDimAlias = selectItems.some((itemRaw) => {
          const item = String(itemRaw || '').trim()
          if (!item) return false
          return groupDims.some((dim) => {
            const fieldName = String(dim?.field_name ?? '').trim()
            if (!fieldName) return false
            const escapedField = quoteIdent(fieldName).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
            const escapedBaseRef = String(baseRef).replace(/"/g, '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
            return new RegExp(
              `(?:^|[^A-Za-z0-9_])(?:${escapedField}|${escapedBaseRef}\\s*\\.\\s*${escapedField})(?:$|[^A-Za-z0-9_])`,
              'i',
            ).test(item)
          })
        })
        if (dependsOnProjectedGroupedDimAlias) return null
      }
      return {
        innerSql,
        selectItems,
        fromTail,
        innerStart: openParen + 1,
        innerEnd: closeParen,
      }
    }

    const rewriteChunkBaseSharedStage = (
      sqlRaw: string,
      expectedInnerSql: string,
      replacementSql: string,
    ): string => {
      const sql = String(sqlRaw || '')
      const extracted = extractDeepestChunkSharedStage(sql)
      if (!extracted) return sql
      if (String(extracted.innerSql || '').trim() !== String(expectedInnerSql || '').trim()) return sql
      return `${sql.slice(0, extracted.innerStart)}${replacementSql}${sql.slice(extracted.innerEnd)}`
    }

    const canonicalizeSharedStageFromTailKey = (fromTailRaw: string): string => {
      const fromTail = String(fromTailRaw || '').trim()
      if (!fromTail) return fromTail
      const groupingSetsIdx = findTopLevelKeywordSql(fromTail, 'GROUP BY GROUPING SETS')
      if (groupingSetsIdx < 0) return normalizeSqlExpr(fromTail)
      const openParen = fromTail.indexOf('(', groupingSetsIdx)
      if (openParen < 0) return normalizeSqlExpr(fromTail)
      let depth = 0
      let closeParen = -1
      for (let idx = openParen; idx < fromTail.length; idx += 1) {
        const ch = fromTail[idx]
        if (ch === '(') {
          depth += 1
          continue
        }
        if (ch !== ')') continue
        depth -= 1
        if (depth === 0) {
          closeParen = idx
          break
        }
      }
      if (closeParen < 0) return normalizeSqlExpr(fromTail)
      const before = fromTail.slice(0, openParen + 1)
      const groupingSetsBody = fromTail.slice(openParen + 1, closeParen)
      const after = fromTail.slice(closeParen)
      const canonicalGroupSetItem = (itemRaw: string): string => {
        const item = String(itemRaw || '').trim()
        if (!item.startsWith('(') || !item.endsWith(')')) return normalizeSqlExpr(item)
        const body = item.slice(1, -1).trim()
        if (!body) return '()'
        const exprs = splitTopLevelSqlList(body)
          .map((expr) => normalizeSqlExpr(expr))
          .filter(Boolean)
          .sort()
        return `(${exprs.join(', ')})`
      }
      const canonicalSets = splitTopLevelSqlList(groupingSetsBody)
        .map((item) => canonicalGroupSetItem(item))
        .filter(Boolean)
        .sort()
      return normalizeSqlExpr(`${before}${canonicalSets.join(', ')}${after}`)
    }

    const buildSharedStageProjectionSql = (
      selectItems: string[],
      placeholderName: string,
    ): string => {
      const sharedRef = quoteIdent('__fg_mnv_shared_src')
      const projected = selectItems.map((itemRaw) => {
        const item = String(itemRaw || '').trim()
        if (!item) return item
        if (/^[A-Za-z_][A-Za-z0-9_]*\.\*$/.test(item) || /^"[^"]+"\.\*$/.test(item)) {
          return `${sharedRef}.*`
        }
        if (item === '*') {
          return `${sharedRef}.*`
        }
        const aliasMatch = /\bAS\s+("[^"]+"|[A-Za-z_][A-Za-z0-9_]*)\s*$/i.exec(item)
        const alias = String(aliasMatch?.[1] || '').trim()
        if (!alias) return item
        return `${sharedRef}.${alias} AS ${alias}`
      }).filter(Boolean)
      return `SELECT ${projected.join(', ')} FROM ${placeholderName} AS ${sharedRef}`
    }

    const extractSelectItemAlias = (itemRaw: string): string => {
      const item = String(itemRaw || '').trim()
      if (!item) return ''
      const aliasMatch = /\bAS\s+("[^"]+"|[A-Za-z_][A-Za-z0-9_]*)\s*$/i.exec(item)
      if (aliasMatch?.[1]) return String(aliasMatch[1]).trim()
      return ''
    }

    const unquoteProjectedAliasName = (raw: string): string => {
      const name = String(raw || '').trim()
      if (!name) return ''
      if (name.startsWith('"') && name.endsWith('"')) {
        return name.slice(1, -1).replace(/""/g, '"').trim()
      }
      return name
    }

    const normalizeProjectedAliasKey = (raw: string): string =>
      unquoteProjectedAliasName(String(raw || '')).toLowerCase()

    const parseProjectedAliasRef = (
      exprRaw: string,
      sourceAliasRaw: string,
    ): string | null => {
      const expr = String(exprRaw || '').trim()
      if (!expr) return null
      const sourceAliasKey = normalizeProjectedAliasKey(sourceAliasRaw)
      const quoted =
        /^(?:(\"[^\"]+\"|[A-Za-z_][A-Za-z0-9_]*)\s*\.\s*)?\"((?:[^\"]|\"\")+)\"$/i.exec(expr)
      if (quoted) {
        const qualifierKey = normalizeProjectedAliasKey(String(quoted[1] || ''))
        if (qualifierKey && sourceAliasKey && qualifierKey !== sourceAliasKey) return null
        const aliasName = String(quoted[2] || '').replace(/""/g, '"').trim()
        return aliasName || null
      }
      const bare =
        /^(?:(\"[^\"]+\"|[A-Za-z_][A-Za-z0-9_]*)\s*\.\s*)?([A-Za-z_][A-Za-z0-9_]*)$/i.exec(expr)
      if (!bare) return null
      const qualifierKey = normalizeProjectedAliasKey(String(bare[1] || ''))
      if (qualifierKey && sourceAliasKey && qualifierKey !== sourceAliasKey) return null
      const aliasName = String(bare[2] || '').trim()
      return aliasName || null
    }

    const collectDirectProjectedStageAliases = (
      selectItemsRaw: string[],
      sourceAliasRaw: string,
    ): string[] => {
      if (!Array.isArray(selectItemsRaw) || selectItemsRaw.length <= 0) return []
      const out: string[] = []
      const seen = new Set<string>()
      for (const itemRaw of selectItemsRaw) {
        const item = String(itemRaw || '').trim()
        if (!item) continue
        const outputAlias = unquoteProjectedAliasName(extractSelectItemAlias(item))
        if (!outputAlias) continue
        const aliasMatch = /\bAS\s+("[^"]+"|[A-Za-z_][A-Za-z0-9_]*)\s*$/i.exec(item)
        const expr = String(aliasMatch?.index != null ? item.slice(0, aliasMatch.index).trim() : item).trim()
        if (!expr) continue
        const sourceAliasName = parseProjectedAliasRef(expr, sourceAliasRaw)
        if (!sourceAliasName) continue
        const outputAliasKey = normalizeProjectedAliasKey(outputAlias)
        if (
          !outputAliasKey ||
          outputAliasKey !== normalizeProjectedAliasKey(sourceAliasName) ||
          seen.has(outputAliasKey)
        ) {
          continue
        }
        seen.add(outputAliasKey)
        out.push(outputAlias)
      }
      return out
    }

    const collectReferencedStageSourceColumnAliases = (
      sqlRaw: string,
      sourceAliasRaw: string,
    ): string[] => {
      const sql = String(sqlRaw || '')
      const sourceAlias = String(sourceAliasRaw || '').trim()
      if (!sql || !sourceAlias) return []
      const sourceAliasVariants = new Set<string>([sourceAlias])
      const unquotedSourceAlias = unquoteProjectedAliasName(sourceAlias)
      if (
        unquotedSourceAlias &&
        /^[A-Za-z_][A-Za-z0-9_]*$/.test(unquotedSourceAlias)
      ) {
        sourceAliasVariants.add(unquotedSourceAlias)
      }
      const out: string[] = []
      const seen = new Set<string>()
      const pushAlias = (aliasRaw: string): void => {
        const aliasName = unquoteProjectedAliasName(String(aliasRaw || ''))
        const aliasKey = normalizeProjectedAliasKey(aliasName)
        if (!aliasName || !aliasKey || seen.has(aliasKey)) return
        seen.add(aliasKey)
        out.push(aliasName)
      }
      for (const prefix of sourceAliasVariants) {
        const escapedPrefix = prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        const prefixPattern =
          prefix.startsWith('"') && prefix.endsWith('"')
            ? escapedPrefix
            : `\\b${escapedPrefix}\\b`
        const quotedColumnPattern = new RegExp(
          `${prefixPattern}\\s*\\.\\s*"((?:[^"]|"")+)"`,
          'gi',
        )
        const bareColumnPattern = new RegExp(
          `${prefixPattern}\\s*\\.\\s*([A-Za-z_][A-Za-z0-9_]*)`,
          'gi',
        )
        for (const match of sql.matchAll(quotedColumnPattern)) {
          pushAlias(String(match[1] || '').replace(/""/g, '"'))
        }
        for (const match of sql.matchAll(bareColumnPattern)) {
          pushAlias(String(match[1] || '').trim())
        }
      }
      return out
    }

    const replaceProjectedSelectItemSourceAlias = (
      selectItemRaw: string,
      sourceAliasRaw: string,
      replacementAliasRaw: string,
    ): string => {
      let next = String(selectItemRaw || '').trim()
      const sourceAlias = String(sourceAliasRaw || '').trim()
      const replacementAlias = String(replacementAliasRaw || '').trim()
      if (!next || !sourceAlias || !replacementAlias || sourceAlias === replacementAlias) {
        return next
      }
      const sourceAliasVariants = new Set<string>([sourceAlias])
      const unquotedSourceAlias = unquoteProjectedAliasName(sourceAlias)
      if (
        unquotedSourceAlias &&
        /^[A-Za-z_][A-Za-z0-9_]*$/.test(unquotedSourceAlias)
      ) {
        sourceAliasVariants.add(unquotedSourceAlias)
      }
      for (const prefix of sourceAliasVariants) {
        const escapedPrefix = prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        const prefixPattern =
          prefix.startsWith('"') && prefix.endsWith('"')
            ? escapedPrefix
            : `\\b${escapedPrefix}\\b`
        next = next.replace(new RegExp(`${prefixPattern}\\s*\\.`, 'g'), `${replacementAlias}.`)
      }
      return next
    }

    const appendRequiredProjectedSelectItemsFromImmediateSource = (
      sqlRaw: string,
      requiredSelectItemsRaw:
        | Array<{
          selectItem?: string | null
          sourceAlias?: string | null
        }>
        | null
        | undefined,
      opts?: {
        skipFinalize?: boolean
      },
    ): string => {
      const sql = finalizeSql(String(sqlRaw || ''))
      const requiredSelectItems = Array.isArray(requiredSelectItemsRaw)
        ? requiredSelectItemsRaw
            .map((entry) => ({
              selectItem: String(entry?.selectItem || '').trim(),
              sourceAlias: String(entry?.sourceAlias || '').trim(),
            }))
            .filter((entry) => entry.selectItem && entry.sourceAlias)
        : []
      if (!sql || requiredSelectItems.length <= 0) return sql
      if (
        /^\s*SELECT\s+DISTINCT\b/i.test(sql) ||
        findTopLevelKeywordSql(sql, 'HAVING') >= 0 ||
        findTopLevelKeywordSql(sql, 'QUALIFY') >= 0
      ) {
        return sql
      }
      const topStage = extractTopLevelSelectStage(sql)
      const topSelectItems = Array.isArray(topStage?.selectItems) ? topStage.selectItems : []
      if (topSelectItems.length <= 0 || selectItemsContainWildcard(topSelectItems)) return sql
      const immediateSource = extractTopLevelFromSubqueryStage(sql)
      const targetSourceAlias = String(immediateSource?.sourceAlias || '').trim()
      if (!targetSourceAlias) return sql
      const existingAliasKeys = new Set(
        topSelectItems
          .map((selectItem) =>
            normalizeProjectedAliasKey(
              unquoteProjectedAliasName(extractSelectItemAlias(String(selectItem || ''))),
            ),
          )
          .filter(Boolean),
      )
      const explicitSelectItems: string[] = []
      for (const requiredSelectItem of requiredSelectItems) {
        const rewrittenSelectItem = replaceProjectedSelectItemSourceAlias(
          requiredSelectItem.selectItem,
          requiredSelectItem.sourceAlias,
          targetSourceAlias,
        )
        const aliasKey = normalizeProjectedAliasKey(
          unquoteProjectedAliasName(extractSelectItemAlias(rewrittenSelectItem)),
        )
        if (!rewrittenSelectItem || !aliasKey || existingAliasKeys.has(aliasKey)) continue
        existingAliasKeys.add(aliasKey)
        explicitSelectItems.push(rewrittenSelectItem)
      }
      if (explicitSelectItems.length <= 0) return sql
      const rebuiltSql =
        `SELECT ${[...topSelectItems, ...explicitSelectItems].join(', ')} ${String(topStage?.fromTail || '').trim()}`
      if (opts?.skipFinalize) return rebuiltSql
      return finalizeSql(rebuiltSql) || sql
    }

    const propagateRequiredProjectedSelectItemsThroughNestedSources = (
      sqlRaw: string,
      requiredSelectItemsRaw:
        | Array<{
          selectItem?: string | null
          sourceAlias?: string | null
        }>
        | null
        | undefined,
      remainingDepth = 24,
    ): string => {
      const sql = finalizeSql(String(sqlRaw || ''))
      if (!sql || remainingDepth <= 0) return sql
      const requiredSelectItems = Array.isArray(requiredSelectItemsRaw)
        ? requiredSelectItemsRaw
            .map((entry) => ({
              selectItem: String(entry?.selectItem || '').trim(),
              sourceAlias: String(entry?.sourceAlias || '').trim(),
              aliasKey: normalizeProjectedAliasKey(
                unquoteProjectedAliasName(extractSelectItemAlias(String(entry?.selectItem || ''))),
              ),
            }))
            .filter((entry) => entry.selectItem && entry.sourceAlias && entry.aliasKey)
        : []
      if (requiredSelectItems.length <= 0) return sql
      const topStage = extractTopLevelSelectStage(sql)
      const topSelectItems = Array.isArray(topStage?.selectItems) ? topStage.selectItems : []
      const fromTail = String(topStage?.fromTail || '').trim()
      if (topSelectItems.length <= 0 || !fromTail || selectItemsContainWildcard(topSelectItems)) return sql
      const immediateSource = extractTopLevelFromSubqueryStage(sql)
      const sourceAlias = String(immediateSource?.sourceAlias || '').trim()
      if (!sourceAlias) return sql
      const neededSelectItems = requiredSelectItems.filter((requiredSelectItem) => {
        for (const selectItemRaw of topSelectItems) {
          const selectItem = String(selectItemRaw || '').trim()
          if (!selectItem) continue
          const aliasMatch = /\bAS\s+("[^"]+"|[A-Za-z_][A-Za-z0-9_]*)\s*$/i.exec(selectItem)
          const expr = String(aliasMatch?.index != null ? selectItem.slice(0, aliasMatch.index).trim() : selectItem).trim()
          const outputAliasKey = normalizeProjectedAliasKey(
            unquoteProjectedAliasName(extractSelectItemAlias(selectItem)),
          )
          const sourceAliasName = parseProjectedAliasRef(expr, sourceAlias)
          if (!outputAliasKey || !sourceAliasName) continue
          if (
            outputAliasKey === requiredSelectItem.aliasKey &&
            outputAliasKey === normalizeProjectedAliasKey(String(sourceAliasName || ''))
          ) {
            return true
          }
        }
        return false
      })
      if (neededSelectItems.length <= 0 || !/^FROM\s*\(/i.test(fromTail)) return sql
      const openParen = sql.indexOf('(', sql.length - fromTail.length)
      if (openParen < 0) return sql
      let depth = 0
      let closeParen = -1
      for (let idx = openParen; idx < sql.length; idx += 1) {
        const ch = sql[idx]
        if (ch === '(') {
          depth += 1
          continue
        }
        if (ch !== ')') continue
        depth -= 1
        if (depth === 0) {
          closeParen = idx
          break
        }
      }
      if (closeParen <= openParen + 1) return sql
      const innerSql = sql.slice(openParen + 1, closeParen).trim()
      const rewrittenInner = appendRequiredProjectedSelectItemsFromImmediateSource(
        propagateRequiredProjectedSelectItemsThroughNestedSources(
          innerSql,
          neededSelectItems,
          remainingDepth - 1,
        ),
        neededSelectItems,
      )
      if (!rewrittenInner || rewrittenInner === innerSql) return sql
      return `${sql.slice(0, openParen + 1)}${rewrittenInner}${sql.slice(closeParen)}`
    }

    const appendRequiredProjectedAliasesFromImmediateSource = (
      sqlRaw: string,
      requiredAliasesRaw: string[] | null | undefined,
      opts?: {
        skipFinalize?: boolean
      },
    ): string => {
      const sql = finalizeSql(String(sqlRaw || ''))
      const requiredAliases = Array.isArray(requiredAliasesRaw)
        ? requiredAliasesRaw
            .map((aliasRaw) => unquoteProjectedAliasName(String(aliasRaw || '')))
            .filter(Boolean)
        : []
      if (!sql || requiredAliases.length <= 0) return sql
      if (
        /^\s*SELECT\s+DISTINCT\b/i.test(sql) ||
        findTopLevelKeywordSql(sql, 'HAVING') >= 0 ||
        findTopLevelKeywordSql(sql, 'QUALIFY') >= 0
      ) {
        return sql
      }
      const topStage = extractTopLevelSelectStage(sql)
      const topSelectItems = Array.isArray(topStage?.selectItems) ? topStage.selectItems : []
      if (topSelectItems.length <= 0 || selectItemsContainWildcard(topSelectItems)) return sql
      const fromTail = String(topStage?.fromTail || '').trim()
      const isDirectRelationSource = fromTail.length > 0 && !/^FROM\s*\(/i.test(fromTail)
      const immediateSource = extractTopLevelFromSubqueryStage(sql)
      const sourceAlias = String(immediateSource?.sourceAlias || '').trim()
      const sourceSelectItems = Array.isArray(immediateSource?.selectItems)
        ? immediateSource.selectItems
        : []
      if (!sourceAlias || sourceSelectItems.length <= 0) return sql
      const groupByExprKeys = (() => {
        const groupByIdx = findTopLevelKeywordSql(sql, 'GROUP BY')
        if (groupByIdx < 0) return null
        if (/^\s*GROUP\s+BY\s+GROUPING\s+SETS\b/i.test(sql.slice(groupByIdx))) return null
        let endIdx = sql.length
        for (const keyword of ['WINDOW', 'ORDER BY', 'LIMIT', 'OFFSET']) {
          const candidateIdx = findTopLevelKeywordSql(sql, keyword)
          if (candidateIdx > groupByIdx && candidateIdx < endIdx) endIdx = candidateIdx
        }
        const body = sql.slice(groupByIdx + 'GROUP BY'.length, endIdx).trim()
        if (!body) return null
        return new Set(
          splitTopLevelSqlList(body)
            .map((exprRaw) => normalizeSqlExpr(String(exprRaw || '').trim()))
            .filter(Boolean),
        )
      })()
      const availableAliasByKey = new Map<string, string>()
      for (const selectItem of sourceSelectItems) {
        const aliasName = unquoteProjectedAliasName(extractSelectItemAlias(String(selectItem || '')))
        const aliasKey = normalizeProjectedAliasKey(aliasName)
        if (!aliasKey || availableAliasByKey.has(aliasKey)) continue
        availableAliasByKey.set(aliasKey, aliasName)
      }
      const existingAliasKeys = new Set(
        topSelectItems
          .map((selectItem) =>
            normalizeProjectedAliasKey(
              unquoteProjectedAliasName(extractSelectItemAlias(String(selectItem || ''))),
            ),
          )
          .filter(Boolean),
      )
      const passthroughItems: string[] = []
      for (const requiredAlias of requiredAliases) {
        const aliasKey = normalizeProjectedAliasKey(requiredAlias)
        if (!aliasKey || existingAliasKeys.has(aliasKey)) continue
        const sourceAliasName = availableAliasByKey.get(aliasKey)
        const passthroughAliasName = requiredAlias
        const passthroughExpr = sourceAliasName
          ? `${sourceAlias}.${quoteIdent(sourceAliasName)}`
          : isDirectRelationSource
            ? `${sourceAlias}.${quoteIdent(requiredAlias)}`
            : ''
        if (!passthroughExpr) continue
        let passthroughProjection = `${passthroughExpr} AS ${quoteIdent(passthroughAliasName)}`
        if (
          groupByExprKeys &&
          !groupByExprKeys.has(normalizeSqlExpr(passthroughExpr))
        ) {
          if (!sourceAliasName) continue
          passthroughProjection =
            `ANY_VALUE(${passthroughExpr}) AS ${quoteIdent(passthroughAliasName)}`
        }
        passthroughItems.push(passthroughProjection)
        existingAliasKeys.add(aliasKey)
      }
      if (passthroughItems.length <= 0) return sql
      const rebuiltSql =
        `SELECT ${[...topSelectItems, ...passthroughItems].join(', ')} ${String(topStage?.fromTail || '').trim()}`
      if (opts?.skipFinalize) return rebuiltSql
      return finalizeSql(rebuiltSql) || sql
    }

    const propagateRequiredProjectedAliasesThroughNestedSources = (
      sqlRaw: string,
      requiredAliasesRaw: string[] | null | undefined,
      remainingDepth = 24,
    ): string => {
      const sql = finalizeSql(String(sqlRaw || ''))
      if (!sql || remainingDepth <= 0) return sql
      const requiredAliases = Array.isArray(requiredAliasesRaw)
        ? requiredAliasesRaw
            .map((aliasRaw) => unquoteProjectedAliasName(String(aliasRaw || '')))
            .filter(Boolean)
        : []
      const hasExplicitRequiredAliases = requiredAliases.length > 0
      const topStage = extractTopLevelSelectStage(sql)
      const topSelectItems = Array.isArray(topStage?.selectItems) ? topStage.selectItems : []
      const fromTail = String(topStage?.fromTail || '').trim()
      if (topSelectItems.length <= 0 || !fromTail) return sql
      const immediateSource = extractTopLevelFromSubqueryStage(sql)
      const sourceAlias = String(immediateSource?.sourceAlias || '').trim()
      const topHasWildcard = selectItemsContainWildcard(topSelectItems)
      const topExplicitAliasKeys = new Set(
        topSelectItems
          .map((selectItem) =>
            normalizeProjectedAliasKey(
              unquoteProjectedAliasName(extractSelectItemAlias(String(selectItem || ''))),
            ),
          )
          .filter(Boolean),
      )
      const neededAliasNames: string[] = []
      const neededAliasKeys = new Set<string>()
      const pushNeededAlias = (aliasRaw: string) => {
        const aliasName = unquoteProjectedAliasName(String(aliasRaw || ''))
        const aliasKey = normalizeProjectedAliasKey(aliasName)
        if (!aliasName || !aliasKey || neededAliasKeys.has(aliasKey)) return
        neededAliasKeys.add(aliasKey)
        neededAliasNames.push(aliasName)
      }
      if (sourceAlias) {
        for (const aliasName of collectDirectProjectedStageAliases(topSelectItems, sourceAlias)) {
          pushNeededAlias(aliasName)
        }
      }
      for (const aliasName of requiredAliases) {
        pushNeededAlias(aliasName)
      }
      let rewritten = sql
      if (/^FROM\s*\(/i.test(fromTail) && neededAliasNames.length > 0 && hasExplicitRequiredAliases) {
        const openParen = sql.indexOf('(', sql.length - fromTail.length)
        if (openParen >= 0) {
          let depth = 0
          let closeParen = -1
          for (let idx = openParen; idx < sql.length; idx += 1) {
            const ch = sql[idx]
            if (ch === '(') {
              depth += 1
              continue
            }
            if (ch !== ')') continue
            depth -= 1
            if (depth === 0) {
              closeParen = idx
              break
            }
          }
          if (closeParen > openParen + 1) {
            const innerSql = sql.slice(openParen + 1, closeParen).trim()
            const rewrittenInner = propagateRequiredProjectedAliasesThroughNestedSources(
              innerSql,
              neededAliasNames,
              remainingDepth - 1,
            )
            if (rewrittenInner && rewrittenInner !== innerSql) {
              rewritten = `${sql.slice(0, openParen + 1)}${rewrittenInner}${sql.slice(closeParen)}`
            }
          }
        }
      }
      if (!topHasWildcard && neededAliasNames.length > 0) {
        return appendRequiredProjectedAliasesFromImmediateSource(rewritten, neededAliasNames)
      }
      return rewritten
    }

    function selectItemsContainWildcard(selectItems: string[]): boolean {
      return selectItems.some((itemRaw) => {
        const item = String(itemRaw || '').trim()
        if (!item) return false
        return item === '*' || /^[A-Za-z_][A-Za-z0-9_]*\.\*$/.test(item) || /^"[^"]+"\.\*$/.test(item)
      })
    }

    function extractTopLevelSelectStage(sqlRaw: string): {
      selectItems: string[]
      fromTail: string
    } | null {
      const sql = String(sqlRaw || '').trim()
      if (!sql || !/^SELECT\b/i.test(sql)) return null
      const fromIdx = findTopLevelKeywordSql(sql, 'FROM')
      if (fromIdx <= 0) return null
      const selectItems = splitTopLevelSqlList(sql.slice('SELECT'.length, fromIdx).trim())
      const fromTail = sql.slice(fromIdx).trim()
      if (selectItems.length <= 0 || !fromTail) return null
      return { selectItems, fromTail }
    }

    function extractTopLevelFromSubqueryStage(sqlRaw: string): {
      sourceAlias: string
      selectItems: string[]
      fromTail: string
    } | null {
      const top = extractTopLevelSelectStage(sqlRaw)
      const fromTail = String(top?.fromTail || '').trim()
      if (!/^FROM\s*\(/i.test(fromTail)) {
        const directAliasMatch =
          /^FROM\s+("[^"]+"|[A-Za-z_][A-Za-z0-9_]*)(?:\s+(?:AS\s+)?("[^"]+"|[A-Za-z_][A-Za-z0-9_]*))?/i.exec(fromTail)
        const sourceAlias = String(directAliasMatch?.[2] || directAliasMatch?.[1] || '').trim()
        if (!sourceAlias || !Array.isArray(top?.selectItems) || top.selectItems.length <= 0) return null
        return {
          sourceAlias,
          selectItems: top.selectItems,
          fromTail,
        }
      }
      const openParen = fromTail.indexOf('(')
      if (openParen < 0) return null
      let depth = 0
      let closeParen = -1
      for (let idx = openParen; idx < fromTail.length; idx += 1) {
        const ch = fromTail[idx]
        if (ch === '(') {
          depth += 1
          continue
        }
        if (ch !== ')') continue
        depth -= 1
        if (depth === 0) {
          closeParen = idx
          break
        }
      }
      if (closeParen <= openParen + 1) return null
      const innerSql = fromTail.slice(openParen + 1, closeParen).trim()
      const innerStage = extractTopLevelSelectStage(innerSql)
      if (!innerStage) return null
      const aliasTail = fromTail.slice(closeParen + 1).trim()
      const aliasMatch = /^(?:AS\s+)?("[^"]+"|[A-Za-z_][A-Za-z0-9_]*)/i.exec(aliasTail)
      const sourceAlias = String(aliasMatch?.[1] || '').trim()
      if (!sourceAlias) return null
      return {
        sourceAlias,
        selectItems: innerStage.selectItems,
        fromTail: innerStage.fromTail,
      }
    }

    function factorRepeatedMonthDateTruncExprsInNestedSubquery(sqlRaw: string, remainingDepth = 6): string {
      const sql = String(sqlRaw || '').trim()
      if (!sql || remainingDepth <= 0) return sql
      const direct = factorRepeatedMonthDateTruncExprs(sql)
      if (direct && direct !== sql) return direct
      const top = extractTopLevelSelectStage(sql)
      const fromTail = String(top?.fromTail || '').trim()
      if (!/^FROM\s*\(/i.test(fromTail)) return sql
      const openParen = sql.indexOf('(', sql.length - fromTail.length)
      if (openParen < 0) return sql
      let depth = 0
      let closeParen = -1
      for (let idx = openParen; idx < sql.length; idx += 1) {
        const ch = sql[idx]
        if (ch === '(') {
          depth += 1
          continue
        }
        if (ch !== ')') continue
        depth -= 1
        if (depth === 0) {
          closeParen = idx
          break
        }
      }
      if (closeParen <= openParen + 1) return sql
      const innerSql = sql.slice(openParen + 1, closeParen).trim()
      const rewrittenInner = factorRepeatedMonthDateTruncExprsInNestedSubquery(innerSql, remainingDepth - 1)
      if (!rewrittenInner || rewrittenInner === innerSql) return sql
      return `${sql.slice(0, openParen + 1)}${rewrittenInner}${sql.slice(closeParen)}`
    }

    const rewriteTopLevelFromSubquery = (
      sqlRaw: string,
      replacementSql: string,
    ): string => {
      const sql = String(sqlRaw || '').trim()
      const top = extractTopLevelSelectStage(sql)
      const fromTail = String(top?.fromTail || '').trim()
      if (!/^FROM\s*\(/i.test(fromTail)) return sql
      const openParen = sql.indexOf('(', sql.length - fromTail.length)
      if (openParen < 0) return sql
      let depth = 0
      let closeParen = -1
      for (let idx = openParen; idx < sql.length; idx += 1) {
        const ch = sql[idx]
        if (ch === '(') {
          depth += 1
          continue
        }
        if (ch !== ')') continue
        depth -= 1
        if (depth === 0) {
          closeParen = idx
          break
        }
      }
      if (closeParen <= openParen + 1) return sql
      return `${sql.slice(0, openParen + 1)}${replacementSql}${sql.slice(closeParen)}`
    }

    const extractFromTailSourceName = (fromTailRaw: string): string | null => {
      const fromTail = String(fromTailRaw || '').trim()
      if (!fromTail) return null
      const match = /^FROM\s+("[^"]+"|[A-Za-z_][A-Za-z0-9_]*)/i.exec(fromTail)
      return String(match?.[1] || '').trim() || null
    }

    const extractNestedFromSubqueryFamilyKeys = (sqlRaw: string, maxDepth = 4): string[] => {
      const out: string[] = []
      let currentSql = String(sqlRaw || '').trim()
      for (let depth = 0; depth < maxDepth; depth += 1) {
        const stage = extractTopLevelFromSubqueryStage(currentSql)
        if (!stage) break
        const familyKey = canonicalizeSharedStageFromTailKey(stage.fromTail || '')
        if (!familyKey) break
        out.push(familyKey)
        currentSql = `SELECT ${stage.selectItems.join(', ')} ${stage.fromTail}`.trim()
      }
      return out
    }

    // Peel off DISTINCT-style outer wrappers of the form
    //   SELECT t."col" AS "col", t."col2" AS "col2", ... FROM (<inner>) AS t
    //   GROUP BY t."col", t."col2", ...
    // when `<inner>` already has its own GROUP BY (i.e. inner rows are already
    // distinct on the output columns) AND the outer projects the same full
    // column set that inner emits AND the outer GROUP BY covers that full
    // column set. In that shape the outer GROUP BY is a provable no-op but
    // DuckDB still hashes/sorts every row, which for Monthly-Comparison-style
    // aggregates produces 4 nested passthrough GROUP BYs wrapping the real
    // GROUPING SETS aggregate — each extra layer quadratically multiplies the
    // runtime of the `_agg_base_src` materialization. Collapsing them lets
    // DuckDB materialize the grouped aggregate in a single pass.
    //
    // `ANY_VALUE(t."col") AS "col"` items are also accepted as passthroughs
    // because when inner is already distinct on the outer GROUP BY keys there
    // is exactly one row per group, so `ANY_VALUE(x)` returns that row's value
    // of `x` — semantically identical to a plain `t."col"` reference. Tableau's
    // MNV pipeline emits multiple stacked layers of this shape around the core
    // GROUPING SETS aggregate (one per compilation phase), and each adds a
    // full hash+group pass in DuckDB.
    const tryPeelOnePassthroughGroupBy = (sqlRaw: string): string | null => {
      const current = String(sqlRaw || '').trim()
      if (!current) return null
      if (
        findTopLevelKeywordSql(current, 'HAVING') >= 0 ||
        findTopLevelKeywordSql(current, 'QUALIFY') >= 0 ||
        findTopLevelKeywordSql(current, 'ORDER BY') >= 0 ||
        findTopLevelKeywordSql(current, 'LIMIT') >= 0 ||
        findTopLevelKeywordSql(current, 'WINDOW') >= 0
      ) return null
      const groupByIdx = findTopLevelKeywordSql(current, 'GROUP BY')
      if (groupByIdx < 0) return null
      if (/\bGROUPING\s+SETS\b/i.test(current.slice(groupByIdx, groupByIdx + 40))) return null
      if (/\bOVER\s*\(/i.test(current.slice(0, groupByIdx))) return null
      const top = extractTopLevelSelectStage(current)
      const outerSelectItems = Array.isArray(top?.selectItems) ? top.selectItems : []
      if (outerSelectItems.length <= 0 || selectItemsContainWildcard(outerSelectItems)) return null
      const inner = extractTopLevelFromSubqueryStage(current)
      if (!inner) return null
      const sourceAlias = String(inner.sourceAlias || '').trim()
      const innerSelectItems = Array.isArray(inner.selectItems) ? inner.selectItems : []
      const innerFromTail = String(inner.fromTail || '').trim()
      if (!sourceAlias || innerSelectItems.length <= 0 || !innerFromTail) return null
      const outerSelectColNames: string[] = []
      const plainPassthroughRegex =
        /^(?:"[^"]+"\s*\.\s*|[A-Za-z_][A-Za-z0-9_]*\s*\.\s*)?(?:"([^"]+)"|([A-Za-z_][A-Za-z0-9_]*))\s+AS\s+(?:"([^"]+)"|([A-Za-z_][A-Za-z0-9_]*))$/i
      const anyValuePassthroughRegex =
        /^ANY_VALUE\s*\(\s*(?:"[^"]+"\s*\.\s*|[A-Za-z_][A-Za-z0-9_]*\s*\.\s*)?(?:"([^"]+)"|([A-Za-z_][A-Za-z0-9_]*))\s*\)\s+AS\s+(?:"([^"]+)"|([A-Za-z_][A-Za-z0-9_]*))$/i
      let sawAnyValuePassthrough = false
      for (const itemRaw of outerSelectItems) {
        const item = String(itemRaw || '').trim()
        const plain = plainPassthroughRegex.exec(item)
        if (plain) {
          const srcCol = String(plain[1] ?? plain[2] ?? '').trim()
          const dstCol = String(plain[3] ?? plain[4] ?? '').trim()
          if (!srcCol || !dstCol || srcCol.toLowerCase() !== dstCol.toLowerCase()) return null
          outerSelectColNames.push(srcCol.toLowerCase())
          continue
        }
        const anyVal = anyValuePassthroughRegex.exec(item)
        if (anyVal) {
          const srcCol = String(anyVal[1] ?? anyVal[2] ?? '').trim()
          const dstCol = String(anyVal[3] ?? anyVal[4] ?? '').trim()
          if (!srcCol || !dstCol || srcCol.toLowerCase() !== dstCol.toLowerCase()) return null
          outerSelectColNames.push(srcCol.toLowerCase())
          sawAnyValuePassthrough = true
          continue
        }
        return null
      }
      const innerOutputAliases = new Set<string>()
      for (const itemRaw of innerSelectItems) {
        const alias = extractSelectItemAlias(String(itemRaw || ''))
        if (!alias) return null
        const aliasName = alias.replace(/^"/, '').replace(/"$/, '')
        if (!aliasName) return null
        innerOutputAliases.add(aliasName.toLowerCase())
      }
      if (outerSelectColNames.length !== innerOutputAliases.size) return null
      for (const col of outerSelectColNames) {
        if (!innerOutputAliases.has(col)) return null
      }
      const endCandidates = [
        findTopLevelKeywordSql(current, 'HAVING'),
        findTopLevelKeywordSql(current, 'QUALIFY'),
        findTopLevelKeywordSql(current, 'ORDER BY'),
        findTopLevelKeywordSql(current, 'LIMIT'),
        findTopLevelKeywordSql(current, 'WINDOW'),
      ].filter((i) => i > groupByIdx)
      const endIdx = endCandidates.length ? Math.min(...endCandidates) : current.length
      const gbSection = current.slice(groupByIdx + 'GROUP BY'.length, endIdx).trim()
      const gbItems = splitTopLevelSqlList(gbSection).map((s) => s.trim()).filter(Boolean)
      if (gbItems.length <= 0) return null
      const gbRefRegex =
        /^(?:"[^"]+"\s*\.\s*|[A-Za-z_][A-Za-z0-9_]*\s*\.\s*)?(?:"([^"]+)"|([A-Za-z_][A-Za-z0-9_]*))$/i
      const outerSelectColSet = new Set(outerSelectColNames)
      const outerGroupByKeys = new Set<string>()
      for (const gbRaw of gbItems) {
        const m = gbRefRegex.exec(String(gbRaw || '').trim())
        if (!m) return null
        const col = String(m[1] ?? m[2] ?? '').trim().toLowerCase()
        if (!col || !outerSelectColSet.has(col)) return null
        outerGroupByKeys.add(col)
      }
      const innerSql = `SELECT ${innerSelectItems.join(', ')} ${innerFromTail}`.trim()
      if (!/\bGROUP\s+BY\b/i.test(innerSql)) return null
      // ANY_VALUE passthroughs are only safe when inner is provably distinct on
      // the outer GROUP BY keys. The plain-passthrough case relies only on
      // inner having SOME GROUP BY (because plain `t."col"` references in a
      // GROUP BY query are already deterministic), but ANY_VALUE + GROUP BY
      // would silently pick an arbitrary row if inner were NOT distinct on
      // outer's keys, so for that case require that some GROUP BY reachable
      // via pure passthrough / window wrappers covers the outer GROUP BY keys.
      if (sawAnyValuePassthrough) {
        if (!innerOutputIsDistinctOnKeys(innerSql, outerGroupByKeys)) return null
      }
      return innerSql
    }
    // Peel off pure alias wrappers of the form
    //   SELECT "col1" AS "col1", "col2" AS "col2", ... FROM (<inner>) AS t
    // with no GROUP BY / WHERE / HAVING / ORDER BY / LIMIT / WINDOW / OVER at
    // the outer level. These appear in the Monthly-Comparison plan between a
    // window-bearing SELECT and the next GROUP BY layer (e.g. the
    // `AS "__fg_win_base"` alias rename), and contribute nothing but an extra
    // scan. We preserve the inner SELECT order (and the outer alias is only
    // referenced positionally via the enclosing FROM subquery, so dropping it
    // is safe provided the consumer re-binds to the next alias below).
    const tryPeelOnePlainAliasWrapper = (sqlRaw: string): string | null => {
      const current = String(sqlRaw || '').trim()
      if (!current) return null
      if (
        findTopLevelKeywordSql(current, 'WHERE') >= 0 ||
        findTopLevelKeywordSql(current, 'GROUP BY') >= 0 ||
        findTopLevelKeywordSql(current, 'HAVING') >= 0 ||
        findTopLevelKeywordSql(current, 'QUALIFY') >= 0 ||
        findTopLevelKeywordSql(current, 'ORDER BY') >= 0 ||
        findTopLevelKeywordSql(current, 'LIMIT') >= 0 ||
        findTopLevelKeywordSql(current, 'WINDOW') >= 0
      ) return null
      const top = extractTopLevelSelectStage(current)
      const outerSelectItems = Array.isArray(top?.selectItems) ? top.selectItems : []
      if (outerSelectItems.length <= 0 || selectItemsContainWildcard(outerSelectItems)) return null
      // OVER must not appear anywhere in the outer SELECT list (that would
      // make the layer a window computation, not a pure alias wrapper).
      for (const itemRaw of outerSelectItems) {
        if (/\bOVER\s*\(/i.test(String(itemRaw || ''))) return null
      }
      const inner = extractTopLevelFromSubqueryStage(current)
      if (!inner) return null
      const innerSelectItems = Array.isArray(inner.selectItems) ? inner.selectItems : []
      const innerFromTail = String(inner.fromTail || '').trim()
      if (innerSelectItems.length <= 0 || !innerFromTail) return null
      if (outerSelectItems.length !== innerSelectItems.length) return null
      const plainPassthroughRegex =
        /^(?:"[^"]+"\s*\.\s*|[A-Za-z_][A-Za-z0-9_]*\s*\.\s*)?(?:"([^"]+)"|([A-Za-z_][A-Za-z0-9_]*))\s+AS\s+(?:"([^"]+)"|([A-Za-z_][A-Za-z0-9_]*))$/i
      for (let i = 0; i < outerSelectItems.length; i += 1) {
        const itemRaw = String(outerSelectItems[i] || '').trim()
        const m = plainPassthroughRegex.exec(itemRaw)
        if (!m) return null
        const srcCol = String(m[1] ?? m[2] ?? '').trim().toLowerCase()
        const dstCol = String(m[3] ?? m[4] ?? '').trim().toLowerCase()
        if (!srcCol || !dstCol || srcCol !== dstCol) return null
        const innerAlias = extractSelectItemAlias(String(innerSelectItems[i] || ''))
        if (!innerAlias) return null
        const innerAliasName = innerAlias.replace(/^"/, '').replace(/"$/, '').toLowerCase()
        if (innerAliasName !== srcCol) return null
      }
      return `SELECT ${innerSelectItems.join(', ')} ${innerFromTail}`.trim()
    }
    // Walk the inner SQL top-down (through pure passthrough and window-only
    // wrappers) looking for a GROUP BY whose keys cover `requiredKeys`. Used
    // to verify that an ANY_VALUE passthrough peel is semantically safe.
    // Returns true iff we can prove inner is distinct on `requiredKeys`.
    function innerOutputIsDistinctOnKeys(
      innerSqlRaw: string,
      requiredKeys: Set<string>,
      maxDepth = 6,
    ): boolean {
      let current = String(innerSqlRaw || '').trim()
      if (!current || requiredKeys.size <= 0) return false
      for (let depth = 0; depth < maxDepth; depth += 1) {
        if (!current) return false
        const groupByIdx = findTopLevelKeywordSql(current, 'GROUP BY')
        if (groupByIdx >= 0) {
          if (/\bGROUPING\s+SETS\b/i.test(current.slice(groupByIdx, groupByIdx + 40))) {
            // GROUPING SETS does not generally yield distinctness on a single
            // key set — bail out defensively.
            return false
          }
          const endCandidates = [
            findTopLevelKeywordSql(current, 'HAVING'),
            findTopLevelKeywordSql(current, 'QUALIFY'),
            findTopLevelKeywordSql(current, 'ORDER BY'),
            findTopLevelKeywordSql(current, 'LIMIT'),
            findTopLevelKeywordSql(current, 'WINDOW'),
          ].filter((i) => i > groupByIdx)
          const endIdx = endCandidates.length ? Math.min(...endCandidates) : current.length
          const gbSection = current.slice(groupByIdx + 'GROUP BY'.length, endIdx).trim()
          const gbItems = splitTopLevelSqlList(gbSection).map((s) => s.trim()).filter(Boolean)
          if (gbItems.length <= 0) return false
          const gbRefRegex =
            /^(?:"[^"]+"\s*\.\s*|[A-Za-z_][A-Za-z0-9_]*\s*\.\s*)?(?:"([^"]+)"|([A-Za-z_][A-Za-z0-9_]*))$/i
          const groupKeys = new Set<string>()
          for (const gbRaw of gbItems) {
            const m = gbRefRegex.exec(gbRaw)
            if (!m) return false
            const col = String(m[1] ?? m[2] ?? '').trim().toLowerCase()
            if (!col) return false
            groupKeys.add(col)
          }
          for (const k of requiredKeys) {
            if (!groupKeys.has(k)) return false
          }
          return true
        }
        // No top-level GROUP BY — descend through the next FROM subquery.
        // Pure passthrough and window-only layers preserve row count and
        // distinctness, so the required keys still apply to the inner query.
        const sub = extractTopLevelFromSubqueryStage(current)
        if (!sub) return false
        const subSelectItems = Array.isArray(sub.selectItems) ? sub.selectItems : []
        const subFromTail = String(sub.fromTail || '').trim()
        if (subSelectItems.length <= 0 || !subFromTail) return false
        // Ensure every required key is carried through the current projection
        // (either as a plain passthrough or a window expression with that
        // alias). If it isn't in the projection, we can't see through.
        const projectedAliases = new Set<string>()
        for (const itemRaw of subSelectItems) {
          const alias = extractSelectItemAlias(String(itemRaw || ''))
          if (!alias) continue
          projectedAliases.add(alias.replace(/^"/, '').replace(/"$/, '').toLowerCase())
        }
        for (const k of requiredKeys) {
          if (!projectedAliases.has(k)) return false
        }
        current = `SELECT ${subSelectItems.join(', ')} ${subFromTail}`.trim()
      }
      return false
    }
    // Normalize an SQL expression string for byte-level comparison: strip
    // comments, collapse whitespace, and lowercase unquoted identifiers. We
    // keep quoted identifiers case-sensitive (matches DuckDB semantics) and
    // preserve string literals verbatim.
    const normalizeSqlExpressionForMatch = (exprRaw: string): string => {
      const src = String(exprRaw || '').trim()
      if (!src) return ''
      let out = ''
      let i = 0
      let inStr = false
      let inIdent = false
      let prevIsWs = false
      while (i < src.length) {
        const ch = src[i]
        if (inStr) {
          out += ch
          if (ch === "'") {
            if (src[i + 1] === "'") { out += "'"; i += 2; continue }
            inStr = false
          }
          i += 1
          prevIsWs = false
          continue
        }
        if (inIdent) {
          out += ch
          if (ch === '"') {
            if (src[i + 1] === '"') { out += '"'; i += 2; continue }
            inIdent = false
          }
          i += 1
          prevIsWs = false
          continue
        }
        if (ch === "'") { out += ch; inStr = true; i += 1; prevIsWs = false; continue }
        if (ch === '"') { out += ch; inIdent = true; i += 1; prevIsWs = false; continue }
        if (/\s/.test(ch)) {
          if (!prevIsWs && out.length > 0) { out += ' '; prevIsWs = true }
          i += 1
          continue
        }
        // Drop redundant whitespace around parens/commas for more reliable
        // byte-level matching between SELECT items and GROUP BY expressions.
        if (ch === '(' || ch === ')' || ch === ',') {
          if (prevIsWs && out.length > 0 && out[out.length - 1] === ' ') {
            out = out.slice(0, -1)
          }
          out += ch
          prevIsWs = false
          i += 1
          continue
        }
        out += ch.toLowerCase()
        prevIsWs = false
        i += 1
      }
      return out.trim()
    }
    // Walk a single GROUPING SETS clause into a list of grouping sets, each
    // expressed as an array of raw member expressions. Returns null if the
    // GROUP BY isn't a GROUPING SETS clause, or if the clause is malformed.
    const parseGroupingSetsClause = (
      sqlRaw: string,
      groupByIdxRaw: number,
    ): string[][] | null => {
      const current = String(sqlRaw || '')
      if (!current) return null
      const tail = current.slice(groupByIdxRaw + 'GROUP BY'.length).trim()
      const gsMatch = /^GROUPING\s+SETS\s*\(/i.exec(tail)
      if (!gsMatch) return null
      const setsStart = gsMatch[0].length
      let depth = 1
      let inStr = false
      let inIdent = false
      let setsEnd = -1
      for (let i = setsStart; i < tail.length; i += 1) {
        const ch = tail[i]
        if (ch === "'") {
          if (inStr && tail[i + 1] === "'") { i += 1; continue }
          if (!inIdent) inStr = !inStr
          continue
        }
        if (!inStr && ch === '"') {
          if (inIdent && tail[i + 1] === '"') { i += 1; continue }
          inIdent = !inIdent
          continue
        }
        if (inStr || inIdent) continue
        if (ch === '(') depth += 1
        else if (ch === ')') {
          depth -= 1
          if (depth === 0) { setsEnd = i; break }
        }
      }
      if (setsEnd < 0) return null
      const afterSets = tail.slice(setsEnd + 1).trim()
      if (afterSets && !/^(?:HAVING|QUALIFY|ORDER\s+BY|LIMIT|WINDOW)\b/i.test(afterSets)) return null
      const setsBody = tail.slice(setsStart, setsEnd).trim()
      const rawSets = splitTopLevelSqlList(setsBody).map((s) => s.trim()).filter(Boolean)
      if (rawSets.length <= 0) return null
      const parsed: string[][] = []
      for (const setRaw of rawSets) {
        let trimmed = setRaw.trim()
        if (trimmed.startsWith('(') && trimmed.endsWith(')')) {
          trimmed = trimmed.slice(1, -1).trim()
        }
        const members = splitTopLevelSqlList(trimmed).map((s) => s.trim()).filter(Boolean)
        if (members.length <= 0) return null
        parsed.push(members)
      }
      return parsed
    }
    // Build an expression→alias map from a list of SELECT items. Keys are the
    // normalized expressions (see `normalizeSqlExpressionForMatch`), values are
    // lowercased unquoted alias names. Plain column references are also
    // indexed by their bare column name so that GROUPING SETS members written
    // as `t."col"` (or `t.col`) can be resolved against inner SELECT items of
    // the form `t."col" AS "col"`.
    const buildInnerExprToAliasMap = (
      selectItems: readonly string[],
    ): Map<string, string> => {
      const map = new Map<string, string>()
      const aliasTail =
        /\s+AS\s+(?:"((?:[^"]|"")+)"|([A-Za-z_][A-Za-z0-9_]*))\s*$/i
      for (const itemRaw of selectItems) {
        const item = String(itemRaw || '').trim()
        if (!item) continue
        const aliasMatch = aliasTail.exec(item)
        if (!aliasMatch) continue
        const aliasName = String(aliasMatch[1] ?? aliasMatch[2] ?? '')
          .replace(/""/g, '"')
          .trim()
          .toLowerCase()
        if (!aliasName) continue
        const exprRaw = item.slice(0, aliasMatch.index).trim()
        if (!exprRaw) continue
        const normalized = normalizeSqlExpressionForMatch(exprRaw)
        if (normalized) map.set(normalized, aliasName)
        // Also index by bare column name if expression is a plain column ref.
        const plainRef =
          /^(?:"[^"]+"\s*\.\s*|[A-Za-z_][A-Za-z0-9_]*\s*\.\s*)?(?:"([^"]+)"|([A-Za-z_][A-Za-z0-9_]*))$/i
            .exec(exprRaw)
        if (plainRef) {
          const colName = String(plainRef[1] ?? plainRef[2] ?? '').trim().toLowerCase()
          if (colName) map.set(colName, aliasName)
        }
      }
      return map
    }
    // Resolve a single GROUPING SETS member expression to an inner SELECT
    // alias (lowercased). Matches plain column references by bare column name
    // and complex expressions by normalized SQL text. Returns null if the
    // member cannot be matched against any inner SELECT alias.
    const resolveGroupingSetMemberToAlias = (
      memberRaw: string,
      exprToAlias: Map<string, string>,
    ): string | null => {
      const member = String(memberRaw || '').trim()
      if (!member) return null
      const plainRef =
        /^(?:"[^"]+"\s*\.\s*|[A-Za-z_][A-Za-z0-9_]*\s*\.\s*)?(?:"([^"]+)"|([A-Za-z_][A-Za-z0-9_]*))$/i
          .exec(member)
      if (plainRef) {
        const colName = String(plainRef[1] ?? plainRef[2] ?? '').trim().toLowerCase()
        if (colName && exprToAlias.has(colName)) return exprToAlias.get(colName) || null
        if (colName) return colName
      }
      const normalized = normalizeSqlExpressionForMatch(member)
      if (normalized && exprToAlias.has(normalized)) return exprToAlias.get(normalized) || null
      return null
    }
    // True when an expression is a COALESCE-with-string-sentinel guard, which
    // produces a NEVER-NULL value. We require this shape for any grouping set
    // member that isn't universal (doesn't appear in every set), because it's
    // what lets us prove two different grouping sets cannot collide on the
    // outer GROUP BY tuple via the rolled-up-dimension NULL pattern.
    const isCoalesceSentinelExpression = (exprRaw: string): boolean => {
      const src = String(exprRaw || '').trim()
      if (!src) return false
      return /^COALESCE\s*\(.+,\s*'[A-Za-z0-9_]+'\s*\)$/is.test(src)
    }
    // Prove that every inner row is uniquely identified by the values of
    // `outerKeys`. Walks through pass-through layers (plain `SELECT col AS
    // col, ... FROM (...) AS t` with no top-level GROUP BY/HAVING/QUALIFY/
    // ORDER BY/LIMIT/WINDOW/OVER), then at the first layer that has either
    // a GROUP BY or SELECT-level aggregates, proves distinctness as follows:
    //   - plain `GROUP BY <keys>`: inner rows are unique on <keys>, so
    //     `outerKeys ⊇ <keys>` implies distinctness on `outerKeys`.
    //   - `GROUP BY GROUPING SETS (S1, ..., Sn)`: each set's raw expressions
    //     are resolved to the enclosing SELECT's output aliases. Distinctness
    //     on `outerKeys` holds iff every resolved alias is in `outerKeys` AND
    //     every non-universal grouping-set member is a COALESCE-with-sentinel
    //     expression (NULL-collision safety — prevents two different grouping
    //     sets from producing the same outer-key tuple via rolled-up NULLs).
    //   - aggregates in SELECT with no top-level GROUP BY (DuckDB's implicit
    //     group-by-all-non-aggregate-columns semantics): inner rows are
    //     unique on the non-aggregate SELECT aliases, so `outerKeys ⊇ <those
    //     aliases>` implies distinctness.
    //
    // Pass-through layers must carry every `outerKey` through unchanged (as a
    // plain column reference aliased to itself) so that the key identity is
    // preserved. Renames would alter the key set and are rejected here.
    // Returns false on any ambiguity or unsupported shape.
    const outerKeysCoverInnerIdentity = (
      innerSqlRaw: string,
      outerKeys: Set<string>,
      maxDepth = 6,
    ): boolean => {
      let current = String(innerSqlRaw || '').trim()
      if (!current || outerKeys.size <= 0) return false
      const aggregateHeadRegex = /^(?:SUM|MAX|MIN|AVG|COUNT|ANY_VALUE|MEDIAN|STDDEV|STDDEV_SAMP|STDDEV_POP|VAR|VAR_SAMP|VAR_POP|FIRST|LAST|BIT_AND|BIT_OR|BIT_XOR|STRING_AGG|LIST|ARRAY_AGG)\s*\(/i
      for (let depth = 0; depth < maxDepth; depth += 1) {
        if (!current) return false
        if (
          findTopLevelKeywordSql(current, 'HAVING') >= 0 ||
          findTopLevelKeywordSql(current, 'QUALIFY') >= 0 ||
          findTopLevelKeywordSql(current, 'ORDER BY') >= 0 ||
          findTopLevelKeywordSql(current, 'LIMIT') >= 0 ||
          findTopLevelKeywordSql(current, 'WINDOW') >= 0
        ) return false
        const top = extractTopLevelSelectStage(current)
        const selectItems = Array.isArray(top?.selectItems) ? top.selectItems : []
        if (selectItems.length <= 0 || selectItemsContainWildcard(selectItems)) return false
        const groupByIdx = findTopLevelKeywordSql(current, 'GROUP BY')
        if (groupByIdx >= 0) {
          if (/\bOVER\s*\(/i.test(current.slice(0, groupByIdx))) return false
          const isGroupingSets = /\bGROUPING\s+SETS\b/i.test(current.slice(groupByIdx, groupByIdx + 40))
          if (isGroupingSets) {
            const sets = parseGroupingSetsClause(current, groupByIdx)
            if (!sets) return false
            const exprToAlias = buildInnerExprToAliasMap(selectItems)
            const memberAppearances = new Map<string, number>()
            const memberShape = new Map<string, { isCoalesceSentinel: boolean }>()
            const resolvedSets: string[][] = []
            for (const set of sets) {
              const resolved: string[] = []
              const dedup = new Set<string>()
              for (const memberRaw of set) {
                const alias = resolveGroupingSetMemberToAlias(memberRaw, exprToAlias)
                if (!alias) return false
                if (!outerKeys.has(alias)) return false
                if (!dedup.has(alias)) {
                  dedup.add(alias)
                  resolved.push(alias)
                  const shape = memberShape.get(alias)
                  const isSentinel = isCoalesceSentinelExpression(memberRaw)
                  if (!shape) {
                    memberShape.set(alias, { isCoalesceSentinel: isSentinel })
                  } else if (!isSentinel) {
                    // Conservative: if ANY appearance of the member is not a
                    // COALESCE-sentinel expression, downgrade the flag.
                    shape.isCoalesceSentinel = shape.isCoalesceSentinel && isSentinel
                  }
                  memberAppearances.set(alias, (memberAppearances.get(alias) || 0) + 1)
                }
              }
              resolvedSets.push(resolved)
            }
            const totalSets = resolvedSets.length
            for (const [alias, count] of memberAppearances) {
              if (count >= totalSets) continue
              const shape = memberShape.get(alias)
              if (!shape || !shape.isCoalesceSentinel) return false
            }
            return true
          }
          const endCandidates = [
            findTopLevelKeywordSql(current, 'HAVING'),
            findTopLevelKeywordSql(current, 'QUALIFY'),
            findTopLevelKeywordSql(current, 'ORDER BY'),
            findTopLevelKeywordSql(current, 'LIMIT'),
            findTopLevelKeywordSql(current, 'WINDOW'),
          ].filter((i) => i > groupByIdx)
          const endIdx = endCandidates.length ? Math.min(...endCandidates) : current.length
          const gbSection = current.slice(groupByIdx + 'GROUP BY'.length, endIdx).trim()
          const gbItems = splitTopLevelSqlList(gbSection).map((s) => s.trim()).filter(Boolean)
          if (gbItems.length <= 0) return false
          const gbRefRegex =
            /^(?:"[^"]+"\s*\.\s*|[A-Za-z_][A-Za-z0-9_]*\s*\.\s*)?(?:"([^"]+)"|([A-Za-z_][A-Za-z0-9_]*))$/i
          for (const gbRaw of gbItems) {
            const m = gbRefRegex.exec(gbRaw)
            if (!m) return false
            const col = String(m[1] ?? m[2] ?? '').trim().toLowerCase()
            if (!col) return false
            if (!outerKeys.has(col)) return false
          }
          return true
        }
        // No explicit GROUP BY — check for implicit-group-by shape: aggregates
        // in the SELECT list alongside plain passthrough columns. DuckDB's
        // lax-mode semantics treat this as `GROUP BY <all non-aggregate
        // SELECT columns>`, so the non-aggregate aliases must form a subset
        // of `outerKeys` for distinctness on `outerKeys`.
        let sawAggregate = false
        let allPassthroughOrAgg = true
        const nonAggAliases: string[] = []
        for (const itemRaw of selectItems) {
          const item = String(itemRaw || '').trim()
          if (!item) { allPassthroughOrAgg = false; break }
          if (/\bOVER\s*\(/i.test(item)) { allPassthroughOrAgg = false; break }
          if (aggregateHeadRegex.test(item)) {
            sawAggregate = true
            continue
          }
          const alias = extractSelectItemAlias(item)
          if (!alias) { allPassthroughOrAgg = false; break }
          const aliasName = alias.replace(/^"/, '').replace(/"$/, '').toLowerCase()
          if (!aliasName) { allPassthroughOrAgg = false; break }
          const plainRef =
            /^(?:"[^"]+"\s*\.\s*|[A-Za-z_][A-Za-z0-9_]*\s*\.\s*)?(?:"([^"]+)"|([A-Za-z_][A-Za-z0-9_]*))\s+AS\s+(?:"[^"]+"|[A-Za-z_][A-Za-z0-9_]*)$/i
              .exec(item)
          if (!plainRef) { allPassthroughOrAgg = false; break }
          nonAggAliases.push(aliasName)
        }
        if (sawAggregate && allPassthroughOrAgg) {
          for (const alias of nonAggAliases) {
            if (!outerKeys.has(alias)) return false
          }
          return true
        }
        // Descend through a pure passthrough layer: every outer key must be
        // carried through as an identity alias so it still refers to the
        // same value at the next level down.
        const sub = extractTopLevelFromSubqueryStage(current)
        if (!sub) return false
        const subSelectItems = Array.isArray(sub.selectItems) ? sub.selectItems : []
        const subFromTail = String(sub.fromTail || '').trim()
        if (subSelectItems.length <= 0 || !subFromTail) return false
        const identityAliases = new Set<string>()
        for (const itemRaw of selectItems) {
          const item = String(itemRaw || '').trim()
          const plainRef =
            /^(?:"[^"]+"\s*\.\s*|[A-Za-z_][A-Za-z0-9_]*\s*\.\s*)?(?:"([^"]+)"|([A-Za-z_][A-Za-z0-9_]*))\s+AS\s+(?:"([^"]+)"|([A-Za-z_][A-Za-z0-9_]*))$/i
              .exec(item)
          if (!plainRef) continue
          const src = String(plainRef[1] ?? plainRef[2] ?? '').trim().toLowerCase()
          const dst = String(plainRef[3] ?? plainRef[4] ?? '').trim().toLowerCase()
          if (src && dst && src === dst) identityAliases.add(src)
        }
        for (const k of outerKeys) {
          if (!identityAliases.has(k)) return false
        }
        current = `SELECT ${subSelectItems.join(', ')} ${subFromTail}`.trim()
      }
      return false
    }
    // Rewrite a wrapper of the shape
    //   SELECT <passthroughs>, AGG1(expr1) AS "alias1", AGG2(expr2) AS "alias2", ...
    //   FROM (<inner>) AS "t"
    //   GROUP BY <cols>
    // when <inner> is provably distinct on the outer GROUP BY keys and every
    // aggregate is a single-row-reducible form
    //   AGG(expr)   with AGG ∈ {MAX, MIN, SUM, AVG, ANY_VALUE}
    // (COUNT/COUNT-DISTINCT/FILTERed aggregates are excluded because they do
    // not reduce to `expr` on a single-row group). Because each outer group
    // contains exactly one inner row, every such aggregate reduces to `expr`,
    // so the outer GROUP BY can be dropped entirely and the outer is replaced
    // by a plain projection wrapper
    //   SELECT <passthroughs>, <expr1> AS "alias1", <expr2> AS "alias2", ...
    //   FROM (<inner>) AS "t"
    // which DuckDB can execute in a single scan. Monthly-Comparison-style
    // plans stack two or more of these aggregate-rename wrappers around the
    // core GROUPING SETS aggregate (one per __fg_agg*/__fg_ord* renaming
    // phase); each surviving layer forces a full hash+group pass over the
    // potentially millions of rows produced by GROUPING SETS, which is the
    // primary cause of the 180s `_agg_base_src` timeout on production sheets.
    //
    // Distinctness proof for the two supported inner shapes:
    //   - plain `GROUP BY <innerKeys>`: inner rows are unique on <innerKeys>,
    //     so if outer GROUP BY keys ⊇ <innerKeys>, each inner row is uniquely
    //     identified by its outer-GROUP-BY tuple.
    //   - `GROUP BY GROUPING SETS (S1, ..., Sn)`: each set S_i produces rows
    //     unique on S_i; across sets, rows differ in which members are NULL
    //     (the rolled-up dimensions). If outer GROUP BY ⊇ every S_i AND every
    //     rollup dimension is wrapped in `COALESCE(<expr>, '<sentinel>')`
    //     (never NULL in output), then NULL-patterns uniquely identify the
    //     originating grouping set, so each inner row is uniquely identified
    //     by its outer-GROUP-BY tuple as well. Universal-passthrough members
    //     (present in every set) are allowed to be raw column references.
    //
    // We intentionally avoid recursing here: the outer `collapsePassthroughGroupByWrappers`
    // loop re-invokes every peel helper until a fixpoint, which handles the
    // multi-layer stacked case safely.
    const tryPeelAggregateRenameGroupBy = (sqlRaw: string): string | null => {
      const current = String(sqlRaw || '').trim()
      if (!current) return null
      if (
        findTopLevelKeywordSql(current, 'HAVING') >= 0 ||
        findTopLevelKeywordSql(current, 'QUALIFY') >= 0 ||
        findTopLevelKeywordSql(current, 'ORDER BY') >= 0 ||
        findTopLevelKeywordSql(current, 'LIMIT') >= 0 ||
        findTopLevelKeywordSql(current, 'WINDOW') >= 0
      ) return null
      const groupByIdx = findTopLevelKeywordSql(current, 'GROUP BY')
      if (groupByIdx < 0) return null
      if (/\bGROUPING\s+SETS\b/i.test(current.slice(groupByIdx, groupByIdx + 40))) return null
      if (/\bOVER\s*\(/i.test(current.slice(0, groupByIdx))) return null
      const top = extractTopLevelSelectStage(current)
      const outerSelectItems = Array.isArray(top?.selectItems) ? top.selectItems : []
      if (outerSelectItems.length <= 0 || selectItemsContainWildcard(outerSelectItems)) return null
      const innerStage = extractTopLevelFromSubqueryStage(current)
      if (!innerStage) return null
      const sourceAlias = String(innerStage.sourceAlias || '').trim()
      const innerSelectItems = Array.isArray(innerStage.selectItems) ? innerStage.selectItems : []
      const innerFromTail = String(innerStage.fromTail || '').trim()
      if (!sourceAlias || innerSelectItems.length <= 0 || !innerFromTail) return null
      const endCandidates = [
        findTopLevelKeywordSql(current, 'HAVING'),
        findTopLevelKeywordSql(current, 'QUALIFY'),
        findTopLevelKeywordSql(current, 'ORDER BY'),
        findTopLevelKeywordSql(current, 'LIMIT'),
        findTopLevelKeywordSql(current, 'WINDOW'),
      ].filter((i) => i > groupByIdx)
      const endIdx = endCandidates.length ? Math.min(...endCandidates) : current.length
      const gbSection = current.slice(groupByIdx + 'GROUP BY'.length, endIdx).trim()
      const gbItems = splitTopLevelSqlList(gbSection).map((s) => s.trim()).filter(Boolean)
      if (gbItems.length <= 0) return null
      const gbRefRegex =
        /^(?:"[^"]+"\s*\.\s*|[A-Za-z_][A-Za-z0-9_]*\s*\.\s*)?(?:"([^"]+)"|([A-Za-z_][A-Za-z0-9_]*))$/i
      const outerGroupByKeys = new Set<string>()
      for (const gbRaw of gbItems) {
        const m = gbRefRegex.exec(String(gbRaw || '').trim())
        if (!m) return null
        const col = String(m[1] ?? m[2] ?? '').trim().toLowerCase()
        if (!col) return null
        outerGroupByKeys.add(col)
      }
      const innerSql = `SELECT ${innerSelectItems.join(', ')} ${innerFromTail}`.trim()
      if (!outerKeysCoverInnerIdentity(innerSql, outerGroupByKeys)) return null
      // Rewrite each outer SELECT item into a form that is valid without the
      // outer GROUP BY. Plain passthroughs and renames stay as-is; safe
      // aggregates (`AGG(expr) AS alias` with AGG ∈ {MAX,MIN,SUM,AVG,ANY_VALUE}
      // and no DISTINCT/FILTER) collapse to `expr AS alias` (single-row group
      // reduction); expressions containing OVER/window calls or COUNT/LIST/
      // STRING_AGG aggregates are rejected because they have no single-row
      // reduction. Complex non-aggregate expressions are kept verbatim.
      const plainPassthroughOrRenameRegex =
        /^(?:"[^"]+"\s*\.\s*|[A-Za-z_][A-Za-z0-9_]*\s*\.\s*)?(?:"([^"]+)"|([A-Za-z_][A-Za-z0-9_]*))\s+AS\s+(?:"([^"]+)"|([A-Za-z_][A-Za-z0-9_]*))$/i
      const safeAggregatePrefixRegex = /^(MAX|MIN|SUM|AVG|ANY_VALUE)\s*\(/i
      const unsafeAggregatePrefixRegex = /^(COUNT|LIST|ARRAY_AGG|STRING_AGG|MEDIAN|STDDEV|STDDEV_SAMP|STDDEV_POP|VAR|VAR_SAMP|VAR_POP|FIRST|LAST|BIT_AND|BIT_OR|BIT_XOR|BOOL_AND|BOOL_OR|QUANTILE(?:_CONT|_DISC)?)\s*\(/i
      const aliasTailRegex = /^AS\s+(?:"([^"]+)"|([A-Za-z_][A-Za-z0-9_]*))$/i
      const rewrittenItems: string[] = []
      for (const itemRaw of outerSelectItems) {
        const item = String(itemRaw || '').trim()
        if (!item) return null
        if (/\bOVER\s*\(/i.test(item)) return null
        if (unsafeAggregatePrefixRegex.test(item)) return null
        const plain = plainPassthroughOrRenameRegex.exec(item)
        if (plain) {
          rewrittenItems.push(item)
          continue
        }
        const aggMatch = safeAggregatePrefixRegex.exec(item)
        if (aggMatch) {
          const aggHeadLen = aggMatch[0].length
          if (/^(?:DISTINCT|ALL)\b/i.test(item.slice(aggHeadLen))) return null
          const openParen = aggHeadLen - 1
          if (item[openParen] !== '(') return null
          let depth = 1
          let closeParen = -1
          let inString = false
          for (let i = openParen + 1; i < item.length; i += 1) {
            const ch = item[i]
            if (inString) {
              if (ch === "'") {
                if (item[i + 1] === "'") { i += 1; continue }
                inString = false
              }
              continue
            }
            if (ch === "'") { inString = true; continue }
            if (ch === '(') depth += 1
            else if (ch === ')') {
              depth -= 1
              if (depth === 0) { closeParen = i; break }
            }
          }
          if (closeParen < 0) return null
          const innerExpr = item.slice(openParen + 1, closeParen).trim()
          if (!innerExpr) return null
          const afterClose = item.slice(closeParen + 1).trim()
          if (/^FILTER\b/i.test(afterClose)) return null
          const aliasMatch = aliasTailRegex.exec(afterClose)
          if (!aliasMatch) return null
          const aliasName = String(aliasMatch[1] ?? aliasMatch[2] ?? '').trim()
          if (!aliasName) return null
          const quotedAlias = `"${aliasName.replace(/"/g, '""')}"`
          rewrittenItems.push(`${innerExpr} AS ${quotedAlias}`)
          continue
        }
        // Complex non-aggregate expression (e.g. CASE/COALESCE). Require a
        // trailing alias so the outer caller's column references keep their
        // bindings after the peel.
        const aliasMatch = /\s+AS\s+(?:"[^"]+"|[A-Za-z_][A-Za-z0-9_]*)\s*$/i.exec(item)
        if (!aliasMatch) return null
        rewrittenItems.push(item)
      }
      return `SELECT ${rewrittenItems.join(', ')} FROM (${innerSql}) AS ${sourceAlias}`.trim()
    }

    const collapsePassthroughGroupByWrappers = (sqlRaw: string, maxDepth = 10): string => {
      // Peel wrappers at the TOP level first, then recurse into the remaining
      // top-level FROM subquery to peel any wrappers that sit beneath a
      // window-bearing or qualifier-stripping SELECT. Monthly-Comparison-style
      // queries stack up to 5 such wrappers around the core GROUPING SETS
      // aggregate — a mix of plain-passthrough GROUP BYs, ANY_VALUE-passthrough
      // GROUP BYs, aggregate-rename GROUP BYs (MAX/SUM/AVG/ANY_VALUE over a
      // single-row group that just renames inner columns), and pure alias
      // wrappers (no GROUP BY). Each wrapper that survives to DuckDB adds a
      // full hash+group pass over the potentially millions of rows produced
      // by the GROUPING SETS layer, turning a ~seconds aggregate into a
      // 180s+ timeout on production sheets.
      let current = String(sqlRaw || '').trim()
      if (!current || maxDepth <= 0) return current
      for (let depth = 0; depth < maxDepth; depth += 1) {
        let peeled = tryPeelOnePassthroughGroupBy(current)
        if (!peeled || peeled === current) peeled = tryPeelOnePlainAliasWrapper(current)
        if (!peeled || peeled === current) peeled = tryPeelAggregateRenameGroupBy(current)
        if (!peeled || peeled === current) break
        current = peeled
      }
      const top = extractTopLevelSelectStage(current)
      const inner = extractTopLevelFromSubqueryStage(current)
      if (!top || !inner) return current
      const innerSelectItems = Array.isArray(inner.selectItems) ? inner.selectItems : []
      const innerFromTail = String(inner.fromTail || '').trim()
      if (innerSelectItems.length <= 0 || !innerFromTail) return current
      const innerSql = `SELECT ${innerSelectItems.join(', ')} ${innerFromTail}`.trim()
      if (!innerSql) return current
      // Descend into the inner FROM subquery with a strictly smaller depth
      // budget so malformed or deeply-pathological SQL cannot cause unbounded
      // recursion. The outer peel already consumed some depth budget on this
      // level; subtracting 1 is sufficient because each recursion peels a
      // fresh set of wrappers off the new top level before recursing again.
      const collapsedInner = collapsePassthroughGroupByWrappers(innerSql, maxDepth - 1)
      if (!collapsedInner || collapsedInner === innerSql) return current
      const rewritten = rewriteTopLevelFromSubquery(current, collapsedInner)
      if (!rewritten || rewritten === current) return current
      return rewritten
    }

    // Linear-time structural splitter that separates a window-free grouped
    // aggregate from its enclosing window SELECT. Walks down the top-level
    // FROM-subquery chain looking for the DEEPEST layer whose immediate FROM
    // subquery is window-free AND has a GROUP BY. The aggregate+window SQL
    // for Monthly-Comparison-style sheets produces a nested shape:
    //   SELECT ...windows... FROM (
    //     SELECT ...ANY_VALUE... OVER... FROM (
    //       SELECT ...aggregates... FROM <base>
    //       GROUP BY GROUPING SETS (...)
    //     ) AS t
    //   ) AS t
    // We extract that innermost grouped SELECT as `_agg_base_src` and rebind
    // its immediate parent's FROM to the materialized source table. Running
    // this split up front prevents DuckDB from OOM'ing when materializing the
    // entire wrapped aggregate+windows in a single CREATE TEMP TABLE statement.
    //
    // After the split, we further apply `collapsePassthroughGroupByWrappers`
    // to the extracted `sourceSql` to peel off redundant DISTINCT-style outer
    // GROUP BYs. Monthly-Comparison-style queries stack 3-4 of these around
    // the core GROUPING SETS aggregate, and each adds a full hash+group pass
    // in DuckDB (O(n) each), turning an otherwise fast aggregate into a
    // 180s+ timeout.
    //
    // Declared at the `tryBuildMeasureNamesValuesQuery` closure level so both
    // the chunked `stagedChunkPlanEnabled` branch and the non-chunked
    // `hasCompiledCalcDimPartitionSignal` branch can invoke it.
    const cheapSplitAggBaseWindowSource = (
      materializedSqlRaw: string,
      sourceTableName: string,
    ): { sourceSql: string; finalSql: string } | null => {
      const quotedSourceRef = quoteIdent(sourceTableName)
      const recurse = (
        sqlRaw: string,
        remainingDepth: number,
      ): { sourceSql: string; finalSql: string } | null => {
        if (remainingDepth <= 0) return null
        const sqlOriginal = String(sqlRaw || '').trim()
        const sql =
          /\bGROUP\s+BY\b/i.test(sqlOriginal)
            ? (finalizeSql(collapsePassthroughGroupByWrappers(sqlOriginal)) || sqlOriginal)
            : sqlOriginal
        if (!sql || !/\bOVER\s*\(/i.test(sql)) return null
        const inner = extractTopLevelFromSubqueryStage(sql)
        if (!inner) return null
        const sourceAlias = String(inner.sourceAlias || '').trim()
        const innerSelectItems = Array.isArray(inner.selectItems) ? inner.selectItems : []
        const innerFromTail = String(inner.fromTail || '').trim()
        if (!sourceAlias || innerSelectItems.length <= 0 || !innerFromTail) return null
        const innerSql = `SELECT ${innerSelectItems.join(', ')} ${innerFromTail}`
        const innerHasOver = /\bOVER\s*\(/i.test(innerSql)
        const innerHasGroupBy = /\bGROUP\s+BY\b/i.test(innerSql)
        if (!innerHasOver && innerHasGroupBy) {
          const reboundFinalSql = rewriteTopLevelFromSubquery(
            sql,
            `SELECT * FROM ${quotedSourceRef} AS ${sourceAlias}`,
          )
          if (!reboundFinalSql || reboundFinalSql === sql) return null
          const collapsedSourceSql = collapsePassthroughGroupByWrappers(innerSql)
          return { sourceSql: collapsedSourceSql || innerSql, finalSql: reboundFinalSql }
        }
        if (!innerHasOver) return null
        const rec = recurse(innerSql, remainingDepth - 1)
        if (!rec) return null
        const reboundFinalSql = rewriteTopLevelFromSubquery(sql, rec.finalSql)
        if (!reboundFinalSql || reboundFinalSql === sql) return null
        return { sourceSql: rec.sourceSql, finalSql: reboundFinalSql }
      }
      return recurse(String(materializedSqlRaw || '').trim(), 8)
    }

    const normalizeMeasureExprShape = (exprRaw: string): string => String(exprRaw || '')
      .replace(/\/\*__fg_[^*]+\*\//gi, '/*__fg_token__*/')
      .replace(/"__fg_[^"]+"/gi, '"__fg_id__"')
      .replace(/"[A-Za-z_][^"]*"/g, '"id"')
      .replace(/\b\d+(?:\.\d+)?\b/g, 'N')
      .replace(/\s+/g, ' ')
      .trim()

    function foldConstantMonthAnchorExprs(sqlIn: string): string {
      const sql = String(sqlIn || '')
      if (!sql) return sql
      const monthStartLiteralForEpochMs = (epochMsRaw: string): string | null => {
        const epochMs = Number(epochMsRaw)
        if (!Number.isFinite(epochMs)) return null
        const dt = new Date(epochMs)
        if (!Number.isFinite(dt.getTime())) return null
        const yyyy = dt.getUTCFullYear()
        const mm = String(dt.getUTCMonth() + 1).padStart(2, '0')
        return `TIMESTAMP '${yyyy}-${mm}-01 00:00:00'`
      }
      return sql.replace(
        /DATE_TRUNC\(\s*'month'\s*,\s*TRY_CAST\(\s*CAST\(\s*\(\s*CAST\(\s*TO_TIMESTAMP\(\s*\(\s*CAST\(\s*(-?\d+(?:\.\d+)?)\s+AS\s+DOUBLE\)\s*\/\s*1000\.0\s*\)\s*\)\s+AS\s+TIMESTAMP\)\s*\)\s+AS\s+DATE\)\s+AS\s+TIMESTAMP\)\s*\)\s*\)/gi,
        (match, epochMsRaw) => monthStartLiteralForEpochMs(String(epochMsRaw || '')) || match,
      )
    }

    function foldTemplateMonthAnchorParamExprs(sqlIn: string): string {
      const sql = String(sqlIn || '')
      if (!sql) return sql
      const renderMonthStartPlaceholder = (safeKeyRaw: string, kind = 'timestamp_month_start'): string =>
        `CAST(/*__FG_PARAM:${String(safeKeyRaw || '').trim()}:${kind}__*/ NULL AS TIMESTAMP)`
      return sql.replace(
        /DATE_TRUNC\(\s*'month'\s*,\s*TRY_CAST\(\s*(?:CAST\(\s*)?\/\*__FG_PARAM:([A-Za-z0-9_]+):(date|timestamp)__\*\/\s*NULL\s*(?:AS\s+(?:DATE|TIMESTAMP)\s*\))?\s+AS\s+TIMESTAMP\s*\)\s*\)/gi,
        (_match, safeKey: string) => renderMonthStartPlaceholder(safeKey),
      )
    }

    function foldTemplateMonthAnchorOffsetExprs(sqlIn: string): string {
      const sql = String(sqlIn || '')
      if (!sql) return sql
      const renderMonthStartPlaceholder = (safeKeyRaw: string, kind = 'timestamp_month_start'): string =>
        `CAST(/*__FG_PARAM:${String(safeKeyRaw || '').trim()}:${kind}__*/ NULL AS TIMESTAMP)`
      return sql.replace(
        /(?:CAST\(\s*)?\/\*__FG_PARAM:([A-Za-z0-9_]+):timestamp_month_start__\*\/\s*NULL\s*(?:AS\s+TIMESTAMP\s*\))?((?:\s*\))+)\s*\+\s*\(\s*(?:\(\s*)?([+-]?)\s*(\d+)\s*(?:\)\s*)?\)\s*\*\s*INTERVAL 1 MONTH/gi,
        (_match, safeKey: string, closingParensRaw: string, signRaw: string, magnitudeRaw: string) => {
          const magnitude = Math.max(0, Math.floor(Number(magnitudeRaw) || 0))
          const closingParens = String(closingParensRaw || '').trim() || ')'
          if (magnitude <= 0) {
            return `${renderMonthStartPlaceholder(safeKey)}${closingParens}`
          }
          const sign = String(signRaw || '').trim() === '-' ? 'neg' : 'pos'
          return `${renderMonthStartPlaceholder(safeKey, `timestamp_month_start_offset_${sign}_${magnitude}`)}${closingParens}`
        },
      )
    }

    function foldTemplateMonthAnchorDayOffsetExprs(sqlIn: string): string {
      const sql = String(sqlIn || '')
      if (!sql) return sql
      const renderMonthStartPlaceholder = (safeKeyRaw: string, kindRaw: string): string =>
        `CAST(/*__FG_PARAM:${String(safeKeyRaw || '').trim()}:${String(kindRaw || '').trim()}__*/ NULL AS TIMESTAMP)`
      return sql
        .replace(
          /(?:CAST\(\s*)?\/\*__FG_PARAM:([A-Za-z0-9_]+):(timestamp_month_start(?:_offset_(?:neg|pos)_\d+)?)__\*\/\s*NULL\s*(?:AS\s+TIMESTAMP\s*\))?((?:\s*\))+)\s*\+\s*\(\s*(?:\(\s*)?([+-]?)\s*(\d+)\s*(?:\)\s*)?\)\s*\*\s*INTERVAL 1 DAY/gi,
          (_match, safeKey: string, baseTypeRaw: string, closingParensRaw: string, signRaw: string, magnitudeRaw: string) => {
            const magnitude = Math.max(0, Math.floor(Number(magnitudeRaw) || 0))
            const closingParens = String(closingParensRaw || '').trim() || ')'
            if (magnitude <= 0) {
              return `${renderMonthStartPlaceholder(safeKey, baseTypeRaw)}${closingParens}`
            }
            const sign = String(signRaw || '').trim() === '-' ? 'neg' : 'pos'
            return `${renderMonthStartPlaceholder(safeKey, `${String(baseTypeRaw || '').trim()}_day_offset_${sign}_${magnitude}`)}${closingParens}`
          },
        )
        .replace(
          /(?:CAST\(\s*)?\/\*__FG_PARAM:([A-Za-z0-9_]+):(timestamp_month_start(?:_offset_(?:neg|pos)_\d+)?)__\*\/\s*NULL\s*(?:AS\s+TIMESTAMP\s*\))?((?:\s*\))+)\s*([+-])\s*\(\s*TRY_CAST\(\s*(\d+)\s+AS\s+DOUBLE\s*\)\s*\*\s*INTERVAL 1 DAY\s*\)/gi,
          (_match, safeKey: string, baseTypeRaw: string, closingParensRaw: string, opRaw: string, magnitudeRaw: string) => {
          const magnitude = Math.max(0, Math.floor(Number(magnitudeRaw) || 0))
            const closingParens = String(closingParensRaw || '').trim() || ')'
          if (magnitude <= 0) {
              return `${renderMonthStartPlaceholder(safeKey, baseTypeRaw)}${closingParens}`
          }
          const sign = String(opRaw || '').trim() === '-' ? 'neg' : 'pos'
            return `${renderMonthStartPlaceholder(safeKey, `${String(baseTypeRaw || '').trim()}_day_offset_${sign}_${magnitude}`)}${closingParens}`
          },
        )
    }

    function foldMonthlyBeginDateAliasExprs(sqlIn: string): string {
      const sql = String(sqlIn || '')
      if (!sql) return sql
      const sourceAliases = Array.from(
        new Set(
          [...sql.matchAll(/((?:"[^"]+"|[A-Za-z_][A-Za-z0-9_]*))\s*\.\s*"Time Period Begin Dates \(Monthly\)"/g)]
            .map((match) => String(match[1] || '').trim())
            .filter(Boolean),
        ),
      )
      if (sourceAliases.length <= 0) return sql
      let out = sql
      for (const sourceAlias of sourceAliases) {
        const escapedAlias = sourceAlias.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        out = out.replace(
          new RegExp(
            `DATE_TRUNC\\(\\s*'month'\\s*,\\s*TRY_CAST\\(\\s*${escapedAlias}\\s*\\.\\s*"reportenddate"\\s+AS\\s+TIMESTAMP\\s*\\)\\s*\\)`,
            'gi',
          ),
          `TRY_CAST(${sourceAlias}."Time Period Begin Dates (Monthly)" AS TIMESTAMP)`,
        )
      }
      return out
    }

    function liftRawSourceReportEndMonthExpr(sqlIn: string): string {
      const sql = String(sqlIn || '')
      if (!sql) return sql
      const sourceMarker = `FROM ${parquetRef} AS ${baseRef}`
      const sourcePos = sql.lastIndexOf(sourceMarker)
      if (sourcePos < 0 && !new RegExp(`\\bAS\\s+${baseRef.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i').test(sql)) return sql
      let balance = 0
      let openParen = -1
      for (let idx = Math.max(0, sourcePos >= 0 ? sourcePos - 1 : sql.length - 1); idx >= 0; idx -= 1) {
        const ch = sql[idx]
        if (ch === ')') {
          balance += 1
          continue
        }
        if (ch !== '(') continue
        if (balance > 0) {
          balance -= 1
          continue
        }
        const candidate = sql.slice(idx + 1).trimStart()
        if (!/^SELECT\b/i.test(candidate)) continue
        openParen = idx
        break
      }
      if (openParen < 0) return sql
      let depth = 0
      let closeParen = -1
      for (let idx = openParen + 1; idx < sql.length; idx += 1) {
        const ch = sql[idx]
        if (ch === '(') {
          depth += 1
          continue
        }
        if (ch !== ')') continue
        if (depth > 0) {
          depth -= 1
          continue
        }
        closeParen = idx
        break
      }
      if (closeParen < 0 || closeParen <= openParen + 1) return sql
      const innerSql = sql.slice(openParen + 1, closeParen)
      const targetExpr = `DATE_TRUNC('month', TRY_CAST(${baseRef}.\"reportenddate\" AS TIMESTAMP))`
      const occurrences = innerSql.split(targetExpr).length - 1
      if (occurrences < 2) return sql
      const innerStage = extractTopLevelSelectStage(innerSql)
      const innerFromTail = String(innerStage?.fromTail || '').trim()
      if (!innerFromTail) return sql
      if (
        findTopLevelKeywordSql(innerFromTail, 'WHERE') >= 0
        || findTopLevelKeywordSql(innerFromTail, 'GROUP BY') >= 0
        || findTopLevelKeywordSql(innerFromTail, 'HAVING') >= 0
        || findTopLevelKeywordSql(innerFromTail, 'WINDOW') >= 0
        || findTopLevelKeywordSql(innerFromTail, 'ORDER BY') >= 0
        || findTopLevelKeywordSql(innerFromTail, 'LIMIT') >= 0
        || findTopLevelKeywordSql(innerFromTail, 'UNION') >= 0
      ) return sql
      const liftedAlias = '__fg_reportenddate_month'
      const replacedInner = innerSql.split(targetExpr).join(`${baseRef}.${quoteIdent(liftedAlias)}`)
      const replacedStage = extractTopLevelSelectStage(replacedInner)
      const replacedFromTail = String(replacedStage?.fromTail || '').trim()
      if (!replacedFromTail) return sql
      const liftedSource = `FROM (SELECT ${baseRef}.*, ${targetExpr} AS ${quoteIdent(liftedAlias)} ${replacedFromTail}) AS ${baseRef}`
      const fromIdx = findTopLevelKeywordSql(replacedInner, 'FROM')
      if (fromIdx <= 0) return sql
      const nextInner = `${replacedInner.slice(0, fromIdx)}${liftedSource}`
      return `${sql.slice(0, openParen + 1)}${nextInner}${sql.slice(closeParen)}`
    }

    function liftRawSourceReportEndMonthExprInNestedSubquery(sqlRaw: string, remainingDepth = 6): string {
      const sql = String(sqlRaw || '').trim()
      if (!sql || remainingDepth <= 0) return sql
      const direct = liftRawSourceReportEndMonthExpr(sql)
      if (direct && direct !== sql) return direct
      const top = extractTopLevelSelectStage(sql)
      const fromTail = String(top?.fromTail || '').trim()
      if (!/^FROM\s*\(/i.test(fromTail)) return sql
      const openParen = sql.indexOf('(', sql.length - fromTail.length)
      if (openParen < 0) return sql
      let depth = 0
      let closeParen = -1
      for (let idx = openParen; idx < sql.length; idx += 1) {
        const ch = sql[idx]
        if (ch === '(') {
          depth += 1
          continue
        }
        if (ch !== ')') continue
        depth -= 1
        if (depth === 0) {
          closeParen = idx
          break
        }
      }
      if (closeParen <= openParen + 1) return sql
      const innerSql = sql.slice(openParen + 1, closeParen).trim()
      const rewrittenInner = liftRawSourceReportEndMonthExprInNestedSubquery(innerSql, remainingDepth - 1)
      if (!rewrittenInner || rewrittenInner === innerSql) return sql
      return `${sql.slice(0, openParen + 1)}${rewrittenInner}${sql.slice(closeParen)}`
    }

    function foldLiftedReportEndMonthAliasExprs(sqlIn: string): string {
      const sql = String(sqlIn || '')
      if (!sql) return sql
      const sourceAliases = Array.from(
        new Set(
          [...sql.matchAll(/((?:"[^"]+"|[A-Za-z_][A-Za-z0-9_]*))\s*\.\s*"__fg_reportenddate_month"/g)]
            .map((match) => String(match[1] || '').trim())
            .filter(Boolean),
        ),
      )
      if (sourceAliases.length <= 0) return sql
      let out = sql
      for (const sourceAlias of sourceAliases) {
        const escapedAlias = sourceAlias.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        const pattern = new RegExp(
          `DATE_TRUNC\\(\\s*'month'\\s*,\\s*TRY_CAST\\(\\s*${escapedAlias}\\s*\\.\\s*"reportenddate"\\s+AS\\s+TIMESTAMP\\s*\\)\\s*\\)`,
          'gi',
        )
        out = out.replace(pattern, (match: string, offset: number, full: string) => {
          const tail = String(full.slice(offset + match.length, offset + match.length + 80) || '')
          if (/^\s+AS\s+"__fg_reportenddate_month"/i.test(tail)) return match
          return `${sourceAlias}."__fg_reportenddate_month"`
        })
      }
      return out
    }

    function factorRepeatedMonthDateTruncExprs(sqlIn: string): string {
      const findBalancedCallClose = (src: string, openParenIdx: number): number => {
        let depth = 0
        let inStr = false
        let inIdent = false
        for (let idx = openParenIdx; idx < src.length; idx += 1) {
          const ch = src[idx]
          if (inStr) {
            if (ch === "'" && src[idx + 1] === "'") {
              idx += 1
              continue
            }
            if (ch === "'") inStr = false
            continue
          }
          if (inIdent) {
            if (ch === '"') inIdent = false
            continue
          }
          if (ch === "'") {
            inStr = true
            continue
          }
          if (ch === '"') {
            inIdent = true
            continue
          }
          if (ch === '(') {
            depth += 1
            continue
          }
          if (ch === ')') {
            depth -= 1
            if (depth === 0) return idx
          }
        }
        return -1
      }
      const replaceExactOrWhitespaceStable = (sqlRaw: string, rawNeedle: string, replacement: string): string => {
        const source = String(sqlRaw ?? '')
        const needle = String(rawNeedle ?? '').trim()
        if (!source || !needle) return source
        if (source.includes(needle)) return source.split(needle).join(replacement)
        const escaped = needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '\\s+')
        if (!escaped) return source
        try {
          return source.replace(new RegExp(escaped, 'g'), replacement)
        } catch {
          return source
        }
      }
      const extractRepeatedExprs = (exprs: string[], aliasSet: Set<string>): Array<{ expr: string; alias: string }> => {
        const counts = new Map<string, number>()
        const exprByKey = new Map<string, string>()
        for (const expr of exprs) {
          const src = String(expr || '')
          const upper = src.toUpperCase()
          for (let idx = 0; idx < src.length; idx += 1) {
            if (upper.slice(idx, idx + 'DATE_TRUNC'.length) !== 'DATE_TRUNC') continue
            const before = src[idx - 1]
            if (before && /[A-Za-z0-9_]/.test(before)) continue
            let openParenIdx = idx + 'DATE_TRUNC'.length
            while (openParenIdx < src.length && /\s/.test(src[openParenIdx] || '')) openParenIdx += 1
            if (src[openParenIdx] !== '(') continue
            let argStart = openParenIdx + 1
            while (argStart < src.length && /\s/.test(src[argStart] || '')) argStart += 1
            if (src.slice(argStart, argStart + 7).toLowerCase() !== "'month'") continue
            const closeParenIdx = findBalancedCallClose(src, openParenIdx)
            if (closeParenIdx <= openParenIdx) continue
            const candidateExpr = src.slice(idx, closeParenIdx + 1).trim()
            if (candidateExpr.length < 24) {
              idx = closeParenIdx
              continue
            }
            const key = normalizeSqlExpr(candidateExpr)
            if (key) {
              exprByKey.set(key, candidateExpr)
              counts.set(key, (counts.get(key) ?? 0) + 1)
            }
            idx = closeParenIdx
          }
        }
        let aliasIdx = 0
        return Array.from(counts.entries())
          .filter(([, count]) => count >= 2)
          .map(([key]) => String(exprByKey.get(key) || '').trim())
          .filter(Boolean)
          .sort((left, right) => right.length - left.length)
          .map((expr) => {
            let alias = quoteIdent(`__fg_expr_${aliasIdx++}`)
            while (aliasSet.has(alias.toLowerCase())) alias = quoteIdent(`__fg_expr_${aliasIdx++}`)
            aliasSet.add(alias.toLowerCase())
            return { expr, alias }
          })
      }
      const sql = String(sqlIn || '').trim()
      const top = extractTopLevelSelectStage(sql)
      const innerStage = extractTopLevelFromSubqueryStage(sql)
      const sourceAlias = String(innerStage?.sourceAlias || '').trim()
      const innerSelectItems = Array.isArray(innerStage?.selectItems) ? innerStage.selectItems : []
      const fromTail = String(top?.fromTail || '').trim()
      const selectItems = Array.isArray(top?.selectItems) ? top!.selectItems : []
      if (!sql || !sourceAlias || !fromTail || selectItems.length <= 1) return sql
      if (!/^FROM\s*\(/i.test(fromTail)) return sql
      if (selectItemsContainWildcard(selectItems)) return sql
      if (innerSelectItems.length <= 0 || selectItemsContainWildcard(innerSelectItems)) return sql
      const parseProjectedItem = (item: string, idx: number): { expr: string; alias: string } => {
        const trimmed = String(item || '').trim()
        const asMatch = trimmed.match(/\s+AS\s+("[^"]+"|[A-Za-z_][\w$]*)\s*$/i)
        if (asMatch && asMatch.index !== undefined) {
          return { expr: trimmed.slice(0, asMatch.index).trim(), alias: asMatch[1] }
        }
        const tailMatch = trimmed.match(/\s+("[^"]+"|[A-Za-z_][\w$]*)\s*$/)
        if (tailMatch && tailMatch.index !== undefined) {
          return { expr: trimmed.slice(0, tailMatch.index).trim(), alias: tailMatch[1] }
        }
        return { expr: trimmed, alias: quoteIdent(`__fg_expr_${idx}`) }
      }
      const parsedItems = selectItems.map((item, idx) => ({
        item: String(item || '').trim(),
        ...parseProjectedItem(String(item || '').trim(), idx),
      }))
      const aliasSet = new Set(parsedItems.map((item) => String(item.alias || '').trim().toLowerCase()).filter(Boolean))
      const repeatedExprs = extractRepeatedExprs(parsedItems.map((item) => item.expr), aliasSet)
      if (repeatedExprs.length <= 0) return sql
      const rewriteExpr = (exprRaw: string): string => repeatedExprs.reduce(
        (current, candidate) => replaceExactOrWhitespaceStable(current, candidate.expr, `${sourceAlias}.${candidate.alias}`),
        String(exprRaw || ''),
      )
      const outerSelect = parsedItems.map((item) => `${rewriteExpr(item.expr)} AS ${item.alias}`).join(', ')
      const innerSelect = [
        ...innerSelectItems,
        ...repeatedExprs.map((candidate) => `${candidate.expr} AS ${candidate.alias}`),
      ].join(', ')
      return `SELECT ${outerSelect} FROM (SELECT ${innerSelect} ${fromTail}) AS ${sourceAlias}`
    }

    const buildChunkedUnionSql = (): string => {
      stagedChunkSharedStages = []
      stagedChunkReusableAggBases = []
      stagedChunkReusableWrapperStages = []
      stagedChunkReusableWrapperSourceSql = ''
      const chunkEntries: Array<{
        chunkBaseName: string
        outName: string
        chunkBaseRef: string
        chunkList: string
        chunkBaseSql: string
        chunkBaseSqlBare: string
        outSqlTemplate: string
        measureNames: string[]
      }> = []
      const allChunks = chunkMnvMeasuresForPlan(mnvMeasures, mnvOptimization.chunkSize)
      mnvOptimizationStats.chunkCount = allChunks.length
      mnvOptimizationStats.measureExprShapes = mnvMeasures.map((measure) => ({
        measureName: String(measure?.label ?? '').trim(),
        shape: normalizeMeasureExprShape(String(measure?.valueExpr ?? '')),
      }))
      mnvOptimizationStats.chunkDependencyGroups = allChunks.map((chunk) => ({
        measureNames: chunk.map((m) => String(m.label ?? '').trim()).filter(Boolean),
        signature: String(
          chunk.length > 0 ? measureChunkDependencySignature(chunk[0]) : '',
        ),
      }))
      // Compute the set of deepest-chunk-shared-stage signatures across every
      // non-empty chunk in this plan. Previously we only enabled the unified
      // aggregate_base fallback when every chunk shared an *identical*
      // signature (i.e. identical WHERE/JOIN/GROUP BY structure, differing
      // only in which SUM/COUNT/etc. aliases they emit). That guard was too
      // strict: Monthly-Comparison-style sheets mix FIXED-LOD measures with
      // plain SUM measures, producing heterogeneous signatures, which caused
      // us to materialize N separate ~1.4M-row aggregations inside one 122KB
      // CTE union and OOM DuckDB's OPFS temp quota.
      //
      // Because `buildBaseWideOptimizedSql` is designed to combine an
      // arbitrary measure list into a single valid SELECT (sourcing from the
      // closure-shared `baseWideSourceSql` / `sharedWhereConditions` /
      // `groupByExprs` and running per-LOD rewrites inside `rewriteLodWindows
      // ForGroupedQuery`), the unified base is structurally safe even when
      // the per-measure signatures diverge. We keep the signature collection
      // purely for observability.
      const unifiedAggBaseDepSignatures: string[] = []
      let unifiedAggBaseDepSignaturesUniform = true
      let unifiedAggBaseDepSignatureRef: string | null = null
      if (allChunks.length >= 2) {
        for (const chunk of allChunks) {
          if (!chunk || chunk.length === 0) continue
          const sig = String(measureChunkDependencySignature(chunk[0]) || '')
          unifiedAggBaseDepSignatures.push(sig)
          if (unifiedAggBaseDepSignatureRef === null) {
            unifiedAggBaseDepSignatureRef = sig
            continue
          }
          if (sig !== unifiedAggBaseDepSignatureRef) {
            unifiedAggBaseDepSignaturesUniform = false
          }
        }
      }
      ;(mnvOptimizationStats as any).unifiedAggBase = {
        eligible: allChunks.length >= 2,
        uniformSignature: unifiedAggBaseDepSignaturesUniform,
        signature: unifiedAggBaseDepSignatureRef || '',
        signatures: unifiedAggBaseDepSignatures,
      }
      for (let chunkIdx = 0; chunkIdx < allChunks.length; chunkIdx++) {
        const chunk = allChunks[chunkIdx] || []
        if (!chunk.length) continue
        const chunkBaseName = quoteIdent(`__fg_mnv_base_${chunkIdx}`)
        const outName = quoteIdent(`__fg_mnv_out_${chunkIdx}`)
        const chunkBaseRef = quoteIdent(`b_${chunkIdx}`)
        const chunkMeasurePairs = chunk.map((m) => ({
          alias: m.valueAlias,
          expr: String(m.valueExpr || ''),
        }))
        const chunkExprChars = chunkMeasurePairs.reduce((acc, pair) => acc + pair.expr.length, 0)
        mnvOptimizationStats.totalExprCharsBefore += chunkExprChars
        mnvOptimizationStats.totalExprCharsAfter += chunkExprChars
        // Preserve an un-factored form alongside the factored `chunkBaseSql`.
        // If the synthetic unified-aggregate-base fallback activates later
        // (see buildChunkedUnionSql below) it needs to rewrite each chunk's
        // top-level FROM subquery. The factored form introduces chunk-local
        // `__fg_expr_N` aliases whose definitions disappear when the top-level
        // subquery is replaced, so the fallback swaps back to this bare form.
        const chunkBaseSqlBare = propagateRequiredProjectedAliasesThroughNestedSources(
          buildBaseWideOptimizedSql(chunkMeasurePairs, {
            includeTooltipExtraSelects: true,
            includeOrderExtraAliases: true,
          }),
          [],
        )
        const chunkBaseSql = factorRepeatedMonthDateTruncExprsInNestedSubquery(
          factorRepeatedMonthDateTruncExprs(chunkBaseSqlBare),
        )
        const chunkList = baseStructListForMeasures(chunk, chunkBaseRef)
        const outSqlTemplate = `SELECT ${buildUnnestSelectParts(chunkBaseRef).join(', ')}
FROM ${chunkBaseName} AS ${chunkBaseRef}
CROSS JOIN UNNEST(${chunkList}) AS ${unnestAlias}(${unnestItemAlias})`
        chunkEntries.push({
          chunkBaseName,
          outName,
          chunkBaseRef,
          chunkList,
          chunkBaseSql,
          chunkBaseSqlBare,
          outSqlTemplate,
          measureNames: chunk.map((m) => String(m.label ?? '').trim()).filter(Boolean),
        })
      }
      if (chunkEntries.length > 1) {
        const sharedStageGroups = new Map<string, number[]>()
        const extractedByChunk = chunkEntries.map((entry) => extractDeepestChunkSharedStage(entry.chunkBaseSql))
        for (let idx = 0; idx < extractedByChunk.length; idx += 1) {
          const extracted = extractedByChunk[idx]
          const fromTailKey = canonicalizeSharedStageFromTailKey(extracted?.fromTail || '')
          if (!fromTailKey) continue
          const existing = sharedStageGroups.get(fromTailKey) || []
          existing.push(idx)
          sharedStageGroups.set(fromTailKey, existing)
        }
        let sharedStageIdx = 0
        for (const [_sharedFromTailKey, chunkIndexes] of sharedStageGroups.entries()) {
          if (chunkIndexes.length <= 1) continue
          const placeholderName = quoteIdent(`__fg_mnv_shared_stage_${sharedStageIdx}`)
          sharedStageIdx += 1
          const sharedFromTail = String(extractedByChunk[chunkIndexes[0]]?.fromTail || '').trim()
          if (!sharedFromTail) continue
          const sharedStageSelects: string[] = []
          const sharedStageSeen = new Set<string>()
          for (const chunkIdx of chunkIndexes) {
            const extracted = extractedByChunk[chunkIdx]
            for (const selectItem of extracted?.selectItems || []) {
              const key = normalizeSqlExpr(selectItem)
              if (!key || sharedStageSeen.has(key)) continue
              sharedStageSeen.add(key)
              sharedStageSelects.push(selectItem)
            }
          }
          if (sharedStageSelects.length <= 0) continue
          const sharedStageSql = finalizeSql(`SELECT ${sharedStageSelects.join(', ')} ${sharedFromTail}`)
          stagedChunkSharedStages.push({
            placeholderName,
            sql: sharedStageSql,
            chunkIndexes,
          })
          for (const chunkIdx of chunkIndexes) {
            const entry = chunkEntries[chunkIdx]
            const extracted = extractedByChunk[chunkIdx]
            if (!entry) continue
            entry.chunkBaseSql = rewriteChunkBaseSharedStage(
              entry.chunkBaseSql,
              String(extracted?.innerSql || ''),
              buildSharedStageProjectionSql(extracted?.selectItems || [], placeholderName),
            )
          }
        }
      }
      if (stagedChunkSharedStages.length > 0 || !collectExecPlan) {
        for (const entry of chunkEntries) {
          entry.chunkBaseSql = propagateRequiredProjectedAliasesThroughNestedSources(
            finalizeSql(entry.chunkBaseSql),
            [],
          )
        }
      }
      let topLevelChunkStages = chunkEntries.map((entry) => extractTopLevelFromSubqueryStage(entry.chunkBaseSql))
      const reusableFamilyIndexesByKey = new Map<string, number[]>()
      for (let idx = 0; idx < topLevelChunkStages.length; idx += 1) {
        const stage = topLevelChunkStages[idx]
        const key = canonicalizeSharedStageFromTailKey(stage?.fromTail || '')
        if (!key) continue
        const existing = reusableFamilyIndexesByKey.get(key) || []
        existing.push(idx)
        reusableFamilyIndexesByKey.set(key, existing)
      }
      const reusableFamilies = Array.from(reusableFamilyIndexesByKey.values())
        .filter((chunkIndexes) => chunkIndexes.length > 1)
        .sort((left, right) => right.length - left.length)
      mnvOptimizationStats.reusableFamilyCount = reusableFamilies.length
      mnvOptimizationStats.reusableFamilySizes = reusableFamilies.map((chunkIndexes) => chunkIndexes.length)
      mnvOptimizationStats.chunkReusableFamilies = chunkEntries.map((entry, idx) => ({
        measureNames: entry.measureNames,
        familyKey: canonicalizeSharedStageFromTailKey(topLevelChunkStages[idx]?.fromTail || ''),
      }))
      mnvOptimizationStats.chunkStageFamilyKeys = chunkEntries.map((entry) => ({
        measureNames: entry.measureNames,
        stageKeys: extractNestedFromSubqueryFamilyKeys(entry.chunkBaseSql),
      }))
      stagedChunkReusableAggBases = reusableFamilies.map((chunkIndexes, familyIdx) => {
        const sharedStageSelects: string[] = []
        const sharedStageSeen = new Set<string>()
        for (const chunkIdx of chunkIndexes) {
          const stage = topLevelChunkStages[chunkIdx]
          for (const selectItem of stage?.selectItems || []) {
            const key = normalizeSqlExpr(selectItem)
            if (!key || sharedStageSeen.has(key)) continue
            sharedStageSeen.add(key)
            sharedStageSelects.push(selectItem)
          }
        }
        const sharedStageCtes = stagedChunkSharedStages.map((sharedStage) =>
          `${sharedStage.placeholderName} AS MATERIALIZED (${sharedStage.sql})`,
        )
        const reusableMaterializedSqlCore = `SELECT ${sharedStageSelects.join(', ')} ${String(topLevelChunkStages[chunkIndexes[0]]?.fromTail || '').trim()}`
        const reusableMaterializedSql =
          sharedStageCtes.length > 0
            ? `WITH ${sharedStageCtes.join(',\n')}\n${reusableMaterializedSqlCore}`
            : reusableMaterializedSqlCore
        return {
          placeholderName: familyIdx === 0
            ? quoteIdent('__fg_plan_reusable_agg_base__')
            : quoteIdent(`__fg_plan_reusable_agg_base_${familyIdx}`),
          materializedSql: finalizeSql(reusableMaterializedSql),
          chunkIndexes: [...chunkIndexes],
        }
      })
      // Synthetic unified aggregate_base fallback.
      //
      // When the canonical-key family detection above finds no reusable group
      // (e.g. because each chunk projects a different subset of `__fg_mnv_val_*`
      // aliases so their top-level from-tails diverge), we synthesize a
      // single aggregate_base whose SELECT list is the *union* of every
      // chunk's measures. Each chunk's outer projection then references the
      // same materialized table, collapsing N re-aggregations into 1 and
      // shrinking the plan SQL dramatically. This is the critical fix for
      // Monthly-Comparison-style sheets that currently OOM when DuckDB tries
      // to materialize every chunk's aggregate_base independently.
      //
      // Structural safety note: `buildBaseWideOptimizedSql` sources from the
      // closure-shared `baseWideSourceSql`, applies the single
      // `sharedWhereConditions` / `sharedHavingConditions` set, and uses the
      // shared `groupByExprs`; the only per-measure divergence lives inside
      // each measure's SELECT expression (e.g. FIXED-LOD windows), and
      // `rewriteLodWindowsForGroupedQuery` handles those per-expression. The
      // unified inner SQL is therefore structurally valid across
      // heterogeneous-signature chunks.
      if (
        stagedChunkReusableAggBases.length === 0 &&
        chunkEntries.length > 1
      ) {
        const allMeasurePairs = mnvMeasures.map((m) => ({
          alias: m.valueAlias,
          expr: String(m.valueExpr || ''),
        }))
        if (allMeasurePairs.length > 0) {
          const unifiedInnerSql = buildBaseWideOptimizedSql(allMeasurePairs, {
            includeTooltipExtraSelects: true,
            includeOrderExtraAliases: true,
            skipTotalsOuterWrap: true,
          })
          if (unifiedInnerSql && unifiedInnerSql.trim().length > 0) {
            // Swap each chunk back to its un-factored form. The factored form
            // defines chunk-local `__fg_expr_N` aliases inside a middle SELECT
            // layer; once we rewrite the top-level FROM subquery to point at
            // the shared unified base those alias definitions vanish and the
            // outer SELECT's refs to them would become unbound. The bare form
            // references only `__fg_grouped_base.__fg_group_key_*` and the
            // measure val aliases, which the unified base exposes natively.
            for (const entry of chunkEntries) {
              entry.chunkBaseSql = finalizeSql(entry.chunkBaseSqlBare)
            }
            topLevelChunkStages = chunkEntries.map((entry) => extractTopLevelFromSubqueryStage(entry.chunkBaseSql))
            stagedChunkReusableAggBases = [{
              placeholderName: quoteIdent('__fg_plan_reusable_agg_base__'),
              materializedSql: finalizeSql(unifiedInnerSql),
              chunkIndexes: chunkEntries.map((_, idx) => idx),
              skipWindowSourceSplit: true,
            }]
            mnvOptimizationStats.reusableFamilyCount = 1
            mnvOptimizationStats.reusableFamilySizes = [chunkEntries.length]
            ;(mnvOptimizationStats as any).unifiedAggBase = {
              ...(mnvOptimizationStats as any).unifiedAggBase,
              active: true,
              chunkCount: chunkEntries.length,
              unifiedInnerSqlLength: unifiedInnerSql.length,
            }
          }
        }
      }
      const reusableAggBaseByChunkIdx = new Map<number, StagedChunkReusableAggBaseDefinition>()
      for (const reusableAggBase of stagedChunkReusableAggBases) {
        for (const chunkIdx of reusableAggBase.chunkIndexes) {
          reusableAggBaseByChunkIdx.set(chunkIdx, reusableAggBase)
        }
      }
      const reusableChunkBaseEntries = chunkEntries.map((entry, idx) => {
        const sourceStage = topLevelChunkStages[idx]
        const reusableAggBase = reusableAggBaseByChunkIdx.get(idx) || null
        const sourceAlias = String(sourceStage?.sourceAlias || '').trim() || quoteIdent('__fg_grouped_base')
        const buildReusableChunkProjectionSql = (): string | null => {
          if (!reusableAggBase) return null
          const topStage = extractTopLevelSelectStage(entry.chunkBaseSql)
          const topSelectItems = Array.isArray(topStage?.selectItems) ? topStage.selectItems : []
          if (topSelectItems.length <= 0 || selectItemsContainWildcard(topSelectItems)) return null
          const projected: string[] = []
          for (const selectItemRaw of topSelectItems) {
            const alias = extractSelectItemAlias(String(selectItemRaw || ''))
            if (!alias) return null
            projected.push(`${sourceAlias}.${alias} AS ${alias}`)
          }
          return finalizeSql(`SELECT ${projected.join(', ')} FROM ${reusableAggBase.placeholderName} AS ${sourceAlias}`)
        }
        const rewrittenChunkBaseSql = reusableAggBase
          ? buildReusableChunkProjectionSql() ||
            rewriteTopLevelFromSubquery(
              entry.chunkBaseSql,
              `SELECT * FROM ${reusableAggBase.placeholderName} AS ${sourceAlias}`,
            )
          : entry.chunkBaseSql
        return {
          ...entry,
          reusableAggBase,
          sourceAlias,
          rewrittenChunkBaseSql,
          originalChunkBaseSql: entry.chunkBaseSql,
          topLevelStage: reusableAggBase ? extractTopLevelFromSubqueryStage(rewrittenChunkBaseSql) : null,
        }
      })
      const requiredProjectedAliasesByReusableAggBase = new Map<string, string[]>()
      const requiredProjectedAliasKeysByReusableAggBase = new Map<string, Set<string>>()
      const requiredProjectedSelectItemsByReusableAggBase = new Map<string, Array<{
        selectItem: string
        sourceAlias: string
      }>>()
      const requiredProjectedSelectItemKeysByReusableAggBase = new Map<string, Set<string>>()
      const derivedProjectedSelectItemByReusableAggBase = new Map<string, Map<string, {
        selectItem: string
        sourceAlias: string
      }>>()
      const pushRequiredProjectedAlias = (
        placeholderNameRaw: string,
        aliasRaw: string,
      ): void => {
        const placeholderName = String(placeholderNameRaw || '').trim()
        const aliasName = unquoteProjectedAliasName(String(aliasRaw || ''))
        const aliasKey = normalizeProjectedAliasKey(aliasName)
        if (!placeholderName || !aliasName || !aliasKey) return
        const existingAliases =
          requiredProjectedAliasesByReusableAggBase.get(placeholderName) || []
        const seenKeys =
          requiredProjectedAliasKeysByReusableAggBase.get(placeholderName) || new Set<string>()
        if (seenKeys.has(aliasKey)) return
        seenKeys.add(aliasKey)
        existingAliases.push(aliasName)
        requiredProjectedAliasesByReusableAggBase.set(placeholderName, existingAliases)
        requiredProjectedAliasKeysByReusableAggBase.set(placeholderName, seenKeys)
      }
      const pushRequiredProjectedSelectItem = (
        placeholderNameRaw: string,
        selectItemRaw: string,
        sourceAliasRaw: string,
      ): void => {
        const placeholderName = String(placeholderNameRaw || '').trim()
        const selectItem = String(selectItemRaw || '').trim()
        const sourceAlias = String(sourceAliasRaw || '').trim()
        const aliasName = unquoteProjectedAliasName(extractSelectItemAlias(selectItem))
        const aliasKey = normalizeProjectedAliasKey(aliasName)
        if (!placeholderName || !selectItem || !sourceAlias || !aliasKey) return
        const existingSelectItems =
          requiredProjectedSelectItemsByReusableAggBase.get(placeholderName) || []
        const seenKeys =
          requiredProjectedSelectItemKeysByReusableAggBase.get(placeholderName) || new Set<string>()
        if (seenKeys.has(aliasKey)) return
        seenKeys.add(aliasKey)
        existingSelectItems.push({ selectItem, sourceAlias })
        requiredProjectedSelectItemsByReusableAggBase.set(placeholderName, existingSelectItems)
        requiredProjectedSelectItemKeysByReusableAggBase.set(placeholderName, seenKeys)
      }
      const collectDerivedProjectedSelectItemsThroughNestedStages = (
        sqlRaw: string,
        remainingDepth = 12,
      ): Array<{ selectItem: string; sourceAlias: string }> => {
        const sql = finalizeSql(String(sqlRaw || ''))
        if (!sql || remainingDepth <= 0) return []
        const stage = extractTopLevelFromSubqueryStage(sql)
        const selectItems = Array.isArray(stage?.selectItems) ? stage.selectItems : []
        const sourceAlias = String(stage?.sourceAlias || '').trim()
        const fromTail = String(stage?.fromTail || '').trim()
        const out: Array<{ selectItem: string; sourceAlias: string }> = []
        if (sourceAlias && selectItems.length > 0 && !selectItemsContainWildcard(selectItems)) {
          for (const selectItemRaw of selectItems) {
            const selectItem = String(selectItemRaw || '').trim()
            const aliasName = unquoteProjectedAliasName(extractSelectItemAlias(selectItem))
            const aliasKey = normalizeProjectedAliasKey(aliasName)
            if (!selectItem || !aliasKey) continue
            const aliasMatch = /\bAS\s+("[^"]+"|[A-Za-z_][A-Za-z0-9_]*)\s*$/i.exec(selectItem)
            const expr = String(aliasMatch?.index != null ? selectItem.slice(0, aliasMatch.index).trim() : selectItem).trim()
            const sourceAliasName = parseProjectedAliasRef(expr, sourceAlias)
            const isDirectPassthrough =
              Boolean(sourceAliasName) &&
              aliasKey === normalizeProjectedAliasKey(String(sourceAliasName || ''))
            if (isDirectPassthrough) continue
            out.push({ selectItem, sourceAlias })
          }
        }
        if (!/^FROM\s*\(/i.test(fromTail)) return out
        const openParen = sql.indexOf('(', sql.length - fromTail.length)
        if (openParen < 0) return out
        let depth = 0
        let closeParen = -1
        for (let idx = openParen; idx < sql.length; idx += 1) {
          const ch = sql[idx]
          if (ch === '(') {
            depth += 1
            continue
          }
          if (ch !== ')') continue
          depth -= 1
          if (depth === 0) {
            closeParen = idx
            break
          }
        }
        if (closeParen <= openParen + 1) return out
        const innerSql = sql.slice(openParen + 1, closeParen).trim()
        return [
          ...out,
          ...collectDerivedProjectedSelectItemsThroughNestedStages(innerSql, remainingDepth - 1),
        ]
      }
      const requiredOutputAliases = groupDims
        .map((dim) => unquoteProjectedAliasName(String(dim?.field_name ?? '').trim()))
        .filter(Boolean)
      for (const reusableAggBase of stagedChunkReusableAggBases) {
        for (const aliasName of requiredOutputAliases) {
          pushRequiredProjectedAlias(reusableAggBase.placeholderName, aliasName)
        }
      }
      for (const entry of reusableChunkBaseEntries) {
        const reusableAggBase = entry?.reusableAggBase
        const stage = entry?.topLevelStage
        if (!reusableAggBase || !stage?.selectItems?.length) continue
        const directAliases = collectDirectProjectedStageAliases(
          stage.selectItems,
          String(stage.sourceAlias || ''),
        )
        if (directAliases.length <= 0) continue
        for (const aliasName of directAliases) {
          pushRequiredProjectedAlias(reusableAggBase.placeholderName, aliasName)
        }
        const referencedAliases = collectReferencedStageSourceColumnAliases(
          entry.rewrittenChunkBaseSql,
          String(stage.sourceAlias || ''),
        )
        for (const aliasName of referencedAliases) {
          pushRequiredProjectedAlias(reusableAggBase.placeholderName, aliasName)
        }
        const derivedSelectItemsByAlias =
          derivedProjectedSelectItemByReusableAggBase.get(reusableAggBase.placeholderName) || new Map<
            string,
            { selectItem: string; sourceAlias: string }
          >()
        for (const derivedSelectItem of collectDerivedProjectedSelectItemsThroughNestedStages(entry.originalChunkBaseSql || entry.rewrittenChunkBaseSql)) {
          const item = String(derivedSelectItem.selectItem || '').trim()
          const aliasName = unquoteProjectedAliasName(extractSelectItemAlias(item))
          const aliasKey = normalizeProjectedAliasKey(aliasName)
          if (!item || !aliasKey || derivedSelectItemsByAlias.has(aliasKey)) continue
          derivedSelectItemsByAlias.set(aliasKey, {
            selectItem: item,
            sourceAlias: String(derivedSelectItem.sourceAlias || '').trim(),
          })
        }
        if (derivedSelectItemsByAlias.size > 0) {
          derivedProjectedSelectItemByReusableAggBase.set(
            reusableAggBase.placeholderName,
            derivedSelectItemsByAlias,
          )
        }
      }
      for (const reusableAggBase of stagedChunkReusableAggBases) {
        const requiredAliasKeys =
          requiredProjectedAliasKeysByReusableAggBase.get(reusableAggBase.placeholderName) || new Set<string>()
        const derivedSelectItemsByAlias =
          derivedProjectedSelectItemByReusableAggBase.get(reusableAggBase.placeholderName) || new Map<
            string,
            { selectItem: string; sourceAlias: string }
          >()
        for (const aliasKey of requiredAliasKeys) {
          const derivedSelectItem = derivedSelectItemsByAlias.get(aliasKey)
          if (!derivedSelectItem) continue
          pushRequiredProjectedSelectItem(
            reusableAggBase.placeholderName,
            derivedSelectItem.selectItem,
            derivedSelectItem.sourceAlias,
          )
        }
        reusableAggBase.requiredProjectedAliases =
          requiredProjectedAliasesByReusableAggBase.get(reusableAggBase.placeholderName) || []
        reusableAggBase.requiredProjectedSelectItems =
          requiredProjectedSelectItemsByReusableAggBase.get(reusableAggBase.placeholderName) || []
      }
      const reusableWrapperStageByChunkIdx = new Map<number, {
        placeholderName: string
        sourceAlias: string
        selectItems: string[]
      }>()
      const reusableWrapperStageGroups = new Map<string, number[]>()
      for (let idx = 0; idx < reusableChunkBaseEntries.length; idx += 1) {
        const entry = reusableChunkBaseEntries[idx]
        if (!entry?.reusableAggBase) continue
        const stage = entry.topLevelStage
        const familyKey = canonicalizeSharedStageFromTailKey(stage?.fromTail || '')
        if (!familyKey) continue
        const sourceAliasKey = String(stage?.sourceAlias || '').trim()
        if (!sourceAliasKey) continue
        const groupKey = `${entry.reusableAggBase.placeholderName}::${sourceAliasKey}::${familyKey}`
        const chunkIndexes = reusableWrapperStageGroups.get(groupKey) || []
        chunkIndexes.push(idx)
        reusableWrapperStageGroups.set(groupKey, chunkIndexes)
      }
      let reusableWrapperStageIdx = 0
      for (const chunkIndexes of reusableWrapperStageGroups.values()) {
        if (chunkIndexes.length <= 1) continue
        const exemplar = reusableChunkBaseEntries[chunkIndexes[0]]
        const exemplarStage = exemplar?.topLevelStage
        const fromTail = String(exemplarStage?.fromTail || '').trim()
        const sourceAlias = String(exemplarStage?.sourceAlias || '').trim()
        if (!fromTail || !sourceAlias) continue
        if (chunkIndexes.some((chunkIdx) => selectItemsContainWildcard(reusableChunkBaseEntries[chunkIdx]?.topLevelStage?.selectItems || []))) {
          continue
        }
        const placeholderName = quoteIdent(`__fg_mnv_wrapper_stage_${reusableWrapperStageIdx}`)
        reusableWrapperStageIdx += 1
        const sharedSelectItems: string[] = []
        const sharedSelectSeen = new Set<string>()
        for (const chunkIdx of chunkIndexes) {
          const stage = reusableChunkBaseEntries[chunkIdx]?.topLevelStage
          for (const selectItem of stage?.selectItems || []) {
            const alias = extractSelectItemAlias(selectItem)
            const key = alias
              ? `${alias}::${normalizeSqlExpr(selectItem)}`
              : normalizeSqlExpr(selectItem)
            if (!key || sharedSelectSeen.has(key)) continue
            sharedSelectSeen.add(key)
            sharedSelectItems.push(selectItem)
          }
        }
        if (sharedSelectItems.length <= 0) continue
        stagedChunkReusableWrapperStages.push({
          placeholderName,
          sql: finalizeSql(`SELECT ${sharedSelectItems.join(', ')} ${fromTail}`),
        })
        for (const chunkIdx of chunkIndexes) {
          const stage = reusableChunkBaseEntries[chunkIdx]?.topLevelStage
          if (!stage?.selectItems?.length) continue
          reusableWrapperStageByChunkIdx.set(chunkIdx, {
            placeholderName,
            sourceAlias,
            selectItems: [...stage.selectItems],
          })
        }
      }
      mnvOptimizationStats.reusableWrapperStageCount = stagedChunkReusableWrapperStages.length
      const buildReusableChunkBaseSql = (
        entry: (typeof reusableChunkBaseEntries)[number],
        idx: number,
        opts?: { chunkBaseSource?: string | null },
      ): string => {
        const reusableWrapperStage = reusableWrapperStageByChunkIdx.get(idx) || null
        if (reusableWrapperStage) {
          return finalizeSql(
            `SELECT ${reusableWrapperStage.selectItems.join(', ')} FROM ${reusableWrapperStage.placeholderName} AS ${reusableWrapperStage.sourceAlias}`,
          )
        }
        const chunkBaseSource = String(
          opts?.chunkBaseSource || entry.reusableAggBase?.placeholderName || entry.chunkBaseName,
        ).trim() || entry.chunkBaseName
        return finalizeSql(entry.rewrittenChunkBaseSql.replaceAll(entry.chunkBaseName, chunkBaseSource))
      }
      const reusableOutSelects = reusableChunkBaseEntries.map((entry, idx) => {
        const { chunkBaseRef, chunkList } = entry
        const wrapperChunkBaseSql = buildReusableChunkBaseSql(entry, idx)
        return `SELECT ${buildUnnestSelectParts(chunkBaseRef).join(', ')}
FROM (${wrapperChunkBaseSql}) AS ${chunkBaseRef}
CROSS JOIN UNNEST(${chunkList}) AS ${unnestAlias}(${unnestItemAlias})`
      })
      stagedChunkReusableWrapperSourceSql = reusableOutSelects
        .map((sql) => pruneNullishTopLevelGroupOrderTerms(sanitizeNullArtifacts(sql)))
        .join('\nUNION ALL\n')
      const ctes: string[] = []
      const outSelects: string[] = []
      for (const sharedStage of stagedChunkSharedStages) {
        ctes.push(`${sharedStage.placeholderName} AS MATERIALIZED (${sharedStage.sql})`)
      }
      for (let idx = 0; idx < chunkEntries.length; idx += 1) {
        const entry = chunkEntries[idx]
        const { chunkBaseName, outName, chunkBaseRef, chunkList, chunkBaseSql, outSqlTemplate, measureNames } = entry
        ctes.push(`${chunkBaseName} AS MATERIALIZED (${chunkBaseSql})`)
        const outCteSql = `${outName} AS (
  ${outSqlTemplate}
)`
        ctes.push(outCteSql)
        const planChunkBasePlaceholder = quoteIdent('__fg_plan_chunk_base__')
        const reusableChunkBaseEntry = reusableChunkBaseEntries[idx] || null
        const planChunkBaseSql = reusableChunkBaseEntry
          ? buildReusableChunkBaseSql(reusableChunkBaseEntry, idx, {
              chunkBaseSource: planChunkBasePlaceholder,
            })
          : finalizeSql(chunkBaseSql)
        const chunkPlanCtes = [
          // The enclosing `CREATE TEMP TABLE ... AS` is already the materialization boundary for
          // staged chunk execution. Avoid forcing an additional full copy of the chunk base inside
          // the query; that duplicate spool is what the web path currently OOMs on.
          `${planChunkBasePlaceholder} AS (${planChunkBaseSql})`,
          `${outName} AS (
  SELECT ${buildUnnestSelectParts(chunkBaseRef).join(', ')}
FROM ${planChunkBasePlaceholder} AS ${chunkBaseRef}
CROSS JOIN UNNEST(${chunkList}) AS ${unnestAlias}(${unnestItemAlias})
)`,
        ]
        stagedChunkPlanParts.push({
          sql: `WITH ${chunkPlanCtes.join(',\n')}
SELECT * FROM ${outName}`,
          baseSql: planChunkBaseSql,
          outSqlTemplate: `SELECT ${buildUnnestSelectParts(chunkBaseRef).join(', ')}
FROM ${planChunkBasePlaceholder} AS ${chunkBaseRef}
CROSS JOIN UNNEST(${chunkList}) AS ${unnestAlias}(${unnestItemAlias})`,
          measureNames,
        })
        outSelects.push(`SELECT * FROM ${outName}`)
      }
      return `WITH ${ctes.join(',\n')}
${outSelects.join('\nUNION ALL\n')}`
    }

    const union = (() => {
      if (useStagedMnvProjection) {
        return sanitizeNullArtifacts(buildChunkedUnionSql())
      }
      const mnvList = baseStructListForMeasures(mnvMeasures)
      const mnvSelect = `SELECT ${buildUnnestSelectParts(baseOutRef).join(', ')}
FROM ${baseCte} AS ${baseOutRef}
CROSS JOIN UNNEST(${mnvList}) AS ${unnestAlias}(${unnestItemAlias})`
      return sanitizeNullArtifacts(`WITH ${baseCte} AS MATERIALIZED (${baseWideOptimized}) ${mnvSelect}`)
    })()
    try {
      const root: any =
        (typeof window !== 'undefined' && window) ||
        (typeof self !== 'undefined' && self) ||
        null
      if (root && mnvOptimization.debug) {
        const prev = (root.__FG_MNV_SQL_OPT_STATS__ && typeof root.__FG_MNV_SQL_OPT_STATS__ === 'object')
          ? root.__FG_MNV_SQL_OPT_STATS__
          : {}
        root.__FG_MNV_SQL_OPT_STATS__ = {
          ...prev,
          ...mnvOptimizationStats,
        }
      }
      if (mnvOptimization.debug) {
        console.log('[FG-MNV-SQL-OPT]', {
          ...mnvOptimizationStats,
          enabled: mnvOptimization.enabled,
        })
      }
    } catch { }

    const buildColDomainCte = (
      colDimNames: string[],
      colDimCols: string[],
      colDimExprsForSeed: string[],
      colDimFieldRefs: string[],
      cteName = '__col_domain',
      restrictToRowDomainCols?: string[] | null,
    ): { sql: string; seededDateColIdxs: Set<number> } => {
      const colDomainCols = [...colDimCols, quoteIdent('Measure Names')].filter(Boolean)
      const rowDomainCols = Array.isArray(restrictToRowDomainCols)
        ? restrictToRowDomainCols.map((col) => String(col ?? '').trim()).filter(Boolean)
        : []
      const rowDomainJoin =
        rowDomainCols.length > 0
          ? ` FROM __base b INNER JOIN __row_domain r ON ${rowDomainCols.map((col) => `b.${col} IS NOT DISTINCT FROM r.${col}`).join(' AND ')}`
          : ' FROM __base'
      const baseSql = `SELECT DISTINCT ${colDomainCols.join(', ')}${rowDomainJoin}`
      if (!colDimNames.length || !colDimCols.length) {
        return { sql: `${cteName} AS (${baseSql})`, seededDateColIdxs: new Set<number>() }
      }

      const timePeriodIdx = colDimNames.findIndex((name) => {
        const k = norm(name)
        if (!k) return false
        if (!k.includes('time period')) return false
        if (k.includes('begin date') || k.includes('end date')) return false
        return true
      })
      if (timePeriodIdx < 0) {
        return { sql: `__col_domain AS (${baseSql})`, seededDateColIdxs: new Set<number>() }
      }
      const timePeriodCol = colDimCols[timePeriodIdx] || ''
      const timePeriodFieldName = String(colDimNames[timePeriodIdx] ?? '').trim()
      const timePeriodFieldRef = String(colDimFieldRefs[timePeriodIdx] ?? timePeriodFieldName).trim()
      if (!timePeriodCol || !timePeriodFieldName) {
        return { sql: `${cteName} AS (${baseSql})`, seededDateColIdxs: new Set<number>() }
      }

      const deriveExprForSeedMember = (exprRaw: string, member: string): string | null => {
        const expr = String(exprRaw ?? '').trim()
        if (!expr) return null
        const literal = escapeValue(member)
        const tpIdent = quoteIdent(timePeriodFieldName)
        const candidates = [
          `${baseRef}.${tpIdent}`,
          `${quoteIdent('t')}.${tpIdent}`,
          `t.${tpIdent}`,
          `${tpIdent}`,
        ]
        let out = expr
        let replaced = false
        for (const c of candidates) {
          if (!c) continue
          if (!out.includes(c)) continue
          out = out.split(c).join(literal)
          replaced = true
        }
        if (!replaced) return null
        if (/(^|[^A-Za-z0-9_])(?:"?[A-Za-z_][A-Za-z0-9_]*"?)\s*\.\s*"[^"]+"/.test(out)) {
          return null
        }
        return out
      }

      const parameterEntries = Object.entries(parameters ?? {})
      const resolveParameterDateValue = (parameterName: string): Date | null => {
        const target = norm(parameterName)
        if (!target) return null
        let picked: unknown = null
        for (const [k, v] of parameterEntries) {
          const nk = norm(k)
          if (!nk) continue
          if (nk === target || nk.endsWith(`.${target}`) || nk.includes(target)) {
            picked = v
            break
          }
        }
        if (picked == null) return null
        if (picked instanceof Date && Number.isFinite(picked.getTime())) return picked
        if (typeof picked === 'number' && Number.isFinite(picked)) {
          const ms = picked > 1e12 ? picked : picked * 1000
          const d = new Date(ms)
          return Number.isFinite(d.getTime()) ? d : null
        }
        if (typeof picked === 'string') {
          const trimmed = picked.trim()
          if (!trimmed) return null
          const numeric = Number(trimmed)
          if (Number.isFinite(numeric)) {
            const ms = numeric > 1e12 ? numeric : numeric * 1000
            const d = new Date(ms)
            if (Number.isFinite(d.getTime())) return d
          }
          const d = new Date(trimmed)
          return Number.isFinite(d.getTime()) ? d : null
        }
        return null
      }

      const toIsoDateStart = (d: Date): string => {
        const y = d.getUTCFullYear()
        const m = String(d.getUTCMonth() + 1).padStart(2, '0')
        const day = String(d.getUTCDate()).padStart(2, '0')
        return `${y}-${m}-${day} 00:00:00`
      }

      const addDaysUtc = (d: Date, days: number): Date => {
        const next = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()))
        next.setUTCDate(next.getUTCDate() + days)
        return next
      }

      const truncWeekMondayUtc = (d: Date): Date => {
        const dow = d.getUTCDay() // 0 = Sunday ... 6 = Saturday
        const mondayOffset = (dow + 6) % 7
        return addDaysUtc(d, -mondayOffset)
      }

      const buildWeeklyCaseDateLiterals = (formulaRaw: string): Map<string, string> => {
        const out = new Map<string, string>()
        const formula = String(formulaRaw ?? '')
        if (!formula) return out
        const paramMatch = /\[Parameters\]\.\[([^\]]+)\]/i.exec(formula)
        const parameterName = String(paramMatch?.[1] ?? '').trim()
        if (!parameterName) return out
        const anchorParam = resolveParameterDateValue(parameterName)
        if (!anchorParam) return out
        const weekAnchor = truncWeekMondayUtc(addDaysUtc(anchorParam, 1))
        const rx = /when\s+"([^"]+)"\s+then\s+dateadd\('week',\s*(-?\d+)\s*,\s*datetrunc\('week',[\s\S]*?\)\)\s*(-\s*1)?/gi
        let match: RegExpExecArray | null
        while ((match = rx.exec(formula)) !== null) {
          const label = String(match[1] ?? '').trim()
          if (!label) continue
          const weekDelta = Number(match[2] ?? 0)
          if (!Number.isFinite(weekDelta)) continue
          const minusOneDay = Boolean(match[3])
          const target = addDaysUtc(weekAnchor, weekDelta * 7 + (minusOneDay ? -1 : 0))
          out.set(norm(label), toIsoDateStart(target))
        }
        return out
      }

      const dateLiteralsByColIdx = new Map<number, Map<string, string>>()
      const seededDateColIdxs = new Set<number>()
      colDimNames.forEach((name, idx) => {
        const key = norm(name)
        if (!key) return
        if (!key.includes('time period begin dates') && !key.includes('time period end dates')) return
        const fieldRef = String(colDimFieldRefs[idx] ?? '').trim()
        let literals: Map<string, string> | null = null
        if (weeklyTimePeriodOverrides) {
          if (key.includes('time period begin dates') && weeklyTimePeriodOverrides.beginDateLiterals?.size) {
            literals = weeklyTimePeriodOverrides.beginDateLiterals
          } else if (key.includes('time period end dates') && weeklyTimePeriodOverrides.endDateLiterals?.size) {
            literals = weeklyTimePeriodOverrides.endDateLiterals
          }
        }
        if (!literals || !literals.size) {
          const formula =
            lookupCalculatedFormula(fieldRef) ||
            lookupCalculatedFormula(String(name ?? ''))
          literals = buildWeeklyCaseDateLiterals(formula ?? '')
        }
        if (literals.size) {
          dateLiteralsByColIdx.set(idx, literals)
          seededDateColIdxs.add(idx)
        }
      })

      const canonicalSeedCaseExpr = (
        idx: number,
        memberExprSql: string,
        fallbackSql: string,
      ): string | null => {
        const dateLiterals = dateLiteralsByColIdx.get(idx)
        if (!dateLiterals || dateLiterals.size === 0) return null
        const whens: string[] = []
        for (const [memberKey, dateLiteral] of dateLiterals.entries()) {
          const rawMember =
            dedupedMembers.find((value) => norm(value) === memberKey) ??
            memberKey
          whens.push(`WHEN ${escapeValue(rawMember)} THEN CAST(${escapeValue(dateLiteral)} AS TIMESTAMP)`)
        }
        if (!whens.length) return null
        return `CASE ${memberExprSql} ${whens.join(' ')} ELSE ${fallbackSql} END`
      }

      const literalMembersFromFormula = (rawFormula: string): string[] => {
        const out: string[] = []
        const formula = String(rawFormula ?? '')
        if (!formula) return out
        const seen = new Set<string>()
        for (const m of formula.matchAll(/"([^"]+)"/g)) {
          const v = String(m?.[1] ?? '').trim()
          if (!v) continue
          const k = norm(v)
          if (!k || k === 'null' || k === '(null)' || k === '%null%') continue
          if (seen.has(k)) continue
          seen.add(k)
          out.push(v)
        }
        return out
      }

      const toFilterAtoms = (raw: any): string[] => {
        const vals = Array.isArray(raw) ? raw : [raw]
        const out: string[] = []
        for (const v of vals) {
          const atom = normalizeFilterAtom(v)
          if (atom === null || typeof atom === 'undefined') continue
          const s = String(atom).trim()
          if (!s) continue
          out.push(s)
        }
        return out
      }

      const include = new Set<string>()
      const exclude = new Set<string>()
      for (const f of filters || []) {
        const field = String((f as any)?.field_name ?? '').trim()
        if (!field || norm(field) !== norm(timePeriodFieldName)) continue
        const op = String((f as any)?.operator ?? '').trim().toUpperCase()
        const atoms = toFilterAtoms((f as any)?.value)
        if (!atoms.length) continue
        if (op === 'IN' || op === '=') {
          for (const a of atoms) include.add(norm(a))
          continue
        }
        if (op === 'NOT IN' || op === '!=' || op === '<>') {
          for (const a of atoms) exclude.add(norm(a))
        }
      }

      const manualMembers = manualOrderForField(timePeriodFieldName) || []
      const formulaMembers = literalMembersFromFormula(
        lookupCalculatedFormula(timePeriodFieldRef) || lookupCalculatedFormula(timePeriodFieldName) || ''
      )
      let seedMembers = [...manualMembers, ...formulaMembers]
      if (include.size > 0) {
        seedMembers = seedMembers.filter((v) => include.has(norm(v)))
      }
      const dedupedMembers: string[] = []
      const seenSeed = new Set<string>()
      for (const raw of seedMembers) {
        const v = String(raw ?? '').trim()
        if (!v) continue
        const k = norm(v)
        if (!k || k === '%null%' || k === 'null' || k === '(null)') continue
        if (exclude.has(k)) continue
        if (seenSeed.has(k)) continue
        seenSeed.add(k)
        dedupedMembers.push(v)
      }
      if (!dedupedMembers.length) {
        return { sql: `${cteName} AS (${baseSql})`, seededDateColIdxs: new Set<number>() }
      }

      const memberSelects = dedupedMembers.map((member) => {
        const memberKey = norm(member)
        const parts = colDimCols.map((col, idx) => (
          idx === timePeriodIdx
            ? `${escapeValue(member)} AS ${col}`
            : (() => {
                const dateLiterals = dateLiteralsByColIdx.get(idx)
                const dateLiteral = dateLiterals?.get(memberKey)
                if (dateLiteral) {
                  return `CAST(${escapeValue(dateLiteral)} AS TIMESTAMP) AS ${col}`
                }
                const seededExpr = deriveExprForSeedMember(String(colDimExprsForSeed[idx] ?? ''), member)
                return seededExpr ? `${seededExpr} AS ${col}` : `NULL AS ${col}`
              })()
        ))
        parts.push(`m.${quoteIdent('Measure Names')} AS ${quoteIdent('Measure Names')}`)
        return `SELECT ${parts.join(', ')} FROM __measure_names m`
      }).join(' UNION ALL ')

      const normalizedBaseSelect = [
        ...colDimCols.map((col, idx) => {
          const canonicalExpr = canonicalSeedCaseExpr(
            idx,
            `b.${timePeriodCol}`,
            `TRY_CAST(b.${col} AS TIMESTAMP)`,
          )
          return `${canonicalExpr || `b.${col}`} AS ${col}`
        }),
        `b.${quoteIdent('Measure Names')} AS ${quoteIdent('Measure Names')}`,
      ].join(', ')
      const colBaseWhere = `WHERE b.${timePeriodCol} IS NOT NULL AND TRIM(CAST(b.${timePeriodCol} AS VARCHAR)) <> ''`
      const colBaseCte = '__col_base'
      const measureNamesCte = '__measure_names'
      const colSeedCte = '__col_seed'
      const colBaseFrom =
        rowDomainCols.length > 0
          ? `FROM __base b INNER JOIN __row_domain r ON ${rowDomainCols.map((col) => `b.${col} IS NOT DISTINCT FROM r.${col}`).join(' AND ')}`
          : 'FROM __base b'
      const measureNamesFrom =
        rowDomainCols.length > 0
          ? `FROM ${colBaseCte} b`
          : 'FROM __base b'

      return {
        sql: `${colBaseCte} AS MATERIALIZED (SELECT DISTINCT ${normalizedBaseSelect} ${colBaseFrom} ${colBaseWhere}),
        ${measureNamesCte} AS (SELECT DISTINCT b.${quoteIdent('Measure Names')} AS ${quoteIdent('Measure Names')} ${measureNamesFrom}),
        ${colSeedCte} AS (${memberSelects}),
        ${cteName} AS (
          SELECT * FROM ${colBaseCte}
          UNION
          SELECT * FROM ${colSeedCte} s
          WHERE NOT EXISTS (
            SELECT 1 FROM ${colBaseCte} b
            WHERE b.${timePeriodCol} IS NOT DISTINCT FROM s.${timePeriodCol}
              AND b.${quoteIdent('Measure Names')} = s.${quoteIdent('Measure Names')}
          )
        )`,
        seededDateColIdxs,
      }
    }

    const qualifyWrapperOrderBy = (alias: string, orderSql: string): string => {
      const aliasTrimmed = String(alias || '').trim()
      const rawOrderSql = String(orderSql || '').trim()
      if (!aliasTrimmed || !rawOrderSql) return rawOrderSql
      const wrapperProjectedAliasForField = (fieldNameRaw: any): string => {
        const fieldName = String(fieldNameRaw ?? '').trim()
        if (!fieldName) return ''
        const outputSet = new Set(outputColumns.map((c) => String(c ?? '').trim()).filter(Boolean))
        if (outputSet.has(fieldName)) return fieldName
        const fieldKey = normalizeTableauFieldKey(fieldName)
        const candidates = Array.from(outputSet.values()).filter((candidate) => {
          const key = normalizeTableauFieldKey(candidate)
          if (!key || !fieldKey) return false
          if (key === fieldKey) return true
          return key.startsWith(`${fieldKey} `) || key.startsWith(`${fieldKey}(`)
        })
        if (candidates.length === 1) return candidates[0]!
        return fieldName
      }
      const stripQualifierRefsForWrapperOrder = (exprRaw: string): string => (
        String(exprRaw || '').replace(
          /(?:\"[^\"]+\"|[A-Za-z_][A-Za-z0-9_]*)\s*\.\s*(\"[^\"]+\"|[A-Za-z_][A-Za-z0-9_]*)/g,
          '$1',
        )
      )
      let next = rawOrderSql
      const groupedWrapperOrderReplacements = groupDims
        .map((d, i) => {
          const fieldName = String(d?.field_name ?? '').trim()
          const groupedExpr = String(tableCalcDimExprs[i] ?? dimExprs[i] ?? '').trim()
          if (!fieldName || !groupedExpr) return null
          if (
            groupedExpr === quoteIdent(fieldName)
            || groupedExpr === `${baseRef}.${quoteIdent(fieldName)}`
          ) return null
          const projectedFieldName = wrapperProjectedAliasForField(fieldName)
          return {
            fieldName: projectedFieldName || fieldName,
            groupedExprs: Array.from(new Set([
              groupedExpr,
              stripQualifierRefsForWrapperOrder(groupedExpr).trim(),
            ].filter(Boolean))),
          }
        })
        .filter((entry): entry is { fieldName: string; groupedExprs: string[] } => Boolean(entry))
      const groupedExprVariants = groupedWrapperOrderReplacements
        .flatMap(({ fieldName, groupedExprs }) =>
          groupedExprs.map((groupedExpr) => ({ fieldName, groupedExpr })),
        )
        .sort((a, b) => b.groupedExpr.length - a.groupedExpr.length)
      for (const { fieldName, groupedExpr } of groupedExprVariants) {
        next = next.split(groupedExpr).join(quoteIdent(fieldName))
      }
      for (const dim of groupDims) {
        const fieldName = String(dim?.field_name ?? '').trim()
        const projectedFieldName = wrapperProjectedAliasForField(fieldName)
        if (!fieldName || !projectedFieldName || projectedFieldName === fieldName) continue
        next = next.split(quoteIdent(fieldName)).join(quoteIdent(projectedFieldName))
      }
      for (const projectedAliasRaw of outputColumns) {
        const projectedAlias = String(projectedAliasRaw ?? '').trim()
        if (!projectedAlias) continue
        const baseAlias = projectedAlias.replace(/\s+\([^)]*\)\s*$/g, '').trim()
        if (!baseAlias || baseAlias === projectedAlias) continue
        if (wrapperProjectedAliasForField(baseAlias) !== projectedAlias) continue
        next = next.split(quoteIdent(baseAlias)).join(quoteIdent(projectedAlias))
      }
      const orderFieldNames = Array.from(new Set([
        ...groupDims.map((d) => String(d?.field_name ?? '').trim()).filter(Boolean),
        ...outputColumns.map((c) => String(c ?? '').trim()).filter(Boolean),
        ...tooltipExtraSelects.map((c) => String(c?.alias ?? '').trim()).filter(Boolean),
        ...Array.from(orderExtraAliases).map((c) => String(c ?? '').trim()).filter(Boolean),
        'Measure Names',
        'Measure Values',
      ])).sort((a, b) => b.length - a.length)
      for (const fieldName of orderFieldNames) {
        const ident = quoteIdent(fieldName)
        next = next.split(ident).join(`${aliasTrimmed}.${ident}`)
      }
      const sanitized = sanitizeNullArtifacts(next)
      const items = splitTopLevelCsvForFinalSanitizer(sanitized)
      const kept = items.filter((item) => {
        const expr = stripOrderTermModifiersForFinalSanitizer(item)
        return !isNullishExpr(expr)
      })
      return kept.length > 0 ? kept.join(', ') : sanitized
    }
    const wrapper = (() => {
      const deferredWrapperConditions = deferredSharedFilters
        .map((filter) => buildDeferredWrapperFilterPredicate(filter))
        .filter((pred): pred is string => Boolean(String(pred || '').trim()))
      if (debugFilterLowering && deferredSharedFilters.length > 0) {
        try {
          console.log(`[FG-FILTER-WRAPPER] ${JSON.stringify({
            worksheetName: worksheetNameDebug,
            templateMode,
            deferredFilterCount: deferredSharedFilters.length,
            wrapperConditionCount: deferredWrapperConditions.length,
            filters: deferredSharedFilters.slice(0, 40).map((filter: any) => ({
              field: String(filter?.field_name ?? '').trim() || null,
              operator: String(filter?.operator ?? '').trim().toUpperCase() || null,
              filterIndex: filterIndexByRef.get(filter) ?? null,
              valueCount: Array.isArray(filter?.value) ? filter.value.length : (filter?.value == null ? 0 : 1),
              valuePreview: filterValuePreview(filter?.value),
            })),
            conditionPreview: deferredWrapperConditions.map((pred) => pred.slice(0, 500)).slice(0, 20),
          })}`)
        } catch { }
      }
      const buildOrderedWrapperQuery = (sourceSql: string, projectedColumns?: string[] | null): string => {
        const wsAlias = quoteIdent('__ws')
        const cols = Array.isArray(projectedColumns) ? projectedColumns.filter(Boolean) : []
        const selectClause = cols.length
          ? cols.map((c) => `${wsAlias}.${quoteIdent(c)}`).join(', ')
          : '*'
        const whereClause = deferredWrapperConditions.length > 0
          ? ` WHERE ${deferredWrapperConditions.join(' AND ')}`
          : ''
        const wrapperOrderBy = qualifyWrapperOrderBy(wsAlias, orderBy)
        return `SELECT ${selectClause} FROM (${sourceSql}) AS ${wsAlias}${whereClause} ORDER BY ${wrapperOrderBy}`
      }
      if (!densify) {
        return buildOrderedWrapperQuery(
          union,
          orderExtraAliases.size > 0 && outputColumns.length ? outputColumns : null,
        )
      }

      // Domain completion / densification (Tableau-like):
      // - Use DISTINCT *tuples* for row dims to avoid exploding invalid combinations in hierarchies.
      // - Cross-join row-domain × col-domain to emit missing row/col combinations.
      const dimNames = groupDims.map((d) => String(d?.field_name ?? '')).filter(Boolean)
      const dimCols = dimNames.map((n) => quoteIdent(n))
      const rowDimNames = dimNames.slice(0, Math.max(0, rowDimCount))
      const rowDimCols = dimCols.slice(0, Math.max(0, rowDimCount))
      const colDimNames = dimNames.slice(Math.max(0, rowDimCount))
      const colDimCols = dimCols.slice(Math.max(0, rowDimCount))
      const colDimRefs = groupDims
        .slice(Math.max(0, rowDimCount))
        .map((d) => groupDimFieldRef(d))
      const rowPage = Math.max(0, Math.floor(Number(options.rowPage ?? 0) || 0))
      const rowPageSize = Math.max(0, Math.floor(Number(options.rowPageSize ?? 0) || 0))
      const colPage = Math.max(0, Math.floor(Number(options.colPage ?? 0) || 0))
      const colPageSize = Math.max(0, Math.floor(Number(options.colPageSize ?? 0) || 0))
      const rowDomainPaging = rowDimCols.length > 0 && rowPageSize > 0
      const colDomainPaging = colDimCols.length > 0 && colPageSize > 0
      const dateLikeColIdx = colDimNames.findIndex((name) => /date/i.test(String(name ?? '')))
      const anchorCol = dateLikeColIdx >= 0 ? (colDimCols[dateLikeColIdx] || '') : ''
      const buildNonBlankDomainPredicate = (col: string, alias: string): string => {
        const ident = String(col || '').trim()
        if (!ident) return ''
        return `${alias}.${ident} IS NOT NULL AND TRIM(CAST(${alias}.${ident} AS VARCHAR)) <> ''`
      }
      const buildRowDomainBaseSql = (
        rowCols: string[],
        rowNames: string[],
        colAnchor: string,
      ): string => {
        if (!rowCols.length) return `SELECT 1 AS __one FROM __base LIMIT 1`
        let rowDateAnchorIdx = -1
        for (let idx = rowNames.length - 1; idx >= 0; idx--) {
          if (/date/i.test(String(rowNames[idx] ?? ''))) {
            rowDateAnchorIdx = idx
            break
          }
        }
        const rowDateAnchorCol = rowDateAnchorIdx >= 0 ? String(rowCols[rowDateAnchorIdx] ?? '').trim() : ''
        const whereParts: string[] = []
        const rowDateAnchorPred = buildNonBlankDomainPredicate(rowDateAnchorCol, 'b')
        if (rowDateAnchorPred) whereParts.push(rowDateAnchorPred)
        const colAnchorTrimmed = String(colAnchor || '').trim()
        if (colAnchorTrimmed) {
          const colAnchorPred = buildNonBlankDomainPredicate(colAnchorTrimmed, 'b')
          if (colAnchorPred) whereParts.push(colAnchorPred)
          const maxAnchorPred = buildNonBlankDomainPredicate(colAnchorTrimmed, 's')
          const maxAnchorWhere = maxAnchorPred ? ` WHERE ${maxAnchorPred}` : ''
          whereParts.push(
            `b.${colAnchorTrimmed} = (SELECT MAX(s.${colAnchorTrimmed}) FROM __base s${maxAnchorWhere})`,
          )
        }
        const whereClause = whereParts.length ? ` WHERE ${whereParts.join(' AND ')}` : ''
        return `SELECT DISTINCT ${rowCols.join(', ')} FROM __base b${whereClause}`
      }
      const rowDomainBase = buildRowDomainBaseSql(rowDimCols, rowDimNames, anchorCol)
      const rowDomain = rowDomainPaging
        ? `__row_domain_unpaged AS (${rowDomainBase}),
        __row_domain AS (
          SELECT * FROM __row_domain_unpaged
          ORDER BY ${rowDimCols.join(', ')}
          LIMIT ${rowPageSize}
          OFFSET ${rowPage * rowPageSize}
        )`
        : `__row_domain AS (${rowDomainBase})`

      const {
        sql: colDomainBase,
        seededDateColIdxs,
      } = buildColDomainCte(
        colDimNames,
        colDimCols,
        colDimExprs,
        colDimRefs,
        colDomainPaging ? '__col_domain_unpaged' : '__col_domain',
        rowDomainPaging ? rowDimCols : null,
      )
      const colDomainOrder = [...colDimCols, quoteIdent('Measure Names')].filter(Boolean).map((col) => `c.${col}`).join(', ')
      const colDomain = colDomainPaging
        ? `${colDomainBase},
        __col_domain AS (
          SELECT * FROM __col_domain_unpaged c
          ORDER BY ${colDomainOrder}
          LIMIT ${colPageSize}
          OFFSET ${colPage * colPageSize}
        )`
        : colDomainBase

      const denseSelect = [
        ...(rowDimCols.length ? rowDimCols.map((c) => `r.${c} AS ${c}`) : []),
        ...colDimCols.map((c) => `c.${c} AS ${c}`),
        `c.${quoteIdent('Measure Names')} AS ${quoteIdent('Measure Names')}`,
      ].join(', ')
      const dense = `__dense AS (
        SELECT ${denseSelect}
        FROM __row_domain r
        CROSS JOIN __col_domain c
      )`

      const joinOn = [
        ...rowDimCols.map((col) => `b.${col} IS NOT DISTINCT FROM d.${col}`),
        ...colDimCols
          .map((col, idx) => ({ col, idx }))
          .filter(({ idx }) => !seededDateColIdxs.has(idx))
          .map(({ col }) => `b.${col} IS NOT DISTINCT FROM d.${col}`),
        `b.${quoteIdent('Measure Names')} = d.${quoteIdent('Measure Names')}`,
      ].join(' AND ')
      const filteredBaseJoinOn = [
        ...rowDimCols.map((col) => `b.${col} IS NOT DISTINCT FROM r.${col}`),
        ...colDimCols
          .map((col, idx) => ({ col, idx }))
          .filter(({ idx }) => !seededDateColIdxs.has(idx))
          .map(({ col }) => `b.${col} IS NOT DISTINCT FROM c.${col}`),
        `b.${quoteIdent('Measure Names')} = c.${quoteIdent('Measure Names')}`,
      ].join(' AND ')

      const select = [
        ...dimCols.map((col) => `d.${col} AS ${col}`),
        `d.${quoteIdent('Measure Names')} AS ${quoteIdent('Measure Names')}`,
        `b.${quoteIdent('Measure Values')} AS ${quoteIdent('Measure Values')}`,
        ...tooltipExtraSelects.map((c) => `b.${quoteIdent(c.alias)} AS ${quoteIdent(c.alias)}`),
        ...Array.from(orderExtraAliases).map((alias) => `b.${quoteIdent(alias)} AS ${quoteIdent(alias)}`),
      ].join(', ')
      const baseCteMaterialization =
        rowDomainPaging || colDomainPaging
          ? '__base AS'
          : '__base AS MATERIALIZED'
      const filteredBaseCte =
        rowDomainPaging || colDomainPaging
          ? `,
        __base_filtered AS (
          SELECT b.*
          FROM __base b
          INNER JOIN __row_domain r
            ON ${[
              ...rowDimCols.map((col) => `b.${col} IS NOT DISTINCT FROM r.${col}`),
            ].join(' AND ') || 'TRUE'}
          INNER JOIN __col_domain c
            ON ${filteredBaseJoinOn}
        ),`
          : ''
      const finalBaseRef = rowDomainPaging || colDomainPaging ? '__base_filtered' : '__base'
      const buildDenseCoreQuery = (baseSql: string): string => `
        WITH ${baseCteMaterialization} (${baseSql}),
        ${rowDomain},
        ${colDomain},
        ${filteredBaseCte ? filteredBaseCte.slice(2) : ''}
        ${dense}
        SELECT ${select}
        FROM __dense d
        LEFT JOIN ${finalBaseRef} b
          ON ${joinOn}
      `.trim()

      const denseCore = buildDenseCoreQuery(union)
      return buildOrderedWrapperQuery(
        denseCore,
        orderExtraAliases.size > 0 && outputColumns.length ? outputColumns : null,
      )
    })()

    const bounded = limit > 0 ? `${wrapper} LIMIT ${Math.floor(limit)}` : wrapper

    // Deterministic heuristic chooser for multi-statement execution plans.
    // Goal: reduce parse/plan overhead and enable reuse across related queries in-session.
    if (collectExecPlan) {
      try {
        const unionLen = union.length
        const hasGroupingSets = /\bGROUPING\s+SETS\b/i.test(union)
        const hasLod = /__lod_/i.test(union)
        const countWindowOverClausesCapped = (sqlIn: string, maxCount = 8): number => {
          const sql = String(sqlIn ?? '')
          if (!sql || maxCount <= 0) return 0
          let count = 0
          let inStr = false
          let inIdent = false
          let inLineComment = false
          let inBlockComment = false
          for (let i = 0; i < sql.length - 3; i += 1) {
            const ch = sql[i]
            const next = sql[i + 1]
            if (inLineComment) {
              if (ch === '\n') inLineComment = false
              continue
            }
            if (inBlockComment) {
              if (ch === '*' && next === '/') {
                inBlockComment = false
                i += 1
              }
              continue
            }
            if (!inStr && !inIdent && ch === '-' && next === '-') {
              inLineComment = true
              i += 1
              continue
            }
            if (!inStr && !inIdent && ch === '/' && next === '*') {
              inBlockComment = true
              i += 1
              continue
            }
            if (ch === "'") {
              if (inStr && next === "'") {
                i += 1
                continue
              }
              if (!inIdent) inStr = !inStr
              continue
            }
            if (!inStr && ch === '"') {
              if (inIdent && next === '"') {
                i += 1
                continue
              }
              inIdent = !inIdent
              continue
            }
            if (inStr || inIdent) continue
            if (sql.slice(i, i + 4).toUpperCase() !== 'OVER') continue
            const prev = i > 0 ? sql[i - 1] : ''
            const after = i + 4 < sql.length ? sql[i + 4] : ''
            if ((prev && /[A-Za-z0-9_]/.test(prev)) || (after && /[A-Za-z0-9_]/.test(after))) continue
            let j = i + 4
            while (j < sql.length && /\s/.test(sql[j])) j += 1
            if (sql[j] !== '(') continue
            count += 1
            if (count >= maxCount) return count
            i = j
          }
          return count
        }
        const overCount = countWindowOverClausesCapped(bounded, 8)
        const wantsPlan =
          // A collected exec plan is a physical-plan contract request, not a best-effort hint.
          // The live paging/shared-base path depends on these agg-base boundaries deterministically.
          collectExecPlan ||
          emitExecPlan ||
          unionLen > 180_000 ||
          bounded.length > 260_000 ||
          overCount >= 8 ||
          hasGroupingSets ||
          hasLod ||
          Boolean(densify) ||
          Boolean(includeTotals)

        if (wantsPlan) {
          // Use a deterministic temp table name derived from the actual materialized SQL.
          // Query-shape heuristics alone are too coarse and can alias distinct plan bases.
          const hash32 = (s: string): number => {
            let h = 2166136261
            for (let i = 0; i < s.length; i++) {
              h ^= s.charCodeAt(i)
              h = Math.imul(h, 16777619)
            }
            return h >>> 0
          }
          const materializedSqlHash = hash32(union).toString(16)
          const tmp = `fg_plan_${materializedSqlHash}`

          // Rebuild the wrapper against the materialized base.
          const buildPlanWrapperQuery = (sourceSql: string, projectedColumns?: string[] | null): string => {
            const wsAlias = quoteIdent('__ws')
            const cols = Array.isArray(projectedColumns) ? projectedColumns.filter(Boolean) : []
            const selectClause = cols.length
              ? cols.map((c) => `${wsAlias}.${quoteIdent(c)}`).join(', ')
              : '*'
            const deferredWrapperConditions = deferredSharedFilters
              .map((filter) => buildDeferredWrapperFilterPredicate(filter))
              .filter((pred): pred is string => Boolean(String(pred || '').trim()))
            if (debugFilterLowering && deferredSharedFilters.length > 0) {
              try {
                console.log(`[FG-FILTER-WRAPPER] ${JSON.stringify({
                  worksheetName: worksheetNameDebug,
                  templateMode,
                  plan: true,
                  deferredFilterCount: deferredSharedFilters.length,
                  wrapperConditionCount: deferredWrapperConditions.length,
                  filters: deferredSharedFilters.slice(0, 40).map((filter: any) => ({
                    field: String(filter?.field_name ?? '').trim() || null,
                    operator: String(filter?.operator ?? '').trim().toUpperCase() || null,
                    filterIndex: filterIndexByRef.get(filter) ?? null,
                    valueCount: Array.isArray(filter?.value) ? filter.value.length : (filter?.value == null ? 0 : 1),
                    valuePreview: filterValuePreview(filter?.value),
                  })),
                  conditionPreview: deferredWrapperConditions.map((pred) => pred.slice(0, 500)).slice(0, 20),
                })}`)
              } catch { }
            }
            const whereClause = deferredWrapperConditions.length > 0
              ? ` WHERE ${deferredWrapperConditions.join(' AND ')}`
              : ''
            const wrapperOrderBy = qualifyWrapperOrderBy(wsAlias, orderBy)
            return `SELECT ${selectClause} FROM (${sourceSql}) AS ${wsAlias}${whereClause} ORDER BY ${wrapperOrderBy}`
          }
          const buildPlanDenseCoreQuery = (baseSql: string): string => {
            if (!densify) return baseSql
            const dimNames = groupDims.map((d) => String(d?.field_name ?? '')).filter(Boolean)
            const dimCols = dimNames.map((n) => quoteIdent(n))
            const rowDimNames = dimNames.slice(0, Math.max(0, rowDimCount))
            const rowDimCols = dimCols.slice(0, Math.max(0, rowDimCount))
            const colDimNames = dimNames.slice(Math.max(0, rowDimCount))
            const colDimCols = dimCols.slice(Math.max(0, rowDimCount))
            const colDimRefs = groupDims
              .slice(Math.max(0, rowDimCount))
              .map((d) => groupDimFieldRef(d))
            const rowPage = Math.max(0, Math.floor(Number(options.rowPage ?? 0) || 0))
            const rowPageSize = Math.max(0, Math.floor(Number(options.rowPageSize ?? 0) || 0))
            const colPage = Math.max(0, Math.floor(Number(options.colPage ?? 0) || 0))
            const colPageSize = Math.max(0, Math.floor(Number(options.colPageSize ?? 0) || 0))
            const rowDomainPaging = rowDimCols.length > 0 && rowPageSize > 0
            const colDomainPaging = colDimCols.length > 0 && colPageSize > 0
            const dateLikeColIdx = colDimNames.findIndex((name) => /date/i.test(String(name ?? '')))
            const anchorCol = dateLikeColIdx >= 0 ? (colDimCols[dateLikeColIdx] || '') : ''
            const buildNonBlankDomainPredicate = (col: string, alias: string): string => {
              const ident = String(col || '').trim()
              if (!ident) return ''
              return `${alias}.${ident} IS NOT NULL AND TRIM(CAST(${alias}.${ident} AS VARCHAR)) <> ''`
            }
            const buildRowDomainBaseSql = (
              rowCols: string[],
              rowNames: string[],
              colAnchor: string,
            ): string => {
              if (!rowCols.length) return `SELECT 1 AS __one FROM __base LIMIT 1`
              let rowDateAnchorIdx = -1
              for (let idx = rowNames.length - 1; idx >= 0; idx--) {
                if (/date/i.test(String(rowNames[idx] ?? ''))) {
                  rowDateAnchorIdx = idx
                  break
                }
              }
              const rowDateAnchorCol = rowDateAnchorIdx >= 0 ? String(rowCols[rowDateAnchorIdx] ?? '').trim() : ''
              const whereParts: string[] = []
              const rowDateAnchorPred = buildNonBlankDomainPredicate(rowDateAnchorCol, 'b')
              if (rowDateAnchorPred) whereParts.push(rowDateAnchorPred)
              const colAnchorTrimmed = String(colAnchor || '').trim()
              if (colAnchorTrimmed) {
                const colAnchorPred = buildNonBlankDomainPredicate(colAnchorTrimmed, 'b')
                if (colAnchorPred) whereParts.push(colAnchorPred)
                const maxAnchorPred = buildNonBlankDomainPredicate(colAnchorTrimmed, 's')
                const maxAnchorWhere = maxAnchorPred ? ` WHERE ${maxAnchorPred}` : ''
                whereParts.push(
                  `b.${colAnchorTrimmed} = (SELECT MAX(s.${colAnchorTrimmed}) FROM __base s${maxAnchorWhere})`,
                )
              }
              const whereClause = whereParts.length ? ` WHERE ${whereParts.join(' AND ')}` : ''
              return `SELECT DISTINCT ${rowCols.join(', ')} FROM __base b${whereClause}`
            }
            const rowDomainBase = buildRowDomainBaseSql(rowDimCols, rowDimNames, anchorCol)
            const rowDomain = rowDomainPaging
              ? `__row_domain_unpaged AS (${rowDomainBase}),
        __row_domain AS (
          SELECT * FROM __row_domain_unpaged
          ORDER BY ${rowDimCols.join(', ')}
          LIMIT ${rowPageSize}
          OFFSET ${rowPage * rowPageSize}
        )`
              : `__row_domain AS (${rowDomainBase})`
            const {
              sql: colDomainBase,
              seededDateColIdxs,
            } = buildColDomainCte(
              colDimNames,
              colDimCols,
              colDimExprs,
              colDimRefs,
              colDomainPaging ? '__col_domain_unpaged' : '__col_domain',
              rowDomainPaging ? rowDimCols : null,
            )
            const colDomainOrder = [...colDimCols, quoteIdent('Measure Names')].filter(Boolean).map((col) => `c.${col}`).join(', ')
            const colDomain = colDomainPaging
              ? `${colDomainBase},
        __col_domain AS (
          SELECT * FROM __col_domain_unpaged c
          ORDER BY ${colDomainOrder}
          LIMIT ${colPageSize}
          OFFSET ${colPage * colPageSize}
        )`
              : colDomainBase
            const denseSelect = [
              ...(rowDimCols.length ? rowDimCols.map((c) => `r.${c} AS ${c}`) : []),
              ...colDimCols.map((c) => `c.${c} AS ${c}`),
              `c.${quoteIdent('Measure Names')} AS ${quoteIdent('Measure Names')}`,
            ].join(', ')
            const dense = `__dense AS (
        SELECT ${denseSelect}
        FROM __row_domain r
        CROSS JOIN __col_domain c
      )`
            const joinOn = [
              ...rowDimCols.map((col) => `b.${col} IS NOT DISTINCT FROM d.${col}`),
              ...colDimCols
                .map((col, idx) => ({ col, idx }))
                .filter(({ idx }) => !seededDateColIdxs.has(idx))
                .map(({ col }) => `b.${col} IS NOT DISTINCT FROM d.${col}`),
              `b.${quoteIdent('Measure Names')} = d.${quoteIdent('Measure Names')}`,
            ].join(' AND ')
            const filteredBaseJoinOn = [
              ...rowDimCols.map((col) => `b.${col} IS NOT DISTINCT FROM r.${col}`),
              ...colDimCols
                .map((col, idx) => ({ col, idx }))
                .filter(({ idx }) => !seededDateColIdxs.has(idx))
                .map(({ col }) => `b.${col} IS NOT DISTINCT FROM c.${col}`),
              `b.${quoteIdent('Measure Names')} = c.${quoteIdent('Measure Names')}`,
            ].join(' AND ')
            const select = [
              ...dimCols.map((col) => `d.${col} AS ${col}`),
              `d.${quoteIdent('Measure Names')} AS ${quoteIdent('Measure Names')}`,
              `b.${quoteIdent('Measure Values')} AS ${quoteIdent('Measure Values')}`,
              ...tooltipExtraSelects.map((c) => `b.${quoteIdent(c.alias)} AS ${quoteIdent(c.alias)}`),
              ...Array.from(orderExtraAliases).map((alias) => `b.${quoteIdent(alias)} AS ${quoteIdent(alias)}`),
            ].join(', ')
            const baseCteMaterialization =
              rowDomainPaging || colDomainPaging
                ? '__base AS'
                : '__base AS MATERIALIZED'
            const filteredBaseCte =
              rowDomainPaging || colDomainPaging
                ? `,
        __base_filtered AS (
          SELECT b.*
          FROM __base b
          INNER JOIN __row_domain r
            ON ${[
              ...rowDimCols.map((col) => `b.${col} IS NOT DISTINCT FROM r.${col}`),
            ].join(' AND ') || 'TRUE'}
          INNER JOIN __col_domain c
            ON ${filteredBaseJoinOn}
        ),`
                : ''
            const finalBaseRef = rowDomainPaging || colDomainPaging ? '__base_filtered' : '__base'
            return `
        WITH ${baseCteMaterialization} (${baseSql}),
        ${rowDomain},
        ${colDomain},
        ${filteredBaseCte ? filteredBaseCte.slice(2) : ''}
        ${dense}
        SELECT ${select}
        FROM __dense d
        LEFT JOIN ${finalBaseRef} b
          ON ${joinOn}
      `.trim()
          }
          const wrapPlanSourceSql = (sourceSql: string): string => {
            const projectedColumns = orderExtraAliases.size > 0 && outputColumns.length ? outputColumns : null
            const q = buildPlanWrapperQuery(
              buildPlanDenseCoreQuery(sourceSql),
              projectedColumns,
            )
            const normalized = pruneNullishTopLevelGroupOrderTerms(sanitizeNullArtifacts(q))
            return limit > 0 ? `${normalized} LIMIT ${Math.floor(limit)}` : normalized
          }
          const optimizeWindowInputSourceSql = (sourceSqlRaw: string): string => {
            const replaceExactOrWhitespaceStable = (sqlIn: string, rawNeedle: string, replacement: string): string => {
              const source = String(sqlIn ?? '')
              const needle = String(rawNeedle ?? '').trim()
              if (!source || !needle) return source
              if (source.includes(needle)) return source.split(needle).join(replacement)
              const escaped = needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '\\s+')
              if (!escaped) return source
              try {
                return source.replace(new RegExp(escaped, 'g'), replacement)
              } catch {
                return source
              }
            }
            const factorRepeatedTopLevelProjectedExprs = (sqlIn: string): string => {
              const findBalancedCallClose = (src: string, openParenIdx: number): number => {
                let depth = 0
                let inStr = false
                let inIdent = false
                for (let idx = openParenIdx; idx < src.length; idx += 1) {
                  const ch = src[idx]
                  if (inStr) {
                    if (ch === "'" && src[idx + 1] === "'") {
                      idx += 1
                      continue
                    }
                    if (ch === "'") inStr = false
                    continue
                  }
                  if (inIdent) {
                    if (ch === '"') inIdent = false
                    continue
                  }
                  if (ch === "'") {
                    inStr = true
                    continue
                  }
                  if (ch === '"') {
                    inIdent = true
                    continue
                  }
                  if (ch === '(') {
                    depth += 1
                    continue
                  }
                  if (ch === ')') {
                    depth -= 1
                    if (depth === 0) return idx
                  }
                }
                return -1
              }
              const extractRepeatedMonthDateTruncExprs = (
                exprs: string[],
                aliasSet: Set<string>,
              ): Array<{ expr: string; alias: string }> => {
                const candidateCounts = new Map<string, number>()
                const candidateExprByKey = new Map<string, string>()
                const addCandidate = (rawExpr: string) => {
                  const expr = String(rawExpr || '').trim()
                  if (!expr || expr.length < 24) return
                  const key = normalizeSqlExpr(expr)
                  if (!key) return
                  candidateExprByKey.set(key, expr)
                  candidateCounts.set(key, (candidateCounts.get(key) ?? 0) + 1)
                }
                for (const expr of exprs) {
                  const src = String(expr || '')
                  const upper = src.toUpperCase()
                  for (let idx = 0; idx < src.length; idx += 1) {
                    if (upper.slice(idx, idx + 'DATE_TRUNC'.length) !== 'DATE_TRUNC') continue
                    const before = src[idx - 1]
                    if (before && /[A-Za-z0-9_]/.test(before)) continue
                    let openParenIdx = idx + 'DATE_TRUNC'.length
                    while (openParenIdx < src.length && /\s/.test(src[openParenIdx] || '')) openParenIdx += 1
                    if (src[openParenIdx] !== '(') continue
                    let argStart = openParenIdx + 1
                    while (argStart < src.length && /\s/.test(src[argStart] || '')) argStart += 1
                    if (src.slice(argStart, argStart + 7).toLowerCase() !== "'month'") continue
                    const closeParenIdx = findBalancedCallClose(src, openParenIdx)
                    if (closeParenIdx <= openParenIdx) continue
                    addCandidate(src.slice(idx, closeParenIdx + 1))
                    idx = closeParenIdx
                  }
                }
                let aliasIdx = 0
                const candidates = Array.from(candidateCounts.entries())
                  .filter(([, count]) => count >= 2)
                  .map(([key]) => String(candidateExprByKey.get(key) || '').trim())
                  .filter(Boolean)
                  .sort((left, right) => right.length - left.length)
                return candidates.map((expr) => {
                  let alias = quoteIdent(`__fg_expr_${aliasIdx++}`)
                  while (aliasSet.has(alias.toLowerCase())) alias = quoteIdent(`__fg_expr_${aliasIdx++}`)
                  aliasSet.add(alias.toLowerCase())
                  return { expr, alias }
                })
              }
              const rewriteExprWithAliasMappings = (
                exprIn: string,
                mappings: Array<{ expr: string; alias: string }>,
                sourceAliasRef: string,
              ): string => {
                let expr = String(exprIn || '')
                for (const mapping of mappings) {
                  expr = replaceExactOrWhitespaceStable(expr, mapping.expr, `${sourceAliasRef}.${mapping.alias}`)
                }
                return expr
              }
              const sql = String(sqlIn || '').trim()
              const top = extractTopLevelSelectStage(sql)
              const sourceAlias = String(extractTopLevelFromSubqueryStage(sql)?.sourceAlias || '').trim()
              const fromTail = String(top?.fromTail || '').trim()
              const selectItems = Array.isArray(top?.selectItems) ? top!.selectItems : []
              if (!sql || !sourceAlias || !fromTail || selectItems.length <= 1) return sql
              if (selectItemsContainWildcard(selectItems)) return sql
              const parsedItems = selectItems
                .map((itemRaw) => {
                  const item = String(itemRaw || '').trim()
                  const alias = extractSelectItemAlias(item)
                  if (!item || !alias) return null
                  const aliasMatch = /\bAS\s+("[^"]+"|[A-Za-z_][A-Za-z0-9_]*)\s*$/i.exec(item)
                  const expr = String(aliasMatch?.index != null ? item.slice(0, aliasMatch.index).trim() : '').trim()
                  if (!expr) return null
                  return { item, alias, expr }
                })
                .filter(Boolean) as Array<{ item: string; alias: string; expr: string }>
              if (parsedItems.length <= 1) return sql
              const reservedAliases = new Set(parsedItems.map(({ alias }) => String(alias || '').trim().toLowerCase()).filter(Boolean))
              const nestedScalarMappings = extractRepeatedMonthDateTruncExprs(
                parsedItems.map(({ expr }) => expr),
                reservedAliases,
              )
              const rewrittenParsedItems = parsedItems.map((item) => ({
                ...item,
                expr: rewriteExprWithAliasMappings(item.expr, nestedScalarMappings, sourceAlias),
              }))
              const candidates = parsedItems.filter(({ expr }) => {
                if (/\bOVER\s*\(/i.test(expr)) return false
                return /\bCASE\b/i.test(expr) || /\bGROUPING\s*\(/i.test(expr) || /\bDATE_TRUNC\s*\(\s*'month'/i.test(expr)
              })
              if (candidates.length <= 0 && nestedScalarMappings.length <= 0) return sql
              const usedCandidates = candidates.filter((candidate) =>
                rewrittenParsedItems.some((item) => {
                  if (item.alias === candidate.alias) return false
                  const rewritten = replaceExactOrWhitespaceStable(item.expr, candidate.expr, `${sourceAlias}.${candidate.alias}`)
                  return rewritten !== item.expr
                }),
              )
              if (usedCandidates.length <= 0 && nestedScalarMappings.length <= 0) return sql
              const innerSelect = [
                `${sourceAlias}.*`,
                ...nestedScalarMappings.map((mapping) => `${mapping.expr} AS ${mapping.alias}`),
                ...usedCandidates.map((candidate) => `${candidate.expr} AS ${candidate.alias}`),
              ].join(', ')
              const outerSelect = parsedItems.map((item) => {
                let expr = rewriteExprWithAliasMappings(item.expr, nestedScalarMappings, sourceAlias)
                for (const candidate of usedCandidates) {
                  if (item.alias === candidate.alias) {
                    expr = `${sourceAlias}.${candidate.alias}`
                    continue
                  }
                  expr = replaceExactOrWhitespaceStable(expr, candidate.expr, `${sourceAlias}.${candidate.alias}`)
                }
                return `${expr} AS ${item.alias}`
              }).join(', ')
              return `SELECT ${outerSelect} FROM (SELECT ${innerSelect} ${fromTail}) AS ${sourceAlias}`
            }
            let nextSql = String(sourceSqlRaw || '').trim()
            if (!nextSql) return nextSql
            for (let pass = 0; pass < 4; pass += 1) {
              const scalarFactoredSql = factorRepeatedTopLevelProjectedExprs(nextSql)
              const windowFactoredSql = factorRepeatedTopLevelWindowExprs(scalarFactoredSql)
              if (!windowFactoredSql || windowFactoredSql === nextSql) break
              nextSql = windowFactoredSql
            }
            return nextSql
          }
          const stagedChunkPlanEnabled =
            useStagedMnvProjection &&
            stagedChunkPlanParts.length > 0 &&
            (stagedChunkPlanParts.length > 1 || artifactExportMode)
          let planFinalSql = ''
          if (stagedChunkPlanEnabled) {
            const chunkTables = stagedChunkPlanParts.map((_part, idx) => `fg_plan_${materializedSqlHash}_chunk_${idx}`)
            const reusableAggBaseTable = `fg_plan_${materializedSqlHash}_agg_base`
            const sharedStageTables = stagedChunkSharedStages.map((sharedStage, idx) => ({
              ...sharedStage,
              tableName: `fg_plan_${materializedSqlHash}_shared_stage_${idx}`,
            }))
            const reusableAggBaseTables = stagedChunkReusableAggBases.map((reusableAggBase, idx) => ({
              ...reusableAggBase,
              tableName: idx === 0
                ? reusableAggBaseTable
                : `fg_plan_${materializedSqlHash}_agg_base_${idx}`,
              sourceTableName: idx === 0
                ? `fg_plan_${materializedSqlHash}_agg_base_src`
                : `fg_plan_${materializedSqlHash}_agg_base_${idx}_src`,
            }))
            const reusableWrapperStageTables = stagedChunkReusableWrapperStages.map((sharedStage, idx) => ({
              ...sharedStage,
              tableName: `fg_plan_${materializedSqlHash}_wrapper_stage_${idx}`,
            }))
            const bindPlanTempTables = (sqlRaw: string): string => {
              let sql = String(sqlRaw || '')
              for (const sharedStage of sharedStageTables) {
                sql = sql.replaceAll(sharedStage.placeholderName, quoteIdent(sharedStage.tableName))
              }
              for (const reusableAggBase of reusableAggBaseTables) {
                sql = sql.replaceAll(reusableAggBase.placeholderName, quoteIdent(reusableAggBase.tableName))
              }
              for (const sharedStage of reusableWrapperStageTables) {
                sql = sql.replaceAll(sharedStage.placeholderName, quoteIdent(sharedStage.tableName))
              }
              return sql
            }
            const splitReusableAggBaseWindowSource = (
              materializedSqlRaw: string,
              sourceTableName: string,
            ): { sourceSql: string; finalSql: string } | null => {
              const finalizeSplitWindowStageSql = (sqlRaw: string): string => {
                const finalized = finalizeSql(String(sqlRaw || ''))
                if (!finalized) return finalized
                const optimized = finalizeSql(optimizeWindowInputSourceSql(finalized))
                return optimized || finalized
              }
              const canProjectDirectlyFromAlias = (
                selectItemsRaw: string[],
                sourceAliasRaw: string,
                opts?: { allowWindow?: boolean },
              ): boolean => {
                const sourceAlias = String(sourceAliasRaw || '').trim()
                if (!sourceAlias || selectItemsRaw.length <= 0 || selectItemsContainWildcard(selectItemsRaw)) return false
                for (const itemRaw of selectItemsRaw) {
                  const item = String(itemRaw || '').trim()
                  if (!item) return false
                  const aliasMatch = /\bAS\s+("[^"]+"|[A-Za-z_][A-Za-z0-9_]*)\s*$/i.exec(item)
                  const expr = String(aliasMatch?.index != null ? item.slice(0, aliasMatch.index).trim() : item).trim()
                  if (!expr) return false
                  if (/\bANY_VALUE\s*\(/i.test(expr)) return false
                  if (!opts?.allowWindow && /\bOVER\s*\(/i.test(expr)) return false
                  if (/\b__fg_(?:win|expr)_\d+\b/i.test(expr)) return false
                  const qualifierMatches = Array.from(expr.matchAll(/("[^"]+"|[A-Za-z_][A-Za-z0-9_]*)\s*\./g))
                  for (const qualifierMatch of qualifierMatches) {
                    const qualifier = String(qualifierMatch?.[1] ?? '').trim()
                    if (qualifier && qualifier !== sourceAlias) return false
                  }
                }
                return true
              }
              const buildDirectSplitWindowStageSql = (
                sqlRaw: string,
                sourceTableNameRaw: string,
              ): string | null => {
                const extractFirstWindowStage = (
                  sqlTextRaw: string,
                  maxDepth = 6,
                ): { sourceAlias: string; selectItems: string[] } | null => {
                  let currentSql = finalizeSql(String(sqlTextRaw || ''))
                  for (let depth = 0; depth < maxDepth; depth += 1) {
                    const topStage = extractTopLevelSelectStage(currentSql)
                    const selectItems = Array.isArray(topStage?.selectItems) ? topStage.selectItems : []
                    const sourceAlias = String(extractTopLevelFromSubqueryStage(currentSql)?.sourceAlias || '').trim()
                    if (
                      sourceAlias &&
                      selectItems.length > 0 &&
                      selectItems.some((item) => /\bOVER\s*\(/i.test(String(item || '')))
                    ) {
                      return { sourceAlias, selectItems }
                    }
                    const nextStage = extractTopLevelFromSubqueryStage(currentSql)
                    if (!nextStage) break
                    currentSql = finalizeSql(`SELECT ${nextStage.selectItems.join(', ')} ${nextStage.fromTail}`)
                  }
                  return null
                }
                const outerStage = extractTopLevelSelectStage(sqlRaw)
                const outerSelectItems = Array.isArray(outerStage?.selectItems) ? outerStage.selectItems : []
                const outerSourceAlias = String(extractTopLevelFromSubqueryStage(sqlRaw)?.sourceAlias || '').trim()
                const windowStage = extractFirstWindowStage(sqlRaw)
                if (!windowStage || !outerSourceAlias || outerSelectItems.length <= 0) return null
                if (!canProjectDirectlyFromAlias(windowStage.selectItems, windowStage.sourceAlias, { allowWindow: true })) {
                  return null
                }
                const directWindowStageSql = finalizeSplitWindowStageSql(
                  `SELECT ${windowStage.selectItems.join(', ')} FROM ${quoteIdent(sourceTableNameRaw)} AS ${windowStage.sourceAlias}`,
                )
                if (!directWindowStageSql) return null
                if (!canProjectDirectlyFromAlias(outerSelectItems, outerSourceAlias)) return null
                const reboundOuterAlias = quoteIdent('__fg_split_win')
                const reboundOuterSelectItems = outerSelectItems.map((itemRaw) =>
                  replaceSqlAliasQualifier(String(itemRaw || ''), reboundOuterAlias),
                )
                return finalizeSplitWindowStageSql(
                  `SELECT ${reboundOuterSelectItems.join(', ')} FROM (${directWindowStageSql}) AS ${reboundOuterAlias}`,
                )
              }
              const collapseRedundantGroupedPassthroughWrappers = (sqlRaw: string): string => {
                const normalizeAliasNameLocal = (raw: string): string => {
                  const trimmed = String(raw || '').trim()
                  const unquoted =
                    trimmed.startsWith('"') && trimmed.endsWith('"')
                      ? trimmed.slice(1, -1)
                      : trimmed
                  return unquoted.trim().toLowerCase()
                }
                const parseTopLevelGroupByItems = (sqlTextRaw: string): string[] => {
                  const sqlText = String(sqlTextRaw || '').trim()
                  const groupByIdx = findTopLevelKeywordSql(sqlText, 'GROUP BY')
                  if (groupByIdx < 0) return []
                  const endCandidates = [
                    findTopLevelKeywordSql(sqlText, 'HAVING'),
                    findTopLevelKeywordSql(sqlText, 'QUALIFY'),
                    findTopLevelKeywordSql(sqlText, 'ORDER BY'),
                    findTopLevelKeywordSql(sqlText, 'LIMIT'),
                    findTopLevelKeywordSql(sqlText, 'WINDOW'),
                  ].filter((idx) => idx > groupByIdx)
                  const endIdx = endCandidates.length > 0 ? Math.min(...endCandidates) : sqlText.length
                  return splitTopLevelSqlList(sqlText.slice(groupByIdx + 'GROUP BY'.length, endIdx).trim())
                    .map((item) => String(item || '').trim())
                    .filter(Boolean)
                }
                const hasBlockingTopLevelClauses = (sqlTextRaw: string): boolean => {
                  const sqlText = String(sqlTextRaw || '').trim()
                  return (
                    findTopLevelKeywordSql(sqlText, 'HAVING') >= 0 ||
                    findTopLevelKeywordSql(sqlText, 'QUALIFY') >= 0 ||
                    findTopLevelKeywordSql(sqlText, 'ORDER BY') >= 0 ||
                    findTopLevelKeywordSql(sqlText, 'LIMIT') >= 0 ||
                    findTopLevelKeywordSql(sqlText, 'WINDOW') >= 0
                  )
                }
                const parseQualifiedAliasRef = (
                  exprRaw: string,
                  sourceAliasRaw: string,
                ): { aliasKey: string; aliasName: string } | null => {
                  const expr = String(exprRaw || '').trim()
                  const sourceAliasKey = normalizeAliasNameLocal(String(sourceAliasRaw || ''))
                  if (!expr) return null
                  const quoted = /^(?:(\"[^\"]+\"|[A-Za-z_][A-Za-z0-9_]*)\s*\.\s*)?\"((?:[^\"]|\"\")+)\"$/i.exec(expr)
                  if (quoted) {
                    const qualifierKey = normalizeAliasNameLocal(String(quoted[1] || ''))
                    if (qualifierKey && sourceAliasKey && qualifierKey !== sourceAliasKey) return null
                    const aliasName = String(quoted[2] || '').replace(/""/g, '"').trim()
                    if (!aliasName) return null
                    return { aliasKey: normalizeAliasNameLocal(aliasName), aliasName }
                  }
                  const bare = /^(?:(\"[^\"]+\"|[A-Za-z_][A-Za-z0-9_]*)\s*\.\s*)?([A-Za-z_][A-Za-z0-9_]*)$/i.exec(expr)
                  if (!bare) return null
                  const qualifierKey = normalizeAliasNameLocal(String(bare[1] || ''))
                  if (qualifierKey && sourceAliasKey && qualifierKey !== sourceAliasKey) return null
                  const aliasName = String(bare[2] || '').trim()
                  if (!aliasName) return null
                  return { aliasKey: normalizeAliasNameLocal(aliasName), aliasName }
                }
                const parsePassthroughBinding = (
                  itemRaw: string,
                  sourceAliasRaw: string,
                ): { aliasKey: string; aliasName: string; outputAlias: string; kind: 'direct' | 'any_value' } | null => {
                  const item = String(itemRaw || '').trim()
                  const outputAlias = extractSelectItemAlias(item)
                  const aliasMatch = /\bAS\s+("[^"]+"|[A-Za-z_][A-Za-z0-9_]*)\s*$/i.exec(item)
                  const expr = String(aliasMatch?.index != null ? item.slice(0, aliasMatch.index).trim() : item).trim()
                  const outputAliasKey = normalizeAliasNameLocal(outputAlias)
                  if (!outputAliasKey || !expr) return null
                  const directRef = parseQualifiedAliasRef(expr, sourceAliasRaw)
                  if (directRef && directRef.aliasKey === outputAliasKey) {
                    return { ...directRef, outputAlias, kind: 'direct' }
                  }
                  const anyValueMatch = /^ANY_VALUE\s*\(([\s\S]+)\)$/i.exec(expr)
                  const anyValueRef = parseQualifiedAliasRef(String(anyValueMatch?.[1] || ''), sourceAliasRaw)
                  if (anyValueRef && anyValueRef.aliasKey === outputAliasKey) {
                    return { ...anyValueRef, outputAlias, kind: 'any_value' }
                  }
                  return null
                }
                let currentSql = finalizeSql(String(sqlRaw || ''))
                for (let depth = 0; depth < 6; depth += 1) {
                  if (!currentSql || hasBlockingTopLevelClauses(currentSql)) break
                  const topStage = extractTopLevelSelectStage(currentSql)
                  const outerSelectItems = Array.isArray(topStage?.selectItems) ? topStage.selectItems : []
                  if (outerSelectItems.length <= 0 || selectItemsContainWildcard(outerSelectItems)) break
                  const outerSubqueryStage = extractTopLevelFromSubqueryStage(currentSql)
                  const outerSourceAlias = String(outerSubqueryStage?.sourceAlias || '').trim()
                  const topFromTail = String(topStage?.fromTail || '').trim()
                  if (!outerSourceAlias || !/^FROM\s*\(/i.test(topFromTail)) break
                  const outerGroupByItems = parseTopLevelGroupByItems(currentSql)
                  if (outerGroupByItems.length <= 0) break
                  const innerSql = finalizeSql(
                    `SELECT ${(outerSubqueryStage?.selectItems || []).join(', ')} ${String(outerSubqueryStage?.fromTail || '').trim()}`,
                  )
                  if (!innerSql || hasBlockingTopLevelClauses(innerSql)) break
                  const innerGroupByItems = parseTopLevelGroupByItems(innerSql)
                  if (innerGroupByItems.length <= 0) break
                  const bindings = outerSelectItems
                    .map((item) => parsePassthroughBinding(item, outerSourceAlias))
                    .filter(Boolean) as Array<{ aliasKey: string; aliasName: string; outputAlias: string; kind: 'direct' | 'any_value' }>
                  if (bindings.length !== outerSelectItems.length) break
                  const innerAliasKeys = new Set(
                    (outerSubqueryStage?.selectItems || [])
                      .map((item) => normalizeAliasNameLocal(extractSelectItemAlias(String(item || ''))))
                      .filter(Boolean),
                  )
                  if (bindings.some((binding) => !innerAliasKeys.has(binding.aliasKey))) break
                  const groupedAliasKeys = new Set(
                    outerGroupByItems
                      .map((item) => parseQualifiedAliasRef(item, outerSourceAlias)?.aliasKey || '')
                      .filter(Boolean),
                  )
                  const directBindings = bindings.filter((binding) => binding.kind === 'direct')
                  const anyValueBindings = bindings.filter((binding) => binding.kind === 'any_value')
                  if (directBindings.length <= 0 || anyValueBindings.length <= 0) break
                  if (directBindings.some((binding) => !groupedAliasKeys.has(binding.aliasKey))) break
                  const passthroughAlias = quoteIdent('__fg_grouped_base')
                  const projected = bindings.map((binding) =>
                    `${passthroughAlias}.${quoteIdent(binding.aliasName)} AS ${binding.outputAlias}`,
                  )
                  const collapsedSql = finalizeSql(`SELECT ${projected.join(', ')} FROM (${innerSql}) AS ${passthroughAlias}`)
                  if (!collapsedSql || collapsedSql === currentSql) break
                  currentSql = collapsedSql
                }
                return currentSql
              }
              const buildDirectGroupedBaseProjectionSql = (
                sqlRaw: string,
                sourceTableNameRaw: string,
              ): string | null => {
                const outerStage = extractTopLevelSelectStage(sqlRaw)
                const outerSelectItems = Array.isArray(outerStage?.selectItems) ? outerStage.selectItems : []
                const outerSourceAlias = String(extractTopLevelFromSubqueryStage(sqlRaw)?.sourceAlias || '').trim()
                if (!outerSourceAlias || outerSelectItems.length <= 0) return null
                if (!canProjectDirectlyFromAlias(outerSelectItems, outerSourceAlias)) return null
                const reboundAlias = quoteIdent('__fg_grouped_base')
                const reboundOuterSelectItems = outerSelectItems.map((itemRaw) =>
                  replaceSqlAliasQualifier(String(itemRaw || ''), reboundAlias),
                )
                return finalizeSplitWindowStageSql(
                  `SELECT ${reboundOuterSelectItems.join(', ')} FROM ${quoteIdent(sourceTableNameRaw)} AS ${reboundAlias}`,
                )
              }
              const splitNestedWindowStage = (
                sqlRaw: string,
                sourceTableNameRaw: string,
                remainingDepth = 4,
              ): { sourceSql: string; finalSql: string } | null => {
                const sql = finalizeSql(String(sqlRaw || ''))
                if (!sql || remainingDepth <= 0) return null
                if (!/\bover\s*\(/i.test(sql)) return null
                const topStage = extractTopLevelSelectStage(sql)
                const topFromTail = String(topStage?.fromTail || '').trim()
                if (!/^FROM\s*\(/i.test(topFromTail)) return null
                const extracted = extractTopLevelFromSubqueryStage(sql)
                const sourceAlias = String(extracted?.sourceAlias || '').trim()
                const sourceFromTail = String(extracted?.fromTail || '').trim()
                const sourceSelectItems = Array.isArray(extracted?.selectItems) ? extracted.selectItems : []
                if (!sourceAlias || !sourceFromTail || sourceSelectItems.length <= 0) return null
                const sourceSql = finalizeSql(`SELECT ${sourceSelectItems.join(', ')} ${sourceFromTail}`)
                // Run both collapse strategies to a fixpoint. The local
                // `collapseRedundantGroupedPassthroughWrappers` only handles
                // MIXED direct+any_value GROUP BY wrappers. The shared
                // `collapsePassthroughGroupByWrappers` (defined at the
                // `tryBuildMeasureNamesValuesQuery` closure level) handles the
                // complementary cases: pure plain-passthrough GROUP BY wrappers,
                // pure ANY_VALUE-passthrough GROUP BY wrappers, and pure alias
                // wrappers with no GROUP BY. Monthly-Comparison-style queries
                // produce stacked wrappers of all three shapes around the core
                // GROUPING SETS aggregate, and each un-peeled layer triggers a
                // full hash+group pass in DuckDB on millions of rows. Alternating
                // the two collapses until no further peels are possible ensures
                // any mix of shapes is fully unwrapped before the split decides
                // whether this is a window-free grouped aggregate.
                let collapsedSourceSql = sourceSql
                for (let pass = 0; pass < 6; pass += 1) {
                  const mixedCollapsed =
                    collapseRedundantGroupedPassthroughWrappers(collapsedSourceSql)
                  const afterMixed = finalizeSql(mixedCollapsed || collapsedSourceSql)
                  const pureCollapsed = collapsePassthroughGroupByWrappers(afterMixed)
                  const afterPure = finalizeSql(pureCollapsed || afterMixed)
                  if (!afterPure || afterPure === collapsedSourceSql) break
                  collapsedSourceSql = afterPure
                }
                const usableSourceSql = finalizeSql(collapsedSourceSql || sourceSql)
                if (!usableSourceSql) return null
                if (/\bGROUP\s+BY\b/i.test(usableSourceSql) && !/\bover\s*\(/i.test(usableSourceSql)) {
                  const directGroupedBaseSql =
                    buildDirectGroupedBaseProjectionSql(sql, sourceTableNameRaw)
                  const directWindowStageSql =
                    directGroupedBaseSql
                      ? null
                      : buildDirectSplitWindowStageSql(sql, sourceTableNameRaw)
                  const fallbackSplitSql =
                    directGroupedBaseSql || directWindowStageSql
                      ? null
                      : finalizeSplitWindowStageSql(
                        rewriteTopLevelFromSubquery(
                          sql,
                          `SELECT * FROM ${quoteIdent(sourceTableNameRaw)} AS ${sourceAlias}`,
                        ),
                      )
                  const rewrittenFinalSql =
                    directGroupedBaseSql ||
                    directWindowStageSql ||
                    fallbackSplitSql
                  if (!rewrittenFinalSql || rewrittenFinalSql === sql) return null
                  return {
                    sourceSql: usableSourceSql,
                    finalSql: rewrittenFinalSql,
                  }
                }
                if (!/\bover\s*\(/i.test(usableSourceSql)) return null
                const nested = splitNestedWindowStage(usableSourceSql, sourceTableNameRaw, remainingDepth - 1)
                if (!nested) return null
                const rewrittenFinalSql = finalizeSplitWindowStageSql(
                  rewriteTopLevelFromSubquery(sql, nested.finalSql),
                )
                if (!rewrittenFinalSql || rewrittenFinalSql === sql) return null
                return {
                  sourceSql: nested.sourceSql,
                  finalSql: rewrittenFinalSql,
                }
              }
              return splitNestedWindowStage(materializedSqlRaw, sourceTableName)
            }
            // Structural splitter used for the synthetic unified-aggregate
            // fallback. The expensive `splitReusableAggBaseWindowSource`
            // cascade (collapse/finalize/optimize recursion) regresses to
            // 25s+ on a 60KB+ LOD-expanded SELECT — see the
            // `StagedChunkReusableAggBaseDefinition.skipWindowSourceSplit`
            // comment. However, skipping the split entirely causes DuckDB to
            // OOM at runtime when materializing a wide aggregate+GROUPING
            // SETS with 14+ window functions inline (Monthly-Comparison
            // shape). `cheapSplitAggBaseWindowSource` is hoisted to the
            // `tryBuildMeasureNamesValuesQuery` scope so it can be reused by
            // the non-chunked `hasCompiledCalcDimPartitionSignal` branch too.
            const cheapSplitReusableAggBaseWindowSource = cheapSplitAggBaseWindowSource
            const primaryReusableAggBasePlaceholderName =
              String(stagedChunkReusableAggBases[0]?.placeholderName ?? '').trim() || null
            const normalizeReboundReusableAggBaseSql = (sqlRaw: string): string => {
              let current = finalizeSql(String(sqlRaw || ''))
              if (!current) return current
              for (let pass = 0; pass < 4; pass += 1) {
                const collapsed = finalizeSql(collapsePassthroughGroupByWrappers(current))
                if (!collapsed || collapsed === current) break
                current = collapsed
              }
              return current
            }
            const reusableAggBaseWindowInputSqlByPlaceholder = new Map<
              string,
              {
                materializedSql: string
                sourceSql?: string | null
                sourceTableName?: string | null
              }
            >()
            let primaryReusableAggBasePlanSql: string | null = null
            const statements = [
              ...sharedStageTables.flatMap((sharedStage) => [
                `DROP TABLE IF EXISTS ${sharedStage.tableName}`,
                `CREATE TEMP TABLE ${sharedStage.tableName} AS ${bindPlanTempTables(sharedStage.sql)}`,
              ]),
              ...reusableAggBaseTables.flatMap((reusableAggBase) => {
                // Guard the window-source splitter against runtime regressions
                // (e.g. unexpected SQL shapes that cause recursion blow-ups in
                // the splitter itself). Swallowing the error and falling
                // through to the direct materialization keeps the plan valid;
                // without this guard a throw from the splitter would cause
                // plan emission to fail entirely and silently fall back to the
                // non-plan SQL execution path (which then OOMs DuckDB on wide
                // Monthly-Comparison-shaped aggregates).
                let splitStage: { sourceSql: string; finalSql: string } | null = null
                try {
                  splitStage = reusableAggBase.skipWindowSourceSplit
                    ? cheapSplitReusableAggBaseWindowSource(
                        reusableAggBase.materializedSql,
                        reusableAggBase.sourceTableName,
                      )
                    : splitReusableAggBaseWindowSource(
                        reusableAggBase.materializedSql,
                        reusableAggBase.sourceTableName,
                      )
                } catch {
                  splitStage = null
                }
                const requiredProjectedAliases = Array.isArray(reusableAggBase.requiredProjectedAliases)
                  ? reusableAggBase.requiredProjectedAliases
                  : []
                const requiredProjectedSelectItems = Array.isArray(reusableAggBase.requiredProjectedSelectItems)
                  ? reusableAggBase.requiredProjectedSelectItems
                  : []
                if (!splitStage) {
                  const reboundMaterializedSql = normalizeReboundReusableAggBaseSql(
                    appendRequiredProjectedAliasesFromImmediateSource(
                      appendRequiredProjectedSelectItemsFromImmediateSource(
                        propagateRequiredProjectedSelectItemsThroughNestedSources(
                          propagateRequiredProjectedAliasesThroughNestedSources(
                            reusableAggBase.materializedSql,
                            requiredProjectedAliases,
                          ),
                          requiredProjectedSelectItems,
                        ),
                      ),
                      requiredProjectedAliases,
                    ),
                  )
                  if (
                    primaryReusableAggBasePlaceholderName &&
                    reusableAggBase.placeholderName === primaryReusableAggBasePlaceholderName
                  ) {
                    primaryReusableAggBasePlanSql = reboundMaterializedSql
                  }
                  reusableAggBaseWindowInputSqlByPlaceholder.set(
                    String(reusableAggBase.placeholderName || ''),
                    {
                      materializedSql: reboundMaterializedSql,
                    },
                  )
                  return [
                    `DROP TABLE IF EXISTS ${reusableAggBase.tableName}`,
                    `CREATE TEMP TABLE ${reusableAggBase.tableName} AS ${bindPlanTempTables(reboundMaterializedSql)}`,
                  ]
                }
                const reboundSourceSql = normalizeReboundReusableAggBaseSql(
                  appendRequiredProjectedAliasesFromImmediateSource(
                    appendRequiredProjectedSelectItemsFromImmediateSource(
                      propagateRequiredProjectedSelectItemsThroughNestedSources(
                        propagateRequiredProjectedAliasesThroughNestedSources(
                          splitStage.sourceSql,
                          requiredProjectedAliases,
                        ),
                        requiredProjectedSelectItems,
                      ),
                    ),
                    requiredProjectedAliases,
                  ),
                )
                const reboundSourceVisibleAliases = Array.from(
                  new Set(
                    (extractTopLevelSelectStage(reboundSourceSql)?.selectItems || [])
                      .map((selectItem) => unquoteProjectedAliasName(extractSelectItemAlias(String(selectItem || ''))))
                      .filter((aliasName) => aliasName && !/^__fg_/i.test(aliasName)),
                  ),
                )
                const reboundSourceTargetAliases = reboundSourceVisibleAliases.filter((aliasName) => shouldTracePlannerColumn(aliasName))
                if (reboundSourceTargetAliases.length > 0 || requiredProjectedAliases.some((aliasName) => shouldTracePlannerColumn(aliasName))) {
                  logPlannerRootTrace('agg_base_split_provenance', {
                    worksheet: worksheetNameDebug,
                    mode: 'staged_chunk_reusable',
                    materializedSqlHash,
                    sourceTableName: reusableAggBase.sourceTableName,
                    tableName: reusableAggBase.tableName,
                    splitApplied: true,
                    targetSourceAliases: reboundSourceTargetAliases,
                    targetRequiredAliases: requiredProjectedAliases.filter((aliasName) => shouldTracePlannerColumn(aliasName)),
                    requiredProjectedAliasCount: requiredProjectedAliases.length,
                    reboundSourceAliasCount: reboundSourceVisibleAliases.length,
                  })
                }
                const finalAfterPropagateAliases = splitStage.finalSql
                const finalAfterPropagateSelectItems = finalAfterPropagateAliases
                const finalAfterAppendSelectItems = finalAfterPropagateSelectItems
                const finalAfterAppendAliases = appendRequiredProjectedAliasesFromImmediateSource(
                  finalAfterAppendSelectItems,
                  [...requiredProjectedAliases, ...reboundSourceVisibleAliases],
                  { skipFinalize: true },
                )
                const reboundFinalSql = finalAfterAppendAliases
                if (
                  primaryReusableAggBasePlaceholderName &&
                  reusableAggBase.placeholderName === primaryReusableAggBasePlaceholderName
                ) {
                  primaryReusableAggBasePlanSql = reboundFinalSql
                }
                reusableAggBaseWindowInputSqlByPlaceholder.set(
                  String(reusableAggBase.placeholderName || ''),
                  {
                    materializedSql: reboundFinalSql,
                    sourceSql: reboundSourceSql,
                    sourceTableName: reusableAggBase.sourceTableName,
                  },
                )
                return [
                  `DROP TABLE IF EXISTS ${reusableAggBase.sourceTableName}`,
                  `CREATE TEMP TABLE ${reusableAggBase.sourceTableName} AS ${bindPlanTempTables(reboundSourceSql)}`,
                  `DROP TABLE IF EXISTS ${reusableAggBase.tableName}`,
                  `CREATE TEMP TABLE ${reusableAggBase.tableName} AS ${bindPlanTempTables(reboundFinalSql)}`,
                ]
              }),
              ...reusableWrapperStageTables.flatMap((sharedStage) => [
                `DROP TABLE IF EXISTS ${sharedStage.tableName}`,
                `CREATE TEMP TABLE ${sharedStage.tableName} AS ${bindPlanTempTables(sharedStage.sql)}`,
              ]),
              ...chunkTables.flatMap((tableName, idx) => [
                `DROP TABLE IF EXISTS ${tableName}`,
                // Build each staged chunk directly from the reusable-bound chunk SQL instead of
                // persisting a one-shot `chunk_*_base` temp table first.
                `CREATE TEMP TABLE ${tableName} AS ${bindPlanTempTables(stagedChunkPlanParts[idx].sql)}`,
              ]),
              `DROP TABLE IF EXISTS ${tmp}`,
              `CREATE TEMP TABLE ${tmp} AS ${chunkTables.map((tableName) => `SELECT * FROM ${tableName}`).join('\nUNION ALL\n')}`,
            ]
            planFinalSql = wrapPlanSourceSql(`SELECT * FROM ${tmp}`)
            const primaryReusableAggBase = stagedChunkReusableAggBases[0] || null
            const reusableAggBasesForWindowInput = stagedChunkReusableAggBases.map((reusableAggBase, idx) => {
              const reboundWindowInputSql = reusableAggBaseWindowInputSqlByPlaceholder.get(
                String(reusableAggBase.placeholderName || ''),
              )
              const requiredProjectedAliases = Array.isArray(reusableAggBase.requiredProjectedAliases)
                ? reusableAggBase.requiredProjectedAliases
                : []
              const requiredProjectedSelectItems = Array.isArray(reusableAggBase.requiredProjectedSelectItems)
                ? reusableAggBase.requiredProjectedSelectItems
                : []
              const reboundMaterializedSql =
                reboundWindowInputSql?.materializedSql ||
                normalizeReboundReusableAggBaseSql(
                  appendRequiredProjectedAliasesFromImmediateSource(
                    appendRequiredProjectedSelectItemsFromImmediateSource(
                      propagateRequiredProjectedSelectItemsThroughNestedSources(
                        propagateRequiredProjectedAliasesThroughNestedSources(
                          reusableAggBase.materializedSql,
                          requiredProjectedAliases,
                        ),
                        requiredProjectedSelectItems,
                      ),
                    ),
                    requiredProjectedAliases,
                  ),
                )
              return {
                placeholderName: reusableAggBase.placeholderName,
                materializedSql: reboundMaterializedSql,
                sourceSql: reboundWindowInputSql?.sourceSql,
                sourceTableName: reboundWindowInputSql?.sourceTableName,
                ...(idx === 0 ? { tableName: reusableAggBaseTable } : {}),
              }
            })
            const windowInputSql = primaryReusableAggBase
              ? optimizeWindowInputSourceSql(renderStagedChunkReusableAggBaseSourceSql({
                  wrapperSourceSql: stagedChunkReusableWrapperSourceSql,
                  reusableAggBases: reusableAggBasesForWindowInput,
                  sharedStages: [...stagedChunkSharedStages, ...stagedChunkReusableWrapperStages],
                }))
              : optimizeWindowInputSourceSql(union)
            const aggBaseWrapperSql = primaryReusableAggBase
              ? wrapPlanSourceSql(
                renderStagedChunkReusableAggBaseSourceSql({
                  wrapperSourceSql: stagedChunkReusableWrapperSourceSql,
                  reusableAggBases: reusableAggBasesForWindowInput,
                  sharedStages: [...stagedChunkSharedStages, ...stagedChunkReusableWrapperStages],
                }),
              )
              : null
            const aggBaseWrapperSourceTable = (() => {
              if (!aggBaseWrapperSql || !reusableAggBaseTable) return null
              const quoted = quoteIdent(reusableAggBaseTable)
              return aggBaseWrapperSql.includes(quoted) || aggBaseWrapperSql.includes(reusableAggBaseTable)
                ? reusableAggBaseTable
                : null
            })()
            noteExecPlan({
              statements,
              finalSql: planFinalSql,
              cleanup: [
                `DROP TABLE IF EXISTS ${tmp}`,
                ...sharedStageTables.map((sharedStage) => `DROP TABLE IF EXISTS ${sharedStage.tableName}`),
                ...reusableAggBaseTables.map((reusableAggBase) => `DROP TABLE IF EXISTS ${reusableAggBase.sourceTableName}`),
                ...reusableAggBaseTables.map((reusableAggBase) => `DROP TABLE IF EXISTS ${reusableAggBase.tableName}`),
                ...reusableWrapperStageTables.map((sharedStage) => `DROP TABLE IF EXISTS ${sharedStage.tableName}`),
                ...chunkTables.map((tableName) => `DROP TABLE IF EXISTS ${tableName}`),
              ],
              materializedTable: tmp,
	              materializedSqlHash,
	              materializedSql: union,
	              interactionCubeBuildSql,
	              interactionCubeSlicerColumns: interactionCubeSlicerDims.map((slicer) => slicer.columnName),
	              interactionCubeAxisFields,
	              interactionCubeMeasureComponents,
	              windowInputSql,
              ...(primaryReusableAggBase
                ? {
                  aggBaseMaterializedTable: reusableAggBaseTable,
                  aggBaseMaterializedSql:
                    primaryReusableAggBasePlanSql || primaryReusableAggBase.materializedSql,
                  aggBaseWrapperSql,
                  ...(aggBaseWrapperSourceTable ? { aggBaseWrapperSourceTable } : {}),
                }
                : {}),
            })
          } else {
            // When a MNV measure's valueExpr embeds a compiled calculated-
            // dimension expression inside its `OVER (PARTITION BY (...))`
            // clause (e.g. Monthly Comparison's `("t"."haircutcount" > 0)`),
            // surface the grouped aggregate as a reusable `_agg_base_src`
            // table with an `_agg_base` wrapper that applies the windows
            // over it. `tmp` is then sourced from `_agg_base`, so the
            // grouped aggregate materializes once per plan run (instead of
            // being recomputed by every downstream reader) and DuckDB can
            // stream the window functions over the already-aggregated rows
            // rather than fusing the aggregate+windows into one OOM-prone
            // CREATE TEMP TABLE. `materializedSql` is kept as the original
            // `union` so template-mode drift detection and window-input
            // hashing stay stable.
            if (hasCompiledCalcDimPartitionSignal) {
              const aggBaseSrcTable = `${tmp}_agg_base_src`
              const aggBaseTable = `${tmp}_agg_base`
              const aggBaseSrcQuoted = quoteIdent(aggBaseSrcTable)
              const groupedBaseAlias = quoteIdent('__fg_grouped_base')
              // Try a structural split: pull the innermost window-free
              // grouped SELECT into `_agg_base_src` and bind the outer
              // window SELECT in `_agg_base` to reference that temp table.
              // Falls back to a pass-through pair when the union doesn't
              // match the expected nested aggregate+window shape.
              const splitStage = cheapSplitAggBaseWindowSource(union, aggBaseSrcTable)
              const aggBaseSrcSelect = splitStage
                ? splitStage.sourceSql
                : `SELECT * FROM (${union}) AS ${groupedBaseAlias}`
              const aggBaseSelect = splitStage
                ? splitStage.finalSql
                : `SELECT * FROM ${aggBaseSrcQuoted} AS ${groupedBaseAlias}`
              const aggBaseSourceAliases = Array.from(
                new Set(
                  (extractTopLevelSelectStage(aggBaseSrcSelect)?.selectItems || [])
                    .map((selectItem) => unquoteProjectedAliasName(extractSelectItemAlias(String(selectItem || ''))))
                    .filter(Boolean),
                ),
              )
              const targetAggBaseSourceAliases = aggBaseSourceAliases.filter((aliasName) => shouldTracePlannerColumn(aliasName))
              if (targetAggBaseSourceAliases.length > 0) {
                logPlannerRootTrace('agg_base_split_provenance', {
                  worksheet: worksheetNameDebug,
                  mode: 'compiled_calc_dim_partition',
                  materializedSqlHash,
                  sourceTableName: aggBaseSrcTable,
                  tableName: aggBaseTable,
                  splitApplied: Boolean(splitStage),
                  targetSourceAliases: targetAggBaseSourceAliases,
                  sourceAliasCount: aggBaseSourceAliases.length,
                })
              }
              try {
                const rootObj: any =
                  (typeof window !== 'undefined' && window) ||
                  (typeof self !== 'undefined' && self) ||
                  {}
                rootObj.__FG_AGGP_SRC_SQL_LAST__ = aggBaseSrcSelect
                rootObj.__FG_AGGP_FINAL_SQL_LAST__ = aggBaseSelect
                rootObj.__FG_AGGP_SPLIT_APPLIED__ = Boolean(splitStage)
              } catch { /* ignore */ }
              const statements = [
                `DROP TABLE IF EXISTS ${aggBaseSrcTable}`,
                `CREATE TEMP TABLE ${aggBaseSrcTable} AS ${aggBaseSrcSelect}`,
                `DROP TABLE IF EXISTS ${aggBaseTable}`,
                `CREATE TEMP TABLE ${aggBaseTable} AS ${aggBaseSelect}`,
                `DROP TABLE IF EXISTS ${tmp}`,
                `CREATE TEMP TABLE ${tmp} AS SELECT * FROM ${aggBaseTable}`,
              ]
              planFinalSql = wrapPlanSourceSql(`SELECT * FROM ${tmp}`)
              noteExecPlan({
                statements,
                finalSql: planFinalSql,
                cleanup: [
                  `DROP TABLE IF EXISTS ${tmp}`,
                  `DROP TABLE IF EXISTS ${aggBaseTable}`,
                  `DROP TABLE IF EXISTS ${aggBaseSrcTable}`,
                ],
                materializedTable: tmp,
	                materializedSqlHash,
	                materializedSql: union,
	                interactionCubeBuildSql,
	                interactionCubeSlicerColumns: interactionCubeSlicerDims.map((slicer) => slicer.columnName),
	                interactionCubeAxisFields,
	                interactionCubeMeasureComponents,
	                windowInputSql: optimizeWindowInputSourceSql(union),
	              })
            } else {
              const artifactFallbackRisky =
                artifactExportMode &&
                (
                  useStagedMnvProjection ||
                  /\b__fg_mnv_/i.test(union) ||
                  /\bOVER\s*\(/i.test(union) ||
                  /\bGROUPING\s*\(/i.test(union) ||
                  /\bGROUPING\s+SETS\b/i.test(union) ||
                  /\b(?:ROLLUP|CUBE)\s*\(/i.test(union)
                )
              if (artifactFallbackRisky) {
                const worksheetHint = worksheetNameDebug ? ` (${worksheetNameDebug})` : ''
                throw new Error(`Artifact export mode requires a staged worksheet exec plan for risky Measure Names/Values SQL${worksheetHint}`)
              }
              const statements = [
                `DROP TABLE IF EXISTS ${tmp}`,
                `CREATE TEMP TABLE ${tmp} AS ${union}`,
              ]
              planFinalSql = wrapPlanSourceSql(`SELECT * FROM ${tmp}`)
              noteExecPlan({
                statements,
                finalSql: planFinalSql,
                cleanup: [`DROP TABLE IF EXISTS ${tmp}`],
                materializedTable: tmp,
	                materializedSqlHash,
	                materializedSql: union,
	                interactionCubeBuildSql,
	                interactionCubeSlicerColumns: interactionCubeSlicerDims.map((slicer) => slicer.columnName),
	                interactionCubeAxisFields,
	                interactionCubeMeasureComponents,
	                windowInputSql: optimizeWindowInputSourceSql(union),
	              })
            }
          }
          if (emitExecPlan) return planFinalSql
        }
      } catch (err) {
        if (artifactExportMode) throw err
      }
    }

    return bounded
  }

  _markPhase('pre_advanced')
  const advanced = tryBuildMeasureNamesValuesQuery()
  _markPhase('advanced_build')
  if (advanced) { const r = finalizeSql(advanced); _markPhase('finalizeSql'); _emitTimings(); return r }
  if (!ALLOW_FALLBACKS && requiresMeasureNames) {
    throw new Error('Measure Names/Values requested but query builder could not derive a safe query')
  }

  // Build SELECT clause
  const fallbackWorksheetName =
    String(
      options.worksheetName ??
      (spec as any)?.source?.tableau?.worksheet_name ??
      (spec as any)?.source?.tableau?.worksheetName ??
      ''
    ).trim()
  const fallbackWorksheet =
    fallbackWorksheetName
      ? (Array.isArray((workbookIr as any)?.worksheets) ? (workbookIr as any).worksheets : []).find((w: any) => {
          const a = String(w?.worksheet_name ?? w?.name ?? '').trim()
          const b = String(w?.internal_name ?? '').trim()
          return a === fallbackWorksheetName || b === fallbackWorksheetName
        }) || null
      : null
  const extractFallbackTokens = (raw: string): string[] => {
    const s = String(raw || '')
    const matches = s.match(/\[[^\]]+\](?:\.\[[^\]]+\])?/g) || []
    return matches.map((m) => cleanBracketedFieldRef0(m) || String(m)).map((x) => x.trim()).filter(Boolean)
  }
  const fallbackRowShelfTokens = extractFallbackTokens(String((fallbackWorksheet as any)?.rows?.expr ?? ''))
  const fallbackColShelfTokens = extractFallbackTokens(String((fallbackWorksheet as any)?.cols?.expr ?? ''))
  const fallbackFieldCaptionKey = (raw: any): string =>
    normalizeTableauFieldKey(String(raw ?? '').trim()) || String(raw ?? '').trim().toLowerCase()
  const fallbackCiByTokenKey = new Map<string, any>()
  const fallbackFd = (workbookIr as any)?.field_dictionary ?? null
  const fallbackFdCols: any[] = Array.isArray(fallbackFd?.columns) ? fallbackFd.columns : []
  const fallbackFdCis: any[] = Array.isArray(fallbackFd?.column_instances) ? fallbackFd.column_instances : []
  for (const ci of fallbackFdCis) {
    const nm = String((ci as any)?.name ?? '').trim()
    if (!nm) continue
    const tokenKey = normalizeTableauFieldToken(cleanBracketedFieldRef0(nm) || nm).trim().toLowerCase()
    if (tokenKey && !fallbackCiByTokenKey.has(tokenKey)) fallbackCiByTokenKey.set(tokenKey, ci)
  }
  const fallbackWorksheetTokenCaptionKey = (tokenRaw: any): string => {
    const token = String(tokenRaw ?? '').trim()
    if (!token) return ''
    if (isMeasureNames(token)) return fallbackFieldCaptionKey('Measure Names')
    if (isMultipleValues(token)) return fallbackFieldCaptionKey('Measure Values')
    const tokenKey = normalizeTableauFieldToken(cleanBracketedFieldRef0(token) || token).trim().toLowerCase()
    const ci = tokenKey ? (fallbackCiByTokenKey.get(tokenKey) || null) : null
    const ciCaption = String((ci as any)?.caption ?? '').trim()
    if (ciCaption) return fallbackFieldCaptionKey(ciCaption)
    const base = String((ci as any)?.column ?? '').trim()
    if (base) {
      const baseMeta = fallbackFdCols.find((c) => String((c as any)?.name ?? '').trim() === base) || null
      const baseCaption = String((baseMeta as any)?.caption ?? '').trim()
      if (baseCaption) return fallbackFieldCaptionKey(baseCaption)
    }
    return fallbackFieldCaptionKey(cleanBracketedFieldRef0(token) || token)
  }
  const fallbackAttachWorksheetShelfTokenHints = (fields: any[] | null | undefined, shelfTokens: string[]): any[] => {
    let cursor = 0
    return (Array.isArray(fields) ? fields : []).map((field) => {
      if (!field || typeof field !== 'object') return field
      const explicit = String(
        (field as any)?.tableau_token ??
        (field as any)?.tableau_token_raw ??
        (field as any)?.field_id ??
        ''
      ).trim()
      if (explicit) return { ...(field as any), __fg_field_ref: explicit }
      const fieldName = String((field as any)?.field_name ?? (field as any)?.field ?? '').trim()
      const targetKey = fallbackFieldCaptionKey(fieldName)
      if (!targetKey) return field
      const matchFrom = (start: number): string => {
        for (let i = start; i < shelfTokens.length; i++) {
          const token = String(shelfTokens[i] ?? '').trim()
          if (!token) continue
          if (fallbackWorksheetTokenCaptionKey(token) !== targetKey) continue
          cursor = i + 1
          return token
        }
        return ''
      }
      const token = matchFrom(cursor) || matchFrom(0)
      return token ? { ...(field as any), __fg_field_ref: token } : field
    })
  }
  const fallbackRowsResolved = fallbackAttachWorksheetShelfTokenHints(
    applyCrosstabDateTimeGrainHints(crosstabSpec.rows || []),
    fallbackRowShelfTokens,
  )
  const fallbackColsResolved = fallbackAttachWorksheetShelfTokenHints(
    applyCrosstabDateTimeGrainHints(crosstabSpec.columns || []),
    fallbackColShelfTokens,
  )
  const fallbackShelfFieldRef = (field: any): string =>
    String(
      (field as any)?.__fg_field_ref ??
      (field as any)?.tableau_token ??
      (field as any)?.tableau_token_raw ??
      (field as any)?.field_id ??
      (field as any)?.field_name ??
      (field as any)?.field ??
      ''
    ).trim()
  const selectFields: string[] = []
  const groupByFields: string[] = []
  const groupBySet = new Set<string>()
  const allFieldNames = new Set<string>()
  const normKey = (s: any) => String(s ?? '').trim().toLowerCase().replace(/[\[\]]/g, '')
  const selectedAliasKeys = new Set<string>()
  const groupedExprByFieldKey = new Map<string, string>()
  const isMeasureNamesFallback = isMeasureNames
  const applyTimeGrainFallback = (expr: string, grain: string | null, isDiscrete?: boolean | null): string => {
    const g = String(grain ?? '').trim().toLowerCase()
    if (!g) return expr
    const ts = `TRY_CAST(${expr} AS TIMESTAMP)`
    if (isDiscrete) {
      if (g === 'year') return `EXTRACT(year FROM ${ts})`
      if (g === 'quarter' || g === 'month' || g === 'week' || g === 'day' || g === 'hour' || g === 'minute' || g === 'second') {
        return `DATE_TRUNC('${g}', ${ts})`
      }
      return expr
    }
    if (g === 'quarter' || g === 'month' || g === 'week' || g === 'day' || g === 'hour' || g === 'minute' || g === 'second' || g === 'year') {
      return `DATE_TRUNC('${g}', ${ts})`
    }
    return expr
  }

  // Add row fields
  for (const rowField of fallbackRowsResolved) {
    const rawName = String(rowField.field_name ?? '')
    const rawRef = fallbackShelfFieldRef(rowField)
    const fieldName = quoteIdent(rawName)
    allFieldNames.add(rowField.field_name)
    selectedAliasKeys.add(normKey(rawName))
    
    // Tableau "Measure Names" is a virtual field (not a physical column in extracts).
    // If we got here, the advanced Measure Names/Values path couldn't be built, so we degrade
    // gracefully by emitting a stable placeholder instead of referencing a missing Parquet column.
    if (isMeasureNamesFallback(rawName)) {
      selectFields.push(`${escapeValue('Measure Names')} AS ${quoteIdent('Measure Names')}`)
      continue
    }

    const inlineAgg = (!rowField.aggregation ? parseAggFieldName(rawName) : null)
    const aggFn = rowField.aggregation ? String(rowField.aggregation).trim().toUpperCase() : (inlineAgg?.agg ?? '')
    const innerRaw = inlineAgg ? inlineAgg.inner : rawName

    if (aggFn) {
      const innerExpr = (() => {
        const innerBase = String(innerRaw ?? '').trim()
        const innerRef = inlineAgg ? stripBracketsAroundFieldName(innerBase) : (rawRef || innerBase)
        const inner = stripBracketsAroundFieldName(String(innerRef ?? '').trim())
        if (!inner) return 'NULL'
        if (/^[+-]?\d+(\.\d+)?$/.test(inner)) return inner
        return fieldExprFor(inner, quoteIdent('t'))
      })()
      const aggUpper = aggFn.toUpperCase()
      const expr =
        (aggUpper === 'COUNTD' || aggUpper === 'COUNTDISTINCT' || aggUpper === 'COUNT_DISTINCT')
          ? `COUNT(DISTINCT CAST(${innerExpr} AS VARCHAR))`
          : (aggUpper === 'COUNT')
            ? `COUNT(${innerExpr})`
            : (aggUpper === 'AVG' || aggUpper === 'AVERAGE' || aggUpper === 'MEAN')
              ? `AVG(TRY_CAST(${innerExpr} AS DOUBLE))`
              : (aggUpper === 'MIN')
                ? `MIN(${innerExpr})`
                : (aggUpper === 'MAX')
                  ? `MAX(${innerExpr})`
                  : `SUM(TRY_CAST(${innerExpr} AS DOUBLE))`
      selectFields.push(`${expr} AS ${fieldName}`)
    } else {
      const exprRaw = fieldExprFor(rawRef || rawName, quoteIdent('t'))
      const timeGrain =
        String((rowField as any)?.time_grain ?? (rowField as any)?.timeGrain ?? '').trim().toLowerCase() ||
        inferTimeGrainFromPill(rowField) ||
        timeGrainFromMeta(rawName) ||
        inferTimeGrainFromField(rawName) ||
        null
      const isDiscreteRaw = (rowField as any)?.is_discrete
      const typeRaw = String((rowField as any)?.type ?? '').trim().toLowerCase()
      const isDiscrete = (typeof isDiscreteRaw === 'boolean') ? isDiscreteRaw : (typeRaw ? typeRaw === 'discrete' : null)
      const expr = applyTimeGrainFallback(exprRaw, timeGrain, isDiscrete)
      selectFields.push(`${expr} AS ${fieldName}`)
      const fieldKey = normKey(rawName)
      if (fieldKey && expr) groupedExprByFieldKey.set(fieldKey, expr)
      // Window/table calc and aggregate-derived dimensions cannot appear in GROUP BY.
      if (canSqlExpressionAppearInGroupBy(expr)) {
        const groupByExpr = expr
        if (!groupBySet.has(groupByExpr)) {
          groupBySet.add(groupByExpr)
          groupByFields.push(groupByExpr)
        }
      }
    }
  }

	  // Add column fields
	  for (const colField of fallbackColsResolved) {
    const rawName = String(colField.field_name ?? '')
    const rawRef = fallbackShelfFieldRef(colField)
    const fieldName = quoteIdent(rawName)
    allFieldNames.add(colField.field_name)
    selectedAliasKeys.add(normKey(rawName))
    
    if (isMeasureNamesFallback(rawName)) {
      selectFields.push(`${escapeValue('Measure Names')} AS ${quoteIdent('Measure Names')}`)
      continue
    }

    const inlineAgg = (!colField.aggregation ? parseAggFieldName(rawName) : null)
    const aggFn = colField.aggregation ? String(colField.aggregation).trim().toUpperCase() : (inlineAgg?.agg ?? '')
    const innerRaw = inlineAgg ? inlineAgg.inner : rawName

    if (aggFn) {
      const innerExpr = (() => {
        const innerBase = String(innerRaw ?? '').trim()
        const innerRef = inlineAgg ? stripBracketsAroundFieldName(innerBase) : (rawRef || innerBase)
        const inner = stripBracketsAroundFieldName(String(innerRef ?? '').trim())
        if (!inner) return 'NULL'
        if (/^[+-]?\d+(\.\d+)?$/.test(inner)) return inner
        return fieldExprFor(inner, quoteIdent('t'))
      })()
      const aggUpper = aggFn.toUpperCase()
      const expr =
        (aggUpper === 'COUNTD' || aggUpper === 'COUNTDISTINCT' || aggUpper === 'COUNT_DISTINCT')
          ? `COUNT(DISTINCT CAST(${innerExpr} AS VARCHAR))`
          : (aggUpper === 'COUNT')
            ? `COUNT(${innerExpr})`
            : (aggUpper === 'AVG' || aggUpper === 'AVERAGE' || aggUpper === 'MEAN')
              ? `AVG(TRY_CAST(${innerExpr} AS DOUBLE))`
              : (aggUpper === 'MIN')
                ? `MIN(${innerExpr})`
                : (aggUpper === 'MAX')
                  ? `MAX(${innerExpr})`
                  : `SUM(TRY_CAST(${innerExpr} AS DOUBLE))`
      selectFields.push(`${expr} AS ${fieldName}`)
    } else {
      const exprRaw = fieldExprFor(rawRef || rawName, quoteIdent('t'))
      const timeGrain =
        String((colField as any)?.time_grain ?? (colField as any)?.timeGrain ?? '').trim().toLowerCase() ||
        inferTimeGrainFromPill(colField) ||
        timeGrainFromMeta(rawName) ||
        inferTimeGrainFromField(rawName) ||
        null
      const isDiscreteRaw = (colField as any)?.is_discrete
      const typeRaw = String((colField as any)?.type ?? '').trim().toLowerCase()
      const isDiscrete = (typeof isDiscreteRaw === 'boolean') ? isDiscreteRaw : (typeRaw ? typeRaw === 'discrete' : null)
      const expr = applyTimeGrainFallback(exprRaw, timeGrain, isDiscrete)
      selectFields.push(`${expr} AS ${fieldName}`)
      const fieldKey = normKey(rawName)
      if (fieldKey && expr) groupedExprByFieldKey.set(fieldKey, expr)
      if (canSqlExpressionAppearInGroupBy(expr)) {
        const groupByExpr = expr
        if (!groupBySet.has(groupByExpr)) {
          groupBySet.add(groupByExpr)
          groupByFields.push(groupByExpr)
        }
	      }
	    }
	  }

	  // Tableau mark-card encodings participate in the worksheet level of detail even
	  // when the worksheet is represented as a crosstab spec. Dense/sparse mark grids
	  // in particular need Color/Detail/Shape as grouped dimensions and Size as an
	  // aggregate measure; treating those fields as tooltip-only ATTR columns collapses
	  // the marks and loses Tableau's intended encoding.
	  const fallbackMarkFieldName = (raw: any): string => {
	    const text = String(raw ?? '').trim()
	    if (!text) return ''
	    const cleaned = cleanBracketedFieldRef0(text) || stripBracketsAroundFieldName(text) || text
	    const token = String(cleaned ?? '').replace(/^:+/, '').trim()
	    const parts = token.split(':').map((part) => String(part ?? '').trim()).filter(Boolean)
	    if (parts.length >= 2) return parts[parts.length - 2] || token
	    return token
	  }
	  const normalizeMarkAgg0 = (raw: any): string | null => {
	    const a0 = String(raw ?? '').trim().toLowerCase()
	    if (!a0 || a0 === 'none') return null
	    if (a0 === 'count distinct' || a0 === 'count_distinct' || a0 === 'countdistinct' || a0 === 'ctd') return 'COUNTD'
	    if (a0 === 'average' || a0 === 'mean') return 'AVG'
	    if (a0 === 'sum' || a0 === 'avg' || a0 === 'min' || a0 === 'max' || a0 === 'count' || a0 === 'countd') return a0.toUpperCase()
	    return null
	  }
	  const markCardItems = (() => {
	    const out: Array<{ shelf: string; item: any }> = []
	    const seen = new Set<string>()
	    const push = (shelf: string, item: any) => {
	      if (!item || typeof item !== 'object') return
	      const field = fallbackMarkFieldName((item as any)?.field_name ?? (item as any)?.field ?? (item as any)?.name)
	      if (!field) return
	      const agg = normalizeMarkAgg0((item as any)?.aggregation)
	      const key = `${shelf}:${normKey(field)}:${agg ?? ''}`
	      if (!key || seen.has(key)) return
	      seen.add(key)
	      out.push({ shelf, item: { ...(item as any), field_name: field, field, aggregation: agg ?? (item as any)?.aggregation } })
	    }
	    const marks = ((crosstabSpec as any)?.marks && typeof (crosstabSpec as any).marks === 'object') ? (crosstabSpec as any).marks : null
	    for (const shelf of ['color', 'detail', 'shape', 'tooltip', 'size']) {
	      const raw = marks ? (marks as any)[shelf] : null
	      const items = Array.isArray(raw) ? raw : (raw ? [raw] : [])
	      for (const item of items) push(shelf, item)
	    }
	    const intentEncodings = Array.isArray((spec as any)?.tableau_intent?.encodings)
	      ? ((spec as any).tableau_intent.encodings as any[])
	      : []
	    for (const enc of intentEncodings) {
	      const kind0 = String((enc as any)?.kind ?? '').trim().toLowerCase()
	      const shelf =
	        kind0 === 'label' || kind0 === 'text' ? 'tooltip' :
	        kind0 === 'color' || kind0 === 'detail' || kind0 === 'shape' || kind0 === 'tooltip' || kind0 === 'size'
	          ? kind0
	          : ''
	      if (!shelf) continue
	      const field = fallbackMarkFieldName((enc as any)?.field)
	      if (!field) continue
	      push(shelf, {
	        field_name: field,
	        field,
	        ...(shelf === 'size' ? { role: 'measure', is_measure: true, aggregation: 'SUM' } : null),
	      })
	    }
	    return out
	  })()
	  const addMarkCardDimensionSelect = (fieldRaw: any) => {
	    const field = fallbackMarkFieldName(fieldRaw)
	    if (!field) return
	    const key = normKey(field)
	    if (!key || selectedAliasKeys.has(key)) return
	    const expr = fieldExprFor(field, quoteIdent('t'))
	    if (!expr || isNullishExpr(expr)) return
	    selectFields.push(`${expr} AS ${quoteIdent(field)}`)
	    selectedAliasKeys.add(key)
	    allFieldNames.add(field)
	    groupedExprByFieldKey.set(key, expr)
	    if (canSqlExpressionAppearInGroupBy(expr) && !groupBySet.has(expr)) {
	      groupBySet.add(expr)
	      groupByFields.push(expr)
	    }
	  }
	  const addMarkCardMeasureSelect = (fieldRaw: any, aggRaw: any, aliasRaw?: any) => {
	    const field = fallbackMarkFieldName(fieldRaw)
	    if (!field) return
	    const aggUpper = normalizeMarkAgg0(aggRaw) || 'SUM'
	    const alias = String(aliasRaw ?? `${aggUpper === 'COUNTD' ? 'COUNTD' : aggUpper}(${field})`).trim()
	    if (!alias || selectedAliasKeys.has(normKey(alias))) return
	    const exprRaw = fieldExprFor(field, quoteIdent('t'))
	    if (!exprRaw || isNullishExpr(exprRaw)) return
	    const expr =
	      (aggUpper === 'COUNTD' || aggUpper === 'COUNTDISTINCT' || aggUpper === 'COUNT_DISTINCT')
	        ? `COUNT(DISTINCT CAST(${exprRaw} AS VARCHAR))`
	        : (aggUpper === 'COUNT')
	          ? `COUNT(${exprRaw})`
	          : (aggUpper === 'AVG' || aggUpper === 'AVERAGE' || aggUpper === 'MEAN')
	            ? `AVG(TRY_CAST(${exprRaw} AS DOUBLE))`
	            : (aggUpper === 'MIN')
	              ? `MIN(${exprRaw})`
	              : (aggUpper === 'MAX')
	                ? `MAX(${exprRaw})`
	                : `SUM(TRY_CAST(${exprRaw} AS DOUBLE))`
	    selectFields.push(`${expr} AS ${quoteIdent(alias)}`)
	    selectedAliasKeys.add(normKey(alias))
	    allFieldNames.add(alias)
	  }
	  for (const { shelf, item } of markCardItems) {
	    const field = (item as any)?.field_name ?? (item as any)?.field
	    const role = String((item as any)?.role ?? '').trim().toLowerCase()
	    const agg = normalizeMarkAgg0((item as any)?.aggregation)
	    const isMeasure = shelf === 'size' || role === 'measure' || Boolean((item as any)?.is_measure) || Boolean(agg)
	    if (isMeasure) {
	      addMarkCardMeasureSelect(field, agg || (shelf === 'size' ? 'SUM' : 'SUM'))
	    } else {
	      addMarkCardDimensionSelect(field)
	    }
	  }
	  if (worksheetDebugEnabled && markCardItems.length) {
	    try {
	      console.warn('[worksheetQueryBuilder] fallback crosstab mark-card selects', {
	        worksheetName: options.worksheetName ?? null,
	        markCardItems: markCardItems.map(({ shelf, item }) => ({
	          shelf,
	          field: (item as any)?.field_name ?? (item as any)?.field ?? null,
	          aggregation: (item as any)?.aggregation ?? null,
	        })),
	        selectCount: selectFields.length,
	        groupByCount: groupByFields.length,
	      })
	    } catch { }
	  }

	  // Tooltip-only extra SELECT columns (non-Measure Names/Values path).
  // This matches the Measure Names path behavior: parse tooltip template tokens and
  // add any missing token expressions as extra columns so each cell/mark has real data.
  const tooltipCfg0: any = (spec as any)?.view?.tooltip ?? null
  const tooltipEnabled = tooltipCfg0?.enabled !== false
  const tooltipMode = String(tooltipCfg0?.mode ?? 'auto').trim().toLowerCase()
  const tooltipTemplate =
    tooltipMode === 'rich'
      ? String(tooltipCfg0?.template_html ?? '')
      : String(tooltipCfg0?.template ?? '')
  const allowTooltipTemplateTokens = !lazyTooltips || (tooltipMode !== 'auto' && tooltipTemplate)

  const tooltipNeeds: TooltipTokenNeed[] = (() => {
    if (!tooltipEnabled) return []
    const out: TooltipTokenNeed[] = []
    const seen = new Set<string>()
    const pushToken = (tok: string) => {
      const parsed = parseTooltipTokenNeed(tok)
      if (!parsed) return
      const k = parsed.kind === 'all_fields' ? 'all fields' : String((parsed as any).token ?? '').trim().toLowerCase()
      if (!k) return
      if (seen.has(k)) return
      seen.add(k)
      out.push(parsed)
    }

    if (tooltipTemplate && allowTooltipTemplateTokens) {
      for (const t of extractTooltipTokensFromTemplate(tooltipTemplate)) pushToken(t)
    }

    try {
      const arr = Array.isArray((crosstabSpec as any)?.marks?.tooltip) ? ((crosstabSpec as any).marks.tooltip as any[]) : []
      const aggTok = (raw: any): string | null => {
        const a = String(raw ?? '').trim().toLowerCase()
        if (!a) return null
        if (a === 'average' || a === 'mean') return 'avg'
        if (a === 'ctd') return 'countd'
        if (a === 'count distinct' || a === 'count_distinct') return 'countd'
        if (a === 'sum' || a === 'avg' || a === 'min' || a === 'max' || a === 'count' || a === 'countd') return a
        return null
      }
      for (const it of arr) {
        const field = stripBracketsAroundFieldName(String(it?.field_name ?? ''))
        if (!field) continue
        const role = String(it?.role ?? '').trim().toLowerCase()
        const agg = aggTok(it?.aggregation) || (role === 'measure' ? 'sum' : null)
        if (agg) {
          const fn = agg === 'countd' ? 'COUNTD' : agg.toUpperCase()
          pushToken(`${fn}(${field})`)
        } else {
          pushToken(field)
        }
      }
    } catch { }

    return out
  })()

  const addAliasKey = (k: string) => {
    const nk = normKey(k)
    if (!nk) return
    selectedAliasKeys.add(nk)
  }

  const tableauAttrSql0 = (expr: string): string =>
    `CASE WHEN COUNT(DISTINCT CAST(${expr} AS VARCHAR)) = 1 THEN MIN(CAST(${expr} AS VARCHAR)) ELSE '*' END`

  const splitTopLevelArgs0 = (src: string): string[] => {
    const s = String(src ?? '')
    const out: string[] = []
    let buf = ''
    let depth = 0
    let inSq = false
    let inDq = false
    for (let i = 0; i < s.length; i++) {
      const ch = s[i]
      if (!inDq && ch === "'" && s[i - 1] !== '\\') { inSq = !inSq; buf += ch; continue }
      if (!inSq && ch === '"' && s[i - 1] !== '\\') { inDq = !inDq; buf += ch; continue }
      if (inSq || inDq) { buf += ch; continue }
      if (ch === '(') { depth++; buf += ch; continue }
      if (ch === ')') { depth = Math.max(0, depth - 1); buf += ch; continue }
      if (ch === ',' && depth === 0) {
        if (buf.trim()) out.push(buf.trim())
        buf = ''
        continue
      }
      buf += ch
    }
    if (buf.trim()) out.push(buf.trim())
    return out
  }

  const resolveAnyExpr0 = (raw: string): string => {
    const s = String(raw ?? '').trim()
    if (!s) return 'NULL'
    if (/^[-+]?\\d+(\\.\\d+)?$/.test(s)) return s
    if (/^(true|false)$/i.test(s)) return s.toUpperCase()
    if ((s.startsWith("'") && s.endsWith("'")) || (s.startsWith('"') && s.endsWith('"'))) return s
    const field = stripBracketsAroundFieldName(s)
    if (!field) return 'NULL'
    const resolved = resolveColOrRawWhenNoSchema(field)
    return resolved ? quoteIdent(resolved) : 'NULL'
  }

  const buildDatePartSql0 = (fnUpper: string, innerExpr: string): string | null => {
    const fn = String(fnUpper ?? '').trim().toUpperCase()
    if (!fn) return null
    const ts = `TRY_CAST(${innerExpr} AS TIMESTAMP)`
    if (fn === 'YEAR') return `EXTRACT(year FROM ${ts})`
    if (fn === 'QUARTER') return `EXTRACT(quarter FROM ${ts})`
    if (fn === 'MONTH') return `EXTRACT(month FROM ${ts})`
    if (fn === 'DAY') return `EXTRACT(day FROM ${ts})`
    if (fn === 'HOUR') return `EXTRACT(hour FROM ${ts})`
    if (fn === 'MINUTE') return `EXTRACT(minute FROM ${ts})`
    if (fn === 'SECOND') return `EXTRACT(second FROM ${ts})`
    return null
  }

  const fdCols: any[] = Array.isArray((workbookDerived as any)?.fdCols)
    ? ((workbookDerived as any).fdCols as any[])
    : []
  const findSpecAggForField = (field: string): string | null => {
    const key = normKey(field)
    if (!key) return null
    for (const f of [...(crosstabSpec.rows || []), ...(crosstabSpec.columns || [])] as any[]) {
      const fn = normKey(f?.field_name)
      if (!fn || fn !== key) continue
      const agg = String(f?.aggregation ?? '').trim().toUpperCase()
      if (agg) return agg
    }
    return null
  }
  const normalizeAggUpper0 = (raw: any): string | null => {
    const a0 = String(raw ?? '').trim().toLowerCase()
    if (!a0) return null
    if (a0 === 'count distinct' || a0 === 'count_distinct' || a0 === 'countdistinct' || a0 === 'ctd') return 'COUNTD'
    if (a0 === 'average' || a0 === 'mean') return 'AVG'
    if (a0 === 'sum' || a0 === 'avg' || a0 === 'min' || a0 === 'max' || a0 === 'count' || a0 === 'countd') return a0.toUpperCase()
    return null
  }
  const aggFromFieldToken0 = (rawField: string): string | null => {
    const cleaned = cleanBracketedFieldRef0(String(rawField ?? '').trim()) || String(rawField ?? '').trim()
    if (!cleaned) return null
    const token = cleaned.replace(/^:+/, '')
    const m = token.match(/^([A-Za-z_][A-Za-z0-9_]*)\s*:/)
    if (!m) return null
    return normalizeAggUpper0(m[1])
  }
  const inferAggForTooltipField0 = (rawField: string, resolvedField: string): string | null => {
    const fromToken = aggFromFieldToken0(rawField)
    if (fromToken) return fromToken
    const fromSpec = findSpecAggForField(rawField) || findSpecAggForField(resolvedField)
    if (fromSpec) return normalizeAggUpper0(fromSpec)
    const keys = [normKey(rawField), normKey(resolvedField)].filter(Boolean)
    if (keys.length === 0) return null
    const hit = (Array.isArray(fdCols) ? fdCols : []).find((c: any) => {
      const nameKey = normKey(c?.name ?? '')
      const captionKey = normKey(c?.caption ?? c?.attrs?.caption ?? '')
      return keys.includes(nameKey) || keys.includes(captionKey)
    })
    if (!hit) return null
    const fromMetaAgg = normalizeAggUpper0((hit as any)?.aggregation ?? (hit as any)?.attrs?.aggregation)
    if (fromMetaAgg) return fromMetaAgg
    const role = String((hit as any)?.role ?? (hit as any)?.attrs?.role ?? '').trim().toLowerCase()
    const typ = String((hit as any)?.type ?? (hit as any)?.attrs?.type ?? '').trim().toLowerCase()
    if (role === 'measure' || typ === 'quantitative') return 'SUM'
    return null
  }
  const buildAggExpr0 = (aggUpper: string, colRef: string): string => {
    if (aggUpper === 'COUNTD') return `COUNT(DISTINCT CAST(${colRef} AS VARCHAR))`
    if (aggUpper === 'COUNT') return `COUNT(${colRef})`
    if (aggUpper === 'AVG') return `AVG(TRY_CAST(${colRef} AS DOUBLE))`
    if (aggUpper === 'MIN') return `MIN(${colRef})`
    if (aggUpper === 'MAX') return `MAX(${colRef})`
    return `SUM(TRY_CAST(${colRef} AS DOUBLE))`
  }

  const tooltipExtraSelects: string[] = []
  for (const need of tooltipNeeds) {
    if (need.kind === 'all_fields') continue

    if (need.kind === 'field') {
      const field = stripBracketsAroundFieldName(need.field)
      if (!field) continue
      // If the field is already in the output (Rows/Cols), nothing to add.
      if (selectedAliasKeys.has(normKey(field))) continue
      const resolvedField = stripBracketsAroundFieldName(resolveColOrRawWhenNoSchema(field))
      if (!resolvedField) continue
      const colRef = quoteIdent(resolvedField)
      const aggUpper = inferAggForTooltipField0(field, resolvedField)
      const expr = aggUpper ? buildAggExpr0(aggUpper, colRef) : tableauAttrSql0(colRef)
      tooltipExtraSelects.push(`${expr} AS ${quoteIdent(resolvedField)}`)
      addAliasKey(resolvedField)
      continue
    }

    // Function token
    const fn = String(need.fn ?? '').trim().toUpperCase()
    const argRaw = String(need.arg ?? '').trim()
    const arg = stripBracketsAroundFieldName(argRaw)
    if (!fn) continue
    const resolvedArg = arg ? stripBracketsAroundFieldName(resolveColOrRawWhenNoSchema(arg)) : ''
    const colRef = quoteIdent(resolvedArg)

    // Canonical alias (matches Measure Names path behavior).
    const alias =
      (fn === 'COUNTDISTINCT' || fn === 'COUNT_DISTINCT') ? `COUNTD(${arg})` :
      `${fn}(${arg})`
    if (selectedAliasKeys.has(normKey(alias))) continue

    let expr: string | null = null

    if (fn === 'ATTR') {
      if (!resolvedArg) continue
      expr = tableauAttrSql0(colRef)
    } else if (fn === 'IFNULL') {
      const parts = splitTopLevelArgs0(argRaw)
      if (parts.length >= 2) {
        const a0 = resolveAnyExpr0(parts[0])
        const a1 = resolveAnyExpr0(parts[1])
        const isNumericLiteral = /^[-+]?(\d+(\.\d*)?|\.\d+)$/.test(parts[1].trim())
        expr = isNumericLiteral
          ? tableauAttrSql0(`COALESCE(TRY_CAST(${a0} AS DOUBLE), TRY_CAST(${a1} AS DOUBLE))`)
          : tableauAttrSql0(`COALESCE(${a0}, ${a1})`)
      }
    } else if (fn === 'ZN') {
      if (!argRaw) continue
      const a0 = resolveAnyExpr0(argRaw)
      expr = tableauAttrSql0(`COALESCE(TRY_CAST(${a0} AS DOUBLE), 0.0)`)
    } else if (fn === 'STR') {
      if (!argRaw) continue
      const a0 = resolveAnyExpr0(argRaw)
      expr = tableauAttrSql0(`CAST(${a0} AS VARCHAR)`)
    } else if (fn === 'AGG') {
      if (!resolvedArg) continue
      const agg0 = findSpecAggForField(arg)
      if (agg0) {
        if (agg0 === 'SUM') expr = `SUM(TRY_CAST(${colRef} AS DOUBLE))`
        else if (agg0 === 'AVG') expr = `AVG(TRY_CAST(${colRef} AS DOUBLE))`
        else if (agg0 === 'MIN') expr = `MIN(${colRef})`
        else if (agg0 === 'MAX') expr = `MAX(${colRef})`
        else if (agg0 === 'COUNT') expr = `COUNT(${colRef})`
        else if (agg0 === 'COUNTD') expr = `COUNT(DISTINCT CAST(${colRef} AS VARCHAR))`
        else expr = tableauAttrSql0(colRef)
      } else {
        // Best-effort: if we don't know how it's aggregated on the view, treat it like ATTR.
        expr = tableauAttrSql0(colRef)
      }
    } else {
      if (!resolvedArg) continue
      const dp = buildDatePartSql0(fn, colRef)
      if (dp) {
        // Date part tokens behave like ATTR(datepart) in Tableau tooltips.
        expr = tableauAttrSql0(dp)
      } else if (fn === 'COUNTD' || fn === 'COUNTDISTINCT' || fn === 'COUNT_DISTINCT') {
        expr = `COUNT(DISTINCT CAST(${colRef} AS VARCHAR))`
      } else if (fn === 'COUNT') {
        expr = `COUNT(${colRef})`
      } else if (fn === 'SUM') {
        expr = `SUM(TRY_CAST(${colRef} AS DOUBLE))`
      } else if (fn === 'AVG' || fn === 'AVERAGE' || fn === 'MEAN') {
        expr = `AVG(TRY_CAST(${colRef} AS DOUBLE))`
      } else if (fn === 'MIN') {
        expr = `MIN(${colRef})`
      } else if (fn === 'MAX') {
        expr = `MAX(${colRef})`
      } else {
        // Unknown function; skip.
        expr = null
      }
    }

    if (!expr) continue
    tooltipExtraSelects.push(`${expr} AS ${quoteIdent(alias)}`)
    addAliasKey(alias)
  }

  if (selectFields.length === 0) {
    selectFields.push('*')
  }

  // If we ended up needing tooltip-only aggregates, we cannot mix `*` with aggregates.
  if (tooltipExtraSelects.length > 0) {
    if (selectFields.length === 1 && selectFields[0] === '*') {
      selectFields.length = 0
    }
    for (const extra of tooltipExtraSelects) {
      selectFields.push(extra)
    }
  }

  // Apply parameter substitution to field references
  const escapeParamValue = (key: string, value: any): string => {
    const t = String(paramTypeByKeyForSubst.get(normalizeParamKey(key)) || '').trim().toLowerCase()
    if (value === null || value === undefined) {
      if (t === 'date') return 'CAST(NULL AS DATE)'
      if (t === 'timestamp') return 'CAST(NULL AS TIMESTAMP)'
      if (t === 'number') return 'CAST(NULL AS DOUBLE)'
      if (t === 'bool') return 'CAST(NULL AS BOOLEAN)'
      if (t === 'string') return 'CAST(NULL AS VARCHAR)'
      const keyHint = String(key || '').trim().toLowerCase()
      if (keyHint.includes('date') || keyHint.includes('time') || keyHint.includes('timestamp')) {
        return 'CAST(NULL AS TIMESTAMP)'
      }
      return 'NULL'
    }
    if (!t) return escapeValue(value)
    const looksEpochMs = (typeof value === 'number') && Number.isFinite(value) && Math.abs(value) >= 1e12
    if ((t === 'date' || t === 'timestamp') && looksEpochMs) {
      // Avoid `(<bigint> * INTERVAL 1 MILLISECOND)` because DuckDB can downcast to INT32 and overflow.
      // `to_timestamp()` expects seconds since epoch, but returns TIMESTAMP WITH TIME ZONE; cast eagerly
      // to plain TIMESTAMP so interval arithmetic works (`TIMESTAMP + INTERVAL`).
      const ts = `CAST(TO_TIMESTAMP((CAST(${String(value)} AS DOUBLE) / 1000.0)) AS TIMESTAMP)`
      return t === 'date' ? `CAST(${ts} AS DATE)` : ts
    }
    if (t === 'date') return `TRY_CAST(${escapeValue(value)} AS DATE)`
    if (t === 'timestamp') return `TRY_CAST(${escapeValue(value)} AS TIMESTAMP)`
    return escapeValue(value)
  }
  const substituteParameters = (sql: string): string => {
    let result = sql
    for (const [key, value] of Object.entries(parameters)) {
      // Replace [ParameterName] with actual value
      const pattern = new RegExp(`\\[${key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\]`, 'gi')
      result = result.replace(pattern, escapeParamValue(String(key), value))
    }
    return result
  }
  const paramPlaceholder = (key: string): string =>
    buildParamTemplatePlaceholder(key, undefined, (parameters as any)?.[key])
  const substituteParametersTemplate = (sql: string): string => {
    let result = sql
    for (const key of Object.keys(parameters)) {
      // Replace [ParameterName] with a placeholder; caller must bind before execution.
      const pattern = new RegExp(`\\[${String(key).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\]`, 'gi')
      result = result.replace(pattern, paramPlaceholder(String(key)))
    }
    return result
  }

  // Build WHERE clause from filters
  const whereConditions: string[] = []
  const qualifyConditions: string[] = []
  const postSelectFilters: Array<{ field: string; operator: 'IS NULL' | 'IS NOT NULL' }> = []
  const looksWindowPredicate = (expr: string): boolean =>
    /\bOVER\s*\(/i.test(expr) || /\bROW_NUMBER\s*\(/i.test(expr) || /\bLAG\s*\(/i.test(expr) || /\bLEAD\s*\(/i.test(expr)
  const pushRowFilterPredicate = (pred: string) => {
    if (!pred) return
    if (looksWindowPredicate(pred)) qualifyConditions.push(pred)
    else whereConditions.push(pred)
  }
  const havingConditions: string[] = []
  const contextFilters = (Array.isArray(filters) ? filters : []).filter((ff: any) => {
    const ft = String((ff as any)?.filter_type ?? '').trim().toLowerCase()
    return ft === 'context' || Boolean((ff as any)?.context)
  })
  const filterBindScalar = (idx: number): string => `/*__FG_FILTER:${idx}__*/ NULL`
  const filterBindPart = (idx: number, part: string): string => `/*__FG_FILTER:${idx}:${part}__*/ NULL`
  const filterBindTableIdent = (idx: number): string => `__FG_FILTER_TABLE__${idx}__`
  const buildInPredicateFromValueTemplate = (
    exprSql: string,
    _value: any,
    not: boolean,
    filterIndex: number,
    opts?: { includeNullsOnNot?: boolean },
  ): string | null => {
    const looksDateLikeExpr = (expr: string): boolean => {
      const s = String(expr || '')
      if (!s) return false
      if (/\bdate_trunc\s*\(/i.test(s)) return true
      if (/\bto_timestamp\s*\(/i.test(s)) return true
      if (/\bcurrent_date\b/i.test(s)) return true
      if (/try_cast\s*\([\s\S]*\bas\s+(?:timestamp|date)\b/i.test(s)) return true
      if (/"[^"]*(?:date|time)[^"]*"/i.test(s)) return true
      if (/\b[a-z_][a-z0-9_]*\s*\.\s*"[^"]*(?:date|time)[^"]*"/i.test(s)) return true
      if (/\breportenddate\b/i.test(s)) return true
      return false
    }
    const includeNullsOnNot = Boolean(opts?.includeNullsOnNot)
    const tbl = filterBindTableIdent(filterIndex)
    const any = `EXISTS (SELECT 1 FROM ${tbl})`
    const hasNull = `EXISTS (SELECT 1 FROM ${tbl} WHERE value IS NULL)`
    const exprAsVarchar = `CAST(${exprSql} AS VARCHAR)`
    const exprAsDouble = `TRY_CAST(${exprSql} AS DOUBLE)`
    const exprAsBoolean = `TRY_CAST(${exprSql} AS BOOLEAN)`
    const exprAsDate = `TRY_CAST(${exprSql} AS DATE)`
    const exprAsTimestamp = `TRY_CAST(${exprSql} AS TIMESTAMP)`
    const rawValueAsVarchar = `CAST(value AS VARCHAR)`
    const canonicalValueAsVarchar = `CAST(value_canonical AS VARCHAR)`
    const valueAsDouble = `COALESCE(TRY_CAST(value_canonical AS DOUBLE), TRY_CAST(value AS DOUBLE))`
    const valueAsBoolean = `COALESCE(TRY_CAST(value_canonical AS BOOLEAN), TRY_CAST(value AS BOOLEAN))`
    const valueAsDate = `COALESCE(TRY_CAST(value_canonical AS DATE), TRY_CAST(value AS DATE))`
    const valueAsTimestamp = `COALESCE(TRY_CAST(value_canonical AS TIMESTAMP), TRY_CAST(value AS TIMESTAMP))`
    const valueAsMonthNumber = `COALESCE(TRY_CAST(value_canonical AS DOUBLE), TRY_CAST(value AS DOUBLE))`
    const valueAsYearNumber = `COALESCE(TRY_CAST(value_canonical AS DOUBLE), TRY_CAST(value AS DOUBLE))`
    const exprMonthNumber = `EXTRACT(MONTH FROM ${exprAsTimestamp})`
    const exprYearNumber = `EXTRACT(YEAR FROM ${exprAsTimestamp})`
    const dateLikeRuntimeMatches = looksDateLikeExpr(exprSql)
      ? `
          OR (${exprAsTimestamp} IS NOT NULL AND ${valueAsMonthNumber} IS NOT NULL AND ${valueAsMonthNumber} >= 1 AND ${valueAsMonthNumber} <= 12 AND ${exprMonthNumber} = ${valueAsMonthNumber})
          OR (${exprAsTimestamp} IS NOT NULL AND ${valueAsYearNumber} IS NOT NULL AND ${valueAsYearNumber} >= 1000 AND ${valueAsYearNumber} <= 9999 AND ${exprYearNumber} = ${valueAsYearNumber})
        `
      : ''
    const nonNullMatch = `EXISTS (
      SELECT 1
      FROM ${tbl}
      WHERE value IS NOT NULL
        AND (
          ${exprAsVarchar} = ${rawValueAsVarchar}
          OR (value_canonical IS NOT NULL AND ${exprAsVarchar} = ${canonicalValueAsVarchar})
          OR (${exprAsDouble} IS NOT NULL AND ${valueAsDouble} IS NOT NULL AND ${exprAsDouble} = ${valueAsDouble})
          OR (${exprAsBoolean} IS NOT NULL AND ${valueAsBoolean} IS NOT NULL AND ${exprAsBoolean} = ${valueAsBoolean})
          OR (${exprAsDate} IS NOT NULL AND ${valueAsDate} IS NOT NULL AND ${exprAsDate} = ${valueAsDate})
          OR (${exprAsTimestamp} IS NOT NULL AND ${valueAsTimestamp} IS NOT NULL AND ${exprAsTimestamp} = ${valueAsTimestamp})
          ${dateLikeRuntimeMatches}
        )
    )`

    // Semantics are driven entirely by the temp-table contents at runtime:
    // - empty table => no-op filter (TRUE)
    // - NULL row present => treat NULL as selected (IN) / excluded (NOT IN)
    // - includeNullsOnNot => include NULLs when list doesn't explicitly include NULL
    if (!not) {
      return `((NOT ${any}) OR ${nonNullMatch} OR (${hasNull} AND ${exprSql} IS NULL))`
    }

    return includeNullsOnNot
      ? `(CASE WHEN NOT ${any} THEN TRUE WHEN ${hasNull} THEN (${exprSql} IS NOT NULL AND NOT ${nonNullMatch}) ELSE (${exprSql} IS NULL OR NOT ${nonNullMatch}) END)`
      : `(CASE WHEN NOT ${any} THEN TRUE WHEN ${hasNull} THEN (${exprSql} IS NOT NULL AND NOT ${nonNullMatch}) ELSE (NOT ${nonNullMatch}) END)`
  }

  for (let filterIndex = 0; filterIndex < filters.length; filterIndex++) {
    const filter: any = (filters as any)[filterIndex]
    const rawField = String((filter as any)?.field_name ?? '')
    if (isMeasureNamesFallback(rawField)) {
      // Virtual field; can't be applied at row-level in this fallback query.
      continue
    }
    const operator = filter.operator.toUpperCase()
    const value = filter.value
    const explicitEmpty = isExplicitEmptyFilter(filter)
    const explicitEmptyInFilter = operator === 'IN' && explicitEmpty && (
      templateMode ||
      (Array.isArray(value) && value.length === 0)
    )

    const filterType = String((filter as any)?.filter_type ?? '').trim().toLowerCase()
    const isMeasureFilter = filterType === 'measure' || Boolean((filter as any)?.is_measure)
    if (isMeasureFilter) {
      if (explicitEmptyInFilter) {
        havingConditions.push('FALSE')
        continue
      }
      // Best-effort: treat as aggregated measure filter (HAVING) when the query has any grouping/aggregates.
      const raw = String((filter as any)?.field_name ?? '').trim()
      const mTok = raw.match(/^([A-Za-z_][A-Za-z0-9_]*)\s*\(\s*(.*?)\s*\)\s*$/)
      const tokAgg = mTok ? String(mTok[1] ?? '').trim().toUpperCase() : ''
      const innerField = mTok ? stripBracketsAroundFieldName(String(mTok[2] ?? '').trim()) : raw
      const colId = resolveColOrRawWhenNoSchema(innerField)
      if (!colId || colId === UNRESOLVED_FIELD) continue
      const colExpr = quoteIdent(colId)

      const aggRaw = String((filter as any)?.aggregation ?? '').trim()
      const aggUpper = (aggRaw || tokAgg || 'SUM').toUpperCase()
      const aggExpr =
        (aggUpper === 'COUNTD' || aggUpper === 'COUNTDISTINCT' || aggUpper === 'COUNT_DISTINCT')
          ? `COUNT(DISTINCT CAST(${colExpr} AS VARCHAR))`
          : (aggUpper === 'COUNT')
            ? `COUNT(${colExpr})`
            : (aggUpper === 'AVG')
              ? `AVG(TRY_CAST(${colExpr} AS DOUBLE))`
              : (aggUpper === 'MIN')
                ? `MIN(${colExpr})`
                : (aggUpper === 'MAX')
                  ? `MAX(${colExpr})`
                  : `SUM(TRY_CAST(${colExpr} AS DOUBLE))`

      if (operator === 'IN' || operator === 'NOT IN') {
        const not = operator === 'NOT IN'
        if (!templateMode && Array.isArray(value) && value.length === 0) {
          if (!not && explicitEmpty) havingConditions.push('FALSE')
          continue
        }
        if (templateMode && !not && explicitEmpty) {
          havingConditions.push('FALSE')
          continue
        }
        const pred = templateMode
          ? buildInPredicateFromValueTemplate(aggExpr, value, not, filterIndex, { includeNullsOnNot: true })
          : buildInPredicateFromValue(aggExpr, value, not, { includeNullsOnNot: true })
        if (pred) havingConditions.push(pred)
      } else if (operator === 'IS NULL' || operator === 'IS NOT NULL') {
        havingConditions.push(`${aggExpr} ${operator}`)
      } else if (operator === 'BETWEEN') {
        if (Array.isArray(value) && value.length === 2) {
          havingConditions.push(
            templateMode
              ? `${aggExpr} BETWEEN ${filterBindPart(filterIndex, 'lo')} AND ${filterBindPart(filterIndex, 'hi')}`
              : buildBetweenPredicate(aggExpr, value[0], value[1], filter, rawField)
          )
        }
      } else if (operator === 'LIKE' || operator === 'NOT LIKE') {
        havingConditions.push(
          templateMode
            ? `${aggExpr} ${operator} ${filterBindScalar(filterIndex)}`
            : `${aggExpr} ${operator} ${escapeValue(value)}`
        )
      } else if (operator === '!=' || operator === '<>') {
        havingConditions.push(
          templateMode
            ? `(${aggExpr} IS NULL OR ${aggExpr} ${operator} ${filterBindScalar(filterIndex)})`
            : `(${aggExpr} IS NULL OR ${aggExpr} ${operator} ${escapeValue(value)})`
        )
      } else {
        havingConditions.push(
          templateMode
            ? `${aggExpr} ${operator} ${filterBindScalar(filterIndex)}`
            : `${aggExpr} ${operator} ${escapeValue(value)}`
        )
      }
      continue
    }

    if (explicitEmptyInFilter) {
      pushRowFilterPredicate('FALSE')
      continue
    }

    const fieldExprRaw =
      (resolvedWeeklyTimePeriodExprForField
        ? resolvedWeeklyTimePeriodExprForField(rawField)
        : null) ||
      fieldExprFor(rawField, quoteIdent('t'))
    if (isNullishExpr(fieldExprRaw)) continue
    const fieldName = `(${fieldExprRaw})`
    const isWindowFieldExpr = looksWindowPredicate(fieldExprRaw)

    if (operator === 'RELATIVE_DATE') {
      const pred = templateMode
        ? buildRelativeDatePredicateTemplate(fieldName, filterIndex)
        : buildRelativeDatePredicate(fieldName, value)
      if (pred) pushRowFilterPredicate(pred)
      continue
    }
    if (operator === 'TOP_N') {
      const dimId = resolveColOrRawWhenNoSchema(String(filter.field_name || ''))
      if (!dimId) continue
      const pred = buildTopNPredicate(
        fieldName,
        dimId,
        String(filter.field_name || ''),
        value,
        contextFilters as any,
        templateMode ? { filterIndex, filterIndexByRef } : null,
      )
      if (pred) pushRowFilterPredicate(pred)
      continue
    }
    if (operator === 'IN' || operator === 'NOT IN') {
      const not = operator === 'NOT IN'
      if (!templateMode && Array.isArray(value) && value.length === 0) {
        if (!not && explicitEmpty) pushRowFilterPredicate('FALSE')
        // NOT IN [] => no-op
        continue
      }
      if (templateMode && !not && explicitEmpty) {
        pushRowFilterPredicate('FALSE')
        continue
      }
      const pred = templateMode
        ? buildInPredicateFromValueTemplate(fieldName, value, not, filterIndex)
        : buildInPredicateFromValue(fieldName, value, not)
      if (pred) pushRowFilterPredicate(pred)
    } else if (operator === 'IS NULL' || operator === 'IS NOT NULL') {
      if (isWindowFieldExpr) {
        postSelectFilters.push({
          field: rawField,
          operator: operator as 'IS NULL' | 'IS NOT NULL',
        })
        continue
      }
      pushRowFilterPredicate(`${fieldName} ${operator}`)
    } else if (operator === 'BETWEEN') {
      if (Array.isArray(value) && value.length === 2) {
        pushRowFilterPredicate(
          templateMode
            ? `${fieldName} BETWEEN ${filterBindPart(filterIndex, 'lo')} AND ${filterBindPart(filterIndex, 'hi')}`
            : buildBetweenPredicate(fieldName, value[0], value[1], filter, rawField)
        )
      }
    } else if (operator === 'LIKE' || operator === 'NOT LIKE') {
      pushRowFilterPredicate(
        templateMode
          ? `${fieldName} ${operator} ${filterBindScalar(filterIndex)}`
          : `${fieldName} ${operator} ${escapeValue(value)}`
      )
    } else {
      // =, !=, <, >, <=, >=
      pushRowFilterPredicate(
        templateMode
          ? `${fieldName} ${operator} ${filterBindScalar(filterIndex)}`
          : `${fieldName} ${operator} ${escapeValue(value)}`
      )
    }
  }

  // Build SQL query
  let sql = `SELECT ${selectFields.join(', ')} FROM ${parquetRef} AS ${quoteIdent('t')}`
  
  if (whereConditions.length > 0) {
    sql += ` WHERE ${whereConditions.join(' AND ')}`
  }

  if (groupByFields.length === 0 && havingConditions.length > 0) {
    sql += ` HAVING ${havingConditions.join(' AND ')}`
  }
  
  if (groupByFields.length > 0) {
    sql += ` GROUP BY ${groupByFields.join(', ')}`
    if (havingConditions.length > 0) {
      sql += ` HAVING ${havingConditions.join(' AND ')}`
    }
    if (qualifyConditions.length > 0) {
      sql += ` QUALIFY ${qualifyConditions.join(' AND ')}`
    }
    // Deterministic row order helps parity (Tableau is stable).
    const shouldDescDim = (fieldName: string): boolean => {
      const k = String(fieldName || '').trim().toLowerCase().replace(/^:+/, '')
      if (!k) return false
      if (k === 'reportenddate') return true
      if (k.includes('end date')) return true
      if (k.includes('end dates')) return true
      return false
    }
    const manualOrderCaseExpr = (expr: string, orderedValues: string[]): string => {
      const whens = orderedValues.map((v, i) => `WHEN ${escapeValue(v)} THEN ${i}`).join(' ')
      // Add a deterministic fallback rank for unknown values.
      return `CASE ${expr} ${whens} ELSE ${orderedValues.length + 999999} END`.replace(/\s+/g, ' ').trim()
    }
    const dirFor = (field: any): 'ASC' | 'DESC' => {
      const sortObj = field?.sort ?? field?.sort_config ?? field?.sortConfig ?? null
      const sortRaw = String(
        (typeof sortObj === 'string')
          ? sortObj
          : (sortObj && typeof sortObj === 'object')
            ? (sortObj as any)?.direction ?? (sortObj as any)?.dir ?? (sortObj as any)?.order ?? ''
            : ''
      ).trim().toLowerCase()
      if (sortRaw === 'desc') return 'DESC'
      if (sortRaw === 'asc') return 'ASC'
      const fn = String(field?.field_name ?? '')
      return shouldDescDim(fn) ? 'DESC' : 'ASC'
    }
    const orderTerms: string[] = []
    const pushOrderForField = (field: any) => {
      if ((field as any)?.aggregation) return
      const fn = String((field as any)?.field_name ?? '')
      if (!fn) return
      if (isMeasureNamesFallback(fn)) return
      const fieldKey = normKey(fn)
      const groupedExpr = String((fieldKey && groupedExprByFieldKey.get(fieldKey)) || '').trim()
      const col = groupedExpr || quoteIdent(fn)
      const k = String(fn || '').trim().toLowerCase().replace(/^:+/, '')
      const dir = dirFor(field)

      // Keep Tableau-like rollup totals ("All") stable: always sort sentinel totals LAST.
      pushOrderTerm(orderTerms, `CASE WHEN LOWER(CAST(${col} AS VARCHAR)) IN ('all','(all)') THEN 1 ELSE 0 END`, 'ASC')

      // XML-driven manual ordering (Tableau "Manual" sort).
      const sortObj = field?.sort ?? field?.sort_config ?? field?.sortConfig ?? null
      const customOrder =
        (sortObj && typeof sortObj === 'object' && Array.isArray((sortObj as any).custom_order))
          ? (sortObj as any).custom_order
          : (sortObj && typeof sortObj === 'object' && Array.isArray((sortObj as any).customOrder))
            ? (sortObj as any).customOrder
            : null
      const manualVals = Array.isArray(customOrder) ? customOrder.map((v) => String(v ?? '').trim()).filter(Boolean) : []
      if (manualVals.length) {
        pushOrderTerm(orderTerms, manualOrderCaseExpr(col, manualVals), 'ASC')
        // Tie-break unknown values deterministically.
        pushOrderTerm(orderTerms, `CAST(${col} AS VARCHAR)`, 'ASC')
        return
      }

      const isDateLike =
        k.includes('date') ||
        k.includes('time') ||
        k === 'reportenddate' ||
        k.includes('end date') ||
        k.includes('end dates')
      if (isDateLike) {
        pushOrderTerm(orderTerms, `TRY_CAST(${col} AS TIMESTAMP)`, dir)
        pushOrderTerm(orderTerms, `CAST(${col} AS VARCHAR)`, dir)
        return
      }

      pushOrderTerm(orderTerms, col, dir)
    }
    for (const rf of crosstabSpec.rows || []) {
      pushOrderForField(rf)
    }
    for (const cf of crosstabSpec.columns || []) {
      pushOrderForField(cf)
    }
    const finalizedOrderTerms = finalizeOrderTerms(orderTerms)
    if (typeof window !== 'undefined') {
      try {
        const qp = new URLSearchParams(window.location.search)
        const debugExecPlan = String(qp.get('fg_debug_exec_plan') ?? '').trim().toLowerCase()
        if (debugExecPlan && !['0', 'false', 'off', 'no'].includes(debugExecPlan)) {
          ;(window as any).__FG_ORDER_TERM_DEBUG__ = {
            worksheetName: options.worksheetName ?? null,
            rawOrderTerms: orderTerms.slice(0, 32),
            finalizedOrderTerms: finalizedOrderTerms.slice(0, 32),
            removedOrderTerms: orderTerms.filter((term) => !finalizedOrderTerms.includes(term)).slice(0, 32),
            ts: Date.now(),
          }
        }
      } catch { }
    }
    sql += ` ORDER BY ${finalizedOrderTerms.length ? finalizedOrderTerms.join(', ') : groupByFields.join(', ')}`
  }

  if (groupByFields.length === 0 && qualifyConditions.length > 0) {
    sql += ` QUALIFY ${qualifyConditions.join(' AND ')}`
  }

  if (postSelectFilters.length > 0) {
    const postAlias = quoteIdent('__fg_post')
    const postPred = postSelectFilters
      .map((f) => `${postAlias}.${quoteIdent(String(f.field || '').trim())} ${f.operator}`)
      .join(' AND ')
    sql = `SELECT * FROM (${sql}) AS ${postAlias} WHERE ${postPred}`
  }

  const restoreResidualTaggedLodWindowAggregates = (sqlIn: string): string => {
    return normalizeResidualTaggedLodWindowAggregates(sqlIn)
  }

  // Some measure-names/union assembly paths introduce grouped LOD windows late, after the
  // earlier grouped-query rewrite pass has already run. Normalize one final time so preserved
  // `LOD_OUTER_AGG` tags cannot leak into executable SQL.
  sql = restoreResidualTaggedLodWindowAggregates(sql)
  if (/\bgroup\s+by\b/i.test(sql) && /\/\*\s*LOD\b/i.test(sql) && /\bover\s*\(/i.test(sql)) {
    sql = rewriteLodWindowsForGroupedQuery(sql)
    const hasFactoredWindowCorrelates =
      /\b__fg_win_corr\b/i.test(sql) || /\b__fg_win_src_\d+\b/i.test(sql)
    if (!skipNestedWindowRewrite && !hasFactoredWindowCorrelates) {
      sql = rewriteNestedWindowInGroupedSelectCached(sql)
    }
  }

  // Apply parameter substitution
  sql = templateMode ? substituteParametersTemplate(sql) : substituteParameters(sql)

  if (limit > 0) {
    sql += ` LIMIT ${Math.floor(limit)}`
  }

  const detectStrictNullFallbackReason = (sqlText: string): string | null => {
    const raw = String(sqlText ?? '')
    if (!raw) return null
    const checks: Array<{ re: RegExp; reason: string }> = [
      { re: /\bSUM\s*\(\s*TRY_CAST\(\s*(?:\(+\s*)?NULL(?:\s*\)+)?\s+AS\s+DOUBLE\s*\)\s*\)/i, reason: 'sum_try_cast_null' },
      { re: /\b(?:MIN|MAX|AVG)\s*\(\s*TRY_CAST\(\s*(?:\(+\s*)?NULL(?:\s*\)+)?\s+AS\s+DOUBLE\s*\)\s*\)/i, reason: 'agg_try_cast_null' },
      { re: /\b(?:MIN|MAX|SUM|AVG)\s*\(\s*NULL\s*\)\s*OVER\b/i, reason: 'window_agg_null' },
      {
        re: /\bTRY_CAST\(\s*NULL\s+AS\s+(?:DOUBLE|DECIMAL|INTEGER|BIGINT|SMALLINT|TINYINT|FLOAT|REAL|VARCHAR|BOOLEAN|TIMESTAMP|DATE)\s*\)/i,
        reason: 'try_cast_null',
      },
    ]
    const hit = checks.find(({ re }) => re.test(raw))
    return hit ? hit.reason : null
  }

  if (!ALLOW_FALLBACKS && unresolvedEvents.length > 0) {
    try { reportUnresolved(sql) } catch { }
    const ws = String(options.worksheetName ?? '').trim()
    const hint = ws ? ` (worksheet: ${ws})` : ''
    const uniq = new Set<string>()
    for (const ev of unresolvedEvents) {
      const f = String((ev as any)?.field ?? '').trim()
      if (f) uniq.add(f)
      if (uniq.size >= 5) break
    }
    const sample = Array.from(uniq)
    const sampleMsg = sample.length ? `: ${sample.join(', ')}` : ''
    throw new Error(`Unresolved field reference${hint}${sampleMsg}`)
  }

  if (!ALLOW_FALLBACKS) {
    const reason = detectStrictNullFallbackReason(sql)
    if (reason) {
      try { reportUnresolved(sql) } catch { }
      const ws = String(options.worksheetName ?? '').trim()
      const hint = ws ? ` (worksheet: ${ws})` : ''
      throw new Error(`Unresolved field reference${hint} [${reason}]`)
    }
  }

  if (!ALLOW_FALLBACKS) {
    const unresolved = UNRESOLVED_FIELD.toLowerCase()
    if (sql.toLowerCase().includes(unresolved)) {
      const ws = String(options.worksheetName ?? '').trim()
      const hint = ws ? ` (worksheet: ${ws})` : ''
      throw new Error(`Unresolved field reference${hint}`)
    }
  }
  const _finalResult = finalizeSql(sql)
  if (!ALLOW_FALLBACKS) {
    const finalReason = detectStrictNullFallbackReason(_finalResult)
    if (finalReason) {
      try { reportUnresolved(_finalResult) } catch { }
      const ws = String(options.worksheetName ?? '').trim()
      const hint = ws ? ` (worksheet: ${ws})` : ''
      throw new Error(`Unresolved field reference${hint} [${finalReason}]`)
    }
  }
  _markPhase('finalizeSql_fallback')
  _emitTimings()
  return _finalResult
}

export function compileWorksheetQuery(
  spec: WorksheetSpecV1,
  options: WorksheetQueryOptions & { emitExecPlan?: boolean; collectExecPlan?: boolean }
): WorksheetCompilation {
  const logCompileBoundary = (phase: string, extra?: Record<string, any>) => {
    try {
      console.warn(`[FG-COMPILE-BOUNDARY] ${JSON.stringify({
        worksheet:
          String(
            (options as any)?.worksheetName ??
            (spec as any)?.worksheet_name ??
            (spec as any)?.source?.tableau?.worksheet_name ??
            '',
          ).trim() || null,
        phase,
        ...(extra && typeof extra === 'object' ? extra : {}),
      })}`)
    } catch { }
  }
  const sourceSchemaForCompile = ((options as any)?.sourceSchema || (options as any)?.source_schema || null) as SourceSchema | null
  const summarizePlanSchemaAnomalies = (planRaw: any): Record<string, any> | null => {
    const schemaColumns = sourceSchemaColumnNames(sourceSchemaForCompile)
    if (!schemaColumns.length || !planRaw || typeof planRaw !== 'object') return null
    const schemaKeys = new Set(schemaColumns.map(plannerRootTraceKey).filter(Boolean))
    const statements = Array.isArray((planRaw as any)?.statements) ? (planRaw as any).statements : []
    const missing = new Map<string, { column: string; stmtIndex: number; stmtKind: string; target: boolean }>()
    const collectQualifiedRefs = (sqlRaw: string): string[] => {
      const sql = String(sqlRaw ?? '')
      if (!sql) return []
      const out: string[] = []
      const seen = new Set<string>()
      let inString = false
      for (let i = 0; i < sql.length; i += 1) {
        const ch = sql[i]
        if (ch === "'") {
          if (inString && sql[i + 1] === "'") {
            i += 1
            continue
          }
          inString = !inString
          continue
        }
        if (inString) continue

        const before = sql[i - 1]
        if (before && /[A-Za-z0-9_]/.test(before)) continue
        let j = i
        if (sql[j] === '"') {
          j += 1
          for (; j < sql.length; j += 1) {
            if (sql[j] === '"') {
              if (sql[j + 1] === '"') {
                j += 1
                continue
              }
              break
            }
          }
          if (sql[j] !== '"') continue
          j += 1
        } else {
          const aliasMatch = sql.slice(j).match(/^([A-Za-z_][A-Za-z0-9_]*)/)
          if (!aliasMatch) continue
          j += String(aliasMatch[1] ?? '').length
        }
        while (j < sql.length && /\s/.test(sql[j])) j += 1
        if (sql[j] !== '.') continue
        j += 1
        while (j < sql.length && /\s/.test(sql[j])) j += 1
        let col = ''
        if (sql[j] === '"') {
          j += 1
          for (; j < sql.length; j += 1) {
            const c = sql[j]
            if (c === '"') {
              if (sql[j + 1] === '"') {
                col += '"'
                j += 1
                continue
              }
              break
            }
            col += c
          }
        } else {
          const colMatch = sql.slice(j).match(/^([A-Za-z_][A-Za-z0-9_]*)/)
          if (!colMatch) continue
          col = String(colMatch[1] ?? '')
        }
        const key = plannerRootTraceKey(col)
        if (!key || seen.has(key)) continue
        seen.add(key)
        out.push(col)
      }
      return out
    }
    statements.forEach((stmtRaw: any, idx: number) => {
      const stmt = String(stmtRaw ?? '')
      const stmtKind = /_agg_base_src\b/i.test(stmt)
        ? 'agg_base_src'
        : /_agg_base\b/i.test(stmt)
          ? 'agg_base'
          : /fg_plan_/i.test(stmt)
            ? 'plan_temp'
            : 'other'
      for (const colRaw of collectQualifiedRefs(stmt)) {
        const col = String(colRaw ?? '').trim()
        if (!col || col.startsWith('__') || /^fg_plan_/i.test(col)) continue
        const key = plannerRootTraceKey(col)
        if (!key || schemaKeys.has(key)) continue
        if (!missing.has(key)) {
          missing.set(key, {
            column: col,
            stmtIndex: idx + 1,
            stmtKind,
            target: PLANNER_ROOT_TRACE_COLUMN_KEYS.has(key),
          })
        }
      }
    })
    const missingColumns = Array.from(missing.values())
    const targetMissingColumns = missingColumns.filter((entry) => entry.target)
    if (!targetMissingColumns.length) return null
    return {
      source_schema_count: schemaColumns.length,
      source_schema_signature: String((sourceSchemaForCompile as any)?.schemaFingerprint ?? '').trim() || null,
      source_relation: String((sourceSchemaForCompile as any)?.relationName ?? '').trim() || null,
      missing_columns: missingColumns.slice(0, 32),
      target_missing_columns: targetMissingColumns,
      statement_count: statements.length,
      materialized_sql_hash: String((planRaw as any)?.materializedSqlHash ?? '').trim() || null,
      materialized_table: String((planRaw as any)?.materializedTable ?? '').trim() || null,
      agg_base_materialized_table: String((planRaw as any)?.aggBaseMaterializedTable ?? '').trim() || null,
    }
  }
  const compileOptions: any = {
    ...(options as any),
    emitExecPlan: Boolean((options as any)?.emitExecPlan),
  }
  delete compileOptions.__fg_duckdb_exec_plan
  const finalSql = buildWorksheetQuerySql(spec, compileOptions)
  logCompileBoundary('after_buildWorksheetQuerySql', {
    finalSqlLength: finalSql.length,
  })
  const execPlan = (compileOptions as any).__fg_duckdb_exec_plan ?? null
  const planSchemaAnomaly = summarizePlanSchemaAnomalies(execPlan)
  if (planSchemaAnomaly) {
    logPlannerRootTrace('exec_plan_source_schema_anomaly', {
      worksheet:
        String(
          (options as any)?.worksheetName ??
          (spec as any)?.worksheet_name ??
          (spec as any)?.source?.tableau?.worksheet_name ??
          '',
        ).trim() || null,
      ...planSchemaAnomaly,
    })
  }
  // `compilation.aggBaseSql` is the human/test-inspection view of the compiled aggregate
  // base. We keep the `materializedSql` fallback here so template-mode drift detection
  // (which reads this field) continues to work for simple plans where the materialized SQL
  // IS effectively the aggregate base. Whether that SQL should actually drive runtime
  // `__fg_aggp_*` TEMP TABLE materialization is decided separately below by the
  // `planHasReusableAggBase` check.
  const planAggBaseMaterializedSqlRaw = (execPlan as any)?.aggBaseMaterializedSql
  const planHasReusableAggBase =
    typeof planAggBaseMaterializedSqlRaw === 'string' &&
    String(planAggBaseMaterializedSqlRaw).trim().length > 0
  const aggBaseSql =
    planHasReusableAggBase
      ? String(planAggBaseMaterializedSqlRaw).trim()
      : typeof (execPlan as any)?.materializedSql === 'string'
        ? String((execPlan as any).materializedSql).trim() || null
        : null
  const windowInputSql =
    typeof (execPlan as any)?.windowInputSql === 'string'
      ? String((execPlan as any).windowInputSql).trim() || null
      : typeof (execPlan as any)?.materializedSql === 'string'
        ? String((execPlan as any).materializedSql).trim() || null
      : null
  logCompileBoundary('after_extract_exec_plan_sql', {
    hasExecPlan: Boolean(execPlan),
    aggBaseSqlLength: aggBaseSql?.length ?? 0,
    windowInputSqlLength: windowInputSql?.length ?? 0,
    materializedSqlHash: String((execPlan as any)?.materializedSqlHash ?? '').trim() || null,
    materializedTable: String((execPlan as any)?.materializedTable ?? '').trim() || null,
    aggBaseMaterializedTable: String((execPlan as any)?.aggBaseMaterializedTable ?? '').trim() || null,
    schemaAnomaly: planSchemaAnomaly
      ? {
        missingCount: Array.isArray(planSchemaAnomaly.missing_columns) ? planSchemaAnomaly.missing_columns.length : 0,
        targetMissingColumns: planSchemaAnomaly.target_missing_columns,
      }
      : null,
  })
  const aggBaseSignature = buildAggBasePlannerSignature(aggBaseSql, execPlan)
  logCompileBoundary('after_agg_base_signature', {
    aggBaseSignature,
  })
  const windowInputSignature = buildDatasourceExecutionNodePlannerSignature(
    'window_input_base',
    windowInputSql,
    execPlan,
  )
  logCompileBoundary('after_window_input_signature', {
    windowInputSignature,
  })
  // Only surface an `aggregate_base` datasource node when the plan explicitly carries a
  // reusable aggregate base (via `aggBaseMaterializedSql`). The `materializedSql` fallback
  // used above for `aggBaseSql` inspection must NOT drive runtime `__fg_aggp_*` TEMP TABLE
  // materialization: in MNV / window / staged-chunk paths, `materializedSql` is the final
  // compound query (CTEs, window functions, UNION ALL of chunks) rather than a reusable
  // pre-aggregation, and materializing it all blows DuckDB memory on large datasets
  // (observed OOM on Monthly Comparison with 1.4M rows × N measures).
  const datasourceNodes = [
    ...(planHasReusableAggBase && aggBaseSql && aggBaseSignature
      ? [{ nodeKind: 'aggregate_base' as const, nodeSignature: aggBaseSignature, sql: aggBaseSql }]
      : []),
    ...(windowInputSql && windowInputSignature
      ? [{
        nodeKind: 'window_input_base' as const,
        nodeSignature: windowInputSignature,
        sql: windowInputSql,
        plannerNodeSignature: aggBaseSignature,
      }]
      : []),
  ]
  logCompileBoundary('after_datasource_nodes', {
    nodeCount: datasourceNodes.length,
  })
  const hashSql = (sqlText: string | null): string | null => {
    const text = String(sqlText ?? '').trim()
    if (!text) return null
    return Math.abs(hashString(text)).toString(36)
  }
  logCompileBoundary('before_return')
  return {
    finalSql,
    execPlan,
    aggBaseSql,
    signatures: {
      finalSqlHash: hashSql(finalSql),
      aggBaseSqlHash: hashSql(aggBaseSql),
      aggBaseSignature,
      datasourceNodeIdentities: {
        aggregate_base: aggBaseSignature,
        window_input_base: windowInputSignature,
      },
    },
    diagnostics: {
      execPlanRequested: Boolean(
        (options as any)?.emitExecPlan ||
        (options as any)?.collectExecPlan ||
        (options as any)?.collect_exec_plan
      ),
      execPlanGenerated: Boolean(execPlan && Array.isArray((execPlan as any).statements) && typeof (execPlan as any).finalSql === 'string'),
      densifyEnabled: Boolean((options as any)?.densify),
      includeTotalsEnabled: Boolean((options as any)?.includeTotals),
      datasourceNodes,
    },
  }
}

export function buildWorksheetQuery(
  spec: WorksheetSpecV1,
  options: WorksheetQueryOptions
): string {
  return compileWorksheetQuery(spec, options).finalSql
}

/**
 * Extract field names from a worksheet spec (for CSV headers)
 */
export function extractFieldNames(spec: WorksheetSpecV1): string[] {
  if (spec.kind !== 'crosstab') {
    return []
  }

  const crosstabSpec = spec.spec as CrosstabSpecV1
  const fieldNames: string[] = []

  // Add row fields
  for (const rowField of crosstabSpec.rows || []) {
    fieldNames.push(rowField.field_name)
  }

  // Add column fields
  for (const colField of crosstabSpec.columns || []) {
    fieldNames.push(colField.field_name)
  }

  return fieldNames
}
